import React, { useRef, useEffect, useState, useMemo, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { worldLocations, getAvatarUrl, allTags, personalDataFields, socialPlatforms } from './profilesData';
import {
  connectWallet,
  disconnectWallet as disconnectWalletCrypto,
  formatAddress,
  getSavedWalletAddress,
  getSavedWalletSignature,
  setupAccountChangeListener,
  isMetaMaskInstalled,
  generateEncryptionKey,
  generateEncryptionKeyFromSignature,
  encryptionKeyToHex,
  encryptProfile,
  authenticateWalletUnified,
  uploadToPinata,
  decryptProfile,
  fetchFromIPFS,
  hasFirestoreEncryptionKey,
  generateTemporaryWalletFromUsername,
  getUsernameFromTemporaryAddress
} from './cryptoOperations.js';
import {
  getUsernameFromWallet,
  getUserProfileData,
  getUserProfileByWallet,
  saveUserProfile,
  updatePublishedMetadata,
  updateUserPhotos,
  deleteUserProfile,
  getAllProfiles,
  clearAllProfiles,
  isUsernameTaken,
  clearAllTestProfiles,
  updateLastVisit,
  getWalletFromUsername
} from './userMappingService.js';
import { populateDatabase } from './populateDatabase.js';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from './firebase';
import { ethers } from 'ethers';
import { saveCIDToBlockchain as saveCIDToBlockchainLib } from '@peopletag/protocol';
import CozyConnections from './components/CozyConnections';
import Messaging from './components/Messaging';
import Groups from './components/Groups';
import ActivityFeed from './components/ActivityFeed';
import GbixDisplay from './components/GbixDisplay';
import { getUserGroups, discoverGroups } from './services/groupsService';
import Tooltip from './components/Tooltip';
import MobileLayout from './components/MobileLayout';
import NetworkStatus from './components/NetworkStatus';
import HobbyMascot from './components/HobbyMascot';
import { selectRandomGbixItem } from './config/gbixEngine';
import { getCIDFromArc, saveCIDToArc } from './services/migrationService';
import { getUSDCBalance, sendUSDC } from './services/walletService';
import { subscribeToConversations, subscribeToUnreadCount, sendMessage } from './services/messagingService';
import { ARC_TESTNET_NETWORK } from './config/blockchain';
import { getUserLastActivity, isUserRecentlyActive } from './services/activityService';
import {
  submitReview,
  getUserRatingSummary,
  getUserReviews,
  getReviewByReviewer,
  subscribeToRatingSummary,
  subscribeToUserReviews,
  deleteReview
} from './services/ratingService';
import * as utils from './utils';
import { getClusterGroupConfig, createDefaultProfileIcon, createSelectedProfileIcon } from './config/leafletClusterConfig';
import { DISCORD_LINK, TWITTER_HANDLE, TWITTER_URL, APP_URL } from './config/constants';
import {
  getUserGamificationData,
  updateActivityStreak,
  updateProfileCompleteness,
  checkAndAwardBadges,
  trackMessageSent,
  trackProfileViewed,
  getLeaderboard,
  getUserRank,
  BADGES,
  calculateProfileCompleteness,
  checkSocialConnections,
  awardBullishPost,
  getOrCreateReferralCode,
  processReferral,
  getReferralStats
} from './services/gamificationService';

// Make populateDatabase available globally for console access
if (typeof window !== 'undefined') {
  window.populateDatabase = populateDatabase;
}

// Helper function to fetch Firestore metadata with retry logic
// This prevents the bug where published states appear as false when Firestore query times out
const fetchFirestoreMetadataWithRetry = async (walletAddress, maxRetries = 5, delayMs = 300) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const metadata = await getUserProfileByWallet(walletAddress, null);
      if (metadata) {
        console.log(`✅ Firestore metadata loaded successfully on attempt ${attempt}`);
        return metadata;
      }
      // If metadata is null and not the last attempt, wait and retry
      if (attempt < maxRetries) {
        const waitTime = delayMs * Math.pow(1.5, attempt - 1); // Exponential backoff: 300ms, 450ms, 675ms, 1012ms
        console.log(`⚠️ Firestore metadata returned null, retrying in ${Math.round(waitTime)}ms (attempt ${attempt}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    } catch (error) {
      console.error(`Error fetching Firestore metadata (attempt ${attempt}/${maxRetries}):`, error);
      // If not the last attempt, wait and retry
      if (attempt < maxRetries) {
        const waitTime = delayMs * Math.pow(1.5, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }
  
  // Final attempt with longer timeout (2 seconds) before giving up
  console.log('⚠️ All retries failed, attempting final synchronous fetch with longer timeout...');
  try {
    const finalMetadata = await Promise.race([
      getUserProfileByWallet(walletAddress, null),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Final timeout')), 2000))
    ]);
    if (finalMetadata) {
      console.log('✅ Firestore metadata loaded on final attempt');
      return finalMetadata;
    }
  } catch (finalError) {
    console.error('❌ Final Firestore metadata fetch also failed:', finalError);
  }
  
  console.error('❌ Failed to fetch Firestore metadata after all retries. Published states will be inferred from IPFS data.');
  return null;
};

function App({ isMobile = false }) {
  const location = useLocation();
  const routerNavigate = useNavigate();
  
  // Mobile-aware pathname and navigation
  // Strip /m prefix from pathname for internal routing logic
  const getNormalizedPathname = useCallback(() => {
    const pathname = location.pathname;
    if (isMobile && pathname.startsWith('/m')) {
      const normalized = pathname.replace(/^\/m/, '') || '/';
      // Ensure we have a leading slash and handle trailing slash
      return normalized.startsWith('/') ? normalized : `/${normalized}`;
    }
    return pathname;
  }, [location.pathname, isMobile]);

  const getWindowNormalizedPathname = useCallback(() => {
    if (typeof window === 'undefined') {
      return '/';
    }
    let pathname = window.location.pathname || '/';
    if (isMobile && pathname.startsWith('/m')) {
      pathname = pathname.replace(/^\/m/, '') || '/';
    }
    return pathname.startsWith('/') ? pathname : `/${pathname}`;
  }, [isMobile]);
  
  const normalizedPathname = getNormalizedPathname();
  
  // Mobile-aware navigate function that adds /m prefix when needed
  const navigate = useCallback((path, options) => {
    const targetPath = path.startsWith('/') ? path : `/${path}`;
    if (isMobile) {
      routerNavigate(`/m${targetPath}`, options);
    } else {
      routerNavigate(targetPath, options);
    }
  }, [isMobile, routerNavigate]);
  
  const mobileNavigate = useCallback((path, options) => {
    navigate(path, options);
  }, [navigate]);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const headerNavRef = useRef(null);
  const footerBottomRef = useRef(null);
  const [profileVisible, setProfileVisible] = useState(false);
  const [groupsVisible, setGroupsVisible] = useState(false);
  const [messagesVisible, setMessagesVisible] = useState(false);
  const [activityFeedVisible, setActivityFeedVisible] = useState(false);
  const [tagRunVisible, setTagRunVisible] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [hasMessagesKey, setHasMessagesKey] = useState(false); // Track if Firestore encryption key is cached
  const [selectedMessageRecipient, setSelectedMessageRecipient] = useState(null);
  const [selectedMessageRecipientWallet, setSelectedMessageRecipientWallet] = useState(null); // Store wallet address separately
  const [bugVisible, setBugVisible] = useState(false);
  const [bugReport, setBugReport] = useState({ text: '', file: null });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leftSectionVisible, setLeftSectionVisible] = useState(false);
  const [smartRecommendationsVisible, setSmartRecommendationsVisible] = useState(false);
  const [smartIconError, setSmartIconError] = useState(false);
  const [isSwitchingSections, setIsSwitchingSections] = useState(false);
  const [aiAssistantVisible, setAiAssistantVisible] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState([]);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [selectedModel, setSelectedModel] = useState('llama-3.1-8b');
  const [availableModels, setAvailableModels] = useState([]);
  const aiInputRef = useRef(null);
  const aiConversationRef = useRef(null);
  const [isGeneratingTwitterPost, setIsGeneratingTwitterPost] = useState(false);
  const [generatedPostText, setGeneratedPostText] = useState('');
  const [copiedVisible, setCopiedVisible] = useState(false);
  const [copiedTagIndex, setCopiedTagIndex] = useState(null);
  const [showCatAds, setShowCatAds] = useState(false);
  const [showCookingAds, setShowCookingAds] = useState(false);
  const [sortType, setSortType] = useState('match'); // 'match', 'distance', 'city', 'name', 'activity'
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortDropdownRef = useRef(null);
  const sortButtonRef = useRef(null);
  // Search Filter: "No test users"
  const [excludeTestUsers, setExcludeTestUsers] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef(null);
  const filterButtonRef = useRef(null);
  const [walletAddress, setWalletAddress] = useState(null);
  const walletAddressRef = useRef(null);
  const encryptedCountRef = useRef(0);
  const isLoadingMyProfileRef = useRef(false); // Track if we're loading "My Profile" to prevent "other profile" flash
  const [isConnecting, setIsConnecting] = useState(false);
  const [aboutVisible, setAboutVisible] = useState(false);
  const [termsVisible, setTermsVisible] = useState(false);
  const [termsClosedByUser, setTermsClosedByUser] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false); // Track if Terms was opened via "Read here" (expanded mode)
  const [messagingHelpExpanded, setMessagingHelpExpanded] = useState(false); // Track if messaging help section is expanded
  const [contractSourceExpanded, setContractSourceExpanded] = useState(false); // Track if contract source code section is expanded
  
  // Gamification state
  const [gamificationData, setGamificationData] = useState(null);
  const [viewedProfileGamificationData, setViewedProfileGamificationData] = useState(null);
  const [gamificationDetailsVisible, setGamificationDetailsVisible] = useState(false);
  const [xPostUrl, setXPostUrl] = useState('');
  const [isSubmittingXPost, setIsSubmittingXPost] = useState(false);
  const [xPostSubmissionVisible, setXPostSubmissionVisible] = useState(false);
  const [submittedXPostUrl, setSubmittedXPostUrl] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardType, setLeaderboardType] = useState('points');
  const [userRank, setUserRank] = useState(null);
  const [leaderboardUsernames, setLeaderboardUsernames] = useState({});
  const [newBadges, setNewBadges] = useState([]);
  const [hoveredBadge, setHoveredBadge] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, badgeId: null });
  const [referralCode, setReferralCode] = useState(null); // Referral code from URL
  const [referralStats, setReferralStats] = useState(null);
  const [inviteMessage, setInviteMessage] = useState('');
  const [isGeneratingInvite, setIsGeneratingInvite] = useState(false);
  const [inviterProfile, setInviterProfile] = useState(null); // Profile of the user who invited this user
  
  // AI Invite Message Generation Prompt - separate from TagAI
  // You can customize this prompt to change how invite messages are generated
  const INVITE_MESSAGE_AI_PROMPT = `Write a short, friendly social media invite message. 

Context about the person inviting: {PROFILE_CONTEXT}

Requirements:
- Maximum 170 characters
- Friendly and inviting tone
- Mention "PeopleTag" (a social network)
- Encourage people to join
- Make it personal based on the context provided
- Do NOT include any links or URLs
- Just write the message text only, nothing else
- You may change words sequence or vary message a bit
- Randomly Greet or not, introduce or not, tell about your hobbies or not

Example format: "Hey! I'm [name] and I just joined PeopleTag - a cool social network for connecting with people who share your interests. Join me!"
Another example format: "I just joined PeopleTag - a place where you may find friends with same interest like #coffee or #music! Join me there."
Now write a unique invite message:`;
  
  // Rating/Review state
  const [userRatingSummary, setUserRatingSummary] = useState({ averageRating: 0, reviewCount: 0, totalRating: 0 });
  const [userReviews, setUserReviews] = useState([]);
  const [reviewFormVisible, setReviewFormVisible] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [ratingHelpVisible, setRatingHelpVisible] = useState(false);
  const [reviewsListModalVisible, setReviewsListModalVisible] = useState(false);
  const [isDeletingReview, setIsDeletingReview] = useState(false);
  const [reviewerUsernames, setReviewerUsernames] = useState({});
  
  // Keep walletAddressRef in sync with walletAddress state
  useEffect(() => {
    walletAddressRef.current = walletAddress;
  }, [walletAddress]);

  // USDC balance + transfer modal state
  const [usdcBalance, setUsdcBalance] = useState(null);
  const [isLoadingUsdcBalance, setIsLoadingUsdcBalance] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [transferRecipientInput, setTransferRecipientInput] = useState('');
  const [transferSelectedProfile, setTransferSelectedProfile] = useState(null);
  const [transferShowSuggestions, setTransferShowSuggestions] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [transferTxHash, setTransferTxHash] = useState('');
  const [balanceRefreshTick, setBalanceRefreshTick] = useState(0);

  // Heuristic: a temp/generated wallet (e.g. for test users) won't accept funds.
  // generateTemporaryWalletFromUsername creates a deterministic 0x address from
  // the username, so check the username->address map in localStorage.
  const isTempWalletAddress = useCallback((address) => {
    if (!address) return true;
    try {
      const map = JSON.parse(localStorage.getItem('tempAddressToUsername') || '{}');
      return Boolean(map[address.toLowerCase()]);
    } catch {
      return false;
    }
  }, []);

  // Fetch USDC balance whenever the connected wallet changes (and after sends).
  useEffect(() => {
    if (!walletAddress) {
      setUsdcBalance(null);
      return;
    }
    let cancelled = false;
    setIsLoadingUsdcBalance(true);
    getUSDCBalance(walletAddress)
      .then(b => { if (!cancelled) setUsdcBalance(b); })
      .catch(err => {
        console.warn('Failed to fetch USDC balance:', err);
        if (!cancelled) setUsdcBalance(null);
      })
      .finally(() => { if (!cancelled) setIsLoadingUsdcBalance(false); });
    return () => { cancelled = true; };
  }, [walletAddress, balanceRefreshTick]);

  const openTransferModal = useCallback((profile) => {
    if (profile && (profile.username || profile.walletAddress)) {
      setTransferRecipientInput(profile.username ? `@${profile.username}` : '');
      setTransferSelectedProfile(profile);
    } else {
      setTransferRecipientInput('');
      setTransferSelectedProfile(null);
    }
    setTransferShowSuggestions(false);
    setTransferAmount('');
    setTransferError('');
    setTransferTxHash('');
    setTransferModalVisible(true);
  }, []);

  const closeTransferModal = useCallback(() => {
    if (isTransferring) return;
    setTransferModalVisible(false);
    setTransferError('');
  }, [isTransferring]);

  const selectTransferRecipient = useCallback((profile) => {
    setTransferSelectedProfile(profile);
    setTransferRecipientInput(profile.username ? `@${profile.username}` : '');
    setTransferShowSuggestions(false);
    setTransferError('');
  }, []);

  const handleSendUSDC = useCallback(async () => {
    setTransferError('');
    setTransferTxHash('');
    if (!walletAddress) {
      setTransferError('Connect your wallet first.');
      return;
    }
    if (!transferSelectedProfile) {
      setTransferError('Pick a PeopleTag user from the suggestions.');
      return;
    }
    if (transferSelectedProfile._isTestRecord || isTempWalletAddress(transferSelectedProfile.walletAddress)) {
      setTransferError('This is a test user — they have no real wallet, so funds cannot be delivered.');
      return;
    }
    if (!transferSelectedProfile.walletAddress) {
      setTransferError('This user has no wallet address on file.');
      return;
    }
    try {
      setIsTransferring(true);
      const recipientAddress = transferSelectedProfile.walletAddress.trim();
      const hash = await sendUSDC(recipientAddress, transferAmount);
      setTransferTxHash(hash);
      setBalanceRefreshTick(t => t + 1);
      // Drop a note into the recipient's inbox so they see the incoming transfer
      // even with no prior conversation. Non-fatal if it fails.
      try {
        const noteText = `Incoming money send, amount: ${transferAmount} USDC, TX: ${hash}`;
        await sendMessage(walletAddress, recipientAddress, noteText);
      } catch (msgErr) {
        console.warn('Could not post transfer notification message:', msgErr);
      }
    } catch (err) {
      setTransferError(err?.message || 'Transfer failed');
    } finally {
      setIsTransferring(false);
    }
  }, [walletAddress, transferSelectedProfile, transferAmount, isTempWalletAddress]);


  const formatUsdcBalance = useCallback((b) => {
    if (b == null) return '—';
    const n = Number(b);
    if (!isFinite(n)) return b;
    return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }, []);

  // Check if Messages encryption key is cached (for button glow)
  useEffect(() => {
    if (!walletAddress) {
      setHasMessagesKey(false);
      return;
    }

    const checkKeyStatus = () => {
      try {
        const hasKey = hasFirestoreEncryptionKey(walletAddress);
        setHasMessagesKey(hasKey);
      } catch (error) {
        console.warn('Error checking Firestore encryption key status:', error);
        setHasMessagesKey(false);
      }
    };

    // Check immediately
    checkKeyStatus();

    // Check periodically (every 2 seconds) to update when key is cached/expired
    const interval = setInterval(checkKeyStatus, 2000);

    return () => clearInterval(interval);
  }, [walletAddress]);

  // Listen for unread messages count
  useEffect(() => {
    if (!walletAddress) {
      setUnreadMessagesCount(0);
      return;
    }

    // Subscribe to unread message count for badge display
    const unsubscribe = subscribeToUnreadCount(walletAddress, (count) => {
      console.log('Unread messages count updated:', count);
      const prev = encryptedCountRef.current || 0;
      encryptedCountRef.current = count || 0;
      setUnreadMessagesCount(count);
      // New incoming message: refresh USDC balance (transfer notifications
      // arrive as messages, and any other message could coincide with a tx).
      if ((count || 0) > prev) {
        setBalanceRefreshTick(t => t + 1);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
      encryptedCountRef.current = 0;
    };
  }, [walletAddress]);

  // Debug: Log badge state changes
  useEffect(() => {
    console.log('Badge state - unreadMessagesCount:', unreadMessagesCount, 'messagesVisible:', messagesVisible, 'shouldShow:', !messagesVisible && unreadMessagesCount > 0);
  }, [unreadMessagesCount, messagesVisible]);

  // When TagRUN is visible, hide all other sections
  useEffect(() => {
    if (tagRunVisible) {
      setProfileVisible(false);
      setGroupsVisible(false);
      setMessagesVisible(false);
      setActivityFeedVisible(false);
      setBugVisible(false);
      setAboutVisible(false);
      setAiAssistantVisible(false);
    }
  }, [tagRunVisible]);

  // When Profile or Groups is visible, hide TagRUN
  useEffect(() => {
    if (profileVisible || groupsVisible) {
      setTagRunVisible(false);
    }
  }, [profileVisible, groupsVisible]);

  // Cache for profile lookup map to avoid refetching on every change
  const profileLookupMapRef = useRef({});
  const profileLookupMapCacheTimeRef = useRef(0);
  const PROFILE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Load leaderboard data only when TagRUN is visible (to avoid unnecessary loads)
  useEffect(() => {
    // Only load if TagRUN is visible
    if (!tagRunVisible) {
      return;
    }

    const loadLeaderboard = async () => {
      try {
        const leaderboard = await getLeaderboard(leaderboardType, 50);
        setLeaderboardData(leaderboard);
        if (walletAddress) {
          const rank = await getUserRank(walletAddress, leaderboardType);
          setUserRank(rank);
        }
        
        // Build username map efficiently
        const leaderboardWallets = leaderboard.map(e => e.walletAddress.toLowerCase());
        let profileLookupMap = profileLookupMapRef.current;
        const now = Date.now();
        
        // Only rebuild profile lookup map if cache is stale or empty
        if (Object.keys(profileLookupMap).length === 0 || (now - profileLookupMapCacheTimeRef.current) > PROFILE_CACHE_DURATION) {
          try {
            const profilesRef = collection(db, 'profiles');
            const allProfilesSnapshot = await getDocs(profilesRef);
            
            profileLookupMap = {};
            allProfilesSnapshot.forEach((doc) => {
              const data = doc.data();
              const username = data.username || doc.id;
              
              if (data.walletAddress && username) {
                profileLookupMap[data.walletAddress.toLowerCase()] = username;
              }
              if (data._ownerAddress && username) {
                profileLookupMap[data._ownerAddress.toLowerCase()] = username;
              }
            });
            
            // Update cache
            profileLookupMapRef.current = profileLookupMap;
            profileLookupMapCacheTimeRef.current = now;
            console.log(`Cached ${Object.keys(profileLookupMap).length} profile mappings`);
          } catch (profileError) {
            console.error('Error loading profiles:', profileError);
            // Use existing cache if available
            profileLookupMap = profileLookupMapRef.current;
          }
        }
        
        // Build username map from cached lookup (no additional queries needed)
        const usernameMap = {};
        leaderboard.forEach((entry) => {
          const normalizedAddr = entry.walletAddress.toLowerCase();
          let username = profileLookupMap[normalizedAddr];
          
          // Fallback: try temporary address lookup (for test users in localStorage)
          if (!username) {
            username = getUsernameFromTemporaryAddress(normalizedAddr);
          }
          
          // Final fallback: generate username for test users
          if (!username) {
            const shortAddr = normalizedAddr.slice(2, 10);
            username = `testuser_${shortAddr}`;
          }
          
          usernameMap[normalizedAddr] = username;
        });
        
        setLeaderboardUsernames(usernameMap);
      } catch (error) {
        console.error('Error loading leaderboard:', error);
      }
    };

    loadLeaderboard();
  }, [leaderboardType, walletAddress, tagRunVisible]);

  // Drag-to-scroll functionality for header navigation on mobile
  useEffect(() => {
    const headerNav = headerNavRef.current;
    if (!headerNav) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    const handleMouseDown = (e) => {
      // Only enable drag on mobile/touch devices or if content overflows
      if (window.innerWidth <= 768 || headerNav.scrollWidth > headerNav.clientWidth) {
        isDown = true;
        headerNav.style.cursor = 'grabbing';
        startX = e.pageX - headerNav.offsetLeft;
        scrollLeft = headerNav.scrollLeft;
        headerNav.style.userSelect = 'none';
      }
    };

    const handleMouseLeave = () => {
      isDown = false;
      headerNav.style.cursor = 'grab';
      headerNav.style.userSelect = '';
    };

    const handleMouseUp = () => {
      isDown = false;
      headerNav.style.cursor = 'grab';
      headerNav.style.userSelect = '';
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - headerNav.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      headerNav.scrollLeft = scrollLeft - walk;
    };

    // Touch event handlers for mobile
    let touchStartX;
    let touchScrollLeft;

    const handleTouchStart = (e) => {
      if (window.innerWidth <= 768 || headerNav.scrollWidth > headerNav.clientWidth) {
        touchStartX = e.touches[0].pageX - headerNav.offsetLeft;
        touchScrollLeft = headerNav.scrollLeft;
      }
    };

    const handleTouchMove = (e) => {
      if (touchStartX === undefined) return;
      const x = e.touches[0].pageX - headerNav.offsetLeft;
      const walk = (x - touchStartX) * 2;
      headerNav.scrollLeft = touchScrollLeft - walk;
    };

    const handleTouchEnd = () => {
      touchStartX = undefined;
    };

    // Add event listeners
    headerNav.addEventListener('mousedown', handleMouseDown);
    headerNav.addEventListener('mouseleave', handleMouseLeave);
    headerNav.addEventListener('mouseup', handleMouseUp);
    headerNav.addEventListener('mousemove', handleMouseMove);
    headerNav.addEventListener('touchstart', handleTouchStart, { passive: true });
    headerNav.addEventListener('touchmove', handleTouchMove, { passive: true });
    headerNav.addEventListener('touchend', handleTouchEnd);

    return () => {
      headerNav.removeEventListener('mousedown', handleMouseDown);
      headerNav.removeEventListener('mouseleave', handleMouseLeave);
      headerNav.removeEventListener('mouseup', handleMouseUp);
      headerNav.removeEventListener('mousemove', handleMouseMove);
      headerNav.removeEventListener('touchstart', handleTouchStart);
      headerNav.removeEventListener('touchmove', handleTouchMove);
      headerNav.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Drag-to-scroll functionality for footer on mobile
  useEffect(() => {
    const footerBottom = footerBottomRef.current;
    if (!footerBottom) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    const handleMouseDown = (e) => {
      if (window.innerWidth <= 768 || footerBottom.scrollWidth > footerBottom.clientWidth) {
        isDown = true;
        footerBottom.style.cursor = 'grabbing';
        startX = e.pageX - footerBottom.offsetLeft;
        scrollLeft = footerBottom.scrollLeft;
        footerBottom.style.userSelect = 'none';
      }
    };

    const handleMouseLeave = () => {
      isDown = false;
      footerBottom.style.cursor = 'grab';
      footerBottom.style.userSelect = '';
    };

    const handleMouseUp = () => {
      isDown = false;
      footerBottom.style.cursor = 'grab';
      footerBottom.style.userSelect = '';
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - footerBottom.offsetLeft;
      const walk = (x - startX) * 2;
      footerBottom.scrollLeft = scrollLeft - walk;
    };

    // Touch event handlers for mobile
    let touchStartX;
    let touchScrollLeft;

    const handleTouchStart = (e) => {
      if (window.innerWidth <= 768 || footerBottom.scrollWidth > footerBottom.clientWidth) {
        touchStartX = e.touches[0].pageX - footerBottom.offsetLeft;
        touchScrollLeft = footerBottom.scrollLeft;
      }
    };

    const handleTouchMove = (e) => {
      if (touchStartX === undefined) return;
      const x = e.touches[0].pageX - footerBottom.offsetLeft;
      const walk = (x - touchStartX) * 2;
      footerBottom.scrollLeft = touchScrollLeft - walk;
    };

    const handleTouchEnd = () => {
      touchStartX = undefined;
    };

    footerBottom.addEventListener('mousedown', handleMouseDown);
    footerBottom.addEventListener('mouseleave', handleMouseLeave);
    footerBottom.addEventListener('mouseup', handleMouseUp);
    footerBottom.addEventListener('mousemove', handleMouseMove);
    footerBottom.addEventListener('touchstart', handleTouchStart, { passive: true });
    footerBottom.addEventListener('touchmove', handleTouchMove, { passive: true });
    footerBottom.addEventListener('touchend', handleTouchEnd);

    return () => {
      footerBottom.removeEventListener('mousedown', handleMouseDown);
      footerBottom.removeEventListener('mouseleave', handleMouseLeave);
      footerBottom.removeEventListener('mouseup', handleMouseUp);
      footerBottom.removeEventListener('mousemove', handleMouseMove);
      footerBottom.removeEventListener('touchstart', handleTouchStart);
      footerBottom.removeEventListener('touchmove', handleTouchMove);
      footerBottom.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const [aboutSection, setAboutSection] = useState('what-is-peopletag');
  const [donationAddressCopied, setDonationAddressCopied] = useState(false);
  const aboutSectionRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null); // username of current wallet user
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState(null); // Error message when profile loading fails
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [isPopulating, setIsPopulating] = useState(false);
  const [populationResult, setPopulationResult] = useState(null);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [isClearingDatabase, setIsClearingDatabase] = useState(false);
  const [isClearingTestRecords, setIsClearingTestRecords] = useState(false);
  const [notification, setNotification] = useState(null);
  const [encryptionKey, setEncryptionKey] = useState(null); // Store encryption key in memory (not localStorage)
  const encryptionKeyRef = useRef(null); // Store encryption key in ref for use in event handlers
  const [encryptionKeyError, setEncryptionKeyError] = useState(null); // Store error message if key generation fails
  const isGeneratingEncryptionKeyRef = useRef(false); // Track if we're currently generating encryption key to prevent duplicates
  const [key1Revealed, setKey1Revealed] = useState(false); // Track if first encryption key is revealed
  const [key2Revealed, setKey2Revealed] = useState(false); // Track if second encryption key is revealed
  const [key3Revealed, setKey3Revealed] = useState(false); // Track if third encryption key (AES-GCM) is revealed
  const [encryptedProfileData, setEncryptedProfileData] = useState(null); // Store encrypted profile data
  const [encryptionDataExpanded, setEncryptionDataExpanded] = useState(false); // Expand/collapse encryption data section
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false); // Expand/collapse analytics section
  const [viewStats, setViewStats] = useState({ totalViews: 0, uniqueViewers: 0, uniqueLoggedInViewers: 0, uniqueUnloggedViewers: 0, recentViews: [] }); // Profile view statistics
  const [isSavingToBlockchain, setIsSavingToBlockchain] = useState(false); // Track blockchain transaction progress
  const [lastBaseTxHash, setLastBaseTxHash] = useState(null); // Store last Arc (Circle) transaction hash
  const [profileNetwork, setProfileNetwork] = useState(null); // Track which network was used to load current profile (always 'base' now)
  const [dbRecords, setDbRecords] = useState(null); // Store all database records related to user profile
  const [isLoadingDbRecords, setIsLoadingDbRecords] = useState(false); // Loading state for database records
  const [utilitiesExpanded, setUtilitiesExpanded] = useState(false); // Expand/collapse utilities section
  const [utilitiesActiveTab, setUtilitiesActiveTab] = useState('profile'); // Active tab in utilities panel
  const [adminExpanded, setAdminExpanded] = useState(false); // Expand/collapse admin section
  const [recommendationsExpanded, setRecommendationsExpanded] = useState(false); // Expand/collapse recommendations section
  const [recommendations, setRecommendations] = useState([]); // Array of recommended profiles
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false); // Loading state for recommendations
  const [recommendationCriteria, setRecommendationCriteria] = useState('all'); // 'all', 'tags', 'music', 'location', 'age', 'bio', 'social', 'personal'
  const prevSmartRecommendationsVisibleRef = useRef(false); // Track previous visibility state for auto-loading
  const [registrationData, setRegistrationData] = useState({
    name: '',
    email: '',
    username: '',
    tags: [''],
    socials: [''],
    music: [], // Start with empty array - no fields shown until music is added
    birthdate: '',
    city: '',
    cityLat: null,
    cityLng: null
  });
  const [popularTags, setPopularTags] = useState([]); // Array of {tag: string, count: number}
  const [isLoadingPopularTags, setIsLoadingPopularTags] = useState(false);
  const calculatingPopularTagsRef = useRef(false);
  const popularTagsTimeoutRef = useRef(null);
  const [selectingCity, setSelectingCity] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const cityInputRef = useRef(null);
  const citySuggestionsTimeoutRef = useRef(null);
  const [isLoadingCitySuggestions, setIsLoadingCitySuggestions] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [isSavingSocials, setIsSavingSocials] = useState(false);
  const [isSavingMusic, setIsSavingMusic] = useState(false);
  const [isSavingBirthDate, setIsSavingBirthDate] = useState(false);
  const [isSavingCity, setIsSavingCity] = useState(false);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);
  const [isAvatarLoading, setIsAvatarLoading] = useState(false);
  // Published state for public visibility
  const [publishedTags, setPublishedTags] = useState([]); // Array of published tag indices or tag values
  const [publishedGroups, setPublishedGroups] = useState([]); // Array of published group names
  const [publishedSocials, setPublishedSocials] = useState([]); // Array of published social indices
  const [publishedMusic, setPublishedMusic] = useState([]); // Array of published music indices
  const [publishedBirthDate, setPublishedBirthDate] = useState(false);
  const [publishedCity, setPublishedCity] = useState(false);
  const [publishedWallet, setPublishedWallet] = useState(false);
  const [publishedEmail, setPublishedEmail] = useState(false);
  const [publishedName, setPublishedName] = useState(false);
  const [publishedBio, setPublishedBio] = useState(false);
  const [publishedPreferredContact, setPublishedPreferredContact] = useState(false);
  const [publishedRelationship, setPublishedRelationship] = useState(false);
  const [publishedLookingFor, setPublishedLookingFor] = useState(false);
  const [publishedLanguages, setPublishedLanguages] = useState(false);
  const [publishedEducation, setPublishedEducation] = useState(false);
  const [publishedOccupation, setPublishedOccupation] = useState(false);
  const [isUpdatingPublish, setIsUpdatingPublish] = useState(false);
  const [showPublishedNotification, setShowPublishedNotification] = useState(null); // Track which element shows notification: {key: string, isPublished: boolean}
  const [showPublishAllConfirm, setShowPublishAllConfirm] = useState(false); // Confirmation modal for publishing everything
  const [isPublishingAll, setIsPublishingAll] = useState(false); // Loading state for publish all
  const [showUnpublishAllConfirm, setShowUnpublishAllConfirm] = useState(false); // Confirmation modal for unpublishing everything
  const [isUnpublishingAll, setIsUnpublishingAll] = useState(false); // Loading state for unpublish all
  const [isLoadingProfileFromIPFS, setIsLoadingProfileFromIPFS] = useState(false); // Loading indicator when loading profile from IPFS
  const [showProfileCompletenessWizard, setShowProfileCompletenessWizard] = useState(false); // Profile completeness wizard modal
  const [wizardFormData, setWizardFormData] = useState({}); // Form data for profile completeness wizard
  const [aiBioSuggestions, setAiBioSuggestions] = useState([]); // AI-generated bio suggestions
  const [isLoadingBioSuggestions, setIsLoadingBioSuggestions] = useState(false); // Loading state for AI bio suggestions
  const [isSavingWizard, setIsSavingWizard] = useState(false); // Loading state for saving wizard changes
  const tagInputRefs = useRef({});
  const socialInputRefs = useRef({});
  const musicInputRefs = useRef({});
  const musicSearchDebounceRef = useRef(null);
  const avatarInputRef = useRef(null);
  const avatarImgRef = useRef(null);
  const photosInputRef = useRef(null);
  const [isPhotoGalleryOpen, setIsPhotoGalleryOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [editingTags, setEditingTags] = useState([]);
  const [isEditingSocials, setIsEditingSocials] = useState(false);
  const [editingSocials, setEditingSocials] = useState([]);
  const [isEditingMusic, setIsEditingMusic] = useState(false);
  const [editingMusic, setEditingMusic] = useState([]);
  const [musicSearchQuery, setMusicSearchQuery] = useState('');
  const [musicSearchResults, setMusicSearchResults] = useState([]);
  const [isSearchingMusic, setIsSearchingMusic] = useState(false);
  const [isEditingBirthDate, setIsEditingBirthDate] = useState(false);
  const [editingBirthDate, setEditingBirthDate] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [editingBio, setEditingBio] = useState('');
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [isEditingPreferredContact, setIsEditingPreferredContact] = useState(false);
  const [editingPreferredContact, setEditingPreferredContact] = useState('');
  const [isSavingPreferredContact, setIsSavingPreferredContact] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editingEmail, setEditingEmail] = useState('');
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  // Email verification (code-based)
  const [emailVerificationOpen, setEmailVerificationOpen] = useState(false);
  const [emailVerificationPurpose, setEmailVerificationPurpose] = useState('register'); // 'register' | 'change'
  const [emailToVerify, setEmailToVerify] = useState('');
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [isSendingEmailCode, setIsSendingEmailCode] = useState(false);
  const [isVerifyingEmailCode, setIsVerifyingEmailCode] = useState(false);
  const [emailVerificationError, setEmailVerificationError] = useState('');
  const pendingEmailVerificationActionRef = useRef(null);
  const lastEmailVerificationRef = useRef({ emailLower: null, purpose: null, verifiedAtMs: 0 });
  const [isEditingRelationship, setIsEditingRelationship] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState('');
  const [isSavingRelationship, setIsSavingRelationship] = useState(false);
  const [isEditingLookingFor, setIsEditingLookingFor] = useState(false);
  const [editingLookingFor, setEditingLookingFor] = useState('');
  const [isSavingLookingFor, setIsSavingLookingFor] = useState(false);
  const [isEditingLanguages, setIsEditingLanguages] = useState(false);
  const [editingLanguages, setEditingLanguages] = useState('');
  const [isSavingLanguages, setIsSavingLanguages] = useState(false);
  const [isEditingEducation, setIsEditingEducation] = useState(false);
  const [editingEducation, setEditingEducation] = useState('');
  const [isSavingEducation, setIsSavingEducation] = useState(false);
  const [isEditingOccupation, setIsEditingOccupation] = useState(false);
  const [editingOccupation, setEditingOccupation] = useState('');
  const [isSavingOccupation, setIsSavingOccupation] = useState(false);
  const [editingCity, setEditingCity] = useState('');
  const [editingCityLat, setEditingCityLat] = useState(null);
  const [editingCityLng, setEditingCityLng] = useState(null);
  const [editingCitySelecting, setEditingCitySelecting] = useState(false);
  // Deferred save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const pendingProfileRef = useRef(null);
  const deferProfileSavesRef = useRef(true);
  // Store original values when editing starts (for cancel functionality)
  const originalBioRef = useRef(null);
  const originalNameRef = useRef(null);
  const originalEmailRef = useRef(null);
  const originalPreferredContactRef = useRef(null);
  // Store expected bio for IPFS verification after save
  const expectedBioForVerificationRef = useRef(null);

  // Change detection helpers moved below currentProfile initialization later
  const lastSavedProfileRef = useRef(null);
  const [recentlyChanged, setRecentlyChanged] = useState({
    tags: new Set(),
    socials: new Set(),
    music: new Set(),
    name: false,
    bio: false,
    birthDate: false,
    city: false,
    preferredContact: false,
    email: false,
    relationship: false,
    lookingFor: false,
    languages: false,
    education: false,
    occupation: false,
  });

  const getApiUrl = () => {
    return process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
  };

  const getApiWalletAuthHeaders = () => {
    const sig = getSavedWalletSignature?.();
    const address = walletAddress || getSavedWalletAddress?.();
    if (!sig?.signature || !sig?.message || !address) return {};
    return {
      'x-wallet-address': address,
      'x-signature': sig.signature,
      // Browsers disallow newline characters in header values. The signed message contains newlines,
      // so we send it base64-encoded and decode server-side.
      'x-message-b64': btoa(unescape(encodeURIComponent(sig.message)))
    };
  };

  const isEmailRecentlyVerified = (email, purpose) => {
    const emailLower = (email || '').trim().toLowerCase();
    if (!emailLower) return false;
    const cache = lastEmailVerificationRef.current || {};
    const maxAgeMs = 15 * 60 * 1000; // 15 minutes
    return (
      cache.emailLower === emailLower &&
      cache.purpose === purpose &&
      cache.verifiedAtMs &&
      (Date.now() - cache.verifiedAtMs) < maxAgeMs
    );
  };

  const openEmailVerificationModal = async ({ email, purpose, onVerified }) => {
    setEmailVerificationError('');
    setEmailVerificationCode('');
    setEmailToVerify(email);
    setEmailVerificationPurpose(purpose || 'register');
    pendingEmailVerificationActionRef.current = onVerified || null;
    setEmailVerificationOpen(true);

    // Send code immediately on open
    setIsSendingEmailCode(true);
    try {
      const apiUrl = getApiUrl();
      const resp = await fetch(`${apiUrl}/api/v1/email-verification/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getApiWalletAuthHeaders()
        },
        body: JSON.stringify({ email, purpose })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || `Failed to send code (${resp.status})`);
      }
    } catch (e) {
      console.error('Failed to send email verification code:', e);
      setEmailVerificationError(e.message || 'Failed to send verification code');
    } finally {
      setIsSendingEmailCode(false);
    }
  };

  const verifyEmailCode = async () => {
    if (isVerifyingEmailCode) return;
    setEmailVerificationError('');
    setIsVerifyingEmailCode(true);
    try {
      const apiUrl = getApiUrl();
      const resp = await fetch(`${apiUrl}/api/v1/email-verification/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getApiWalletAuthHeaders()
        },
        body: JSON.stringify({
          email: emailToVerify,
          code: emailVerificationCode,
          purpose: emailVerificationPurpose
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || `Verification failed (${resp.status})`);
      }
      if (!data.verified) {
        throw new Error('Verification failed');
      }
      lastEmailVerificationRef.current = {
        emailLower: (emailToVerify || '').trim().toLowerCase(),
        purpose: emailVerificationPurpose,
        verifiedAtMs: Date.now()
      };
      setEmailVerificationOpen(false);
      const cb = pendingEmailVerificationActionRef.current;
      pendingEmailVerificationActionRef.current = null;
      if (cb) {
        await cb();
      }
    } catch (e) {
      console.error('Email verification failed:', e);
      setEmailVerificationError(e.message || 'Invalid code');
    } finally {
      setIsVerifyingEmailCode(false);
    }
  };

  // Check if wallet address is admin
  const isAdminWallet = (address) => {
    if (!address) return false;
    const adminWallets = [
      '0xbd2862ee9935575cbe5a94a9b8e5e1643059db4a'
    ];
    return adminWallets.includes(address.toLowerCase());
  };

  // Get location coordinates for a city (use first location from that city)
  const getCityCoordinates = (cityName) => {
    if (!cityName) return null;
    const cityLocation = worldLocations.find(loc => loc.city === cityName);
    return cityLocation ? { lat: cityLocation.lat, lng: cityLocation.lng } : null;
  };
  
  // Fetch city suggestions from Nominatim API
  const fetchCitySuggestions = async (query) => {
    if (!query || query.trim().length < 2) {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
      return;
    }

    // Clear previous timeout
    if (citySuggestionsTimeoutRef.current) {
      clearTimeout(citySuggestionsTimeoutRef.current);
    }

    // Debounce the API call
    citySuggestionsTimeoutRef.current = setTimeout(async () => {
      try {
        setIsLoadingCitySuggestions(true);
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1&accept-language=en&featuretype=city`,
          {
            headers: {
              'Accept-Language': 'en',
              'User-Agent': 'PeopleTag App'
            }
          }
        );
        const data = await response.json();
        
        // Extract city names and coordinates
        const suggestions = data.map(item => ({
          name: item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || item.display_name.split(',')[0],
          displayName: item.display_name,
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon)
        })).filter((item, index, self) => 
          // Remove duplicates based on name
          index === self.findIndex(t => t.name.toLowerCase() === item.name.toLowerCase())
        );

        setCitySuggestions(suggestions);
        setShowCitySuggestions(suggestions.length > 0);
      } catch (error) {
        console.error('Error fetching city suggestions:', error);
        setCitySuggestions([]);
        setShowCitySuggestions(false);
      } finally {
        setIsLoadingCitySuggestions(false);
      }
    }, 300); // 300ms debounce
  };
  
  // Shorten wallet address to half length (show first 6 and last 4 characters)
  const shortenWalletAddress = (address) => {
    if (!address || address.length < 10) return address;
    const start = address.substring(0, 6); // 0x + 4 chars
    const end = address.substring(address.length - 4); // last 4 chars
    return `${start}...${end}`;
  };
  
  // Copy text to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedVisible(true);
      setTimeout(() => {
        setCopiedVisible(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Auto-search music when query changes (debounced)
  useEffect(() => {
    // Clear previous timeout
    if (musicSearchDebounceRef.current) {
      clearTimeout(musicSearchDebounceRef.current);
    }

    // Don't search if query is empty or too short
    if (!musicSearchQuery || musicSearchQuery.trim().length < 2) {
      setMusicSearchResults([]);
      setIsSearchingMusic(false);
      return;
    }

    // Set searching state immediately for UI feedback
    setIsSearchingMusic(true);

    // Debounce the search by 400ms
    musicSearchDebounceRef.current = setTimeout(() => {
      searchMusic(musicSearchQuery);
    }, 400);

    // Cleanup on unmount or when query changes
    return () => {
      if (musicSearchDebounceRef.current) {
        clearTimeout(musicSearchDebounceRef.current);
      }
    };
  }, [musicSearchQuery]);

  // Search for music using iTunes Search API - free, no API key required
  const searchMusic = async (query) => {
    if (!query || !query.trim()) {
      setMusicSearchResults([]);
      return;
    }

    setIsSearchingMusic(true);
    try {
      // Use iTunes Search API for real music search
      const response = await fetch(
        `https://itunes.apple.com/search?term=${encodeURIComponent(query)}&media=music&entity=song&limit=10`
      );
      
      if (!response.ok) {
        throw new Error('Music search failed');
      }
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const results = data.results.map(track => ({
          title: track.trackName,
          artist: track.artistName,
          album: track.collectionName,
          artwork: track.artworkUrl100?.replace('100x100', '60x60'), // Smaller artwork for list
          artworkLarge: track.artworkUrl100,
          previewUrl: track.previewUrl
        }));
        setMusicSearchResults(results);
      } else {
        // No results found, offer manual entry option
        setMusicSearchResults([{
          title: query,
          artist: 'Add as custom entry',
          isManual: true
        }]);
      }
    } catch (error) {
      console.error('Error searching music:', error);
      // Fallback to manual entry on error
      const parts = query.split(' - ');
      setMusicSearchResults([{
        title: parts[0] || query,
        artist: parts[1] || 'Add as custom entry',
        isManual: true
      }]);
    } finally {
      setIsSearchingMusic(false);
    }
  };

  // Load profile recommendations
  const loadRecommendations = async (criteria = null) => {
    // Always show loading state when loading recommendations
    setIsLoadingRecommendations(true);
    
    // Wait for data to be ready if not available yet
    if (!currentProfile || allProfiles.length === 0) {
      // Wait longer for data to be ready - data might still be loading from Firestore
      let attempts = 0;
      const maxAttempts = 50; // Wait up to 5 seconds (50 * 100ms)
      
      while ((!currentProfile || allProfiles.length === 0) && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      // If still no data after waiting, keep loading state visible and return
      // The useEffect will trigger loadRecommendations again when data becomes available
      if (!currentProfile || allProfiles.length === 0) {
        setRecommendations([]);
        // Keep loading state visible - don't clear it
        // The useEffect will detect when data becomes available and reload
        return;
      }
    }

    try {
      const criteriaToUse = criteria || recommendationCriteria;
      let recs = [];

      // Ensure we include all profiles including test users for recommendations
      // allProfiles already includes both production and test profiles from getAllProfiles()
      const profilesForRecommendations = allProfiles; // Includes test users

      if (criteriaToUse === 'all') {
        recs = utils.getProfileRecommendations(currentProfile, profilesForRecommendations, {
          limit: 20,
          minScore: 30,
          excludeUsernames: [currentProfile.username],
          maxDistance: 100
        });
      } else {
        recs = utils.getRecommendationsByCriteria(currentProfile, profilesForRecommendations, criteriaToUse, {
          limit: 20,
          minScore: 30,
          excludeUsernames: [currentProfile.username],
          maxDistance: 100
        });
      }

      setRecommendations(recs);
    } catch (error) {
      console.error('Error loading recommendations:', error);
      setRecommendations([]);
    } finally {
      setIsLoadingRecommendations(false);
    }
  };

  // Commit all pending profile changes
  const commitPendingProfileChanges = async () => {
    console.log('💾 commitPendingProfileChanges called', {
      hasPendingData: !!pendingProfileRef.current,
      pendingDataKeys: pendingProfileRef.current ? Object.keys(pendingProfileRef.current) : [],
      hasUnsavedChanges,
      isSavingProfile
    });
    
    // Check if wallet is connected
    if (!walletAddress) {
      setNotification({
        message: 'Wallet not connected. Please connect your wallet first.',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 5000);
      return;
    }
    
    // Check if encryption key is available
    if (!encryptionKey && !encryptionKeyRef.current) {
      setNotification({
        message: 'Encryption key not available. Please reconnect your wallet.',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 5000);
      return;
    }
    
    // Ensure we have data to save - merge pending changes with currentProfile to ensure all fields are present
    let dataToSave;
    if (!pendingProfileRef.current) {
      console.warn('⚠️ No pending profile data to save. Current profile will be used instead.');
      // If there's no pending data but hasUnsavedChanges is true, use currentProfile
      if (!currentProfile) {
        console.error('❌ No pending data and no current profile to save');
        setNotification({
          message: 'No changes to save. Please make some changes first.',
          type: 'error'
        });
        return;
      }
      // Use currentProfile as the data to save
      dataToSave = safeCloneProfile(currentProfile);
    } else {
      // Merge pending changes with currentProfile to ensure all required fields are present
      const base = currentProfile ? safeCloneProfile(currentProfile) : {};
      dataToSave = { ...base, ...pendingProfileRef.current };
      console.log('🔄 Merged pending changes with current profile', {
        baseKeys: Object.keys(base),
        pendingKeys: Object.keys(pendingProfileRef.current),
        mergedKeys: Object.keys(dataToSave)
      });
    }
    
    // Merge any active editing states (bio, name, email, preferredContact) into dataToSave
    // This ensures that if user edited these fields but clicked "Save All" instead of individual "Save",
    // the edited values are included in the save operation
    
    // Merge editingBio if user was actively editing OR if editingBio has a non-empty value
    // IMPORTANT: Don't merge empty editingBio - it would overwrite bio from wizard/pendingProfileRef
    // Only merge if: (1) user is actively editing bio, OR (2) editingBio has actual content
    if (editingBio !== undefined && editingBio !== null) {
      const trimmedBio = editingBio.trim();
      const currentBio = (dataToSave.bio || '').trim();
      // Only merge if user is actively editing OR editingBio has a non-empty value
      // This prevents empty editingBio from overwriting bio that came from wizard/pendingProfileRef
      const shouldMergeBio = isEditingBio || (trimmedBio !== '' && trimmedBio !== currentBio);
      if (shouldMergeBio) {
        dataToSave.bio = trimmedBio || null;
        console.log('📝 Merging editingBio into dataToSave:', { 
          bio: trimmedBio || null, 
          reason: isEditingBio ? 'isEditingBio=true' : 'editingBio has value and differs',
          editingBioLength: editingBio.length,
          currentBioLength: currentBio.length
        });
      }
    }
    if (isEditingName && editingName !== undefined) {
      dataToSave.name = editingName.trim() || null;
      console.log('📝 Merging editingName into dataToSave:', { name: editingName.trim() || null });
    }
    if (isEditingEmail && editingEmail !== undefined) {
      // Email is required - preserve existing email if new one is empty
      const trimmedEmail = editingEmail.trim();
      if (trimmedEmail) {
        dataToSave.email = trimmedEmail;
      } else {
        // Don't set email to null - preserve existing email
        // Email cannot be deleted, only changed
        console.log('📝 Email field is empty, preserving existing email');
      }
      console.log('📝 Merging editingEmail into dataToSave:', { email: trimmedEmail ? trimmedEmail.substring(0, 10) + '...' : 'preserved' });
    }
    if (isEditingPreferredContact && editingPreferredContact !== undefined) {
      dataToSave.preferredContactMethod = editingPreferredContact.trim() || null;
      console.log('📝 Merging editingPreferredContact into dataToSave:', { preferredContactMethod: editingPreferredContact.trim() || null });
    }
    
    console.log('📦 Data to save:', {
      hasUsername: !!dataToSave.username,
      hasTags: !!dataToSave.tagsList,
      hasSocials: !!dataToSave.socialLinks,
      keys: Object.keys(dataToSave)
    });
    
    // Store expected bio for IPFS verification after save
    expectedBioForVerificationRef.current = dataToSave.bio;
    
    // Don't clear pendingProfileRef.current yet - only clear it on success
    try {
      // Snapshot previous saved state
      const prevSaved = lastSavedProfileRef.current ? safeCloneProfile(lastSavedProfileRef.current) : null;
      const currentSnapshot = currentProfile ? safeCloneProfile(currentProfile) : null;
      setIsSavingProfile(true);
      console.log('🔄 Starting save process...');
      
      // Temporarily disable deferral for this commit
      const prevDefer = deferProfileSavesRef.current;
      deferProfileSavesRef.current = false;
      console.log('🔓 Deferral disabled, calling encryptAndSaveProfile...');
      
      const result = await encryptAndSaveProfile(dataToSave, false);
      deferProfileSavesRef.current = prevDefer;
      console.log('✅ encryptAndSaveProfile completed');
      
      // Only clear pending changes and update state if save was successful
      // encryptAndSaveProfile will throw if blockchain transaction fails, so if we get here, it succeeded
      if (!result) {
        console.error('❌ Save operation returned no result');
        throw new Error('Save operation returned no result');
      }
      
      // Verify that we have both encrypted data and IPFS hash (blockchain transaction completed)
      if (!result.encryptedData || !result.ipfsHash) {
        console.warn('⚠️ Save operation completed but missing required data:', result);
        const missingParts = [];
        if (!result.encryptedData) missingParts.push('encrypted data');
        if (!result.ipfsHash) missingParts.push('IPFS hash');
        
        let errorMsg = `Save operation incomplete - missing ${missingParts.join(' and ')}.`;
        if (!result.ipfsHash) {
          errorMsg += ' This usually means the IPFS upload failed. Check if Pinata API credentials are configured.';
        }
        throw new Error(errorMsg);
      }
      
      console.log('✅ Save operation successful! Clearing pending changes...');
      pendingProfileRef.current = null;
      setEncryptedProfileData(result.encryptedData);
      setHasUnsavedChanges(false);
      
      // Update currentProfile with saved data to ensure UI reflects saved values
      if (result?.ipfsHash) {
        setCurrentProfile(prev => ({
          ...prev,
          ...dataToSave,
          ipfsHash: result.ipfsHash
        }));
      } else {
        // Even if ipfsHash update failed, update profile with saved data
        setCurrentProfile(prev => ({
          ...prev,
          ...dataToSave
        }));
      }
      
      // Clear any active editing states since changes are now saved
      if (isEditingBio) {
        setIsEditingBio(false);
        setEditingBio('');
      }
      if (isEditingName) {
        setIsEditingName(false);
        setEditingName('');
      }
      if (isEditingEmail) {
        setIsEditingEmail(false);
        setEditingEmail('');
      }
      if (isEditingPreferredContact) {
        setIsEditingPreferredContact(false);
        setEditingPreferredContact('');
      }

      // After successful save, update last saved snapshot and clear all change indicators/outlines
      if (currentSnapshot) {
        const newSaved = safeCloneProfile(dataToSave);
        lastSavedProfileRef.current = newSaved;
        setRecentlyChanged({
          tags: new Set(),
          socials: new Set(),
          music: new Set(),
          name: false,
          bio: false,
          birthDate: false,
          city: false
        });
      }
    } catch (e) {
      console.error('❌ Failed to commit pending profile changes:', e);
      console.error('Error details:', {
        message: e.message,
        code: e.code,
        isBlockchainRejection: e.isBlockchainRejection,
        stack: e.stack
      });
      
      // Restore pending changes so user can try again
      // Since we didn't clear pendingProfileRef.current until success, it should still have the original data
      // But to be safe, ensure it's set to the data we were trying to save
      if (!pendingProfileRef.current) {
        pendingProfileRef.current = dataToSave;
      } else {
        // Merge to preserve any additional changes that might have been made
        pendingProfileRef.current = { ...pendingProfileRef.current, ...dataToSave };
      }
      // Keep unsaved indicator so user can retry
      setHasUnsavedChanges(true);
      
      // Show error notification to user
      let errorMessage = e.message || 'Unknown error';
      if (e.isRpcError) {
        // RPC errors need special handling - they prevent MetaMask from showing the sign popup
        errorMessage = 'RPC endpoint error: MetaMask cannot show the sign popup. Please check your network connection and MetaMask settings, then try again in a few moments.';
      } else if (e.isBlockchainRejection) {
        errorMessage = 'Transaction rejected. Changes not saved.';
      } else {
        errorMessage = `Failed to save: ${errorMessage}`;
      }
      
      setNotification({
        message: errorMessage,
        type: 'error'
      });
      
      // Keep RPC error notifications visible longer so user can read the instructions
      const notificationTimeout = e.isRpcError ? 10000 : 6000;
      setTimeout(() => setNotification(null), notificationTimeout);
    } finally {
      setIsSavingProfile(false);
      console.log('🏁 commitPendingProfileChanges finished');
    }
  };

  // Helper function to clear all change tracking state
  const clearChangeTrackingState = () => {
    setRecentlyChanged({
      tags: new Set(),
      socials: new Set(),
      music: new Set(),
      name: false,
      bio: false,
      birthDate: false,
      city: false,
      preferredContact: false,
      email: false,
      relationship: false,
      lookingFor: false,
      languages: false,
      education: false,
      occupation: false,
    });
    pendingProfileRef.current = null;
    setHasUnsavedChanges(false);
    lastSavedProfileRef.current = null;
  };

  // Reject all pending profile changes and reload profile
  const rejectPendingProfileChanges = async () => {
    if (!walletAddress) return;
    
    // Clear all change tracking state
    clearChangeTrackingState();
    
    // Reload profile from database/IPFS
    try {
      await loadUserProfile(walletAddress, true); // skipUnifiedAuth=true since we're already authenticated
    } catch (error) {
      console.error('Failed to reload profile after rejecting changes:', error);
    }
  };
  
  // Helper function to get photos array from profile (with backward compatibility)
  const getPhotosFromProfile = (profile) => {
    if (!profile) return [];
    // If photos array exists, use it
    if (Array.isArray(profile.photos) && profile.photos.length > 0) {
      return profile.photos;
    }
    // Backward compatibility: if avatar exists but no photos array, convert it
    if (profile.avatar) {
      return [profile.avatar];
    }
    return [];
  };

  // Helper function to get avatar (first photo or old avatar field)
  const getAvatarFromProfile = (profile) => {
    if (!profile) return null;
    const photos = getPhotosFromProfile(profile);
    if (photos.length > 0) {
      return photos[0];
    }
    // Backward compatibility
    return profile.avatar || null;
  };

  // Helper function to replace avatar/photos data with placeholders
  const replaceAvatarDataWithPlaceholders = (data) => {
    if (!data || typeof data !== 'object') return data;
    
    // Create a deep copy to avoid mutating the original
    const sanitized = Array.isArray(data) ? [...data] : { ...data };
    
    for (const key in sanitized) {
      if (key === 'avatar' && sanitized[key] != null && typeof sanitized[key] === 'string') {
        // Replace avatar with placeholder
        sanitized[key] = '[avatar1_picture_code]';
      } else if (key === 'photos' && Array.isArray(sanitized[key])) {
        // Replace each photo in the array with numbered placeholders
        sanitized[key] = sanitized[key].map((photo, index) => {
          if (photo != null && typeof photo === 'string') {
            return `[avatar${index + 1}_picture_code]`;
          }
          return photo;
        });
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        // Recursively process nested objects
        sanitized[key] = replaceAvatarDataWithPlaceholders(sanitized[key]);
      }
    }
    
    return sanitized;
  };

  // Helper function to extract only encryptable profile fields (exclude internal fields)
  const getEncryptableProfileData = (profile) => {
    if (!profile) return null;
    
    // First, replace any avatar/photos data with placeholders (in case they're nested)
    const profileWithPlaceholders = replaceAvatarDataWithPlaceholders(profile);
    
    // Extract only the fields that should be encrypted
    // Exclude: encryptedData (old encrypted data), marker (map marker reference), avatar (picture data), photos (picture data), and other internal fields
    const {
      encryptedData,    // Exclude - this is the old encrypted data
      marker,           // Exclude - this is a map marker reference
      avatar,           // Exclude - avatar picture (base64 string, can be large, stored separately)
      photos,           // Exclude - photos array (base64 strings, can be large, stored separately)
      ...encryptableFields
    } = profileWithPlaceholders;
    
    // Log the size of data being encrypted for debugging
    const dataSize = JSON.stringify(encryptableFields).length;
    console.log(`Encrypting profile data: ${dataSize} bytes (excluded encryptedData, marker, avatar, and photos)`);
    
    // Return only the fields that should be encrypted
    return encryptableFields;
  };

  const ensureEncryptionKey = () => {
    if (encryptionKeyRef.current && encryptionKeyRef.current.length === 32) {
      return encryptionKeyRef.current;
    }
    try {
      const savedSignatureData = getSavedWalletSignature();
      if (savedSignatureData?.signature) {
        const key = generateEncryptionKeyFromSignature(savedSignatureData.signature);
        setEncryptionKey(key);
        encryptionKeyRef.current = key;
        return key;
      }
    } catch (error) {
      console.warn('Failed to ensure encryption key from signature:', error);
    }
    return null;
  };

  // Helper function to encrypt and save profile data
  const encryptAndSaveProfile = async (profileData, isRegistration = false) => {
    if (!profileData) {
      console.error('No profile data provided to encryptAndSaveProfile');
      return null;
    }
    
    // Defer saves in My Profile edits until user presses Save
    if (!isRegistration && deferProfileSavesRef.current) {
      console.log('⏸️ Deferring save - merging with pending changes', {
        hasPendingData: !!pendingProfileRef.current,
        hasCurrentProfile: !!currentProfile,
        profileDataKeys: Object.keys(profileData || {})
      });
      
      // Start with currentProfile as base to ensure we have all required fields
      const base = currentProfile ? safeCloneProfile(currentProfile) : {};
      const existing = pendingProfileRef.current || {};
      // Merge: base (currentProfile) -> existing (previous pending) -> profileData (latest edits)
      pendingProfileRef.current = { ...base, ...existing, ...profileData };
      
      console.log('📝 Pending changes updated', {
        pendingKeys: Object.keys(pendingProfileRef.current),
        hasUsername: !!pendingProfileRef.current.username
      });
      
      setHasUnsavedChanges(true);
      // Return a minimal object so callers don't break
      return { deferred: true };
    }
    
    // Create a clean copy without internal fields (like encryptedData, marker, etc.)
    const cleanProfileData = getEncryptableProfileData(profileData);
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'App.js:1358',message:'cleanProfileData after getEncryptableProfileData',data:{hasBio:cleanProfileData?.bio!==undefined,bioValue:cleanProfileData?.bio,bioLength:cleanProfileData?.bio?.length,profileDataBio:profileData?.bio,profileDataBioLength:profileData?.bio?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    
    // Debug: Log what we're encrypting
    console.log('🔐 Encrypting profile data:', {
      tagsList: cleanProfileData?.tagsList,
      socialLinks: cleanProfileData?.socialLinks,
      musicList: cleanProfileData?.musicList,
      bio: cleanProfileData?.bio ? (cleanProfileData.bio.substring(0, 50) + (cleanProfileData.bio.length > 50 ? '...' : '')) : null,
      bioLength: cleanProfileData?.bio?.length || 0,
      name: cleanProfileData?.name,
      birthDate: cleanProfileData?.birthDate,
      age: cleanProfileData?.age,
      email: cleanProfileData?.email ? cleanProfileData.email.substring(0, 10) + '...' : null,
      city: cleanProfileData?.city,
      location: cleanProfileData?.location,
      tagsListLength: cleanProfileData?.tagsList?.length || 0,
      socialLinksLength: cleanProfileData?.socialLinks?.length || 0,
      musicListLength: cleanProfileData?.musicList?.length || 0
    });
    
    if (!cleanProfileData) {
      console.error('Failed to extract encryptable profile data');
      // Still save without encryption
      await saveUserProfile(walletAddress, profileData);
      return null;
    }
    
    // Ensure encryption key is available
    let activeEncryptionKey = encryptionKeyRef.current || encryptionKey;
    if (!activeEncryptionKey) {
      activeEncryptionKey = ensureEncryptionKey();
    }

    // Encrypt profile data if encryption key is available
    let encryptedData = null;
    let ipfsHash = null;
    
    if (activeEncryptionKey) {
      try {
        // Encrypt only the clean profile data (without encryptedData field)
        encryptedData = encryptProfile(cleanProfileData, activeEncryptionKey);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'App.js:1398',message:'After encryptProfile',data:{hasEncryptedData:!!encryptedData,cleanProfileDataBio:cleanProfileData?.bio,cleanProfileDataBioLength:cleanProfileData?.bio?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
        // #endregion
        
        // Log the size of encrypted data for debugging
        const encryptedSize = encryptedData.ciphertext.length + encryptedData.nonce.length;
        console.log(`Encrypted profile data: ${encryptedSize} bytes (ciphertext: ${encryptedData.ciphertext.length}, nonce: ${encryptedData.nonce.length})`);
        
        setEncryptedProfileData(encryptedData);
        console.log('Profile data re-encrypted successfully');
        
        // Upload to Pinata IPFS (REQUIRED - profile data is stored only in IPFS, not Firestore)
        // Always upload new version - don't reuse old hash
        let blockchainRejectionError = null;
        try {
          console.log('🚀 Starting upload to Pinata...');
          ipfsHash = await uploadToPinata(encryptedData);
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'App.js:1412',message:'After uploadToPinata',data:{ipfsHash:ipfsHash,hasIpfsHash:!!ipfsHash,cleanProfileDataBio:cleanProfileData?.bio},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
          // #endregion
          console.log('✅ Successfully uploaded to Pinata IPFS! CID:', ipfsHash);
          
          // Automatically save CID to Arc (Circle) blockchain
          // forceSave=true ensures the transaction always happens when user explicitly saves
          if (ipfsHash && walletAddress) {
            try {
              console.log('🔗 Saving CID to Arc (Circle) blockchain...');
              // Show notification that user should check MetaMask
              setNotification({ 
                message: 'Please check MetaMask and approve the transaction to complete saving.', 
                type: 'info' 
              });
              await saveCIDToBlockchain(ipfsHash, true); // forceSave=true to always attempt transaction
              console.log('✅ CID saved to Arc (Circle) blockchain');
              // Update profile network to Arc (Circle) after successful save
              setProfileNetwork('base');
              localStorage.setItem(`profile_network_${walletAddress.toLowerCase()}`, 'base');
            } catch (blockchainError) {
              // Check if user rejected the transaction - if so, mark it and re-throw
              const errorMessage = blockchainError.message || 'Unknown error occurred';
              const errorCode = blockchainError.code;
              const isUserRejection = errorCode === 4001 || 
                                     errorMessage.toLowerCase().includes('denied') || 
                                     errorMessage.toLowerCase().includes('rejected') ||
                                     errorMessage.toLowerCase().includes('user denied');
              
              // Check if it's an RPC error - these prevent MetaMask from showing the sign popup
              const isRpcError = errorCode === -32002 || 
                                errorMessage.includes('RPC endpoint') ||
                                errorMessage.includes('could not coalesce error');
              
              if (isRpcError) {
                // RPC errors mean MetaMask can't even show the sign popup
                console.error('❌ RPC endpoint error - MetaMask sign popup cannot appear:', blockchainError);
                blockchainRejectionError = blockchainError;
                blockchainRejectionError.isBlockchainRejection = true;
                blockchainRejectionError.isRpcError = true;
                throw blockchainRejectionError;
              } else if (isUserRejection) {
                // User rejected - mark the error and re-throw so the save operation is considered failed
                // This allows commitPendingProfileChanges to restore pending changes
                console.error('⚠️ User rejected blockchain transaction - changes will be preserved');
                blockchainRejectionError = blockchainError;
                // Mark it so outer catch blocks can identify it
                blockchainRejectionError.isBlockchainRejection = true;
                throw blockchainRejectionError;
              } else {
                // Other blockchain errors - also fail the save operation
                // The CID must be on the blockchain for the save to be considered successful
                console.error('❌ Failed to save CID to blockchain - save operation will fail:', blockchainError);
                blockchainRejectionError = blockchainError;
                blockchainRejectionError.isBlockchainRejection = true;
                throw blockchainRejectionError;
              }
            }
          }
        } catch (pinataError) {
          // If this is a blockchain rejection, re-throw it directly without wrapping
          if (pinataError.isBlockchainRejection || blockchainRejectionError) {
            throw pinataError;
          }
          console.error('❌ Failed to upload to Pinata:', pinataError);
          
          // Preserve the original error message if it contains useful information (like missing credentials)
          const errorMessage = pinataError.message || 'Unknown error';
          if (errorMessage.includes('Pinata API credentials') || errorMessage.includes('REACT_APP_PINATA')) {
            throw new Error(`Cannot save profile: ${errorMessage}. Please configure Pinata API credentials in your environment variables.`);
          } else {
            throw new Error(`Failed to upload profile to IPFS: ${errorMessage}. Profile cannot be saved without IPFS hash.`);
          }
        }
        
        // Create profile object with metadata for Firestore (public data only, no IPFS hash)
        // Public data (socialLinks, birthDate, age) is stored in Firestore so other users can see it
        // Private data (email, additionalPersonalData, etc.) is stored encrypted in IPFS
        // IPFS hash is stored on Arc (Circle) blockchain, not in Firestore
        // During registration, save all data from profileData (nothing is published yet)
        // After registration, only save published elements
        // Get photos array (with backward compatibility)
        const photos = getPhotosFromProfile(profileData);
        const avatar = getAvatarFromProfile(profileData); // For backward compatibility
        
        const profileToSave = {
          username: cleanProfileData.username,
          walletAddress: walletAddress ? walletAddress.toLowerCase() : null, // Always save walletAddress for profile lookup
          avatar: avatar, // Keep for backward compatibility
          photos: photos, // New photos array
          name: isRegistration
            ? (cleanProfileData.name || profileData.name || null)
            : (publishedName ? (cleanProfileData.name || profileData.name || null) : null),
          city: cleanProfileData.city || profileData.city || null,
          location: cleanProfileData.location && cleanProfileData.location.lat != null && cleanProfileData.location.lng != null
            ? {
                lat: cleanProfileData.location.lat,
                lng: cleanProfileData.location.lng,
                city: cleanProfileData.location.city || cleanProfileData.city || null
              }
            : (profileData.location && profileData.location.lat != null && profileData.location.lng != null
                ? {
                    lat: profileData.location.lat,
                    lng: profileData.location.lng,
                    city: profileData.location.city || profileData.city || null
                  }
                : null),
          // During registration, save all data from profileData (nothing is published yet)
          // After registration, only save published elements to Firestore (for public visibility)
          // But IPFS always contains ALL tags/socials (both published and unpublished)
          tagsList: isRegistration 
            ? (Array.isArray(cleanProfileData.tagsList) ? cleanProfileData.tagsList : [])
            : (Array.isArray(publishedTags) && publishedTags.length > 0 ? publishedTags : []),
          socialLinks: isRegistration
            ? (Array.isArray(cleanProfileData.socialLinks) ? cleanProfileData.socialLinks : [])
            : (Array.isArray(publishedSocials) && publishedSocials.length > 0 ? publishedSocials : []),
          musicList: isRegistration
            ? (Array.isArray(cleanProfileData.musicList) ? cleanProfileData.musicList : [])
            : (Array.isArray(publishedMusic) && publishedMusic.length > 0 ? publishedMusic : []),
          birthDate: isRegistration
            ? (cleanProfileData.birthDate || profileData.birthDate || null)
            : (publishedBirthDate ? (cleanProfileData.birthDate || profileData.birthDate || null) : null),
          age: isRegistration
            ? (cleanProfileData.age != null ? cleanProfileData.age : (profileData.age != null ? profileData.age : null))
            : (publishedBirthDate ? (cleanProfileData.age != null ? cleanProfileData.age : (profileData.age != null ? profileData.age : null)) : null),
          city: isRegistration
            ? (cleanProfileData.city || profileData.city || null)
            : (publishedCity ? (cleanProfileData.city || profileData.city || null) : null),
          location: isRegistration
            ? (cleanProfileData.location || profileData.location || null)
            : (publishedCity && cleanProfileData.location ? cleanProfileData.location : (publishedCity && profileData.location ? profileData.location : null)),
          // Bio: Always save to IPFS (in cleanProfileData), but only save to Firestore if published
          // During registration, save all data (nothing is published yet)
          // After registration, only save published bio to Firestore (for public visibility)
          // But IPFS always contains ALL bio data (both published and unpublished)
          bio: isRegistration
            ? (cleanProfileData.bio || profileData.bio || null)
            : (publishedBio ? (cleanProfileData.bio || profileData.bio || null) : null),
          // Save email during registration
          email: isRegistration
            ? (cleanProfileData.email || profileData.email || null)
            : null
        };
        
        // Store encrypted data in state for UI display
        setEncryptedProfileData(encryptedData);
        
        // At this point, blockchain save succeeded - Firestore is just for caching/metadata
        // If Firestore fails, the profile is still saved on blockchain and will work on refresh
        console.log('💾 Saving profile metadata to Firestore (IPFS hash stored on blockchain)');
        
        try {
          // Save only metadata to Firestore (profile data is in IPFS, hash is on blockchain)
          // IMPORTANT: Also store IPFS hash in Firestore as fallback in case blockchain query fails
          const profileToSaveWithHash = {
            ...profileToSave,
            ipfsHash: ipfsHash // Store hash in Firestore as fallback
          };
          await saveUserProfile(walletAddress, profileToSaveWithHash);
          console.log('✅ Profile metadata saved (IPFS hash on blockchain:', ipfsHash, ', also stored in Firestore as fallback)');
          
          // Clear the "profile deleted" marker now that a new profile is successfully created
          // This allows the profile to be loaded from blockchain/IPFS again
          const normalizedWalletAddress = walletAddress.toLowerCase();
          localStorage.removeItem(`profileDeleted_${normalizedWalletAddress}`);
        } catch (firestoreError) {
          // Firestore save failed, but blockchain save succeeded
          // Log warning but don't fail - the profile IS saved on blockchain
          console.warn('⚠️ Firestore metadata save failed, but profile is saved on blockchain:', firestoreError.message);
          console.log('ℹ️ Profile will be available on refresh via blockchain CID');
          // Don't throw - continue with successful return since blockchain save worked
        }
      } catch (error) {
        // Check if this is a blockchain rejection - if so, don't save anything and re-throw
        if (error.isBlockchainRejection || 
            (error.code === 4001) || 
            (error.message && (
              error.message.toLowerCase().includes('denied') || 
              error.message.toLowerCase().includes('rejected') ||
              error.message.toLowerCase().includes('user denied')
            ))) {
          console.error('⚠️ Blockchain transaction rejected - not saving profile changes');
          // Re-throw so commitPendingProfileChanges can handle it properly
          throw error;
        }
        
        console.error('Error encrypting/saving profile data:', error);
        // This catch is for IPFS upload or blockchain save failures - these are critical
        throw error;
      }
    } else {
      throw new Error('Encryption key unavailable. Please reconnect your wallet.');
    }
    
    // Log the result for debugging
    console.log('encryptAndSaveProfile result:', { 
      hasEncryptedData: !!encryptedData,
      ipfsHash: ipfsHash
    });
    
    return { encryptedData, ipfsHash };
  };

  // Note: We use getCIDFromArc for reading CID from Arc (Circle) network (not getCIDFromBlockchain which reads from Arbitrum)

  // Save CID to Arc (Circle) blockchain via smart contract
  // Wrapper that integrates with React state and uses @peopletag/protocol library
  // All users save to Arc (Circle) blockchain
  const saveCIDToBlockchain = async (cid, forceSave = false) => {
    // Check if CID has changed (avoid duplicate transactions)
    // But if forceSave is true, always attempt the transaction (e.g., when user explicitly saves)
    const lastSavedCID = localStorage.getItem(`lastSavedCID_${walletAddress}`);
    if (!forceSave && lastSavedCID === cid) {
      console.log('CID unchanged, skipping blockchain transaction');
      return;
    }

    // Verify MetaMask is available before attempting transaction
    if (!isMetaMaskInstalled()) {
      throw new Error('MetaMask is not installed. Please install MetaMask to save to blockchain.');
    }

    if (!window.ethereum) {
      throw new Error('MetaMask provider is not available. Please ensure MetaMask is installed and unlocked.');
    }

    // Verify wallet is connected
    if (!walletAddress) {
      throw new Error('Wallet not connected. Please connect your wallet first.');
    }

    // Verify we have the correct account selected
    // If MetaMask is locked, eth_accounts returns empty array, so we need to request accounts to unlock
    try {
      let accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts.length === 0) {
        // MetaMask is likely locked - request accounts to prompt unlock
        console.log('🔓 MetaMask appears to be locked. Requesting account access...');
        setNotification({ 
          message: 'Please unlock MetaMask to continue.', 
          type: 'info' 
        });
        accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts.length === 0) {
          throw new Error('MetaMask is locked. Please unlock MetaMask and try again.');
        }
      }
      const currentAddress = accounts[0].toLowerCase();
      if (currentAddress !== walletAddress.toLowerCase()) {
        throw new Error(`Wallet mismatch. Expected ${walletAddress}, but MetaMask has ${currentAddress}. Please reconnect your wallet.`);
      }
    } catch (accountError) {
      console.error('Error verifying MetaMask account:', accountError);
      // If user rejected the unlock request, provide a helpful message
      if (accountError.code === 4001 || accountError.message?.includes('rejected') || accountError.message?.includes('denied')) {
        throw new Error('Please unlock MetaMask to save your changes.');
      }
      throw new Error(`Failed to verify MetaMask connection: ${accountError.message || 'Please ensure MetaMask is unlocked and try again.'}`);
    }

    try {
      setIsSavingToBlockchain(true);
      
      console.log('🔗 Initiating blockchain transaction for CID:', cid);
      console.log('📝 MetaMask will show prompts for:');
      console.log('   1. Network switch to Arc (Circle) (if needed)');
      console.log('   2. Transaction approval');
      console.log('   Please approve both prompts in MetaMask.');
      
      // Note: saveCIDToArc handles network switching internally, so we don't need to do it here
      // Show notification to user about what to expect
      setNotification({ 
        message: 'Please check MetaMask: You may need to approve network switch to Arc (Circle) and transaction.',
        type: 'info' 
      });
      
      // Set a timeout to detect if MetaMask prompt doesn't appear
      const transactionTimeout = setTimeout(() => {
        console.warn('⚠️ Blockchain transaction is taking longer than expected.');
        setNotification({ 
          message: 'Transaction is taking longer than expected. Please wait a bit or check MetaMask for pending prompts.', 
          type: 'info' 
        });
      }, 15000); // 15 second warning

      let txHash;
      try {
        // Use saveCIDToArc to save to Arc (Circle) network
        console.log('🔵 Saving to Arc (Circle) network...');
        setNotification({ message: 'Saving changes to blockchain...', type: 'info' });
        txHash = await saveCIDToArc(cid);
        console.log('✅ Transaction confirmed. TxHash:', txHash);
        
        // saveCIDToArc already waits for transaction confirmation
        // NO NEED to verify IPFS - we just uploaded it! Pinata returned the CID, data is already there
        // NO NEED to poll blockchain - saveCIDToArc already confirmed the tx
        
        console.log('✅ Successfully saved to Arc (Circle) network. TxHash:', txHash);
        setNotification({ 
          message: 'Changes saved successfully!', 
          type: 'success' 
        });
        clearTimeout(transactionTimeout);
        setTimeout(() => setNotification(null), 4000);
        
        // Update localStorage to track successful save
        setLastBaseTxHash(txHash);
        localStorage.setItem(`lastBaseTxHash_${walletAddress}`, txHash);
        localStorage.setItem(`lastSavedCID_${walletAddress}`, cid);
        localStorage.setItem(`profile_network_${walletAddress.toLowerCase()}`, 'base');
        
        // Clear the verification ref (no longer used but clean up)
        expectedBioForVerificationRef.current = null;
      } catch (libError) {
        clearTimeout(transactionTimeout);
        console.error('❌ Error from saveCIDToArc:', libError);
        console.error('Error details:', {
          message: libError.message,
          code: libError.code,
          stack: libError.stack
        });
        
        // Provide clearer error messages
        const errorMessage = libError.message || 'Unknown error';
        const errorCode = libError.code;
        
        // Check if MetaMask might be locked - if transaction was rejected and no accounts are available
        // This could mean MetaMask is locked and needs to be unlocked first
        if (errorCode === 4001 || errorMessage.includes('user rejected') || errorMessage.includes('denied')) {
          // Check if MetaMask is locked by checking accounts
          try {
            const accounts = await window.ethereum.request({ method: 'eth_accounts' });
            if (accounts.length === 0) {
              // MetaMask is locked - prompt user to unlock
              console.log('🔓 MetaMask appears to be locked. Requesting account access...');
              setNotification({ 
                message: 'MetaMask is locked. Please unlock MetaMask to continue.', 
                type: 'info' 
              });
              try {
                await window.ethereum.request({ method: 'eth_requestAccounts' });
                // If unlock succeeded, retry the transaction
                console.log('✅ MetaMask unlocked. Retrying transaction...');
                setNotification({ 
                  message: 'MetaMask unlocked. Retrying transaction...', 
                  type: 'info' 
                });
                // Retry the transaction - use saveCIDToArc to explicitly save to Arc (Circle)
                console.log('🔵 Retrying save to Arc (Circle) network...');
                setNotification({ message: 'Retrying save to Arc (Circle) network...', type: 'info' });
                txHash = await saveCIDToArc(cid);
                console.log('✅ Transaction submitted after retry. TxHash:', txHash);
                
                // Wait for confirmation and verify both blockchain AND IPFS
                setNotification({ message: 'Transaction submitted. Waiting for confirmation...', type: 'info' });
                
                let blockchainConfirmed = false;
                let ipfsConfirmed = false;
                let verificationAttempts = 0;
                const maxVerificationAttempts = 30;
                
                // Get encryption key for IPFS verification
                const activeEncryptionKey = encryptionKeyRef.current || encryptionKey;
                
                while ((!blockchainConfirmed || !ipfsConfirmed) && verificationAttempts < maxVerificationAttempts) {
                  await new Promise(resolve => setTimeout(resolve, 1000));
                  
                  // Step 1: Verify CID is on-chain
                  if (!blockchainConfirmed) {
                    try {
                      const { getCIDFromArc } = await import('./services/migrationService');
                      const onChainCID = await getCIDFromArc(walletAddress);
                      
                      if (onChainCID === cid) {
                        blockchainConfirmed = true;
                        console.log('✅ CID verified on-chain after retry');
                      }
                    } catch (verifyError) {
                      console.warn('Verification attempt failed:', verifyError);
                    }
                  }
                  
                  // Step 2: Verify IPFS data is accessible (only if blockchain confirmed)
                  if (blockchainConfirmed && !ipfsConfirmed && activeEncryptionKey) {
                    try {
                      const encryptedData = await fetchFromIPFS(cid);
                      const decryptedProfile = decryptProfile(encryptedData, activeEncryptionKey);
                      
                      // Verify the bio is in the decrypted data
                      // Use the expected bio stored before save started
                      const expectedBio = expectedBioForVerificationRef.current;
                      const ipfsBio = decryptedProfile.bio;
                      
                      // Handle null/undefined/empty string comparisons
                      const ipfsBioTrimmed = ipfsBio ? ipfsBio.trim() : '';
                      const expectedBioTrimmed = expectedBio ? expectedBio.trim() : '';
                      
                      if (ipfsBio === expectedBio || 
                          (ipfsBioTrimmed === expectedBioTrimmed) ||
                          (!ipfsBio && !expectedBio) ||
                          (ipfsBioTrimmed === '' && expectedBioTrimmed === '')) {
                        ipfsConfirmed = true;
                        console.log('✅ IPFS data verified after retry');
                        // Clear the ref after successful verification
                        expectedBioForVerificationRef.current = null;
                        break;
                      }
                    } catch (ipfsError) {
                      console.log('IPFS verification attempt failed (data may not be ready yet):', ipfsError.message);
                    }
                  }
                  
                  verificationAttempts++;
                  
                  if (verificationAttempts % 3 === 0) {
                    const status = blockchainConfirmed 
                      ? (ipfsConfirmed ? 'Verified' : 'Verifying IPFS data...') 
                      : 'Waiting for blockchain confirmation...';
                    setNotification({ 
                      message: `${status} (${verificationAttempts}s)`, 
                      type: 'info' 
                    });
                  }
                }
                
                // If retry succeeded, skip the rest of the error handling
                if (txHash) {
                  clearTimeout(transactionTimeout);
                  setLastBaseTxHash(txHash);
                  localStorage.setItem(`lastBaseTxHash_${walletAddress}`, txHash);
                  localStorage.setItem(`lastSavedCID_${walletAddress}`, cid);
                  localStorage.setItem(`profile_network_${walletAddress.toLowerCase()}`, 'base');
                  
                  if (!blockchainConfirmed) {
                    console.warn('⚠️ Could not verify CID on-chain within timeout after retry');
                    setNotification({ 
                      message: 'Transaction submitted. It may take a few seconds to appear. Please wait before refreshing.', 
                      type: 'warning' 
                    });
                    setTimeout(() => setNotification(null), 8000);
                  } else if (!ipfsConfirmed) {
                    console.warn('⚠️ CID is on-chain but IPFS data not yet accessible after retry');
                    setNotification({ 
                      message: 'Transaction confirmed. Waiting for IPFS propagation... Please wait 10-20 seconds before refreshing.', 
                      type: 'warning' 
                    });
                    setTimeout(() => setNotification(null), 10000);
                  } else {
                    console.log('✅ Blockchain and IPFS verified after unlock. TxHash:', txHash);
                    setNotification({ 
                      message: 'Changes saved successfully! Blockchain and IPFS verified. Safe to refresh.', 
                      type: 'success' 
                    });
                    setTimeout(() => setNotification(null), 5000);
                  }
                  return txHash;
                }
              } catch (unlockError) {
                // User rejected unlock or other error
                if (unlockError.code === 4001 || unlockError.message?.includes('rejected')) {
                  throw new Error('Please unlock MetaMask to save your changes.');
                }
                // If retry failed for other reasons, throw a new error with context
                throw new Error(`Transaction failed after unlocking MetaMask: ${unlockError.message || 'Unknown error'}. Please try again.`);
              }
            } else {
              // Accounts are available, so it was a genuine transaction rejection
              throw new Error('Transaction rejected. Please approve the transaction in MetaMask to save your changes.');
            }
          } catch (checkError) {
            // If checking accounts fails, assume it was a genuine rejection
            console.warn('Could not check MetaMask lock status:', checkError);
            throw new Error('Transaction rejected. Please approve the transaction in MetaMask to save your changes.');
          }
        }
        
        // Handle RPC endpoint errors specifically - these prevent MetaMask from showing the sign popup
        if (errorCode === -32002 || 
            errorMessage.includes('RPC endpoint returned too many errors') ||
            errorMessage.includes('could not coalesce error') ||
            errorMessage.includes('RPC endpoint')) {
          const rpcErrorMsg = 'MetaMask RPC endpoint is having issues. This prevents the transaction from starting. Please:\n' +
            '1. Wait 1-2 minutes and try again\n' +
            '2. Check your internet connection\n' +
            '3. In MetaMask, go to Settings > Networks > Arc (Circle) > Edit and try a different RPC URL\n' +
            '4. Or restart MetaMask';
          throw new Error(rpcErrorMsg);
        } else if (errorCode === -32002 && errorMessage.includes('already pending')) {
          throw new Error('Transaction request already pending. Please check MetaMask and approve the pending transaction.');
        } else if (errorCode === 4902) {
          throw new Error('Arc (Circle) network not found. Please add Arc (Circle) network in MetaMask and try again.');
        } else if (errorMessage.includes('Network switch timeout') || errorMessage.includes('switch to Arc')) {
          throw new Error('Please switch to Arc (Circle) network in MetaMask and try again.');
        } else if (errorMessage.includes('MetaMask is not installed')) {
          throw new Error('MetaMask is not installed. Please install MetaMask to save to blockchain.');
        } else if (errorMessage.includes('could not coalesce error') || errorMessage.includes('RPC endpoint')) {
          throw new Error('RPC endpoint error. Please check your MetaMask network settings or try again in a few moments.');
        } else {
          throw new Error(`Blockchain transaction failed: ${errorMessage}. Please check MetaMask and try again.`);
        }
      }

      if (!txHash) {
        throw new Error('Blockchain transaction failed - no transaction hash returned. Please try again.');
      }

      // Save transaction hash and CID to localStorage
      setLastBaseTxHash(txHash);
      localStorage.setItem(`lastBaseTxHash_${walletAddress}`, txHash);
      localStorage.setItem(`lastSavedCID_${walletAddress}`, cid);
      // Also store network preference
      localStorage.setItem(`profile_network_${walletAddress.toLowerCase()}`, 'base');
      
      console.log('✅ Blockchain transaction completed successfully. TxHash:', txHash);
      return txHash;
    } catch (error) {
      console.error('❌ Blockchain transaction failed:', error);
      
      // Re-throw with improved error message if not already improved
      if (error.message && (
        error.message.includes('Transaction rejected') ||
        error.message.includes('Transaction request already pending') ||
        error.message.includes('Blockchain transaction failed')
      )) {
        throw error;
      }
      
      // Provide a generic error message
      throw new Error(`Failed to save to blockchain: ${error.message || 'Unknown error'}. Please check MetaMask and try again.`);
    } finally {
      setIsSavingToBlockchain(false);
    }
  };

  // Compress and resize image for avatar upload
  const compressImage = (file, maxDimension = 500, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxDimension) {
              height = (height * maxDimension) / width;
              width = maxDimension;
            }
        } else {
            if (height > maxDimension) {
              width = (width * maxDimension) / height;
              height = maxDimension;
            }
          }
          
          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const base64String = canvas.toDataURL('image/jpeg', quality);
          resolve(base64String);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };
  
  // Start with no profile selected - only show when wallet is connected
  const [currentProfile, setCurrentProfile] = useState(null);
  const [selectedGbixItem, setSelectedGbixItem] = useState(null); // Selected GBIX item to display
  const [profileGroups, setProfileGroups] = useState([]);
  const [viewerGroups, setViewerGroups] = useState([]); // Groups of the current viewer (for match detection)
  const [viewedProfileWalletAddress, setViewedProfileWalletAddress] = useState(null); // Track the wallet address of the profile being viewed (for messaging)
  const currentProfileWalletAddressRef = useRef(null); // Track the wallet address used to load current profile
  const trackedViewsRef = useRef(new Set()); // Track which profile views have already been tracked to prevent duplicates

  // Fetch groups for the current profile
  useEffect(() => {
    const fetchProfileGroups = async () => {
      if (!currentProfile) {
        setProfileGroups([]);
        return;
      }

      try {
        // Determine wallet address for the profile
        let profileWalletAddress = null;
        const isOwnProfile = isMyProfile();
        
        if (isOwnProfile) {
          // For own profile, use the connected wallet address
          profileWalletAddress = walletAddress;
        } else {
          // For other profiles, use viewedProfileWalletAddress or try to get from username
          profileWalletAddress = viewedProfileWalletAddress;
          if (!profileWalletAddress && currentProfile.username) {
            try {
              profileWalletAddress = await getWalletFromUsername(currentProfile.username);
            } catch (error) {
              console.warn('Could not get wallet address for profile groups:', error);
            }
          }
        }

        if (profileWalletAddress) {
          const groups = await getUserGroups(profileWalletAddress);
          // Extract group names for display
          const groupNames = groups.map(group => group.name || group.groupId || '');
          
          // Load publishedGroups from Firestore
          try {
            const firestoreMetadata = await getUserProfileByWallet(profileWalletAddress, null);
            const firestoreGroups = firestoreMetadata?.groups || [];
            
            if (isOwnProfile) {
              // For own profile, set all groups and published groups
              setProfileGroups(groupNames);
              const publishedGroupsList = groupNames.filter(group => firestoreGroups.includes(group));
              setPublishedGroups(publishedGroupsList);
            } else {
              // For other users, only show published groups
              const publishedGroupsList = groupNames.filter(group => firestoreGroups.includes(group));
              setProfileGroups(publishedGroupsList);
              // Store all groups separately if needed, but for display we only show published ones
            }
          } catch (error) {
            console.warn('Could not load published groups from Firestore:', error);
            if (isOwnProfile) {
              setProfileGroups(groupNames);
              setPublishedGroups([]);
            } else {
              setProfileGroups([]);
            }
          }
        } else {
          setProfileGroups([]);
          setPublishedGroups([]);
        }
      } catch (error) {
        console.error('Error fetching profile groups:', error);
        setProfileGroups([]);
      }
    };

    fetchProfileGroups();
  }, [currentProfile, currentUser, walletAddress, viewedProfileWalletAddress]);

  // Fetch viewer's groups for match detection when viewing other profiles
  useEffect(() => {
    const fetchViewerGroups = async () => {
      // Only fetch if viewing someone else's profile and we have a wallet address
      const isOwnProfile = isMyProfile();
      if (isOwnProfile || !walletAddress || !currentProfile) {
        setViewerGroups([]);
        return;
      }

      try {
        const groups = await getUserGroups(walletAddress);
        const groupNames = groups.map(group => group.name || group.groupId || '');
        setViewerGroups(groupNames);
      } catch (error) {
        console.error('Error fetching viewer groups:', error);
        setViewerGroups([]);
      }
    };

    fetchViewerGroups();
  }, [currentProfile, currentUser, walletAddress]);

  // Calculate and select GBIX item based on current user's profile tags
  useEffect(() => {
    if (!currentProfile || !walletAddress) {
      setSelectedGbixItem(null);
      return;
    }

    // Only show GBIX for the current user's own profile
    const isOwnProfile = currentProfile.walletAddress && 
                         currentProfile.walletAddress.toLowerCase() === walletAddress.toLowerCase();
    
    if (!isOwnProfile) {
      setSelectedGbixItem(null);
      return;
    }

    // Get user's tags from profile
    const userTags = currentProfile.tagsList || [];
    
    if (userTags.length === 0) {
      setSelectedGbixItem(null);
      return;
    }

    // Select random GBIX item based on tags
    const gbixItem = selectRandomGbixItem(userTags);
    setSelectedGbixItem(gbixItem);
  }, [currentProfile, walletAddress]);
  
  // Sync URL with state - handle route changes (placed after currentProfile declaration)
  useEffect(() => {
    const pathname = getNormalizedPathname();
    
    // Handle /myprofile route
    if (pathname === '/myprofile') {
      setAboutVisible(false);
      setMessagesVisible(false);
      setGroupsVisible(false);
      setBugVisible(false);
      setActivityFeedVisible(false);
      // Always show profile section if wallet is connected (profile will load asynchronously)
      if (walletAddress) {
        // REVISED APPROACH: Set flag immediately that we're loading "My Profile"
        // This prevents "other profile" flash even if currentUser isn't set yet
        isLoadingMyProfileRef.current = true;
        
        const normalizedWallet = walletAddress.toLowerCase();
        
        // CRITICAL FIX: Only clear profile if it's definitely wrong - don't clear if it matches
        // This prevents race conditions where we clear a valid profile before new one loads
        const currentProfileMatches = currentProfile && 
          currentProfile.walletAddress && 
          currentProfile.walletAddress.toLowerCase() === normalizedWallet;
        
        if (currentProfile && !currentProfileMatches) {
          console.log('🔄 [My Profile Route] Clearing incorrect profile state before loading own profile');
          setCurrentProfile(null);
          setCurrentUser(null);
          setViewedProfileWalletAddress(null);
        }
        
        // If we have currentProfile and it matches, set currentUser immediately and keep profile visible
        if (currentProfileMatches) {
          if (currentProfile.username) {
            setCurrentUser(currentProfile.username);
            console.log('✅ [My Profile Route] Set currentUser from currentProfile:', currentProfile.username);
          }
          // Keep the existing profile visible while we reload fresh data
          setProfileVisible(true);
          setIsLoadingProfileFromIPFS(true); // Show loading indicator while refreshing
        }
        // If not set yet, try to set from allProfiles (synchronous check)
        else if (allProfiles && allProfiles.length > 0 && !currentUser) {
          const myProfile = allProfiles.find(p => 
            p.walletAddress && p.walletAddress.toLowerCase() === normalizedWallet
          );
          if (myProfile && myProfile.username) {
            setCurrentUser(myProfile.username);
            console.log('✅ [My Profile Route] Set currentUser from allProfiles:', myProfile.username);
          }
        }
        
        // Only set loading state if we don't have a matching profile already
        if (!currentProfileMatches) {
          setIsLoadingProfileFromIPFS(true);
        }
        setShowRegistrationForm(false);
        setProfileLoadError(null);
        setProfileVisible(true);
        (async () => {
          try {
            console.log('🔄 [My Profile] Loading user profile using loadUserProfile...');
            await loadUserProfile(walletAddress, true); // skipUnifiedAuth=true - will set currentUser correctly
            // Clear flag after loading completes
            isLoadingMyProfileRef.current = false;
          } catch (error) {
            console.error('❌ [My Profile] Error loading profile:', error);
            setIsLoadingProfileFromIPFS(false);
            isLoadingMyProfileRef.current = false;
            setProfileLoadError('Failed to load profile. Please try again.');
          }
        })();
      } else {
        isLoadingMyProfileRef.current = false;
        // If no wallet, clear any profile state
        setCurrentProfile(null);
        setCurrentUser(null);
        setViewedProfileWalletAddress(null);
      }
    }
    // Handle /groups route
    else if (pathname === '/groups') {
      setAboutVisible(false);
      setProfileVisible(false); // Always keep profile hidden on /groups route
      setMessagesVisible(false);
      setBugVisible(false);
      // Only show groups if wallet is connected
      if (walletAddress) {
        setGroupsVisible(true);
      }
    }
    // Handle /tagrun route
    else if (pathname === '/tagrun') {
      setAboutVisible(false);
      setProfileVisible(false);
      setGroupsVisible(false);
      setMessagesVisible(false);
      setActivityFeedVisible(false);
      setBugVisible(false);
      // Only show TagRun if wallet is connected
      if (walletAddress) {
        setTagRunVisible(true);
      }
    }
    // Handle /about routes (including nested routes)
    else if (pathname.startsWith('/about')) {
      setAboutVisible(true);
      setProfileVisible(false);
      setMessagesVisible(false);
      setGroupsVisible(false);
      setBugVisible(false);
      setTagRunVisible(false);
      
      // Extract section from pathname
      let section = pathname.replace('/about/', '').replace('/about', '') || 'what-is-peopletag';
      
      // Redirect roadmap to what-is-peopletag since roadmap is disabled
      if (section === 'roadmap') {
        mobileNavigate('/about/what-is-peopletag', { replace: true });
        section = 'what-is-peopletag';
      }
      
      // Map URL-friendly names to state values
      const sectionMap = {
        'what-is-peopletag': 'what-is-peopletag',
        'whatispeopletag': 'what-is-peopletag',
        'how-it-works': 'how-it-works',
        'howitworks': 'how-it-works',
        'api': 'api',
        'public-testnet': 'public-testnet',
        'publictestnet': 'public-testnet',
        'whitepaper': 'whitepaper',
        'roadmap': 'what-is-peopletag', // Map roadmap to what-is-peopletag
        'team': 'team',
        'contact-us': 'contact-us',
        'contactus': 'contact-us'
      };
      const mappedSection = sectionMap[section] || 'what-is-peopletag';
      setAboutSection(mappedSection);
    }
    // Handle /profile/:username route
    else if (pathname.startsWith('/profile/')) {
      const username = pathname.replace('/profile/', '');
      if (username) {
        setAboutVisible(false);
        setGroupsVisible(false);
        setMessagesVisible(false);
        setBugVisible(false);
        // Always show profile section when loading profile from URL
        // Keep it visible even if currentProfile is null during loading
        setProfileVisible(true);
        // The actual profile loading will happen in a separate effect or handler
      }
    }
    // Handle /messages route
    else if (pathname.startsWith('/messages')) {
      setAboutVisible(false);
      setProfileVisible(false); // Always keep profile hidden on /messages route
      setGroupsVisible(false);
      setBugVisible(false);
      // Only show messages if wallet is connected
      if (walletAddress) {
        setMessagesVisible(true);
        // Extract username from /messages/:username pattern
        // CRITICAL: URL always contains username, never displayName or wallet address
        if (pathname.startsWith('/messages/') && pathname !== '/messages') {
          const username = decodeURIComponent(pathname.replace('/messages/', ''));
          if (username) {
            // Convert username to wallet address for the wallet prop
            // But pass username to initialRecipient (Messaging component will handle lookup)
            (async () => {
              try {
                const recipientWallet = await getWalletFromUsername(username);
                // Always pass username to initialRecipient (never wallet address or displayName)
                setSelectedMessageRecipient(username);
                // Also set wallet address separately if found
                if (recipientWallet) {
                  setSelectedMessageRecipientWallet(recipientWallet);
                } else {
                  setSelectedMessageRecipientWallet(null);
                  console.warn(`Wallet not found for username "${username}"`);
                }
              } catch (error) {
                console.error('Error converting username to wallet address:', error);
                // On error, still pass username (Messaging component will handle lookup)
                setSelectedMessageRecipient(username);
                setSelectedMessageRecipientWallet(null);
              }
            })();
          }
        } else if (pathname === '/messages') {
          // If just /messages without username, clear selected recipient AND wallet
          // This prevents stale state from opening wrong conversations with icebreakers
          if (selectedMessageRecipient) {
            setSelectedMessageRecipient(null);
          }
          if (selectedMessageRecipientWallet) {
            setSelectedMessageRecipientWallet(null);
          }
        }
      }
    }
    // Handle /search routes
    else if (pathname.startsWith('/search')) {
      setAboutVisible(false);
      setGroupsVisible(false);
      setMessagesVisible(false);
      setBugVisible(false);
      // Show Search section
      setLeftSectionVisible(true);
      // Show My profile if wallet is connected
      if (walletAddress && (currentProfile || showRegistrationForm)) {
        setProfileVisible(true);
      }
      // Handle /search/:query route - extract query and set searchQuery
      if (pathname.startsWith('/search/')) {
        const query = decodeURIComponent(pathname.replace('/search/', ''));
        if (query && query !== searchQuery) {
          isSettingSearchFromUrlRef.current = true;
          setSearchQuery(query);
        }
      } else {
        // /search route without query - clear search
        if (searchQuery !== '') {
          isSettingSearchFromUrlRef.current = true;
          setSearchQuery('');
        }
      }
    }
    // Handle root route (/)
    else if (pathname === '/') {
      setAboutVisible(false);
      setTagRunVisible(false);
      // If wallet is connected, load user's own profile as "My Profile"
      if (walletAddress) {
        // CRITICAL FIX: Set flag IMMEDIATELY that we're loading "My Profile"
        // This prevents "other profile" flash even if currentUser isn't set yet
        isLoadingMyProfileRef.current = true;
        
        const normalizedWallet = walletAddress.toLowerCase();
        
        // CRITICAL FIX: Clear any incorrect profile state FIRST before loading
        // If currentProfile exists but doesn't match our wallet, clear it to prevent showing wrong profile
        if (currentProfile && currentProfile.walletAddress && currentProfile.walletAddress.toLowerCase() !== normalizedWallet) {
          console.log('🔄 [Root Route] Clearing incorrect profile state before loading own profile');
          setCurrentProfile(null);
          setCurrentUser(null);
          setViewedProfileWalletAddress(null);
        }
        
        // FIX: Try to set currentUser IMMEDIATELY from multiple sources (synchronous)
        // 1. Check if currentProfile matches
        if (currentProfile && currentProfile.walletAddress && currentProfile.walletAddress.toLowerCase() === normalizedWallet) {
          if (currentProfile.username) {
            setCurrentUser(currentProfile.username);
            console.log('✅ [Root Route] Set currentUser from currentProfile:', currentProfile.username);
          }
        }
        // 2. If not set yet, check allProfiles (synchronous check)
        else if (allProfiles && allProfiles.length > 0) {
          const myProfile = allProfiles.find(p => 
            p.walletAddress && p.walletAddress.toLowerCase() === normalizedWallet
          );
          if (myProfile && myProfile.username) {
            setCurrentUser(myProfile.username);
            console.log('✅ [Root Route] Set currentUser from allProfiles:', myProfile.username);
          }
        }
        
        setShowRegistrationForm(false);
        setProfileLoadError(null);
        setProfileVisible(true);
        (async () => {
          try {
            setIsLoadingProfileFromIPFS(true);
            await loadUserProfile(walletAddress, true); // skipUnifiedAuth=true - will set currentUser correctly
            // Clear flag after loading completes
            isLoadingMyProfileRef.current = false;
          } catch (error) {
            console.error('❌ [Root Route] Error loading profile:', error);
            setIsLoadingProfileFromIPFS(false);
            isLoadingMyProfileRef.current = false;
          }
        })();
      } else {
        isLoadingMyProfileRef.current = false;
        // If no wallet, clear any profile state
        setCurrentProfile(null);
        setCurrentUser(null);
        setViewedProfileWalletAddress(null);
      }
    }
  }, [getNormalizedPathname, walletAddress]); // allProfiles is accessed directly in the effect, not needed in deps. showRegistrationForm removed to prevent infinite loop
  
  // Enforce route preservation - prevent profile from opening on /groups, /messages, /tagrun, /about, or /activityfeed routes
  useEffect(() => {
    const pathname = getNormalizedPathname();
    const isOnAboutPage = pathname.startsWith('/about');
    const isOnGroupsPage = pathname === '/groups';
    const isOnMessagesPage = pathname.startsWith('/messages');
    const isOnTagRunPage = pathname === '/tagrun';
    const isOnActivityFeedPage = pathname === '/activityfeed';
    
    if ((isOnAboutPage || isOnGroupsPage || isOnMessagesPage || isOnTagRunPage || isOnActivityFeedPage) && profileVisible) {
      // If we're on a route that should preserve state, and profile became visible, hide it
      setProfileVisible(false);
      if (isOnGroupsPage) {
        setGroupsVisible(true);
      } else if (isOnMessagesPage) {
        setMessagesVisible(true);
      } else if (isOnTagRunPage) {
        setTagRunVisible(true);
      } else if (isOnAboutPage) {
        setAboutVisible(true);
      } else if (isOnActivityFeedPage) {
        setActivityFeedVisible(true);
      }
    }
  }, [getNormalizedPathname, profileVisible]);
  
  // Handle /activityfeed route - show activity feed and hide profile
  useEffect(() => {
    const pathname = getNormalizedPathname();
    if (pathname === '/activityfeed') {
      if (walletAddress) {
        setActivityFeedVisible(true);
        setProfileVisible(false);
        setGroupsVisible(false);
        setMessagesVisible(false);
        setBugVisible(false);
        setTagRunVisible(false);
        setAboutVisible(false);
      }
    }
  }, [getNormalizedPathname, walletAddress]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [allProfiles, setAllProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [searchType, setSearchType] = useState('people'); // 'people', 'groups', or 'services'
  const [filteredGroups, setFilteredGroups] = useState([]);
  const isSettingSearchFromUrlRef = useRef(false); // Track when setting searchQuery from URL to prevent URL sync loop

  // Change detection helpers (for per-field outlines) - placed after currentProfile initialization
  const arraysEqual = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };
  const normalizeTags = (list) => (Array.isArray(list) ? list : [])
    .map(t => (t || '').trim())
    .filter(Boolean)
    .map(t => t.startsWith('#') ? t.toLowerCase() : ('#' + t.toLowerCase()))
    .sort();
  const normalizeSocial = (link) => (link || '')
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '');
  const normalizeSocials = (list) => (Array.isArray(list) ? list : [])
    .map(s => normalizeSocial(s))
    .filter(Boolean)
    .sort();

  // Calculate popular tags from all profiles
  const calculatePopularTags = useCallback(() => {
    // Prevent multiple simultaneous calculations
    if (calculatingPopularTagsRef.current) {
      return;
    }
    
    calculatingPopularTagsRef.current = true;
    
    // Clear any pending timeout
    if (popularTagsTimeoutRef.current) {
      clearTimeout(popularTagsTimeoutRef.current);
    }
    
    // Use setTimeout to ensure loading state is visible even for fast calculations
    popularTagsTimeoutRef.current = setTimeout(() => {
      try {
        if (allProfiles.length === 0) {
          setPopularTags([]);
          setIsLoadingPopularTags(false);
          calculatingPopularTagsRef.current = false;
          return;
        }

        const tagCounts = new Map();
        
        // Count tags from all profiles
        allProfiles.forEach(profile => {
          if (Array.isArray(profile.tagsList) && profile.tagsList.length > 0) {
            profile.tagsList.forEach(tag => {
              if (tag && typeof tag === 'string') {
                const normalizedTag = tag.trim().toLowerCase();
                if (normalizedTag) {
                  const currentCount = tagCounts.get(normalizedTag) || 0;
                  tagCounts.set(normalizedTag, currentCount + 1);
                }
              }
            });
          }
        });

        // Convert to array and sort by count (descending)
        const popularTagsArray = Array.from(tagCounts.entries())
          .map(([tag, count]) => ({ tag, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 20); // Get top 20 tags

        setPopularTags(popularTagsArray);
      } catch (error) {
        console.error('Error calculating popular tags:', error);
        setPopularTags([]);
      } finally {
        setIsLoadingPopularTags(false);
        calculatingPopularTagsRef.current = false;
        popularTagsTimeoutRef.current = null;
      }
    }, 100); // Small delay to ensure loading state is visible
  }, [allProfiles]);

  // Update popular tags when registration form is shown
  useEffect(() => {
    if (showRegistrationForm) {
      // Set loading state immediately when form is shown
      setIsLoadingPopularTags(true);
      // Calculate if we have profiles, otherwise wait for them to load
      if (allProfiles.length > 0 && !calculatingPopularTagsRef.current) {
        calculatePopularTags();
      }
    } else {
      // Reset when form is hidden
      if (popularTagsTimeoutRef.current) {
        clearTimeout(popularTagsTimeoutRef.current);
        popularTagsTimeoutRef.current = null;
      }
      calculatingPopularTagsRef.current = false;
      setIsLoadingPopularTags(false);
      setPopularTags([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRegistrationForm]); // Only depend on showRegistrationForm

  // Recalculate when allProfiles changes while form is visible (only if we don't have tags yet)
  useEffect(() => {
    if (showRegistrationForm && allProfiles.length > 0 && popularTags.length === 0 && !calculatingPopularTagsRef.current) {
      // Don't set loading here - it's already set when form was shown, just calculate
      calculatePopularTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProfiles.length]); // Only recalculate if profiles are loaded and we don't have tags

  // Update popular tags when editing tags is opened
  useEffect(() => {
    if (isEditingTags) {
      // Set loading state immediately when editing tags is opened
      setIsLoadingPopularTags(true);
      // Calculate if we have profiles, otherwise wait for them to load
      if (allProfiles.length > 0 && !calculatingPopularTagsRef.current) {
        calculatePopularTags();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingTags]); // Only depend on isEditingTags

  // Recalculate when allProfiles changes while editing tags (only if we don't have tags yet)
  useEffect(() => {
    if (isEditingTags && allProfiles.length > 0 && popularTags.length === 0 && !calculatingPopularTagsRef.current) {
      // Don't set loading here - it's already set when editing was opened, just calculate
      calculatePopularTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingTags, allProfiles.length]); // Recalculate if editing tags and profiles are loaded

  // Handle clicking on a popular tag to add/remove it from registration form
  const handlePopularTagClick = (tag) => {
    // Remove # prefix and normalize
    const tagWithoutHash = tag.trim().replace(/^#+/, '').toLowerCase();
    if (!tagWithoutHash) return;

    // Get current tags with normalized values and their indices
    const currentTags = [...registrationData.tags];
    const normalizedTags = currentTags.map(t => {
      const normalized = t.trim().replace(/^#+/, '').toLowerCase();
      return normalized;
    });
    
    // Check if tag already exists
    const existingIndex = normalizedTags.findIndex(normalized => normalized === tagWithoutHash);
    
    if (existingIndex >= 0) {
      // Tag exists - remove it
      currentTags.splice(existingIndex, 1);
      // Ensure at least one empty tag slot remains
      if (currentTags.length === 0) {
        currentTags.push('');
      }
    } else {
      // Tag doesn't exist - add it
      // Find the first empty tag slot or add a new one
      const emptyIndex = currentTags.findIndex(t => !t || !t.trim());
      
      if (emptyIndex >= 0) {
        // Fill the empty slot (without #)
        currentTags[emptyIndex] = tagWithoutHash;
      } else {
        // Add new tag slot (without #)
        currentTags.push(tagWithoutHash);
      }
    }

    setRegistrationData({ ...registrationData, tags: currentTags });
  };

  // Handler for popular tag clicks in edit tags mode
  const handleEditPopularTagClick = (tag) => {
    // Remove # prefix and normalize
    const tagWithoutHash = tag.trim().replace(/^#+/, '').toLowerCase();
    if (!tagWithoutHash) return;

    // Get current editing tags with normalized values and their indices
    const currentTags = [...editingTags];
    const normalizedTags = currentTags.map(t => {
      const normalized = (t || '').trim().replace(/^#+/, '').toLowerCase();
      return normalized;
    });
    
    // Check if tag already exists
    const existingIndex = normalizedTags.findIndex(normalized => normalized === tagWithoutHash);
    
    if (existingIndex >= 0) {
      // Tag exists - remove it
      currentTags.splice(existingIndex, 1);
      // Ensure at least one empty tag slot remains
      if (currentTags.length === 0) {
        currentTags.push('');
      }
    } else {
      // Tag doesn't exist - add it
      // Find the first empty tag slot or add a new one
      const emptyIndex = currentTags.findIndex(t => !t || !t.trim());
      
      if (emptyIndex >= 0) {
        // Fill the empty slot (without #)
        currentTags[emptyIndex] = tagWithoutHash;
      } else {
        // Add new tag slot (without #)
        currentTags.push(tagWithoutHash);
      }
    }

    setEditingTags(currentTags);
  };

  // Helper: mark changed fields right after a section Save (before global commit)
  const markRecentlyChanged = (updatedProfile, specificFields = null) => {
    // If specificFields is provided, only mark those fields as changed
    // Otherwise, compare all fields (for backward compatibility)
    if (specificFields && Array.isArray(specificFields)) {
      setRecentlyChanged(prevState => {
        const newState = { ...prevState };
        specificFields.forEach(field => {
          if (field === 'tags') {
            const prev = lastSavedProfileRef.current || {};
            const newTags = Array.isArray(updatedProfile.tagsList) ? updatedProfile.tagsList : [];
            const prevTags = Array.isArray(prev.tagsList) ? prev.tagsList : [];
            const addedTags = newTags.filter(t => !prevTags.includes(t));
            newState.tags = new Set([...(prevState.tags || new Set()), ...addedTags]);
          } else if (field === 'socials') {
            const prev = lastSavedProfileRef.current || {};
            const newSocials = Array.isArray(updatedProfile.socialLinks) ? updatedProfile.socialLinks : [];
            const prevSocials = Array.isArray(prev.socialLinks) ? prev.socialLinks : [];
            const addedSocials = newSocials.filter(s => !prevSocials.includes(s));
            newState.socials = new Set([...(prevState.socials || new Set()), ...addedSocials]);
          } else if (field === 'music') {
            const prev = lastSavedProfileRef.current || {};
            const newMusic = Array.isArray(updatedProfile.musicList) ? updatedProfile.musicList : [];
            const prevMusic = Array.isArray(prev.musicList) ? prev.musicList : [];
            const addedMusic = newMusic.filter(s => !prevMusic.includes(s));
            newState.music = new Set([...(prevState.music || new Set()), ...addedMusic]);
          } else {
            newState[field] = true;
          }
        });
        return newState;
      });
      return;
    }

    // Original logic for comparing all fields (backward compatibility)
    const prev = lastSavedProfileRef.current || {};
    const newTags = Array.isArray(updatedProfile.tagsList) ? updatedProfile.tagsList : [];
    const prevTags = Array.isArray(prev.tagsList) ? prev.tagsList : [];
    const addedTags = newTags.filter(t => !prevTags.includes(t));

    const newSocials = Array.isArray(updatedProfile.socialLinks) ? updatedProfile.socialLinks : [];
    const prevSocials = Array.isArray(prev.socialLinks) ? prev.socialLinks : [];
    const addedSocials = newSocials.filter(s => !prevSocials.includes(s));

    const newMusic = Array.isArray(updatedProfile.musicList) ? updatedProfile.musicList : [];
    const prevMusic = Array.isArray(prev.musicList) ? prev.musicList : [];
    const addedMusic = newMusic.filter(s => !prevMusic.includes(s));

    setRecentlyChanged(prevState => ({
      tags: new Set([...(prevState.tags || new Set()), ...addedTags]),
      socials: new Set([...(prevState.socials || new Set()), ...addedSocials]),
      music: new Set([...(prevState.music || new Set()), ...addedMusic]),
      name: prevState.name || ((prev.name || '') !== (updatedProfile.name || '')),
      bio: prevState.bio || ((prev.bio || '') !== (updatedProfile.bio || '')),
      birthDate: prevState.birthDate || ((prev.birthDate || '') !== (updatedProfile.birthDate || '')),
      city: prevState.city || ((prev.city || prev?.location?.city || '') !== (updatedProfile.city || updatedProfile?.location?.city || '')),
      email: prevState.email || ((prev.email || '') !== (updatedProfile.email || '')),
      preferredContact: prevState.preferredContact || ((prev.preferredContactMethod || '') !== (updatedProfile.preferredContactMethod || '')),
      relationship: prevState.relationship || ((prev.relationshipStatus || '') !== (updatedProfile.relationshipStatus || '')),
      lookingFor: prevState.lookingFor || ((prev.lookingFor || '') !== (updatedProfile.lookingFor || '')),
      languages: prevState.languages || ((prev.languages || '') !== (updatedProfile.languages || '')),
      education: prevState.education || ((prev.education || '') !== (updatedProfile.education || '')),
      occupation: prevState.occupation || ((prev.occupation || '') !== (updatedProfile.occupation || '')),
    }));
  };
  const nameChanged = React.useMemo(() => {
    // Only show change after save (permanent indicator)
    return !!recentlyChanged.name;
  }, [recentlyChanged]);
  const bioChanged = React.useMemo(() => {
    return !!recentlyChanged.bio;
  }, [recentlyChanged]);
  const tagsChanged = React.useMemo(() => {
    // section-level flag if any tag changed
    return recentlyChanged.tags && recentlyChanged.tags.size > 0;
  }, [recentlyChanged]);
  const socialsChanged = React.useMemo(() => {
    return recentlyChanged.socials && recentlyChanged.socials.size > 0;
  }, [recentlyChanged]);
  const birthDateChanged = React.useMemo(() => {
    return !!recentlyChanged.birthDate;
  }, [recentlyChanged]);
  const cityChanged = React.useMemo(() => {
    return !!recentlyChanged.city;
  }, [recentlyChanged]);

  // Initialize lastSavedProfileRef once when profile first loads
  useEffect(() => {
    if (currentProfile && !lastSavedProfileRef.current) {
      lastSavedProfileRef.current = safeCloneProfile(currentProfile);
    }
  }, [currentProfile?.username]);

  // Clear change tracking state when switching to another profile (not "My Profile")
  useEffect(() => {
    if (currentUser === null && currentProfile) {
      // Clear all change indicators when viewing another profile
      clearChangeTrackingState();
    }
  }, [currentUser, currentProfile?.username]);

  // Detect referral code from URL on page load (case-insensitive)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      // Normalize to uppercase for storage (codes are case-insensitive)
      const normalizedCode = refCode.toUpperCase();
      setReferralCode(normalizedCode);
      // Store in localStorage to persist across page reloads
      localStorage.setItem('pendingReferralCode', normalizedCode);
      console.log('📎 Referral code detected from URL:', normalizedCode);
    } else {
      // Check localStorage for pending referral code
      const pendingRef = localStorage.getItem('pendingReferralCode');
      if (pendingRef) {
        setReferralCode(pendingRef);
        console.log('📎 Referral code loaded from localStorage:', pendingRef);
      }
    }
  }, [location.search]);

  // Load gamification data and referral stats when wallet connects
  useEffect(() => {
    if (!walletAddress) {
      setGamificationData(null);
      setReferralStats(null);
      return;
    }

    const loadGamificationData = async () => {
      try {
        const data = await getUserGamificationData(walletAddress);
        setGamificationData(data);
        
        // Load last submitted X post URL from gamification data
        if (data && data.claimedXPosts && Array.isArray(data.claimedXPosts) && data.claimedXPosts.length > 0) {
          const lastSubmittedPost = data.claimedXPosts[data.claimedXPosts.length - 1];
          setSubmittedXPostUrl(lastSubmittedPost);
        } else {
          // Clear if no submitted posts
          setSubmittedXPostUrl(null);
        }
        
        // Update activity streak
        const streakData = await updateActivityStreak(walletAddress);
        if (streakData) {
          setGamificationData(prev => prev ? { ...prev, ...streakData } : null);
        }
        
        // Load referral stats
        const stats = await getReferralStats(walletAddress);
        setReferralStats(stats);
        
        // Get or create referral code (or migrate if old format)
        if (stats) {
          const existingCode = stats.referralCode;
          const isOldFormat = existingCode && existingCode.length === 12 && /^[0-9A-F]{12}$/i.test(existingCode);
          
          if (!existingCode || isOldFormat) {
            // No code exists, or it's in old format - get/create/migrate
            const code = await getOrCreateReferralCode(walletAddress);
            if (code) {
              const updatedStats = await getReferralStats(walletAddress);
              setReferralStats(updatedStats);
            }
          }
        }
      } catch (error) {
        console.error('Error loading gamification data:', error);
      }
    };

    loadGamificationData();
    
    // Reload gamification data periodically to catch updates from other components (like Messaging)
    const interval = setInterval(() => {
      if (walletAddress) {
        getUserGamificationData(walletAddress).then(data => {
          if (data) {
            setGamificationData(prev => {
              // Only update if data actually changed to avoid unnecessary re-renders
              if (!prev || JSON.stringify(prev.badges) !== JSON.stringify(data.badges) || 
                  prev.messagesSent !== data.messagesSent || prev.profilesViewed !== data.profilesViewed) {
                return data;
              }
              return prev;
            });
          }
        }).catch(err => console.error('Error reloading gamification data:', err));
      }
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(interval);
  }, [walletAddress]);

  // Load inviter's profile when referralStats.referredBy is available
  useEffect(() => {
    const loadInviterProfile = async () => {
      if (!referralStats?.referredBy) {
        setInviterProfile(null);
        return;
      }

      try {
        // Get inviter's username from wallet address
        const inviterUsername = await getUsernameFromWallet(referralStats.referredBy);
        if (!inviterUsername) {
          setInviterProfile(null);
          return;
        }

        // Get inviter's profile metadata from Firestore
        const profileRef = doc(db, 'profiles', inviterUsername);
        const profileSnap = await getDoc(profileRef);
        
        if (profileSnap.exists()) {
          const profileData = profileSnap.data();
          setInviterProfile({
            username: inviterUsername,
            name: profileData.name || inviterUsername,
            avatar: profileData.avatar || null
          });
        } else {
          setInviterProfile({
            username: inviterUsername,
            name: inviterUsername,
            avatar: null
          });
        }
      } catch (error) {
        console.error('Error loading inviter profile:', error);
        setInviterProfile(null);
      }
    };

    loadInviterProfile();
  }, [referralStats?.referredBy]);

  // Reload referral stats when tagrun becomes visible to ensure fresh data
  useEffect(() => {
    const reloadReferralStats = async () => {
      if (tagRunVisible && walletAddress) {
        try {
          const stats = await getReferralStats(walletAddress);
          setReferralStats(stats);
        } catch (error) {
          console.error('Error reloading referral stats:', error);
        }
      }
    };

    reloadReferralStats();
  }, [tagRunVisible, walletAddress]);

  // Update profile completeness when profile changes
  // Use ref to track last completeness update to prevent infinite loops
  const lastCompletenessUpdateRef = useRef(null);
  
  useEffect(() => {
    if (!walletAddress || !currentProfile || !currentUser || currentProfile.username !== currentUser) {
      return;
    }

    // Create a stable key for the profile to prevent duplicate updates
    const profileKey = `${currentProfile.username}_${JSON.stringify(currentProfile).slice(0, 100)}`;
    if (lastCompletenessUpdateRef.current === profileKey) {
      return; // Already updated for this profile version
    }

    const updateCompleteness = async () => {
      try {
        const completeness = await updateProfileCompleteness(walletAddress, currentProfile);
        if (completeness !== null) {
          // Mark as updated
          lastCompletenessUpdateRef.current = profileKey;
          // Update local state immediately
          setGamificationData(prev => prev ? { ...prev, profileCompleteness: completeness } : null);
          // Also reload from Firestore to ensure consistency
          const updatedData = await getUserGamificationData(walletAddress);
          if (updatedData) {
            setGamificationData(updatedData);
            console.log('🔄 Refreshed gamification data from Firestore:', updatedData.profileCompleteness + '%');
          }
        }
      } catch (error) {
        console.error('Error updating profile completeness:', error);
      }
    };

    updateCompleteness();
  }, [walletAddress, currentProfile?.username, currentUser]);

  // Check for new badges when gamification data updates
  useEffect(() => {
    if (!walletAddress || !gamificationData) return;

    const checkBadges = async () => {
      try {
        const newBadgesAwarded = await checkAndAwardBadges(walletAddress, {
          profileCompleteness: gamificationData.profileCompleteness,
          currentStreak: gamificationData.currentStreak,
          messagesSent: gamificationData.messagesSent,
          profilesViewed: gamificationData.profilesViewed
        }, currentProfile);
        
        if (newBadgesAwarded.length > 0) {
          setNewBadges(newBadgesAwarded);
          // Reload gamification data to get updated badges
          const updatedData = await getUserGamificationData(walletAddress);
          setGamificationData(updatedData);
          
          // Auto-hide badge notification after 5 seconds
          setTimeout(() => {
            setNewBadges([]);
          }, 5000);
        }
      } catch (error) {
        console.error('Error checking badges:', error);
      }
    };

    checkBadges();
  }, [walletAddress, gamificationData?.profileCompleteness, gamificationData?.currentStreak, gamificationData?.messagesSent, gamificationData?.profilesViewed, currentProfile]);

  // Initialize wizard form data when wizard opens
  useEffect(() => {
    if (showProfileCompletenessWizard && currentProfile) {
      setWizardFormData({
        name: currentProfile.name || '',
        bio: currentProfile.bio || '',
        city: currentProfile.city || '',
        birthDate: currentProfile.birthDate || '',
        tagsList: [...(currentProfile.tagsList || [])],
        socialLinks: [...(currentProfile.socialLinks || [])],
        relationshipStatus: currentProfile.relationshipStatus || '',
        lookingFor: currentProfile.lookingFor || '',
        languages: currentProfile.languages || '',
        education: currentProfile.education || '',
        occupation: currentProfile.occupation || '',
        email: currentProfile.email || '',
        preferredContactMethod: currentProfile.preferredContactMethod || ''
      });
      // Reset AI bio suggestions when wizard opens
      setAiBioSuggestions([]);
      setIsLoadingBioSuggestions(false);
    }
  }, [showProfileCompletenessWizard, currentProfile?.username]);

  const [expandedCities, setExpandedCities] = useState(new Set());
  const [isEditingCity, setIsEditingCity] = useState(false);
  const markersRef = useRef([]);
  const markerClusterGroupRef = useRef(null); // Marker cluster group for grouping markers
  const distanceCacheRef = useRef(new Map()); // Cache for distance calculations
  const markerCreationBatchRef = useRef(null); // Track marker creation batch
  const selectedMarkerRef = useRef(null); // Track currently highlighted (blue) marker
  
  // Get unique cities from worldLocations
  const availableCities = useMemo(() => {
    const cities = new Set();
    worldLocations.forEach(loc => cities.add(loc.city));
    return Array.from(cities).sort();
  }, []);
  
  // Handle city change
  const handleCityChange = (newCity) => {
    const coords = getCityCoordinates(newCity);
    if (!coords) return;
    
    if (!currentProfile || !currentUser) return;
    
    // Update currentProfile
    const updatedProfile = {
      ...currentProfile,
      city: newCity,
      location: coords
    };
    
    setIsEditingCity(false);
    
    // Update or create map marker for user's profile
    if (mapInstanceRef.current) {
      // If marker exists, update it
      if (currentProfile.marker) {
        currentProfile.marker.setLatLng([coords.lat, coords.lng]);
        updatedProfile.marker = currentProfile.marker;
        } else {
        // Create a new marker for the user's profile
        const marker = window.L.marker([coords.lat, coords.lng])
          .addTo(mapInstanceRef.current);
        
        marker.on('click', async () => {
          // Check current walletAddress using ref (always up-to-date)
          const currentWalletAddress = walletAddressRef.current;
          if (currentWalletAddress) {
            // Always load fresh profile from IPFS when clicking on own marker
            try {
              console.log('🔄 Loading fresh profile data from IPFS for current user...');
              const currentEncryptionKey = encryptionKeyRef.current || encryptionKey;
              
              // Get Firestore metadata first (contains only published elements) - with retry to prevent race condition
              const firestoreMetadata = await fetchFirestoreMetadataWithRetry(currentWalletAddress);
              
              // Set published states immediately from Firestore metadata (don't wait for IPFS)
              // This prevents the "unpublished" flash when profile loads
              // Note: If firestoreMetadata is null, we'll set published states after IPFS loads (fallback)
              if (firestoreMetadata) {
                // Simple fields: published if they exist in Firestore (Firestore only stores published fields)
                setPublishedName(!!(firestoreMetadata?.name && firestoreMetadata.name.trim() !== ''));
                setPublishedBio(!!(firestoreMetadata?.bio && firestoreMetadata.bio.trim() !== ''));
                setPublishedEmail(!!(firestoreMetadata?.email && firestoreMetadata.email.trim() !== ''));
                setPublishedPreferredContact(!!(firestoreMetadata?.preferredContactMethod && firestoreMetadata.preferredContactMethod.trim() !== ''));
                setPublishedRelationship(!!(firestoreMetadata?.relationshipStatus && firestoreMetadata.relationshipStatus.trim() !== ''));
                setPublishedLookingFor(!!(firestoreMetadata?.lookingFor && firestoreMetadata.lookingFor.trim() !== ''));
                setPublishedLanguages(!!(firestoreMetadata?.languages && firestoreMetadata.languages.trim() !== ''));
                setPublishedEducation(!!(firestoreMetadata?.education && firestoreMetadata.education.trim() !== ''));
                setPublishedOccupation(!!(firestoreMetadata?.occupation && firestoreMetadata.occupation.trim() !== ''));
                // Wallet: use _isWalletPublishedInFirestore flag (walletAddress always has fallback)
                setPublishedWallet(firestoreMetadata?._isWalletPublishedInFirestore === true);
                // Note: Tags, socials, music, birthDate, and city need IPFS data to compare, so they'll be set after IPFS loads
              }
              
              // Get full profile from IPFS
              const freshProfile = await getUserProfileByWallet(currentWalletAddress, currentEncryptionKey);
              
              if (freshProfile) {
                console.log('✅ Loaded fresh profile from IPFS');
                // IPFS hash already comes from blockchain via getUserProfileByWallet
                const profileWithHash = {
                  ...freshProfile,
                  ipfsHash: freshProfile.ipfsHash || null
                };
                const cleanReloaded = cleanProfileData(profileWithHash);
                
                // CRITICAL FIX: Merge Firestore metadata with IPFS profile to prevent IPFS from overwriting Firestore values
                // Get Firestore metadata for merge (use existing firestoreMetadata if available, otherwise fetch it with retry)
                let firestoreMetaForReload = firestoreMetadata;
                if (!firestoreMetaForReload) {
                  try {
                    firestoreMetaForReload = await fetchFirestoreMetadataWithRetry(currentWalletAddress);
                  } catch (e) {
                    console.warn('Could not load Firestore metadata for merge:', e);
                  }
                }
                
                const mergedReloaded = { ...cleanReloaded };
                if (firestoreMetaForReload) {
                  // Only use Firestore values if they're non-empty strings (published)
                  // If null/undefined/empty (unpublished), use IPFS values instead
                  if (firestoreMetaForReload.bio && typeof firestoreMetaForReload.bio === 'string' && firestoreMetaForReload.bio.trim() !== '') {
                    mergedReloaded.bio = firestoreMetaForReload.bio;
                  }
                  if (firestoreMetaForReload.name && typeof firestoreMetaForReload.name === 'string' && firestoreMetaForReload.name.trim() !== '') {
                    mergedReloaded.name = firestoreMetaForReload.name;
                  }
                  if (firestoreMetaForReload.email && typeof firestoreMetaForReload.email === 'string' && firestoreMetaForReload.email.trim() !== '') {
                    mergedReloaded.email = firestoreMetaForReload.email;
                  }
                }
                
                // Close activity feed FIRST to prevent overlap during loading
                setActivityFeedVisible(false);
                setCurrentUser(mergedReloaded.username);
                setCurrentProfile(mergedReloaded);
                setGroupsVisible(false);
                setAboutVisible(false);
                // Show profile AFTER closing activity feed
                setProfileVisible(true);
                
                // Determine published state by comparing Firestore (only published) with IPFS (all elements)
                // Update tags, socials, birthDate, and city (these need IPFS data to compare)
                const firestoreTags = firestoreMetadata?.tagsList || [];
                const firestoreSocials = firestoreMetadata?.socialLinks || [];
                const firestoreBirthDate = firestoreMetadata?.birthDate || null;
                const firestoreCity = firestoreMetadata?.city || null;
                // Tags: published if in Firestore
                const publishedTagsList = cleanReloaded.tagsList.filter(tag => firestoreTags.includes(tag));
                setPublishedTags(publishedTagsList);
                
                // Socials: published if in Firestore
                const publishedSocialsList = cleanReloaded.socialLinks.filter(social => firestoreSocials.includes(social));
                setPublishedSocials(publishedSocialsList);
                
                // Music: published if in Firestore
                const firestoreMusic = firestoreMetadata?.musicList || [];
                const publishedMusicList = (cleanReloaded.musicList || []).filter(song => firestoreMusic.includes(song));
                setPublishedMusic(publishedMusicList);
                
                // BirthDate: published if in Firestore
                setPublishedBirthDate(!!firestoreBirthDate && firestoreBirthDate === cleanReloaded.birthDate);
                
                // City: published if in Firestore
                setPublishedCity(!!firestoreCity && firestoreCity === cleanReloaded.city);
                
                // Re-verify simple fields (should already be set, but ensure consistency)
                const firestoreName = firestoreMetadata?.name || null;
                const nameIsPublishedReloaded = !!(firestoreName && firestoreName.trim() !== '');
                setPublishedName(nameIsPublishedReloaded);
                
                const firestoreBioReloaded = firestoreMetadata?.bio || null;
                const bioIsPublishedReloaded = !!(firestoreBioReloaded && firestoreBioReloaded.trim() !== '');
                setPublishedBio(bioIsPublishedReloaded);
                
                const firestoreEmail = firestoreMetadata?.email || null;
                const emailIsPublishedReloaded = !!(firestoreEmail && firestoreEmail.trim() !== '');
                setPublishedEmail(emailIsPublishedReloaded);
                
                const firestorePreferredContactReloaded = firestoreMetadata?.preferredContactMethod || null;
                const preferredContactIsPublishedReloaded = !!(firestorePreferredContactReloaded && firestorePreferredContactReloaded.trim() !== '');
                setPublishedPreferredContact(preferredContactIsPublishedReloaded);
                
                const firestoreRelationshipReloaded = firestoreMetadata?.relationshipStatus || null;
                const relationshipIsPublishedReloaded = !!(firestoreRelationshipReloaded && firestoreRelationshipReloaded.trim() !== '');
                setPublishedRelationship(relationshipIsPublishedReloaded);
                
                const firestoreLookingForReloaded = firestoreMetadata?.lookingFor || null;
                const lookingForIsPublishedReloaded = !!(firestoreLookingForReloaded && firestoreLookingForReloaded.trim() !== '');
                setPublishedLookingFor(lookingForIsPublishedReloaded);
                
                const firestoreLanguagesReloaded = firestoreMetadata?.languages || null;
                const languagesIsPublishedReloaded = !!(firestoreLanguagesReloaded && firestoreLanguagesReloaded.trim() !== '');
                setPublishedLanguages(languagesIsPublishedReloaded);
                
                const firestoreEducationReloaded = firestoreMetadata?.education || null;
                const educationIsPublishedReloaded = !!(firestoreEducationReloaded && firestoreEducationReloaded.trim() !== '');
                setPublishedEducation(educationIsPublishedReloaded);
                
                const firestoreOccupationReloaded = firestoreMetadata?.occupation || null;
                const occupationIsPublishedReloaded = !!(firestoreOccupationReloaded && firestoreOccupationReloaded.trim() !== '');
                setPublishedOccupation(occupationIsPublishedReloaded);
                
              } else {
                // Fallback to updatedProfile if IPFS load fails
                setCurrentProfile(updatedProfile);
                setProfileVisible(true);
              }
            } catch (error) {
              console.error('Error loading fresh profile from IPFS:', error);
              // Fallback to updatedProfile if IPFS load fails
              setCurrentProfile(updatedProfile);
              setProfileVisible(true);
            }
          } else {
            setCurrentProfile(null);
            setProfileVisible(false);
            setNotification({ message: 'Log in to see profile', type: 'info' });
            setTimeout(() => setNotification(null), 4000);
          }
        });
        
        updatedProfile.marker = marker;
      }
      
      // Center map on new city
      mapInstanceRef.current.setView([coords.lat, coords.lng], 13);
      if (updatedProfile.marker) {
        updatedProfile.marker.openPopup();
      }
    }
    
    // Save to localStorage
    try {
      localStorage.setItem('userProfile', JSON.stringify({
        city: newCity,
        location: coords
      }));
    } catch (e) {
      console.error('Error saving profile to localStorage:', e);
    }
    
    // Update state with marker reference (this will trigger recalculation of distances)
    setCurrentProfile(updatedProfile);
  };

  // Calculate distance between two coordinates (Haversine formula)
  // Memoized version with caching for performance
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    // Create cache key
    const cacheKey = `${lat1.toFixed(4)},${lon1.toFixed(4)},${lat2.toFixed(4)},${lon2.toFixed(4)}`;
    
    // Check cache first
    if (distanceCacheRef.current.has(cacheKey)) {
      return distanceCacheRef.current.get(cacheKey);
    }
    
    // Calculate distance
    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    // Cache the result (limit cache size to prevent memory issues)
    if (distanceCacheRef.current.size > 10000) {
      // Clear oldest entries (first 5000)
      const entries = Array.from(distanceCacheRef.current.entries());
      distanceCacheRef.current.clear();
      entries.slice(5000).forEach(([key, value]) => {
        distanceCacheRef.current.set(key, value);
      });
    }
    distanceCacheRef.current.set(cacheKey, distance);
    
    return distance;
  }, []);

  // Toggle city expansion
  const toggleCity = (city) => {
    setExpandedCities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(city)) {
        newSet.delete(city);
      } else {
        newSet.add(city);
      }
      return newSet;
    });
  };

  useEffect(() => {
    // Wait for Leaflet to be available
    const initMap = () => {
      if (typeof window.L === 'undefined') {
        // Wait a bit for script to load
        setTimeout(initMap, 100);
        return;
      }

      if (!mapInstanceRef.current && mapRef.current) {
        // Default to Berlin, will be updated when user connects wallet
        const mapCenter = { lat: 32.47, lng: 13.405 };
        
        // Initialize the map
        const map = window.L.map(mapRef.current, {
          center: [mapCenter.lat, mapCenter.lng],
          zoom: 13,
          zoomControl: false,
          attributionControl: false,
          maxBounds: [
            [-90, -180], // Southwest corner
            [90, 250]    // Northeast corner
          ],
          maxBoundsViscosity: 1.0 // Prevents panning outside bounds
        });
        
        // Explicitly set the view to ensure zoom is respected (prevents browser caching issues)
        map.setView([mapCenter.lat, mapCenter.lng], 3, { reset: true });

        // Store map instance for location button
        mapInstanceRef.current = map;

        // Add colorful map tiles
        window.L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
          maxZoom: 19,
          minZoom: 3,
          attribution: '',
          subdomains: 'abcd',
          tileSize: 256,
          zoomOffset: 0
        }).addTo(map);

        // Initialize with empty profiles - will be loaded from Firestore
        const markers = [];
        const profiles = [];

        markersRef.current = markers;
        setAllProfiles(profiles);
        setFilteredProfiles(profiles);

        mapInstanceRef.current = map;
        
        // Initialize marker cluster group for grouping markers when zoomed out
        // Configuration is loaded from src/config/leafletClusterConfig.js
        if (window.L.markerClusterGroup) {
          const clusterConfig = getClusterGroupConfig(window.L);
          markerClusterGroupRef.current = window.L.markerClusterGroup(clusterConfig).addTo(map);
        }
        
        // Load all profiles from Firestore and create markers (with delay to ensure map is ready)
        setTimeout(() => {
          loadAllProfilesAndCreateMarkers();
        }, 500);

        // Note: Viewport-based marker visibility removed - it was causing markers to disappear
        // Leaflet handles marker rendering efficiently, and filtering logic controls visibility

        // Handle window resize
        const handleResize = () => {
          map.invalidateSize();
        };
        window.addEventListener('resize', handleResize);

        // Background processing simulation
        function backgroundTask() {
          console.log('Background task running...', new Date().toLocaleTimeString());
        }
        const intervalId = setInterval(backgroundTask, 30000);
        backgroundTask();

        // Store interval ID for cleanup
        mapInstanceRef.current._intervalId = intervalId;
        mapInstanceRef.current._handleResize = handleResize;
      }
    };

    initMap();

    // Cleanup function
    return () => {
      if (markerClusterGroupRef.current) {
        markerClusterGroupRef.current.clearLayers();
        if (mapInstanceRef.current) {
          mapInstanceRef.current.removeLayer(markerClusterGroupRef.current);
        }
        markerClusterGroupRef.current = null;
      }
      if (mapInstanceRef.current) {
        if (mapInstanceRef.current._handleResize) {
          window.removeEventListener('resize', mapInstanceRef.current._handleResize);
        }
        if (mapInstanceRef.current._intervalId) {
          clearInterval(mapInstanceRef.current._intervalId);
        }
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Ensure map resizes correctly on mobile layout changes
  useEffect(() => {
    if (!isMobile || !mapInstanceRef.current) {
      return;
    }
    const resizeTimeout = setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    }, 200);
    return () => clearTimeout(resizeTimeout);
  }, [
    isMobile,
    leftSectionVisible,
    smartRecommendationsVisible,
    profileVisible,
    groupsVisible,
    messagesVisible,
    activityFeedVisible,
    tagRunVisible,
    aboutVisible
  ]);

  // Ensure profile panel is visible when on profile routes in mobile
  useEffect(() => {
    if (!isMobile) return;
    if (normalizedPathname === '/myprofile' || normalizedPathname.startsWith('/profile/')) {
      setProfileVisible(true);
    }
  }, [isMobile, normalizedPathname]);

  // Cleanup city suggestions timeout on unmount
  useEffect(() => {
    return () => {
      if (citySuggestionsTimeoutRef.current) {
        clearTimeout(citySuggestionsTimeoutRef.current);
      }
    };
  }, []);

  // Load all profiles from Firestore and create markers on the map
  // Optimized with batched marker creation for better performance
  const loadAllProfilesAndCreateMarkers = async () => {
    if (!mapInstanceRef.current) {
      console.log('Map not ready, retrying in 500ms...');
      setTimeout(loadAllProfilesAndCreateMarkers, 500);
      return;
    }

    try {
      const profiles = await getAllProfiles();
      console.log('Loaded profiles:', profiles.length);
      
      // Clear existing markers from cluster group
      if (markerClusterGroupRef.current) {
        markerClusterGroupRef.current.clearLayers();
      } else {
        // Fallback: remove markers directly from map if cluster group not available
        markersRef.current.forEach(marker => {
          if (marker && mapInstanceRef.current) {
            mapInstanceRef.current.removeLayer(marker);
          }
        });
      }
      markersRef.current = [];
      
      // Cancel any pending marker creation batch
      if (markerCreationBatchRef.current) {
        clearTimeout(markerCreationBatchRef.current);
        markerCreationBatchRef.current = null;
      }
      
      const markers = [];
      const profilesWithMarkers = [];
      const profilesToProcess = [];
      
      // Process profiles in batches to avoid blocking the UI
      for (const profile of profiles) {
        // Only skip profiles with no published data at all
        const hasAvatar = getAvatarFromProfile(profile);
        const hasPublishedData = profile.username || profile.bio || hasAvatar || profile.tags || profile.location;
        
        // Only skip profiles with no published data at all
        if (!hasPublishedData) {
          return; // Skip profiles with no published data
        }
        
        // Check if profile has location coordinates AND a city name
        const hasLocation = profile.location && profile.location.lat && profile.location.lng;
        const hasCity = (profile.city && profile.city.trim() !== '') || (profile.location?.city && profile.location.city.trim() !== '');
        
        if (hasLocation && hasCity) {
          // Only create marker if profile has both location coordinates and a city name
          profilesToProcess.push(profile);
        } else {
          // Add profile without marker if location or city is missing
          profilesWithMarkers.push(profile);
        }
      }
      
      // Create markers in batches to avoid blocking the UI
      // Reduced batch size and added delay to prevent freezes with 1000+ markers
      const BATCH_SIZE = 20; // Reduced from 50 to 20 to prevent freezes
      let currentIndex = 0;
      
      // Function to create a profile marker
      const createProfileMarker = (profile) => {
        if (!profile.location || !profile.location.lat || !profile.location.lng) {
          return null;
        }
        
        const lat = profile.location.lat;
        const lng = profile.location.lng;
        
        // Create marker with default (non-selected) tan icon to match cluster color
        const marker = window.L.marker([lat, lng], { icon: createDefaultProfileIcon(window.L) });

        // Tag marker with profile identity so we can highlight it when selected
        marker._profileWallet = (profile.walletAddress || '').toLowerCase();
        marker._profileUsername = profile.username || '';
        
        // Add marker to cluster group if available, otherwise add directly to map
        if (markerClusterGroupRef.current) {
          markerClusterGroupRef.current.addLayer(marker);
        } else if (mapInstanceRef.current) {
          marker.addTo(mapInstanceRef.current);
        }
        
        // Add click handler - use ref to get current walletAddress at click time
        marker.on('click', async () => {
          const currentWalletAddress = walletAddressRef.current;
          if (currentWalletAddress) {
            // Check if this is the current user's profile
            if (profile.walletAddress && profile.walletAddress.toLowerCase() === currentWalletAddress.toLowerCase()) {
              // This is the current user's profile - load fresh data from IPFS to ensure we have the latest data
              try {
                console.log('🔄 Loading fresh profile data from IPFS for current user...');
                const currentEncryptionKey = encryptionKeyRef.current || encryptionKey;
                
                // Get Firestore metadata first (contains only published elements) - with retry to prevent race condition
                const firestoreMetadata = await fetchFirestoreMetadataWithRetry(currentWalletAddress);
                
                // Set published states immediately from Firestore metadata (don't wait for IPFS)
                // This prevents the "unpublished" flash when profile loads
                // Note: If firestoreMetadata is null, we'll set published states after IPFS loads (fallback)
                if (firestoreMetadata) {
                  // Simple fields: published if they exist in Firestore (Firestore only stores published fields)
                  setPublishedName(!!(firestoreMetadata?.name && firestoreMetadata.name.trim() !== ''));
                  setPublishedBio(!!(firestoreMetadata?.bio && firestoreMetadata.bio.trim() !== ''));
                  setPublishedEmail(!!(firestoreMetadata?.email && firestoreMetadata.email.trim() !== ''));
                  setPublishedPreferredContact(!!(firestoreMetadata?.preferredContactMethod && firestoreMetadata.preferredContactMethod.trim() !== ''));
                  // Wallet: use _isWalletPublishedInFirestore flag (walletAddress always has fallback)
                  setPublishedWallet(firestoreMetadata?._isWalletPublishedInFirestore === true);
                  // Note: Tags, socials, music, birthDate, and city need IPFS data to compare, so they'll be set after IPFS loads
                }
                
                // Get full profile from IPFS
                const freshProfile = await getUserProfileByWallet(currentWalletAddress, currentEncryptionKey);
                
                if (freshProfile) {
                  console.log('✅ Loaded fresh profile from IPFS');
                  // IPFS hash already comes from blockchain via getUserProfileByWallet
                  const profileWithHash = {
                    ...freshProfile,
                    ipfsHash: freshProfile.ipfsHash || null
                  };
                  const cleanReloaded = cleanProfileData(profileWithHash);
                  
                  // Close activity feed FIRST to prevent overlap during loading
                  setActivityFeedVisible(false);
                  setCurrentUser(cleanReloaded.username);
                  setCurrentProfile(cleanReloaded);
                  setAboutVisible(false);
                  // Show profile AFTER closing activity feed
                  setProfileVisible(true);
                  
                  // Determine published state by comparing Firestore (only published) with IPFS (all elements)
                  // Update tags, socials, birthDate, and city (these need IPFS data to compare)
                  const firestoreTags = firestoreMetadata?.tagsList || [];
                  const firestoreSocials = firestoreMetadata?.socialLinks || [];
                  const firestoreBirthDate = firestoreMetadata?.birthDate || null;
                  const firestoreCity = firestoreMetadata?.city || null;
                  
                  // Tags: published if in Firestore
                  const publishedTagsList = cleanReloaded.tagsList.filter(tag => firestoreTags.includes(tag));
                  setPublishedTags(publishedTagsList);
                  
                  // Socials: published if in Firestore
                  const publishedSocialsList = cleanReloaded.socialLinks.filter(social => firestoreSocials.includes(social));
                  setPublishedSocials(publishedSocialsList);
                  
                  // Music: published if in Firestore
                  const firestoreMusic = firestoreMetadata?.musicList || [];
                  const publishedMusicList = (cleanReloaded.musicList || []).filter(song => firestoreMusic.includes(song));
                  setPublishedMusic(publishedMusicList);
                  
                  // BirthDate: published if in Firestore
                  setPublishedBirthDate(!!firestoreBirthDate && firestoreBirthDate === cleanReloaded.birthDate);
                  
                  // City: published if in Firestore
                  setPublishedCity(!!firestoreCity && firestoreCity === cleanReloaded.city);
                  
                  // Re-verify simple fields (should already be set, but ensure consistency)
                  const firestoreEmail = firestoreMetadata?.email || null;
                  const emailIsPublishedMarker = !!(firestoreEmail && firestoreEmail.trim() !== '');
                  setPublishedEmail(emailIsPublishedMarker);
                  
                  const firestoreName = firestoreMetadata?.name || null;
                  const nameIsPublishedMarker = !!(firestoreName && firestoreName.trim() !== '');
                  setPublishedName(nameIsPublishedMarker);
                  
                  const firestoreBioMarker = firestoreMetadata?.bio || null;
                  const bioIsPublishedMarker = !!(firestoreBioMarker && firestoreBioMarker.trim() !== '');
                  setPublishedBio(bioIsPublishedMarker);
                  
                  const firestorePreferredContactMarker = firestoreMetadata?.preferredContactMethod || null;
                  const preferredContactIsPublishedMarker = !!(firestorePreferredContactMarker && firestorePreferredContactMarker.trim() !== '');
                  setPublishedPreferredContact(preferredContactIsPublishedMarker);
                } else {
                  // If IPFS load failed, try to reload from Firestore metadata as fallback
                  console.warn('⚠️ Failed to load profile from IPFS, using Firestore metadata as fallback');
                  if (firestoreMetadata) {
                    // Get IPFS hash from blockchain for fallback (Arc (Circle) network)
                    const fallbackIpfsHash = await getCIDFromArc(currentWalletAddress);
                    const fallbackProfile = cleanProfileData({
                      ...firestoreMetadata,
                      ipfsHash: fallbackIpfsHash || null
                    });
                    setCurrentUser(fallbackProfile.username);
                    setCurrentProfile(fallbackProfile);
                    setProfileVisible(true);
                    setAboutVisible(false);
                    setActivityFeedVisible(false);
                    // Reset published states since we don't have full IPFS data
                    setPublishedTags([]);
                    setPublishedSocials([]);
                    setPublishedBirthDate(false);
                    setPublishedCity(false);
                    setPublishedWallet(false);
                    setPublishedEmail(false);
                    setPublishedName(false);
                    setPublishedBio(false);
                    setPublishedPreferredContact(false);
                  } else {
                    console.error('❌ No profile data available from IPFS or Firestore');
                  }
                }
              } catch (error) {
                console.error('❌ Error loading fresh profile from IPFS:', error);
                // Try to reload from Firestore as last resort
                try {
                  const fallbackMetadata = await getUserProfileByWallet(currentWalletAddress, null);
                  if (fallbackMetadata) {
                    const fallbackProfile = cleanProfileData({
                      ...fallbackMetadata,
                      ipfsHash: fallbackMetadata.ipfsHash || null
                    });
                    // Close activity feed FIRST to prevent overlap during loading
                    setActivityFeedVisible(false);
                    setCurrentUser(fallbackProfile.username);
                    setCurrentProfile(fallbackProfile);
                    setAboutVisible(false);
                    // Show profile AFTER closing activity feed
                    setProfileVisible(true);
                    // Reset published states since we don't have full IPFS data
                    setPublishedTags([]);
                    setPublishedSocials([]);
                    setPublishedBirthDate(false);
                    setPublishedCity(false);
                    setPublishedWallet(false);
                    setPublishedEmail(false);
                    setPublishedName(false);
                    setPublishedBio(false);
                    setPublishedPreferredContact(false);
                  }
                } catch (fallbackError) {
                  console.error('❌ Failed to load fallback profile:', fallbackError);
                }
              }
            } else {
              // This is not the current user's profile - load full profile data using loadUserProfile
              // This ensures IPFS data is loaded properly for all users including test users
              const cleanOtherProfile = cleanProfileData(profile);
              
              // CRITICAL FIX: Check if this is a test user - if so, use profile data directly
              // Test users should never show registration form, they always have data in allProfiles
              // Check both original profile and cleaned profile for _isTestRecord flag
              const isTestUser = (profile._isTestRecord === true) || (cleanOtherProfile._isTestRecord === true);
              
              // Navigate to profile URL immediately for instant feedback
              if (cleanOtherProfile.username) {
                navigate(`/profile/${cleanOtherProfile.username}`);
              }
              
              // Clear change tracking state immediately when switching to another profile
              clearChangeTrackingState();
              // Close activity feed FIRST to prevent overlap during loading
              setActivityFeedVisible(false);
              setAboutVisible(false);
              // Ensure other right-side panels are closed when opening profile
              setGroupsVisible(false);
              setMessagesVisible(false);
              setBugVisible(false);
              setAiAssistantVisible(false);
              
              if (isTestUser) {
                // For test users, show profile directly without calling loadUserProfile
                // The URL navigation will trigger the URL loading logic which also handles test users
                console.log('✅ [Marker Click] Test user detected - using profile data directly', {
                  originalHasFlag: profile._isTestRecord === true,
                  cleanedHasFlag: cleanOtherProfile._isTestRecord === true
                });
                setCurrentUser(null); // Test users are never "My Profile"
                setCurrentProfile(cleanOtherProfile);
                setShowRegistrationForm(false);
                setIsLoadingProfileFromIPFS(false);
                setIsLoadingUser(false);
                setProfileLoadError(null);
                setProfileVisible(true);
              } else {
                // For non-test users, get wallet address and load full profile data
                // Get wallet address for loading profile
                let profileWalletForMessaging = cleanOtherProfile.walletAddress;
                
                // If no wallet address, try to look it up from username
                if (!profileWalletForMessaging && cleanOtherProfile.username) {
                  try {
                    console.log('🔄 [Marker Click] Looking up wallet address for:', cleanOtherProfile.username);
                    profileWalletForMessaging = await getWalletFromUsername(cleanOtherProfile.username);
                    if (profileWalletForMessaging) {
                      console.log('✅ [Marker Click] Found wallet address:', profileWalletForMessaging);
                    }
                  } catch (error) {
                    console.warn('⚠️ [Marker Click] Could not get wallet address:', error);
                  }
                }
                
                // Show small loading indicator immediately when switching profiles
                setIsLoadingProfileFromIPFS(true);
                setProfileVisible(true);
                
                // Set timeout to prevent infinite loading
                const loadingTimeout = setTimeout(() => {
                  console.warn('⚠️ [Marker Click] Loading timeout - clearing loading state');
                  setIsLoadingProfileFromIPFS(false);
                }, 10000); // 10 second timeout
                
                // Load full profile data using loadUserProfile (handles IPFS loading properly)
                if (profileWalletForMessaging) {
                  console.log('🔄 [Marker Click] Loading full profile data for:', profileWalletForMessaging);
                  try {
                    await loadUserProfile(profileWalletForMessaging, true); // skipUnifiedAuth=true
                    clearTimeout(loadingTimeout); // Clear timeout on success
                  } catch (error) {
                    console.error('❌ [Marker Click] Error loading profile:', error);
                    clearTimeout(loadingTimeout);
                    // On error, show basic profile data and clear loading state
                    setCurrentUser(null);
                    setCurrentProfile(cleanOtherProfile);
                    setIsLoadingProfileFromIPFS(false);
                  }
                } else {
                  // Fallback: show basic profile data if no wallet address
                  console.warn('⚠️ [Marker Click] No wallet address, showing basic profile data');
                  setCurrentUser(null);
                  setCurrentProfile(cleanOtherProfile);
                  setIsLoadingProfileFromIPFS(false);
                }
              }
              
              // Track profile view for gamification
              if (walletAddress) {
                trackProfileViewed(walletAddress).then(result => {
                  if (result && result.updatedData) {
                    setGamificationData(result.updatedData);
                    if (result.newBadges && result.newBadges.length > 0) {
                      setNewBadges(result.newBadges);
                      setTimeout(() => setNewBadges([]), 5000);
                    }
                  }
                }).catch(err => {
                  console.error('Error tracking profile view:', err);
                });
              }
            }
          } else {
            // Allow viewing profiles even when wallet is not connected.
            // Only interactive actions (message/review/report) should be disabled.
            const cleanOtherProfile = cleanProfileData(profile);
            
            // Get wallet address for loading profile
            let profileWalletForMessaging = cleanOtherProfile.walletAddress;
            if (!profileWalletForMessaging && cleanOtherProfile.username) {
              try {
                profileWalletForMessaging = await getWalletFromUsername(cleanOtherProfile.username);
              } catch (error) {
                console.warn('⚠️ [Marker Click] Could not get wallet address:', error);
              }
            }
            
            // Navigate to profile URL immediately for instant feedback
            if (cleanOtherProfile.username) {
              navigate(`/profile/${cleanOtherProfile.username}`);
            }
            
            clearChangeTrackingState();
            // Close activity feed FIRST to prevent overlap during loading
            setActivityFeedVisible(false);
            setAboutVisible(false);
            setGroupsVisible(false);
            setMessagesVisible(false);
            setBugVisible(false);
            setAiAssistantVisible(false);
            
            // For other users' profiles, don't show big loading screen
            // loadUserProfile will show profile from Firestore immediately without loading screen
            setIsLoadingProfileFromIPFS(false); // Don't show loading screen for other users
            setProfileVisible(true);
            
            // REVISED: Clear "My Profile" flag since we're loading another user's profile
            isLoadingMyProfileRef.current = false;
            
            // CRITICAL FIX: Check if this is a test user - if so, use profile data directly
            // Test users should never show registration form, they always have data in allProfiles
            // Check both original profile and cleaned profile for _isTestRecord flag
            const isTestUser = (profile._isTestRecord === true) || (cleanOtherProfile._isTestRecord === true);
            if (isTestUser) {
              console.log('✅ [Marker Click] Test user detected - using profile data directly', {
                originalHasFlag: profile._isTestRecord === true,
                cleanedHasFlag: cleanOtherProfile._isTestRecord === true
              });
              setCurrentUser(null); // Test users are never "My Profile"
              setCurrentProfile(cleanOtherProfile);
              setShowRegistrationForm(false);
              setIsLoadingProfileFromIPFS(false);
              setIsLoadingUser(false);
              setProfileLoadError(null);
            } else if (profileWalletForMessaging) {
              // For non-test users, load full profile data using loadUserProfile
              console.log('🔄 [Marker Click] Loading full profile data for:', profileWalletForMessaging);
              try {
                await loadUserProfile(profileWalletForMessaging, true); // skipUnifiedAuth=true
              } catch (error) {
                console.error('❌ [Marker Click] Error loading profile:', error);
                // On error, show basic profile data
                setCurrentUser(null);
                setCurrentProfile(cleanOtherProfile);
                setIsLoadingProfileFromIPFS(false);
              }
            } else {
              // Fallback: show basic profile data if no wallet address
              console.warn('⚠️ [Marker Click] No wallet address, showing basic profile data');
              setCurrentUser(null);
              setCurrentProfile(cleanOtherProfile);
              setIsLoadingProfileFromIPFS(false);
            }
            
            setNotification({ message: 'Connect wallet to message, leave reviews, or report users.', type: 'info' });
            setTimeout(() => setNotification(null), 4000);
          }
          if (mapInstanceRef.current) {
            // Pan to location without changing zoom level
            mapInstanceRef.current.panTo([lat, lng]);
            marker.openPopup();
          }
        });
        
        return marker;
      };
      
      const createMarkerBatch = () => {
        const endIndex = Math.min(currentIndex + BATCH_SIZE, profilesToProcess.length);
        
        for (let i = currentIndex; i < endIndex; i++) {
          const profile = profilesToProcess[i];
          
          const marker = createProfileMarker(profile);
          if (marker) {
            markers.push(marker);
            profilesWithMarkers.push(profile);
          }
        }
        
        currentIndex = endIndex;
        
        if (currentIndex < profilesToProcess.length) {
          // Small delay between batches to prevent freezes
          markerCreationBatchRef.current = setTimeout(createMarkerBatch, 10);
        } else {
          // All markers created, update state once
          markersRef.current = markers;
          
          // Batch state updates to prevent multiple re-renders
          setAllProfiles(profilesWithMarkers);
          setFilteredProfiles([]); // Empty initially, will be populated by filter effect
          
          console.log('Created markers:', markers.length);
          markerCreationBatchRef.current = null;
          
          // Markers are already added to cluster group, no need to add them again
          // The cluster group handles visibility automatically based on zoom level
        }
      };
      
      // Start creating markers in batches with initial delay
      markerCreationBatchRef.current = setTimeout(createMarkerBatch, 10);
    } catch (error) {
      console.error('Error loading profiles:', error);
    }
  };

  // Cleanup city suggestions timeout on unmount
  useEffect(() => {
    return () => {
      if (citySuggestionsTimeoutRef.current) {
        clearTimeout(citySuggestionsTimeoutRef.current);
      }
    };
  }, []);

  // Sync URL with search query when on /search routes (only when user types, not when set from URL)
  useEffect(() => {
    // Skip URL sync if we're setting searchQuery from URL to prevent loops
    if (isSettingSearchFromUrlRef.current) {
      isSettingSearchFromUrlRef.current = false; // Reset flag immediately
      return;
    }
    
    const pathname = getNormalizedPathname();
    if (pathname.startsWith('/search')) {
      const trimmedQuery = searchQuery.trim();
      
      // Get current query from URL to compare
      const currentQuery = pathname.startsWith('/search/') 
        ? decodeURIComponent(pathname.replace('/search/', ''))
        : '';
      
      // Only navigate if the searchQuery doesn't match what's already in the URL
      if (trimmedQuery) {
        if (currentQuery !== trimmedQuery) {
          // Update URL to /search/:query
          const encodedQuery = encodeURIComponent(trimmedQuery);
          mobileNavigate(`/search/${encodedQuery}`, { replace: true });
        }
      } else {
        // Clear query - navigate to /search only if not already there
        if (pathname !== '/search') {
          mobileNavigate('/search', { replace: true });
        }
      }
    }
  }, [searchQuery, getNormalizedPathname, mobileNavigate]);

  // Debounce search query to avoid filtering on every keystroke
  useEffect(() => {
    // Set loading state when search query changes
    if (searchQuery && searchQuery.trim() !== '') {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
    
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Invalidate map size when profile visibility changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      setTimeout(() => {
        mapInstanceRef.current.invalidateSize();
      }, 300);
    }
  }, [profileVisible]);

  // Highlight the marker of the currently-opened profile with the blue pin;
  // all other markers use the default tan circle icon that matches clusters.
  useEffect(() => {
    if (!window.L) return;

    // Reset previously highlighted marker back to the default icon
    if (selectedMarkerRef.current) {
      try {
        selectedMarkerRef.current.setIcon(createDefaultProfileIcon(window.L));
      } catch (e) {
        // marker may have been removed; ignore
      }
      selectedMarkerRef.current = null;
    }

    if (!profileVisible || !currentProfile || !markersRef.current || markersRef.current.length === 0) {
      return;
    }

    const wallet = (currentProfile.walletAddress || '').toLowerCase();
    const username = currentProfile.username || '';

    const match = markersRef.current.find(m => {
      if (wallet && m._profileWallet && m._profileWallet === wallet) return true;
      if (username && m._profileUsername && m._profileUsername === username) return true;
      return false;
    });

    if (match) {
      try {
        match.setIcon(createSelectedProfileIcon(window.L));
        selectedMarkerRef.current = match;
      } catch (e) {
        // ignore
      }
    }
  }, [currentProfile, profileVisible]);

  // Set avatar loading state when profile changes
  useEffect(() => {
    if (currentProfile && getAvatarFromProfile(currentProfile)) {
      setIsAvatarLoading(true);
      // Check if image is already loaded after a brief delay to allow DOM update
      setTimeout(() => {
        if (avatarImgRef.current && avatarImgRef.current.complete) {
          setIsAvatarLoading(false);
        }
      }, 0);
    } else {
      setIsAvatarLoading(false);
    }
  }, [currentProfile]);

  // Filter profiles or groups based on debounced search query
  useEffect(() => {
    // Handle groups search separately
    if (searchType === 'groups') {
      if (!debouncedSearchQuery || debouncedSearchQuery.trim() === '') {
        setFilteredGroups([]);
        setFilteredProfiles([]);
        setIsSearching(false);
      } else {
        setIsSearching(true);
        const query = debouncedSearchQuery.trim().toLowerCase();
        
        // Search groups using discoverGroups
        (async () => {
          try {
            // Extract tags from query (support + for multiple tags)
            const hasMultipleTerms = query.includes('+');
            let searchTags = [];
            
            if (hasMultipleTerms) {
              searchTags = query.split('+').map(term => term.trim()).filter(term => term.length > 0);
            } else if (query === '*') {
              // For "*", search all groups (empty tags array means no tag filter)
              searchTags = [];
            } else {
              searchTags = [query];
            }
            
            // Remove # prefix from tags if present
            searchTags = searchTags.map(tag => tag.startsWith('#') ? tag.substring(1) : tag);
            
            const groups = await discoverGroups(searchTags, null, 100);
            setFilteredGroups(groups);
            setFilteredProfiles([]);
            setIsSearching(false);
          } catch (error) {
            console.error('Error searching groups:', error);
            setFilteredGroups([]);
            setFilteredProfiles([]);
            setIsSearching(false);
          }
        })();
      }
    } else {
      // Original people search logic
      if (allProfiles.length === 0) {
        setIsSearching(false);
        return;
      }

    if (!debouncedSearchQuery || debouncedSearchQuery.trim() === '') {
      // When search is empty, don't show any results in the list
      setFilteredProfiles([]);
      setFilteredGroups([]);
      setShowCatAds(false);
      setShowCookingAds(false);
      setIsSearching(false);
      // Markers are managed by cluster group, no need to add them directly
      // The cluster group automatically shows/hides markers based on zoom level
      return;
    }
    
    // Set loading state at the start of filtering
    setIsSearching(true);

    const query = debouncedSearchQuery.trim().toLowerCase();
    
    // Check if search is for "cats" - show cat ads
    if (query === 'cats' || query.includes('cat')) {
      setShowCatAds(true);
      setShowCookingAds(false);
    } else if (query === 'cooking' || query.includes('cook')) {
      // Check if search is for "cooking" - show cooking ads
      setShowCookingAds(true);
      setShowCatAds(false);
    } else {
      setShowCatAds(false);
      setShowCookingAds(false);
    }
    
    // If query is "*", show all profiles - optimized batch operation
    if (query === '*') {
      const allAfterTestFilter = excludeTestUsers
        ? allProfiles.filter(p => {
            if (p?._isTestRecord) return false;
            // A profile should be visible if it has ANY published data
            const hasPublishedData = 
              (Array.isArray(p.tagsList) && p.tagsList.length > 0) ||
              (p.city && p.city.trim() !== '') ||
              (p.name && p.name.trim() !== '') ||
              (p.bio && p.bio.trim() !== '') ||
              (Array.isArray(p.socialLinks) && p.socialLinks.length > 0) ||
              (p.birthDate && p.birthDate.trim() !== '');
            return hasPublishedData;
          })
        : allProfiles.filter(p => {
            // A profile should be visible if it has ANY published data
            const hasPublishedData = 
              (Array.isArray(p.tagsList) && p.tagsList.length > 0) ||
              (p.city && p.city.trim() !== '') ||
              (p.name && p.name.trim() !== '') ||
              (p.bio && p.bio.trim() !== '') ||
              (Array.isArray(p.socialLinks) && p.socialLinks.length > 0) ||
              (p.birthDate && p.birthDate.trim() !== '');
            return hasPublishedData;
          });
      setFilteredProfiles(allAfterTestFilter);
      setIsSearching(false);
      // Markers are managed by cluster group, no need to add them directly
      return;
    }

    // Check if query contains "+" for multiple tag search
    const hasMultipleTerms = query.includes('+');
    let searchTerms = [];
    
    if (hasMultipleTerms) {
      // Split by "+" and trim each term
      searchTerms = query.split('+').map(term => term.trim()).filter(term => term.length > 0);
    } else {
      // Single term search
      searchTerms = [query];
    }

    // Pre-process search terms for better performance
    const processedSearchTerms = searchTerms.map(term => {
      const searchTerm = term.startsWith('#') ? term.substring(1) : term;
      const tagTerm = term.startsWith('#') ? term : `#${term}`;
      return {
        original: term,
        searchTerm: searchTerm.toLowerCase(),
        tagTerm: tagTerm.toLowerCase()
      };
    });
    
    // Filter profiles by tags or groups - optimized with early returns
    const filtered = allProfiles.filter(profile => {
      // Apply "No test users" filter
      if (excludeTestUsers && profile?._isTestRecord) {
        return false;
      }
      
      // A profile should be visible if it has ANY published data (tags, city, name, bio, etc.)
      // Wallet publication status only controls whether wallet address is shown, not profile visibility
      const hasPublishedData = 
        (Array.isArray(profile.tagsList) && profile.tagsList.length > 0) ||
        (profile.city && profile.city.trim() !== '') ||
        (profile.name && profile.name.trim() !== '') ||
        (profile.bio && profile.bio.trim() !== '') ||
        (Array.isArray(profile.socialLinks) && profile.socialLinks.length > 0) ||
        (profile.birthDate && profile.birthDate.trim() !== '');
      
      // Only exclude profiles that have NO published data at all
      if (!hasPublishedData) {
        return false; // Exclude profiles with no published data
      }
      // Ensure tagsList and groups are arrays
      const tagsList = Array.isArray(profile.tagsList) ? profile.tagsList : [];
      const groups = Array.isArray(profile.groups) ? profile.groups : [];
      
      // Early return if no tags or groups
      if (tagsList.length === 0 && groups.length === 0) {
        return false;
      }
      
      // Pre-process tags and groups into lowercase for faster matching
      const tagsLower = tagsList
        .filter(tag => tag && typeof tag === 'string')
        .map(tag => tag.toLowerCase());
      const groupsLower = groups
        .filter(group => group && typeof group === 'string')
        .map(group => group.toLowerCase());
      
      if (hasMultipleTerms) {
        // AND logic: all terms must be found in tags or groups
        return processedSearchTerms.every(({ searchTerm, tagTerm }) => {
          // Check if term matches any tag (with or without #)
          const tagsMatch = tagsLower.some(tagLower => 
            tagLower.includes(tagTerm) || tagLower.includes(searchTerm)
          );
          
          // Check if term matches any group
          const groupsMatch = groupsLower.some(groupLower => 
            groupLower.includes(searchTerm)
          );
          
          return tagsMatch || groupsMatch;
        });
      } else {
        // OR logic: single term matches tags or groups (original behavior)
        const { searchTerm, tagTerm } = processedSearchTerms[0];
        
        // Check tags
        const tagsMatch = tagsLower.some(tagLower => 
          tagLower.includes(tagTerm) || tagLower.includes(searchTerm)
        );
        
        // Check groups
        const groupsMatch = groupsLower.some(groupLower => 
          groupLower.includes(searchTerm)
        );
        
        return tagsMatch || groupsMatch;
      }
    });

    setFilteredProfiles(filtered);
    setFilteredGroups([]); // Clear groups when searching people
    
    // Clear loading state after filtering is complete
    // Use setTimeout to ensure state update happens after filtering
    setTimeout(() => {
      setIsSearching(false);
    }, 0);

    // Optimize marker visibility updates using Set for O(1) lookup
    const filteredProfileIds = new Set();
    filtered.forEach(profile => {
      // Use username as unique identifier
      if (profile.username) {
        filteredProfileIds.add(profile.username);
      }
      // Also use walletAddress if available
      if (profile.walletAddress) {
        filteredProfileIds.add(profile.walletAddress.toLowerCase());
      }
    });
    
    // Batch marker visibility updates for better performance
    // Use requestAnimationFrame to batch DOM operations and prevent freezes
    requestAnimationFrame(() => {
      if (!mapInstanceRef.current) return;
      
      const map = mapInstanceRef.current;
      const markersToShow = [];
      const markersToHide = [];
      
      // Manage marker visibility through cluster group
      if (markerClusterGroupRef.current) {
        allProfiles.forEach((profile) => {
          const marker = profile.marker;
          if (!marker) return;
          
          const isFiltered = profile.username && filteredProfileIds.has(profile.username) ||
                            profile.walletAddress && filteredProfileIds.has(profile.walletAddress.toLowerCase());
          
          const isInCluster = markerClusterGroupRef.current.hasLayer(marker);
          
          if (isFiltered && !isInCluster) {
            // Add marker back to cluster group
            markerClusterGroupRef.current.addLayer(marker);
          } else if (!isFiltered && isInCluster) {
            // Remove marker from cluster group
            markerClusterGroupRef.current.removeLayer(marker);
          }
        });
      } else {
        // Fallback: manage markers directly on map if cluster group not available
        allProfiles.forEach((profile) => {
          const marker = profile.marker;
          if (!marker) return;
          
          const isFiltered = profile.username && filteredProfileIds.has(profile.username) ||
                            profile.walletAddress && filteredProfileIds.has(profile.walletAddress.toLowerCase());
          
          const isOnMap = map.hasLayer(marker);
          
          if (isFiltered && !isOnMap) {
            markersToShow.push(marker);
          } else if (!isFiltered && isOnMap) {
            markersToHide.push(marker);
          }
        });
        
        // Apply marker visibility changes in batches
        // Process in chunks to prevent freezes with 1000+ markers
        const processBatch = (items, operation, batchSize = 50) => {
          let index = 0;
          const processChunk = () => {
            const end = Math.min(index + batchSize, items.length);
            for (let i = index; i < end; i++) {
              operation(items[i]);
            }
            index = end;
            if (index < items.length) {
              setTimeout(processChunk, 0);
            }
          };
          processChunk();
        };
        
        // Process show operations
        if (markersToShow.length > 0) {
          processBatch(markersToShow, (marker) => {
            if (map && !map.hasLayer(marker)) {
              marker.addTo(map);
            }
          });
        }
        
        // Process hide operations
        if (markersToHide.length > 0) {
          processBatch(markersToHide, (marker) => {
            if (map && map.hasLayer(marker)) {
              marker.remove();
            }
          });
        }
      }
    });
    }
  }, [debouncedSearchQuery, allProfiles, excludeTestUsers, searchType]);

  // Group filtered profiles by city and sort by distance from user's city
  // Profiles without published city are shown at the end without city and distance
  // Helper function to truncate city name if too long
  const truncateCityName = (cityName, maxLength = 20) => {
    if (!cityName) return '';
    if (cityName.length <= maxLength) return cityName;
    return cityName.substring(0, maxLength) + '...';
  };
  
  const truncateName = (name, maxLength = 25) => {
    if (!name) return '';
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  };

  const truncateSongName = (song, maxLength = 40) => {
    if (!song) return '';
    if (song.length <= maxLength) return song;
    return song.substring(0, maxLength) + '...';
  };

  // Calculate match percentage between current user's profile and another profile
  // Returns what percentage of YOUR tags match with the other profile
  // Optimized with useCallback to prevent unnecessary re-renders
  const calculateMatchPercentage = useCallback((myProfile, otherProfile) => {
    if (!myProfile || !otherProfile) return 0;
    
    // Only consider tags for match calculation
    const myTags = Array.isArray(myProfile.tagsList) ? myProfile.tagsList.map(t => t.toLowerCase().trim()) : [];
    const otherTags = Array.isArray(otherProfile.tagsList) ? otherProfile.tagsList.map(t => t.toLowerCase().trim()) : [];
    
    // Early return if no tags
    if (myTags.length === 0) return 0;
    
    // Use Set for O(1) lookup instead of includes (O(n))
    const otherTagsSet = new Set(otherTags);
    
    // Calculate how many of YOUR tags match with the other profile
    const tagMatches = myTags.filter(tag => otherTagsSet.has(tag)).length;
    
    // Calculate match percentage as percentage of YOUR tags that match
    // Example: If you have 6 tags and 2 match, that's 2/6 = 33.33%
    const matchPercentage = (tagMatches / myTags.length) * 100;
    return Math.round(matchPercentage * 100) / 100; // Round to 2 decimal places
  }, []);

  // Helper function to safely clone profile data without circular references (excludes marker property)
  const safeCloneProfile = (profileData) => {
    if (!profileData) return null;
    // Create a copy without the marker property (which has circular references)
    const { marker, ...profileWithoutMarker } = profileData;
    try {
      return JSON.parse(JSON.stringify(profileWithoutMarker));
    } catch (error) {
      console.warn('Error cloning profile, using shallow copy:', error);
      // Fallback to shallow copy if JSON operations fail
      return { ...profileWithoutMarker };
    }
  };

  // Helper function to determine if current profile is "My Profile"
  // Uses multiple checks to prevent race conditions and "other profile" flash
  const isMyProfile = () => {
    if (!currentProfile) return false;
    
    // Check 1: Standard check - currentUser matches profile username
    if (currentUser && currentProfile.username === currentUser) {
      return true;
    }
    
    // Check 2: Ref flag - we're actively loading "My Profile"
    if (isLoadingMyProfileRef.current) {
      return true;
    }
    
    // Check 3: Wallet address match - profile wallet matches connected wallet
    if (walletAddress && currentProfile.walletAddress) {
      const normalizedWallet = walletAddress.toLowerCase();
      const normalizedProfileWallet = currentProfile.walletAddress.toLowerCase();
      if (normalizedWallet === normalizedProfileWallet) {
        return true;
      }
    }
    
    return false;
  };

  // Helper function to ensure clean profile structure (prevents data leakage between profiles)
  const cleanProfileData = (profileData) => {
    if (!profileData) return null;
    // Get photos array (with backward compatibility)
    const photos = getPhotosFromProfile(profileData);
    
    // Debug: Log what we're cleaning
    console.log('🧹 Cleaning profile data:', {
      hasTagsList: Array.isArray(profileData.tagsList),
      tagsListLength: profileData.tagsList?.length || 0,
      hasMusicList: Array.isArray(profileData.musicList),
      musicListLength: profileData.musicList?.length || 0,
      hasSocialLinks: Array.isArray(profileData.socialLinks),
      socialLinksLength: profileData.socialLinks?.length || 0,
      hasBirthDate: !!profileData.birthDate,
      hasEmail: !!profileData.email,
      hasCity: !!profileData.city
    });
    
    // IMPORTANT: Preserve ALL fields from profileData using spread operator first
    // Then explicitly ensure arrays exist (but don't override if they're already arrays)
    const cleaned = {
      ...profileData, // Preserve ALL fields first (tagsList, musicList, socialLinks, birthDate, email, city, location, etc.)
      // Explicitly ensure these fields exist as arrays (even if empty)
      // This prevents undefined/null values from causing issues when switching profiles
      tagsList: Array.isArray(profileData.tagsList) ? profileData.tagsList : (profileData.tagsList || []),
      socialLinks: Array.isArray(profileData.socialLinks) ? profileData.socialLinks : (profileData.socialLinks || []),
      musicList: Array.isArray(profileData.musicList) ? profileData.musicList : (profileData.musicList || []),
      groups: Array.isArray(profileData.groups) ? profileData.groups : (profileData.groups || []),
      additionalPersonalData: Array.isArray(profileData.additionalPersonalData) ? profileData.additionalPersonalData : (profileData.additionalPersonalData || []),
      photos: photos, // Ensure photos array exists
      // Preserve other fields (don't set to null if they exist)
      bio: profileData.bio !== undefined ? profileData.bio : null,
      birthDate: profileData.birthDate !== undefined ? profileData.birthDate : null,
      age: profileData.age !== undefined ? profileData.age : null,
      email: profileData.email !== undefined ? profileData.email : null,
      city: profileData.city !== undefined ? profileData.city : null,
      location: profileData.location !== undefined ? profileData.location : null,
      // Preserve ipfsHash if it exists
      ipfsHash: profileData.ipfsHash || null
    };
    
    console.log('✅ Cleaned profile data:', {
      finalTagsListLength: cleaned.tagsList?.length || 0,
      finalMusicListLength: cleaned.musicList?.length || 0,
      finalSocialLinksLength: cleaned.socialLinks?.length || 0,
      hasBirthDate: !!cleaned.birthDate,
      hasEmail: !!cleaned.email
    });
    
    return cleaned;
  };

  // Filter profile to only include published fields for other users
  const filterProfileForOtherUsers = (profile, firestoreMetadata) => {
    const isTestUser = profile._isTestRecord === true;
    
    // For test users, show all available fields from profile (don't filter by publish status)
    if (isTestUser) {
      return {
        ...profile,
        _isTestRecord: true,
        // Ensure arrays exist
        tagsList: Array.isArray(profile.tagsList) ? profile.tagsList : [],
        socialLinks: Array.isArray(profile.socialLinks) ? profile.socialLinks : [],
        musicList: Array.isArray(profile.musicList) ? profile.musicList : [],
        groups: Array.isArray(profile.groups) ? profile.groups : [],
        photos: Array.isArray(profile.photos) ? profile.photos : []
      };
    }
    
    if (!firestoreMetadata) {
      // If no Firestore metadata, return profile with only basic fields (username, walletAddress)
      return {
        username: profile.username,
        walletAddress: profile.walletAddress,
        ipfsHash: profile.ipfsHash || null,
        location: profile.location || null,
        photos: profile.photos || [],
        _isTestRecord: profile._isTestRecord === true ? true : false // Preserve test user flag
      };
    }

    // Create filtered profile with only published fields (for non-test users)
    const filtered = {
      username: profile.username,
      walletAddress: profile.walletAddress,
      ipfsHash: profile.ipfsHash || null,
      location: profile.location || null,
      photos: profile.photos || [],
      _isTestRecord: profile._isTestRecord === true ? true : false, // Preserve test user flag
      // Simple fields: only include if published (exists in Firestore)
      name: firestoreMetadata.name && firestoreMetadata.name.trim() !== '' ? profile.name : null,
      bio: firestoreMetadata.bio && firestoreMetadata.bio.trim() !== '' ? profile.bio : null,
      email: firestoreMetadata.email && firestoreMetadata.email.trim() !== '' ? profile.email : null,
      preferredContactMethod: firestoreMetadata.preferredContactMethod && firestoreMetadata.preferredContactMethod.trim() !== '' ? profile.preferredContactMethod : null,
      relationshipStatus: firestoreMetadata.relationshipStatus && firestoreMetadata.relationshipStatus.trim() !== '' ? profile.relationshipStatus : null,
      lookingFor: firestoreMetadata.lookingFor && firestoreMetadata.lookingFor.trim() !== '' ? profile.lookingFor : null,
      languages: firestoreMetadata.languages && firestoreMetadata.languages.trim() !== '' ? profile.languages : null,
      education: firestoreMetadata.education && firestoreMetadata.education.trim() !== '' ? profile.education : null,
      occupation: firestoreMetadata.occupation && firestoreMetadata.occupation.trim() !== '' ? profile.occupation : null,
      // Wallet: only include if published flag is set
      ...(firestoreMetadata._isWalletPublishedInFirestore === true && profile.walletAddress ? { walletAddress: profile.walletAddress } : {}),
      // Array fields: filter to only published items
      tagsList: (profile.tagsList || []).filter(tag => (firestoreMetadata.tagsList || []).includes(tag)),
      socialLinks: (profile.socialLinks || []).filter(social => (firestoreMetadata.socialLinks || []).includes(social)),
      musicList: (profile.musicList || []).filter(song => (firestoreMetadata.musicList || []).includes(song)),
      groups: (profile.groups || []).filter(group => (firestoreMetadata.groups || []).includes(group)),
      // BirthDate and City: only include if published (match Firestore value)
      birthDate: (firestoreMetadata.birthDate && firestoreMetadata.birthDate === profile.birthDate) ? profile.birthDate : null,
      age: (firestoreMetadata.birthDate && firestoreMetadata.birthDate === profile.birthDate && profile.age != null) ? profile.age : null,
      city: (firestoreMetadata.city && firestoreMetadata.city === profile.city) ? profile.city : null,
      // Additional personal data: preserve if exists (no publish filter for this field)
      additionalPersonalData: profile.additionalPersonalData || []
    };

    return filtered;
  };

  // Load profile by username when /profile/:username route is accessed
  // Use ref to track last loaded profile to prevent infinite loops
  const lastLoadedProfileUsernameRef = useRef(null);
  
  useEffect(() => {
    const pathname = getNormalizedPathname();
    
    // IMPORTANT: Skip if pathname is /myprofile - that's handled by a different useEffect
    if (pathname === '/myprofile') {
      // Reset ref when navigating away from profile routes
      lastLoadedProfileUsernameRef.current = null;
      return;
    }
    
    if (pathname.startsWith('/profile/')) {
      const username = pathname.replace('/profile/', '').trim();
      
      // Reset ref if currentProfile is null (e.g., on page refresh)
      if (!currentProfile) {
        lastLoadedProfileUsernameRef.current = null;
      }
      // Skip if we've already loaded this exact profile AND currentProfile exists
      // On refresh, currentProfile will be null, so we should always load
      if (lastLoadedProfileUsernameRef.current === username && currentProfile && currentProfile.username === username) {
        return;
      }
      // Always load if we have a username (ignore currentProfile to ensure we always load from URL)
      if (username) {
        // Mark as loading
        lastLoadedProfileUsernameRef.current = username;
        // Load profile by username
        const loadProfileByUsername = async () => {
          try {
            console.log('🔄 [URL Profile] Loading profile by username from URL:', username);
            // REVISED: Clear "My Profile" flag initially - will be set by loadUserProfile if it's actually "My Profile"
            isLoadingMyProfileRef.current = false;
            // Ensure profile section stays visible during loading
            setProfileVisible(true);
            // Set loading state to true while we're loading the profile
            setIsLoadingProfileFromIPFS(true);
            setProfileLoadError(null); // Clear any previous errors
            
            // FIRST: Check if profile exists in allProfiles array (same as map marker click)
            // This is the fastest and most reliable method for profiles visible on the map
            // IMPORTANT: Wait a bit if allProfiles is empty - it might be loading
            let profileData = null;
            
            // If allProfiles is empty, wait a short time for it to populate (handles timing issues)
            if (allProfiles.length === 0) {
              // Wait up to 2 seconds for allProfiles to populate (check every 200ms)
              for (let i = 0; i < 10; i++) {
                await new Promise(resolve => setTimeout(resolve, 200));
                if (allProfiles.length > 0) {
                  break;
                }
              }
            }
            
            if (allProfiles.length > 0) {
              try {
                console.log('🔄 [URL Profile] Checking loaded profiles array first (same as map marker click)');
                
                const foundProfile = allProfiles.find(profile => 
                  profile.username && profile.username.toLowerCase() === username.toLowerCase()
                );
                
                if (foundProfile) {
                  console.log('✅ [URL Profile] Found profile in loaded profiles array - loading full data using loadUserProfile');
                  const cleanOtherProfile = cleanProfileData(foundProfile);
                  
                  // CRITICAL FIX: Check if this is a test user - if so, use profile data directly
                  // Test users should never show registration form, they always have data in allProfiles
                  // Check both original profile and cleaned profile for _isTestRecord flag
                  const isTestUser = (foundProfile._isTestRecord === true) || (cleanOtherProfile._isTestRecord === true);
                  if (isTestUser) {
                    console.log('✅ [URL Profile] Test user detected - using profile data directly', {
                      originalHasFlag: foundProfile._isTestRecord === true,
                      cleanedHasFlag: cleanOtherProfile._isTestRecord === true
                    });
                    setCurrentUser(null); // Test users are never "My Profile"
                    setCurrentProfile(cleanOtherProfile);
                    setShowRegistrationForm(false);
                    setIsLoadingProfileFromIPFS(false);
                    setIsLoadingUser(false);
                    setProfileLoadError(null);
                    setAboutVisible(false);
                    setGroupsVisible(false);
                    setMessagesVisible(false);
                    setBugVisible(false);
                    setProfileVisible(true);
                    
                    // Center map on profile location if available
                    if (cleanOtherProfile.location && cleanOtherProfile.location.lat && cleanOtherProfile.location.lng) {
                      const centerMap = () => {
                        if (mapInstanceRef.current) {
                          console.log('🗺️ [URL Profile] Centering map on profile location:', cleanOtherProfile.location.lat, cleanOtherProfile.location.lng);
                          mapInstanceRef.current.panTo([cleanOtherProfile.location.lat, cleanOtherProfile.location.lng]);
                        } else {
                          setTimeout(centerMap, 200);
                        }
                      };
                      setTimeout(centerMap, 100);
                    }
                    return; // Exit early - test user profile loaded
                  }
                  
                  // Get wallet address for loading profile
                  let profileWalletForMessaging = cleanOtherProfile.walletAddress;
                  if (!profileWalletForMessaging && cleanOtherProfile.username) {
                    try {
                      profileWalletForMessaging = await getWalletFromUsername(cleanOtherProfile.username);
                    } catch (error) {
                      console.warn('⚠️ [URL Profile] Could not get wallet address:', error);
                    }
                  }
                  
                  // For other users' profiles, don't show big loading screen
                  // loadUserProfile will show profile from Firestore immediately without loading screen
                  setIsLoadingProfileFromIPFS(false); // Don't show loading screen for other users
                  
                  // Load full profile data using loadUserProfile (handles IPFS loading properly)
                  if (profileWalletForMessaging) {
                    console.log('🔄 [URL Profile] Loading full profile data for:', profileWalletForMessaging);
                    try {
                      await loadUserProfile(profileWalletForMessaging, true); // skipUnifiedAuth=true
                      
                      // Center map on profile location if available
                      if (cleanOtherProfile.location && cleanOtherProfile.location.lat && cleanOtherProfile.location.lng) {
                        const centerMap = () => {
                          if (mapInstanceRef.current) {
                            console.log('🗺️ [URL Profile] Centering map on profile location:', cleanOtherProfile.location.lat, cleanOtherProfile.location.lng);
                            mapInstanceRef.current.panTo([cleanOtherProfile.location.lat, cleanOtherProfile.location.lng]);
                          } else {
                            setTimeout(centerMap, 200);
                          }
                        };
                        setTimeout(centerMap, 100);
                      }
                      
                      console.log('✅ [URL Profile] Profile loaded successfully using loadUserProfile');
                      return; // Exit early - profile loaded successfully
                    } catch (error) {
                      console.error('❌ [URL Profile] Error loading profile:', error);
                      setIsLoadingProfileFromIPFS(false);
                      // Show fallback profile
                      setCurrentUser(null);
                      setCurrentProfile(cleanOtherProfile);
                      setAboutVisible(false);
                      setGroupsVisible(false);
                      setMessagesVisible(false);
                      setBugVisible(false);
                      setProfileLoadError(null);
                      setProfileVisible(true);
                      return;
                    }
                  } else {
                    // Fallback: show basic profile data if no wallet address
                    console.warn('⚠️ [URL Profile] No wallet address, showing basic profile data');
                    setCurrentUser(null);
                    setCurrentProfile(cleanOtherProfile);
                    setAboutVisible(false);
                    setGroupsVisible(false);
                    setMessagesVisible(false);
                    setBugVisible(false);
                    setIsLoadingProfileFromIPFS(false);
                    setProfileLoadError(null);
                    setProfileVisible(true);
                    return;
                  }
                }
              } catch (searchError) {
                console.warn('⚠️ [URL Profile] Error searching in loaded profiles:', searchError);
              }
            }
            
            // If not found in allProfiles, fall back to Firestore/IPFS loading
            console.log('🔄 [URL Profile] Profile not found in allProfiles, trying Firestore/IPFS...');
            
            // Get wallet address from username
            let profileWallet = null;
            try {
              profileWallet = await getWalletFromUsername(username);
              console.log('📝 [URL Profile] Wallet address for username:', profileWallet ? `found: ${profileWallet}` : 'not found');
            } catch (walletError) {
              console.error('❌ [URL Profile] Error getting wallet from username:', walletError);
            }
            
            // Fallback: If wallet lookup failed, try to find wallet in allProfiles array
            // This handles test users or profiles that might not be in the walletUsers mapping
            if (!profileWallet && allProfiles.length > 0) {
              try {
                console.log('🔄 [URL Profile] Trying to find wallet address in loaded profiles array');
                const foundProfile = allProfiles.find(profile => 
                  profile.username && profile.username.toLowerCase() === username.toLowerCase()
                );
                if (foundProfile && foundProfile.walletAddress) {
                  profileWallet = foundProfile.walletAddress;
                  console.log('✅ [URL Profile] Found wallet address in loaded profiles:', profileWallet);
                } else if (foundProfile) {
                  // Check for wallet in other possible fields
                  const possibleWalletFields = ['wallet', 'address', 'walletAddress', 'ethAddress', 'ethereumAddress'];
                  for (const field of possibleWalletFields) {
                    if (foundProfile[field] && typeof foundProfile[field] === 'string' && foundProfile[field].startsWith('0x')) {
                      profileWallet = foundProfile[field];
                      console.log(`✅ [URL Profile] Found wallet address in field '${field}':`, profileWallet);
                      break;
                    }
                  }
                }
              } catch (searchError) {
                console.warn('⚠️ [URL Profile] Error searching for wallet in loaded profiles:', searchError);
              }
            }
            
            // Fallback: If still no wallet, try searching Firestore profiles collection by username
            if (!profileWallet) {
              try {
                console.log('🔄 [URL Profile] Trying to find wallet address in Firestore profiles collection');
                const profilesRef = collection(db, 'profiles');
                const q = query(profilesRef, where('username', '==', username));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                  const profileDoc = querySnapshot.docs[0];
                  const profileData = profileDoc.data();
                  profileWallet = profileData.walletAddress || profileData._ownerAddress;
                  if (profileWallet) {
                    console.log('✅ [URL Profile] Found wallet address in Firestore:', profileWallet);
                  }
                }
              } catch (firestoreSearchError) {
                console.warn('⚠️ [URL Profile] Error searching Firestore for wallet:', firestoreSearchError);
              }
            }
            
            // Try to load profile data from Firestore/IPFS (only if not found in allProfiles)
            
            if (profileWallet) {
              // Load profile data using wallet address (without encryption key since it's another user's profile)
              try {
                profileData = await getUserProfileByWallet(profileWallet, null);
                console.log('📦 [URL Profile] Profile data loaded via wallet:', profileData ? `found for ${profileData.username || 'unknown'}` : 'not found');
              } catch (profileError) {
                console.error('❌ [URL Profile] Error loading profile data via wallet:', profileError);
              }
            }
            
            // Fallback 1: If wallet lookup failed or profile data not found, try loading directly from IPFS via blockchain
            // This handles cases where profile exists in IPFS but not in Firestore
            if (!profileData && profileWallet) {
              try {
                console.log('🔄 [URL Profile] Trying fallback 1a: loading profile directly from IPFS via blockchain');
                // Get IPFS hash from blockchain (Arc (Circle) network)
                const ipfsHash = await getCIDFromArc(profileWallet);
                if (ipfsHash) {
                  console.log('📦 [URL Profile] IPFS hash from blockchain:', ipfsHash);
                  // Fetch profile data from IPFS
                  const ipfsData = await fetchFromIPFS(ipfsHash);
                  if (ipfsData) {
                    // Try to parse as JSON (profile might be encrypted, but we'll try)
                    let parsedData = null;
                    try {
                      if (typeof ipfsData === 'string') {
                        parsedData = JSON.parse(ipfsData);
                      } else {
                        parsedData = ipfsData;
                      }
                    } catch (parseError) {
                      console.warn('⚠️ [URL Profile] Could not parse IPFS data as JSON, might be encrypted:', parseError);
                    }
                    
                    if (parsedData && typeof parsedData === 'object') {
                      // Create profile data from IPFS with username from URL
                      profileData = {
                        ...parsedData,
                        username: username, // Use username from URL
                        walletAddress: profileWallet,
                        ipfsHash: ipfsHash
                      };
                      console.log('✅ [URL Profile] Profile data loaded from IPFS');
                    } else {
                      // IPFS data might be encrypted - create minimal profile with username and wallet
                      // This allows the profile to be displayed even if full data isn't available
                      console.log('⚠️ [URL Profile] IPFS data appears to be encrypted or invalid, creating minimal profile');
                      profileData = {
                        username: username,
                        walletAddress: profileWallet,
                        ipfsHash: ipfsHash
                      };
                    }
                  }
                }
              } catch (ipfsError) {
                console.warn('⚠️ [URL Profile] Error loading profile from IPFS:', ipfsError);
                // Even if IPFS load fails, create minimal profile if we have wallet and username
                if (profileWallet && username) {
                  console.log('🔄 [URL Profile] Creating minimal profile from username and wallet');
                  try {
                    const ipfsHash = await getCIDFromArc(profileWallet);
                    profileData = {
                      username: username,
                      walletAddress: profileWallet,
                      ipfsHash: ipfsHash || null
                    };
                  } catch (hashError) {
                    console.warn('⚠️ [URL Profile] Could not get IPFS hash:', hashError);
                  }
                }
              }
            }
            
            // Fallback 1b: If IPFS load failed or no wallet, try loading directly from Firestore by username
            if (!profileData) {
              try {
                console.log('🔄 [URL Profile] Trying fallback 1b: loading profile directly from Firestore by username');
                const profileRef = doc(db, 'profiles', username);
                const profileSnap = await getDoc(profileRef);
                
                if (profileSnap.exists()) {
                  const metadata = profileSnap.data();
                  console.log('✅ [URL Profile] Found profile in Firestore:', metadata.username);
                  
                  // Get wallet address from the metadata (if available)
                  const walletFromMetadata = metadata.walletAddress;
                  
                  // Get IPFS hash from blockchain if we have wallet address (Arc (Circle) network)
                  let ipfsHash = null;
                  if (walletFromMetadata) {
                    try {
                      ipfsHash = await getCIDFromArc(walletFromMetadata);
                      console.log('📦 [URL Profile] IPFS hash from blockchain:', ipfsHash || 'not found');
                    } catch (blockchainError) {
                      console.warn('⚠️ [URL Profile] Could not get IPFS hash from blockchain:', blockchainError);
                    }
                  }
                  
                  // Create profile data from Firestore metadata
                  profileData = {
                    ...metadata,
                    ipfsHash: ipfsHash || null
                  };
                  console.log('📦 [URL Profile] Profile data created from Firestore metadata');
                } else {
                  console.log('❌ [URL Profile] Profile document does not exist in Firestore');
                }
              } catch (firestoreError) {
                console.error('❌ [URL Profile] Error loading profile from Firestore:', firestoreError);
              }
            }
            
            // Fallback 2: Search in already-loaded allProfiles array
            // This is important for test users or profiles that are loaded on the map but not in Firestore
            // This should be checked early since test users are often in allProfiles but not in Firestore
            if (!profileData && allProfiles.length > 0) {
              try {
                console.log('🔄 [URL Profile] Trying fallback 2: searching in loaded profiles array');
                const foundProfile = allProfiles.find(profile => 
                  profile.username && profile.username.toLowerCase() === username.toLowerCase()
                );
                
                if (foundProfile) {
                  console.log('✅ [URL Profile] Found profile in loaded profiles array:', foundProfile.username);
                  profileData = { ...foundProfile }; // Create a copy to avoid mutations
                  
                  // If we found the profile but don't have wallet address yet, try to get it
                  if (!profileData.walletAddress && profileWallet) {
                    profileData.walletAddress = profileWallet;
                    console.log('📝 [URL Profile] Added wallet address to profile from fallback:', profileWallet);
                  }
                  
                  // If we still don't have wallet, try to get IPFS hash from blockchain if we have wallet from earlier (Arc (Circle) network)
                  if (profileData.walletAddress && !profileData.ipfsHash) {
                    try {
                      const ipfsHash = await getCIDFromArc(profileData.walletAddress);
                      if (ipfsHash) {
                        profileData = {
                          ...profileData,
                          ipfsHash: ipfsHash
                        };
                        console.log('📦 [URL Profile] Added IPFS hash to profile from blockchain');
                      }
                    } catch (hashError) {
                      console.warn('⚠️ [URL Profile] Could not get IPFS hash:', hashError);
                    }
                  }
                  
                  // Ensure wallet address is in profileData - check multiple possible fields
                  if (!profileData.walletAddress) {
                    // Try to get from profileWallet (from earlier username lookup)
                    if (profileWallet) {
                      profileData.walletAddress = profileWallet;
                      console.log('📝 [URL Profile] Added wallet address from username lookup:', profileWallet);
                    } else {
                      // Try to get wallet address from username again (with more thorough search)
                      try {
                        console.log('🔄 [URL Profile] Attempting comprehensive wallet address lookup for:', foundProfile.username);
                        const walletFromUsername = await getWalletFromUsername(foundProfile.username);
                        if (walletFromUsername) {
                          profileData.walletAddress = walletFromUsername;
                          console.log('✅ [URL Profile] Found wallet address from username lookup:', walletFromUsername);
                        } else {
                          // Last resort: check if profile has any wallet-related field
                          const possibleWalletFields = ['wallet', 'address', 'walletAddress', 'ethAddress', 'ethereumAddress'];
                          for (const field of possibleWalletFields) {
                            if (foundProfile[field] && typeof foundProfile[field] === 'string' && foundProfile[field].startsWith('0x')) {
                              profileData.walletAddress = foundProfile[field];
                              console.log(`✅ [URL Profile] Found wallet address in field '${field}':`, foundProfile[field]);
                              break;
                            }
                          }
                        }
                      } catch (walletError) {
                        console.warn('⚠️ [URL Profile] Could not get wallet address from username:', walletError);
                      }
                    }
                  }
                  
                  // Try to get IPFS hash from blockchain if we have wallet address (Arc (Circle) network)
                  if (profileData.walletAddress) {
                    try {
                      const ipfsHash = await getCIDFromArc(profileData.walletAddress);
                      if (ipfsHash) {
                        profileData = {
                          ...profileData,
                          ipfsHash: ipfsHash
                        };
                        console.log('📦 [URL Profile] IPFS hash retrieved from blockchain');
                      }
                    } catch (blockchainError) {
                      console.warn('⚠️ [URL Profile] Could not get IPFS hash from blockchain:', blockchainError);
                    }
                  }
                } else {
                  console.log('❌ [URL Profile] Profile not found in loaded profiles array');
                }
              } catch (searchError) {
                console.error('❌ [URL Profile] Error searching in loaded profiles:', searchError);
              }
            }
            
            // If we found profileData, use loadUserProfile to load it properly (handles currentUser detection)
            // This ensures consistent loading behavior and proper currentUser setting
            if (profileData && profileWallet) {
              console.log('✅ [URL Profile] Found profile data, loading via loadUserProfile:', profileWallet);
              // Use loadUserProfile which properly handles currentUser detection and loading states
              try {
                await loadUserProfile(profileWallet, true); // skipUnifiedAuth=true
                // Ensure loading state is cleared after successful load
                setIsLoadingProfileFromIPFS(false);
                
                // Center map on profile location if available
                if (profileData.location && profileData.location.lat && profileData.location.lng) {
                  const centerMap = () => {
                    if (mapInstanceRef.current) {
                      console.log('🗺️ [URL Profile] Centering map on profile location:', profileData.location.lat, profileData.location.lng);
                      mapInstanceRef.current.panTo([profileData.location.lat, profileData.location.lng]);
                    } else {
                      setTimeout(centerMap, 200);
                    }
                  };
                  setTimeout(centerMap, 100);
                }
              } catch (loadError) {
                console.error('❌ [URL Profile] Error loading via loadUserProfile:', loadError);
                // Fallback: show profile data directly
                const cleanProfile = cleanProfileData(profileData);
                const filteredProfile = filterProfileForOtherUsers(cleanProfile, null);
                setCurrentUser(null);
                setCurrentProfile(filteredProfile);
                setIsLoadingProfileFromIPFS(false);
                setProfileVisible(true);
              }
            } else if (profileData && !profileWallet) {
              // Profile found but no wallet - show it directly (test user or incomplete profile)
              console.log('✅ [URL Profile] Found profile data without wallet, showing directly');
              const cleanProfile = cleanProfileData(profileData);
              const filteredProfile = filterProfileForOtherUsers(cleanProfile, null);
              setCurrentUser(null);
              setCurrentProfile(filteredProfile);
              setIsLoadingProfileFromIPFS(false);
              setProfileVisible(true);
            } else {
              console.error('❌ [URL Profile] No profile data found for username:', username);
              setIsLoadingProfileFromIPFS(false);
              setProfileLoadError(`Profile "${username}" not found. The profile may not exist or may not be published.`);
              setProfileVisible(true);
            }
          } catch (error) {
            console.error('❌ [URL Profile] Error loading profile by username:', error);
            // Close activity feed FIRST to prevent overlap during loading
            setActivityFeedVisible(false);
            setIsLoadingProfileFromIPFS(false);
            setProfileLoadError('Failed to load profile. Please try again.');
            // Show profile AFTER closing activity feed
            setProfileVisible(true);
          }
        };
        loadProfileByUsername();
      }
    } else {
      // Reset ref when not on a profile route
      lastLoadedProfileUsernameRef.current = null;
    }
  }, [getNormalizedPathname, allProfiles.length]); // Include allProfiles.length so it re-runs when profiles are loaded
  
  // Center map on profile location when opening profile via URL (/profile/:username only, not /myprofile)
  useEffect(() => {
    const pathname = getNormalizedPathname();
    // Only auto-center for other users' profiles, not for /myprofile (user's own profile)
    const isOtherProfileRoute = pathname.startsWith('/profile/');
    
    if (isOtherProfileRoute && currentProfile && currentProfile.location && 
        currentProfile.location.lat && currentProfile.location.lng) {
      // Wait a bit for map to be ready, then center
      const centerMap = () => {
        if (mapInstanceRef.current) {
          console.log('🗺️ Centering map on profile location from second effect:', currentProfile.location.lat, currentProfile.location.lng);
          // Pan to location without changing zoom level
          mapInstanceRef.current.panTo([currentProfile.location.lat, currentProfile.location.lng]);
        } else {
          // Retry after a short delay if map isn't ready
          setTimeout(centerMap, 200);
        }
      };
      centerMap();
    }
  }, [getNormalizedPathname, currentProfile?.location?.lat, currentProfile?.location?.lng]);

  // Store current user's profile separately for match calculation
  const [myProfileForMatch, setMyProfileForMatch] = useState(null);
  
  // Load current user's profile when wallet is connected
  // This should always load the profile of the connected wallet, regardless of which profile is currently displayed
  useEffect(() => {
    const loadMyProfileForMatch = async () => {
      // Only depend on walletAddress and encryptionKey, not currentUser
      // This ensures myProfileForMatch is always the logged-in user's profile, even when viewing other profiles
      if (walletAddress && encryptionKey) {
        try {
          const myProfileData = await getUserProfileByWallet(walletAddress, encryptionKey);
          if (myProfileData) {
            setMyProfileForMatch(cleanProfileData(myProfileData));
          } else {
            setMyProfileForMatch(null);
          }
        } catch (error) {
          console.error('Error loading my profile for match calculation:', error);
          setMyProfileForMatch(null);
        }
      } else {
        setMyProfileForMatch(null);
      }
    };
    
    loadMyProfileForMatch();
  }, [walletAddress, encryptionKey]);
  
  // Auto-load recommendations when smart recommendations section becomes visible and data becomes ready
  useEffect(() => {
    // Load when section is visible and data becomes available (even if section was already open)
    if (smartRecommendationsVisible && currentProfile && allProfiles.length > 0) {
      // Load if we don't have recommendations yet OR if we're currently loading (data just became available)
      if (recommendations.length === 0 || isLoadingRecommendations) {
        loadRecommendations();
      }
    }
  }, [smartRecommendationsVisible, currentProfile, allProfiles.length]);
  
  // Position dropdown and close when clicking outside
  useEffect(() => {
    if (showSortDropdown && sortButtonRef.current && sortDropdownRef.current) {
      const rect = sortButtonRef.current.getBoundingClientRect();
      sortDropdownRef.current.style.top = `${rect.bottom + 4}px`;
      sortDropdownRef.current.style.left = `${rect.left}px`;
    }
    
    const handleClickOutside = (event) => {
      if (showSortDropdown && 
          sortButtonRef.current && 
          !sortButtonRef.current.contains(event.target) &&
          sortDropdownRef.current &&
          !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSortDropdown]);
  
  // Position filter dropdown and close on outside click
  useEffect(() => {
    if (showFilterDropdown && filterButtonRef.current && filterDropdownRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      filterDropdownRef.current.style.top = `${rect.bottom + 4}px`;
      filterDropdownRef.current.style.left = `${rect.left - 63}px`;
    }
    
    const handleClickOutside = (event) => {
      if (showFilterDropdown && 
          filterButtonRef.current && 
          !filterButtonRef.current.contains(event.target) &&
          filterDropdownRef.current &&
          !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);
  

  // Pre-compute display values helper functions (memoized)
  const getNameLower = useCallback((profile) => (profile.name || profile.username || '').toLowerCase(), []);
  const getCityLower = useCallback((profile) => (profile.city || '').toLowerCase(), []);
  
  const sortedProfiles = useMemo(() => {
    if (filteredProfiles.length === 0) return [];
    
    // Get user's city coordinates (from myProfileForMatch if available, otherwise default to Berlin)
    const userCityLat = (myProfileForMatch && myProfileForMatch.location?.lat) || 52.52;
    const userCityLng = (myProfileForMatch && myProfileForMatch.location?.lng) || 13.405;
    
    // Use myProfileForMatch for match calculation (always current user's profile)
    const myProfile = myProfileForMatch;
    const myWalletAddress = walletAddress ? walletAddress.toLowerCase() : null;
    
    // Filter out current user's own profile from search results
    // Use myProfileForMatch?.username instead of currentUser, since currentUser can be null when viewing other profiles
    const myUsername = myProfileForMatch?.username || null;
    const profilesToProcess = filteredProfiles.filter(profile => {
      // Exclude own profile by comparing walletAddress or username
      if (myWalletAddress && profile.walletAddress) {
        return profile.walletAddress.toLowerCase() !== myWalletAddress;
      }
      if (myUsername && profile.username) {
        return profile.username !== myUsername;
      }
      return true; // If we can't determine, include it
    });
    
    // When sorting by match, don't separate by location - sort all together
    // For other sort types, separate profiles with and without location
    const profilesWithLocation = [];
    const profilesWithoutLocation = [];
    const allProfilesWithMatch = [];
    
    // Pre-compute helper values for sorting
    const getName = (profile) => getNameLower(profile);
    
    profilesToProcess.forEach(profile => {
      // Calculate match percentage if we have current user's profile
      const matchPercentage = myProfile ? calculateMatchPercentage(myProfile, profile) : 0;
      
      // Pre-compute display values
      const rawName = profile.name || profile.username || '';
      const displayName = truncateName(rawName, 25);
      const displayCity = profile.city ? truncateCityName(profile.city, 15) : null;
      
      if (!profile.location || profile.location.lat == null || profile.location.lng == null) {
        // Profile without published city
        const profileData = {
          ...profile,
          distance: null, // No distance for profiles without location
          matchPercentage: matchPercentage,
          displayName: displayName,
          displayCity: displayCity,
          displayDistance: null,
          nameLower: getName(profile), // Pre-compute for sorting
          cityLower: getCityLower(profile) // Pre-compute for sorting
        };
        profilesWithoutLocation.push(profileData);
        if (sortType === 'match') {
          allProfilesWithMatch.push(profileData);
        }
        return;
      }
      // Calculate distance for each profile with location only if wallet is connected
      // When no wallet is connected, don't show distance as it's unclear how far that is
      let profileDistance = null;
      let displayDistance = null;
      if (walletAddress && myProfileForMatch && myProfileForMatch.location) {
        // Only calculate distance if wallet is connected and user has a location
        profileDistance = calculateDistance(
          userCityLat,
          userCityLng,
          profile.location.lat,
          profile.location.lng
        );
        displayDistance = `${profileDistance.toFixed(0)} km`;
      }
      const profileData = {
        ...profile,
        distance: profileDistance,
        matchPercentage: matchPercentage,
        displayName: displayName,
        displayCity: displayCity,
        displayDistance: displayDistance,
        nameLower: getName(profile), // Pre-compute for sorting
        cityLower: getCityLower(profile) // Pre-compute for sorting
      };
      profilesWithLocation.push(profileData);
      if (sortType === 'match') {
        allProfilesWithMatch.push(profileData);
      }
    });
    
    // Optimized sorting function - avoid creating intermediate arrays when possible
    const sortProfiles = (profiles) => {
      // Sort in-place to avoid creating new array
      profiles.sort((a, b) => {
        switch (sortType) {
          case 'distance':
            // Sort by distance (ascending), then by match %
            if (a.distance != null && b.distance != null) {
              if (a.distance !== b.distance) {
                return a.distance - b.distance;
              }
            } else if (a.distance != null) {
              return -1; // a has distance, b doesn't - a comes first
            } else if (b.distance != null) {
              return 1; // b has distance, a doesn't - b comes first
            }
            // Both have no distance, sort by match %
            return b.matchPercentage - a.matchPercentage;
            
          case 'city':
            // Sort by city name (alphabetically), then by match %
            if (a.cityLower !== b.cityLower) {
              return a.cityLower.localeCompare(b.cityLower);
            }
            return b.matchPercentage - a.matchPercentage;
            
          case 'name':
            // Sort by name (alphabetically), then by match %
            if (a.nameLower !== b.nameLower) {
              return a.nameLower.localeCompare(b.nameLower);
            }
            return b.matchPercentage - a.matchPercentage;
            
          case 'activity':
            // Sort by last activity timestamp (most recent first), then by match %
            // Note: Activity data should be loaded separately and attached to profiles
            const aActivity = a.lastActivityTimestamp || 0;
            const bActivity = b.lastActivityTimestamp || 0;
            if (bActivity !== aActivity) {
              return bActivity - aActivity;
            }
            return b.matchPercentage - a.matchPercentage;
            
          default:
            return 0;
        }
      });
      return profiles;
    };
    
    // If sorting by match, return all profiles sorted together by match percentage
    if (sortType === 'match') {
      allProfilesWithMatch.sort((a, b) => {
        // Sort by match % (descending), then by distance/name
        if (b.matchPercentage !== a.matchPercentage) {
          return b.matchPercentage - a.matchPercentage;
        }
        // If match percentages are equal, prefer profiles with distance
        if (a.distance != null && b.distance != null) {
          return a.distance - b.distance;
        }
        if (a.distance != null) return -1; // a has distance, b doesn't - a comes first
        if (b.distance != null) return 1; // b has distance, a doesn't - b comes first
        // Both have no distance, sort by name
        return a.nameLower.localeCompare(b.nameLower);
      });
      return allProfilesWithMatch;
    }
    
    // Sort profiles with location
    sortProfiles(profilesWithLocation);
    
    // Sort profiles without location
    sortProfiles(profilesWithoutLocation);
    
    // Combine: profiles with location first, then profiles without location
    return [...profilesWithLocation, ...profilesWithoutLocation];
  }, [filteredProfiles, calculateDistance, calculateMatchPercentage, myProfileForMatch, walletAddress, sortType, getNameLower, getCityLower]);

  const handleToggleSwitch = (e) => {
    e.currentTarget.classList.toggle('off');
  };

  const toggleProfile = () => {
    // CRITICAL FIX: Don't check for currentProfile or showRegistrationForm when navigating to /myprofile
    // The useEffect will handle loading the profile - we just need to navigate
    // This matches the behavior of directly typing the URL in the address bar
    if (!walletAddress) {
      return;
    }
    const newProfileVisible = !profileVisible;
    // Close activity feed FIRST before navigation to prevent it from reopening
    if (newProfileVisible) {
      setActivityFeedVisible(false);
    }
    // Navigate based on visibility - let the useEffect handle the rest
    // Don't set profileVisible here - let the route useEffect handle it
    if (newProfileVisible) {
      navigate('/myprofile');
      // The useEffect for /myprofile route will set profileVisible and load the profile
    } else {
      navigate('/');
      setProfileVisible(false);
    }
    // Close groups, messages, and bug when opening profile
    if (newProfileVisible) {
      setGroupsVisible(false);
      setMessagesVisible(false);
      setBugVisible(false);
    }
  };

  const toggleGroups = () => {
    const newGroupsVisible = !groupsVisible;
    // Navigate based on visibility
    if (newGroupsVisible) {
      navigate('/groups');
    } else {
      navigate('/');
    }
    // Close any open profile section (but keep profile data)
    setProfileVisible(false);
    // Close TagRUN when opening groups
    setTagRunVisible(false);
    // Close messages, activity feed, and bug when opening groups
    if (newGroupsVisible) {
      setMessagesVisible(false);
      setActivityFeedVisible(false);
      setBugVisible(false);
    }
    // Toggle groups section
    setGroupsVisible(newGroupsVisible);
  };

  const toggleMessages = () => {
    const newMessagesVisible = !messagesVisible;
    // Navigate based on visibility
    if (newMessagesVisible) {
      navigate('/messages');
    } else {
      navigate('/');
    }
    // Close any open profile section (but keep profile data)
    setProfileVisible(false);
    // Close groups and bug when opening messages
    if (newMessagesVisible) {
      setGroupsVisible(false);
      setBugVisible(false);
      setActivityFeedVisible(false);
    }
    // Toggle messages section
    setMessagesVisible(newMessagesVisible);
  };

  const toggleActivityFeed = () => {
    const newActivityFeedVisible = !activityFeedVisible;
    // Close any open profile section (but keep profile data)
    setProfileVisible(false);
    // Close groups, messages, bug, and TagRUN when opening activity feed
    if (newActivityFeedVisible) {
      setGroupsVisible(false);
      setMessagesVisible(false);
      setBugVisible(false);
      setTagRunVisible(false);
      // Navigate to /activityfeed route
      navigate('/activityfeed');
    } else {
      // If closing, navigate away from /activityfeed
      if (normalizedPathname === '/activityfeed') {
        navigate('/');
      }
    }
    // Toggle activity feed section
    setActivityFeedVisible(newActivityFeedVisible);
  };

  const toggleBug = () => {
    const newBugVisible = !bugVisible;

    // When opening bug report – hide all other sections and go to Discover
    if (newBugVisible) {
      // Navigate to Discover
      navigate('/');

      // Hide all other overlays/sections
      setProfileVisible(false);
      setGroupsVisible(false);
      setMessagesVisible(false);
      setActivityFeedVisible(false);
      setTagRunVisible(false);
      setAboutVisible(false);
      setAiAssistantVisible(false);
    } else {
      // When closing bug report, stay on Discover
      navigate('/');
    }

    // Toggle bug section visibility
    setBugVisible(newBugVisible);
  };

  const handleBugFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (2MB = 2 * 1024 * 1024 bytes)
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('File size must be less than 2MB. Please choose a smaller file.');
        e.target.value = ''; // Clear the input
        return;
      }
      setBugReport({ ...bugReport, file: file });
    }
  };

  const handleSendBugReport = async (e) => {
    e.preventDefault();
    
    if (!bugReport.text.trim() && !bugReport.file) {
      alert('Please provide a bug description or attach a file.');
      return;
    }

    try {
      const subject = encodeURIComponent('Bug Report from PeopleTag');
      let body = bugReport.text || 'No description provided.';
      
      if (bugReport.file) {
        body += `\n\n--- File Attachment ---\n`;
        body += `File Name: ${bugReport.file.name}\n`;
        body += `File Size: ${(bugReport.file.size / 1024).toFixed(2)} KB\n`;
        body += `File Type: ${bugReport.file.type || 'Unknown'}\n`;
        body += `\nNote: The file cannot be attached via email link. Please attach the file manually when sending the email.`;
      }

      // Create mailto link
      const mailtoLink = `mailto:info@peopletag.xyz?subject=${subject}&body=${encodeURIComponent(body)}`;
      
      // Open email client
      window.location.href = mailtoLink;

      // Show success message with instructions
      const message = bugReport.file 
        ? 'Bug report email opened. Please attach the file manually and send the email to complete the report.'
        : 'Bug report email opened. Please send the email to complete the report.';
      alert(message);
      
      // Reset form
      setBugReport({ text: '', file: null });
      const fileInput = document.getElementById('bug-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      console.error('Error sending bug report:', error);
      alert('Error sending bug report. Please try again.');
    }
  };

  const toggleLeftSection = () => {
    const newValue = !leftSectionVisible;
    
    // If we're opening Search while Smart Recommendations is visible - instant switch
    if (newValue && smartRecommendationsVisible) {
      setIsSwitchingSections(true);
      setSmartRecommendationsVisible(false);
      setLeftSectionVisible(true);
      setTimeout(() => setIsSwitchingSections(false), 50);
    } else {
      // Normal open/close - with animation
      setLeftSectionVisible(newValue);
      if (newValue) {
        setSmartRecommendationsVisible(false);
      }
    }
  };

  const toggleSmartRecommendations = () => {
    const newValue = !smartRecommendationsVisible;
    
    // If we're opening Smart Recommendations while Search is visible - instant switch
    if (newValue && leftSectionVisible) {
      setIsSwitchingSections(true);
      setLeftSectionVisible(false);
      setSmartRecommendationsVisible(true);
      // Always load recommendations when opening the section (will show loading and wait for data)
      loadRecommendations();
      setTimeout(() => setIsSwitchingSections(false), 50);
    } else {
      // Normal open/close - with animation
      setSmartRecommendationsVisible(newValue);
      if (newValue) {
        setLeftSectionVisible(false);
        // Always load recommendations when opening the section (will show loading and wait for data)
        loadRecommendations();
      }
    }
  };

  const toggleAiAssistant = (e) => {
    e.preventDefault();
    setAiAssistantVisible(!aiAssistantVisible);
  };

  // Fetch available models when AI assistant is opened
  useEffect(() => {
    if (aiAssistantVisible && availableModels.length === 0) {
      const fetchModels = async () => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || 
            (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
          const response = await fetch(`${apiUrl}/api/v1/ai/models`);
          if (response.ok) {
            const data = await response.json();
            if (data.models && data.models.length > 0) {
              setAvailableModels(data.models);
              // Set default model if available
              if (data.defaultModel) {
                setSelectedModel(data.defaultModel);
              }
            }
          }
        } catch (error) {
          console.error('Failed to fetch models:', error);
          // Set default models as fallback
          setAvailableModels([
            { key: 'llama-3.1-8b', name: 'Llama 3.1 8B', description: 'Best balance of quality and speed', recommended: true },
            { key: 'llama-3.2-3b', name: 'Llama 3.2 3B', description: 'Fastest and smallest', recommended: false },
            { key: 'mistral-7b', name: 'Mistral 7B', description: 'High quality responses', recommended: true },
            { key: 'llama-3.1-70b', name: 'Llama 3.1 70B', description: 'Highest quality but slower', recommended: false },
            { key: 'mixtral-8x7b', name: 'Mixtral 8x7B', description: 'Excellent quality with good speed', recommended: true },
            { key: 'llama-3-8b', name: 'Llama 3 8B', description: 'Meta\'s Llama 3 model', recommended: false },
            { key: 'gemma-2-7b', name: 'Gemma 2 7B', description: 'Google\'s Gemma 2 model', recommended: false },
            { key: 'mistral-small', name: 'Mistral Small', description: 'Mistral\'s smaller model', recommended: false }
          ]);
        }
      };
      fetchModels();
    }
  }, [aiAssistantVisible, availableModels.length]);

  const handleAiSubmit = async (e) => {
    e.preventDefault();
    
    if (!aiInput.trim()) {
      return;
    }

    const userMessage = aiInput.trim();
    
    // If this is the first message, mark it as no longer the first
    if (isFirstMessage) {
      setIsFirstMessage(false);
    }
    
    // Add user message to conversation
    const newUserMessage = { role: 'user', content: userMessage };
    setAiMessages(prev => [...prev, newUserMessage]);
    setAiInput('');
    setAiLoading(true);

    try {
      // Build conversation history for the API
      const allMessages = [...aiMessages, newUserMessage];
      
      // Call our simple proxy endpoint (server handles Hugging Face API)
      // In production, uses Firebase Functions via /api route
      // In development, uses local server on port 3001
      const apiUrl = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
      const response = await fetch(`${apiUrl}/api/v1/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: allMessages,
          model: selectedModel
        }),
      });

      // Check content type before parsing
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If response is HTML, return error without trying to parse JSON
        if (contentType.includes('text/html')) {
          const htmlText = await response.text();
          console.error('Server returned HTML instead of JSON:', htmlText.substring(0, 200));
          throw new Error(`Server returned HTML error page. Status: ${response.status}. This usually means the Hugging Face API endpoint is misconfigured.`);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.status} ${response.statusText}`);
      }

      // Check if response is JSON before parsing
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Expected JSON but received:', contentType, text.substring(0, 200));
        throw new Error(`Server returned ${contentType} instead of JSON. This usually means there's an error on the server side.`);
      }

      const data = await response.json();
      
      // Extract the AI response from the proxy response
      const aiResponse = data.message || data.text || 'Sorry, I could not generate a response.';

      // Add AI response to conversation
      const aiMessage = { role: 'assistant', content: aiResponse };
      setAiMessages(prev => [...prev, aiMessage]);
      
    } catch (error) {
      console.error('AI Assistant error:', error);
      let errorMessage = error.message || 'Failed to get AI response. Please try again.';
      // Provide helpful error message if server is not running
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_CONNECTION_REFUSED') || errorMessage.includes('Connection refused')) {
        errorMessage = 'API server is not running. Please start it with: npm run server (in a separate terminal)';
      }
      
      const errorMsg = { role: 'assistant', content: `Error: ${errorMessage}` };
      setAiMessages(prev => [...prev, errorMsg]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleGenerateTwitterPost = async () => {
    setIsGeneratingTwitterPost(true);
    setGeneratedPostText('');
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
      
      // Use referral link if available, otherwise use plain APP_URL
      const referralLink = referralStats?.referralCode 
        ? `${APP_URL}?ref=${referralStats.referralCode}` 
        : APP_URL;
      
      // Use template-based approach for now - generates varied posts without AI
      const postTemplates = [
        `Just discovered PeopleTag - decentralized social networking with encrypted profiles! Privacy-first platform built on blockchain. ${referralLink} ${TWITTER_HANDLE} #Web3`,
        `Check out PeopleTag! Revolutionary social network with blockchain privacy and IPFS storage. Your data, your control. ${referralLink} ${TWITTER_HANDLE} #Privacy #Decentralized`,
        `Excited about PeopleTag - encrypted profiles, blockchain security, and true privacy. This is the future of social networking! ${referralLink} ${TWITTER_HANDLE} #Privacy #Blockchain`,
        `PeopleTag is game-changing! Decentralized social networking with encrypted profiles and blockchain technology. Join the Web3 revolution! ${referralLink} ${TWITTER_HANDLE} #Web3 #Privacy`,
        `Amazing platform alert! PeopleTag combines blockchain privacy with IPFS storage for truly decentralized social networking. ${referralLink} ${TWITTER_HANDLE} #Decentralized #Web3`,
        `Love this! PeopleTag offers encrypted profiles and blockchain-based privacy. The future of social networking is here! ${referralLink} ${TWITTER_HANDLE} #Blockchain #Privacy`,
        `Revolutionary social networking platform! PeopleTag uses blockchain and IPFS for true decentralization. Privacy-first design. ${referralLink} ${TWITTER_HANDLE} #Web3 #Decentralized`,
        `Incredible innovation! PeopleTag brings encrypted profiles and blockchain privacy to social networking. Game changer! ${referralLink} ${TWITTER_HANDLE} #Privacy #Blockchain`,
        `Breaking: PeopleTag is revolutionizing social media with blockchain technology! Encrypted profiles, IPFS storage, and true data ownership. ${referralLink} ${TWITTER_HANDLE} #Blockchain #Web3`,
        `Finally! A social network that respects your privacy. PeopleTag uses encryption and blockchain to keep your data secure. ${referralLink} ${TWITTER_HANDLE} #Privacy #Decentralized`,
        `Web3 social networking done right! PeopleTag combines encrypted profiles with blockchain privacy. No more data harvesting! ${referralLink} ${TWITTER_HANDLE} #Web3 #Privacy`,
        `PeopleTag = Privacy + Blockchain + Social. Encrypted profiles, decentralized storage, user-controlled data. This is how it should be! ${referralLink} ${TWITTER_HANDLE} #Decentralized #Blockchain`,
        `Stop giving away your data! PeopleTag offers encrypted social networking on Arc (Circle). Your profile, your control. ${referralLink} ${TWITTER_HANDLE} #Privacy #Web3`,
        `Next-gen social platform alert! PeopleTag uses IPFS and blockchain for decentralized networking. No central servers, no data mining. ${referralLink} ${TWITTER_HANDLE} #IPFS #Decentralized`,
        `Privacy-first social networking is here! PeopleTag encrypts your profile and stores it on IPFS. Built on Arc (Circle) blockchain. ${referralLink} ${TWITTER_HANDLE} #Blockchain #Privacy`,
        `Social media 3.0: PeopleTag. Encrypted profiles, blockchain privacy, IPFS storage. Connect without compromising your data. ${referralLink} ${TWITTER_HANDLE} #Web3 #SocialFi`,
        `Discover PeopleTag - where social networking meets blockchain privacy. Encrypted profiles, decentralized infrastructure, true ownership. ${referralLink} ${TWITTER_HANDLE} #Decentralized #Privacy`,
        `The future of social media: PeopleTag. Blockchain-powered, privacy-focused, user-controlled. Join the decentralized revolution! ${referralLink} ${TWITTER_HANDLE} #Web3 #Blockchain`
      ];
      
      // Simulate a brief delay for UX
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Use a random template - this ensures variety without AI issues
      const randomTemplate = postTemplates[Math.floor(Math.random() * postTemplates.length)];
      
      // Store the generated post
      setGeneratedPostText(randomTemplate);
      
    } catch (error) {
      console.error('Error generating Twitter post:', error);
      // Fallback post on error - use referral link if available
      const referralLink = referralStats?.referralCode 
        ? `${APP_URL}?ref=${referralStats.referralCode}` 
        : APP_URL;
      const fallbackTemplates = [
        `Just discovered PeopleTag - decentralized social networking with encrypted profiles! Privacy-first platform built on blockchain. ${referralLink} ${TWITTER_HANDLE} #Web3`,
        `Check out PeopleTag! Revolutionary social network with blockchain privacy and IPFS storage. Your data, your control. ${referralLink} ${TWITTER_HANDLE} #Privacy #Decentralized`
      ];
      const fallbackText = fallbackTemplates[Math.floor(Math.random() * fallbackTemplates.length)];
      setGeneratedPostText(fallbackText);
    } finally {
      setIsGeneratingTwitterPost(false);
    }
  };

  const handlePostToX = () => {
    // Use referral link if available, otherwise use plain APP_URL
    const referralLink = referralStats?.referralCode 
      ? `${APP_URL}?ref=${referralStats.referralCode}` 
      : APP_URL;
    const postText = generatedPostText || `Check out PeopleTag! ${referralLink} ${TWITTER_HANDLE}`;
    const encodedText = encodeURIComponent(postText);
    const twitterComposeUrl = `https://x.com/intent/tweet?text=${encodedText}`;
    window.open(twitterComposeUrl, '_blank', 'noopener,noreferrer');
  };

  const handleGetLocation = () => {
    if (!mapInstanceRef.current) return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          mapInstanceRef.current.setView([latitude, longitude], 15);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Unable to get your location. Please enable location services.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  };

  const handleCopyUsername = async (e) => {
    e.preventDefault();
    if (!currentProfile) return;
    const username = `@${currentProfile.username}`;
    try {
      await navigator.clipboard.writeText(username);
      setCopiedVisible(true);
      setTimeout(() => {
        setCopiedVisible(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Function to update publish status for profile elements
  const updatePublishStatus = async (elementType, elementIndex = null, isPublished = false) => {
    if (!walletAddress || !currentProfile || currentProfile.username !== currentUser) {
      return;
    }

    // Save original state for rollback on error
    const originalPublishedTags = [...publishedTags];
    const originalPublishedGroups = [...publishedGroups];
    const originalPublishedSocials = [...publishedSocials];
    const originalPublishedMusic = [...publishedMusic];
    const originalPublishedBirthDate = publishedBirthDate;
    const originalPublishedCity = publishedCity;
    const originalPublishedWallet = publishedWallet;
    const originalPublishedEmail = publishedEmail;
    const originalPublishedName = publishedName;
    const originalPublishedBio = publishedBio;
    const originalPublishedPreferredContact = publishedPreferredContact;
    const originalPublishedRelationship = publishedRelationship;
    const originalPublishedLookingFor = publishedLookingFor;
    const originalPublishedLanguages = publishedLanguages;
    const originalPublishedEducation = publishedEducation;
    const originalPublishedOccupation = publishedOccupation;

    // Optimistic UI update - update state immediately for instant feedback
    let updatedPublishedTags = [...publishedTags];
    let updatedPublishedGroups = [...publishedGroups];
    let updatedPublishedSocials = [...publishedSocials];
    let updatedPublishedMusic = [...publishedMusic];
    let updatedPublishedBirthDate = publishedBirthDate;
    let updatedPublishedCity = publishedCity;
    let updatedPublishedWallet = publishedWallet;
    let updatedPublishedEmail = publishedEmail;
    let updatedPublishedName = publishedName;
    let updatedPublishedBio = publishedBio;
    let updatedPublishedPreferredContact = publishedPreferredContact;
    let updatedPublishedRelationship = publishedRelationship;
    let updatedPublishedLookingFor = publishedLookingFor;
    let updatedPublishedLanguages = publishedLanguages;
    let updatedPublishedEducation = publishedEducation;
    let updatedPublishedOccupation = publishedOccupation;

    // Update the appropriate publish state immediately
    switch (elementType) {
      case 'tag':
        if (elementIndex !== null) {
          const tag = currentProfile.tagsList[elementIndex];
          if (isPublished) {
            if (!updatedPublishedTags.includes(tag)) {
              updatedPublishedTags.push(tag);
            }
          } else {
            updatedPublishedTags = updatedPublishedTags.filter(t => t !== tag);
          }
          setPublishedTags(updatedPublishedTags);
        }
        break;
      case 'group':
        if (elementIndex !== null && profileGroups && profileGroups[elementIndex]) {
          const group = profileGroups[elementIndex];
          if (isPublished) {
            if (!updatedPublishedGroups.includes(group)) {
              updatedPublishedGroups.push(group);
            }
          } else {
            updatedPublishedGroups = updatedPublishedGroups.filter(g => g !== group);
          }
          setPublishedGroups(updatedPublishedGroups);
        }
        break;
      case 'social':
        if (elementIndex !== null) {
          const social = currentProfile.socialLinks[elementIndex];
          if (isPublished) {
            if (!updatedPublishedSocials.includes(social)) {
              updatedPublishedSocials.push(social);
            }
          } else {
            updatedPublishedSocials = updatedPublishedSocials.filter(s => s !== social);
          }
          setPublishedSocials(updatedPublishedSocials);
        }
        break;
      case 'music':
        if (elementIndex !== null) {
          const song = currentProfile.musicList[elementIndex];
          if (isPublished) {
            if (!updatedPublishedMusic.includes(song)) {
              updatedPublishedMusic.push(song);
            }
          } else {
            updatedPublishedMusic = updatedPublishedMusic.filter(s => s !== song);
          }
          setPublishedMusic(updatedPublishedMusic);
        }
        break;
      case 'birthDate':
        updatedPublishedBirthDate = isPublished;
        setPublishedBirthDate(updatedPublishedBirthDate);
        break;
      case 'city':
        updatedPublishedCity = isPublished;
        setPublishedCity(updatedPublishedCity);
        break;
      case 'wallet':
        updatedPublishedWallet = isPublished;
        setPublishedWallet(updatedPublishedWallet);
        break;
      case 'email':
        updatedPublishedEmail = isPublished;
        setPublishedEmail(updatedPublishedEmail);
        break;
      case 'name':
        updatedPublishedName = isPublished;
        setPublishedName(updatedPublishedName);
        break;
      case 'bio':
        updatedPublishedBio = isPublished;
        setPublishedBio(updatedPublishedBio);
        break;
      case 'preferredContact':
        updatedPublishedPreferredContact = isPublished;
        setPublishedPreferredContact(updatedPublishedPreferredContact);
        break;
      case 'relationship':
        updatedPublishedRelationship = isPublished;
        setPublishedRelationship(updatedPublishedRelationship);
        break;
      case 'lookingFor':
        updatedPublishedLookingFor = isPublished;
        setPublishedLookingFor(updatedPublishedLookingFor);
        break;
      case 'languages':
        updatedPublishedLanguages = isPublished;
        setPublishedLanguages(updatedPublishedLanguages);
        break;
      case 'education':
        updatedPublishedEducation = isPublished;
        setPublishedEducation(updatedPublishedEducation);
        break;
      case 'occupation':
        updatedPublishedOccupation = isPublished;
        setPublishedOccupation(updatedPublishedOccupation);
        break;
    }

    // Show notification immediately (Published or Unpublished)
    const notificationKey = `${elementType}-${elementIndex !== null ? elementIndex : 'single'}`;
    setShowPublishedNotification({ key: notificationKey, isPublished: isPublished });

    // Save in background (don't block UI)
    setIsUpdatingPublish(true);
    try {
      // Prepare published metadata for Firestore (no IPFS update needed)
      const publishedTagsList = currentProfile.tagsList.filter(tag => updatedPublishedTags.includes(tag));
      const publishedGroupsList = profileGroups ? profileGroups.filter(group => updatedPublishedGroups.includes(group)) : [];
      const publishedSocialsList = currentProfile.socialLinks.filter((_, idx) => updatedPublishedSocials.includes(currentProfile.socialLinks[idx]));
      const publishedMusicList = currentProfile.musicList ? currentProfile.musicList.filter(song => updatedPublishedMusic.includes(song)) : [];
      
      const publishedMetadata = {
        username: currentProfile.username,
        publishedTags: publishedTagsList,
        publishedGroups: publishedGroupsList,
        publishedSocials: publishedSocialsList,
        publishedMusic: publishedMusicList,
        publishedBirthDate: updatedPublishedBirthDate ? currentProfile.birthDate : null,
        publishedAge: updatedPublishedBirthDate ? currentProfile.age : null,
        publishedCity: updatedPublishedCity ? currentProfile.city : null,
        publishedLocation: updatedPublishedCity ? currentProfile.location : null,
        publishedWallet: updatedPublishedWallet,
        publishedEmail: updatedPublishedEmail ? currentProfile.email : null,
        publishedName: updatedPublishedName ? currentProfile.name : null,
        publishedBio: updatedPublishedBio ? currentProfile.bio : null,
        publishedPreferredContact: updatedPublishedPreferredContact ? currentProfile.preferredContactMethod : null,
        publishedRelationship: updatedPublishedRelationship ? currentProfile.relationshipStatus : null,
        publishedLookingFor: updatedPublishedLookingFor ? currentProfile.lookingFor : null,
        publishedLanguages: updatedPublishedLanguages ? currentProfile.languages : null,
        publishedEducation: updatedPublishedEducation ? currentProfile.education : null,
        publishedOccupation: updatedPublishedOccupation ? currentProfile.occupation : null,
      };

      // Update only Firestore metadata (no IPFS update)
      console.log('💾 Saving publish status to Firestore:', {
        elementType,
        isPublished,
        publishedBio: publishedMetadata.publishedBio
      });
      await updatePublishedMetadata(walletAddress, publishedMetadata);
      console.log('✅ Publish status saved to Firestore successfully');

      // Wait a moment for Firestore to process the update (eventual consistency)
      await new Promise(resolve => setTimeout(resolve, 500));

      // Reload profile metadata from Firestore to verify and update published states
      // This ensures publishedName and publishedBio states are correctly set after update
      let retryCount = 0;
      const maxRetries = 3;
      let updatedMetadata = null;
      
      while (retryCount < maxRetries && !updatedMetadata) {
        try {
          updatedMetadata = await getUserProfileByWallet(walletAddress, null);
          if (updatedMetadata) {
            // Verify the save worked by checking the relevant field
            let fieldMatches = true;
            let needsRetry = false;
            
            if (elementType === 'bio') {
              const savedBio = updatedMetadata?.bio;
              const expectedBio = publishedMetadata.publishedBio;
              fieldMatches = (savedBio === expectedBio) || 
                            (!savedBio && !expectedBio) ||
                            (savedBio && expectedBio && savedBio.trim() === expectedBio.trim());
              needsRetry = !fieldMatches && !isPublished && retryCount < maxRetries - 1;
              console.log('🔄 Reloaded metadata after save (attempt ' + (retryCount + 1) + '):', {
                elementType: 'bio',
                savedBio,
                expectedBio,
                bioMatches: fieldMatches,
                bioExists: savedBio !== undefined
              });
            } else if (elementType === 'name') {
              const savedName = updatedMetadata?.name;
              const expectedName = publishedMetadata.publishedName;
              fieldMatches = (savedName === expectedName) || 
                            (!savedName && !expectedName) ||
                            (savedName && expectedName && savedName.trim() === expectedName.trim());
              needsRetry = !fieldMatches && !isPublished && retryCount < maxRetries - 1;
              console.log('🔄 Reloaded metadata after save (attempt ' + (retryCount + 1) + '):', {
                elementType: 'name',
                savedName,
                expectedName,
                nameMatches: fieldMatches,
                nameExists: savedName !== undefined
              });
            } else if (elementType === 'birthDate') {
              const savedBirthDate = updatedMetadata?.birthDate;
              const expectedBirthDate = publishedMetadata.publishedBirthDate;
              fieldMatches = (savedBirthDate === expectedBirthDate) || (!savedBirthDate && !expectedBirthDate);
              needsRetry = !fieldMatches && !isPublished && retryCount < maxRetries - 1;
              console.log('🔄 Reloaded metadata after save (attempt ' + (retryCount + 1) + '):', {
                elementType: 'birthDate',
                savedBirthDate,
                expectedBirthDate,
                birthDateMatches: fieldMatches
              });
            } else if (elementType === 'city') {
              const savedCity = updatedMetadata?.city;
              const expectedCity = publishedMetadata.publishedCity;
              fieldMatches = (savedCity === expectedCity) || (!savedCity && !expectedCity);
              needsRetry = !fieldMatches && !isPublished && retryCount < maxRetries - 1;
              console.log('🔄 Reloaded metadata after save (attempt ' + (retryCount + 1) + '):', {
                elementType: 'city',
                savedCity,
                expectedCity,
                cityMatches: fieldMatches
              });
            } else if (elementType === 'wallet') {
              const savedWallet = updatedMetadata?.walletAddress;
              const expectedWallet = isPublished ? walletAddress.toLowerCase() : null;
              fieldMatches = (savedWallet && expectedWallet && savedWallet.toLowerCase() === expectedWallet) ||
                            (!savedWallet && !expectedWallet);
              needsRetry = !fieldMatches && !isPublished && retryCount < maxRetries - 1;
              console.log('🔄 Reloaded metadata after save (attempt ' + (retryCount + 1) + '):', {
                elementType: 'wallet',
                savedWallet,
                expectedWallet,
                walletMatches: fieldMatches
              });
            } else if (elementType === 'preferredContact') {
              const savedPreferredContact = updatedMetadata?.preferredContactMethod;
              const expectedPreferredContact = publishedMetadata.publishedPreferredContact;
              fieldMatches = (savedPreferredContact === expectedPreferredContact) || 
                            (!savedPreferredContact && !expectedPreferredContact) ||
                            (savedPreferredContact && expectedPreferredContact && savedPreferredContact.trim() === expectedPreferredContact.trim());
              needsRetry = !fieldMatches && !isPublished && retryCount < maxRetries - 1;
              console.log('🔄 Reloaded metadata after save (attempt ' + (retryCount + 1) + '):', {
                elementType: 'preferredContact',
                savedPreferredContact,
                expectedPreferredContact,
                preferredContactMatches: fieldMatches,
                preferredContactExists: savedPreferredContact !== undefined
              });
            } else if (elementType === 'relationship' || elementType === 'lookingFor' || elementType === 'languages' || elementType === 'education' || elementType === 'occupation') {
              // For text fields, just verify they match or are both null/empty
              const fieldNameMap = {
                'relationship': { saved: 'relationshipStatus', expected: 'publishedRelationship' },
                'lookingFor': { saved: 'lookingFor', expected: 'publishedLookingFor' },
                'languages': { saved: 'languages', expected: 'publishedLanguages' },
                'education': { saved: 'education', expected: 'publishedEducation' },
                'occupation': { saved: 'occupation', expected: 'publishedOccupation' }
              };
              const fieldMap = fieldNameMap[elementType];
              const savedValue = updatedMetadata?.[fieldMap.saved];
              const expectedValue = publishedMetadata[fieldMap.expected];
              fieldMatches = (savedValue === expectedValue) || 
                            (!savedValue && !expectedValue) ||
                            (savedValue && expectedValue && savedValue.trim() === expectedValue.trim());
              needsRetry = !fieldMatches && !isPublished && retryCount < maxRetries - 1;
              console.log('🔄 Reloaded metadata after save (attempt ' + (retryCount + 1) + '):', {
                elementType,
                savedValue,
                expectedValue,
                fieldMatches,
                fieldExists: savedValue !== undefined
              });
            } else {
              // For tags, socials, and music, just log - they're arrays so verification is more complex
              console.log('🔄 Reloaded metadata after save (attempt ' + (retryCount + 1) + '):', {
                elementType,
                tagsList: updatedMetadata?.tagsList,
                socialLinks: updatedMetadata?.socialLinks,
                musicList: updatedMetadata?.musicList
              });
            }
            
            // If field doesn't match and we're unpublishing, wait and retry (Firestore eventual consistency)
            if (needsRetry) {
              console.log('⏳ ' + elementType + ' field still exists, waiting for Firestore to sync...');
              await new Promise(resolve => setTimeout(resolve, 500));
              updatedMetadata = null; // Reset to retry
              retryCount++;
              continue;
            }
            
            // Update ALL published states based on updated Firestore metadata
            // This ensures all fields are correctly synced after save
            
            // Tags: published if in Firestore
            const updatedFirestoreTags = updatedMetadata?.tagsList || [];
            const updatedPublishedTagsList = currentProfile.tagsList.filter(tag => updatedFirestoreTags.includes(tag));
            setPublishedTags(updatedPublishedTagsList);
            console.log('🔄 Updated tags publish status after save:', {
              firestoreTags: updatedFirestoreTags,
              publishedTags: updatedPublishedTagsList
            });
            
            // Socials: published if in Firestore
            const updatedFirestoreSocials = updatedMetadata?.socialLinks || [];
            const updatedPublishedSocialsList = currentProfile.socialLinks.filter(social => updatedFirestoreSocials.includes(social));
            setPublishedSocials(updatedPublishedSocialsList);
            console.log('🔄 Updated socials publish status after save:', {
              firestoreSocials: updatedFirestoreSocials,
              publishedSocials: updatedPublishedSocialsList
            });
            
            // Music: published if in Firestore
            const updatedFirestoreMusic = updatedMetadata?.musicList || [];
            const updatedPublishedMusicList = (currentProfile.musicList || []).filter(song => updatedFirestoreMusic.includes(song));
            setPublishedMusic(updatedPublishedMusicList);
            console.log('🔄 Updated music publish status after save:', {
              firestoreMusic: updatedFirestoreMusic,
              publishedMusic: updatedPublishedMusicList
            });
            
            // BirthDate: published if in Firestore
            const updatedFirestoreBirthDate = updatedMetadata?.birthDate;
            const updatedBirthDatePublished = !!(updatedFirestoreBirthDate && updatedFirestoreBirthDate === currentProfile.birthDate);
            setPublishedBirthDate(updatedBirthDatePublished);
            console.log('🔄 Updated birthDate publish status after save:', {
              firestoreBirthDate: updatedFirestoreBirthDate,
              profileBirthDate: currentProfile.birthDate,
              published: updatedBirthDatePublished
            });
            
            // City: published if in Firestore
            const updatedFirestoreCity = updatedMetadata?.city;
            const updatedCityPublished = !!(updatedFirestoreCity && updatedFirestoreCity === currentProfile.city);
            setPublishedCity(updatedCityPublished);
            console.log('🔄 Updated city publish status after save:', {
              firestoreCity: updatedFirestoreCity,
              profileCity: currentProfile.city,
              published: updatedCityPublished
            });
            
            // Wallet: published if _isWalletPublishedInFirestore flag is true (set by getUserProfileByWallet)
            // This flag explicitly tracks whether walletAddress was in Firestore vs using fallback
            const updatedWalletPublished = updatedMetadata?._isWalletPublishedInFirestore === true;
            setPublishedWallet(updatedWalletPublished);
            console.log('🔄 Updated wallet publish status after save:', {
              isWalletPublishedInFirestore: updatedMetadata?._isWalletPublishedInFirestore,
              currentWallet: walletAddress,
              published: updatedWalletPublished
            });
            
            // Name: published if name exists in Firestore (not null/undefined/empty)
            const updatedFirestoreName = updatedMetadata?.name;
            const updatedNameIsPublished = !!(updatedFirestoreName && typeof updatedFirestoreName === 'string' && updatedFirestoreName.trim() !== '');
            console.log('🔄 Updated name publish status after save:', {
              updatedFirestoreName,
              updatedNameIsPublished
            });
            setPublishedName(updatedNameIsPublished);
            
            // Bio: published if bio exists in Firestore (not null/undefined/empty)
            const updatedFirestoreBio = updatedMetadata?.bio;
            const updatedBioIsPublished = !!(updatedFirestoreBio && typeof updatedFirestoreBio === 'string' && updatedFirestoreBio.trim() !== '');
            console.log('🔄 Updated bio publish status after save:', {
              updatedFirestoreBio,
              bioExists: updatedFirestoreBio !== undefined,
              updatedBioIsPublished,
              expectedPublished: isPublished && elementType === 'bio'
            });
            setPublishedBio(updatedBioIsPublished);
            
            // Preferred Contact: published if preferredContactMethod exists in Firestore (not null/undefined/empty)
            const updatedFirestorePreferredContact = updatedMetadata?.preferredContactMethod;
            const updatedPreferredContactIsPublished = !!(updatedFirestorePreferredContact && typeof updatedFirestorePreferredContact === 'string' && updatedFirestorePreferredContact.trim() !== '');
            console.log('🔄 Updated preferredContact publish status after save:', {
              updatedFirestorePreferredContact,
              preferredContactExists: updatedFirestorePreferredContact !== undefined,
              updatedPreferredContactIsPublished,
              expectedPublished: isPublished && elementType === 'preferredContact'
            });
            setPublishedPreferredContact(updatedPreferredContactIsPublished);
            
            // Relationship: published if relationshipStatus exists in Firestore
            const updatedFirestoreRelationship = updatedMetadata?.relationshipStatus;
            const updatedRelationshipIsPublished = !!(updatedFirestoreRelationship && typeof updatedFirestoreRelationship === 'string' && updatedFirestoreRelationship.trim() !== '');
            setPublishedRelationship(updatedRelationshipIsPublished);
            
            // Looking For: published if lookingFor exists in Firestore
            const updatedFirestoreLookingFor = updatedMetadata?.lookingFor;
            const updatedLookingForIsPublished = !!(updatedFirestoreLookingFor && typeof updatedFirestoreLookingFor === 'string' && updatedFirestoreLookingFor.trim() !== '');
            setPublishedLookingFor(updatedLookingForIsPublished);
            
            // Languages: published if languages exists in Firestore
            const updatedFirestoreLanguages = updatedMetadata?.languages;
            const updatedLanguagesIsPublished = !!(updatedFirestoreLanguages && typeof updatedFirestoreLanguages === 'string' && updatedFirestoreLanguages.trim() !== '');
            setPublishedLanguages(updatedLanguagesIsPublished);
            
            // Education: published if education exists in Firestore
            const updatedFirestoreEducation = updatedMetadata?.education;
            const updatedEducationIsPublished = !!(updatedFirestoreEducation && typeof updatedFirestoreEducation === 'string' && updatedFirestoreEducation.trim() !== '');
            setPublishedEducation(updatedEducationIsPublished);
            
            // Occupation: published if occupation exists in Firestore
            const updatedFirestoreOccupation = updatedMetadata?.occupation;
            const updatedOccupationIsPublished = !!(updatedFirestoreOccupation && typeof updatedFirestoreOccupation === 'string' && updatedFirestoreOccupation.trim() !== '');
            setPublishedOccupation(updatedOccupationIsPublished);
            
            break; // Success, exit retry loop
          }
        } catch (reloadError) {
          console.error('Error reloading metadata after publish (attempt ' + (retryCount + 1) + '):', reloadError);
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      if (!updatedMetadata) {
        console.warn('⚠️ Failed to reload metadata after save, but save operation completed');
      }

      // Reload markers only when the change affects the map (own marker
      // visibility depends on published city/location; wallet affects messaging
      // hooks tied to markers). Other field publishes don't change the map.
      if (elementType === 'city' || elementType === 'wallet') {
        loadAllProfilesAndCreateMarkers().catch(error => {
          console.error('Error reloading markers after publish:', error);
        });
      }
    } catch (error) {
      console.error('Error updating publish status:', error);
      // Revert optimistic update on error
      setPublishedTags(originalPublishedTags);
      setPublishedSocials(originalPublishedSocials);
      setPublishedBirthDate(originalPublishedBirthDate);
      setPublishedCity(originalPublishedCity);
      setPublishedWallet(originalPublishedWallet);
      setPublishedEmail(originalPublishedEmail);
      setPublishedName(originalPublishedName);
      setPublishedBio(originalPublishedBio);
      setPublishedPreferredContact(originalPublishedPreferredContact);
      setPublishedRelationship(originalPublishedRelationship);
      setPublishedLookingFor(originalPublishedLookingFor);
      setPublishedLanguages(originalPublishedLanguages);
      setPublishedEducation(originalPublishedEducation);
      setPublishedOccupation(originalPublishedOccupation);
      setShowPublishedNotification(null);
      alert('Error updating publish status. Please try again.');
    } finally {
      // Hide notification and unblock UI after save completes
      setIsUpdatingPublish(false);
      setShowPublishedNotification(null);
    }
  };

  // Publish all unpublished profile elements at once
  const publishAllUnpublished = async () => {
    if (!walletAddress || !currentProfile || currentProfile.username !== currentUser) {
      return;
    }

    setIsPublishingAll(true);
    setShowPublishAllConfirm(false);

    try {
      // Determine what items exist and can be published
      const hasTags = Array.isArray(currentProfile.tagsList) && currentProfile.tagsList.length > 0;
      const hasSocials = Array.isArray(currentProfile.socialLinks) && currentProfile.socialLinks.length > 0;
      const hasMusic = Array.isArray(currentProfile.musicList) && currentProfile.musicList.length > 0;
      const hasGroups = Array.isArray(profileGroups) && profileGroups.length > 0;
      const hasName = currentProfile.name && currentProfile.name.trim() !== '';
      const hasBio = currentProfile.bio && currentProfile.bio.trim() !== '';
      const hasEmail = currentProfile.email && currentProfile.email.trim() !== '';
      const hasBirthDate = currentProfile.birthDate && currentProfile.birthDate.trim() !== '';
      const hasCity = currentProfile.city && currentProfile.city.trim() !== '';
      const hasPreferredContact = currentProfile.preferredContactMethod && currentProfile.preferredContactMethod.trim() !== '';
      const hasRelationship = currentProfile.relationshipStatus && currentProfile.relationshipStatus.trim() !== '';
      const hasLookingFor = currentProfile.lookingFor && currentProfile.lookingFor.trim() !== '';
      const hasLanguages = currentProfile.languages && currentProfile.languages.trim() !== '';
      const hasEducation = currentProfile.education && currentProfile.education.trim() !== '';
      const hasOccupation = currentProfile.occupation && currentProfile.occupation.trim() !== '';
      // Set all published states to true for items that have values
      const newPublishedTags = hasTags ? [...currentProfile.tagsList] : [];
      const newPublishedSocials = hasSocials ? [...currentProfile.socialLinks] : [];
      const newPublishedMusic = hasMusic ? [...currentProfile.musicList] : [];
      const newPublishedGroups = hasGroups ? [...profileGroups] : [];

      // Update all local states
      setPublishedTags(newPublishedTags);
      setPublishedSocials(newPublishedSocials);
      setPublishedMusic(newPublishedMusic);
      setPublishedGroups(newPublishedGroups);
      if (hasName) setPublishedName(true);
      if (hasBio) setPublishedBio(true);
      if (hasEmail) setPublishedEmail(true);
      if (hasBirthDate) setPublishedBirthDate(true);
      if (hasCity) setPublishedCity(true);
      setPublishedWallet(true); // Always publish wallet
      if (hasPreferredContact) setPublishedPreferredContact(true);
      if (hasRelationship) setPublishedRelationship(true);
      if (hasLookingFor) setPublishedLookingFor(true);
      if (hasLanguages) setPublishedLanguages(true);
      if (hasEducation) setPublishedEducation(true);
      if (hasOccupation) setPublishedOccupation(true);
      // Prepare published metadata for Firestore
      const publishedMetadata = {
        username: currentProfile.username,
        publishedTags: newPublishedTags,
        publishedGroups: newPublishedGroups,
        publishedSocials: newPublishedSocials,
        publishedMusic: newPublishedMusic,
        publishedBirthDate: hasBirthDate ? currentProfile.birthDate : null,
        publishedAge: hasBirthDate ? currentProfile.age : null,
        publishedCity: hasCity ? currentProfile.city : null,
        publishedLocation: hasCity ? currentProfile.location : null,
        publishedWallet: true,
        publishedEmail: hasEmail ? currentProfile.email : null,
        publishedName: hasName ? currentProfile.name : null,
        publishedBio: hasBio ? currentProfile.bio : null,
        publishedPreferredContact: hasPreferredContact ? currentProfile.preferredContactMethod : null,
        publishedRelationship: hasRelationship ? currentProfile.relationshipStatus : null,
        publishedLookingFor: hasLookingFor ? currentProfile.lookingFor : null,
        publishedLanguages: hasLanguages ? currentProfile.languages : null,
        publishedEducation: hasEducation ? currentProfile.education : null,
        publishedOccupation: hasOccupation ? currentProfile.occupation : null,
      };

      // Save to Firestore
      console.log('💾 Publishing all unpublished items to Firestore');
      await updatePublishedMetadata(walletAddress, publishedMetadata);
      console.log('✅ All items published successfully');

      // Show success notification
      setNotification({
        message: 'All profile items published successfully!',
        type: 'success'
      });
      setTimeout(() => setNotification(null), 3000);

    } catch (error) {
      console.error('Error publishing all items:', error);
      setNotification({
        message: 'Error publishing items. Please try again.',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsPublishingAll(false);
    }
  };

  // Unpublish all published profile elements at once
  const unpublishAllPublished = async () => {
    if (!walletAddress || !currentProfile || currentProfile.username !== currentUser) {
      return;
    }

    setIsUnpublishingAll(true);
    setShowUnpublishAllConfirm(false);

    try {
      // Set all published states to false/empty
      setPublishedTags([]);
      setPublishedGroups([]);
      setPublishedSocials([]);
      setPublishedMusic([]);
      setPublishedName(false);
      setPublishedBio(false);
      setPublishedEmail(false);
      setPublishedBirthDate(false);
      setPublishedCity(false);
      setPublishedWallet(false);
      setPublishedPreferredContact(false);
      setPublishedRelationship(false);
      setPublishedLookingFor(false);
      setPublishedLanguages(false);
      setPublishedEducation(false);
      setPublishedOccupation(false);

      // Prepare empty published metadata for Firestore
      const publishedMetadata = {
        username: currentProfile.username,
        publishedTags: [],
        publishedGroups: [],
        publishedSocials: [],
        publishedMusic: [],
        publishedBirthDate: null,
        publishedAge: null,
        publishedCity: null,
        publishedLocation: null,
        publishedWallet: false,
        publishedEmail: null,
        publishedName: null,
        publishedBio: null,
        publishedPreferredContact: null,
        publishedRelationship: null,
        publishedLookingFor: null,
        publishedLanguages: null,
        publishedEducation: null,
        publishedOccupation: null,
      };

      // Save to Firestore
      console.log('💾 Unpublishing all items from Firestore');
      await updatePublishedMetadata(walletAddress, publishedMetadata);
      console.log('✅ All items unpublished successfully');

      // Show success notification
      setNotification({
        message: 'All profile items unpublished successfully!',
        type: 'success'
      });
      setTimeout(() => setNotification(null), 3000);

    } catch (error) {
      console.error('Error unpublishing all items:', error);
      setNotification({
        message: 'Error unpublishing items. Please try again.',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsUnpublishingAll(false);
    }
  };

  // Load user profile based on wallet address
  // skipUnifiedAuth: if true, skip encryption key auth (already handled by unified auth)
  const loadUserProfile = async (address, skipUnifiedAuth = false) => {
      if (!address) {
        setCurrentUser(null);
        setCurrentProfile(null);
        setProfileNetwork(null);
        currentProfileWalletAddressRef.current = null;
        setViewedProfileWalletAddress(null);
        setProfileVisible(false);
        setShowRegistrationForm(false);
        return;
      }
    
    // Store the wallet address used to load this profile (normalize to lowercase)
    const normalizedAddress = address.toLowerCase();
    
    // CRITICAL FIX: Check if this is the current user's profile IMMEDIATELY
    // This must happen before any async operations to prevent race conditions.
    // Use the ref as fallback: when called right after a wallet connect, the
    // walletAddress state from closure is still stale, but the ref was synced
    // synchronously in the connect handler.
    const effectiveWalletAddress = walletAddress || walletAddressRef.current;
    const isCurrentUserProfile = effectiveWalletAddress && normalizedAddress === effectiveWalletAddress.toLowerCase();
    if (isCurrentUserProfile) {
      // Set flag immediately to prevent "other profile" flash
      isLoadingMyProfileRef.current = true;
      console.log('✅ [loadUserProfile] Detected own profile - setting flag immediately');
    } else {
      // Clear flag for other users' profiles
      isLoadingMyProfileRef.current = false;
      console.log('ℹ️ [loadUserProfile] Loading other user profile');
    }
    
    // Check if this wallet's profile was deleted - if so, verify with blockchain before showing registration form
    // If profile was re-registered, the blockchain will have a new CID and we should load it
    // Add timeout to prevent blocking (2s max)
    const wasDeleted = localStorage.getItem(`profileDeleted_${normalizedAddress}`);
    if (wasDeleted === 'true') {
      console.log('🗑️ Profile deletion flag found - verifying with blockchain...');
      // Quick check: if profile exists on Arc (Circle) blockchain, it was re-registered, so clear the flag and continue
      // Only check Arc (Circle) - ignore Arbitrum (all users should be on Arc now)
      try {
        const quickCidCheck = await Promise.race([
          getCIDFromArc(address),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Blockchain check timeout')), 2000))
        ]).catch(err => {
          if (err.message === 'Blockchain check timeout') {
            console.log('⚠️ Blockchain check timed out, assuming profile was deleted');
            return null;
          }
          throw err;
        });
        
        if (quickCidCheck) {
          console.log('✅ Profile exists on Arc (Circle) blockchain despite deletion flag - clearing flag and loading profile');
          localStorage.removeItem(`profileDeleted_${normalizedAddress}`);
          // Continue loading instead of returning early
        } else {
          console.log('❌ Profile not found on Arc (Circle) blockchain - showing registration form');
          setCurrentUser(null);
          setCurrentProfile(null);
          currentProfileWalletAddressRef.current = normalizedAddress;
          // Only set viewedProfileWalletAddress if it's not the current user's profile
          if (!isCurrentUserProfile) {
            setViewedProfileWalletAddress(normalizedAddress);
          } else {
            setViewedProfileWalletAddress(null);
          }
          setShowRegistrationForm(true);
          setProfileVisible(true);
          setAboutVisible(false);
          setIsLoadingUser(false);
          return;
        }
      } catch (cidCheckError) {
        console.warn('⚠️ Could not verify with blockchain, assuming profile was deleted:', cidCheckError);
        setCurrentUser(null);
        setCurrentProfile(null);
        currentProfileWalletAddressRef.current = normalizedAddress;
        // Only set viewedProfileWalletAddress if it's not the current user's profile
        if (!isCurrentUserProfile) {
          setViewedProfileWalletAddress(normalizedAddress);
        } else {
          setViewedProfileWalletAddress(null);
        }
        setShowRegistrationForm(true);
        setProfileVisible(true);
        setAboutVisible(false);
        setIsLoadingUser(false);
        return;
      }
    }

    currentProfileWalletAddressRef.current = normalizedAddress;
    // Only set viewedProfileWalletAddress for other users' profiles (not for own profile)
    // This helps isMyProfile() function correctly identify own profile
    if (!isCurrentUserProfile) {
      setViewedProfileWalletAddress(normalizedAddress);
    } else {
      // Clear viewedProfileWalletAddress for own profile
      setViewedProfileWalletAddress(null);
    }

    // Check current route - don't change visibility if on /about, /groups, /messages, or /tagrun routes
    const currentPath = normalizedPathname;
    const isOnAboutPage = currentPath.startsWith('/about');
    const isOnGroupsPage = currentPath === '/groups';
    const isOnMessagesPage = currentPath.startsWith('/messages');
    const isOnTagRunPage = currentPath === '/tagrun';
    const shouldPreserveRoute = isOnAboutPage || isOnGroupsPage || isOnMessagesPage || isOnTagRunPage;

    try {
      console.log('Loading user profile for address:', address);
      
      // Generate encryption key first (needed to decrypt profile from IPFS)
      let profileEncryptionKey = encryptionKeyRef.current || encryptionKey;
      console.log('🔑 Encryption key check:', { 
        hasKeyFromRef: !!encryptionKeyRef.current, 
        hasKeyFromState: !!encryptionKey,
        hasKey: !!profileEncryptionKey,
        skipUnifiedAuth 
      });
      if (!profileEncryptionKey && !skipUnifiedAuth) {
        // Generate encryption key if not available
        try {
          const keyData = await generateEncryptionKeyFromSignature(getSavedWalletSignature()?.signature);
          profileEncryptionKey = keyData;
          setEncryptionKey(keyData);
          encryptionKeyRef.current = keyData;
        } catch (keyError) {
          console.warn('Could not generate encryption key for profile loading:', keyError);
        }
      }
      
      // Load Firestore metadata first (fast) and show profile immediately, then load IPFS in background
      // Firestore has shorter timeout (2s for very fast loading), IPFS can take longer
      const firestorePromise = Promise.race([
        getUserProfileByWallet(address, null), // No encryption key = only Firestore metadata
        new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore timeout')), 2000))
      ]).catch(err => {
        if (err.message === 'Firestore timeout') {
          console.log('Firestore metadata load timed out');
          return null;
        }
        console.error('Error loading Firestore metadata:', err);
        return null;
      });
      
      // Create a separate timeout for IPFS promise (reduced to 6s for faster failure)
      const ipfsTimeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('IPFS load timeout')), 6000); // 6 seconds for IPFS
      });
      const ipfsPromise = Promise.race([
        getUserProfileByWallet(address, profileEncryptionKey), // With encryption key = full IPFS data
        ipfsTimeoutPromise
      ]).catch(err => {
        if (err.message === 'IPFS load timeout') {
          console.log('IPFS profile load timed out (will retry in background)');
          return null;
        }
        console.error('Error loading profile from IPFS:', err);
        return null;
      });
      
      // Load Firestore first (fast) to get metadata and username
      const firestoreMetadata = await firestorePromise;
      
      // REVISED: Set currentUser for own profile (flag already set above)
      if (isCurrentUserProfile && firestoreMetadata && firestoreMetadata.username) {
        setCurrentUser(firestoreMetadata.username);
        console.log('✅ [loadUserProfile] Set currentUser from Firestore metadata:', firestoreMetadata.username);
      } else if (!isCurrentUserProfile) {
        // Ensure flag is cleared and currentUser is null for other users' profiles
        isLoadingMyProfileRef.current = false;
        setCurrentUser(null); // Clear to indicate this is NOT "My Profile"
      }
      
      // Set loading state and show loading screen while waiting for full IPFS profile
      // This ensures unpublished fields are available immediately when profile is shown
      // Only show loading screen for current user's profile (with encryption key)
      // For other users (including test users), show profile immediately from Firestore metadata WITHOUT big loading screen
      if (profileEncryptionKey && isCurrentUserProfile) {
        // CRITICAL: Only set loading state if we don't already have a matching profile
        // This prevents clearing a valid profile when navigating to /myprofile
        if (!currentProfile || currentProfile.walletAddress?.toLowerCase() !== normalizedAddress) {
          setIsLoadingProfileFromIPFS(true);
        }
        setProfileVisible(true);
        setShowRegistrationForm(false);
        setIsLoadingUser(false);
      } else if (firestoreMetadata && !currentProfile) {
        // For other users' profiles (including test users), show immediately from Firestore
        // Don't show big loading screen - just show the profile data
        setIsLoadingProfileFromIPFS(false); // CRITICAL: Don't show loading screen for other users
        // If no encryption key, show Firestore-only profile (for other users' profiles)
        console.log('🚀 Showing profile from Firestore metadata (no encryption key for IPFS)');
        const cleanMetadata = cleanProfileData(firestoreMetadata);
        // CRITICAL FIX: Set currentUser BEFORE currentProfile to prevent "other profile" flash
        // React batches these updates, but setting currentUser first ensures it's set when profile renders
        // Note: isCurrentUserProfile is already defined at function level
        if (isCurrentUserProfile && cleanMetadata.username) {
          setCurrentUser(cleanMetadata.username);
          console.log('✅ [loadUserProfile] Set currentUser BEFORE setting currentProfile:', cleanMetadata.username);
        } else {
          setCurrentUser(null); // Clear currentUser to indicate this is NOT "My Profile"
        }
        // Now set currentProfile - React will batch these updates together
        setCurrentProfile(cleanMetadata);
        setShowRegistrationForm(false);
        setIsLoadingUser(false);
        setIsLoadingProfileFromIPFS(false); // CRITICAL: Clear loading state for other users
        // Set published states from Firestore metadata
        setPublishedName(!!(firestoreMetadata?.name && firestoreMetadata.name.trim() !== ''));
        setPublishedBio(!!(firestoreMetadata?.bio && firestoreMetadata.bio.trim() !== ''));
        setPublishedEmail(!!(firestoreMetadata?.email && firestoreMetadata.email.trim() !== ''));
        setPublishedPreferredContact(!!(firestoreMetadata?.preferredContactMethod && firestoreMetadata.preferredContactMethod.trim() !== ''));
        setPublishedRelationship(!!(firestoreMetadata?.relationshipStatus && firestoreMetadata.relationshipStatus.trim() !== ''));
        setPublishedLookingFor(!!(firestoreMetadata?.lookingFor && firestoreMetadata.lookingFor.trim() !== ''));
        setPublishedLanguages(!!(firestoreMetadata?.languages && firestoreMetadata.languages.trim() !== ''));
        setPublishedEducation(!!(firestoreMetadata?.education && firestoreMetadata.education.trim() !== ''));
        setPublishedOccupation(!!(firestoreMetadata?.occupation && firestoreMetadata.occupation.trim() !== ''));
        setPublishedWallet(firestoreMetadata?._isWalletPublishedInFirestore === true);
        // Tags, socials, music
        if (firestoreMetadata.tagsList) setPublishedTags(firestoreMetadata.tagsList);
        if (firestoreMetadata.socialLinks) setPublishedSocials(firestoreMetadata.socialLinks);
        if (firestoreMetadata.musicList) setPublishedMusic(firestoreMetadata.musicList);
        setPublishedBirthDate(!!firestoreMetadata?.birthDate);
        setPublishedCity(!!firestoreMetadata?.city);
        setIsLoadingProfileFromIPFS(false);
      }
      
      // Note: Blockchain query is already done in getUserProfileByWallet, so we don't need to do it again here
      // Set network to Arc (Circle) (default for all users now)
      setProfileNetwork('base');
      localStorage.setItem(`profile_network_${address.toLowerCase()}`, 'base');
      
      // Load IPFS data - wait for it if we have encryption key (for own profile)
      // This ensures all fields (published + unpublished) are available immediately
      let profileData = null;
      console.log('🔍 Profile load check:', {
        hasEncryptionKey: !!profileEncryptionKey,
        hasFirestoreMetadata: !!firestoreMetadata,
        alreadyShown: !!currentProfile
      });
      
      // Always await ipfsPromise if we have encryption key - it has all the data we need
      if (profileEncryptionKey) {
        try {
          console.log('⏳ Awaiting ipfsPromise (encryption key available - loading full profile with unpublished fields)...');
          profileData = await ipfsPromise;
          console.log('📦 ipfsPromise resolved:', { hasProfileData: !!profileData, username: profileData?.username });
        } catch (ipfsAwaitError) {
          console.error('Error awaiting ipfsPromise:', ipfsAwaitError);
          profileData = null;
        }
      }
      
      // If we got profile data from IPFS, use it immediately
      if (profileData) {
        // Clear any stale "deleted" flag since profile was successfully loaded
        // This handles cases where profile was re-registered but flag wasn't cleared properly
        localStorage.removeItem(`profileDeleted_${normalizedAddress}`);
        
        // Update profile with full IPFS data
        // IPFS hash comes from blockchain (already in profileData from getUserProfileByWallet)
        const profileWithHash = {
          ...profileData,
          ipfsHash: profileData.ipfsHash || null
        };
        const cleanIPFSProfile = cleanProfileData(profileWithHash);
        
        // CRITICAL FIX: Merge Firestore metadata with IPFS profile
        // Firestore is source of truth for PUBLISHED fields, IPFS has ALL data (published + unpublished)
        // This prevents IPFS (which may have stale data) from overwriting fresh Firestore data after a save
        // BUT: If Firestore value is null/undefined/empty (unpublished), use IPFS value instead
        const mergedProfile = { ...cleanIPFSProfile };
        
        // If we have Firestore metadata, use it for published fields only
        // This is the fix for the bug where bio reverts to old value after refresh
        if (firestoreMetadata) {
          // Only use Firestore bio if it's a non-empty string (published)
          // If it's null/undefined/empty (unpublished), use IPFS bio instead (IPFS has the actual unpublished value)
          if (firestoreMetadata.bio && typeof firestoreMetadata.bio === 'string' && firestoreMetadata.bio.trim() !== '') {
            mergedProfile.bio = firestoreMetadata.bio;
          }
          // Only use Firestore name if it's a non-empty string (published)
          if (firestoreMetadata.name && typeof firestoreMetadata.name === 'string' && firestoreMetadata.name.trim() !== '') {
            mergedProfile.name = firestoreMetadata.name;
          }
          // Only use Firestore email if it's a non-empty string (published)
          if (firestoreMetadata.email && typeof firestoreMetadata.email === 'string' && firestoreMetadata.email.trim() !== '') {
            mergedProfile.email = firestoreMetadata.email;
          }
        }
        
        // Log the merge to verify it's working
        const ipfsBioPreview = cleanIPFSProfile?.bio ? cleanIPFSProfile.bio.substring(0, 50) + '...' : 'null';
        const firestoreBioPreview = firestoreMetadata?.bio !== undefined 
          ? (firestoreMetadata.bio ? firestoreMetadata.bio.substring(0, 50) + '...' : 'null') 
          : 'undefined';
        const mergedBioPreview = mergedProfile?.bio !== undefined 
          ? (mergedProfile.bio ? mergedProfile.bio.substring(0, 50) + '...' : 'null') 
          : 'undefined';
        console.log('🔄 MERGING IPFS with Firestore (Firestore takes precedence):', {
          hasFirestoreMetadata: !!firestoreMetadata,
          ipfsBio: ipfsBioPreview,
          firestoreBio: firestoreBioPreview,
          mergedBio: mergedBioPreview,
          bioWasOverridden: cleanIPFSProfile?.bio !== mergedProfile?.bio
        });
        
        setCurrentProfile(mergedProfile);
        // REVISED: Set currentUser if this is actually the current user's profile (flag already set above)
        if (isCurrentUserProfile) {
          setCurrentUser(cleanIPFSProfile.username);
          console.log('✅ [loadUserProfile] Set currentUser from IPFS profile:', cleanIPFSProfile.username);
        } else {
          // Ensure flag is cleared and currentUser is null for other users' profiles
          isLoadingMyProfileRef.current = false;
          setCurrentUser(null); // Clear currentUser to indicate this is NOT "My Profile"
        }
        // Network is already set above when detecting profile network
        setShowRegistrationForm(false);
        setIsLoadingUser(false);
        setIsLoadingProfileFromIPFS(false);
        
        // CRITICAL FIX: If firestoreMetadata is null (timed out), retry fetching it for current user's profile
        // This ensures published states are correctly loaded even if initial Firestore query timed out
        let metadataForPublishedStates = firestoreMetadata;
        if (isCurrentUserProfile && !firestoreMetadata) {
          console.log('⚠️ [loadUserProfile] Firestore metadata timed out, retrying with fetchFirestoreMetadataWithRetry...');
          try {
            metadataForPublishedStates = await fetchFirestoreMetadataWithRetry(normalizedAddress);
            if (metadataForPublishedStates) {
              console.log('✅ [loadUserProfile] Successfully loaded Firestore metadata on retry');
            } else {
              console.warn('⚠️ [loadUserProfile] Firestore metadata still null after retry, published states will be inferred from IPFS');
            }
          } catch (retryError) {
            console.error('❌ [loadUserProfile] Error retrying Firestore metadata:', retryError);
          }
        }
        
        // Update published states
        const firestoreTags = metadataForPublishedStates?.tagsList || [];
        const firestoreSocials = metadataForPublishedStates?.socialLinks || [];
        const firestoreMusic = metadataForPublishedStates?.musicList || [];
        const publishedTagsList = cleanIPFSProfile.tagsList.filter(tag => firestoreTags.includes(tag));
        const publishedSocialsList = cleanIPFSProfile.socialLinks.filter(social => firestoreSocials.includes(social));
        const publishedMusicList = (cleanIPFSProfile.musicList || []).filter(song => firestoreMusic.includes(song));
        setPublishedTags(publishedTagsList);
        setPublishedSocials(publishedSocialsList);
        setPublishedMusic(publishedMusicList);
        setPublishedBirthDate(!!metadataForPublishedStates?.birthDate && metadataForPublishedStates.birthDate === cleanIPFSProfile.birthDate);
        setPublishedCity(!!metadataForPublishedStates?.city && metadataForPublishedStates.city === cleanIPFSProfile.city);
        // Wallet: use _isWalletPublishedInFirestore flag (walletAddress always has fallback)
        setPublishedWallet(metadataForPublishedStates?._isWalletPublishedInFirestore === true);
        setPublishedEmail(!!(metadataForPublishedStates?.email && metadataForPublishedStates.email.trim() !== ''));
        setPublishedName(!!(metadataForPublishedStates?.name && metadataForPublishedStates.name.trim() !== ''));
        setPublishedBio(!!(metadataForPublishedStates?.bio && metadataForPublishedStates.bio.trim() !== ''));
        setPublishedPreferredContact(!!(metadataForPublishedStates?.preferredContactMethod && metadataForPublishedStates.preferredContactMethod.trim() !== ''));
        setPublishedRelationship(!!(metadataForPublishedStates?.relationshipStatus && metadataForPublishedStates.relationshipStatus.trim() !== ''));
        setPublishedLookingFor(!!(metadataForPublishedStates?.lookingFor && metadataForPublishedStates.lookingFor.trim() !== ''));
        setPublishedLanguages(!!(metadataForPublishedStates?.languages && metadataForPublishedStates.languages.trim() !== ''));
        setPublishedEducation(!!(metadataForPublishedStates?.education && metadataForPublishedStates.education.trim() !== ''));
        setPublishedOccupation(!!(metadataForPublishedStates?.occupation && metadataForPublishedStates.occupation.trim() !== ''));
        
        // Profile loaded successfully - return early to prevent any further state changes
        console.log('✅ Profile loaded successfully from IPFS, returning early');
        return;
      }
      
      // Load encrypted data from IPFS in parallel (for "My encryption data" section)
      // Use profileData.ipfsHash (already includes blockchain hash from getUserProfileByWallet)
      const ipfsHashForEncrypted = profileData?.ipfsHash || firestoreMetadata?.ipfsHash;
      let encryptedDataFromIPFS = null;
      
      // Try to load encrypted data from IPFS if we have hash and encryption key
      if (ipfsHashForEncrypted && profileEncryptionKey) {
        try {
          // Create a fresh timeout promise (don't reuse timeoutPromise which started earlier)
          const encryptedDataTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Encrypted data load timeout')), 15000);
          });
          encryptedDataFromIPFS = await Promise.race([
            fetchFromIPFS(ipfsHashForEncrypted),
            encryptedDataTimeoutPromise
          ]).catch((err) => {
            console.warn('Failed to load encrypted data from IPFS (will retry later):', err);
            return null;
          });
        } catch (err) {
          console.warn('Error loading encrypted data from IPFS (will retry later):', err);
        }
      }
      
      // Helper function to get IPFS hash from current profile or blockchain
      const getIpfsHash = () => {
        return currentProfile?.ipfsHash || profileData?.ipfsHash || firestoreMetadata?.ipfsHash || null;
      };
      
      // Check if profile exists
      // Profile exists if firestoreMetadata exists (Firestore is source of truth for registration)
      // OR if IPFS hash exists on blockchain (profile is registered even if Firestore query timed out)
      // This prevents showing registration form when profile exists but Firestore query timed out
      let profileExists = !!firestoreMetadata;
      
      // If Firestore timed out but we have IPFS hash, profile definitely exists
      // Retry Firestore with longer timeout to verify
      if (!profileExists && getIpfsHash()) {
        console.log('⚠️ Firestore timed out but IPFS hash exists on blockchain - profile is registered, retrying Firestore...');
        try {
          const retryFirestoreMetadata = await Promise.race([
            getUserProfileByWallet(address, null),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore retry timeout')), 10000))
          ]).catch(err => {
            if (err.message === 'Firestore retry timeout') {
              console.log('Firestore retry also timed out, but profile exists (IPFS hash on blockchain)');
              return null;
            }
            console.error('Error in Firestore retry:', err);
            return null;
          });
          
          if (retryFirestoreMetadata) {
            console.log('✅ Firestore retry succeeded - profile exists');
            profileExists = true;
            // Use retried metadata
            if (!firestoreMetadata) {
              // Update published states from retried metadata
              const metadata = retryFirestoreMetadata;
              setPublishedName(!!(metadata?.name && metadata.name.trim() !== ''));
              setPublishedBio(!!(metadata?.bio && metadata.bio.trim() !== ''));
              setPublishedEmail(!!(metadata?.email && metadata.email.trim() !== ''));
              setPublishedPreferredContact(!!(metadata?.preferredContactMethod && metadata.preferredContactMethod.trim() !== ''));
              setPublishedRelationship(!!(metadata?.relationshipStatus && metadata.relationshipStatus.trim() !== ''));
              setPublishedLookingFor(!!(metadata?.lookingFor && metadata.lookingFor.trim() !== ''));
              setPublishedLanguages(!!(metadata?.languages && metadata.languages.trim() !== ''));
              setPublishedEducation(!!(metadata?.education && metadata.education.trim() !== ''));
              setPublishedOccupation(!!(metadata?.occupation && metadata.occupation.trim() !== ''));
              // Wallet: use _isWalletPublishedInFirestore flag (walletAddress always has fallback)
              setPublishedWallet(metadata?._isWalletPublishedInFirestore === true);
            }
          } else {
            // Firestore retry failed but IPFS hash exists
            // Check if we have VALID profile data (with username) - not just empty objects
            console.log('⚠️ Firestore retry failed but IPFS hash exists on Arc (Circle)');
            const hasValidProfileData = profileData && profileData.username;
            
            if (hasValidProfileData) {
              // We have valid decrypted profile data, so profile exists
              console.log('✅ Valid profile data loaded - profile exists');
              profileExists = true;
            } else {
              // No Firestore metadata AND no valid profile data = orphaned/incomplete profile, allow re-registration
              // Encrypted data alone doesn't count - we need actual decrypted profile data
              console.log('⚠️ CID exists on Arc (Circle) but no Firestore metadata and no valid profile data - allowing re-registration');
              profileExists = false;
            }
          }
        } catch (retryErr) {
          console.error('Error during Firestore retry:', retryErr);
          // Only consider profile exists if we have VALID profile data (with username), not just a CID
          const hasValidProfileData = profileData && profileData.username;
          
          if (getIpfsHash() && hasValidProfileData) {
            profileExists = true;
          } else {
            // CID exists but no valid profile data = orphaned profile, allow re-registration
            // Encrypted data alone doesn't count - we need actual decrypted profile data
            console.log('⚠️ CID exists on Arc (Circle) but no valid profile data - allowing re-registration');
            profileExists = false;
          }
        }
      }
      
      const ipfsHashForCheck = getIpfsHash();
      console.log('🔍 Profile existence check:', {
        hasMetadata: !!firestoreMetadata,
        hasIpfsHash: !!ipfsHashForCheck,
        ipfsHash: ipfsHashForCheck,
        profileExists,
        hasProfileData: !!profileData,
        encryptedDataLoaded: !!encryptedDataFromIPFS
      });
      
      if (!profileExists) {
        // If CID exists but we have no data, allow re-registration (orphaned profile)
        // Only do final verification if we don't have CID
        let finalVerification = false;
        if (!ipfsHashForCheck) {
          try {
            console.log('🔍 Final verification: checking Arc (Circle) blockchain for IPFS hash...');
            const verificationHash = await Promise.race([
              getCIDFromArc(address),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Verification timeout')), 8000))
            ]);
            if (verificationHash) {
              // CID exists but we have no data - check if we have VALID profile data
              console.log('⚠️ CID found on Arc (Circle) but checking if we have valid profile data...');
              const hasValidProfileData = profileData && profileData.username;
              
              // If we still don't have valid profile data, allow re-registration (orphaned profile)
              // Encrypted data alone doesn't count - we need actual decrypted profile data
              if (!hasValidProfileData) {
                console.log('✅ CID exists but no valid accessible profile data - allowing re-registration (orphaned profile)');
                finalVerification = false; // Don't block registration
              } else {
                console.log('✅ Profile exists (IPFS hash found on Arc (Circle) blockchain) - profile load failed, showing error instead of registration form');
                finalVerification = true;
              }
            }
          } catch (verifyErr) {
            console.log('Final verification failed:', verifyErr);
          }
        } else {
          // We already have CID but no valid data - check if we have valid profile data (with username)
          const hasValidProfileData = profileData && profileData.username;
          
          if (!hasValidProfileData) {
            // No valid profile data - allow re-registration
            // Encrypted data alone doesn't count - we need actual decrypted profile data
            console.log('⚠️ CID exists on Arc (Circle) but no valid profile data - allowing re-registration');
            finalVerification = false;
          } else {
            // We have valid profile data, so profile exists
            console.log('✅ CID exists and we have valid profile data - profile exists');
            finalVerification = true;
          }
        }
        
        if (finalVerification) {
          // Profile exists but load failed - show error instead of registration form
          console.log('❌ Profile exists but failed to load - showing error screen');
          setIsLoadingProfileFromIPFS(false);
          setIsLoadingUser(false);
          setCurrentUser(null);
          setCurrentProfile(null);
          setShowRegistrationForm(false);
          setProfileLoadError('Failed to load your profile. This may be due to network issues. Please refresh the page to try again.');
          if (!shouldPreserveRoute) {
            setProfileVisible(true);
            setAboutVisible(false);
          }
          return;
        }
        
        // No profile found after all checks - show registration form
        // BUT: If this is a test user (has _isTestRecord flag in Firestore), show profile from Firestore instead
        if (firestoreMetadata && firestoreMetadata._isTestRecord === true) {
          // This is a test user - show profile from Firestore metadata even if IPFS loading failed
          console.log('✅ Test user detected - showing profile from Firestore metadata');
          const cleanMetadata = cleanProfileData(firestoreMetadata);
          setCurrentProfile(cleanMetadata);
          setCurrentUser(null); // Test users are never "My Profile"
          setShowRegistrationForm(false);
          setIsLoadingUser(false);
          setIsLoadingProfileFromIPFS(false);
          setProfileLoadError(null);
          // Set published states from Firestore metadata
          setPublishedName(!!(firestoreMetadata?.name && firestoreMetadata.name.trim() !== ''));
          setPublishedBio(!!(firestoreMetadata?.bio && firestoreMetadata.bio.trim() !== ''));
          setPublishedEmail(!!(firestoreMetadata?.email && firestoreMetadata.email.trim() !== ''));
          setPublishedPreferredContact(!!(firestoreMetadata?.preferredContactMethod && firestoreMetadata.preferredContactMethod.trim() !== ''));
          setPublishedRelationship(!!(firestoreMetadata?.relationshipStatus && firestoreMetadata.relationshipStatus.trim() !== ''));
          setPublishedLookingFor(!!(firestoreMetadata?.lookingFor && firestoreMetadata.lookingFor.trim() !== ''));
          setPublishedLanguages(!!(firestoreMetadata?.languages && firestoreMetadata.languages.trim() !== ''));
          setPublishedEducation(!!(firestoreMetadata?.education && firestoreMetadata.education.trim() !== ''));
          setPublishedOccupation(!!(firestoreMetadata?.occupation && firestoreMetadata.occupation.trim() !== ''));
          setPublishedWallet(firestoreMetadata?._isWalletPublishedInFirestore === true);
          setPublishedSocials(!!(firestoreMetadata?.socials && Object.keys(firestoreMetadata.socials).length > 0));
          setPublishedCity(!!firestoreMetadata?.city);
          if (!shouldPreserveRoute) {
            setProfileVisible(true);
            setAboutVisible(false);
          }
          return;
        }
        
        console.log('❌ No profile found - showing registration form');
        setIsLoadingProfileFromIPFS(false);
        setCurrentUser(null);
        setCurrentProfile(null);
        setEncryptedProfileData(null);
        setShowRegistrationForm(true);
        setIsLoadingUser(false);
        setProfileLoadError(null); // Clear any loading errors
        if (!shouldPreserveRoute) {
          setProfileVisible(true); // Show profile section to display registration form
          setAboutVisible(false);
        }
        return;
      }
      
      // Clear any previous loading errors when profile exists
      setProfileLoadError(null);
      
      const ipfsHashForLog = profileData?.ipfsHash || firestoreMetadata?.ipfsHash;
      console.log('✅ Profile exists with IPFS hash:', ipfsHashForLog);
      
      // Use IPFS data if available (has ALL data), otherwise fall back to Firestore metadata (published only)
      // IMPORTANT: If we have IPFS hash from blockchain and encryption key, we should have profileData
      // If profileData is null, it means IPFS load failed - we should retry, not show incomplete Firestore data
      let finalProfileData = profileData;
      
      // Only use Firestore metadata as fallback if we don't have encryption key (viewing someone else's profile)
      // If we have encryption key but no profileData, it means IPFS load failed - we should retry
      // Never show incomplete Firestore data for own profile - show error instead
      if (!finalProfileData && !profileEncryptionKey && firestoreMetadata) {
        // No encryption key = viewing someone else's profile, can only show published data from Firestore
        finalProfileData = firestoreMetadata;
        // CRITICAL: Clear loading state for other users' profiles loaded from Firestore
        setIsLoadingProfileFromIPFS(false);
      } else if (!finalProfileData && profileEncryptionKey && getIpfsHash()) {
        // We have encryption key and IPFS hash but no profileData = IPFS load failed
        // Don't show incomplete Firestore data - will retry below
        console.warn('⚠️ IPFS load failed but we have hash and encryption key - will retry');
      } else if (!finalProfileData && profileEncryptionKey && !getIpfsHash() && firestoreMetadata) {
        // We have encryption key but blockchain query failed - profile exists in Firestore
        // Don't show incomplete data - show error instead
        console.error('❌ Blockchain query failed - cannot load profile without IPFS hash');
      }
      
      // If IPFS load failed but we have hash and encryption key, retry more aggressively
      // This is critical - we need IPFS data for own profile, not Firestore metadata
      const ipfsHashForRetry = getIpfsHash();
      if (!profileData && profileEncryptionKey && ipfsHashForRetry) {
        console.log('⚠️ IPFS profile load failed or timed out, retrying with longer timeout...');
        // Retry IPFS load with longer timeout - this is critical for own profile
        (async () => {
          try {
            const retryTimeout = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('IPFS retry timeout')), 15000); // Longer timeout
            });
            const retryProfileData = await Promise.race([
              getUserProfileByWallet(address, profileEncryptionKey),
              retryTimeout
            ]).catch(err => {
              console.error('IPFS retry failed:', err);
              return null;
            });
            
            if (retryProfileData) {
              console.log('✅ IPFS profile loaded successfully on retry - updating profile...');
              // Update profile with full IPFS data (includes ALL tags/socials/bio)
              const profileWithHash = {
                ...retryProfileData,
                ipfsHash: retryProfileData.ipfsHash || ipfsHashForRetry || null
              };
              const cleanRetryProfile = cleanProfileData(profileWithHash);
              setCurrentProfile(cleanRetryProfile);
              // CRITICAL FIX: Only set currentUser if this is actually the current user's profile
              const isCurrentUserProfile = walletAddress && normalizedAddress === walletAddress.toLowerCase();
              if (isCurrentUserProfile) {
                setCurrentUser(cleanRetryProfile.username);
              } else {
                setCurrentUser(null); // Clear currentUser to indicate this is NOT "My Profile"
              }
              setShowRegistrationForm(false);
              setIsLoadingUser(false);
              setIsLoadingProfileFromIPFS(false); // Clear IPFS loading state
              if (!shouldPreserveRoute) {
                setProfileVisible(true);
              }
              
              // Update published states based on Firestore
              const firestoreTags = firestoreMetadata?.tagsList || [];
              const firestoreSocials = firestoreMetadata?.socialLinks || [];
              const firestoreMusic = firestoreMetadata?.musicList || [];
              const publishedTagsList = cleanRetryProfile.tagsList.filter(tag => firestoreTags.includes(tag));
              const publishedSocialsList = cleanRetryProfile.socialLinks.filter(social => firestoreSocials.includes(social));
              const publishedMusicList = (cleanRetryProfile.musicList || []).filter(song => firestoreMusic.includes(song));
              setPublishedTags(publishedTagsList);
              setPublishedSocials(publishedSocialsList);
              setPublishedMusic(publishedMusicList);
              
              // Update other published states
              setPublishedBirthDate(!!firestoreMetadata?.birthDate && firestoreMetadata.birthDate === cleanRetryProfile.birthDate);
              setPublishedCity(!!firestoreMetadata?.city && firestoreMetadata.city === cleanRetryProfile.city);
              // Wallet: use _isWalletPublishedInFirestore flag (walletAddress always has fallback)
              setPublishedWallet(firestoreMetadata?._isWalletPublishedInFirestore === true);
              setPublishedEmail(!!(firestoreMetadata?.email && firestoreMetadata.email.trim() !== ''));
              setPublishedName(!!(firestoreMetadata?.name && firestoreMetadata.name.trim() !== ''));
              setPublishedBio(!!(firestoreMetadata?.bio && firestoreMetadata.bio.trim() !== ''));
              setPublishedPreferredContact(!!(firestoreMetadata?.preferredContactMethod && firestoreMetadata.preferredContactMethod.trim() !== ''));
            setPublishedRelationship(!!(firestoreMetadata?.relationshipStatus && firestoreMetadata.relationshipStatus.trim() !== ''));
            setPublishedLookingFor(!!(firestoreMetadata?.lookingFor && firestoreMetadata.lookingFor.trim() !== ''));
            setPublishedLanguages(!!(firestoreMetadata?.languages && firestoreMetadata.languages.trim() !== ''));
            setPublishedEducation(!!(firestoreMetadata?.education && firestoreMetadata.education.trim() !== ''));
            setPublishedOccupation(!!(firestoreMetadata?.occupation && firestoreMetadata.occupation.trim() !== ''));
            } else {
              console.error('❌ IPFS retry failed - cannot load full profile data. Please check your connection and try again.');
              // Clear isLoadingUser but keep isLoadingProfileFromIPFS true so error UI shows
              setIsLoadingUser(false);
              // Don't set isLoadingProfileFromIPFS to false - keep it true so error UI shows instead of registration form
              // setIsLoadingProfileFromIPFS(false);
              // Set error state to show error message in UI
              setProfileLoadError('Failed to load your profile data from IPFS. This may be due to network issues or IPFS connectivity problems. Please refresh the page to try again.');
            }
          } catch (retryErr) {
            console.error('Error during IPFS retry:', retryErr);
            // Clear isLoadingUser but keep isLoadingProfileFromIPFS true so error UI shows
            setIsLoadingUser(false);
            // Don't set isLoadingProfileFromIPFS to false - keep it true so error UI shows instead of registration form
            // setIsLoadingProfileFromIPFS(false);
            // Set error state to show error message in UI
            setProfileLoadError('Error loading profile from IPFS. This may be due to network issues or IPFS connectivity problems. Please refresh the page to try again.');
          }
        })();
        
        // Don't show incomplete Firestore data - wait for IPFS retry
        // Set finalProfileData to null so we don't show empty profile
        finalProfileData = null;
      }
      
      // Set encrypted data if loaded, but don't clear existing data if load failed
      // We'll retry loading it later if we have IPFS hash and encryption key
      if (encryptedDataFromIPFS) {
        setEncryptedProfileData(encryptedDataFromIPFS);
        console.log('✅ Encrypted data loaded from IPFS');
      } else if (ipfsHashForEncrypted && profileEncryptionKey) {
        // If we have IPFS hash and encryption key but loading failed, try to reload it
        // Don't clear existing encryptedProfileData - keep it if available
        console.log('⚠️ Encrypted data load failed, will retry after profile setup');
        // Schedule a retry after profile is set up
        setTimeout(async () => {
          try {
            const retryEncryptedData = await fetchFromIPFS(ipfsHashForEncrypted);
            if (retryEncryptedData) {
              setEncryptedProfileData(retryEncryptedData);
              console.log('✅ Encrypted data loaded on retry');
            }
          } catch (retryErr) {
            console.warn('Retry failed to load encrypted data:', retryErr);
          }
        }, 1000);
      }
      
      if (finalProfileData) {
        console.log('🔍 Profile data loaded:', {
          hasName: !!finalProfileData.name,
          name: finalProfileData.name,
          username: finalProfileData.username
        });
      }
      
      if (finalProfileData) {
        // CRITICAL FIX: Prevent overwriting valid profile with empty Firestore data after registration
        // After registration, Firestore has all fields unpublished (empty), but IPFS has full data
        // If we have a valid currentProfile and this is Firestore-only data (no IPFS data), don't overwrite
        // Note: isCurrentUserProfile is already defined at function level (line 6518)
        const isFirestoreOnlyData = !profileData && firestoreMetadata === finalProfileData;
        const hasValidCurrentProfile = currentProfile && 
          currentProfile.username && 
          currentProfile.walletAddress && 
          currentProfile.walletAddress.toLowerCase() === normalizedAddress &&
          // Check if current profile has actual data (not empty)
          (currentProfile.tagsList?.length > 0 || currentProfile.name || currentProfile.bio);
        const newDataIsEmpty = !finalProfileData.tagsList?.length && 
          !finalProfileData.name && 
          !finalProfileData.bio &&
          !finalProfileData.socialLinks?.length &&
          !finalProfileData.musicList?.length;
        
        // If this is own profile, we have a valid currentProfile with data, and new data is empty Firestore data, skip overwriting
        // BUT: Only skip if currentProfile actually exists and has data - if it's null, we need to set it even if Firestore is empty
        // This prevents getting stuck in loading state when navigating to /myprofile
        // CRITICAL: Check that currentProfile exists (not null) before skipping
        if (isCurrentUserProfile && isFirestoreOnlyData && hasValidCurrentProfile && newDataIsEmpty && currentProfile !== null) {
          console.log('🛡️ Preventing overwrite of valid profile with empty Firestore data after registration');
          // Keep existing profile, just update published states from Firestore metadata
          // Set published states from Firestore metadata (even though they're empty after registration)
          setPublishedName(!!(firestoreMetadata?.name && firestoreMetadata.name.trim() !== ''));
          setPublishedBio(!!(firestoreMetadata?.bio && firestoreMetadata.bio.trim() !== ''));
          setPublishedEmail(!!(firestoreMetadata?.email && firestoreMetadata.email.trim() !== ''));
          setPublishedPreferredContact(!!(firestoreMetadata?.preferredContactMethod && firestoreMetadata.preferredContactMethod.trim() !== ''));
          setPublishedRelationship(!!(firestoreMetadata?.relationshipStatus && firestoreMetadata.relationshipStatus.trim() !== ''));
          setPublishedLookingFor(!!(firestoreMetadata?.lookingFor && firestoreMetadata.lookingFor.trim() !== ''));
          setPublishedLanguages(!!(firestoreMetadata?.languages && firestoreMetadata.languages.trim() !== ''));
          setPublishedEducation(!!(firestoreMetadata?.education && firestoreMetadata.education.trim() !== ''));
          setPublishedOccupation(!!(firestoreMetadata?.occupation && firestoreMetadata.occupation.trim() !== ''));
          setPublishedWallet(firestoreMetadata?._isWalletPublishedInFirestore === true);
          if (firestoreMetadata?.tagsList) setPublishedTags(firestoreMetadata.tagsList);
          if (firestoreMetadata?.socialLinks) setPublishedSocials(firestoreMetadata.socialLinks);
          if (firestoreMetadata?.musicList) setPublishedMusic(firestoreMetadata.musicList);
          setPublishedBirthDate(!!firestoreMetadata?.birthDate);
          setPublishedCity(!!firestoreMetadata?.city);
          // Don't update currentProfile - keep the existing one with all data
        } else {
          // Profile exists - completely replace profile state (don't merge with old data)
          // Use cleanProfileData helper to ensure all fields are properly structured
          // Ensure ipfsHash is preserved from blockchain if not in profileData
          // Always include wallet address (even if not published) - we loaded the profile with this address
          const profileWithHash = {
            ...finalProfileData,
            walletAddress: finalProfileData.walletAddress || address, // Always include wallet address
            ipfsHash: finalProfileData.ipfsHash || getIpfsHash() || null
          };
          const cleanProfile = cleanProfileData(profileWithHash);
          
          // Ensure wallet address is preserved after cleaning
          if (!cleanProfile.walletAddress) {
            cleanProfile.walletAddress = address;
          }
          
          // CRITICAL FIX: Only set currentUser if this is actually the current user's profile
          if (isCurrentUserProfile) {
            setCurrentUser(cleanProfile.username);
          } else {
            setCurrentUser(null); // Clear currentUser to indicate this is NOT "My Profile"
          }
          setCurrentProfile(cleanProfile);
        }
        // Ensure wallet address ref is set (should already be set at start of loadUserProfile)
        if (!currentProfileWalletAddressRef.current) {
          currentProfileWalletAddressRef.current = address.toLowerCase();
        }
        setShowRegistrationForm(false);
        setIsLoadingUser(false);
        setIsLoadingProfileFromIPFS(false); // CRITICAL: Always clear loading state when profile is set
        setProfileLoadError(null); // Clear any loading errors on successful load
        if (!shouldPreserveRoute) {
          setProfileVisible(true);
          setAboutVisible(false);
        }
        
        // Note: Profile data is loaded from IPFS and decrypted in getUserProfileByWallet
        // No need to set encryptedData here - it's already decrypted in profileData
        
        // Generate encryption key for existing profile (key is always the same for same wallet)
        // Skip if skipUnifiedAuth is true (already authenticated via unified auth)
        if (!skipUnifiedAuth) {
          if (!isGeneratingEncryptionKeyRef.current) {
            isGeneratingEncryptionKeyRef.current = true;
            setEncryptionKeyError(null);
            try {
              console.log('Generating encryption key for existing profile...');
              const keyData = await generateEncryptionKey(address);
              setEncryptionKey(keyData.encryptionKey);
              encryptionKeyRef.current = keyData.encryptionKey;
              setEncryptionKeyError(null);
              console.log('Encryption key generated successfully for existing profile');
            } catch (error) {
              console.error('Error generating encryption key for existing profile:', error);
              setEncryptionKey(null);
              encryptionKeyRef.current = null;
              setEncryptionKeyError(error.message || 'Failed to generate encryption key. Please try again.');
            } finally {
              isGeneratingEncryptionKeyRef.current = false;
            }
          }
        } else {
          console.log('Skipping encryption key generation - already authenticated via unified auth');
        }
        
        // Center map on user's location - use currentProfile which will have the location data
        // Only center if we actually updated the profile (not if we skipped overwrite)
        if (currentProfile && currentProfile.walletAddress && currentProfile.walletAddress.toLowerCase() === normalizedAddress) {
          const centerMapOnLocation = () => {
            if (mapInstanceRef.current && currentProfile.location) {
              const location = currentProfile.location;
              if (location && typeof location === 'object' && location.lat && location.lng) {
                console.log('Centering map on location:', location.lat, location.lng);
                mapInstanceRef.current.setView([location.lat, location.lng], 13);
                return true;
              } else {
                console.log('Location data invalid:', location);
              }
            }
            return false;
          };
          
          // Try immediately, then retry after a delay if map isn't ready
          if (!centerMapOnLocation()) {
            setTimeout(() => {
              if (!centerMapOnLocation()) {
                // Retry one more time after longer delay
                setTimeout(centerMapOnLocation, 500);
              }
            }, 200);
          }
        }
      } else if (profileEncryptionKey && getIpfsHash()) {
        // We have encryption key and IPFS hash but no profileData = IPFS load failed
        // Retry is happening in background - keep showing existing profile if available
        console.log('⏳ Waiting for IPFS profile data to load...');
        
        // IMPORTANT FIX: Don't clear profile if Firestore data is already showing
        // This prevents the jarring reload flash when IPFS times out
        if (currentProfile) {
          // Keep existing profile from Firestore, just show IPFS loading indicator
          console.log('📋 Keeping Firestore profile visible while IPFS retries...');
          setIsLoadingProfileFromIPFS(true);
          // Don't set isLoadingUser - we have a profile showing already
          // Don't clear currentProfile or currentUser - keep showing them
        } else if (firestoreMetadata) {
          // No currentProfile but we have Firestore metadata - show it as fallback
          // This prevents getting stuck in loading state when navigating to /myprofile
          console.log('📋 No currentProfile but Firestore metadata available - showing as fallback while IPFS retries...');
          const cleanMetadata = cleanProfileData(firestoreMetadata);
          if (isCurrentUserProfile && cleanMetadata.username) {
            setCurrentUser(cleanMetadata.username);
          }
          setCurrentProfile(cleanMetadata);
          setIsLoadingProfileFromIPFS(true); // Still show loading indicator for IPFS retry
          setIsLoadingUser(false); // Don't show full loading screen since we have profile data
          setShowRegistrationForm(false);
          // Set published states from Firestore metadata
          setPublishedName(!!(firestoreMetadata?.name && firestoreMetadata.name.trim() !== ''));
          setPublishedBio(!!(firestoreMetadata?.bio && firestoreMetadata.bio.trim() !== ''));
          setPublishedEmail(!!(firestoreMetadata?.email && firestoreMetadata.email.trim() !== ''));
          setPublishedPreferredContact(!!(firestoreMetadata?.preferredContactMethod && firestoreMetadata.preferredContactMethod.trim() !== ''));
          setPublishedRelationship(!!(firestoreMetadata?.relationshipStatus && firestoreMetadata.relationshipStatus.trim() !== ''));
          setPublishedLookingFor(!!(firestoreMetadata?.lookingFor && firestoreMetadata.lookingFor.trim() !== ''));
          setPublishedLanguages(!!(firestoreMetadata?.languages && firestoreMetadata.languages.trim() !== ''));
          setPublishedEducation(!!(firestoreMetadata?.education && firestoreMetadata.education.trim() !== ''));
          setPublishedOccupation(!!(firestoreMetadata?.occupation && firestoreMetadata.occupation.trim() !== ''));
          setPublishedWallet(firestoreMetadata?._isWalletPublishedInFirestore === true);
          if (firestoreMetadata?.tagsList) setPublishedTags(firestoreMetadata.tagsList);
          if (firestoreMetadata?.socialLinks) setPublishedSocials(firestoreMetadata.socialLinks);
          if (firestoreMetadata?.musicList) setPublishedMusic(firestoreMetadata.musicList);
          setPublishedBirthDate(!!firestoreMetadata?.birthDate);
          setPublishedCity(!!firestoreMetadata?.city);
        } else {
          // No profile showing yet and no Firestore metadata - but don't clear if we're on /myprofile route
          // This prevents getting stuck when navigating to /myprofile - keep existing profile if it matches
          const isOnMyProfileRoute = normalizedPathname === '/myprofile';
          const existingProfileMatches = currentProfile && 
            currentProfile.walletAddress && 
            currentProfile.walletAddress.toLowerCase() === normalizedAddress;
          
          if (isOnMyProfileRoute && existingProfileMatches) {
            // Keep existing profile visible while IPFS retries
            console.log('📋 [My Profile Route] Keeping existing profile visible while IPFS retries...');
            setIsLoadingProfileFromIPFS(true);
            setIsLoadingUser(false);
          } else {
            // No profile and no Firestore metadata - show full loading screen
            setCurrentProfile(null);
            setCurrentUser(null);
            setIsLoadingProfileFromIPFS(true);
            setIsLoadingUser(true); // Keep isLoadingUser true to show loading screen
            setShowRegistrationForm(false);
            if (!shouldPreserveRoute) {
              setProfileVisible(true); // Show profile section to display loading screen
            }
          }
        }
        setProfileLoadError(null); // Clear any previous errors while retrying
        // Keep loading state - profile will be updated when IPFS retry succeeds
        // Published states will be set when IPFS data loads successfully in the retry block above
        
        // Load saved Arc (Circle) transaction hash from localStorage (do this early)
        const savedTxHash = localStorage.getItem(`lastBaseTxHash_${address}`);
        if (savedTxHash) {
          setLastBaseTxHash(savedTxHash);
          console.log('✅ Loaded last Arc (Circle) transaction hash from localStorage:', savedTxHash);
        } else {
          console.log('No saved Arc (Circle) transaction hash found in localStorage');
        }
        
        // Published states will be set when IPFS data loads successfully in the retry block above
        // Don't try to set them here since we don't have profileData yet
      } else if (profileEncryptionKey && !(profileData?.ipfsHash || firestoreMetadata?.ipfsHash) && firestoreMetadata) {
        // We have encryption key but no IPFS hash - profile exists in Firestore
        // Don't show incomplete data - show error instead
        console.error('❌ Cannot load profile: no IPFS hash available');
        setCurrentProfile(null);
        setCurrentUser(null);
        setIsLoadingProfileFromIPFS(false);
        setIsLoadingUser(false);
        setShowRegistrationForm(false);
        if (!shouldPreserveRoute) {
          setProfileVisible(true);
        }
        setProfileLoadError('Failed to load profile data from blockchain. This may be due to network issues. Please refresh the page to try again.');
      } else {
        // No profile found - ensure all state is cleared before showing registration form
        console.log('No profile found for wallet, showing registration form');
        if (!shouldPreserveRoute) {
          setAboutVisible(false);
          setProfileVisible(true);
        }
        setCurrentUser(null);
        setCurrentProfile(null);
        setEncryptedProfileData(null);
        setShowRegistrationForm(true);
        setIsLoadingUser(false);
        
        // Encryption key should already be set from unified authentication in connectMetaMask
        // But if skipUnifiedAuth is false (meaning we're not using unified auth), generate it here
        if (!skipUnifiedAuth) {
          // Generate encryption key for new profile (only if not already generating)
          if (!isGeneratingEncryptionKeyRef.current) {
            isGeneratingEncryptionKeyRef.current = true;
            setEncryptionKeyError(null);
            try {
              console.log('Generating encryption key for new profile...');
              const keyData = await generateEncryptionKey(address);
              setEncryptionKey(keyData.encryptionKey);
              encryptionKeyRef.current = keyData.encryptionKey;
              setEncryptionKeyError(null);
              console.log('Encryption key generated successfully');
            } catch (error) {
              console.error('Error generating encryption key:', error);
              // Don't block registration if key generation fails, but log the error
              setEncryptionKey(null);
              encryptionKeyRef.current = null;
              setEncryptionKeyError(error.message || 'Failed to generate encryption key. Please try again.');
            } finally {
              isGeneratingEncryptionKeyRef.current = false;
            }
          }
        } else {
          console.log('Skipping encryption key generation - already handled in unified authentication');
        }
        
        // Reset registration form data if it's empty
        setRegistrationData(prev => {
          // Only reset if all fields are empty
          if (!prev.name && !prev.email && !prev.username && (!prev.tags || prev.tags.length === 0 || (prev.tags.length === 1 && !prev.tags[0]))) {
            return {
              name: '',
              email: '',
              username: '',
              tags: [''],
              socials: [''],
              birthdate: '',
              city: '',
              cityLat: null,
              cityLng: null
            };
          }
          // Otherwise keep existing data but ensure tags and socials are arrays
          return {
            ...prev,
            tags: Array.isArray(prev.tags) ? prev.tags : (prev.tags ? [prev.tags] : ['']),
            socials: Array.isArray(prev.socials) ? prev.socials : (prev.socials ? [prev.socials] : [''])
          };
        });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      
      // Before showing registration form, verify that profile really doesn't exist
      // This prevents showing registration form when profile exists but load failed due to timeout/network issues
      let profileExistsInError = false;
      try {
        console.log('Verifying profile existence in error handler...');
        
        // First check blockchain for IPFS hash (more reliable than Firestore when network is slow)
        let blockchainHash = null;
        try {
          blockchainHash = await Promise.race([
            getCIDFromArc(address),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Blockchain verification timeout')), 8000))
          ]);
        } catch (blockchainErr) {
          console.log('Blockchain verification failed:', blockchainErr);
        }
        
        // Then check Firestore
        const errorVerificationMetadata = await Promise.race([
          getUserProfileByWallet(address, null),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore verification timeout')), 8000))
        ]).catch(err => {
          if (err.message === 'Firestore verification timeout') {
            console.log('Firestore verification timed out');
            return null;
          }
          throw err;
        });
        
        // Profile exists if we have Firestore metadata OR blockchain hash
        if (errorVerificationMetadata || blockchainHash) {
          profileExistsInError = true;
          
          if (errorVerificationMetadata) {
            // Profile exists! Try to use Firestore metadata as fallback
            console.log('✅ Profile exists in Firestore, using metadata as fallback');
            const fallbackProfile = cleanProfileData({
              ...errorVerificationMetadata,
              ipfsHash: errorVerificationMetadata.ipfsHash || blockchainHash || null
            });
            setCurrentUser(fallbackProfile.username);
            setCurrentProfile(fallbackProfile);
            setShowRegistrationForm(false);
            setIsLoadingUser(false);
            if (!shouldPreserveRoute) {
              setProfileVisible(true);
              setAboutVisible(false);
            }
            setIsLoadingProfileFromIPFS(false);
            
            // Determine published states from Firestore metadata (which contains only published elements)
            const fallbackTags = errorVerificationMetadata?.tagsList || [];
            const fallbackSocials = errorVerificationMetadata?.socialLinks || [];
            const fallbackMusic = errorVerificationMetadata?.musicList || [];
            const fallbackBirthDate = errorVerificationMetadata?.birthDate || null;
            const fallbackCity = errorVerificationMetadata?.city || null;
            const fallbackName = errorVerificationMetadata?.name || null;
            
            // Tags: published if in Firestore (compare with profile tags from IPFS if available, otherwise use Firestore tags)
            const fallbackProfileTags = fallbackProfile.tagsList || [];
            const publishedTagsFromFallback = fallbackProfileTags.filter(tag => fallbackTags.includes(tag));
            setPublishedTags(publishedTagsFromFallback.length > 0 ? publishedTagsFromFallback : fallbackTags);
            
            // Socials: published if in Firestore
            const fallbackProfileSocials = fallbackProfile.socialLinks || [];
            const publishedSocialsFromFallback = fallbackProfileSocials.filter(social => fallbackSocials.includes(social));
            setPublishedSocials(publishedSocialsFromFallback.length > 0 ? publishedSocialsFromFallback : fallbackSocials);
            
            // Music: published if in Firestore
            const fallbackProfileMusic = fallbackProfile.musicList || [];
            const publishedMusicFromFallback = fallbackProfileMusic.filter(song => fallbackMusic.includes(song));
            setPublishedMusic(publishedMusicFromFallback.length > 0 ? publishedMusicFromFallback : fallbackMusic);
            
            // BirthDate: published if in Firestore
            setPublishedBirthDate(!!fallbackBirthDate && (fallbackBirthDate === fallbackProfile.birthDate || !fallbackProfile.birthDate));
            
            // City: published if in Firestore
            setPublishedCity(!!fallbackCity && (fallbackCity === fallbackProfile.city || !fallbackProfile.city));
            
            // Wallet: use _isWalletPublishedInFirestore flag (walletAddress always has fallback)
            setPublishedWallet(errorVerificationMetadata?._isWalletPublishedInFirestore === true);
            
            // Name: published if name exists in Firestore
            const nameIsPublishedFromFallback = !!(fallbackName && fallbackName.trim() !== '');
            setPublishedName(nameIsPublishedFromFallback);
            
            // Bio: published if bio exists in Firestore
            const fallbackBio = errorVerificationMetadata?.bio || null;
            const bioIsPublishedFromFallback = !!(fallbackBio && fallbackBio.trim() !== '');
            setPublishedBio(bioIsPublishedFromFallback);
            
            console.log('Profile loaded from Firestore metadata (IPFS load failed), published states set from Firestore');
            return; // Exit early - profile loaded from metadata
          } else if (blockchainHash) {
            // Profile exists (IPFS hash on blockchain) but Firestore query failed
            console.log('✅ Profile exists (IPFS hash on blockchain) but Firestore query failed - showing error instead of registration form');
            setCurrentUser(null);
            setCurrentProfile(null);
            setShowRegistrationForm(false);
            setIsLoadingUser(false);
            setIsLoadingProfileFromIPFS(false);
            setProfileLoadError('Failed to load your profile. This may be due to network issues. Please refresh the page to try again.');
            if (!shouldPreserveRoute) {
              setProfileVisible(true);
              setAboutVisible(false);
            }
            return; // Exit early - show error instead of registration form
          }
        }
      } catch (verifyErr) {
        console.error('Error verifying profile existence in error handler:', verifyErr);
        // If verification also fails, proceed to check blockchain one more time
        if (!profileExistsInError) {
          try {
            const finalCheckHash = await Promise.race([
              getCIDFromArc(address),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Final check timeout')), 5000))
            ]);
            if (finalCheckHash) {
              console.log('✅ Profile exists (IPFS hash found on Arc (Circle) blockchain) - showing error instead of registration form');
              profileExistsInError = true;
              setCurrentUser(null);
              setCurrentProfile(null);
              setShowRegistrationForm(false);
              setIsLoadingUser(false);
              setIsLoadingProfileFromIPFS(false);
              setProfileLoadError('Failed to load your profile. This may be due to network issues. Please refresh the page to try again.');
              if (!shouldPreserveRoute) {
                setProfileVisible(true);
                setAboutVisible(false);
              }
              return;
            }
          } catch (finalErr) {
            console.log('Final check also failed:', finalErr);
          }
        }
      }
      
      // On error, show registration form ONLY if we're certain profile doesn't exist
      // Note: If we got here, it means we couldn't verify profile existence, so we should generate the key
      if (address && !profileExistsInError) {
        console.log('Error occurred and no profile found after verification, showing registration form');
        if (!shouldPreserveRoute) {
          setAboutVisible(false);
        }
        setCurrentUser(null);
        setCurrentProfile(null);
        setShowRegistrationForm(true);
        if (!shouldPreserveRoute) {
          setProfileVisible(true);
        }
        setIsLoadingUser(false); // Stop loading, show registration form
        
        // Encryption key should already be set from unified authentication in connectMetaMask
        // But if we're in error handler and skipUnifiedAuth is false, it might not be set, so generate it here
        // Note: This is a fallback - normally unified auth handles this
        if (!skipUnifiedAuth) {
          // Generate encryption key for new profile (only if not already generating)
          if (!isGeneratingEncryptionKeyRef.current) {
            isGeneratingEncryptionKeyRef.current = true;
            setEncryptionKeyError(null);
            try {
              console.log('Generating encryption key for new profile...');
              const keyData = await generateEncryptionKey(address);
              setEncryptionKey(keyData.encryptionKey);
              encryptionKeyRef.current = keyData.encryptionKey;
              setEncryptionKeyError(null);
              console.log('Encryption key generated successfully');
            } catch (error) {
              console.error('Error generating encryption key:', error);
              // Don't block registration if key generation fails, but log the error
              setEncryptionKey(null);
              encryptionKeyRef.current = null;
              setEncryptionKeyError(error.message || 'Failed to generate encryption key. Please try again.');
            } finally {
              isGeneratingEncryptionKeyRef.current = false;
            }
          }
        } else {
          console.log('Skipping encryption key generation - already handled in unified authentication');
        }
        
        // Reset registration form data
        setRegistrationData({
          name: '',
          email: '',
          username: '',
          tags: [''],
          socials: [''],
          birthdate: '',
          city: '',
          cityLat: null,
          cityLng: null
        });
      } else {
        setIsLoadingUser(false);
      }
    }
  };

  // Helper function to replace avatar data with placeholder
  const sanitizeDataForDisplay = (data) => {
    if (!data || typeof data !== 'object') return data;
    
    const sanitized = Array.isArray(data) ? [...data] : { ...data };
    
    for (const key in sanitized) {
      if (key === 'avatar' && sanitized[key] != null && typeof sanitized[key] === 'string') {
        sanitized[key] = '[avatar1_picture_code]';
      } else if (key === 'photos' && Array.isArray(sanitized[key])) {
        // Replace each photo in the array with numbered placeholders
        sanitized[key] = sanitized[key].map((photo, index) => {
          if (photo != null && typeof photo === 'string') {
            return `[avatar${index + 1}_picture_code]`;
          }
          return photo;
        });
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = sanitizeDataForDisplay(sanitized[key]);
      }
    }
    
    return sanitized;
  };

  // Fetch all database records related to user profile
  const fetchAllDbRecords = useCallback(async (address, username) => {
    if (!address) return;

    setIsLoadingDbRecords(true);
    try {
      const normalizedAddress = address.toLowerCase();
      const records = {};

      // 1. Profile document from 'profiles' collection
      if (username) {
        try {
          const profileRef = doc(db, 'profiles', username);
          const profileSnap = await getDoc(profileRef);
          if (profileSnap.exists()) {
            records.profile = {
              collection: 'profiles',
              documentId: username,
              data: profileSnap.data()
            };
          }
        } catch (err) {
          console.error('Error fetching profile:', err);
        }
      }

      // 2. Wallet-to-username mapping from 'walletUsers' collection
      try {
        const walletUserRef = doc(db, 'walletUsers', normalizedAddress);
        const walletUserSnap = await getDoc(walletUserRef);
        if (walletUserSnap.exists()) {
          records.walletUser = {
            collection: 'walletUsers',
            documentId: normalizedAddress,
            data: walletUserSnap.data()
          };
        }
      } catch (err) {
        console.error('Error fetching walletUser:', err);
      }

      // 3. Messages sent by user
      try {
        const messagesRef = collection(db, 'messages');
        const messagesQuery = query(messagesRef, where('sender', '==', normalizedAddress));
        const messagesSnapshot = await getDocs(messagesQuery);
        
        // Remove any duplicates by message ID
        const uniqueSentMessages = Array.from(new Map(
          messagesSnapshot.docs.map(doc => [doc.id, doc])
        ).values());
        
        console.log(`[Database Records] Found ${uniqueSentMessages.length} unique messages sent by ${normalizedAddress}`);
        
        records.messagesSent = {
          collection: 'messages',
          query: 'sender == ' + normalizedAddress,
          count: uniqueSentMessages.length,
          documents: uniqueSentMessages.map(doc => ({
            id: doc.id,
            data: doc.data()
          }))
        };
      } catch (err) {
        console.error('Error fetching messages sent:', err);
        records.messagesSent = {
          collection: 'messages',
          query: 'sender == ' + normalizedAddress,
          count: 0,
          documents: [],
          error: err.message
        };
      }

      // 4. Messages received by user
      try {
        const messagesRef = collection(db, 'messages');
        const messagesQuery = query(messagesRef, where('recipient', '==', normalizedAddress));
        const messagesSnapshot = await getDocs(messagesQuery);
        
        // Remove any duplicates by message ID
        const uniqueReceivedMessages = Array.from(new Map(
          messagesSnapshot.docs.map(doc => [doc.id, doc])
        ).values());
        
        console.log(`[Database Records] Found ${uniqueReceivedMessages.length} unique messages received by ${normalizedAddress}`);
        
        records.messagesReceived = {
          collection: 'messages',
          query: 'recipient == ' + normalizedAddress,
          count: uniqueReceivedMessages.length,
          documents: uniqueReceivedMessages.map(doc => ({
            id: doc.id,
            data: doc.data()
          }))
        };
      } catch (err) {
        console.error('Error fetching messages received:', err);
        records.messagesReceived = {
          collection: 'messages',
          query: 'recipient == ' + normalizedAddress,
          count: 0,
          documents: [],
          error: err.message
        };
      }

      // 5. Conversations where user is participant
      // Conversations are stored with document IDs like: conversationId_userAddress
      // We need to get all conversations and filter by document ID ending with user address
      try {
        const conversationsRef = collection(db, 'conversations');
        // Get all conversations (we can't query by document ID suffix directly in Firestore)
        // So we'll get all and filter client-side
        const allConversationsSnapshot = await getDocs(conversationsRef);
        const userConversations = allConversationsSnapshot.docs.filter(doc => {
          // Document ID format: conversationId_userAddress
          // Check if document ID ends with the user's normalized address
          const docId = doc.id.toLowerCase();
          return docId.endsWith('_' + normalizedAddress) || docId.endsWith(normalizedAddress);
        });
        
        records.conversations = {
          collection: 'conversations',
          query: 'document ID ends with _' + normalizedAddress,
          count: userConversations.length,
          documents: userConversations.map(doc => ({
            id: doc.id,
            data: doc.data()
          }))
        };
      } catch (err) {
        console.error('Error fetching conversations:', err);
        records.conversations = {
          collection: 'conversations',
          query: 'document ID ends with _' + normalizedAddress,
          count: 0,
          documents: [],
          error: err.message
        };
      }

      // 6. Message signatures
      try {
        const signaturesRef = collection(db, 'messageSignatures');
        const signaturesQuery = query(signaturesRef, where('walletAddress', '==', normalizedAddress));
        const signaturesSnapshot = await getDocs(signaturesQuery);
        if (!signaturesSnapshot.empty) {
          records.messageSignatures = {
            collection: 'messageSignatures',
            query: 'walletAddress == ' + normalizedAddress,
            count: signaturesSnapshot.size,
            documents: signaturesSnapshot.docs.map(doc => ({
              id: doc.id,
              data: doc.data()
            }))
          };
        }
      } catch (err) {
        console.error('Error fetching message signatures:', err);
      }

      // 7. Blocks (users blocked by this user)
      try {
        const blocksRef = collection(db, 'blocks');
        const blocksQuery = query(blocksRef, where('blocker', '==', normalizedAddress));
        const blocksSnapshot = await getDocs(blocksQuery);
        if (!blocksSnapshot.empty) {
          records.blocks = {
            collection: 'blocks',
            query: 'blocker == ' + normalizedAddress,
            count: blocksSnapshot.size,
            documents: blocksSnapshot.docs.map(doc => ({
              id: doc.id,
              data: doc.data()
            }))
          };
        }
      } catch (err) {
        console.error('Error fetching blocks:', err);
      }

      // 8. Reports made by user
      try {
        const reportsRef = collection(db, 'reports');
        const reportsQuery = query(reportsRef, where('reporter', '==', normalizedAddress));
        const reportsSnapshot = await getDocs(reportsQuery);
        if (!reportsSnapshot.empty) {
          records.reports = {
            collection: 'reports',
            query: 'reporter == ' + normalizedAddress,
            count: reportsSnapshot.size,
            documents: reportsSnapshot.docs.map(doc => ({
              id: doc.id,
              data: doc.data()
            }))
          };
        }
      } catch (err) {
        console.error('Error fetching reports:', err);
      }

      // Calculate total messages (sent + received, excluding duplicates if someone messaged themselves)
      if (records.messagesSent && records.messagesReceived) {
        const sentIds = new Set(records.messagesSent.documents.map(d => d.id));
        const receivedIds = new Set(records.messagesReceived.documents.map(d => d.id));
        // Count unique message IDs (messages sent to yourself would appear in both)
        const totalUniqueMessages = new Set([...sentIds, ...receivedIds]).size;
        records.messagesTotal = {
          collection: 'messages',
          query: 'total unique messages (sent + received)',
          count: totalUniqueMessages,
          documents: [] // Don't duplicate the full message list
        };
        console.log(`[Database Records] Total unique messages: ${totalUniqueMessages} (${records.messagesSent.count} sent + ${records.messagesReceived.count} received)`);
      }

      setDbRecords(records);
    } catch (error) {
      console.error('Error fetching database records:', error);
      setDbRecords(null);
    } finally {
      setIsLoadingDbRecords(false);
    }
  }, []);

  // Fetch database records when encryption data section is expanded
  useEffect(() => {
    if (encryptionDataExpanded && walletAddress && currentProfile?.username) {
      fetchAllDbRecords(walletAddress, currentProfile.username);
    }
  }, [encryptionDataExpanded, walletAddress, currentProfile?.username, fetchAllDbRecords]);

  // Debug: Log state changes for registration form visibility
  useEffect(() => {
    if (walletAddress) {
      console.log('State check:', {
        walletAddress: !!walletAddress,
        showRegistrationForm,
        profileVisible,
        hasCurrentProfile: !!currentProfile,
        aboutVisible
      });
    }
  }, [walletAddress, showRegistrationForm, profileVisible, currentProfile?.username, aboutVisible]);

  // Load ratings and reviews when profile changes
  // Use useRef to track the last loaded profile to prevent infinite loops
  const lastLoadedProfileRef = useRef(null);
  
  useEffect(() => {
    // Create a stable key for the current profile to compare
    const profileKey = currentProfile ? `${currentProfile.username || ''}_${currentProfile.walletAddress || ''}` : null;
    
    // Skip if we've already loaded data for this exact profile
    if (lastLoadedProfileRef.current === profileKey) {
      return;
    }
    
    if (!currentProfile) {
      setUserRatingSummary({ averageRating: 0, reviewCount: 0, totalRating: 0 });
      setUserReviews([]);
      setExistingReview(null);
      lastLoadedProfileRef.current = null;
      return;
    }

    // Mark this profile as loaded
    lastLoadedProfileRef.current = profileKey;

    // Get wallet address from profile
    // IMPORTANT: If viewing own profile, use walletAddress (current user's wallet)
    // If viewing other user's profile, use viewedProfileWalletAddress or currentProfile.walletAddress
    const isOwnProfile = isMyProfile();
    
    // Track profile view (for analytics) - only track views of other users' profiles
    // Use a unique key to prevent duplicate tracking (username + walletAddress/sessionId)
    if (!isOwnProfile && currentProfile.username) {
      // For unlogged users, get or generate session ID to create unique tracking key
      let viewerIdentifier = walletAddress ? walletAddress.toLowerCase() : null;
      if (!viewerIdentifier) {
        const sessionKey = 'peopletag_anonymous_session_id';
        viewerIdentifier = sessionStorage.getItem(sessionKey);
        if (!viewerIdentifier) {
          viewerIdentifier = `anonymous_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem(sessionKey, viewerIdentifier);
        }
      }
      const trackingKey = `${currentProfile.username}_${viewerIdentifier}`;
      
      // Check if we've already tracked this view in this session
      if (!trackedViewsRef.current.has(trackingKey)) {
        trackedViewsRef.current.add(trackingKey);
        console.log('Attempting to track profile view:', { 
          viewedUsername: currentProfile.username, 
          hasWallet: !!walletAddress,
          profileVisible,
          isOwnProfile,
          trackingKey,
          isUnlogged: !walletAddress
        });
        // Track view for the viewed profile (use walletAddress for logged-in users, null for unlogged)
        utils.trackProfileView(currentProfile.username, walletAddress || null).then(() => {
          console.log('✅ Profile view tracked successfully for:', currentProfile.username);
        }).catch(err => {
          console.error('❌ Error tracking profile view:', err);
          // Remove from tracked set on error so it can be retried
          trackedViewsRef.current.delete(trackingKey);
        });
      } else {
        console.log('Skipping duplicate profile view tracking:', { trackingKey });
      }
    } else {
      console.log('Skipping profile view tracking:', { isOwnProfile, hasUsername: !!currentProfile?.username });
    }
    let profileWalletAddress = isOwnProfile 
      ? walletAddress 
      : (viewedProfileWalletAddress || currentProfile.walletAddress);
    
    // Helper function to load reviews for a wallet address
    const loadReviewsForWallet = (walletAddr) => {
      // Load rating summary
      getUserRatingSummary(walletAddr).then(summary => {
        setUserRatingSummary(summary || { averageRating: 0, reviewCount: 0, totalRating: 0 });
      }).catch(error => {
        console.error('Error loading rating summary:', error);
        setUserRatingSummary({ averageRating: 0, reviewCount: 0, totalRating: 0 });
      });

      // Load reviews - IMPORTANT: Load reviews for the PROFILE being viewed (reviewed user), not the reviewer
      getUserReviews(walletAddr, { limitCount: 50 }).then(reviews => {
        setUserReviews(reviews || []);
      }).catch(error => {
        console.error('Error loading reviews:', error);
        setUserReviews([]);
      });

      // Check if current user has already reviewed this profile
      if (walletAddress && walletAddress.toLowerCase() !== walletAddr.toLowerCase()) {
        getReviewByReviewer(walletAddress, walletAddr).then(review => {
          setExistingReview(review);
          if (review) {
            setReviewRating(review.rating || 0);
            setReviewText(review.reviewText || '');
          } else {
            setReviewRating(0);
            setReviewText('');
          }
        }).catch(error => {
          console.error('Error checking existing review:', error);
          setExistingReview(null);
          setReviewRating(0);
          setReviewText('');
        });
      } else {
        setExistingReview(null);
        setReviewRating(0);
        setReviewText('');
      }
    };
    
    // If no wallet address found, try to get it from username (same fallback as submit)
    if (!profileWalletAddress && currentProfile.username) {
      // Use async function to handle the lookup
      (async () => {
        try {
          let resolvedWallet = await getWalletFromUsername(currentProfile.username);
          if (!resolvedWallet) {
            // Generate temporary wallet from username (same as submit logic - MUST be deterministic)
            resolvedWallet = generateTemporaryWalletFromUsername(currentProfile.username);
          }
          
          // Now load reviews with the resolved wallet address
          loadReviewsForWallet(resolvedWallet);
        } catch (error) {
          console.error('Error resolving wallet address:', error);
          setUserRatingSummary({ averageRating: 0, reviewCount: 0, totalRating: 0 });
          setUserReviews([]);
          setExistingReview(null);
        }
      })();
      return; // Exit early, async function will handle loading
    }
    
    if (!profileWalletAddress) {
      setUserRatingSummary({ averageRating: 0, reviewCount: 0, totalRating: 0 });
      setUserReviews([]);
      setExistingReview(null);
      return;
    }
    
    // Load reviews synchronously if we have wallet address
    loadReviewsForWallet(profileWalletAddress);
  }, [currentProfile?.username, currentProfile?.walletAddress, viewedProfileWalletAddress, walletAddress, currentUser]);

  const [viewerUsernames, setViewerUsernames] = useState({}); // Map of wallet address/session ID to username

  // Load profile view statistics
  useEffect(() => {
    const loadViewStats = async () => {
      if (!currentProfile?.username) {
        setViewStats({ totalViews: 0, uniqueViewers: 0, uniqueLoggedInViewers: 0, uniqueUnloggedViewers: 0, recentViews: [] });
        setViewerUsernames({});
        return;
      }

      try {
        const stats = await utils.getProfileViewStats(currentProfile.username);
        setViewStats(stats);
        
        // Load usernames for wallet addresses in recent views
        const usernameMap = {};
        const loadUsernames = async () => {
          const walletAddresses = stats.recentViews
            .filter(view => view.viewerWalletAddress)
            .map(view => view.viewerWalletAddress.toLowerCase());
          
          // Remove duplicates
          const uniqueWallets = [...new Set(walletAddresses)];
          
          // Try to get usernames from walletUsers collection first
          for (const walletAddress of uniqueWallets) {
            try {
              const username = await getUsernameFromWallet(walletAddress);
              if (username) {
                // Store mapping for all variations of the address
                usernameMap[walletAddress] = username;
                // Also store for any case variations that might exist in views
                stats.recentViews.forEach(view => {
                  if (view.viewerWalletAddress && view.viewerWalletAddress.toLowerCase() === walletAddress) {
                    usernameMap[view.viewerWalletAddress] = username;
                  }
                });
              }
            } catch (error) {
              console.warn('Error loading username for wallet:', walletAddress, error);
            }
          }
          
          // If some usernames weren't found via walletUsers, try querying profiles collection
          const missingWallets = uniqueWallets.filter(addr => !usernameMap[addr]);
          if (missingWallets.length > 0) {
            try {
              // Query profiles by walletAddress (published) or _ownerAddress (always present)
              const profilesRef = collection(db, 'profiles');
              
              // Try walletAddress first (for published addresses)
              const walletAddressQuery = query(profilesRef, where('walletAddress', 'in', missingWallets.slice(0, 10))); // Firestore 'in' limit is 10
              const walletAddressSnapshot = await getDocs(walletAddressQuery);
              walletAddressSnapshot.forEach(doc => {
                const profileData = doc.data();
                if (profileData.walletAddress && profileData.username) {
                  const normalizedAddr = profileData.walletAddress.toLowerCase();
                  usernameMap[normalizedAddr] = profileData.username;
                  // Also store for any case variations
                  stats.recentViews.forEach(view => {
                    if (view.viewerWalletAddress && view.viewerWalletAddress.toLowerCase() === normalizedAddr) {
                      usernameMap[view.viewerWalletAddress] = profileData.username;
                    }
                  });
                }
              });
              
              // Also try _ownerAddress for remaining missing wallets (handles unpublished walletAddress)
              const stillMissing = missingWallets.filter(addr => !usernameMap[addr]);
              if (stillMissing.length > 0) {
                for (const walletAddr of stillMissing.slice(0, 10)) {
                  try {
                    const ownerAddressQuery = query(profilesRef, where('_ownerAddress', '==', walletAddr));
                    const ownerAddressSnapshot = await getDocs(ownerAddressQuery);
                    ownerAddressSnapshot.forEach(doc => {
                      const profileData = doc.data();
                      if (profileData.username) {
                        usernameMap[walletAddr] = profileData.username;
                        // Also store for any case variations
                        stats.recentViews.forEach(view => {
                          if (view.viewerWalletAddress && view.viewerWalletAddress.toLowerCase() === walletAddr) {
                            usernameMap[view.viewerWalletAddress] = profileData.username;
                          }
                        });
                      }
                    });
                  } catch (error) {
                    console.warn('Error querying by _ownerAddress for:', walletAddr, error);
                  }
                }
              }
            } catch (error) {
              console.warn('Error querying profiles for usernames:', error);
            }
          }
          
          console.log('Username mapping completed:', { 
            totalWallets: uniqueWallets.length, 
            foundUsernames: Object.keys(usernameMap).length,
            usernameMap 
          });
          
          setViewerUsernames(prev => ({ ...prev, ...usernameMap }));
        };
        
        loadUsernames();
      } catch (error) {
        console.error('Error loading view stats:', error);
        setViewStats({ totalViews: 0, uniqueViewers: 0, uniqueLoggedInViewers: 0, uniqueUnloggedViewers: 0, recentViews: [] });
        setViewerUsernames({});
      }
    };

    loadViewStats();
  }, [currentProfile?.username, analyticsExpanded]);

  // Load gamification data for viewed profile (if not own profile)
  useEffect(() => {
    if (!currentProfile) {
      setViewedProfileGamificationData(null);
      return;
    }

    const isOwnProfile = isMyProfile();
    
    // If viewing own profile, don't load separate gamification data (already have it)
    if (isOwnProfile) {
      setViewedProfileGamificationData(null);
      return;
    }

    // Load gamification data for the viewed profile
    const profileWalletAddress = viewedProfileWalletAddress || currentProfile.walletAddress;
    if (profileWalletAddress) {
      getUserGamificationData(profileWalletAddress).then(data => {
        if (data) {
          setViewedProfileGamificationData(data);
        } else {
          setViewedProfileGamificationData(null);
        }
      }).catch(error => {
        console.error('Error loading viewed profile gamification data:', error);
        setViewedProfileGamificationData(null);
      });
    } else {
      setViewedProfileGamificationData(null);
    }
  }, [currentProfile, viewedProfileWalletAddress, currentUser]);

  // Load reviewer usernames when reviews list modal opens
  useEffect(() => {
    if (reviewsListModalVisible && userReviews.length > 0) {
      const loadUsernames = async () => {
        const usernameMap = {};
        for (const review of userReviews) {
          if (review.reviewerAddress && !reviewerUsernames[review.reviewerAddress]) {
            try {
              const username = await getUsernameFromWallet(review.reviewerAddress);
              if (username) {
                usernameMap[review.reviewerAddress] = username;
              }
            } catch (error) {
              console.error('Error loading username for reviewer:', error);
            }
          } else if (review.reviewerAddress && reviewerUsernames[review.reviewerAddress]) {
            usernameMap[review.reviewerAddress] = reviewerUsernames[review.reviewerAddress];
          }
        }
        if (Object.keys(usernameMap).length > 0) {
          setReviewerUsernames(prev => ({ ...prev, ...usernameMap }));
        }
      };
      loadUsernames();
    }
  }, [reviewsListModalVisible, userReviews]);

  // Debug: Log state changes for registration form visibility
  useEffect(() => {
    if (encryptionKey) {
      encryptionKeyRef.current = encryptionKey;
    }
  }, [encryptionKey]);

  // Handle registration form submission
  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    
    if (!walletAddress) {
      alert('Wallet not connected');
      return;
    }

    // Validate form
    if (!registrationData.name || !registrationData.username || !registrationData.email) {
      alert('Please fill in all required fields (name, username, email)');
      return;
    }

    // Validate username format (alphanumeric and underscore)
    if (!/^[a-zA-Z0-9_]+$/.test(registrationData.username)) {
      alert('Username can only contain letters, numbers, and underscores');
      return;
    }

    // Check if username is already taken
    const usernameTaken = await isUsernameTaken(registrationData.username);
    if (usernameTaken) {
      alert('This username is already taken. Please choose a different username.');
      setIsSavingProfile(false);
      return;
    }

    setIsSavingProfile(true);
    try {
      // Require email verification before saving registration email
      const emailForRegistration = (registrationData.email || '').trim();
      if (emailForRegistration && !isEmailRecentlyVerified(emailForRegistration, 'register')) {
        setIsSavingProfile(false);
        return openEmailVerificationModal({
          email: emailForRegistration,
          purpose: 'register',
          onVerified: async () => {
            // Resume registration without relying on the submit event object
            await handleRegistrationSubmit({ preventDefault: () => {} });
          }
        });
      }

      // Parse tags (array, add # if not present)
      const tagsList = registrationData.tags
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0)
        .map(tag => tag.startsWith('#') ? tag : `#${tag}`);

      // Check for duplicate tags (case-insensitive)
      const tagsLower = tagsList.map(tag => tag.toLowerCase());
      const duplicateTags = tagsList.filter((tag, index) => tagsLower.indexOf(tag.toLowerCase()) !== index);
      
      if (duplicateTags.length > 0) {
        alert(`Duplicate tags are not allowed. Please remove duplicates: ${duplicateTags.join(', ')}`);
        setIsSavingProfile(false);
        return;
      }

      // Parse socials (array)
      const socialLinks = registrationData.socials
        .map(social => social.trim())
        .filter(social => social.length > 0);

      // Normalize social links for duplicate checking (remove http/https, trailing slashes, lowercase)
      const normalizeSocial = (link) => {
        return link
          .toLowerCase()
          .replace(/^https?:\/\//, '')
          .replace(/\/$/, '');
      };
      
      const normalizedLinks = socialLinks.map(normalizeSocial);
      const duplicateSocials = socialLinks.filter((link, index) => 
        normalizedLinks.indexOf(normalizeSocial(link)) !== index
      );
      
      if (duplicateSocials.length > 0) {
        alert(`Duplicate social links are not allowed. Please remove duplicates: ${duplicateSocials.join(', ')}`);
        setIsSavingProfile(false);
        return;
      }

      // Parse music (array, filter empty)
      const musicList = registrationData.music
        .map(song => song.trim())
        .filter(song => song.length > 0);

      // Check for duplicate music (case-insensitive)
      const musicLower = musicList.map(song => song.toLowerCase());
      const duplicateMusic = musicList.filter((song, index) => musicLower.indexOf(song.toLowerCase()) !== index);
      
      if (duplicateMusic.length > 0) {
        alert(`Duplicate songs are not allowed. Please remove duplicates: ${duplicateMusic.join(', ')}`);
        setIsSavingProfile(false);
        return;
      }

      // Parse birthdate and calculate age
      let age = 0;
      let birthDate = registrationData.birthdate;
      if (birthDate) {
        try {
          const birth = new Date(birthDate);
          const today = new Date();
          age = today.getFullYear() - birth.getFullYear();
          const monthDiff = today.getMonth() - birth.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
          }
          // Format as DD.MM.YYYY
          const day = String(birth.getDate()).padStart(2, '0');
          const month = String(birth.getMonth() + 1).padStart(2, '0');
          birthDate = `${day}.${month}.${birth.getFullYear()}`;
      } catch (e) {
          console.error('Error parsing birthdate:', e);
        }
      }

      // Get city coordinates - use selected coordinates if available, otherwise try to get from city name
      let location;
      if (registrationData.cityLat && registrationData.cityLng) {
        // Use coordinates selected from map
        location = {
          name: registrationData.city,
          city: registrationData.city,
          lat: registrationData.cityLat,
          lng: registrationData.cityLng
        };
      } else {
        // Try to get coordinates from city name
        const coords = getCityCoordinates(registrationData.city);
        location = coords ? {
          name: registrationData.city,
          city: registrationData.city,
          lat: coords.lat,
          lng: coords.lng
        } : {
          name: registrationData.city,
          city: registrationData.city,
          lat: 52.52,
          lng: 13.405
        };
      }

      // Create profile data
      const profileData = {
        name: registrationData.name,
        username: registrationData.username,
        email: (registrationData.email || '').trim(),
        emailVerifiedAt: new Date().toISOString(),
        tagsList: tagsList,
        groups: [],
        socialLinks: socialLinks,
        musicList: musicList,
        birthDate: birthDate,
        age: age,
        city: registrationData.city,
        location: location,
        avatar: null, // No avatar by default - user can upload one
        additionalPersonalData: [],
        rating: 5,
        surveyCount: 0,
        tags: tagsList.length,
      };

      // Use encryptAndSaveProfile to encrypt and save (isRegistration=true for initial registration)
      const result = await encryptAndSaveProfile(profileData, true);
      
      if (!result || !result.ipfsHash) {
        throw new Error('Failed to save profile to IPFS. Please try again.');
      }
      
      // Update state with the saved profile
      const savedProfile = {
        ...profileData,
        ipfsHash: result.ipfsHash
      };
      
      setCurrentUser(savedProfile.username);
      setCurrentProfile(savedProfile);
      // Update myProfileForMatch immediately so search can calculate match percentages
      setMyProfileForMatch(cleanProfileData(savedProfile));
      setShowRegistrationForm(false);
      setProfileVisible(true);
      
      if (result.encryptedData) {
        setEncryptedProfileData(result.encryptedData);
      }
      
      // After registration, ALL fields are UNPUBLISHED by default
      // User must explicitly publish them if they want them visible to others
      setPublishedTags([]);
      setPublishedSocials([]);
      setPublishedMusic([]);
      setPublishedBirthDate(false);
      setPublishedCity(false);
      setPublishedWallet(false);
      setPublishedName(false);
      setPublishedBio(false);
      setPublishedEmail(false); // Unpublished by default after registration
      setPublishedPreferredContact(false);
      setPublishedRelationship(false);
      setPublishedLookingFor(false);
      setPublishedLanguages(false);
      setPublishedEducation(false);
      setPublishedOccupation(false);
      
      // Wait a bit to ensure Firestore has saved the data from encryptAndSaveProfile
      // This is important because Firestore operations are eventually consistent
      await new Promise(resolve => setTimeout(resolve, 2000)); // Increased wait time
      
      // After registration, ensure ALL fields are UNPUBLISHED in Firestore
      // Note: encryptAndSaveProfile saves data to Firestore during registration (isRegistration=true),
      // but we need to call updatePublishedMetadata to UNPUBLISH everything (remove from Firestore)
      // Data remains in IPFS (encrypted) but is not visible to others until user publishes
      try {
        console.log('📝 Ensuring all fields are UNPUBLISHED after registration:', {
          note: 'All data saved to IPFS but not published to Firestore - user must publish explicitly'
        });
        
        // First, verify what's actually in Firestore right now (should have data from encryptAndSaveProfile)
        let firestoreCheck = null;
        try {
          firestoreCheck = await getUserProfileByWallet(walletAddress, null);
          console.log('🔍 Firestore check before updatePublishedMetadata (will be unpublished):', {
            hasEmail: !!(firestoreCheck?.email && firestoreCheck.email.trim() !== ''),
            hasTags: Array.isArray(firestoreCheck?.tagsList) && firestoreCheck.tagsList.length > 0,
            tagsCount: firestoreCheck?.tagsList?.length || 0,
            hasSocials: Array.isArray(firestoreCheck?.socialLinks) && firestoreCheck.socialLinks.length > 0,
            hasMusic: Array.isArray(firestoreCheck?.musicList) && firestoreCheck.musicList.length > 0
          });
        } catch (checkError) {
          console.warn('⚠️ Could not check Firestore before update:', checkError);
        }
        
        // IMPORTANT: After registration, ALL fields are UNPUBLISHED by default
        // Data is saved to IPFS (encrypted) but NOT published to Firestore (not visible to others)
        // User must explicitly publish fields if they want them visible
        const publishedMetadata = {
          username: savedProfile.username,
          publishedTags: [], // Unpublished - empty array
          publishedSocials: [], // Unpublished - empty array
          publishedMusic: [], // Unpublished - empty array
          publishedBirthDate: null, // Unpublished
          publishedAge: null, // Unpublished
          publishedCity: null, // Unpublished
          publishedLocation: null, // Unpublished
          publishedWallet: false, // Unpublished
          publishedName: null, // Unpublished
          publishedBio: null, // Unpublished
          publishedEmail: null // Unpublished - user must publish explicitly
        };
        
        console.log('📝 All fields UNPUBLISHED after registration (user must publish explicitly):', {
          tagsCount: 0,
          socialsCount: 0,
          musicCount: 0,
          hasBirthDate: false,
          hasCity: false,
          hasName: false,
          hasEmail: false
        });
        await updatePublishedMetadata(walletAddress, publishedMetadata);
        console.log('✅ Called updatePublishedMetadata to ensure all fields are UNPUBLISHED');
        
        // Process referral if referral code was provided
        const pendingRefCode = referralCode || localStorage.getItem('pendingReferralCode');
        if (pendingRefCode && walletAddress) {
          try {
            console.log('🎁 Processing referral code:', pendingRefCode);
            const referralResult = await processReferral(walletAddress, pendingRefCode);
            if (referralResult.success) {
              console.log('✅ Referral processed successfully! Points awarded:', referralResult.pointsAwarded);
              setNotification({
                message: `🎉 Referral processed! You earned ${referralResult.pointsAwarded} points!`,
                type: 'success'
              });
              setTimeout(() => setNotification(null), 5000);
              // Clear pending referral code
              localStorage.removeItem('pendingReferralCode');
              setReferralCode(null);
              // Reload gamification data to show updated points
              const updatedData = await getUserGamificationData(walletAddress);
              setGamificationData(updatedData);
              // Reload referral stats to show inviter information
              const updatedStats = await getReferralStats(walletAddress);
              setReferralStats(updatedStats);
            } else {
              console.log('⚠️ Referral processing result:', referralResult.message);
            }
          } catch (refError) {
            console.error('Error processing referral:', refError);
          }
        }
        
        // Wait a bit more and verify the update worked
        await new Promise(resolve => setTimeout(resolve, 1500));
        try {
          const verifyMetadata = await getUserProfileByWallet(walletAddress, null);
          const hasEmailInFirestore = !!(verifyMetadata?.email && verifyMetadata.email.trim() !== '');
          const hasTagsInFirestore = Array.isArray(verifyMetadata?.tagsList) && verifyMetadata.tagsList.length > 0;
          const hasSocialsInFirestore = Array.isArray(verifyMetadata?.socialLinks) && verifyMetadata.socialLinks.length > 0;
          const hasMusicInFirestore = Array.isArray(verifyMetadata?.musicList) && verifyMetadata.musicList.length > 0;
          const hasBirthDateInFirestore = !!(verifyMetadata?.birthDate);
          const hasCityInFirestore = !!(verifyMetadata?.city);
          const hasNameInFirestore = !!(verifyMetadata?.name);
          
          console.log('✅ Verification after updatePublishedMetadata:', {
            hasEmail: hasEmailInFirestore,
            email: verifyMetadata?.email ? verifyMetadata.email.substring(0, 10) + '...' : 'none',
            hasTags: hasTagsInFirestore,
            tagsCount: verifyMetadata?.tagsList?.length || 0,
            hasSocials: hasSocialsInFirestore,
            socialsCount: verifyMetadata?.socialLinks?.length || 0,
            hasMusic: hasMusicInFirestore,
            musicCount: verifyMetadata?.musicList?.length || 0,
            hasBirthDate: hasBirthDateInFirestore,
            hasCity: hasCityInFirestore,
            hasName: hasNameInFirestore
          });
          
          // Verify that all fields are UNPUBLISHED (not in Firestore)
          if (hasEmailInFirestore) {
            console.warn('⚠️ Email is still in Firestore - should be unpublished');
          }
          if (hasTagsInFirestore) {
            console.warn('⚠️ Tags are still in Firestore - should be unpublished');
          }
          if (hasSocialsInFirestore) {
            console.warn('⚠️ Socials are still in Firestore - should be unpublished');
          }
          if (hasMusicInFirestore) {
            console.warn('⚠️ Music is still in Firestore - should be unpublished');
          }
          if (hasBirthDateInFirestore) {
            console.warn('⚠️ BirthDate is still in Firestore - should be unpublished');
          }
          if (hasCityInFirestore) {
            console.warn('⚠️ City is still in Firestore - should be unpublished');
          }
          if (hasNameInFirestore) {
            console.warn('⚠️ Name is still in Firestore - should be unpublished');
          }
          
          console.log('✅ All fields successfully UNPUBLISHED - data is in IPFS but not visible to others');
        } catch (verifyError) {
          console.warn('⚠️ Could not verify published metadata:', verifyError);
        }
      } catch (updateError) {
        console.error('⚠️ Error ensuring email and tags are published after registration:', updateError);
        // Don't fail registration if this update fails - it's not critical
        // The data is still in IPFS, just not published in Firestore
      }
      
      // Verify that profile was saved correctly by checking Firestore
      // Retry verification multiple times to ensure data is available
      let verificationSuccess = false;
      let verificationAttempts = 0;
      const maxVerificationAttempts = 5;
      
      while (!verificationSuccess && verificationAttempts < maxVerificationAttempts) {
        try {
          // Verify IPFS hash is on blockchain (not in Firestore) - Arc (Circle) network
          const blockchainIpfsHash = await getCIDFromArc(walletAddress);
          if (blockchainIpfsHash === result.ipfsHash) {
            console.log(`✅ Profile verification successful (attempt ${verificationAttempts + 1}/${maxVerificationAttempts}) - ipfsHash found on blockchain:`, blockchainIpfsHash);
            verificationSuccess = true;
          } else {
            verificationAttempts++;
            if (verificationAttempts < maxVerificationAttempts) {
              console.log(`⚠️ Profile verification failed (attempt ${verificationAttempts}/${maxVerificationAttempts}):`, {
                expectedIpfsHash: result.ipfsHash,
                foundIpfsHash: blockchainIpfsHash
              });
              // Wait before retrying (blockchain may take time to update)
              await new Promise(resolve => setTimeout(resolve, 2000));
            } else {
              console.warn('⚠️ Profile verification failed after all attempts:', {
                expectedIpfsHash: result.ipfsHash,
                foundIpfsHash: blockchainIpfsHash
              });
            }
          }
        } catch (verifyError) {
          verificationAttempts++;
          console.error(`Error verifying profile after registration (attempt ${verificationAttempts}/${maxVerificationAttempts}):`, verifyError);
          if (verificationAttempts < maxVerificationAttempts) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      // Additional wait to ensure Firestore is fully consistent
      if (verificationSuccess) {
        console.log('✅ Profile verified successfully, waiting additional 500ms for Firestore consistency...');
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        console.warn('⚠️ Profile verification incomplete, but continuing with additional wait...');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Reload profile from IPFS to ensure we have the latest data with ipfsHash
      // This ensures that after refresh, the profile will be found correctly
      const loadMarkersPromise = loadAllProfilesAndCreateMarkers();
      
      // Also reload the profile to ensure ipfsHash is in currentProfile
      if (walletAddress && encryptionKey) {
        try {
          // Wait a bit more before reloading to ensure Firestore is ready
          await new Promise(resolve => setTimeout(resolve, 300));
          const reloadedProfile = await getUserProfileByWallet(walletAddress, encryptionKey);
          if (reloadedProfile && reloadedProfile.ipfsHash) {
            const cleanReloaded = cleanProfileData(reloadedProfile);
            setCurrentProfile(cleanReloaded);
            // Update myProfileForMatch with reloaded profile to ensure search has latest data
            setMyProfileForMatch(cleanReloaded);
            console.log('✅ Profile reloaded after registration with ipfsHash:', cleanReloaded.ipfsHash);
          } else {
            console.warn('⚠️ Profile reload failed - no ipfsHash found');
          }
        } catch (reloadError) {
          console.error('Error reloading profile after registration:', reloadError);
          // Continue anyway - profile is already saved
        }
      }
      
      if (mapInstanceRef.current && profileData.location) {
        const location = profileData.location;
        if (location && location.lat && location.lng) {
          setTimeout(() => {
            if (mapInstanceRef.current) {
              mapInstanceRef.current.setView([location.lat, location.lng], 13);
            }
          }, 200);
        }
      }
      
      await loadMarkersPromise;

    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Connect to MetaMask with unified authentication (single signature for wallet proof + encryption key)
  const connectMetaMask = async () => {
    if (!isMetaMaskInstalled()) {
      alert('MetaMask is not installed. Please install MetaMask to continue.');
      window.open('https://metamask.io/download/', '_blank');
      return;
    }

    // Check if window.ethereum is available
    if (!window.ethereum) {
      setNotification({ 
        message: 'MetaMask is not available. Please ensure MetaMask is installed and unlocked.', 
        type: 'error' 
      });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    setIsConnecting(true);
    try {
      // Step 1: Request account access
      let accounts;
      try {
        accounts = await window.ethereum.request({
          method: 'eth_requestAccounts',
        });
      } catch (requestError) {
        // Handle connection errors specifically
        if (requestError.code === 4001) {
          throw new Error('Connection request rejected. Please approve the connection request in MetaMask.');
        } else if (requestError.code === -32002) {
          throw new Error('Connection request already pending. Please check MetaMask and approve the connection.');
        } else {
          throw new Error(`Failed to connect to MetaMask: ${requestError.message || 'Unknown error'}. Please ensure MetaMask is unlocked and try again.`);
        }
      }

      if (accounts.length === 0) {
        throw new Error('No accounts found. Please unlock MetaMask and try again.');
      }

      const address = accounts[0];
      
      // Step 2: Use unified authentication (single signature for wallet proof + encryption key)
      // This will request ONE signature that covers:
      // - Proof of wallet ownership
      // - Encryption key generation
      console.log('Starting unified authentication...');
      console.log('A signature request will appear in MetaMask. Please approve it to continue.');
      let authResult;
      try {
        authResult = await authenticateWalletUnified(address);
        console.log('Unified authentication successful');
      } catch (authError) {
        // Provide clearer error messages for signature-related errors
        if (authError.code === 4001) {
          throw new Error('Signature request rejected. Please approve the signature request in MetaMask to connect.');
        } else if (authError.message && authError.message.includes('Signature request rejected')) {
          throw authError;
        } else {
          throw new Error(`Failed to get signature: ${authError.message || 'Please approve the signature request in MetaMask.'}`);
        }
      }

      // Clear ALL profile-related state before loading new wallet's profile
      // This prevents data from previous wallet from leaking into new profile
      setCurrentUser(null);
      setCurrentProfile(null);
      setEncryptedProfileData(null);
      setAboutVisible(false);
      setShowRegistrationForm(false);
      setIsLoadingUser(true);
      // Reset published states before loading new profile
      setPublishedTags([]);
      setPublishedSocials([]);
      setPublishedBirthDate(false);
      setPublishedCity(false);
      setPublishedWallet(false);
      setPublishedName(false);
      setPublishedBio(false);
      
      // Set wallet address and encryption key AFTER clearing state
      setWalletAddress(address);
      // Sync ref immediately so loadUserProfile (called below) sees the new wallet
      // before React re-renders and useEffect updates the ref.
      walletAddressRef.current = address;
      setEncryptionKey(authResult.encryptionKey);
      encryptionKeyRef.current = authResult.encryptionKey;
      setEncryptionKeyError(null);


      // If the user connected from a public profile URL (/profile/:username),
      // switch to the canonical "My Profile" route so the URL matches the view.
      // Use replace to avoid cluttering browser history.
      try {
        const currentPath = getWindowNormalizedPathname();
        if (currentPath.startsWith('/profile/')) {
          navigate('/myprofile', { replace: true });
        }
      } catch (e) {
        // Non-fatal: routing will still work, but URL may remain unchanged.
        console.warn('Failed to redirect to /myprofile after wallet connect:', e);
      }
      
      // Show profile section (will show loading or registration form)
      setProfileVisible(true);

      // Step 3: Load user profile - this will determine if registration form or profile should be shown
      try {
        await loadUserProfile(address, true); // Pass flag to skip re-authentication
        // Update lastvisit timestamp after successful profile load
        await updateLastVisit(address);
      } catch (err) {
        console.error('Error loading user profile:', err);
        
        // Before showing registration form, verify that profile really doesn't exist
        // This prevents showing registration form when profile exists but load failed due to timeout/network issues
        let profileExistsInConnect = false;
        try {
          console.log('Verifying profile existence before showing registration form...');
          // Check if IPFS hash exists on blockchain (more reliable than Firestore when network is slow)
          let blockchainIpfsHash = null;
          try {
            blockchainIpfsHash = await Promise.race([
              getCIDFromArc(address),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Blockchain verification timeout')), 8000))
            ]);
          } catch (blockchainErr) {
            console.log('Blockchain verification failed:', blockchainErr);
          }
          
          // Check Firestore
          const verificationMetadata = await Promise.race([
            getUserProfileByWallet(address, null),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore verification timeout')), 8000))
          ]).catch(err => {
            if (err.message === 'Firestore verification timeout') {
              console.log('Firestore verification timed out');
              return null;
            }
            throw err;
          });
          
          // Profile exists if we have Firestore metadata OR blockchain hash
          if (verificationMetadata || blockchainIpfsHash) {
            profileExistsInConnect = true;
            
            if (verificationMetadata && blockchainIpfsHash) {
              // Profile exists! Retry loading with retries
              console.log('✅ Profile exists (IPFS hash on blockchain), retrying load...');
              let retryCount = 0;
              const maxRetries = 3;
              let profileLoaded = false;
              
              while (retryCount < maxRetries && !profileLoaded) {
                try {
                  await loadUserProfile(address, true);
                  profileLoaded = true;
                  // Update lastvisit timestamp after successful profile load
                  await updateLastVisit(address);
                } catch (retryErr) {
                  retryCount++;
                  console.log(`Retry ${retryCount}/${maxRetries} failed:`, retryErr);
                  if (retryCount < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)); // Exponential backoff
                  }
                }
              }
              
              if (!profileLoaded) {
                console.error('Failed to load profile after retries, but profile exists');
                // Profile exists but couldn't load - show error instead of registration form
                setIsLoadingUser(false);
                setIsLoadingProfileFromIPFS(false);
                setShowRegistrationForm(false);
                setProfileLoadError('Failed to load your profile. This may be due to network issues. Please refresh the page to try again.');
                setProfileVisible(true);
              }
              return; // Exit early if profile was loaded or error shown
            } else if (blockchainIpfsHash) {
              // Profile exists (IPFS hash on blockchain) but Firestore query failed
              console.log('✅ Profile exists (IPFS hash on blockchain) but Firestore query failed - showing error instead of registration form');
              setIsLoadingUser(false);
              setIsLoadingProfileFromIPFS(false);
              setShowRegistrationForm(false);
              setProfileLoadError('Failed to load your profile. This may be due to network issues. Please refresh the page to try again.');
              setProfileVisible(true);
              return; // Exit early - show error instead of registration form
            }
          }
        } catch (verifyErr) {
          console.error('Error verifying profile existence:', verifyErr);
          // If verification also fails, check blockchain one more time
          if (!profileExistsInConnect) {
            try {
              const finalCheckHash = await Promise.race([
                getCIDFromArc(address),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Final check timeout')), 5000))
              ]);
              if (finalCheckHash) {
                console.log('✅ Profile exists (IPFS hash found on blockchain) - showing error instead of registration form');
                profileExistsInConnect = true;
                setIsLoadingUser(false);
                setIsLoadingProfileFromIPFS(false);
                setShowRegistrationForm(false);
                setProfileLoadError('Failed to load your profile. This may be due to network issues. Please refresh the page to try again.');
                setProfileVisible(true);
                return;
              }
            } catch (finalErr) {
              console.log('Final check also failed:', finalErr);
            }
          }
        }
        
        // Only show registration form if profile really doesn't exist after all verification attempts
        if (!profileExistsInConnect) {
          setShowRegistrationForm(true);
        }
        setIsLoadingUser(false);

        // Reset registration form data
        setRegistrationData({
          name: '',
          email: '',
          username: '',
          tags: [''],
          socials: [''],
          birthdate: '',
          city: '',
          cityLat: null,
          cityLng: null
        });
      }
    } catch (error) {
      console.error('Error connecting to MetaMask:', error);
      const errorMessage = error.message || 'An error occurred while connecting to MetaMask.';
      
      // Check if user rejected the signature (code 4001 or contains "denied"/"rejected")
      const isUserRejection = error.code === 4001 || 
                             errorMessage.toLowerCase().includes('denied') || 
                             errorMessage.toLowerCase().includes('rejected') ||
                             errorMessage.toLowerCase().includes('user denied');
      
      if (isUserRejection) {
        // User rejection - show notification and auto-dismiss after 4 seconds
        setNotification({ 
          message: errorMessage.includes('Signature') 
            ? 'Signature request rejected. Please approve the signature request in MetaMask to connect.' 
            : 'Connection rejected. Please approve the connection request in MetaMask.',
          type: 'error' 
        });
        setTimeout(() => setNotification(null), 5000);
      } else {
        // Other errors - show notification and auto-dismiss after 6 seconds to give user time to read
        setNotification({ message: errorMessage, type: 'error' });
        setTimeout(() => setNotification(null), 6000);
      }
      // Reset states on error
      disconnectWallet();
    } finally {
      setIsConnecting(false);
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    // Session is tied to wallet address and will be restored on reconnection
    disconnectWalletCrypto();
    setWalletAddress(null);
    setCurrentUser(null);
    setCurrentProfile(null);
    setProfileVisible(false);
    setShowRegistrationForm(false);
    setIsLoadingUser(false);
    setEncryptionKey(null); // Clear encryption key from memory
    setLastBaseTxHash(null); // Clear Arc (Circle) transaction hash
    setIsSavingToBlockchain(false); // Clear blockchain saving state
    encryptionKeyRef.current = null; // Clear encryption key from ref
    setEncryptionKeyError(null); // Clear error message
    isGeneratingEncryptionKeyRef.current = false; // Reset generation flag
    // Reset published states when disconnecting
    setPublishedTags([]);
    setPublishedSocials([]);
    setPublishedBirthDate(false);
    setPublishedCity(false);
    setPublishedWallet(false);
    setPublishedName(false);
    setPublishedBio(false);
    setPublishedEmail(false);
    setPublishedPreferredContact(false);
    setPublishedRelationship(false);
    setPublishedLookingFor(false);
    setPublishedLanguages(false);
    setPublishedEducation(false);
    setPublishedOccupation(false);
  };

  // Delete user profile and disconnect wallet
  const handleDeleteProfile = async () => {
    if (!walletAddress) {
      return;
    }

    // Confirm deletion
    const confirmed = window.confirm(
      'Are you sure you want to delete your profile? This action cannot be undone. All your data will be permanently deleted from PeopleTag.'
    );

    if (!confirmed) {
      return;
    }

    try {
      // Show loading state
      setIsLoadingUser(true);
      
      console.log('🗑️ Starting profile deletion for:', walletAddress);
      
      // Step 1: Delete profile and wallet mapping from Firestore
      // This is the key step - Firestore is now the "source of truth" for profile existence
      // Even if blockchain has old IPFS hash, we won't load it without Firestore entry
      console.log('🗑️ Deleting from Firestore...');
      await deleteUserProfile(walletAddress);
      console.log('✅ Firestore data deleted');
      
      // Step 2: Clear all local storage caches for this wallet
      const normalizedAddress = walletAddress.toLowerCase();
      localStorage.removeItem(`lastSavedCID_${walletAddress}`);
      localStorage.removeItem(`lastSavedCID_${normalizedAddress}`);
      localStorage.removeItem(`firestoreEncryptionKey_${walletAddress}`);
      localStorage.removeItem(`firestoreEncryptionKey_${normalizedAddress}`);
      localStorage.removeItem(`profileDeleted_${normalizedAddress}`);
      
      // Step 3: Mark this wallet as having deleted profile (prevents blockchain-only reload)
      localStorage.setItem(`profileDeleted_${normalizedAddress}`, 'true');
      console.log('✅ Local caches cleared and deletion marker set');
      
      // Step 4: Reload all profiles to remove the deleted one
      await loadAllProfilesAndCreateMarkers();
      
      // Step 5: Disconnect wallet and clear all state
      disconnectWallet();
      
      setNotification({ 
        message: 'Your profile has been permanently deleted.', 
        type: 'success' 
      });
      setTimeout(() => setNotification(null), 5000);
      
      console.log('✅ Profile deletion complete');
    } catch (error) {
      console.error('Error deleting profile:', error);
      alert('An error occurred while deleting your profile. Please try again.');
    } finally {
      setIsLoadingUser(false);
    }
  };

  // Check for existing connection on mount and load user profile
  useEffect(() => {
      const loadUserOnMount = async () => {
      // No need to wait - MetaMask is already initialized if wallet was previously connected
      
      // Check if we're viewing someone else's profile - if so, don't load user's own profile
      const pathname = getWindowNormalizedPathname();
      const isViewingOtherProfile = pathname.startsWith('/profile/') && pathname !== '/myprofile';
      const isRootRoute = pathname === '/';
      const isMyProfileRoute = pathname === '/myprofile';
      
      const savedAddress = getSavedWalletAddress();
      if (savedAddress) {
        setWalletAddress(savedAddress);
        
        // Restore encryption key from saved signature (if available)
        // This allows the key to be available immediately on page load without requesting a new signature
        try {
          const savedSignatureData = getSavedWalletSignature();
          if (savedSignatureData && savedSignatureData.signature) {
            console.log('Restoring encryption key from saved signature...');
            const restoredKey = generateEncryptionKeyFromSignature(savedSignatureData.signature);
            setEncryptionKey(restoredKey);
            encryptionKeyRef.current = restoredKey;
            setEncryptionKeyError(null);
            console.log('Encryption key restored successfully from saved signature');
          } else {
            console.log('No saved signature found, encryption key will be generated on next connection');
          }
        } catch (error) {
          console.error('Error restoring encryption key from saved signature:', error);
          // Don't block the UI if restoration fails - key will be generated on next connection
          setEncryptionKeyError(null); // Don't show error on page load
        }
        
        // CRITICAL FIX: Skip loading profile on root route and /myprofile route - let the route handler take care of it
        // This prevents race conditions and ensures proper "My Profile" flag setting
        if (isRootRoute || isMyProfileRoute) {
          console.log(`⏭️ [loadUserOnMount] Skipping profile load on ${isRootRoute ? 'root' : '/myprofile'} route - route handler will handle it`);
          setIsLoadingUser(false);
          setIsLoadingProfileFromIPFS(false);
          return;
        }
        
        // Only load user's own profile if not viewing someone else's profile
        if (!isViewingOtherProfile) {
          // Show loading screen immediately
          setIsLoadingUser(true);
          setProfileVisible(true);
          
          
          // Load user profile for saved wallet
          // Skip unified auth - key already restored above
          // Use loadUserProfile directly (it's optimized and handles all cases)
          try {
            await loadUserProfile(savedAddress, true); // skipUnifiedAuth=true (key already restored)
            // Update lastvisit timestamp after successful profile load
            await updateLastVisit(savedAddress);
          } catch (loadError) {
            console.error('Error loading profile on mount:', loadError);
            // Before showing registration form, verify that profile really doesn't exist
            try {
              const mountVerificationHash = await Promise.race([
                getCIDFromArc(savedAddress),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Mount verification timeout')), 5000))
              ]);
              if (mountVerificationHash) {
                console.log('✅ Profile exists (IPFS hash found on Arc (Circle) blockchain) - showing error instead of registration form');
                setShowRegistrationForm(false);
                setProfileLoadError('Failed to load your profile. This may be due to network issues. Please refresh the page to try again.');
                setProfileVisible(true);
                setIsLoadingUser(false);
                setIsLoadingProfileFromIPFS(false);
                return;
              }
            } catch (mountVerifyErr) {
              console.log('Mount verification failed:', mountVerifyErr);
            }
            // Show registration form as fallback only if profile doesn't exist
            setShowRegistrationForm(true);
            setProfileVisible(true);
            setIsLoadingUser(false);
            setIsLoadingProfileFromIPFS(false);
          }
          
          // After loading, if no profile exists, registration form should be shown
          // This is handled inside loadUserProfile or direct lookup above
        } else {
          // Viewing someone else's profile - just set loading states to false
          setIsLoadingUser(false);
          setIsLoadingProfileFromIPFS(false);
        }
      } else {
        // No wallet connected - ensure profile is hidden
        setCurrentProfile(null);
        setProfileVisible(false);
        setShowRegistrationForm(false);
        setIsLoadingUser(false);
      }
    };

    loadUserOnMount();

    // Setup account change listener
    // When user switches accounts in MetaMask, simply log them out
    const cleanup = setupAccountChangeListener(async (newAddress) => {
      console.log('Account changed in MetaMask, logging out...');
      // Simply disconnect/logout when account changes
      // User will need to reconnect with the new account if they want to use it
      disconnectWallet();
    });

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load encrypted data when encryption section is expanded if it's missing
  useEffect(() => {
    if (encryptionDataExpanded && !encryptedProfileData && currentProfile?.ipfsHash && encryptionKey) {
      console.log('🔄 Loading encrypted data for encryption section display...');
      fetchFromIPFS(currentProfile.ipfsHash)
        .then((data) => {
          if (data) {
            setEncryptedProfileData(data);
            console.log('✅ Encrypted data loaded for encryption section');
          }
        })
        .catch((err) => {
          console.warn('Failed to load encrypted data for encryption section:', err);
        });
    }
  }, [encryptionDataExpanded, encryptedProfileData, currentProfile?.ipfsHash, encryptionKey]);

  // Auto-scroll to bottom of conversation when new messages are added
  useEffect(() => {
    if (aiConversationRef.current) {
      aiConversationRef.current.scrollTop = aiConversationRef.current.scrollHeight;
    }
  }, [aiMessages, aiLoading]);

  const isOnMessages = normalizedPathname.startsWith('/messages');
  const isOnGroups = normalizedPathname === '/groups';
  const isOnProfile = normalizedPathname === '/myprofile' || normalizedPathname.startsWith('/profile/');
  const isOnDiscover = normalizedPathname === '/' || normalizedPathname.startsWith('/search');
  const isOnMore = aboutVisible || tagRunVisible || activityFeedVisible || bugVisible;

  const mobileHeader = (
    <div className="mobile-header">
      <a
        href={isMobile ? "/m" : "/"}
        onClick={(event) => {
          event.preventDefault();
          navigate('/');
        }}
        className="mobile-header__logo"
      >
        <div className="logo-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <text x="12" y="19" textAnchor="middle" fill="white" fontSize="20" fontWeight="bold">#</text>
          </svg>
        </div>
        <div className="logo-text">
          <span className="logo-people">People</span><span className="logo-tag">Tag</span>
        </div>
      </a>
      <div className="mobile-header__actions">
        <button
          className="mobile-bottom-nav__item"
          onClick={toggleLeftSection}
          aria-label="Search"
        >
          Search
        </button>
        {walletAddress ? (
          <button
            className="mobile-bottom-nav__item"
            onClick={() => {
              setMessagesVisible(true);
              navigate('/messages');
            }}
            aria-label="Messages"
          >
            Msg
            {unreadMessagesCount > 0 ? ` (${unreadMessagesCount})` : ''}
          </button>
        ) : null}
        {walletAddress ? (
          <button className="mobile-wallet-chip" onClick={disconnectWallet} title="Disconnect">
            {formatAddress(walletAddress)}
          </button>
        ) : (
          <button className="mobile-wallet-chip" onClick={connectMetaMask} disabled={isConnecting}>
            {isConnecting ? 'Connecting...' : 'Connect'}
          </button>
        )}
      </div>
    </div>
  );

  const mobileBottomNav = (
    <div className="mobile-bottom-nav">
      <button
        className={`mobile-bottom-nav__item ${isOnDiscover ? 'active' : ''}`}
        onClick={() => {
          setMobileMenuOpen(false);
          setAboutVisible(false);
          setTagRunVisible(false);
          setGroupsVisible(false);
          setMessagesVisible(false);
          setActivityFeedVisible(false);
          setBugVisible(false);
          setLeftSectionVisible(false);
          setSmartRecommendationsVisible(false);
          navigate('/');
        }}
      >
        Discover
      </button>
      <button
        className={`mobile-bottom-nav__item ${isOnProfile ? 'active' : ''}`}
        onClick={() => {
          setMobileMenuOpen(false);
          if (!walletAddress) return;
          setProfileVisible(true);
          setAboutVisible(false);
          setGroupsVisible(false);
          setMessagesVisible(false);
          setActivityFeedVisible(false);
          navigate('/myprofile');
        }}
      >
        Profile
      </button>
      <button
        className={`mobile-bottom-nav__item ${isOnMessages ? 'active' : ''}`}
        onClick={() => {
          setMobileMenuOpen(false);
          if (!walletAddress) return;
          setMessagesVisible(true);
          navigate('/messages');
        }}
      >
        Messages
      </button>
      <button
        className={`mobile-bottom-nav__item ${isOnGroups ? 'active' : ''}`}
        onClick={() => {
          setMobileMenuOpen(false);
          if (!walletAddress) return;
          setGroupsVisible(true);
          navigate('/groups');
        }}
      >
        Groups
      </button>
      <button
        className={`mobile-bottom-nav__item ${isOnMore ? 'active' : ''}`}
        onClick={() => setMobileMenuOpen(true)}
      >
        More
      </button>
    </div>
  );

  const appBody = (
    <>
      <header className="app-header" style={{ zIndex: activityFeedVisible ? 1000 : 1001, position: 'relative' }}>
        <div className="header-left">
          <a href={isMobile ? "/m" : "/"} onClick={(e) => { e.preventDefault(); mobileNavigate('/'); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none', color: 'inherit' }}>
            <div className="logo-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <text x="12" y="20" textAnchor="middle" fill="white" fontSize="24" fontWeight="bold">#</text>
              </svg>
            </div>
            <div className="logo-text">
              <span className="logo-people">People</span><span className="logo-tag">Tag</span>
            </div>
          </a>
        </div>
        <div className="header-cozy">
          {/* Cozy animated connections between logo and navigation */}
          <CozyConnections />
        </div>
        <nav
          ref={headerNavRef}
          className="header-nav"
          style={{ 
            // Reserve space for right-side panels even when not logged in (e.g., viewing other profiles)
            marginRight: messagesVisible ? '50%' : 
                        activityFeedVisible ? '400px' :
                        tagRunVisible ? '400px' :
                        (walletAddress && showRegistrationForm && profileVisible && !currentProfile) ? '690px' :
                        (profileVisible || groupsVisible || bugVisible) ? '340px' : '0px',
            zIndex: activityFeedVisible ? 1010 : 'auto'
          }}
        >
          <a href="#" className={`nav-link ${aboutVisible ? 'active' : ''}`} onClick={(e) => {
            e.preventDefault();
            mobileNavigate('/about');
          }}>About PeopleTag</a>
          <a href="#" className={`nav-link ${!aboutVisible ? 'active' : ''}`} onClick={(e) => {
            e.preventDefault();
            mobileNavigate('/');
          }}>Discover</a>
          <a href="#" className={`nav-link ${aboutVisible || !walletAddress ? 'disabled' : ''} ${aiAssistantVisible ? 'active' : ''}`} onClick={aboutVisible || !walletAddress ? undefined : toggleAiAssistant}>TagAI</a>
          <a href="#" className={`nav-link ${!walletAddress ? 'disabled' : ''} ${tagRunVisible ? 'active' : ''}`} 
            onClick={(e) => {
              e.preventDefault();
              if (!walletAddress) return;
              // Hide all sections
              setProfileVisible(false);
              setGroupsVisible(false);
              setMessagesVisible(false);
              setActivityFeedVisible(false);
              setBugVisible(false);
              setAboutVisible(false);
              setAiAssistantVisible(false);
              // Toggle TagRUN section
              const newTagRunVisible = !tagRunVisible;
              setTagRunVisible(newTagRunVisible);
              // Navigate based on visibility
              if (newTagRunVisible) {
                navigate('/tagrun');
              } else {
                navigate('/');
              }
            }}
          >TagRUN</a>
          <a href="#" className={`nav-link ${!walletAddress ? 'disabled' : ''} ${groupsVisible ? 'active' : ''}`} 
            onClick={(e) => {
              e.preventDefault();
              // Only allow if wallet is connected
              if (!walletAddress) {
                return;
              }
              toggleGroups();
            }}
          >Groups</a>
          <a href="#" className={`nav-link ${!walletAddress ? 'disabled' : ''} ${currentUser && currentProfile && currentProfile.username === currentUser && profileVisible ? 'active' : ''}`} 
            onClick={(e) => { 
              e.preventDefault();
              // Only allow if wallet is connected
              if (!walletAddress) {
                return;
              }
              
              // Simply navigate to /myprofile route - let the route handler do all the work
              // This matches the behavior of typing the URL directly in the address bar
              navigate('/myprofile');
            }}
          >My Profile</a>
          <div className="header-wallet">
            {walletAddress ? (
              <div className="wallet-connected">
                <span className="wallet-address">{formatAddress(walletAddress)}</span>
                <button className="wallet-disconnect" onClick={disconnectWallet} title="Disconnect">
                  ×
                </button>
              </div>
            ) : (
              <button 
                className="wallet-connect" 
                onClick={connectMetaMask}
                disabled={isConnecting}
              >
                {isConnecting ? 'Connecting...' : 'Connect Wallet'}
              </button>
            )}
          </div>
        </nav>
      </header>
      {/* Notification */}
      {notification && (
      <div
        style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: notification.type === 'success' ? '#4a90e2' : notification.type === 'info' ? '#666' : '#ff4444',
          color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 10004,
            fontSize: '14px',
            fontWeight: '500',
            animation: 'slideUp 0.3s ease-out'
          }}
        >
          {notification.message}
        </div>
      )}

      {/* Badge Notification */}
      {newBadges.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 10000,
          animation: 'slideIn 0.3s ease'
        }}>
          {newBadges.map((badge, index) => (
            <div
              key={badge.id}
              style={{
                marginBottom: '8px',
                padding: '16px',
                backgroundColor: 'white',
                border: `2px solid ${badge.color}`,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minWidth: '280px'
              }}
            >
              <div style={{ fontSize: '32px' }}>{badge.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '4px' }}>
                  Badge Unlocked!
                </div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: badge.color, marginBottom: '2px' }}>
                  {badge.name}
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>
                  {badge.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {isMobile && mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="mobile-menu-title">More</div>
            <button
              className="mobile-menu-item"
              onClick={() => {
                setMobileMenuOpen(false);
                navigate('/about');
              }}
            >
              About PeopleTag
            </button>
            <button
              className="mobile-menu-item"
              onClick={() => {
                setMobileMenuOpen(false);
                if (!walletAddress) return;
                setTagRunVisible(true);
                navigate('/tagrun');
              }}
            >
              TagRUN
            </button>
            <button
              className="mobile-menu-item"
              onClick={() => {
                setMobileMenuOpen(false);
                if (!walletAddress) return;
                setActivityFeedVisible(true);
                navigate('/activityfeed');
              }}
            >
              Activity Feed
            </button>
            <button
              className="mobile-menu-item"
              onClick={() => {
                setMobileMenuOpen(false);
                setBugVisible(true);
              }}
            >
              Report a bug
            </button>
            <button className="mobile-menu-item" onClick={() => setMobileMenuOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
      {/* AI assistant appears above the map, below the left section but does not affect layout of the left section */}
      {aiAssistantVisible && (
        <div
          className="ai-assistant-section"
        style={{
            position: 'absolute',
            left: '0px',
            width: '100%',
            top: '52px',
            zIndex: 900, // Lower z-index so it's below the profile section, but still above the map
        display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
        alignItems: 'center',
            pointerEvents: 'auto',
            maxHeight: 'calc(100vh - 63px)',
            overflow: 'hidden',
          }}
        >
          <div ref={aiConversationRef} className="ai-conversation-container" style={{ 
            width: '900px', 
            maxWidth: '100%', 
            flex: 1, 
            minHeight: 0,
            overflowY: 'auto',
            padding: isFirstMessage ? '0px 20px' : '6px 20px 20px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            {aiMessages.length === 0 ? (
              <div className="ai-welcome-message" style={{
                textAlign: 'center',
                color: '#666',
                padding: '8px 20px',
                fontSize: '12px',
                lineHeight: '1.4'
              }}>

              </div>
            ) : (
              aiMessages.map((message, index) => (
                <div
                  key={index}
                  style={{
                    alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '80%',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-end'
                  }}
                >
                  <div
                    className={`ai-message ${message.role === 'user' ? 'ai-message-user' : 'ai-message-assistant'}`}
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      borderRadius: '10px',
                      backgroundColor: message.role === 'user' ? '#4a90e2' : '#f0f0f0',
                      color: message.role === 'user' ? 'white' : '#333',
                      fontSize: '13px',
                      lineHeight: '1.4',
                      wordWrap: 'break-word',
                      position: 'relative'
                    }}
                  >
                    {(() => {
                      if (message.role !== 'assistant') return message.content;
                      const parts = [];
                      const regex = /\[([^\]]+)\]\((\/[^)\s]+)\)/g;
                      let lastIndex = 0;
                      let match;
                      let key = 0;
                      while ((match = regex.exec(message.content)) !== null) {
                        if (match.index > lastIndex) {
                          parts.push(message.content.slice(lastIndex, match.index));
                        }
                        const label = match[1];
                        const path = match[2];
                        parts.push(
                          <a
                            key={`lnk-${index}-${key++}`}
                            href={path}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(path);
                            }}
                            style={{ color: '#4a90e2', textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            {label}
                          </a>
                        );
                        lastIndex = match.index + match[0].length;
                      }
                      if (lastIndex < message.content.length) {
                        parts.push(message.content.slice(lastIndex));
                      }
                      return parts.length > 0 ? parts : message.content;
                    })()}
                  </div>
                  {message.role === 'assistant' && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        opacity: 0.6,
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                    >
                      <button
                        onClick={async (e) => {
                          try {
                            await navigator.clipboard.writeText(message.content);
                            // Show brief feedback
                            const btn = e.currentTarget;
                            const originalOpacity = btn.style.opacity;
                            btn.style.opacity = '0.3';
                            setTimeout(() => {
                              btn.style.opacity = originalOpacity;
                            }, 200);
                          } catch (err) {
                            console.error('Failed to copy:', err);
                          }
                        }}
                        style={{
                          width: '24px',
                          height: '24px',
                          padding: '0',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <Tooltip text="Copy to clipboard">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </Tooltip>
                      </button>
                      <button
                        onClick={async () => {
                          // Find the user message that came before this assistant message
                          const userMessageIndex = index - 1;
                          if (userMessageIndex >= 0 && aiMessages[userMessageIndex]?.role === 'user') {
                            const userMessage = aiMessages[userMessageIndex].content;
                            
                            // Remove this assistant message and any messages after it
                            setAiMessages(prev => prev.slice(0, index));
                            
                            // Set the input to the user message and trigger submit
                            setAiInput(userMessage);
                            setAiLoading(true);
                            
                            try {
                              // Build conversation history (up to but not including the regenerated message)
                              const messagesToSend = aiMessages.slice(0, index);
                              
                              const apiUrl = process.env.REACT_APP_API_URL || 
                                (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
                              const response = await fetch(`${apiUrl}/api/v1/ai/chat`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                  messages: messagesToSend,
                                  model: selectedModel
                                }),
                              });

                              // Check content type before parsing
                              const contentType = response.headers.get('content-type') || '';
                              
                              if (!response.ok) {
                                // If response is HTML, return error without trying to parse JSON
                                if (contentType.includes('text/html')) {
                                  const htmlText = await response.text();
                                  console.error('Server returned HTML instead of JSON:', htmlText.substring(0, 200));
                                  throw new Error(`Server returned HTML error page. Status: ${response.status}. This usually means the Hugging Face API endpoint is misconfigured.`);
                                }
                                const errorData = await response.json().catch(() => ({}));
                                throw new Error(errorData.message || errorData.error || `API error: ${response.status} ${response.statusText}`);
                              }

                              // Check if response is JSON before parsing
                              if (!contentType.includes('application/json')) {
                                const text = await response.text();
                                console.error('Expected JSON but received:', contentType, text.substring(0, 200));
                                throw new Error(`Server returned ${contentType} instead of JSON. This usually means there's an error on the server side.`);
                              }

                              const data = await response.json();
                              const aiResponse = data.message || data.text || 'Sorry, I could not generate a response.';
                              
                              // Add new AI response
                              const aiMessage = { role: 'assistant', content: aiResponse };
                              setAiMessages(prev => [...prev, aiMessage]);
                              setAiInput('');
                            } catch (error) {
                              console.error('AI Assistant error:', error);
                              let errorMessage = error.message || 'Failed to get AI response. Please try again.';
                              if (errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_CONNECTION_REFUSED') || errorMessage.includes('Connection refused')) {
                                errorMessage = 'API server is not running. Please start it with: npm run server (in a separate terminal)';
                              }
                              const errorMsg = { role: 'assistant', content: `Error: ${errorMessage}` };
                              setAiMessages(prev => [...prev, errorMsg]);
                            } finally {
                              setAiLoading(false);
                            }
                          }
                        }}
                        style={{
                          width: '24px',
                          height: '24px',
                          padding: '0',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        disabled={aiLoading}
                      >
                        <Tooltip text="Regenerate response">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10"></polyline>
                          <polyline points="1 20 1 14 7 14"></polyline>
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                        </svg>
                        </Tooltip>
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
            {aiLoading && (
              <div className="ai-message ai-message-assistant" style={{
                alignSelf: 'flex-start',
                padding: '10px 14px',
                borderRadius: '10px',
                backgroundColor: '#f0f0f0',
                color: '#666',
                fontSize: '13px',
                fontStyle: 'italic'
              }}>
                <span className="ai-loading-spinner" style={{ display: 'inline-block', marginRight: '8px' }}>#</span>
                Thinking...
              </div>
            )}
          </div>
          <form
            onSubmit={handleAiSubmit}
            className="ai-assistant-form"
            style={{ width: '900px', maxWidth: '100%', padding: '0 20px 6px 20px', display: 'flex', gap: '15px', alignItems: 'flex-start', flexShrink: 0 }}
          >
            <textarea
              ref={aiInputRef}
              className="ai-input-field"
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              placeholder={isFirstMessage ? "Hello! Welcome to PeopleTag. I'm TagAI, here to help you make the most of our community. What would you like to do today? Are you looking to: - Find people with similar interests? - Discover new groups that align with your passions? - Explore services that can make your life easier? Let me know, and I'll be happy to assist you! *** Please note: AI is in early test phase now, answers are not so good yet ***" : ""}
              rows={3}
              style={{ flex: 1, minHeight: isFirstMessage ? '100px' : '50px', maxHeight: '200px', resize: 'vertical' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAiSubmit(e);
                }
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end', flexShrink: 0 }}>
              <button type="submit" className="ai-submit-btn" disabled={aiLoading || !aiInput.trim()}>
                {aiLoading ? (
                  <span className="ai-loading-spinner">#</span>
                ) : (
                  'Send'
                )}
              </button>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '4px',
                fontSize: '11px',
                color: '#666'
              }}>
                <label htmlFor="ai-model-select" style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '4px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  width: '100%'
                }}>
                  <span style={{ fontSize: '11px', fontWeight: '500', color: '#666' }}>Model:</span>
                  <select
                    id="ai-model-select"
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    disabled={aiLoading}
                    style={{
                      padding: '4px 24px 4px 8px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontFamily: 'inherit',
                      backgroundColor: 'white',
                      color: '#333',
                      cursor: aiLoading ? 'not-allowed' : 'pointer',
                      outline: 'none',
                      width: '100%',
                      minWidth: '140px',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 4L6 8L10 4' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 6px center',
                      paddingRight: '28px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#4a90e2';
                      e.target.style.boxShadow = '0 0 0 2px rgba(74, 144, 226, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e0e0e0';
                      e.target.style.boxShadow = 'none';
                    }}
                    onMouseEnter={(e) => {
                      if (!aiLoading) {
                        e.target.style.borderColor = '#d0d0d0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!aiLoading && document.activeElement !== e.target) {
                        e.target.style.borderColor = '#e0e0e0';
                      }
                    }}
                  >
                    {availableModels.length > 0 ? (
                      availableModels.map((model) => (
                        <option key={model.key} value={model.key}>
                          {model.name} {model.recommended ? '⭐' : ''}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="llama-3.1-8b">Llama 3.1 8B ⭐</option>
                        <option value="llama-3.2-3b">Llama 3.2 3B</option>
                        <option value="mistral-7b">Mistral 7B ⭐</option>
                        <option value="llama-3.1-70b">Llama 3.1 70B</option>
                        <option value="mixtral-8x7b">Mixtral 8x7B ⭐</option>
                        <option value="llama-3-8b">Llama 3 8B</option>
                        <option value="gemma-2-7b">Gemma 2 7B</option>
                        <option value="mistral-small">Mistral Small</option>
                      </>
                    )}
                  </select>
                </label>
              </div>
            </div>
          </form>
        </div>
      )}
      <div className="main-content">
        <div id="map" ref={mapRef}></div>
        {/* GBIX Display - shows selected gbix item image in right bottom */}
        <GbixDisplay gbixItem={selectedGbixItem} />
        {/* Profile toggle button - top right corner, matches zoom/location button style */}
        {!aboutVisible && walletAddress && (currentProfile || showRegistrationForm || profileVisible || isLoadingUser || isLoadingProfileFromIPFS) && (
          <button
            id="profile-toggle-btn"
            onClick={toggleProfile}
            className={currentUser && currentProfile && currentProfile.username === currentUser && profileVisible ? 'profile-toggle-glow' : ''}
            style={{
              position: 'absolute',
              ...(messagesVisible ? {
                left: (leftSectionVisible || smartRecommendationsVisible) ? '370px' : 'calc(50% - 59px)',
                right: 'auto'
              } : {
                right: activityFeedVisible ? '420px' :
                       tagRunVisible ? '420px' :
                       groupsVisible ? '370px' :
                       (profileVisible || bugVisible) ? '370px' : '20px',
                left: 'auto'
              }),
              top: '20px',
              zIndex: messagesVisible ? 10010 : 1010
            }}
          >
            {profileVisible ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            )}
          </button>
        )}
        {/* Groups toggle button - below Profile button */}
        {!aboutVisible && walletAddress && (
          <button
            id="groups-toggle-btn"
            onClick={toggleGroups}
            className={groupsVisible ? 'profile-toggle-glow' : ''}
            style={{
              position: 'absolute',
              ...(messagesVisible ? {
                left: (leftSectionVisible || smartRecommendationsVisible) ? '370px' : 'calc(50% - 59px)',
                right: 'auto'
              } : {
                right: groupsVisible ? '370px' : 
                       tagRunVisible ? '420px' :
                       (profileVisible || activityFeedVisible || bugVisible) ? 
                       (activityFeedVisible ? '420px' : '370px') : '20px',
                left: 'auto'
              }),
              top: '74px',
              zIndex: messagesVisible ? 10010 : 1010
            }}
          >
            {groupsVisible ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            )}
          </button>
        )}
        {/* Messages toggle button - below Groups button */}
        {!aboutVisible && walletAddress && (
          <button
            id="messages-toggle-btn"
            onClick={toggleMessages}
            className={!messagesVisible && unreadMessagesCount > 0 ? 'profile-toggle-glow' : ''}
            style={{
              position: 'absolute',
              ...(messagesVisible ? {
                left: (leftSectionVisible || smartRecommendationsVisible) ? '370px' : 'calc(50% - 59px)',
                right: 'auto'
              } : {
                right: tagRunVisible ? '420px' :
                       groupsVisible ? '370px' :
                       (profileVisible || activityFeedVisible || bugVisible) ? 
                       (activityFeedVisible ? '420px' : '370px') : '20px',
                left: 'auto'
              }),
              top: '128px',
              zIndex: messagesVisible ? 10010 : 1010
            }}
          >
            <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {messagesVisible ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              )}
              {!messagesVisible && unreadMessagesCount > 0 && (
                <span 
                  id="messages-badge"
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#7777ff',
                    color: '#fff',
                    borderRadius: unreadMessagesCount > 9 ? '12px' : '50%',
                    minWidth: unreadMessagesCount > 9 ? '28px' : '22px',
                    width: unreadMessagesCount > 9 ? 'auto' : '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    padding: unreadMessagesCount > 9 ? '0 8px' : '0',
                    boxShadow: '0 3px 8px rgba(135,206,235,0.5), 0 0 0 2px #fff',
                    border: '2px solid #fff',
                    zIndex: 10001,
                    lineHeight: '1',
                    pointerEvents: 'none',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}
                >
                  {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                </span>
              )}
            </div>
          </button>
        )}
        {/* Activity Feed toggle button - below Messages button */}
        {!aboutVisible && walletAddress && (
          <button
            id="activity-feed-toggle-btn"
            onClick={toggleActivityFeed}
            className={activityFeedVisible ? 'profile-toggle-glow' : ''}
            style={{
              position: 'absolute',
              ...(messagesVisible ? {
                left: (leftSectionVisible || smartRecommendationsVisible) ? '370px' : 'calc(50% - 59px)',
                right: 'auto'
              } : {
                right: activityFeedVisible ? '420px' : 
                       tagRunVisible ? '420px' :
                       groupsVisible ? '370px' :
                       (profileVisible || bugVisible) ? '370px' : '20px',
                left: 'auto'
              }),
              top: '182px',
              zIndex: messagesVisible ? 10010 : 1010
            }}
            title="Activity Feed"
          >
            {activityFeedVisible ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
            )}
          </button>
        )}
        {/* Save and Reject buttons - appear when there are unsaved changes */}
        {!aboutVisible && walletAddress && hasUnsavedChanges && (
          <>
            <button
              id="save-profile-btn"
              onClick={commitPendingProfileChanges}
              className={`${
                hasUnsavedChanges ? 'profile-toggle-glow' : ''
              } ${isSavingProfile ? 'saving-active' : ''}`}
              disabled={isSavingProfile}
              style={{
                right: activityFeedVisible ? '420px' :
                       messagesVisible ? '370px' :
                       tagRunVisible ? '420px' :
                       (profileVisible || groupsVisible || bugVisible) ? '370px' : '20px',
                top: '286px',
                opacity: isSavingProfile ? 0.7 : 1
              }}
            >
              <Tooltip text={isSavingProfile ? 'Saving...' : 'Save all changes'}>
                <svg className={`save-disk-icon ${isSavingProfile ? 'pulse-icon' : ''}`} width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              </Tooltip>
            </button>
            <button
              id="reject-profile-btn"
              onClick={rejectPendingProfileChanges}
              className={hasUnsavedChanges ? 'reject-toggle-glow' : ''}
              style={{
                right: activityFeedVisible ? '420px' :
                       messagesVisible ? '370px' :
                       tagRunVisible ? '420px' :
                       (profileVisible || groupsVisible || bugVisible) ? '370px' : '20px',
                top: '340px'
              }}
            >
              <Tooltip text="Reject all changes">
                <svg className="reject-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </Tooltip>
            </button>
          </>
        )}
        {!aboutVisible && !(termsVisible || (walletAddress && showRegistrationForm && profileVisible && !currentProfile && !termsClosedByUser)) && (
          <button
            id="left-section-toggle"
            onClick={toggleLeftSection}
            className={`show-hide-btn ${!leftSectionVisible && searchQuery.trim() !== '' ? 'filter-active' : ''}`}
            style={{
              top: '20px',
              left: (leftSectionVisible || smartRecommendationsVisible) ? '370px' : '20px',
              zIndex: 1010,
              width: '44px',
              height: '44px',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              {leftSectionVisible ? (
                <path d="M19 12H5M12 19l-7-7 7-7" />
              ) : (
                <>
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </>
              )}
            </svg>
          </button>
        )}
        {/* Smart Recommendations toggle button - below Search button */}
        {!aboutVisible && walletAddress && currentUser && currentProfile && currentProfile.username === currentUser && !(termsVisible || (walletAddress && showRegistrationForm && profileVisible && !currentProfile && !termsClosedByUser)) && (
          <button
            id="smart-recommendations-toggle"
            onClick={toggleSmartRecommendations}
            className={`show-hide-btn ${smartRecommendationsVisible ? 'filter-active' : ''}`}
            style={{
              top: '74px',
              left: (leftSectionVisible || smartRecommendationsVisible) ? '370px' : '20px',
              zIndex: 1010,
              width: '44px',
              height: '44px',
            }}
          >
            <Tooltip text="Smart Recommendations">
              {smartRecommendationsVisible ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            ) : smartIconError ? (
              <span style={{ fontSize: '27px', lineHeight: '1', filter: 'grayscale(100%)' }}>🧠</span>
            ) : (
              <img 
                src="/smart-recommendations-icon.svg" 
                alt="Smart Recommendations" 
                style={{ 
                  width: '20px', 
                  height: '20px',
                  display: 'block',
                  filter: 'grayscale(100%)'
                }} 
                onError={() => setSmartIconError(true)}
              />
            )}
            </Tooltip>
          </button>
        )}
        {/* Smart Recommendations Section */}
        <div
          id="smart-recommendations-section"
          className={`${!smartRecommendationsVisible ? 'hidden' : ''} ${isSwitchingSections ? 'no-transition' : ''}`}
        >
          <div style={{ padding: '20px', height: '100%', overflowY: 'auto' }}>
            <h2 style={{ marginTop: '0', marginBottom: '20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>
              Smart Recommendations
            </h2>
            
            {/* Criteria Selector */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {['all', 'tags', 'music', 'location', 'age', 'bio', 'social', 'personal'].map(criteria => (
                <button
                  key={criteria}
                  onClick={() => {
                    setRecommendationCriteria(criteria);
                    loadRecommendations(criteria);
                  }}
                  disabled={isLoadingRecommendations}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: recommendationCriteria === criteria ? '#4a90e2' : 'white',
                    color: recommendationCriteria === criteria ? 'white' : '#333',
                    cursor: isLoadingRecommendations ? 'wait' : 'pointer',
                    textTransform: 'capitalize',
                    fontWeight: recommendationCriteria === criteria ? '600' : 'normal',
                    opacity: isLoadingRecommendations && recommendationCriteria === criteria ? 0.7 : 1
                  }}
                >
                  {isLoadingRecommendations && recommendationCriteria === criteria 
                    ? 'Loading...' 
                    : (criteria === 'all' ? 'All Factors' : criteria)}
                </button>
              ))}
            </div>

            {/* Load/Refresh Button */}
            <button
              onClick={() => loadRecommendations()}
              disabled={isLoadingRecommendations || allProfiles.length === 0}
              style={{
                padding: '8px 16px',
                background: isLoadingRecommendations || allProfiles.length === 0 ? '#ccc' : '#4a90e2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isLoadingRecommendations || allProfiles.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                width: '100%',
                marginBottom: '20px'
              }}
            >
              {isLoadingRecommendations ? 'Loading...' : allProfiles.length === 0 ? 'No profiles available' : 'Refresh Recommendations'}
            </button>

            {/* Recommendations List */}
            {isLoadingRecommendations ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                Loading recommendations...
              </div>
            ) : recommendations.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                {allProfiles.length === 0 
                  ? 'No profiles available for recommendations'
                  : 'No recommendations found. Try adjusting the criteria or add more information to your profile.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {recommendations.map((rec, index) => (
                  <div
                    key={rec.profile.username || index}
                    onClick={async () => {
                      // Load the recommended profile by wallet address (viewing other user's profile)
                      try {
                        let otherProfile = null;
                        if (rec.profile.walletAddress) {
                          // Load profile from Firestore/IPFS (null encryption key = viewing other user's profile)
                          otherProfile = await getUserProfileByWallet(rec.profile.walletAddress, null);
                        } else if (rec.profile.username) {
                          // Fallback: try to find wallet address from username
                          try {
                            const profilesRef = collection(db, 'profiles');
                            const q = query(profilesRef, where('username', '==', rec.profile.username));
                            const querySnapshot = await getDocs(q);
                            if (!querySnapshot.empty) {
                              const profileDoc = querySnapshot.docs[0];
                              const profileData = profileDoc.data();
                              if (profileData.walletAddress) {
                                otherProfile = await getUserProfileByWallet(profileData.walletAddress, null);
                              } else {
                                // Fallback: use profile data directly from Firestore
                                otherProfile = cleanProfileData({
                                  ...profileData,
                                  ipfsHash: profileData.ipfsHash || null
                                });
                              }
                            }
                          } catch (queryError) {
                            console.error('Error querying profile by username:', queryError);
                          }
                        }
                        
                        if (otherProfile) {
                          const cleanOther = cleanProfileData(otherProfile);
                          setCurrentUser(null); // null for other users
                          setCurrentProfile(cleanOther);
                          setProfileVisible(true);
                          setAboutVisible(false);
                          setShowRegistrationForm(false); // Ensure registration form is not shown
                          // Ensure other right-side panels are closed when opening profile
                          setGroupsVisible(false);
                          setMessagesVisible(false);
                          setBugVisible(false);
                          setAiAssistantVisible(false);
                          
                          // Track profile view for gamification
                          if (walletAddress) {
                            trackProfileViewed(walletAddress).catch(err => {
                              console.error('Error tracking profile view:', err);
                            });
                          }
                        } else {
                          // Fallback: use the profile data from the recommendation to display basic info
                          const fallbackOther = cleanProfileData(rec.profile);
                          setCurrentUser(null);
                          setCurrentProfile(fallbackOther);
                          setProfileVisible(true);
                          setAboutVisible(false);
                          setShowRegistrationForm(false);
                          setGroupsVisible(false);
                          setMessagesVisible(false);
                          setBugVisible(false);
                          setAiAssistantVisible(false);
                          
                          // Track profile view for gamification
                          if (walletAddress) {
                            trackProfileViewed(walletAddress).catch(err => {
                              console.error('Error tracking profile view:', err);
                            });
                          }
                        }
                      } catch (error) {
                        console.error('Error loading recommended profile:', error);
                        // Fallback to displaying the recommendation data so the panel still opens
                        const fallbackOther = cleanProfileData(rec.profile);
                        setCurrentUser(null);
                        setCurrentProfile(fallbackOther);
                        setProfileVisible(true);
                        setAboutVisible(false);
                        setShowRegistrationForm(false);
                        setGroupsVisible(false);
                        setMessagesVisible(false);
                        setBugVisible(false);
                        setAiAssistantVisible(false);
                        
                        // Track profile view for gamification
                        if (walletAddress) {
                          trackProfileViewed(walletAddress).catch(err => {
                            console.error('Error tracking profile view:', err);
                          });
                        }
                      }
                    }}
                    style={{
                      padding: '12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: '#fafafa'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                      e.currentTarget.style.borderColor = '#4a90e2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fafafa';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                          {rec.profile.name || rec.profile.username}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                          @{rec.profile.username}
                        </div>
                        <div style={{ fontSize: '11px', color: '#888', lineHeight: '1.4' }}>
                          {rec.explanation}
                        </div>
                      </div>
                      <div style={{
                        padding: '4px 10px',
                        background: rec.score >= 70 ? '#4caf50' : rec.score >= 50 ? '#ff9800' : '#ffc107',
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        minWidth: '50px',
                        textAlign: 'center'
                      }}>
                        {rec.score}%
                      </div>
                    </div>
                    {/* Score Breakdown */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '6px', 
                      flexWrap: 'wrap', 
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid #e8e8e8'
                    }}>
                      {rec.scores.tagScore > 0 && (
                        <span style={{ fontSize: '10px', color: '#666', background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                          Tags: {rec.scores.tagScore}%
                        </span>
                      )}
                      {rec.scores.musicScore > 0 && (
                        <span style={{ fontSize: '10px', color: '#666', background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                          Music: {rec.scores.musicScore}%
                        </span>
                      )}
                      {rec.scores.locationScore > 0 && (
                        <span style={{ fontSize: '10px', color: '#666', background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                          Location: {rec.scores.locationScore}%
                        </span>
                      )}
                      {rec.scores.ageScore > 0 && (
                        <span style={{ fontSize: '10px', color: '#666', background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                          Age: {rec.scores.ageScore}%
                        </span>
                      )}
                      {rec.scores.bioScore > 0 && (
                        <span style={{ fontSize: '10px', color: '#666', background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                          Bio: {rec.scores.bioScore}%
                        </span>
                      )}
                      {rec.scores.socialScore > 0 && (
                        <span style={{ fontSize: '10px', color: '#666', background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                          Social: {rec.scores.socialScore}%
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div
          id="left-section"
          className={`${!leftSectionVisible ? 'hidden' : ''} ${isSwitchingSections ? 'no-transition' : ''}`}
          style={{ top: '0px', position: 'absolute', height: 'calc(100%)' }}
        >
          <div className="search-container">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input 
                type="text" 
                className="search-input-field" 
                placeholder="Search your interests tags (* for all)" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button 
                className="clear-search"
                onClick={() => setSearchQuery('')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="search-hint">
              {searchType === 'groups' 
                ? '* = all; [tag1] + [tag2] + [tagN] = only groups with tags'
                : '* = all; [tag1] + [tag2] + [tagN] = only people with tags'}
            </div>
          </div>
          <div className="filter-container" style={{ flexWrap: 'nowrap', gap: '8px', alignItems: 'center' }}>
            <button 
              className={`filter-btn ${searchType === 'people' ? 'active' : ''}`}
              onClick={() => setSearchType('people')}
            >
              people
            </button>
            <button 
              className={`filter-btn ${searchType === 'groups' ? 'active' : ''}`}
              onClick={() => setSearchType('groups')}
            >
              groups
            </button>
            <button 
              className="filter-btn" 
              disabled
              style={{ 
                opacity: 0.5, 
                cursor: 'not-allowed',
                backgroundColor: '#e0e0e0',
                color: '#999'
              }}
            >
              services
            </button>
            {/* Sort */}
            <div ref={sortButtonRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '5px 8px', borderRadius: '6px', backgroundColor: showSortDropdown ? '#f5f5f5' : 'white', border: '1px solid #e0e0e0', flexShrink: 0 }} onClick={() => setShowSortDropdown(!showSortDropdown)}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18M7 12h10M11 18h2"></path>
              </svg>
              <span style={{ fontSize: '11px', fontWeight: '500', color: '#333' }}>sort</span>
              {showSortDropdown && (
                <div ref={sortDropdownRef} style={{
                  position: 'fixed',
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 10000,
                  minWidth: '120px',
                  padding: '4px 0'
                }}>
                  {['match', 'distance', 'city', 'name', 'activity'].map((type) => (
                    <div
                      key={type}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSortType(type);
                        setShowSortDropdown(false);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: sortType === type ? '#f0f0f0' : 'transparent',
                        fontSize: '13px',
                        textTransform: 'capitalize'
                      }}
                      onMouseEnter={(e) => {
                        if (sortType !== type) {
                          e.currentTarget.style.backgroundColor = '#f9f9f9';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (sortType !== type) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {type}
                    </div>
                  ))}
                  {/* Sort menu ends here - no extra filter toggles */}
                </div>
              )}
            </div>
            {/* Filter */}
            <div
              ref={filterButtonRef}
              style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '5px 8px', borderRadius: '6px', backgroundColor: showFilterDropdown ? '#f5f5f5' : 'white', border: '1px solid #e0e0e0', opacity: 1, flexShrink: 0 }}
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
              <span style={{ fontSize: '11px', fontWeight: '500', color: '#333' }}>filter</span>
              {showFilterDropdown && (
                <div
                  ref={filterDropdownRef}
                  style={{
                    position: 'fixed',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 10000,
                    minWidth: '120px',
                    padding: '4px 0'
                  }}
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setExcludeTestUsers(prev => !prev);
                    }}
                    style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.dataset.wasBg = e.currentTarget.style.backgroundColor;
                      if (!excludeTestUsers) {
                        e.currentTarget.style.backgroundColor = '#f9f9f9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = excludeTestUsers ? '#e6f7ff' : 'transparent';
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '3px',
                        border: '1px solid #cfcfcf',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: excludeTestUsers ? '#e6f7ff' : 'white'
                      }}
                    >
                      {excludeTestUsers ? (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1890ff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      ) : null}
                    </span>
                    <span style={{ fontSize: '13px', color: '#333', userSelect: 'none' }}>No test users</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="list-container">
            {!searchQuery || searchQuery.trim() === '' ? (
              // When search is empty, show nothing
              null
            ) : isSearching ? (
              <div className="list-item" style={{ padding: '10px', color: '#666' }}>
                Loading...
              </div>
            ) : searchType === 'groups' ? (
              // Show groups results
              filteredGroups.length === 0 ? (
                <div className="list-item" style={{ padding: '10px', color: '#999' }}>
                  No groups found
                </div>
              ) : (
                <>
                  <div className="list-item" style={{ padding: '5px', backgroundColor: '#ffffff', borderBottom: '1px solid #e0e0e0', marginTop: '5px', marginLeft: '15px' }}>
                    <span style={{ fontWeight: 'normal', color: '#333', minWidth: 'auto', maxWidth: 'none', fontSize: '12px' }}>Total <strong>{filteredGroups.length}</strong> {filteredGroups.length === 1 ? 'group' : 'groups'} found</span>
                  </div>
                  {filteredGroups.map((group, groupIndex) => (
                    <div 
                      key={`group-${groupIndex}`}
                      className="list-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        // TODO: Navigate to group or show group details
                        console.log('Group clicked:', group);
                      }}
                      style={{
                        padding: '12px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #e0e0e0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600', color: '#333', fontSize: '14px' }}>{group.name || group.groupId}</span>
                        {group.memberCount && (
                          <span style={{ fontSize: '12px', color: '#666' }}>{group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}</span>
                        )}
                      </div>
                      {group.description && (
                        <p style={{ fontSize: '12px', color: '#666', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {group.description}
                        </p>
                      )}
                      {group.tags && group.tags.length > 0 && (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                          {group.tags.slice(0, 3).map((tag, tagIdx) => (
                            <span key={tagIdx} style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: '#f0f0f0', borderRadius: '4px', color: '#666' }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )
            ) : sortedProfiles.length === 0 ? (
              <div className="list-item" style={{ padding: '10px', color: '#999' }}>
                No profiles found
              </div>
            ) : (
              <>
                <div className="list-item" style={{ padding: '5px', backgroundColor: '#ffffff', borderBottom: '1px solid #e0e0e0', marginTop: '5px', marginLeft: '15px' }}>
                  <span style={{ fontWeight: 'normal', color: '#333', minWidth: 'auto', maxWidth: 'none', fontSize: '12px' }}>Total <strong>{sortedProfiles.length}</strong> {sortedProfiles.length === 1 ? 'record' : 'records'} found</span>
                </div>
                {sortedProfiles.map((profile, profileIndex) => {
                  // Use pre-computed display values from sortedProfiles
                  const displayName = profile.displayName || (profile.name ? profile.name : profile.username);
                  const displayCity = profile.displayCity || (profile.city ? truncateCityName(profile.city, 15) : null);
                  // Only show distance if wallet is connected (displayDistance is already set to null when no wallet)
                  const displayDistance = profile.displayDistance || (walletAddress && profile.distance != null ? `${profile.distance.toFixed(0)} km` : null);
                  const matchPercentage = profile.matchPercentage != null ? profile.matchPercentage : 0;
                  
                  // Pre-compute match color and opacity (memoized functions)
                  const matchColor = matchPercentage === 0 ? '#999' : 
                                    matchPercentage >= 50 ? '#4caf50' : 
                                    matchPercentage >= 25 ? '#ff9800' : 
                                    matchPercentage >= 12 ? '#ffc107' : '#ff9800';
                  const opacity = matchPercentage === 0 ? 0.5 : 1;
                  
                  return (
                    <div 
                      key={`profile-${profileIndex}`}
                      className="list-item"
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Check current walletAddress - already up-to-date in React event handler
                        if (walletAddress) {
                          // Check if this is the current user's profile
                          // Compare wallet addresses (normalize to lowercase for comparison)
                          const profileWallet = profile.walletAddress ? profile.walletAddress.toLowerCase() : null;
                          const currentWallet = walletAddress.toLowerCase();
                          const isCurrentUserProfile = profileWallet && profileWallet === currentWallet;
                          
                          if (isCurrentUserProfile) {
                            // This is the current user's profile - load full data from IPFS with published states
                            try {
                              console.log('🔄 Loading fresh profile data from IPFS for current user...');
                              const currentEncryptionKey = encryptionKeyRef.current || encryptionKey;
                              
                              // Get Firestore metadata first (contains only published elements) - with retry to prevent race condition
                              const firestoreMetadata = await fetchFirestoreMetadataWithRetry(walletAddress);
                              
                              // Get full profile from IPFS
                              const freshProfile = await getUserProfileByWallet(walletAddress, currentEncryptionKey);
                              
                              if (freshProfile) {
                                console.log('✅ Loaded fresh profile from IPFS');
                                // IPFS hash already comes from blockchain via getUserProfileByWallet
                                const profileWithHash = {
                                  ...freshProfile,
                                  ipfsHash: freshProfile.ipfsHash || null
                                };
                                const cleanReloaded = cleanProfileData(profileWithHash);
                                
                                setCurrentUser(cleanReloaded.username);
                                setCurrentProfile(cleanReloaded);
                                // Navigate to profile URL
                                if (cleanReloaded.username) {
                                  navigate(`/profile/${cleanReloaded.username}`);
                                } else {
                                  navigate('/myprofile');
                                }
                                setProfileVisible(true);
                                setAboutVisible(false);
                                // Ensure other right-side panels are closed when opening profile
                                setGroupsVisible(false);
                                setMessagesVisible(false);
                                setBugVisible(false);
                                setAiAssistantVisible(false);
                                
                                // Determine published state by comparing Firestore (only published) with IPFS (all elements)
                                const firestoreTags = firestoreMetadata?.tagsList || [];
                                const firestoreSocials = firestoreMetadata?.socialLinks || [];
                                const firestoreMusic = firestoreMetadata?.musicList || [];
                                const firestoreBirthDate = firestoreMetadata?.birthDate || null;
                                const firestoreCity = firestoreMetadata?.city || null;
                                
                                // Tags: published if in Firestore
                                const publishedTagsList = cleanReloaded.tagsList.filter(tag => firestoreTags.includes(tag));
                                setPublishedTags(publishedTagsList);
                                
                                // Socials: published if in Firestore
                                const publishedSocialsList = cleanReloaded.socialLinks.filter(social => firestoreSocials.includes(social));
                                setPublishedSocials(publishedSocialsList);
                                
                                // Music: published if in Firestore
                                const publishedMusicList = (cleanReloaded.musicList || []).filter(song => firestoreMusic.includes(song));
                                setPublishedMusic(publishedMusicList);
                                
                                // BirthDate: published if in Firestore
                                setPublishedBirthDate(!!firestoreBirthDate && firestoreBirthDate === cleanReloaded.birthDate);
                                
                                // City: published if in Firestore
                                setPublishedCity(!!firestoreCity && firestoreCity === cleanReloaded.city);
                                
                                // Wallet: use _isWalletPublishedInFirestore flag (walletAddress always has fallback)
                                setPublishedWallet(firestoreMetadata?._isWalletPublishedInFirestore === true);
                              } else {
                                // If IPFS load failed, try to reload from Firestore metadata as fallback
                                console.warn('⚠️ Failed to load profile from IPFS, using Firestore metadata as fallback');
                                if (firestoreMetadata) {
                                  // Get IPFS hash from blockchain for fallback (Arc (Circle) network)
                                  const fallbackIpfsHash = await getCIDFromArc(walletAddress);
                                  const fallbackProfile = cleanProfileData({
                                    ...firestoreMetadata,
                                    ipfsHash: fallbackIpfsHash || null
                                  });
                                  setCurrentUser(fallbackProfile.username);
                                  setCurrentProfile(fallbackProfile);
                                  // Navigate to profile URL
                                  if (fallbackProfile.username) {
                                    navigate(`/profile/${fallbackProfile.username}`);
                                  } else {
                                    navigate('/myprofile');
                                  }
                                  setProfileVisible(true);
                                  setAboutVisible(false);
                                  // Ensure other right-side panels are closed when opening profile
                                  setGroupsVisible(false);
                                  setMessagesVisible(false);
                                  setBugVisible(false);
                                  setAiAssistantVisible(false);
                                  // Reset published states since we don't have full IPFS data
                                  setPublishedTags([]);
                                  setPublishedSocials([]);
                                  setPublishedBirthDate(false);
                                  setPublishedCity(false);
                                  setPublishedWallet(false);
                                } else {
                                  console.error('❌ No profile data available from IPFS or Firestore');
                                }
                              }
                            } catch (error) {
                              console.error('❌ Error loading fresh profile from IPFS:', error);
                              // Try to reload from Firestore as last resort
                              try {
                                const fallbackMetadata = await getUserProfileByWallet(walletAddress, null);
                                if (fallbackMetadata) {
                                  const fallbackProfile = cleanProfileData({
                                    ...fallbackMetadata,
                                    ipfsHash: fallbackMetadata.ipfsHash || null
                                  });
                                  setCurrentUser(fallbackProfile.username);
                                  setCurrentProfile(fallbackProfile);
                                  // Navigate to profile URL
                                  if (fallbackProfile.username) {
                                    navigate(`/profile/${fallbackProfile.username}`);
                                  } else {
                                    navigate('/myprofile');
                                  }
                                  setProfileVisible(true);
                                  setAboutVisible(false);
                                  // Ensure other right-side panels are closed when opening profile
                                  setGroupsVisible(false);
                                  setMessagesVisible(false);
                                  setBugVisible(false);
                                  setAiAssistantVisible(false);
                                  // Reset published states since we don't have full IPFS data
                                  setPublishedTags([]);
                                  setPublishedSocials([]);
                                  setPublishedBirthDate(false);
                                  setPublishedCity(false);
                                  setPublishedWallet(false);
                                }
                              } catch (fallbackError) {
                                console.error('❌ Failed to load fallback profile:', fallbackError);
                              }
                            }
                          } else {
                            // Other user's profile - load full profile data using loadUserProfile
                            // This ensures IPFS data is loaded properly for all users including test users
                            try {
                              // Get wallet address for loading profile
                              let profileWallet = profile.walletAddress;
                              
                              // If no wallet address, try to look it up from username
                              if (!profileWallet && profile.username) {
                                try {
                                  profileWallet = await getWalletFromUsername(profile.username);
                                } catch (error) {
                                  console.warn('⚠️ [Search Click] Could not get wallet address:', error);
                                }
                              }
                              
                              // Navigate to profile URL immediately for instant feedback
                              if (profile.username) {
                                navigate(`/profile/${profile.username}`);
                              }
                              
                              // Close activity feed FIRST to prevent overlap during loading
                              setActivityFeedVisible(false);
                              setAboutVisible(false);
                              // Ensure other right-side panels are closed when opening profile
                              setGroupsVisible(false);
                              setMessagesVisible(false);
                              setBugVisible(false);
                              setAiAssistantVisible(false);
                              
                              // For other users' profiles, don't show big loading screen
                              // loadUserProfile will show profile from Firestore immediately without loading screen
                              setIsLoadingProfileFromIPFS(false); // Don't show loading screen for other users
                              setProfileVisible(true);
                              
                              // Load full profile data using loadUserProfile (handles IPFS loading properly)
                              if (profileWallet) {
                                console.log('🔄 [Search Click] Loading full profile data for:', profileWallet);
                                try {
                                  await loadUserProfile(profileWallet, true); // skipUnifiedAuth=true
                                } catch (error) {
                                  console.error('❌ [Search Click] Error loading profile:', error);
                                  // On error, show basic profile data
                                  const fallbackOther = cleanProfileData(profile);
                                  setCurrentUser(null);
                                  setCurrentProfile(fallbackOther);
                                  setIsLoadingProfileFromIPFS(false);
                                }
                              } else {
                                // Fallback: show basic profile data if no wallet address
                                console.warn('⚠️ [Search Click] No wallet address, showing basic profile data');
                                const fallbackOther = cleanProfileData(profile);
                                setCurrentUser(null);
                                setCurrentProfile(fallbackOther);
                                setIsLoadingProfileFromIPFS(false);
                              }
                              
                              // Track profile view for gamification
                              if (walletAddress) {
                                trackProfileViewed(walletAddress).catch(err => {
                                  console.error('Error tracking profile view:', err);
                                });
                              }
                            } catch (error) {
                              console.error('Error loading other profile:', error);
                              // Fallback to displaying the list item data so the panel still opens
                              const fallbackOther = cleanProfileData(profile);
                              // Close activity feed FIRST to prevent overlap during loading
                              setActivityFeedVisible(false);
                              setCurrentUser(null);
                              setCurrentProfile(fallbackOther);
                              // Navigate to profile URL if username is available
                              if (fallbackOther.username) {
                                navigate(`/profile/${fallbackOther.username}`);
                              }
                              setAboutVisible(false);
                              setGroupsVisible(false);
                              setMessagesVisible(false);
                              setBugVisible(false);
                              setAiAssistantVisible(false);
                              // Show profile AFTER closing activity feed
                              setProfileVisible(true);
                            }
                          }
                        } else {
                          // Allow opening profile from search results even when wallet is not connected.
                          const fallbackOther = cleanProfileData(profile);
                          // Close activity feed FIRST to prevent overlap during loading
                          setActivityFeedVisible(false);
                          setCurrentUser(null);
                          setCurrentProfile(fallbackOther);
                          if (fallbackOther.username) {
                            navigate(`/profile/${fallbackOther.username}`);
                          }
                          setAboutVisible(false);
                          setGroupsVisible(false);
                          setMessagesVisible(false);
                          setBugVisible(false);
                          setAiAssistantVisible(false);
                          // Show profile AFTER closing activity feed
                          setProfileVisible(true);
                          setNotification({ message: 'Connect wallet to message, leave reviews, or report users.', type: 'info' });
                          setTimeout(() => setNotification(null), 4000);
                        }
                        
                        // Pan to location if available
                        if (profile.marker && mapInstanceRef.current && profile.location && profile.location.lat != null && profile.location.lng != null) {
                          // Pan to location without changing zoom level
                          mapInstanceRef.current.panTo([profile.location.lat, profile.location.lng]);
                          profile.marker.openPopup();
                        }
                      }}
                    >
                      <span className="hashtag">{displayName}</span>
                      {displayCity ? (
                        <span className="count">{displayDistance ? `${displayDistance}, ` : ''}{displayCity}</span>
                      ) : (
                        <span className="count">(no location)</span>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                          opacity: opacity
                        }}
                      >
                        <Tooltip text={`${matchPercentage.toFixed(0)}% match`}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {/* Battery-like indicator */}
                            <div
                              style={{
                                position: 'relative',
                                width: '21px',
                                height: '14px',
                                border: '1.5px solid #333',
                                borderRadius: '2px',
                                backgroundColor: '#f5f5f5',
                                overflow: 'hidden',
                                boxShadow: matchPercentage >= 50 ? `0 0 8px ${matchColor}80, 0 0 12px ${matchColor}40` : 'none',
                                transition: 'box-shadow 0.3s ease'
                              }}
                            >
                              {/* Battery fill */}
                              <div
                                style={{
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  height: '100%',
                                  width: `${Math.min(matchPercentage, 100)}%`,
                                  backgroundColor: matchColor,
                                  transition: 'width 0.3s ease, background-color 0.3s ease'
                                }}
                              />
                              {/* Percentage text */}
                              <span
                                style={{
                                  position: 'absolute',
                                  left: '50%',
                                  top: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  fontSize: '7px',
                                  fontWeight: '700',
                                  color: matchPercentage > 50 ? '#fff' : (matchPercentage === 0 ? '#999' : '#333'),
                                  zIndex: 1,
                                  lineHeight: 1,
                                  textShadow: matchPercentage > 50 ? '0 0 2px rgba(0,0,0,0.3)' : 'none'
                                }}
                              >
                                {matchPercentage.toFixed(0)}%
                              </span>
                            </div>
                            {/* Battery terminal */}
                            <div
                              style={{
                                width: '2px',
                                height: '8px',
                                backgroundColor: '#333',
                                borderRadius: '0 1px 1px 0',
                                marginLeft: '1px',
                                opacity: matchPercentage === 0 ? 0.5 : 1
                              }}
                            />
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
        <div className="map-controls" style={{ 
          right: messagesVisible ? 'calc(50% + 20px)' : 
                 (activityFeedVisible || tagRunVisible) ? '420px' :
                 (profileVisible || groupsVisible || bugVisible) ? '370px' : '20px', 
          bottom: '30px' 
        }}>
          {!aboutVisible && walletAddress && (
            <button
              id="bug-toggle-btn"
              className={`location-button ${bugVisible ? 'profile-toggle-glow' : ''}`}
              onClick={toggleBug}
            >
              <Tooltip text="Report Bug">
                {bugVisible ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                ) : (
                  <span style={{ fontSize: '27px', lineHeight: '1', display: 'inline-block', transform: 'rotate(45deg)' }}>𖢥</span>
                )}
              </Tooltip>
            </button>
          )}
          <button className="location-button" onClick={handleGetLocation} title="Get My Location">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="8"></circle>
              <circle cx="12" cy="12" r="3"></circle>
              <line x1="12" y1="2" x2="12" y2="6"></line>
              <line x1="12" y1="18" x2="12" y2="22"></line>
              <line x1="2" y1="12" x2="6" y2="12"></line>
              <line x1="18" y1="12" x2="22" y2="12"></line>
            </svg>
          </button>
          <div className="zoom-controls">
            <button className="zoom-button" onClick={() => mapInstanceRef.current?.zoomIn()} title="Zoom In">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
            <button className="zoom-button" onClick={() => mapInstanceRef.current?.zoomOut()} title="Zoom Out">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
        {/* Show profile section: allow viewing other profiles without wallet; registration/loading require wallet */}
        {((profileVisible || normalizedPathname.startsWith('/profile/') || (isMobile && (normalizedPathname === '/myprofile' || normalizedPathname.startsWith('/profile/')))) && (currentProfile || (walletAddress && (showRegistrationForm || isLoadingUser || isLoadingProfileFromIPFS)) || (isLoadingProfileFromIPFS && profileVisible) || normalizedPathname.startsWith('/profile/') || (isMobile && normalizedPathname === '/myprofile'))) && (
          <div 
            id="profile-section" 
            className={((currentProfile || (walletAddress && (showRegistrationForm || isLoadingUser || isLoadingProfileFromIPFS)) || (isLoadingProfileFromIPFS && profileVisible) || normalizedPathname.startsWith('/profile/') || (isMobile && normalizedPathname === '/myprofile')) && (profileVisible || normalizedPathname.startsWith('/profile/') || (isMobile && normalizedPathname === '/myprofile'))) ? '' : 'hidden'}
      style={{
              display: ((currentProfile || (walletAddress && (showRegistrationForm || isLoadingUser || isLoadingProfileFromIPFS)) || (isLoadingProfileFromIPFS && profileVisible) || normalizedPathname.startsWith('/profile/') || (isMobile && normalizedPathname === '/myprofile')) && (profileVisible || normalizedPathname.startsWith('/profile/') || (isMobile && normalizedPathname === '/myprofile'))) ? 'block' : 'none',
              position: 'fixed',
              zIndex: 10000,
              right: ((termsVisible || (walletAddress && showRegistrationForm && profileVisible && !currentProfile)) && !aboutVisible) ? '0px' : '0px',
              top: '0px',
              height: '100vh',
              width: (walletAddress && showRegistrationForm && profileVisible && !currentProfile) ? '700px' : ((normalizedPathname === '/myprofile' || isLoadingProfileFromIPFS) ? '350px' : '350px'),
              overflow: ((isLoadingUser && walletAddress) || (isLoadingProfileFromIPFS && (currentProfile?.username === currentUser || !currentProfile))) ? 'hidden' : 'auto'
            }}
          >
            {/* Show loading, registration form, or profile */}
            {isLoadingUser && walletAddress ? (
              profileLoadError ? (
                /* Error State - Profile Load Failed */
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  minHeight: '400px',
                  padding: '40px',
                  fontSize: '16px',
                  color: '#666',
                  backgroundColor: '#ffffff',
                  position: 'relative'
                }}>
                  <div style={{
                  fontSize: '24px',
                  marginBottom: '20px',
                  color: '#e74c3c'
                }}>
                  ⚠️
                </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '20px',
                    color: '#333'
                  }}>
                    Failed to Load Profile
                  </div>
                  <div style={{
                    marginBottom: '30px',
                    fontSize: '14px',
                    color: '#666',
                    textAlign: 'center',
                    maxWidth: '400px',
                    lineHeight: '1.6'
                  }}>
                    {profileLoadError}
                  </div>
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#fff',
                      backgroundColor: '#4a90e2',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#357abd'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#4a90e2'}
                  >
                    Refresh Page
                  </button>
                </div>
              ) : (
                /* Loading State - Wallet Connection */
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  minHeight: '400px',
                  padding: '40px',
                  fontSize: '16px',
                  color: '#666',
                  backgroundColor: '#ffffff',
                  position: 'relative'
                }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '20px',
                    color: '#333'
                  }}>
                    Loading Your Profile...
                  </div>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #4a90e2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <div style={{
                    marginTop: '20px',
                    fontSize: '14px',
                    color: '#999'
                  }}>
                    Loading your profile data from IPFS...
                  </div>
                </div>
              )
            ) : isLoadingProfileFromIPFS ? (
              profileLoadError ? (
                /* Error State - Profile Load Failed */
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  minHeight: '400px',
                  padding: '40px',
                  fontSize: '16px',
                  color: '#666',
                  backgroundColor: '#ffffff',
                  position: 'relative'
                }}>
                  <div style={{
                    fontSize: '24px',
                    marginBottom: '20px',
                    color: '#e74c3c'
                  }}>
                    ⚠️
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '20px',
                    color: '#333'
                  }}>
                    Failed to Load Profile
                  </div>
                  <div style={{
                    marginBottom: '30px',
                    fontSize: '14px',
                    color: '#666',
                    textAlign: 'center',
                    maxWidth: '400px',
                    lineHeight: '1.6'
                  }}>
                    {profileLoadError}
                  </div>
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#fff',
                      backgroundColor: '#4a90e2',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#357abd'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#4a90e2'}
                  >
                    Refresh Page
                  </button>
                </div>
              ) : (
                /* Loading State when loading profile */
                <div className="profile-loading-wrapper" style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  minHeight: '400px',
                  padding: '40px',
                  fontSize: '16px',
                  color: '#666',
                  backgroundColor: '#ffffff',
                  position: 'relative'
                }}>
                  <div className="profile-cozy" style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    minHeight: '400px'
                  }}>
                    <CozyConnections />
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '20px',
                    color: '#333',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    {currentUser ? 'Loading Your Profile...' : 'Loading Profile...'}
                  </div>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #4a90e2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    position: 'relative',
                    zIndex: 1
                  }}></div>
                  <div style={{
                    marginTop: '20px',
                    fontSize: '14px',
                    color: '#999',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    {currentUser ? 'Loading your profile data from IPFS...' : 'Loading profile data...'}
                  </div>
                </div>
              )
            ) : (normalizedPathname.startsWith('/profile/') && !currentProfile && isLoadingProfileFromIPFS && currentUser) ? (
              /* Loading State when loading current user's profile from URL */
              <div className="profile-loading-wrapper" style={{ 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center',
                width: '100%',
                height: '100%',
                minHeight: '400px',
                padding: '40px',
                fontSize: '16px',
                color: '#666',
                backgroundColor: '#ffffff',
                position: 'relative'
              }}>
                <div className="profile-cozy">
                  <CozyConnections />
                </div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  color: '#333'
                }}>
                  Loading Profile...
                </div>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #4a90e2',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <div style={{
                  marginTop: '20px',
                  fontSize: '14px',
                  color: '#999'
                }}>
                  Loading profile data...
                </div>
              </div>
            ) : (profileLoadError && !currentProfile && walletAddress) ? (
              /* Error State - Profile Load Failed (shown when error exists even without loading state) */
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center',
                width: '100%',
                height: '100%',
                minHeight: '400px',
                padding: '40px',
                fontSize: '16px',
                color: '#666',
                backgroundColor: '#ffffff',
                position: 'relative'
              }}>
                <div style={{
                  fontSize: '24px',
                  marginBottom: '20px',
                  color: '#e74c3c'
                }}>
                  ⚠️
                </div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  color: '#333'
                }}>
                  Failed to Load Profile
                </div>
                <div style={{
                  marginBottom: '30px',
                  fontSize: '14px',
                  color: '#666',
                  textAlign: 'center',
                  maxWidth: '400px',
                  lineHeight: '1.6'
                }}>
                  {profileLoadError}
                </div>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#4a90e2',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#357abd'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#4a90e2'}
                >
                  Refresh Page
                </button>
              </div>
            ) : (!currentProfile && walletAddress && !profileLoadError && !isLoadingProfileFromIPFS && !normalizedPathname.startsWith('/profile/')) ? (
              isSavingProfile ? (
                /* Saving State - Registration Submit */
                <div className="profile-loading-wrapper" style={{ 
                  position: 'absolute',
                  top: '-20px',
                  left: '-20px',
                  right: '-20px',
                  bottom: '-20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center',
                  padding: '60px',
                  fontSize: '16px',
                  color: '#666',
                  backgroundColor: '#ffffff',
                  width: 'calc(100% + 40px)',
                  height: 'calc(100% + 40px)'
                }}>
                  <div className="profile-cozy">
                    <CozyConnections />
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '20px',
                    color: '#333'
                  }}>
                    Saving Your Profile...
                  </div>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #4a90e2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <div style={{
                    marginTop: '20px',
                    fontSize: '14px',
                    color: '#999',
                    textAlign: 'center',
                    lineHeight: 1.5
                  }}>
                    Encrypting and uploading to IPFS...<br/>
                    Saving CID on Arc (Circle) blockchain...
                  </div>
                </div>
              ) : (
              /* Registration Form */
              <div className="registration-form-wrapper" style={{ position: 'relative', width: '100%', height: '100%', minHeight: '400px' }}>
                <div className="profile-cozy">
                  <CozyConnections alwaysShow={true} />
                </div>
              <div className="registration-form">
                <div className="profile-header">
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <h2 style={{ margin: '0 0 12px 0', fontSize: '28px', fontWeight: '600' }}>Create Your Profile</h2>
                    <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>Fill in your information to get started</p>
                    <p style={{ margin: '0 0 24px 0', color: '#666', fontSize: '13px' }}>* Small fee in ETH on Arc (Circle) network is required to save identificator on blockchain (usually ~0.01$)</p>
                  </div>
                </div>
                
                <form onSubmit={handleRegistrationSubmit}>
                  {/* Two-column layout for basic info */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                    <label htmlFor="reg-name">Name *</label>
                    <input
                      type="text"
                      id="reg-name"
                      value={registrationData.name}
                      onChange={(e) => setRegistrationData({ ...registrationData, name: e.target.value })}
                      required
                      placeholder="Your full name"
                    />
                  </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                    <label htmlFor="reg-email">Email *</label>
                    <input
                      type="email"
                      id="reg-email"
                      value={registrationData.email}
                      onChange={(e) => setRegistrationData({ ...registrationData, email: e.target.value })}
                      required
                      placeholder="your.email@example.com"
                    />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="reg-username">Username (Login) *</label>
                    <input
                      type="text"
                      id="reg-username"
                      value={registrationData.username}
                      onChange={(e) => {
                        const cleanedValue = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 22);
                        setRegistrationData({ ...registrationData, username: cleanedValue });
                      }}
                      required
                      placeholder="username"
                      pattern="[a-zA-Z0-9_]+"
                      maxLength={22}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>Only letters, numbers, and underscores (max 22 characters)</small>
                  </div>

                  <div className="form-group">
                    <label>Tags</label>
                    {registrationData.tags.map((tag, index) => (
                      <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <input
                          ref={(el) => {
                            if (el) tagInputRefs.current[index] = el;
                          }}
                          type="text"
                          value={tag}
                          onChange={(e) => {
                            const newTags = [...registrationData.tags];
                            newTags[index] = e.target.value;
                            setRegistrationData({ ...registrationData, tags: newTags });
                          }}
                          placeholder="Enter a tag"
                          style={{ flex: 1 }}
                        />
                        {index === registrationData.tags.length - 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newIndex = registrationData.tags.length;
                              setRegistrationData({ ...registrationData, tags: [...registrationData.tags, ''] });
                              // Focus the new input after state update
                              setTimeout(() => {
                                if (tagInputRefs.current[newIndex]) {
                                  tagInputRefs.current[newIndex].focus();
                                }
                              }, 0);
                            }}
                            style={{
                              padding: '8px 16px',
                              background: '#4a90e2',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }}
                          >
                            +
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Popular Tags Section */}
                  <div className="form-group">
                    <label style={{ marginBottom: '12px' }}>Popular tags (click to add)</label>
                    <div className="popular-tags-container">
                      {isLoadingPopularTags ? (
                        <div className="popular-tags-loading">
                          <div className="popular-tags-spinner"></div>
                          <span style={{ marginLeft: '8px', color: '#666', fontSize: '14px' }}>Loading...</span>
                        </div>
                      ) : popularTags.length > 0 ? (
                        <div className="popular-tags-list">
                          {popularTags.map(({ tag, count }, index) => {
                            // Compare tags without # prefix
                            const tagWithoutHash = tag.trim().replace(/^#+/, '').toLowerCase();
                            const existingTags = registrationData.tags.map(t => {
                              const normalized = t.trim().replace(/^#+/, '').toLowerCase();
                              return normalized;
                            }).filter(Boolean);
                            const isSelected = existingTags.includes(tagWithoutHash);
                            
                            return (
                              <button
                                key={index}
                                type="button"
                                onClick={() => handlePopularTagClick(tag)}
                                className={`popular-tag-button ${isSelected ? 'selected' : ''}`}
                              >
                                <Tooltip text={isSelected ? `Click to remove (${count} people have this tag)` : `Click to add (${count} people have this tag)`}>
                                  <span className="popular-tag-text">{tag}</span>
                                </Tooltip>
                                <span className="popular-tag-count">({count})</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Social Links</label>
                    {registrationData.socials.map((social, index) => (
                      <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <input
                          ref={(el) => {
                            if (el) socialInputRefs.current[index] = el;
                          }}
                          type="text"
                          value={social}
                          onChange={(e) => {
                            const newSocials = [...registrationData.socials];
                            newSocials[index] = e.target.value;
                            setRegistrationData({ ...registrationData, socials: newSocials });
                          }}
                          placeholder="Enter a social link"
                          style={{ flex: 1 }}
                        />
                        {index === registrationData.socials.length - 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newIndex = registrationData.socials.length;
                              setRegistrationData({ ...registrationData, socials: [...registrationData.socials, ''] });
                              // Focus the new input after state update
                              setTimeout(() => {
                                if (socialInputRefs.current[newIndex]) {
                                  socialInputRefs.current[newIndex].focus();
                                }
                              }, 0);
                            }}
                            style={{
                              padding: '8px 16px',
                              background: '#4a90e2',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }}
                          >
                            +
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="form-group">
                    <label>Music</label>
                    {/* Music Search */}
                    <div style={{ marginBottom: '16px', position: 'relative' }}>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          value={musicSearchQuery}
                          onChange={(e) => setMusicSearchQuery(e.target.value)}
                          placeholder="🎵 Search songs, artists, albums..."
                          style={{
                            width: '100%',
                            padding: '12px 40px 12px 16px',
                            border: '2px solid #e0e0e0',
                            borderRadius: '10px',
                            fontSize: '15px',
                            fontFamily: 'inherit',
                            outline: 'none',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            boxSizing: 'border-box'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#4a90e2';
                            e.target.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        {/* Search/Loading indicator */}
                        <div style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#999',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          {isSearchingMusic ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            </svg>
                          ) : musicSearchQuery ? (
                            <span 
                              onClick={() => {
                                setMusicSearchQuery('');
                                setMusicSearchResults([]);
                              }}
                              style={{ cursor: 'pointer', fontSize: '18px', padding: '2px' }}
                              title="Clear search"
                            >
                              ✕
                            </span>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="11" cy="11" r="8" />
                              <path d="M21 21l-4.35-4.35" />
                            </svg>
                          )}
                        </div>
                      </div>
                      {musicSearchQuery && musicSearchQuery.length < 2 && (
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px', paddingLeft: '4px' }}>
                          Type at least 2 characters to search...
                        </div>
                      )}
                      
                      {/* Search Results */}
                      {musicSearchResults.length > 0 && (
                        <div style={{
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          backgroundColor: '#fafafa',
                          marginBottom: '8px',
                          marginTop: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}>
                          {musicSearchResults.map((result, idx) => (
                            <div
                              key={idx}
                              onClick={() => {
                                // For custom entries, use just the title (remove "- Add as custom entry")
                                let songString;
                                if (result.isManual) {
                                  songString = result.title.trim();
                                } else {
                                  songString = `${result.title} - ${result.artist}`;
                                }
                                const existingMusic = registrationData.music.filter(s => s.trim().length > 0);
                                if (!existingMusic.includes(songString)) {
                                  // Just add to the array (no empty strings to replace since we start with empty array)
                                  setRegistrationData({ ...registrationData, music: [...registrationData.music, songString] });
                                }
                                setMusicSearchQuery('');
                                setMusicSearchResults([]);
                              }}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: idx < musicSearchResults.length - 1 ? '1px solid #eee' : 'none',
                                transition: 'background-color 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8f4fd'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              {/* Album Artwork */}
                              {result.artwork ? (
                                <img 
                                  src={result.artwork} 
                                  alt={result.album || result.title}
                                  style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '6px',
                                    objectFit: 'cover',
                                    flexShrink: 0,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '6px',
                                  backgroundColor: result.isManual ? '#e0e0e0' : '#ddd',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  fontSize: '18px'
                                }}>
                                  {result.isManual ? '✏️' : '🎵'}
                                </div>
                              )}
                              {/* Song Info */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ 
                                  fontWeight: '600', 
                                  fontSize: '14px', 
                                  color: '#222',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {result.title}
                                </div>
                                <div style={{ 
                                  fontSize: '13px', 
                                  color: '#666', 
                                  marginTop: '2px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {result.artist}
                                </div>
                                {result.album && !result.isManual && (
                                  <div style={{ 
                                    fontSize: '11px', 
                                    color: '#999', 
                                    marginTop: '2px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>
                                    {result.album}
                                  </div>
                                )}
                              </div>
                              {/* Add indicator */}
                              <div style={{
                                fontSize: '12px',
                                color: '#4a90e2',
                                fontWeight: '500',
                                flexShrink: 0
                              }}>
                                + Add
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Show music entries - only added via searcher, no manual input fields */}
                    {registrationData.music.length === 0 ? (
                      <div style={{ color: '#999', fontSize: '13px', marginTop: '10px' }}>No music added. Use the search above to add songs.</div>
                    ) : (
                      registrationData.music.map((song, index) => (
                        <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                          <div style={{ 
                            flex: 1,
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            backgroundColor: '#f9f9f9',
                            color: '#333'
                          }}>
                            {song}
                          </div>
                          {/* Show Remove button for ALL entries */}
                          <button
                            type="button"
                            onClick={() => {
                              const newMusic = registrationData.music.filter((_, i) => i !== index);
                              setRegistrationData({ ...registrationData, music: newMusic });
                            }}
                            style={{
                              padding: '8px 12px',
                              background: '#ff4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Two-column layout for location and birthdate */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                    <label htmlFor="reg-birthdate">Birth Date</label>
                    <input
                      type="date"
                      id="reg-birthdate"
                      value={registrationData.birthdate}
                      onChange={(e) => setRegistrationData({ ...registrationData, birthdate: e.target.value })}
                      lang="en"
                      data-lang="en"
                    />
                  </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                    <label htmlFor="reg-city">City</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative' }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input
                          ref={cityInputRef}
                          type="text"
                          id="reg-city"
                          value={registrationData.city}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Try to get coordinates when city name matches a known city
                            const coords = getCityCoordinates(value);
                            setRegistrationData({ 
                              ...registrationData, 
                              city: value, 
                              cityLat: coords ? coords.lat : null, 
                              cityLng: coords ? coords.lng : null 
                            });
                            fetchCitySuggestions(value);
                          }}
                          onFocus={(e) => {
                            if (citySuggestions.length > 0) {
                              setShowCitySuggestions(true);
                            }
                          }}
                          onBlur={() => {
                            // Delay hiding suggestions to allow click on suggestion
                            setTimeout(() => setShowCitySuggestions(false), 200);
                          }}
                          placeholder="Enter city name or click 'Select on Map'"
                          style={{ width: '100%' }}
                        />
                        {showCitySuggestions && citySuggestions.length > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            zIndex: 1000,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            marginTop: '4px'
                          }}>
                            {isLoadingCitySuggestions ? (
                              <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                                Loading suggestions...
                              </div>
                            ) : (
                              citySuggestions.map((suggestion, index) => (
                                <div
                                  key={index}
                                  onClick={() => {
                                    setRegistrationData({
                                      ...registrationData,
                                      city: suggestion.name,
                                      cityLat: suggestion.lat,
                                      cityLng: suggestion.lng
                                    });
                                    setShowCitySuggestions(false);
                                    setCitySuggestions([]);
                                    
                                    // Pan map to selected city
                                    if (mapInstanceRef.current) {
                                      mapInstanceRef.current.setView([suggestion.lat, suggestion.lng], 13);
                                      
                                      // Add a marker at the selected location
                                      if (mapInstanceRef.current.selectedCityMarker) {
                                        mapInstanceRef.current.removeLayer(mapInstanceRef.current.selectedCityMarker);
                                      }
                                      const marker = window.L.marker([suggestion.lat, suggestion.lng], { color: 'red' }).addTo(mapInstanceRef.current);
                                      marker.bindPopup(`Selected: ${suggestion.name}`).openPopup();
                                      mapInstanceRef.current.selectedCityMarker = marker;
                                    }
                                  }}
                                  onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: index < citySuggestions.length - 1 ? '1px solid #eee' : 'none',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                >
                                  <div style={{ fontWeight: '500', color: '#333' }}>{suggestion.name}</div>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                    {suggestion.displayName}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          setSelectingCity(true);
                          setShowCitySuggestions(false);
                          setCitySuggestions([]);
                          if (mapInstanceRef.current) {
                            // Add click handler to map
                            const handleMapClick = async (e) => {
                              const { lat, lng } = e.latlng;
                              setSelectingCity(false);
                              // Remove click handler
                              mapInstanceRef.current.off('click', handleMapClick);
                              
                              // Try to get city name from coordinates using Nominatim (OpenStreetMap)
                              try {
                                const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1&accept-language=en`, {
                                  headers: {
                                    'Accept-Language': 'en'
                                  }
                                });
                                const data = await response.json();
                                const cityName = data.address?.city || data.address?.town || data.address?.village || data.address?.municipality || 
                                                data.address?.county || data.address?.state || data.address?.region || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
                                
                                setRegistrationData(prev => ({
                                  ...prev,
                                  cityLat: lat,
                                  cityLng: lng,
                                  city: cityName
                                }));
                                setShowCitySuggestions(false);
                                setCitySuggestions([]);
                              } catch (error) {
                                console.error('Error getting city name:', error);
                                // Fallback to coordinates
                                setRegistrationData(prev => ({
                                  ...prev,
                                  cityLat: lat,
                                  cityLng: lng,
                                  city: `${lat.toFixed(4)}, ${lng.toFixed(4)}`
                                }));
                                setShowCitySuggestions(false);
                                setCitySuggestions([]);
                              }
                              
                              // Show a marker at selected location
                              if (mapInstanceRef.current.selectedCityMarker) {
                                mapInstanceRef.current.removeLayer(mapInstanceRef.current.selectedCityMarker);
                              }
                              const marker = window.L.marker([lat, lng], { color: 'red' }).addTo(mapInstanceRef.current);
                              marker.bindPopup('Selected location').openPopup();
                              mapInstanceRef.current.selectedCityMarker = marker;
                              mapInstanceRef.current.setView([lat, lng], 13);
                            };
                            mapInstanceRef.current.on('click', handleMapClick);
                          }
                        }}
                        style={{
                          padding: '10px 16px',
                          background: selectingCity ? '#999' : '#4a90e2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: selectingCity ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          whiteSpace: 'nowrap'
                        }}
                        disabled={selectingCity}
                      >
                        {selectingCity ? 'Click on Map...' : 'Select on Map'}
                      </button>
                    </div>
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>Click "Select on Map" and then click on the map to choose your city</small>
                    </div>
                  </div>

                  <div style={{ 
                    marginBottom: '16px', 
                    fontSize: '12px', 
                    color: '#666',
                    textAlign: 'center',
                    lineHeight: '1.5'
                  }}>
                    By connecting your wallet, you agree to our Terms of Service.{' '}
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        setTermsVisible(true);
                        setTermsExpanded(true); // Expand Terms section when "Read here" is clicked
                        setTermsClosedByUser(false); // Reset closed state when user clicks "Read here"
                        setAboutVisible(false);
                        // Don't hide profile/registration form - keep it visible
                        setGroupsVisible(false);
                        setMessagesVisible(false);
                        setBugVisible(false);
                      }}
                      style={{ 
                        color: '#4a90e2', 
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                    >
                      Read here
                    </a>
                  </div>

                  <button 
                    type="submit" 
                    className="registration-submit-btn"
                    disabled={isSavingProfile}
                  >
                    {isSavingProfile ? 'Saving...' : 'Create Profile'}
                  </button>
                </form>
              </div>
              </div>
              )
            ) : (normalizedPathname.startsWith('/profile/') && !currentProfile && !isLoadingProfileFromIPFS) ? (
              /* Loading state when on /profile/:username route but profile not loaded yet */
              <div className="profile-loading-wrapper" style={{ 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center',
                width: '100%',
                height: '100%',
                minHeight: '400px',
                padding: '40px',
                fontSize: '16px',
                color: '#666',
                backgroundColor: '#ffffff',
                position: 'relative'
              }}>
                <div className="profile-cozy">
                  <CozyConnections />
                </div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  color: '#333',
                  position: 'relative',
                  zIndex: 1
                }}>
                  Loading Profile...
                </div>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #4a90e2',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  position: 'relative',
                  zIndex: 1
                }}></div>
                <div style={{
                  marginTop: '20px',
                  fontSize: '14px',
                  color: '#999',
                  position: 'relative',
                  zIndex: 1
                }}>
                  Loading profile data...
                </div>
              </div>
            ) : currentProfile ? (
              /* Existing Profile View */
              <React.Fragment>
              {/* Show full loading screen when loading My Profile data (reloading own profile) */}
              {isLoadingProfileFromIPFS && currentUser && currentProfile && currentProfile.username === currentUser ? (
                <div className="profile-loading-wrapper" style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center',
                  padding: '40px',
                  color: '#666',
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden'
                }}>
                  <div className="profile-cozy" style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '350px',
                    height: '100vh',
                    minWidth: '350px',
                    minHeight: '400px'
                  }}>
                    <CozyConnections />
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '20px',
                    color: '#333',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    Loading Your Profile...
                  </div>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #4a90e2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <div style={{
                    marginTop: '20px',
                    fontSize: '14px',
                    color: '#999'
                  }}>
                    Loading your profile data from IPFS...
                  </div>
                </div>
              ) : (
                <React.Fragment>
              <div className="profile-header">
              <div 
                className={`profile-picture ${currentUser && currentProfile.username === currentUser && profileVisible ? 'profile-picture-glow' : 'profile-picture-other'}`}
                onClick={() => {
                  // Open photo gallery popup for any profile with photos
                  const photos = getPhotosFromProfile(currentProfile);
                  if (photos.length > 0) {
                    setCurrentPhotoIndex(0);
                    setIsPhotoGalleryOpen(true);
                  } else if (currentUser && currentProfile.username === currentUser) {
                    // For own profile with no photos, trigger file upload
                    if (photosInputRef.current) {
                      photosInputRef.current.click();
                    }
                  }
                }}
      style={{
                  cursor: getPhotosFromProfile(currentProfile).length > 0 || (currentUser && currentProfile.username === currentUser) ? 'pointer' : 'default'
                }}
              >
                {getAvatarFromProfile(currentProfile) ? (
                  <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    {isAvatarLoading && (
                      <div 
                        className="profile-avatar"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          background: '#e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '3px solid white',
                          boxShadow: (currentUser && currentProfile.username === currentUser && profileVisible) ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.15)',
                          boxSizing: 'border-box',
                          zIndex: 1
                        }}
                      >
                        <div 
                          style={{
                            width: '32px',
                            height: '32px',
                            border: '3px solid #e0e0e0',
                            borderTop: '3px solid #4a90e2',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}
                        />
                      </div>
                    )}
                    <img 
                      ref={avatarImgRef}
                      key={currentProfile.username || currentProfile.walletAddress}
                      src={getAvatarFromProfile(currentProfile)} 
                      alt={currentProfile.name}
                      className="profile-avatar"
                      style={{
                        opacity: isAvatarLoading ? 0 : 1,
                        transition: 'opacity 0.3s ease'
                      }}
                      onLoad={() => setIsAvatarLoading(false)}
                      onError={() => setIsAvatarLoading(false)}
                    />
                    {/* Network badge - top right */}
                    {profileNetwork && (
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        zIndex: 10
                      }}>
                        <Tooltip 
                          text="Profile loaded from Arc network"
                          placement="left"
                          wrapperStyle={{ display: 'inline-block' }}
                        >
                          <div style={{
                            background: profileNetwork === 'base' ? '#0052FF' : '#28A0F0',
                            color: 'white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            border: '2px solid white',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                            cursor: 'pointer'
                          }}>
                            {profileNetwork === 'base' ? 'A' : 'A'}
                          </div>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                ) : (
                  <div 
                    className="profile-avatar"
                    style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: '#e0e0e0',
        display: 'flex',
        alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    fontSize: '11px',
                    textAlign: 'center',
                    padding: '8px',
                    border: '3px solid white',
                    // boxShadow is handled by CSS when profile-picture-glow class is present
                    boxShadow: (currentUser && currentProfile.username === currentUser && profileVisible) ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.15)',
                    boxSizing: 'border-box',
                    position: 'relative'
                  }}>
                    {currentUser && currentProfile.username === currentUser ? (
                      <span>Click to upload</span>
                    ) : (
                      <span>No avatar</span>
                    )}
                    {/* Network badge - top right (for profiles without avatar) */}
                    {profileNetwork && (
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        zIndex: 10
                      }}>
                        <Tooltip 
                          text="Profile loaded from Arc network"
                          placement="left"
                          wrapperStyle={{ display: 'inline-block' }}
                        >
                          <div style={{
                            background: profileNetwork === 'base' ? '#0052FF' : '#28A0F0',
                            color: 'white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            border: '2px solid white',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                            cursor: 'pointer'
                          }}>
                            {profileNetwork === 'base' ? 'A' : 'A'}
                          </div>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                )}
                {/* Photo count badge - show for all profiles with more than 1 photo */}
                {(() => {
                  const photos = getPhotosFromProfile(currentProfile);
                  const photoCount = photos.length;
                  return photoCount > 1 ? (
                    <div style={{
                      position: 'absolute',
                      bottom: '0',
                      right: '0',
                      background: '#4a90e2',
                      color: 'white',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: '600',
                      border: '2px solid white',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      zIndex: 10
                    }}>
                      {photoCount}
                    </div>
                  ) : null;
                })()}
              </div>
              
              {/* Profile analyzer and publish/unpublish block - only for own profile, positioned on right below avatar */}
              {currentUser && currentProfile.username === currentUser && (
                <div style={{ 
                  position: 'absolute',
                  right: '0px',
                  top: '163px',
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-end',
                  zIndex: 10
                }}>
                  {/* Profile analyzer outline */}
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '4px 8px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '6px',
                    border: '1px solid #e9ecef',
                    fontSize: '11px',
                    color: '#666',
                    width: '152px',
                    boxSizing: 'content-box',
                    marginBottom: '4px'
                  }}>
                    <button
                      onClick={() => setShowProfileCompletenessWizard(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0',
                        cursor: 'pointer',
                        color: '#4a90e2',
                        fontSize: '11px',
                        fontWeight: '500',
                        textAlign: 'left',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Profile Completeness Wizard
                    </button>
                  </div>
                  {/* Buttons row - in separate outlines (swapped to be above Published/Unpublished) */}
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: '4px 8px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                      fontSize: '11px',
                      color: '#666',
                      width: '72px',
                      boxSizing: 'border-box',
                      flexShrink: 0
                    }}>
                      <button
                        onClick={() => setShowPublishAllConfirm(true)}
                        disabled={isPublishingAll}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '0',
                          cursor: isPublishingAll ? 'not-allowed' : 'pointer',
                          color: '#4a90e2',
                          fontSize: '11px',
                          fontWeight: '500',
                          opacity: isPublishingAll ? 0.6 : 1,
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          width: '62px'
                        }}
                      >
                        Publish all
                      </button>
                    </div>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: '4px 8px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                      fontSize: '11px',
                      color: '#666',
                      width: '94px',
                      boxSizing: 'border-box',
                      flexShrink: 0
                    }}>
                      <button
                        onClick={() => setShowUnpublishAllConfirm(true)}
                        disabled={isUnpublishingAll}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '0',
                          cursor: isUnpublishingAll ? 'not-allowed' : 'pointer',
                          color: '#4a90e2',
                          fontSize: '11px',
                          fontWeight: '500',
                          opacity: isUnpublishingAll ? 0.6 : 1,
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          width: '78px'
                        }}
                      >
                        Unpublish all
                      </button>
                    </div>
                  </div>
                  {/* Labels row with outline */}
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '4px 8px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '6px',
                    border: '1px solid #e9ecef',
                    fontSize: '11px',
                    color: '#666',
                    marginBottom: '2px'
                  }}>
                    <Tooltip
                      title="Published"
                      text="Visible to anyone, stored in PeopleTag database and searchable"
                      color="#4a90e2"
                    >
                      <span style={{display: 'flex', alignItems: 'center', gap: '3px', cursor: 'help', width: '62px' }}>
                        <span style={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '2px', 
                          backgroundColor: '#4a90e2',
                          display: 'inline-block',
                          flexShrink: 0
                        }}></span>
                        <span>Published</span>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title="Unpublished"
                      text="Not visible to anyone, not stored in PeopleTag database and not searchable"
                      color="#999"
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'help', width: '78px' }}>
                        <span style={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '2px', 
                          backgroundColor: '#999',
                          display: 'inline-block',
                          flexShrink: 0
                        }}></span>
                        <span>Unpublished</span>
                      </span>
                    </Tooltip>
                  </div>
                </div>
              )}
              
              {/* Hidden file input for avatar upload - always available for own profile */}
              {currentUser && currentProfile.username === currentUser && (
                <input
                  ref={photosInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;

                    const currentPhotos = getPhotosFromProfile(currentProfile);
                    const remainingSlots = 30 - currentPhotos.length;
                    
                    if (files.length > remainingSlots) {
                      setNotification({
                        message: `You can only upload ${remainingSlots} more photo(s). Maximum 30 photos allowed.`,
                        type: 'error'
                      });
                      setTimeout(() => setNotification(null), 5000);
                      e.target.value = '';
                      return;
                    }

                    setIsSavingAvatar(true);
                    try {
                      const newPhotos = [...currentPhotos];
                      
                      for (const file of files) {
                        // Check file size (max 5MB)
                        if (file.size > 5 * 1024 * 1024) {
                          setNotification({
                            message: `${file.name} is too large. Maximum 5MB per image.`,
                            type: 'error'
                          });
                          setTimeout(() => setNotification(null), 5000);
                          continue;
                        }
                        
                        // Check file type
                        if (!file.type.startsWith('image/')) {
                          setNotification({
                            message: `${file.name} is not an image file.`,
                            type: 'error'
                          });
                          setTimeout(() => setNotification(null), 5000);
                          continue;
                        }

                        // Compress image
                        const compressedImage = await compressImage(file, 800, 0.85); // Max 800x800px, 85% quality
                        const base64Size = (compressedImage.length * 3) / 4;
                        
                        if (base64Size > 400 * 1024) {
                          setNotification({
                            message: `${file.name} is too large even after compression. Please use a smaller image.`,
                            type: 'error'
                          });
                          setTimeout(() => setNotification(null), 5000);
                          continue;
                        }

                        newPhotos.push(compressedImage);
                      }

                      if (newPhotos.length === currentPhotos.length) {
                        setIsSavingAvatar(false);
                        e.target.value = '';
                        return;
                      }

                      const updatedProfile = {
                        ...currentProfile,
                        photos: newPhotos,
                        avatar: newPhotos.length > 0 ? newPhotos[0] : null // Update avatar to first photo
                      };
                      setCurrentProfile(updatedProfile);
                      
                      // Save photos directly to Firestore (not encrypted, like avatar)
                      // Use updateUserPhotos to only update photos/avatar without affecting other fields
                      // This preserves the published state of city, bio, and other fields
                      try {
                        await updateUserPhotos(
                          walletAddress,
                          updatedProfile.username,
                          newPhotos,
                          newPhotos.length > 0 ? newPhotos[0] : null
                        );
                        console.log('✅ Photos saved directly to Firestore');
                      } catch (saveError) {
                        console.error('Error saving photos to Firestore:', saveError);
                        throw saveError;
                      }
                      
                      setNotification({
                        message: `${newPhotos.length - currentPhotos.length} photo(s) uploaded successfully!`,
                        type: 'success'
                      });
                      setTimeout(() => setNotification(null), 3000);
                      
                      // Reload profile in background to ensure consistency
                      if (walletAddress) {
                        getUserProfileByWallet(walletAddress, encryptionKey).then(reloadedProfile => {
                          if (reloadedProfile) {
                            const cleanReloaded = cleanProfileData(reloadedProfile);
                            setCurrentProfile(cleanReloaded);
                          }
                        }).catch(err => {
                          console.error('Error reloading profile:', err);
                        });
                      }
                    } catch (error) {
                      console.error('Error uploading photos:', error);
                      setNotification({
                        message: 'Error uploading photos: ' + (error.message || 'Please try again.'),
                        type: 'error'
                      });
                      setTimeout(() => setNotification(null), 5000);
                    } finally {
                      setIsSavingAvatar(false);
                      e.target.value = '';
                    }
                  }}
                />
              )}
              
              <div className={`profile-info ${!(currentUser && currentProfile.username === currentUser) ? 'profile-info-other-user' : ''}`}>
                {isEditingName && currentUser && currentProfile.username === currentUser ? (
                  <div style={{ marginBottom: '10px', position: 'relative' }} className={nameChanged ? 'changed-outline' : ''}>
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      placeholder="Enter your name"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '18px',
                        fontWeight: '600',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontFamily: 'inherit'
                      }}
                      autoFocus
                    />
                    {nameChanged && <span className="change-pill">change</span>}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        onClick={async () => {
                          if (!editingName.trim()) {
                            alert('Name cannot be empty');
                            return;
                          }
                          setIsSavingName(true);
                          try {
                            const updatedProfile = {
                              ...currentProfile,
                              name: editingName.trim()
                            };
                            const result = await encryptAndSaveProfile(updatedProfile);
                          // Mark visual changes immediately (before global commit) - only mark name as changed
                          markRecentlyChanged(updatedProfile, ['name']);
                            
                            // Update currentProfile with new name and ipfsHash
                            if (result?.ipfsHash) {
                              setCurrentProfile(prev => ({
                                ...prev,
                                name: editingName.trim(), // Update name in state
                                ipfsHash: result.ipfsHash
                              }));
                            } else {
                              // Even if ipfsHash update failed, update name in state
                              setCurrentProfile(prev => ({
                                ...prev,
                                name: editingName.trim()
                              }));
                            }
                            
                            // Reload markers in background (don't reload profile to avoid state reset)
                            loadAllProfilesAndCreateMarkers().catch(error => {
                              console.error('Error reloading markers:', error);
                            });
                            
                            setIsEditingName(false);
                            setEditingName('');
                          } catch (error) {
                            console.error('Error saving name:', error);
                            alert('Error saving name. Please try again.');
                          } finally {
                            setIsSavingName(false);
                          }
                        }}
                        disabled={isSavingName}
                        style={{
                          padding: '6px 12px',
                          background: isSavingName ? '#ccc' : '#4a90e2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: isSavingName ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          opacity: isSavingName ? 0.7 : 1
                        }}
                      >
                        {isSavingName ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingName(false);
                          setEditingName('');
                        }}
                        disabled={isSavingName}
                        style={{
                          padding: '6px 12px',
                          background: '#f5f5f5',
                          color: '#333',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: isSavingName ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="profile-name" style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start', textAlign: 'left' }}>
                    {currentUser && currentProfile.username === currentUser ? (
                      <div style={{ position: 'relative', display: 'inline-block', alignSelf: 'flex-start', maxWidth: '100%' }}>
                        <button
                          onClick={() => {
                            updatePublishStatus('name', null, !publishedName);
                          }}
                          disabled={isUpdatingPublish}
                          style={{
                            padding: '8px 16px',
                            fontSize: '18px',
                            fontWeight: '600',
                            border: recentlyChanged.name ? '2px solid #ffcccc' : 'none',
                            borderRadius: '10px',
                            cursor: isUpdatingPublish ? 'not-allowed' : 'pointer',
                            backgroundColor: publishedName ? '#e0f0ff' : '#f5f5f5',
                            color: publishedName ? '#0066cc' : '#333',
                            transition: 'all 0.2s ease',
                            opacity: isUpdatingPublish ? 0.6 : 1,
                            fontFamily: 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            maxWidth: '100%',
                            minWidth: 0,
                            overflow: 'hidden'
                          }}
                        >
                          <Tooltip
                            text={publishedName ? 'Published - visible to others' : 'Not published - hidden from others'}
                          >
                            <span style={{ 
                              display: 'inline-block', 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '200px',
                              minWidth: 0
                            }}>{truncateName(currentProfile.name || 'No name', 13)}</span>
                          </Tooltip>
                          {recentlyChanged.name && (
                            <span
                              style={{
                                fontSize: '9px',
                                fontWeight: '700',
                                color: '#cc2d2d',
                                backgroundColor: '#ffecec',
                                padding: '1px 4px',
                                borderRadius: '8px',
                                border: '1px solid #ffc8c8',
                                lineHeight: 1
                              }}
                            >
                              change
                            </span>
                          )}
                          <span 
                            className="edit-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingName(currentProfile.name || '');
                              setIsEditingName(true);
                            }}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                          >
                            <Tooltip text="Edit name">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </Tooltip>
                          </span>
                        </button>
                        {currentProfile && currentProfile._isTestRecord && (
                          <div style={{ marginTop: '2px', fontSize: '10px', color: '#888' }}>(test user)</div>
                        )}
                        {showPublishedNotification && showPublishedNotification.key === 'name-single' && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '-28px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap',
                              zIndex: 1000,
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                            }}
                          >
                            {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start', textAlign: 'left' }}>
                        <span style={{ textAlign: 'left' }}>{truncateName(currentProfile.name || currentProfile.username || 'No name', 15)}</span>
                        {currentProfile && currentProfile._isTestRecord ? (
                          <span style={{ fontSize: '10px', color: '#888', textAlign: 'left' }}>(test user)</span>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
                {currentProfile.username && (
                  <div 
                    onClick={() => copyToClipboard(`@${currentProfile.username}`)}
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      cursor: 'pointer',
                      marginTop: '4px',
                      display: 'inline-block',
                      transition: 'color 0.2s',
                      userSelect: 'none'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#333'}
                    onMouseLeave={(e) => e.target.style.color = '#666'}
                  >
                    <Tooltip text="Click to copy username">
                      <span style={{ display: 'inline-block' }}>
                        @{currentProfile.username}
                        {currentUser && currentProfile.username === currentUser && (
                          <span style={{ marginLeft: '4px', color: '#888' }}>(you)</span>
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )}
                <div className="profile-rating" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <div 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      cursor: userRatingSummary.reviewCount > 0 ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (userRatingSummary.reviewCount > 0) {
                        setReviewsListModalVisible(true);
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (userRatingSummary.reviewCount > 0) {
                        e.currentTarget.style.opacity = '0.7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (userRatingSummary.reviewCount > 0) {
                        e.currentTarget.style.opacity = '1';
                      }
                    }}
                  >
                    {(() => {
                      const avgRating = userRatingSummary.averageRating || 0;
                      const displayRating = Math.round(avgRating * 10) / 10; // Round to 1 decimal for display
                      const roundedStars = Math.round(avgRating); // Round to nearest integer for stars
                      return (
                        <>
                          {'★'.repeat(roundedStars)}
                          {'☆'.repeat(5 - roundedStars)}
                        </>
                      );
                    })()}
                    <span className="rating-number" style={{ marginLeft: '4px' }}>
                      {userRatingSummary.averageRating > 0 ? Math.round(userRatingSummary.averageRating * 10) / 10 : '0.0'}
                    </span>
                    {userRatingSummary.reviewCount > 0 && (
                      <span style={{ fontSize: '12px', color: '#666', marginLeft: '4px' }}>
                        ({userRatingSummary.reviewCount} {userRatingSummary.reviewCount === 1 ? 'review' : 'reviews'})
                      </span>
                    )}
                  </div>
                </div>
                {/* Badges Display - Show earned badges */}
                {(() => {
                  const isOwnProfile = isMyProfile();
                  const badgesData = isOwnProfile ? gamificationData : viewedProfileGamificationData;
                  const badges = badgesData?.badges || [];
                  
                  return badges.length > 0 ? (
                  <div style={{ 
                    marginTop: '8px', 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 25px)',
                    gap: '4px',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: '83px', // Fixed width: 3 badges * 25px + 2 gaps * 4px = 75px + 8px = 83px
                    maxWidth: '83px', // Prevent expansion
                    minWidth: '83px', // Prevent shrinking
                    position: 'relative',
                    zIndex: 1,
                    marginRight: 'auto', // Push to left, no extra space on right
                    boxSizing: 'border-box' // Include padding/border in width calculation
                  }}>
                    {badges.map((badgeId) => {
                      const badgeKey = Object.keys(BADGES).find(key => BADGES[key].id === badgeId);
                      const badge = badgeKey ? BADGES[badgeKey] : null;
                      if (!badge) return null;
                      
                      return (
                        <Tooltip key={badgeId} text={`${badge.name}: ${badge.description}`}>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              width: '25px',
                              height: '25px',
                              borderRadius: '50%',
                              backgroundColor: `${badge.color}15`,
                              border: `1px solid ${badge.color}`,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              flexShrink: 0,
                              position: 'relative',
                              zIndex: 10
                            }}
                            onClick={() => {
                              setTagRunVisible(true);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.15)';
                              e.currentTarget.style.backgroundColor = `${badge.color}25`;
                              e.currentTarget.style.zIndex = 1000;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.backgroundColor = `${badge.color}15`;
                              e.currentTarget.style.zIndex = 10;
                            }}
                          >
                            {badge.icon}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                  ) : null;
                })()}
                {!currentUser || currentProfile.username !== currentUser ? (
                  <div style={{ marginTop: '8px' }}>
                    <a 
                      href="#" 
                      className="log-off" 
                      style={{ 
                        display: 'block',
                        opacity: walletAddress ? 1 : 0.45,
                        pointerEvents: walletAddress ? 'auto' : 'none'
                      }}
                      onClick={async (e) => {
                        e.preventDefault();
                        if (!walletAddress) {
                          setNotification({ message: 'Connect wallet to send messages.', type: 'info' });
                          setTimeout(() => setNotification(null), 4000);
                          return;
                        }
                        
                        // NEW APPROACH: Use the wallet address that was used to load this profile
                        // This is stored in viewedProfileWalletAddress when loadUserProfile is called
                        let recipientAddress = viewedProfileWalletAddress;
                        let recipientUsername = currentProfile.username;
                        
                        console.log('Setting up message - profile being viewed:', {
                          username: recipientUsername,
                          viewedProfileWalletAddress: viewedProfileWalletAddress,
                          currentProfileWalletAddress: currentProfile.walletAddress,
                          senderWalletAddress: walletAddress
                        });
                        
                        // Validate we have a recipient address - try multiple sources
                        if (!recipientAddress) {
                          // Try 1: Get from currentProfile.walletAddress
                          recipientAddress = currentProfile.walletAddress;
                          
                          // Try 2: Get from username lookup (comprehensive search)
                          if (!recipientAddress && recipientUsername) {
                            try {
                              console.log('Looking up wallet address from username:', recipientUsername);
                              recipientAddress = await getWalletFromUsername(recipientUsername);
                              if (recipientAddress) {
                                console.log('✅ Found wallet address from username lookup:', recipientAddress);
                              } else {
                                console.warn('⚠️ Username lookup returned no wallet address');
                              }
                            } catch (error) {
                              console.error('Error getting wallet from username:', error);
                            }
                          }
                          
                          // Try 3: Check if profile has wallet address in any field
                          if (!recipientAddress) {
                            const possibleFields = ['walletAddress', 'wallet', 'address', 'ethAddress', 'ethereumAddress'];
                            for (const field of possibleFields) {
                              if (currentProfile[field] && typeof currentProfile[field] === 'string' && currentProfile[field].startsWith('0x')) {
                                recipientAddress = currentProfile[field];
                                console.log(`✅ Found wallet address in field '${field}':`, recipientAddress);
                                break;
                              }
                            }
                          }
                        }
                        
                        // All users should have wallets in walletUsers collection (even if unpublished)
                        // If we still don't have an address, the user might not be registered
                        if (!recipientAddress) {
                          console.warn('No wallet address found. Cannot send message.');
                          alert('Cannot send message: Could not identify this user. The user may not be registered or may not have completed their profile setup.');
                          return;
                        }
                        
                        // Validate that we're not trying to message ourselves
                        const normalizedRecipient = recipientAddress.toLowerCase();
                        const normalizedSender = walletAddress?.toLowerCase();
                        
                        if (normalizedRecipient === normalizedSender) {
                          console.error('ERROR: Recipient address matches sender!', {
                            recipientAddress: recipientAddress,
                            senderAddress: walletAddress,
                            viewedProfileWalletAddress: viewedProfileWalletAddress,
                            currentProfileUsername: currentProfile.username,
                            currentUser: currentUser
                          });
                          alert('Cannot send message to yourself. Please select a different user.');
                          return;
                        }
                        
                        // If username is not available but we have wallet address, try to get username
                        if (!recipientUsername && recipientAddress) {
                          try {
                            recipientUsername = await getUsernameFromWallet(recipientAddress);
                          } catch (error) {
                            console.error('Error getting username from wallet:', error);
                          }
                        }
                        
                        // All messages are now encrypted using wallet-to-wallet encryption
                        // The messaging system uses ECDH key exchange to generate shared encryption keys
                        // At this point, recipientAddress should always be set (either real wallet or temporary)
                        console.log('Setting up message to:', {
                          recipientAddress: normalizedRecipient,
                          recipientUsername: recipientUsername,
                          senderAddress: normalizedSender,
                          isTemporaryAddress: recipientAddress && recipientAddress.startsWith('0x') && !viewedProfileWalletAddress && !currentProfile.walletAddress
                        });
                        
                        // Hide profile and show messaging
                        setProfileVisible(false);
                        setMessagesVisible(true);
                        // Always store wallet address (real or temporary) - this is what we need for messaging
                        setSelectedMessageRecipientWallet(recipientAddress);
                        // CRITICAL: Always pass username to initialRecipient, never wallet address
                        // URL should always contain username, never displayName or wallet address
                        if (recipientUsername) {
                          setSelectedMessageRecipient(recipientUsername);
                          navigate(`/messages/${encodeURIComponent(recipientUsername)}`);
                        } else {
                          // If no username, just navigate to /messages without username
                          // Messaging component will use the wallet address from initialRecipientWallet
                          setSelectedMessageRecipient(null);
                          navigate('/messages');
                        }
                      }}
                    >Send message</a>
                    <a 
                      href="#" 
                      className="log-off" 
                      style={{ 
                        display: 'block', 
                        marginTop: '8px',
                        opacity: walletAddress ? 1 : 0.45,
                        pointerEvents: walletAddress ? 'auto' : 'none'
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!walletAddress) {
                          setNotification({ message: 'Connect wallet to leave reviews.', type: 'info' });
                          setTimeout(() => setNotification(null), 4000);
                          return;
                        }
                        setReviewFormVisible(true);
                      }}
                      title={walletAddress ? '' : 'Connect wallet to leave reviews'}
                    >
                      {existingReview ? 'Edit review' : 'Leave review'}
                    </a>
                    <a
                      href="#"
                      className="log-off"
                      style={{
                        display: 'block',
                        marginTop: '8px',
                        opacity: walletAddress ? 1 : 0.45,
                        pointerEvents: walletAddress ? 'auto' : 'none'
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!walletAddress) {
                          setNotification({ message: 'Connect wallet to report users.', type: 'info' });
                          setTimeout(() => setNotification(null), 4000);
                          return;
                        }
                        setNotification({ message: 'Report user is not implemented yet.', type: 'info' });
                        setTimeout(() => setNotification(null), 4000);
                      }}
                      title={walletAddress ? '' : 'Connect wallet to report users'}
                    >
                      Report this user
                    </a>
                  </div>
                ) : null}
              </div>
            </div>

          {/* USDC wallet card (own profile only) */}
          {currentUser && currentProfile.username === currentUser && walletAddress && (
            <div
              style={{
                margin: '16px 0',
                padding: '14px 16px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #eaf3ff 0%, #f7fbff 100%)',
                border: '1px solid #d6e6fb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                flexWrap: 'nowrap'
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#5a7fb0', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  USDC balance
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: '2px' }}>
                  <span style={{ fontSize: '20px', fontWeight: 700, color: '#0a3d80' }}>
                    {isLoadingUsdcBalance ? '…' : formatUsdcBalance(usdcBalance)}
                  </span>
                  <span style={{ fontSize: '12px', color: '#5a7fb0', fontWeight: 500 }}>USDC</span>
                  <button
                    onClick={() => setBalanceRefreshTick(t => t + 1)}
                    title="Refresh balance"
                    style={{
                      marginLeft: '4px',
                      width: '22px',
                      height: '22px',
                      lineHeight: '20px',
                      padding: 0,
                      border: '1px solid #cfe0f5',
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      color: '#4a90e2',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    ↻
                  </button>
                </div>
              </div>
              <button
                onClick={() => openTransferModal(null)}
                style={{
                  padding: '9px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #4a90e2 0%, #2f6fc4 100%)',
                  color: '#fff',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(74,144,226,0.35)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  flex: '0 0 auto',
                  whiteSpace: 'nowrap',
                  marginLeft: 'auto'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                Send USDC
              </button>
            </div>
          )}

          {/* Bio Section */}
          {isEditingBio && currentUser && currentProfile.username === currentUser ? (
            <div className="profile-section">
              <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>Bio</span>
                  <span 
                    className="edit-icon" 
                    onClick={() => {
                      // Revert to original bio value
                      const originalBio = originalBioRef.current;
                      if (currentProfile) {
                        setCurrentProfile(prev => ({
                          ...prev,
                          bio: originalBio
                        }));
                        // Remove bio from pendingProfileRef if it was there
                        if (pendingProfileRef.current) {
                          if (originalBio === null) {
                            delete pendingProfileRef.current.bio;
                          } else {
                            pendingProfileRef.current.bio = originalBio;
                          }
                          // Check if there are any other pending changes
                          const hasOtherChanges = Object.keys(pendingProfileRef.current).length > 0;
                          if (!hasOtherChanges) {
                            pendingProfileRef.current = null;
                            setHasUnsavedChanges(false);
                          }
                        } else {
                          // No pending changes, ensure unsaved changes flag is cleared
                          setHasUnsavedChanges(false);
                        }
                      }
                      setIsEditingBio(false);
                      setEditingBio('');
                      originalBioRef.current = null;
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </span>
                </div>
              </div>
              <div style={{ marginTop: '10px', position: 'relative' }} className={bioChanged ? 'changed-outline' : ''}>
                <textarea
                  value={editingBio}
                  onChange={(e) => {
                    // Only update editingBio state - don't update currentProfile yet
                    // The merge logic in commitPendingProfileChanges will handle merging editingBio into dataToSave
                    setEditingBio(e.target.value);
                  }}
                  placeholder="Tell us something about you..."
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    minHeight: '80px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                {bioChanged && <span className="change-pill">change</span>}
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button
                    onClick={async () => {
                      if (isSavingBio) return;
                      setIsSavingBio(true);
                      // #region agent log
                      fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'App.js:12503',message:'BIO save start',data:{editingBio:editingBio,editingBioLength:editingBio?.length,currentProfileBio:currentProfile?.bio,deferProfileSaves:deferProfileSavesRef.current},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                      // #endregion
                      const updatedProfile = {
                        ...currentProfile,
                        bio: editingBio.trim() || null
                      };
                      // #region agent log
                      fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'App.js:12508',message:'updatedProfile before deferring save',data:{updatedProfileBio:updatedProfile.bio,updatedProfileBioLength:updatedProfile.bio?.length,hasBio:updatedProfile.bio!==null&&updatedProfile.bio!==undefined,deferProfileSaves:deferProfileSavesRef.current},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                      // #endregion
                      
                      // Update local state immediately (for UI feedback)
                      setCurrentProfile(updatedProfile);
                      
                      // Defer the actual save - changes will be saved when user clicks "Save all changes"
                      // This allows multiple changes to be batched into one transaction
                      const result = await encryptAndSaveProfile(updatedProfile);
                      // #region agent log
                      fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'App.js:12510',message:'encryptAndSaveProfile result (deferred)',data:{hasResult:!!result,wasDeferred:result?.deferred,hasPendingData:!!pendingProfileRef.current},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
                      // #endregion
                      
                      // Mark visual changes - show "change" indicator until "Save all changes" is clicked
                      markRecentlyChanged(updatedProfile, ['bio']);
                      
                      setIsEditingBio(false);
                      setIsSavingBio(false);
                      
                      // Show notification that change is pending
                      setNotification({ 
                        message: 'Bio updated. Click "Save all changes" to save to blockchain.', 
                        type: 'info' 
                      });
                      setTimeout(() => setNotification(null), 3000);
                    }}
                    disabled={isSavingBio}
                    style={{
                      padding: '8px 16px',
                      background: isSavingBio ? '#ccc' : '#4a90e2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: isSavingBio ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isSavingBio ? 0.7 : 1
                    }}
                  >
                    {isSavingBio ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      // Revert to original bio value
                      const originalBio = originalBioRef.current;
                      if (currentProfile) {
                        setCurrentProfile(prev => ({
                          ...prev,
                          bio: originalBio
                        }));
                        // Remove bio from pendingProfileRef if it was there
                        if (pendingProfileRef.current) {
                          if (originalBio === null) {
                            delete pendingProfileRef.current.bio;
                          } else {
                            pendingProfileRef.current.bio = originalBio;
                          }
                          // Check if there are any other pending changes
                          const hasOtherChanges = Object.keys(pendingProfileRef.current).length > 0;
                          if (!hasOtherChanges) {
                            pendingProfileRef.current = null;
                            setHasUnsavedChanges(false);
                          }
                        } else {
                          // No pending changes, ensure unsaved changes flag is cleared
                          setHasUnsavedChanges(false);
                        }
                      }
                      setIsEditingBio(false);
                      setEditingBio('');
                      originalBioRef.current = null;
                    }}
                    disabled={isSavingBio}
                    style={{
                      padding: '8px 16px',
                      background: 'white',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      cursor: isSavingBio ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isSavingBio ? 0.7 : 1
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="profile-section">
              <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>Bio</span>
                  {currentUser && currentProfile.username === currentUser && (
                    <span 
                      className="edit-icon" 
                      onClick={() => {
                        // Store original bio value before editing starts
                        originalBioRef.current = currentProfile.bio || null;
                        setEditingBio(currentProfile.bio || '');
                        setIsEditingBio(true);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </span>
                  )}
                </div>
              </div>
              <div style={{ marginTop: '10px' }}>
                {currentProfile.bio ? (
                  <div style={{ position: 'relative', display: 'inline-block', width: '100%' }} className={bioChanged ? 'changed-outline' : ''}>
                    {bioChanged && <span className="change-pill">change</span>}
                    {currentUser && currentProfile.username === currentUser ? (
                      <button
                        onClick={() => updatePublishStatus('bio', null, !publishedBio)}
                        disabled={isUpdatingPublish}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: isUpdatingPublish ? 'not-allowed' : 'pointer',
                          backgroundColor: publishedBio ? '#e0f0ff' : '#f5f5f5',
                          color: publishedBio ? '#0066cc' : '#333',
                          transition: 'all 0.2s ease',
                          opacity: isUpdatingPublish ? 0.6 : 1,
                          fontFamily: 'inherit',
                          width: '100%',
                          textAlign: 'left',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        <Tooltip
                          text={publishedBio ? 'Published - visible to others' : 'Not published - hidden from others'}
                        >
                          <span style={{ display: 'inline-block', width: '100%' }}>{currentProfile.bio}</span>
                        </Tooltip>
                      </button>
                    ) : (
                      <div style={{
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: 'normal',
                        borderRadius: '6px',
                        backgroundColor: '#f5f5f5',
                        color: '#333',
                        fontFamily: 'inherit',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        position: 'relative',
                        border: recentlyChanged.bio ? '2px solid #ffcccc' : 'none'
                      }}>
                        {recentlyChanged.bio && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#cc2d2d',
                              backgroundColor: '#ffecec',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #ffc8c8',
                              lineHeight: 1
                            }}
                          >
                            change
                          </span>
                        )}
                        {currentProfile.bio}
                      </div>
                    )}
                    {showPublishedNotification && showPublishedNotification.key === 'bio-single' && currentUser && currentProfile.username === currentUser && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '-28px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                        }}
                      >
                        {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                      </div>
                    )}
                  </div>
                ) : (
                  <span style={{ color: '#999', fontSize: '13px' }}>
                    {currentUser && currentProfile.username === currentUser ? 'Bio not specified' : 'No bio specified'}
                  </span>
                )}
              </div>
            </div>
          )}


          <div className="profile-section">
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{currentUser && currentProfile.username === currentUser ? 'My tags' : 'Tags'}</span>
            {/* Show match indicator for other users' profiles */}
            {(!currentUser || currentProfile.username !== currentUser) && myProfileForMatch && (
              (() => {
                const matchPercentage = calculateMatchPercentage(myProfileForMatch, currentProfile);
                const getMatchColor = (percentage) => {
                  if (percentage === 0) return '#999';
                  if (percentage >= 50) return '#4caf50';
                  if (percentage >= 25) return '#ff9800';
                  if (percentage >= 12) return '#ffc107';
                  return '#ff9800';
                };
                const getOpacity = (percentage) => {
                  return percentage === 0 ? 0.5 : 1;
                };
                return (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginLeft: '8px',
                      opacity: getOpacity(matchPercentage)
                    }}
                  >
                    <Tooltip text={`${matchPercentage.toFixed(0)}% match`}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* Longer battery-like indicator */}
                        <div
                          style={{
                            position: 'relative',
                            width: '140px',
                            height: '18px',
                            border: '2px solid #333',
                            borderRadius: '3px',
                            backgroundColor: '#f5f5f5',
                            overflow: 'hidden',
                            boxShadow: matchPercentage >= 50 ? `0 0 10px ${getMatchColor(matchPercentage)}80, 0 0 15px ${getMatchColor(matchPercentage)}40` : 'none',
                            transition: 'box-shadow 0.3s ease'
                          }}
                        >
                          {/* Battery fill */}
                          <div
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              height: '100%',
                              width: `${Math.min(matchPercentage, 100)}%`,
                              backgroundColor: getMatchColor(matchPercentage),
                              transition: 'width 0.3s ease, background-color 0.3s ease'
                            }}
                          />
                          {/* Percentage text */}
                          <span
                            style={{
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: matchPercentage > 50 ? '#fff' : (matchPercentage === 0 ? '#999' : '#333'),
                              zIndex: 1,
                              lineHeight: 1,
                              textShadow: matchPercentage > 50 ? '0 0 2px rgba(0,0,0,0.3)' : 'none',
                              whiteSpace: 'nowrap',
                              padding: '0 2px'
                            }}
                          >
                            {matchPercentage.toFixed(0)}% matching your profile
                          </span>
                        </div>
                        {/* Battery terminal */}
                        <div
                          style={{
                            width: '3px',
                            height: '12px',
                            backgroundColor: '#333',
                            borderRadius: '0 2px 2px 0',
                            marginLeft: '2px',
                            opacity: matchPercentage === 0 ? 0.5 : 1
                          }}
                        />
                      </div>
                    </Tooltip>
                  </div>
              );
            })()
            )}
            {currentUser && currentProfile.username === currentUser && !isEditingTags && (
              <span 
                className="edit-icon"
                onClick={() => {
                  // Initialize editing tags with current tags (remove # for editing)
                  const tagsWithoutHash = currentProfile.tagsList ? currentProfile.tagsList.map(tag => tag.startsWith('#') ? tag.substring(1) : tag) : [];
                  setEditingTags(tagsWithoutHash.length > 0 ? tagsWithoutHash : ['']);
                  setIsEditingTags(true);
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </span>
            )}
          </div>
          {isEditingTags && currentUser && currentProfile.username === currentUser ? (
            <div style={{ marginTop: '10px', position: 'relative', padding: '8px' }} className={tagsChanged ? 'changed-outline' : ''}>
              {tagsChanged && <span className="change-pill">change</span>}
              {editingTags.map((tag, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    ref={(el) => {
                      if (el) tagInputRefs.current[`edit-${index}`] = el;
                    }}
                    type="text"
                    value={tag}
                    onChange={(e) => {
                      const newTags = [...editingTags];
                      newTags[index] = e.target.value;
                      setEditingTags(newTags);
                    }}
                    placeholder="Enter a tag"
                    style={{ 
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                  {index === editingTags.length - 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newIndex = editingTags.length;
                        setEditingTags([...editingTags, '']);
                        // Focus the new input after state update
                        setTimeout(() => {
                          if (tagInputRefs.current[`edit-${newIndex}`]) {
                            tagInputRefs.current[`edit-${newIndex}`].focus();
                          }
                        }, 0);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                    >
                      +
                    </button>
                  )}
                </div>
              ))}
              
              {/* Popular Tags Section */}
              <div style={{ marginTop: '20px', marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                  Popular tags (click to add)
                </label>
                <div className="popular-tags-container">
                  {isLoadingPopularTags ? (
                    <div className="popular-tags-loading">
                      <div className="popular-tags-spinner"></div>
                      <span style={{ marginLeft: '8px', color: '#666', fontSize: '14px' }}>Loading...</span>
                    </div>
                  ) : popularTags.length > 0 ? (
                    <div className="popular-tags-list">
                      {popularTags.map(({ tag, count }, index) => {
                        // Compare tags without # prefix
                        const tagWithoutHash = tag.trim().replace(/^#+/, '').toLowerCase();
                        const existingTags = editingTags.map(t => {
                          const normalized = (t || '').trim().replace(/^#+/, '').toLowerCase();
                          return normalized;
                        }).filter(Boolean);
                        const isSelected = existingTags.includes(tagWithoutHash);
                        
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleEditPopularTagClick(tag)}
                            className={`popular-tag-button ${isSelected ? 'selected' : ''}`}
                          >
                            <Tooltip text={isSelected ? `Click to remove (${count} people have this tag)` : `Click to add (${count} people have this tag)`}>
                              <span className="popular-tag-text">{tag}</span>
                              <span className="popular-tag-count">({count})</span>
                            </Tooltip>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <button
                  onClick={async () => {
                    if (isSavingTags) return; // Prevent multiple saves
                    setIsSavingTags(true);
                    
                    try {
                      // Parse tags (array, add # if not present)
                      const tagsList = editingTags
                        .map(tag => tag.trim())
                        .filter(tag => tag.length > 0)
                        .map(tag => tag.startsWith('#') ? tag : `#${tag}`);

                      // Check for duplicates (case-insensitive)
                      const tagsLower = tagsList.map(tag => tag.toLowerCase());
                      const duplicates = tagsList.filter((tag, index) => tagsLower.indexOf(tag.toLowerCase()) !== index);
                      
                      if (duplicates.length > 0) {
                        alert(`Duplicate tags are not allowed. Please remove duplicates: ${duplicates.join(', ')}`);
                        setIsSavingTags(false);
                        return;
                      }

                      // Update publishedTags to only include tags that exist in the new tagsList
                      // This ensures that if a tag was removed, it's also removed from publishedTags
                      const updatedPublishedTags = publishedTags.filter(tag => tagsList.includes(tag));
                      setPublishedTags(updatedPublishedTags);

                      // Update profile with new tags
                      const updatedProfile = {
                        ...currentProfile,
                        tagsList: tagsList,
                        tags: tagsList.length
                      };
                      setCurrentProfile(updatedProfile);
                      
                      // Debug: Log what we're about to save
                      console.log('💾 Saving tags to IPFS:', {
                        tagsList: tagsList,
                        updatedPublishedTags: updatedPublishedTags,
                        currentPublishedTags: publishedTags
                      });
                      
                      // Encrypt and save to IPFS
                      // IMPORTANT: encryptAndSaveProfile will encrypt ALL tags from updatedProfile.tagsList
                      // Firestore will only get publishedTags, but IPFS gets everything
                      const result = await encryptAndSaveProfile(updatedProfile);
                      
                      if (result?.encryptedData) {
                        setEncryptedProfileData(result.encryptedData);
                      }

                      // Mark visual changes immediately (before global commit) - only mark tags as changed
                      markRecentlyChanged(updatedProfile, ['tags']);
                      
                      setIsEditingTags(false);
                      // Update currentProfile with tagsList and ipfsHash
                      if (result?.ipfsHash) {
                        setCurrentProfile(prev => ({
                          ...prev,
                          tagsList: tagsList, // Preserve tagsList field
                          tags: tagsList.length, // Preserve tags count
                          ipfsHash: result.ipfsHash
                        }));
                      } else {
                        // Even if ipfsHash update failed, ensure tagsList is in state
                        setCurrentProfile(prev => ({
                          ...prev,
                          tagsList: tagsList,
                          tags: tagsList.length
                        }));
                      }
                      // Reload markers in background (don't reload profile to avoid state reset)
                      loadAllProfilesAndCreateMarkers().catch(error => {
                        console.error('Error reloading markers:', error);
                      });
                    } catch (error) {
                      console.error('Error saving tags:', error);
                      alert('Error saving tags. Please try again.');
                    } finally {
                      setIsSavingTags(false);
                    }
                  }}
                  disabled={isSavingTags}
                  style={{
                    padding: '8px 16px',
                    background: isSavingTags ? '#ccc' : '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isSavingTags ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: isSavingTags ? 0.7 : 1
                  }}
                >
                  {isSavingTags ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingTags(false);
                    setEditingTags([]);
                  }}
                  disabled={isSavingTags}
                  style={{
                    padding: '8px 16px',
                    background: '#f5f5f5',
                    color: '#333',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    cursor: isSavingTags ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: isSavingTags ? 0.7 : 1
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="tags-container">
              {currentProfile.tagsList && currentProfile.tagsList.length > 0 ? (
                currentProfile.tagsList.map((tag, index) => {
                  const isOwnProfile = isMyProfile();
                  const isPublished = isOwnProfile ? publishedTags.includes(tag) : false; // For other users, always show as unpublished (gray)
                  const notificationKey = `tag-${index}`;
                  const showNotification = isOwnProfile && showPublishedNotification && showPublishedNotification.key === notificationKey;
                  const showCopiedTag = !isOwnProfile && copiedTagIndex === index;
                  
                  // Check if tag matches with my tags (for other users' profiles)
                  const tagMatches = !isOwnProfile && myProfileForMatch && myProfileForMatch.tagsList
                    ? myProfileForMatch.tagsList.some(myTag => myTag.toLowerCase().trim() === tag.toLowerCase().trim())
                    : false;
                  
                  return (
                    <div key={index} style={{ position: 'relative', display: 'inline-block', marginRight: '8px', marginBottom: '8px' }}>
                      <button
                        onClick={async () => {
                          if (isOwnProfile) {
                            updatePublishStatus('tag', index, !isPublished);
                          } else {
                            // For other users, copy tag to clipboard
                            await copyToClipboard(tag);
                            setCopiedTagIndex(index);
                            setTimeout(() => {
                              setCopiedTagIndex(null);
                            }, 2000);
                          }
                        }}
                        disabled={isUpdatingPublish}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: tagMatches ? '2px solid #4a90e2' : (recentlyChanged.tags && recentlyChanged.tags.has(tag) ? '2px solid #ffcccc' : 'none'),
                          borderRadius: '10px',
                          cursor: isUpdatingPublish ? 'not-allowed' : 'pointer',
                          backgroundColor: isOwnProfile && isPublished ? '#e0f0ff' : '#f5f5f5', // Always gray for other users
                          color: isOwnProfile && isPublished ? '#0066cc' : '#333', // Always dark gray for other users
                          transition: 'all 0.2s ease',
                          opacity: isUpdatingPublish ? 0.6 : 1,
                          fontFamily: 'inherit',
                          whiteSpace: 'nowrap',
                          position: 'relative'
                        }}
                      >
                        <Tooltip text={isOwnProfile ? (isPublished ? 'Published - visible to others' : 'Not published - hidden from others') : 'Click to copy tag'}>
                          <span style={{ display: 'inline-block' }}>{tag}</span>
                        </Tooltip>
                        {tagMatches && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#4a90e2',
                              backgroundColor: 'white',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #4a90e2',
                              lineHeight: 1
                            }}
                          >
                            match
                          </span>
                        )}
                        {recentlyChanged.tags && recentlyChanged.tags.has(tag) && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#cc2d2d',
                              backgroundColor: '#ffecec',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #ffc8c8',
                              lineHeight: 1
                            }}
                          >
                            change
                          </span>
                        )}
                      </button>
                      {showCopiedTag && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#4a90e2',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          Copied
                        </div>
                      )}
                      {showNotification && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <span style={{ color: '#999', fontSize: '13px' }}>No tags</span>
              )}
            </div>
          )}
          </div>


          <div className="profile-section">
            <div className="section-title">
              {currentUser && currentProfile.username === currentUser ? 'My Socials' : 'Socials'}
              {currentUser && currentProfile.username === currentUser && !isEditingSocials && (
                <span 
                  className="edit-icon"
                  onClick={() => {
                    // Initialize editing socials with current social links
                    const socials = currentProfile.socialLinks || [];
                    setEditingSocials(socials.length > 0 ? [...socials] : ['']);
                    setIsEditingSocials(true);
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </span>
              )}
            </div>
            {isEditingSocials && currentUser && currentProfile.username === currentUser ? (
              <div style={{ marginTop: '10px', position: 'relative', padding: '8px' }} className={socialsChanged ? 'changed-outline' : ''}>
                {socialsChanged && <span className="change-pill">change</span>}
                {editingSocials.map((social, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      ref={(el) => {
                        if (el) socialInputRefs.current[`edit-${index}`] = el;
                      }}
                      type="text"
                      value={social}
                      onChange={(e) => {
                        const newSocials = [...editingSocials];
                        newSocials[index] = e.target.value;
                        setEditingSocials(newSocials);
                      }}
                      placeholder="Enter a social link"
                      style={{ 
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'inherit'
                      }}
                    />
                    {index === editingSocials.length - 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newIndex = editingSocials.length;
                          setEditingSocials([...editingSocials, '']);
                          // Focus the new input after state update
                          setTimeout(() => {
                            if (socialInputRefs.current[`edit-${newIndex}`]) {
                              socialInputRefs.current[`edit-${newIndex}`].focus();
                            }
                          }, 0);
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#4a90e2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }}
                      >
                        +
                      </button>
                    )}
                  </div>
                ))}
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <button
                  onClick={async () => {
                    if (isSavingSocials) return; // Prevent multiple saves
                    setIsSavingSocials(true);
                    
                    try {
                      // Parse socials (array, filter empty)
                      const socialLinks = editingSocials
                        .map(social => social.trim())
                        .filter(social => social.length > 0);

                      // Normalize social links for duplicate checking (remove http/https, trailing slashes, lowercase)
                      const normalizeSocial = (link) => {
                        return link
                          .toLowerCase()
                          .replace(/^https?:\/\//, '')
                          .replace(/\/$/, '');
                      };
                      
                      const normalizedLinks = socialLinks.map(normalizeSocial);
                      const duplicates = socialLinks.filter((link, index) => 
                        normalizedLinks.indexOf(normalizeSocial(link)) !== index
                      );
                      
                      if (duplicates.length > 0) {
                        alert(`Duplicate social links are not allowed. Please remove duplicates: ${duplicates.join(', ')}`);
                        setIsSavingSocials(false);
                        return;
                      }

                      // Update publishedSocials to only include socials that exist in the new socialLinks
                      // This ensures that if a social was removed, it's also removed from publishedSocials
                      const updatedPublishedSocials = publishedSocials.filter(social => socialLinks.includes(social));
                      setPublishedSocials(updatedPublishedSocials);

                      // Update profile with new social links
                      const updatedProfile = {
                        ...currentProfile,
                        socialLinks: socialLinks
                      };
                      setCurrentProfile(updatedProfile);
                      
                      // Encrypt and save to IPFS
                      const result = await encryptAndSaveProfile(updatedProfile);
                      
                      if (result?.encryptedData) {
                        setEncryptedProfileData(result.encryptedData);
                      }

                      // Mark visual changes immediately (before global commit) - only mark socials as changed
                      markRecentlyChanged(updatedProfile, ['socials']);
                      
                      setIsEditingSocials(false);
                      
                      // Update currentProfile with socialLinks and ipfsHash
                      if (result?.ipfsHash) {
                        setCurrentProfile(prev => ({
                          ...prev,
                          socialLinks: socialLinks, // Preserve socialLinks field
                          ipfsHash: result.ipfsHash
                        }));
                      } else {
                        // Even if ipfsHash update failed, ensure socialLinks is in state
                        setCurrentProfile(prev => ({
                          ...prev,
                          socialLinks: socialLinks
                        }));
                      }
                      // Reload markers in background (don't reload profile to avoid state reset)
                      loadAllProfilesAndCreateMarkers().catch(error => {
                        console.error('Error reloading markers:', error);
                      });
                      
                      // Check for X.com and Discord connections and award badges/points
                      if (walletAddress) {
                        try {
                          const connectionResult = await checkSocialConnections(walletAddress, socialLinks);
                          if (connectionResult.newBadges.length > 0 || connectionResult.pointsAwarded > 0) {
                            // Reload gamification data to show new badges/points
                            const updatedGamificationData = await getUserGamificationData(walletAddress);
                            if (updatedGamificationData) {
                              setGamificationData(updatedGamificationData);
                            }
                            
                            // Show notification
                            const badgeNames = connectionResult.newBadges.map(b => b.name).join(', ');
                            const message = connectionResult.newBadges.length > 0 
                              ? `🎉 You earned ${connectionResult.pointsAwarded} points and unlocked: ${badgeNames}!`
                              : `🎉 You earned ${connectionResult.pointsAwarded} points!`;
                            alert(message);
                          }
                        } catch (connectionError) {
                          console.error('Error checking social connections:', connectionError);
                          // Don't show error to user, just log it
                        }
                      }
                    } catch (error) {
                      console.error('Error saving social links:', error);
                      alert('Error saving social links. Please try again.');
                    } finally {
                      setIsSavingSocials(false);
                    }
                  }}
                  disabled={isSavingSocials}
                  style={{
                    padding: '8px 16px',
                    background: isSavingSocials ? '#ccc' : '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isSavingSocials ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: isSavingSocials ? 0.7 : 1
                  }}
                >
                  {isSavingSocials ? 'Saving...' : 'Save'}
                </button>
                  <button
                    onClick={() => {
                      setIsEditingSocials(false);
                      setEditingSocials([]);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '10px' }}>
                {(currentProfile.socialLinks && currentProfile.socialLinks.length > 0) ? (
                  currentProfile.socialLinks.map((link, index) => {
                    const isPublished = publishedSocials.includes(link);
                    const notificationKey = `social-${index}`;
                    const showNotification = showPublishedNotification && showPublishedNotification.key === notificationKey;
                    return (
                      <div key={index} style={{ 
                        marginBottom: '8px',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {currentUser && currentProfile.username === currentUser ? (
                          <button
                            onClick={() => updatePublishStatus('social', index, !isPublished)}
                            disabled={isUpdatingPublish}
                            style={{
                              padding: '8px 16px',
                              fontSize: '14px',
                              fontWeight: 'normal',
                              border: (recentlyChanged.socials && recentlyChanged.socials.has(link)) ? '2px solid #ffcccc' : 'none',
                              borderRadius: '10px',
                              cursor: isUpdatingPublish ? 'not-allowed' : 'pointer',
                              backgroundColor: isPublished ? '#e0f0ff' : '#f5f5f5',
                              color: isPublished ? '#0066cc' : '#333',
                              transition: 'all 0.2s ease',
                              opacity: isUpdatingPublish ? 0.6 : 1,
                              fontFamily: 'inherit',
                              flex: 1,
                              textAlign: 'left',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Tooltip text={link}>
                              <span style={{ display: 'inline-block' }}>{link}</span>
                            </Tooltip>
                            {(recentlyChanged.socials && recentlyChanged.socials.has(link)) && (
                              <span
                                style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '-4px',
                                  fontSize: '9px',
                                  fontWeight: '700',
                                  color: '#cc2d2d',
                                  backgroundColor: '#ffecec',
                                  padding: '1px 4px',
                                  borderRadius: '8px',
                                  border: '1px solid #ffc8c8',
                                  lineHeight: 1
                                }}
                              >
                                change
                              </span>
                            )}
                          </button>
                        ) : (
                          <a 
                            href={link.startsWith('http') ? link : `https://${link}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                              padding: '8px 16px',
                              fontSize: '14px',
                              fontWeight: 'normal',
                              border: 'none',
                              borderRadius: '10px',
                              backgroundColor: '#f5f5f5',
                              color: '#333',
                              textDecoration: 'none',
                              display: 'inline-block',
                              flex: 1,
                              textAlign: 'left',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Tooltip text={link}>
                              <span style={{ display: 'inline-block' }}>{link}</span>
                            </Tooltip>
                          </a>
                        )}
                        {showNotification && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '-28px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap',
                              zIndex: 1000,
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                            }}
                          >
                            {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <span style={{ color: '#999', fontSize: '13px' }}>No social links</span>
                )}
              </div>
            )}
          </div>

          <div className="profile-section">
            <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {currentUser && currentProfile.username === currentUser ? 'My music' : 'Music'}
              {currentUser && currentProfile.username === currentUser && !isEditingMusic && (
                <span 
                  className="edit-icon"
                  onClick={() => {
                    // Initialize editing music with current music list
                    const music = currentProfile.musicList || [];
                    setEditingMusic(music.length > 0 ? [...music] : []);
                    setIsEditingMusic(true);
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </span>
              )}
            </div>
            {isEditingMusic && currentUser && currentProfile.username === currentUser ? (
              <div style={{ marginTop: '10px', position: 'relative', padding: '8px' }} className={(recentlyChanged.music && recentlyChanged.music.size > 0) ? 'changed-outline' : ''}>
                {(recentlyChanged.music && recentlyChanged.music.size > 0) && <span className="change-pill">change</span>}
                
                {/* Music Search */}
                <div style={{ marginBottom: '16px', position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={musicSearchQuery}
                      onChange={(e) => setMusicSearchQuery(e.target.value)}
                      placeholder="🎵 Search songs, artists, albums..."
                      style={{
                        width: '100%',
                        padding: '12px 40px 12px 16px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '10px',
                        fontSize: '15px',
                        fontFamily: 'inherit',
                        outline: 'none',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#4a90e2';
                        e.target.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e0e0e0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    {/* Search/Loading indicator */}
                    <div style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#999',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {isSearchingMusic ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </svg>
                      ) : musicSearchQuery ? (
                        <span 
                          onClick={() => {
                            setMusicSearchQuery('');
                            setMusicSearchResults([]);
                          }}
                          style={{ cursor: 'pointer', fontSize: '18px', padding: '2px' }}
                          title="Clear search"
                        >
                          ✕
                        </span>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8" />
                          <path d="M21 21l-4.35-4.35" />
                        </svg>
                      )}
                    </div>
                  </div>
                  {musicSearchQuery && musicSearchQuery.length < 2 && (
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px', paddingLeft: '4px' }}>
                      Type at least 2 characters to search...
                    </div>
                  )}
                  
                  {/* Search Results */}
                  {musicSearchResults.length > 0 && (
                    <div style={{
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      backgroundColor: '#fafafa',
                      marginBottom: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                      {musicSearchResults.map((result, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            // For custom entries, use just the title (remove "- Add as custom entry")
                            let songString;
                            if (result.isManual) {
                              songString = result.title.trim();
                            } else {
                              songString = `${result.title} - ${result.artist}`;
                            }
                            if (!editingMusic.includes(songString)) {
                              setEditingMusic([...editingMusic, songString]);
                            }
                            setMusicSearchQuery('');
                            setMusicSearchResults([]);
                          }}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: idx < musicSearchResults.length - 1 ? '1px solid #eee' : 'none',
                            transition: 'background-color 0.15s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8f4fd'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {/* Album Artwork */}
                          {result.artwork ? (
                            <img 
                              src={result.artwork} 
                              alt={result.album || result.title}
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '6px',
                                objectFit: 'cover',
                                flexShrink: 0,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '6px',
                              backgroundColor: result.isManual ? '#e0e0e0' : '#ddd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              fontSize: '18px'
                            }}>
                              {result.isManual ? '✏️' : '🎵'}
                            </div>
                          )}
                          {/* Song Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              fontWeight: '600', 
                              fontSize: '14px', 
                              color: '#222',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {result.title}
                            </div>
                            <div style={{ 
                              fontSize: '13px', 
                              color: '#666', 
                              marginTop: '2px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {result.artist}
                            </div>
                            {result.album && !result.isManual && (
                              <div style={{ 
                                fontSize: '11px', 
                                color: '#999', 
                                marginTop: '2px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {result.album}
                              </div>
                            )}
                          </div>
                          {/* Add indicator */}
                          <div style={{
                            fontSize: '12px',
                            color: '#4a90e2',
                            fontWeight: '500',
                            flexShrink: 0
                          }}>
                            + Add
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Show music entries - only added via searcher, no manual input fields */}
                {editingMusic.length === 0 ? (
                  <div style={{ color: '#999', fontSize: '13px', marginTop: '10px' }}>No music added. Use the search above to add songs.</div>
                ) : (
                  editingMusic.map((song, index) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                      <div 
                        style={{ 
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          backgroundColor: '#f9f9f9',
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={song}
                      >
                        {truncateSongName(song)}
                      </div>
                      {/* Show Remove button for ALL entries */}
                      <button
                        type="button"
                        onClick={() => {
                          setEditingMusic(editingMusic.filter((_, i) => i !== index));
                        }}
                        style={{
                          padding: '8px 12px',
                          background: '#ff4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button
                    onClick={async () => {
                      if (isSavingMusic) return;
                      setIsSavingMusic(true);
                      
                      try {
                        // Parse music (array, filter empty)
                        const musicList = editingMusic
                          .map(song => song.trim())
                          .filter(song => song.length > 0);

                        // Check for duplicates (case-insensitive)
                        const musicLower = musicList.map(song => song.toLowerCase());
                        const duplicates = musicList.filter((song, index) => musicLower.indexOf(song.toLowerCase()) !== index);
                        
                        if (duplicates.length > 0) {
                          alert(`Duplicate songs are not allowed. Please remove duplicates: ${duplicates.join(', ')}`);
                          setIsSavingMusic(false);
                          return;
                        }

                        // Update publishedMusic to only include music that exists in the new musicList
                        const updatedPublishedMusic = publishedMusic.filter(song => musicList.includes(song));
                        setPublishedMusic(updatedPublishedMusic);

                        // Update profile with new music list
                        const updatedProfile = {
                          ...currentProfile,
                          musicList: musicList
                        };
                        setCurrentProfile(updatedProfile);
                        
                        // Encrypt and save to IPFS
                        const result = await encryptAndSaveProfile(updatedProfile);
                        
                        if (result?.encryptedData) {
                          setEncryptedProfileData(result.encryptedData);
                        }

                        // Mark visual changes immediately
                        markRecentlyChanged(updatedProfile, ['music']);
                        
                        setIsEditingMusic(false);
                        
                        if (result?.ipfsHash) {
                          setCurrentProfile(prev => ({
                            ...prev,
                            musicList: musicList,
                            ipfsHash: result.ipfsHash
                          }));
                        } else {
                          setCurrentProfile(prev => ({
                            ...prev,
                            musicList: musicList
                          }));
                        }
                        loadAllProfilesAndCreateMarkers().catch(error => {
                          console.error('Error reloading markers:', error);
                        });
                      } catch (error) {
                        console.error('Error saving music:', error);
                        alert('Error saving music. Please try again.');
                      } finally {
                        setIsSavingMusic(false);
                      }
                    }}
                    disabled={isSavingMusic}
                    style={{
                      padding: '8px 16px',
                      background: isSavingMusic ? '#ccc' : '#4a90e2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isSavingMusic ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isSavingMusic ? 0.7 : 1
                    }}
                  >
                    {isSavingMusic ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingMusic(false);
                      setEditingMusic([]);
                      setMusicSearchQuery('');
                      setMusicSearchResults([]);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '10px' }}>
                {(currentProfile.musicList && currentProfile.musicList.length > 0) ? (
                  currentProfile.musicList.map((song, index) => {
                    const isOwnProfile = isMyProfile();
                    const isPublished = isOwnProfile ? publishedMusic.includes(song) : false;
                    const notificationKey = `music-${index}`;
                    const showNotification = isOwnProfile && showPublishedNotification && showPublishedNotification.key === notificationKey;
                    
                    // Check if song matches with my music (for other users' profiles)
                    const songMatches = !isOwnProfile && myProfileForMatch && myProfileForMatch.musicList
                      ? myProfileForMatch.musicList.some(mySong => mySong.toLowerCase().trim() === song.toLowerCase().trim())
                      : false;
                    
                    return (
                      <div key={index} style={{ position: 'relative', display: 'inline-block', marginRight: '8px', marginBottom: '8px' }}>
                        <button
                          onClick={async () => {
                            if (isOwnProfile) {
                              updatePublishStatus('music', index, !isPublished);
                            } else {
                              // For other users, copy song to clipboard
                              await copyToClipboard(song);
                              setTimeout(() => {
                                // Could show a notification here
                              }, 2000);
                            }
                          }}
                          disabled={isUpdatingPublish}
                          style={{
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: 'normal',
                            border: songMatches ? '2px solid #4a90e2' : (recentlyChanged.music && recentlyChanged.music instanceof Set && recentlyChanged.music.has(song) ? '2px solid #ffcccc' : 'none'),
                            borderRadius: '10px',
                            cursor: isUpdatingPublish ? 'not-allowed' : 'pointer',
                            backgroundColor: isOwnProfile && isPublished ? '#e0f0ff' : '#f5f5f5',
                            color: isOwnProfile && isPublished ? '#0066cc' : '#333',
                            transition: 'all 0.2s ease',
                            opacity: isUpdatingPublish ? 0.6 : 1,
                            fontFamily: 'inherit',
                            whiteSpace: 'nowrap',
                            position: 'relative'
                          }}
                        >
                          <Tooltip text={isOwnProfile ? (isPublished ? 'Published - visible to others' : 'Not published - hidden from others') : (song.length > 40 ? song : 'Click to copy song')}>
                            <span style={{ display: 'inline-block' }}>{truncateSongName(song)}</span>
                          </Tooltip>
                          {songMatches && (
                            <span
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-4px',
                                fontSize: '9px',
                                fontWeight: '700',
                                color: '#4a90e2',
                                backgroundColor: 'white',
                                padding: '1px 4px',
                                borderRadius: '8px',
                                border: '1px solid #4a90e2',
                                lineHeight: 1
                              }}
                            >
                              match
                            </span>
                          )}
                          {(recentlyChanged.music && recentlyChanged.music instanceof Set && recentlyChanged.music.has(song)) && (
                            <span
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-4px',
                                fontSize: '9px',
                                fontWeight: '700',
                                color: '#cc2d2d',
                                backgroundColor: '#ffecec',
                                padding: '1px 4px',
                                borderRadius: '8px',
                                border: '1px solid #ffc8c8',
                                lineHeight: 1
                              }}
                            >
                              change
                            </span>
                          )}
                        </button>
                        {showNotification && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '-28px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap',
                              zIndex: 1000,
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                            }}
                          >
                            {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <span style={{ color: '#999', fontSize: '13px' }}>No music</span>
                )}
              </div>
            )}
          </div>

          <div className="profile-section">
            <div className="section-title">
              {currentUser && currentProfile.username === currentUser ? 'My groups' : 'Groups'}
            </div>
            <div className="groups-container">
              {profileGroups && profileGroups.length > 0 ? (
                profileGroups.map((group, index) => {
                  const isOwnProfile = isMyProfile();
                  const isPublished = isOwnProfile ? publishedGroups.includes(group) : false; // For other users, always show as grey (unpublished style)
                  const notificationKey = `group-${index}`;
                  const showNotification = isOwnProfile && showPublishedNotification && showPublishedNotification.key === notificationKey;
                  
                  // Check if group matches with viewer's groups (for other users' profiles)
                  const groupMatches = !isOwnProfile && walletAddress && viewerGroups.length > 0
                    ? viewerGroups.some(viewerGroup => viewerGroup.toLowerCase().trim() === group.toLowerCase().trim())
                    : false;
                  
                  return (
                    <div key={index} style={{ position: 'relative', display: 'inline-block', marginRight: '8px', marginBottom: '8px' }}>
                      <button
                        onClick={async () => {
                          if (isOwnProfile) {
                            updatePublishStatus('group', index, !isPublished);
                          }
                        }}
                        disabled={isUpdatingPublish}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: groupMatches ? '2px solid #4a90e2' : 'none',
                          borderRadius: '10px',
                          cursor: isOwnProfile && !isUpdatingPublish ? 'pointer' : 'default',
                          backgroundColor: isOwnProfile && isPublished ? '#e0f0ff' : '#f5f5f5', // Always grey for other users
                          color: isOwnProfile && isPublished ? '#0066cc' : '#333', // Always dark grey for other users
                          transition: 'all 0.2s ease',
                          opacity: isUpdatingPublish ? 0.6 : 1,
                          fontFamily: 'inherit',
                          whiteSpace: 'nowrap',
                          position: 'relative'
                        }}
                      >
                        <Tooltip text={isOwnProfile ? (isPublished ? 'Published - visible to others' : 'Not published - hidden from others') : ''}>
                          <span style={{ display: 'inline-block' }}>{group}</span>
                        </Tooltip>
                        {groupMatches && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#4a90e2',
                              backgroundColor: 'white',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #4a90e2',
                              lineHeight: 1
                            }}
                          >
                            match
                          </span>
                        )}
                        {showNotification && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '-28px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap',
                              zIndex: 1000,
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                            }}
                          >
                            {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                          </div>
                        )}
                      </button>
                    </div>
                  );
                })
              ) : (
                <span style={{ color: '#999', fontSize: '11px' }}>-</span>
              )}
            </div>
          </div>


          <div className="profile-section">
            <div className="section-title">Personal data</div>
            {(() => {
              // Check if there's any visible Personal data for other users
              const isOwnProfile = isMyProfile();
              const hasVisibleData = isOwnProfile || 
                currentProfile.walletAddress || 
                currentProfile.email || 
                currentProfile.preferredContactMethod || 
                currentProfile.relationshipStatus || 
                currentProfile.lookingFor || 
                currentProfile.languages || 
                currentProfile.birthDate || 
                currentProfile.education || 
                currentProfile.occupation || 
                (currentProfile.additionalPersonalData && currentProfile.additionalPersonalData.length > 0);
              
              if (!hasVisibleData) {
                return <span style={{ color: '#999', fontSize: '11px' }}>-</span>;
              }
              
              return (
                <>
            {/* Wallet in Personal data */}
            {currentUser && currentProfile.username === currentUser ? (
              <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <button
                    onClick={() => updatePublishStatus('wallet', null, !publishedWallet)}
                    disabled={isUpdatingPublish}
                    style={{
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: 'normal',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: isUpdatingPublish ? 'not-allowed' : 'pointer',
                      backgroundColor: publishedWallet ? '#e0f0ff' : '#f5f5f5',
                      color: publishedWallet ? '#0066cc' : '#333',
                      transition: 'all 0.2s ease',
                      opacity: isUpdatingPublish ? 0.6 : 1,
                      fontFamily: 'inherit',
                      textAlign: 'left'
                    }}
                  >
                    <Tooltip
                      text={publishedWallet ? 'Published - visible to others' : 'Not published - hidden from others'}
                    >
                      <span style={{ display: 'inline-block' }}>Wallet: {walletAddress ? formatAddress(walletAddress) : 'Not connected'}</span>
                    </Tooltip>
                  </button>
                  {showPublishedNotification && showPublishedNotification.key === 'wallet-single' && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '-28px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              currentProfile.walletAddress ? (
                <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                  <div style={{ position: 'relative' }}>
                    <div
                      onClick={() => copyToClipboard(currentProfile.walletAddress)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: 'normal',
                        borderRadius: '10px',
                        backgroundColor: '#f5f5f5',
                        color: '#333',
                        display: 'inline-block',
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Tooltip text="Click to copy full address">
                        <span style={{ display: 'inline-block' }}>Wallet: {shortenWalletAddress(currentProfile.walletAddress)}</span>
                      </Tooltip>
                    </div>
                    {copiedVisible && (
                      <span className="copied-popup" style={{ position: 'absolute', top: '-25px', left: '0', whiteSpace: 'nowrap' }}>(copied)</span>
                    )}
                  </div>
                  {walletAddress && (
                    <div style={{ marginTop: '6px' }}>
                      <button
                        onClick={() => openTransferModal(currentProfile)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '13px',
                          fontWeight: 600,
                          border: 'none',
                          borderRadius: '10px',
                          backgroundColor: '#4a90e2',
                          color: '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        Send USDC
                      </button>
                    </div>
                  )}
                </div>
              ) : null
            )}
            
            {/* Email in Personal data */}
            {(currentUser && currentProfile.username === currentUser) || currentProfile.email ? (
              isEditingEmail && currentUser && currentProfile.username === currentUser ? (
                <div style={{ marginTop: '10px', position: 'relative', marginBottom: '8px' }} className={recentlyChanged.email ? 'changed-outline' : ''}>
                  {recentlyChanged.email && <span className="change-pill">change</span>}
                  <input
                    type="email"
                    value={editingEmail}
                    onChange={(e) => setEditingEmail(e.target.value)}
                    placeholder="Enter email address (required)"
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button
                      onClick={async () => {
                        if (isSavingEmail) return;
                        setIsSavingEmail(true);
                        
                        try {
                          const newEmail = editingEmail.trim();
                          
                          // Email is required - cannot be empty
                          if (!newEmail) {
                            setNotification({ message: 'Email is required and cannot be deleted. Please enter a valid email address.', type: 'error' });
                            setTimeout(() => setNotification(null), 5000);
                            setIsSavingEmail(false);
                            return;
                          }
                          
                          // Basic email validation
                          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
                            setNotification({ message: 'Please enter a valid email address', type: 'error' });
                            setTimeout(() => setNotification(null), 5000);
                            setIsSavingEmail(false);
                            return;
                          }

                          // Always require email verification before saving (even for same email)
                          if (!isEmailRecentlyVerified(newEmail, 'change')) {
                            setIsSavingEmail(false);
                            return openEmailVerificationModal({
                              email: newEmail,
                              purpose: 'change',
                              onVerified: async () => {
                                // After verification, automatically save the email
                                try {
                                  setIsSavingEmail(true);
                                  const updatedProfile = {
                                    ...currentProfile,
                                    email: newEmail,
                                    emailVerifiedAt: new Date().toISOString()
                                  };
                                  setCurrentProfile(updatedProfile);
                                  await encryptAndSaveProfile(updatedProfile);
                                  setIsEditingEmail(false);
                                  setEditingEmail('');
                                  setRecentlyChanged(prev => ({ ...prev, email: true }));
                                  setNotification({ message: 'Email verified and saved successfully', type: 'success' });
                                } catch (error) {
                                  console.error('Error saving email after verification:', error);
                                  setNotification({ message: 'Email verified but failed to save', type: 'error' });
                                } finally {
                                  setIsSavingEmail(false);
                                }
                              }
                            });
                          }
                          
                          // Update profile with new email (email is always set, never null)
                          const updatedProfile = {
                            ...currentProfile,
                            email: newEmail,
                            emailVerifiedAt: new Date().toISOString()
                          };
                          
                          // Update current profile state immediately
                          setCurrentProfile(updatedProfile);
                          
                          // Save to IPFS
                          await encryptAndSaveProfile(updatedProfile);
                          
                          // Clear editing state
                          setIsEditingEmail(false);
                          setEditingEmail('');
                          
                          // Mark as recently changed
                          setRecentlyChanged(prev => ({ ...prev, email: true }));
                          
                          setNotification({ message: 'Email saved successfully', type: 'success' });
                        } catch (error) {
                          console.error('Error saving email:', error);
                          setNotification({ message: 'Error saving email', type: 'error' });
                        } finally {
                          setIsSavingEmail(false);
                        }
                      }}
                      disabled={isSavingEmail || !editingEmail.trim()}
                      style={{
                        padding: '8px 16px',
                        background: (isSavingEmail || !editingEmail.trim()) ? '#ccc' : '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (isSavingEmail || !editingEmail.trim()) ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: (isSavingEmail || !editingEmail.trim()) ? 0.7 : 1
                      }}
                    >
                      {isSavingEmail ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingEmail(false);
                        setEditingEmail('');
                      }}
                      disabled={isSavingEmail}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: isSavingEmail ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingEmail ? 0.7 : 1
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                  {currentProfile.email ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={() => {
                          const isOwnProfile = isMyProfile();
                          if (isOwnProfile && currentProfile.email) {
                            updatePublishStatus('email', null, !publishedEmail);
                          } else if (!isOwnProfile) {
                            copyToClipboard(currentProfile.email);
                          }
                        }}
                        disabled={isUpdatingPublish || !currentProfile.email || !(currentUser && currentProfile.username === currentUser)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: recentlyChanged.email ? '2px solid #ffcccc' : 'none',
                          borderRadius: '10px',
                          cursor: (isUpdatingPublish || !currentProfile.email || !(currentUser && currentProfile.username === currentUser)) ? 'not-allowed' : 'pointer',
                          backgroundColor: (currentUser && currentProfile.username === currentUser && publishedEmail) ? '#e0f0ff' : '#f5f5f5',
                          color: (currentUser && currentProfile.username === currentUser && publishedEmail) ? '#0066cc' : '#333',
                          transition: 'all 0.2s ease',
                          opacity: (isUpdatingPublish || !currentProfile.email) ? 0.6 : 1,
                          fontFamily: 'inherit',
                          textAlign: 'left'
                        }}
                      >
                        <Tooltip
                          text={(currentUser && currentProfile.username === currentUser) ? (publishedEmail ? 'Published - visible to others' : 'Not published - hidden from others') : 'Click to copy email'}
                        >
                          <span style={{ display: 'inline-block' }}>Email: {currentProfile.email}</span>
                        </Tooltip>
                        {recentlyChanged.email && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#cc2d2d',
                              backgroundColor: '#ffecec',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #ffc8c8',
                              lineHeight: 1
                            }}
                          >
                            change
                          </span>
                        )}
                      </button>
                      {showPublishedNotification && showPublishedNotification.key === 'email-single' && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                        </div>
                      )}
                    </div>
                  ) : (
                    currentUser && currentProfile.username === currentUser ? (
                      <span style={{ color: '#999', fontSize: '13px' }}>
                        Email not specified
                      </span>
                    ) : null
                  )}
                  {currentUser && currentProfile.username === currentUser && (
                    <span 
                      className="edit-icon" 
                      onClick={() => {
                        setEditingEmail(currentProfile.email || '');
                        setIsEditingEmail(true);
                      }}
                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </span>
                  )}
                </div>
              )
            ) : null}
            
            {isEditingBirthDate && currentUser && currentProfile.username === currentUser ? (
              <div style={{ marginTop: '0px', marginBottom: '0px', position: 'relative', padding: '8px' }} className={birthDateChanged ? 'changed-outline' : ''}>
                {birthDateChanged && <span className="change-pill">change</span>}
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#333', fontWeight: '500' }}>
                  Birth date:
                </label>
                <input
                  type="date"
                  value={editingBirthDate}
                  onChange={(e) => setEditingBirthDate(e.target.value)}
                  lang="en"
                  data-lang="en"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button
                    onClick={async () => {
                      if (!editingBirthDate) {
                        alert('Please select a birth date');
                        return;
                      }
                      
                      // Calculate age
                      let age = 0;
                      try {
                        const birth = new Date(editingBirthDate + 'T00:00:00');
                        const today = new Date();
                        age = today.getFullYear() - birth.getFullYear();
                        const monthDiff = today.getMonth() - birth.getMonth();
                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                          age--;
                        }
                        // Format as DD.MM.YYYY
                        const day = String(birth.getDate()).padStart(2, '0');
                        const month = String(birth.getMonth() + 1).padStart(2, '0');
                        const birthDateFormatted = `${day}.${month}.${birth.getFullYear()}`;
                        
                        // Update profile
                        const updatedProfile = {
                          ...currentProfile,
                          birthDate: birthDateFormatted,
                          age: age
                        };
                        setCurrentProfile(updatedProfile);
                        
                        // Encrypt and save to IPFS
                        setIsSavingBirthDate(true);
                        try {
                          const result = await encryptAndSaveProfile(updatedProfile);
                          
                          if (result?.encryptedData) {
                            setEncryptedProfileData(result.encryptedData);
                          }

                          // Mark visual changes immediately (before global commit) - only mark birthDate as changed
                          markRecentlyChanged(updatedProfile, ['birthDate']);
                          
                          setIsEditingBirthDate(false);
                          
                          // Update currentProfile with birthDate/age and ipfsHash
                          if (result?.ipfsHash) {
                            setCurrentProfile(prev => ({
                              ...prev,
                              birthDate: birthDateFormatted, // Preserve birthDate field
                              age: age, // Preserve age field
                              ipfsHash: result.ipfsHash
                            }));
                          } else {
                            // Even if ipfsHash update failed, ensure birthDate/age are in state
                            setCurrentProfile(prev => ({
                              ...prev,
                              birthDate: birthDateFormatted,
                              age: age
                            }));
                          }
                          // Reload markers in background (don't reload profile to avoid state reset)
                          loadAllProfilesAndCreateMarkers().catch(error => {
                            console.error('Error reloading markers:', error);
                          });
                        } catch (error) {
                          console.error('Error saving birth date:', error);
                          alert('Error saving birth date. Please try again.');
                        } finally {
                          setIsSavingBirthDate(false);
                        }
                      } catch (error) {
                        console.error('Error parsing birth date:', error);
                        alert('Invalid birth date format');
                      }
                    }}
                    disabled={isSavingBirthDate}
                    style={{
                      padding: '8px 16px',
                      background: isSavingBirthDate ? '#ccc' : '#4a90e2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isSavingBirthDate ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isSavingBirthDate ? 0.7 : 1
                    }}
                  >
                    {isSavingBirthDate ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingBirthDate(false);
                      setEditingBirthDate('');
                    }}
                    disabled={isSavingBirthDate}
                    style={{
                      padding: '8px 16px',
                      background: 'white',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: isSavingBirthDate ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isSavingBirthDate ? 0.7 : 1
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                {currentProfile.birthDate ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      onClick={() => {
                        const isOwnProfile = isMyProfile();
                        if (isOwnProfile && currentProfile.birthDate) {
                          updatePublishStatus('birthDate', null, !publishedBirthDate);
                        }
                      }}
                      disabled={isUpdatingPublish || !currentProfile.birthDate || !(currentUser && currentProfile.username === currentUser)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: 'normal',
                        border: recentlyChanged.birthDate ? '2px solid #ffcccc' : 'none',
                        borderRadius: '10px',
                        cursor: (isUpdatingPublish || !currentProfile.birthDate || !(currentUser && currentProfile.username === currentUser)) ? 'not-allowed' : 'pointer',
                        backgroundColor: (currentUser && currentProfile.username === currentUser && publishedBirthDate) ? '#e0f0ff' : '#f5f5f5', // Always gray for other users
                        color: (currentUser && currentProfile.username === currentUser && publishedBirthDate) ? '#0066cc' : '#333', // Always dark gray for other users
                        transition: 'all 0.2s ease',
                        opacity: (isUpdatingPublish || !currentProfile.birthDate) ? 0.6 : 1,
                        fontFamily: 'inherit',
                        textAlign: 'left'
                      }}
                    >
                      <Tooltip
                        text={(currentUser && currentProfile.username === currentUser) ? (publishedBirthDate ? 'Published - visible to others' : 'Not published - hidden from others') : ''}
                      >
                        <span style={{ display: 'inline-block' }}>Birth date: {currentProfile.birthDate}{currentProfile.age != null ? ` (${currentProfile.age} years old)` : ''}</span>
                      </Tooltip>
                      {recentlyChanged.birthDate && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-4px',
                            fontSize: '9px',
                            fontWeight: '700',
                            color: '#cc2d2d',
                            backgroundColor: '#ffecec',
                            padding: '1px 4px',
                            borderRadius: '8px',
                            border: '1px solid #ffc8c8',
                            lineHeight: 1
                          }}
                        >
                          change
                        </span>
                      )}
                    </button>
                    {showPublishedNotification && showPublishedNotification.key === 'birthDate-single' && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '-28px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                        }}
                      >
                        {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                      </div>
                    )}
                  </div>
                ) : (
                  currentUser && currentProfile.username === currentUser ? (
                    <span style={{ color: '#999', fontSize: '13px' }}>
                      Birth date not specified
                    </span>
                  ) : null
                )}
                {currentUser && currentProfile.username === currentUser && (
                  <span 
                    className="edit-icon" 
                    onClick={() => {
                      // Convert DD.MM.YYYY to YYYY-MM-DD for date input
                      if (currentProfile.birthDate) {
                        const parts = currentProfile.birthDate.split('.');
                        if (parts.length === 3) {
                          setEditingBirthDate(`${parts[2]}-${parts[1]}-${parts[0]}`);
                        } else {
                          setEditingBirthDate('');
                        }
                      } else {
                        setEditingBirthDate('');
                      }
                      setIsEditingBirthDate(true);
                    }}
                    style={{ cursor: 'pointer', marginLeft: '4px' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </span>
                )}
              </div>
            )}
            
            {/* Preferred Contact Method */}
            {(currentUser && currentProfile.username === currentUser) || currentProfile.preferredContactMethod ? (
              isEditingPreferredContact && currentUser && currentProfile.username === currentUser ? (
                <div style={{ marginTop: '10px', position: 'relative', marginBottom: '8px' }} className={recentlyChanged.preferredContact ? 'changed-outline' : ''}>
                  {recentlyChanged.preferredContact && <span className="change-pill">change</span>}
                  <select
                    value={editingPreferredContact}
                    onChange={(e) => setEditingPreferredContact(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select preferred contact method</option>
                    <option value="PeopleTag">PeopleTag</option>
                    <option value="Email">Email</option>
                    {currentProfile.socialLinks && currentProfile.socialLinks.length > 0 && (
                      <>
                        {currentProfile.socialLinks.map((social, index) => (
                          <option key={index} value={social}>{social}</option>
                        ))}
                      </>
                    )}
                  </select>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button
                      onClick={async () => {
                        if (isSavingPreferredContact) return;
                        setIsSavingPreferredContact(true);
                        
                        try {
                          const newPreferredContact = editingPreferredContact && editingPreferredContact.trim() !== '' ? editingPreferredContact.trim() : null;
                          
                          // Update profile with new preferred contact
                          const updatedProfile = {
                            ...currentProfile,
                            preferredContactMethod: newPreferredContact
                          };
                          
                          // Update current profile state immediately
                          setCurrentProfile(updatedProfile);
                          
                          // Save to IPFS
                          await encryptAndSaveProfile(updatedProfile);
                          
                          // Clear editing state
                          setIsEditingPreferredContact(false);
                          setEditingPreferredContact('');
                          
                          // Mark as recently changed
                          setRecentlyChanged(prev => ({ ...prev, preferredContact: true }));
                          
                          setNotification({ message: 'Preferred contact method saved successfully', type: 'success' });
                        } catch (error) {
                          console.error('Error saving preferred contact method:', error);
                          setNotification({ message: 'Error saving preferred contact method', type: 'error' });
                        } finally {
                          setIsSavingPreferredContact(false);
                        }
                      }}
                      disabled={isSavingPreferredContact}
                      style={{
                        padding: '8px 16px',
                        background: isSavingPreferredContact ? '#ccc' : '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isSavingPreferredContact ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingPreferredContact ? 0.7 : 1
                      }}
                    >
                      {isSavingPreferredContact ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingPreferredContact(false);
                        setEditingPreferredContact('');
                      }}
                      disabled={isSavingPreferredContact}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: isSavingPreferredContact ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingPreferredContact ? 0.7 : 1
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                  {currentProfile.preferredContactMethod ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={() => {
                          const isOwnProfile = isMyProfile();
                          if (isOwnProfile && currentProfile.preferredContactMethod) {
                            updatePublishStatus('preferredContact', null, !publishedPreferredContact);
                          }
                        }}
                        disabled={isUpdatingPublish || !currentProfile.preferredContactMethod || !(currentUser && currentProfile.username === currentUser)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: recentlyChanged.preferredContact ? '2px solid #ffcccc' : 'none',
                          borderRadius: '10px',
                          cursor: (isUpdatingPublish || !currentProfile.preferredContactMethod || !(currentUser && currentProfile.username === currentUser)) ? 'not-allowed' : 'pointer',
                          backgroundColor: (currentUser && currentProfile.username === currentUser && publishedPreferredContact) ? '#e0f0ff' : '#f5f5f5',
                          color: (currentUser && currentProfile.username === currentUser && publishedPreferredContact) ? '#0066cc' : '#333',
                          transition: 'all 0.2s ease',
                          opacity: (isUpdatingPublish || !currentProfile.preferredContactMethod) ? 0.6 : 1,
                          fontFamily: 'inherit',
                          textAlign: 'left'
                        }}
                      >
                        <Tooltip
                          text={(currentUser && currentProfile.username === currentUser) ? (publishedPreferredContact ? 'Published - visible to others' : 'Not published - hidden from others') : ''}
                        >
                          <span style={{ display: 'inline-block' }}>Preferred Contact: {currentProfile.preferredContactMethod}</span>
                        </Tooltip>
                        {recentlyChanged.preferredContact && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#cc2d2d',
                              backgroundColor: '#ffecec',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #ffc8c8',
                              lineHeight: 1
                            }}
                          >
                            change
                          </span>
                        )}
                      </button>
                      {showPublishedNotification && showPublishedNotification.key === 'preferredContact-single' && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                        </div>
                      )}
                    </div>
                  ) : (
                    currentUser && currentProfile.username === currentUser ? (
                      <span style={{ color: '#999', fontSize: '13px' }}>
                        Preferred contact not specified
                      </span>
                    ) : null
                  )}
                  {currentUser && currentProfile.username === currentUser && (
                    <span 
                      className="edit-icon" 
                      onClick={() => {
                        setEditingPreferredContact(currentProfile.preferredContactMethod || '');
                        setIsEditingPreferredContact(true);
                      }}
                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </span>
                  )}
                </div>
              )
            ) : null}
            
            {/* Relationship Status */}
            {(currentUser && currentProfile.username === currentUser) || currentProfile.relationshipStatus ? (
              isEditingRelationship && currentUser && currentProfile.username === currentUser ? (
                <div style={{ marginTop: '10px', position: 'relative', marginBottom: '8px' }} className={recentlyChanged.relationship ? 'changed-outline' : ''}>
                  {recentlyChanged.relationship && <span className="change-pill">change</span>}
                  <input
                    type="text"
                    value={editingRelationship}
                    onChange={(e) => setEditingRelationship(e.target.value)}
                    placeholder="e.g., Single, In a relationship, Married"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button
                      onClick={async () => {
                        if (isSavingRelationship) return;
                        setIsSavingRelationship(true);
                        
                        try {
                          const newRelationship = editingRelationship.trim() || null;
                          
                          // Update profile with new relationship
                          const updatedProfile = {
                            ...currentProfile,
                            relationshipStatus: newRelationship
                          };
                          
                          // Update current profile state immediately
                          setCurrentProfile(updatedProfile);
                          
                          // Save to IPFS
                          await encryptAndSaveProfile(updatedProfile);
                          
                          // Clear editing state
                          setIsEditingRelationship(false);
                          setEditingRelationship('');
                          
                          // Mark as recently changed
                          setRecentlyChanged(prev => ({ ...prev, relationship: true }));
                          
                          setNotification({ message: 'Relationship status saved successfully', type: 'success' });
                        } catch (error) {
                          console.error('Error saving relationship status:', error);
                          setNotification({ message: 'Error saving relationship status', type: 'error' });
                        } finally {
                          setIsSavingRelationship(false);
                        }
                      }}
                      disabled={isSavingRelationship}
                      style={{
                        padding: '8px 16px',
                        background: isSavingRelationship ? '#ccc' : '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isSavingRelationship ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingRelationship ? 0.7 : 1
                      }}
                    >
                      {isSavingRelationship ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingRelationship(false);
                        setEditingRelationship('');
                      }}
                      disabled={isSavingRelationship}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: isSavingRelationship ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingRelationship ? 0.7 : 1
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                  {currentProfile.relationshipStatus ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={() => {
                          const isOwnProfile = isMyProfile();
                          if (isOwnProfile && currentProfile.relationshipStatus) {
                            updatePublishStatus('relationship', null, !publishedRelationship);
                          }
                        }}
                        disabled={isUpdatingPublish || !currentProfile.relationshipStatus || !(currentUser && currentProfile.username === currentUser)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: recentlyChanged.relationship ? '2px solid #ffcccc' : 'none',
                          borderRadius: '10px',
                          cursor: (isUpdatingPublish || !currentProfile.relationshipStatus || !(currentUser && currentProfile.username === currentUser)) ? 'not-allowed' : 'pointer',
                          backgroundColor: (currentUser && currentProfile.username === currentUser && publishedRelationship) ? '#e0f0ff' : '#f5f5f5',
                          color: (currentUser && currentProfile.username === currentUser && publishedRelationship) ? '#0066cc' : '#333',
                          transition: 'all 0.2s ease',
                          opacity: (isUpdatingPublish || !currentProfile.relationshipStatus) ? 0.6 : 1,
                          fontFamily: 'inherit',
                          textAlign: 'left'
                        }}
                      >
                        <Tooltip
                          text={(currentUser && currentProfile.username === currentUser) ? (publishedRelationship ? 'Published - visible to others' : 'Not published - hidden from others') : ''}
                        >
                          <span style={{ display: 'inline-block' }}>
                        Relationship: {currentProfile.relationshipStatus}
                        {recentlyChanged.relationship && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#cc2d2d',
                              backgroundColor: '#ffecec',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #ffc8c8',
                              lineHeight: 1
                            }}
                          >
                            change
                          </span>
                        )}
                          </span>
                        </Tooltip>
                      </button>
                      {showPublishedNotification && showPublishedNotification.key === 'relationship-single' && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                        </div>
                      )}
                    </div>
                  ) : (
                    currentUser && currentProfile.username === currentUser ? (
                      <span style={{ color: '#999', fontSize: '13px' }}>
                        Relationship not specified
                      </span>
                    ) : null
                  )}
                  {currentUser && currentProfile.username === currentUser && (
                    <span 
                      className="edit-icon" 
                      onClick={() => {
                        setEditingRelationship(currentProfile.relationshipStatus || '');
                        setIsEditingRelationship(true);
                      }}
                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </span>
                  )}
                </div>
              )
            ) : null}
            
            {/* Looking For */}
            {(currentUser && currentProfile.username === currentUser) || currentProfile.lookingFor ? (
              isEditingLookingFor && currentUser && currentProfile.username === currentUser ? (
                <div style={{ marginTop: '10px', position: 'relative', marginBottom: '8px' }} className={recentlyChanged.lookingFor ? 'changed-outline' : ''}>
                  {recentlyChanged.lookingFor && <span className="change-pill">change</span>}
                  <input
                    type="text"
                    value={editingLookingFor}
                    onChange={(e) => setEditingLookingFor(e.target.value)}
                    placeholder="e.g., Friends, Networking, Dating, Business"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button
                      onClick={async () => {
                        if (isSavingLookingFor) return;
                        setIsSavingLookingFor(true);
                        
                        try {
                          const newLookingFor = editingLookingFor.trim() || null;
                          
                          // Update profile with new lookingFor
                          const updatedProfile = {
                            ...currentProfile,
                            lookingFor: newLookingFor
                          };
                          
                          // Update current profile state immediately
                          setCurrentProfile(updatedProfile);
                          
                          // Save to IPFS
                          await encryptAndSaveProfile(updatedProfile);
                          
                          // Clear editing state
                          setIsEditingLookingFor(false);
                          setEditingLookingFor('');
                          
                          // Mark as recently changed
                          setRecentlyChanged(prev => ({ ...prev, lookingFor: true }));
                          
                          setNotification({ message: 'Looking for saved successfully', type: 'success' });
                        } catch (error) {
                          console.error('Error saving looking for:', error);
                          setNotification({ message: 'Error saving looking for', type: 'error' });
                        } finally {
                          setIsSavingLookingFor(false);
                        }
                      }}
                      disabled={isSavingLookingFor}
                      style={{
                        padding: '8px 16px',
                        background: isSavingLookingFor ? '#ccc' : '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isSavingLookingFor ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingLookingFor ? 0.7 : 1
                      }}
                    >
                      {isSavingLookingFor ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingLookingFor(false);
                        setEditingLookingFor('');
                      }}
                      disabled={isSavingLookingFor}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: isSavingLookingFor ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingLookingFor ? 0.7 : 1
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                  {currentProfile.lookingFor ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={() => {
                          const isOwnProfile = isMyProfile();
                          if (isOwnProfile && currentProfile.lookingFor) {
                            updatePublishStatus('lookingFor', null, !publishedLookingFor);
                          }
                        }}
                        disabled={isUpdatingPublish || !currentProfile.lookingFor || !(currentUser && currentProfile.username === currentUser)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: recentlyChanged.lookingFor ? '2px solid #ffcccc' : 'none',
                          borderRadius: '10px',
                          cursor: (isUpdatingPublish || !currentProfile.lookingFor || !(currentUser && currentProfile.username === currentUser)) ? 'not-allowed' : 'pointer',
                          backgroundColor: (currentUser && currentProfile.username === currentUser && publishedLookingFor) ? '#e0f0ff' : '#f5f5f5',
                          color: (currentUser && currentProfile.username === currentUser && publishedLookingFor) ? '#0066cc' : '#333',
                          transition: 'all 0.2s ease',
                          opacity: (isUpdatingPublish || !currentProfile.lookingFor) ? 0.6 : 1,
                          fontFamily: 'inherit',
                          textAlign: 'left'
                        }}
                      >
                        <Tooltip
                          text={(currentUser && currentProfile.username === currentUser) ? (publishedLookingFor ? 'Published - visible to others' : 'Not published - hidden from others') : ''}
                        >
                          <span style={{ display: 'inline-block' }}>Looking For: {currentProfile.lookingFor}</span>
                        </Tooltip>
                        {recentlyChanged.lookingFor && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#cc2d2d',
                              backgroundColor: '#ffecec',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #ffc8c8',
                              lineHeight: 1
                            }}
                          >
                            change
                          </span>
                        )}
                      </button>
                      {showPublishedNotification && showPublishedNotification.key === 'lookingFor-single' && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                        </div>
                      )}
                    </div>
                  ) : (
                    currentUser && currentProfile.username === currentUser ? (
                      <span style={{ color: '#999', fontSize: '13px' }}>
                        Looking for not specified
                      </span>
                    ) : null
                  )}
                  {currentUser && currentProfile.username === currentUser && (
                    <span 
                      className="edit-icon" 
                      onClick={() => {
                        setEditingLookingFor(currentProfile.lookingFor || '');
                        setIsEditingLookingFor(true);
                      }}
                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </span>
                  )}
                </div>
              )
            ) : null}
            
            {/* Languages */}
            {(currentUser && currentProfile.username === currentUser) || currentProfile.languages ? (
              isEditingLanguages && currentUser && currentProfile.username === currentUser ? (
                <div style={{ marginTop: '10px', position: 'relative', marginBottom: '8px' }} className={recentlyChanged.languages ? 'changed-outline' : ''}>
                  {recentlyChanged.languages && <span className="change-pill">change</span>}
                  <input
                    type="text"
                    value={editingLanguages}
                    onChange={(e) => setEditingLanguages(e.target.value)}
                    placeholder="e.g., English, Spanish, French"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button
                      onClick={async () => {
                        if (isSavingLanguages) return;
                        setIsSavingLanguages(true);
                        
                        try {
                          const newLanguages = editingLanguages.trim() || null;
                          
                          // Update profile with new languages
                          const updatedProfile = {
                            ...currentProfile,
                            languages: newLanguages
                          };
                          
                          // Update current profile state immediately
                          setCurrentProfile(updatedProfile);
                          
                          // Save to IPFS
                          await encryptAndSaveProfile(updatedProfile);
                          
                          // Clear editing state
                          setIsEditingLanguages(false);
                          setEditingLanguages('');
                          
                          // Mark as recently changed
                          setRecentlyChanged(prev => ({ ...prev, languages: true }));
                          
                          setNotification({ message: 'Languages saved successfully', type: 'success' });
                        } catch (error) {
                          console.error('Error saving languages:', error);
                          setNotification({ message: 'Error saving languages', type: 'error' });
                        } finally {
                          setIsSavingLanguages(false);
                        }
                      }}
                      disabled={isSavingLanguages}
                      style={{
                        padding: '8px 16px',
                        background: isSavingLanguages ? '#ccc' : '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isSavingLanguages ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingLanguages ? 0.7 : 1
                      }}
                    >
                      {isSavingLanguages ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingLanguages(false);
                        setEditingLanguages('');
                      }}
                      disabled={isSavingLanguages}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: isSavingLanguages ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingLanguages ? 0.7 : 1
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                  {currentProfile.languages ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={() => {
                          const isOwnProfile = isMyProfile();
                          if (isOwnProfile && currentProfile.languages) {
                            updatePublishStatus('languages', null, !publishedLanguages);
                          }
                        }}
                        disabled={isUpdatingPublish || !currentProfile.languages || !(currentUser && currentProfile.username === currentUser)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: recentlyChanged.languages ? '2px solid #ffcccc' : 'none',
                          borderRadius: '10px',
                          cursor: (isUpdatingPublish || !currentProfile.languages || !(currentUser && currentProfile.username === currentUser)) ? 'not-allowed' : 'pointer',
                          backgroundColor: (currentUser && currentProfile.username === currentUser && publishedLanguages) ? '#e0f0ff' : '#f5f5f5',
                          color: (currentUser && currentProfile.username === currentUser && publishedLanguages) ? '#0066cc' : '#333',
                          transition: 'all 0.2s ease',
                          opacity: (isUpdatingPublish || !currentProfile.languages) ? 0.6 : 1,
                          fontFamily: 'inherit',
                          textAlign: 'left'
                        }}
                      >
                        <Tooltip
                          text={(currentUser && currentProfile.username === currentUser) ? (publishedLanguages ? 'Published - visible to others' : 'Not published - hidden from others') : ''}
                        >
                          <span style={{ display: 'inline-block' }}>Languages: {currentProfile.languages}</span>
                        </Tooltip>
                        {recentlyChanged.languages && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#cc2d2d',
                              backgroundColor: '#ffecec',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #ffc8c8',
                              lineHeight: 1
                            }}
                          >
                            change
                          </span>
                        )}
                      </button>
                      {showPublishedNotification && showPublishedNotification.key === 'languages-single' && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                        </div>
                      )}
                    </div>
                  ) : (
                    currentUser && currentProfile.username === currentUser ? (
                      <span style={{ color: '#999', fontSize: '13px' }}>
                        Languages not specified
                      </span>
                    ) : null
                  )}
                  {currentUser && currentProfile.username === currentUser && (
                    <span 
                      className="edit-icon" 
                      onClick={() => {
                        setEditingLanguages(currentProfile.languages || '');
                        setIsEditingLanguages(true);
                      }}
                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </span>
                  )}
                </div>
              )
            ) : null}
            {currentProfile.additionalPersonalData && currentProfile.additionalPersonalData.map((field, index) => (
              <div key={index} className="personal-detail">{field.label}: {field.value}</div>
            ))}
            
            {/* Education */}
            {(currentUser && currentProfile.username === currentUser) || currentProfile.education ? (
              isEditingEducation && currentUser && currentProfile.username === currentUser ? (
                <div style={{ marginTop: '10px', position: 'relative', marginBottom: '8px' }} className={recentlyChanged.education ? 'changed-outline' : ''}>
                  {recentlyChanged.education && <span className="change-pill">change</span>}
                  <input
                    type="text"
                    value={editingEducation}
                    onChange={(e) => setEditingEducation(e.target.value)}
                    placeholder="e.g., University Degree, Masters, PhD"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button
                      onClick={async () => {
                        if (isSavingEducation) return;
                        setIsSavingEducation(true);
                        
                        try {
                          const newEducation = editingEducation.trim() || null;
                          
                          // Update profile with new education
                          const updatedProfile = {
                            ...currentProfile,
                            education: newEducation
                          };
                          
                          // Update current profile state immediately
                          setCurrentProfile(updatedProfile);
                          
                          // Save to IPFS
                          await encryptAndSaveProfile(updatedProfile);
                          
                          // Clear editing state
                          setIsEditingEducation(false);
                          setEditingEducation('');
                          
                          // Mark as recently changed
                          setRecentlyChanged(prev => ({ ...prev, education: true }));
                          
                          setNotification({ message: 'Education saved successfully', type: 'success' });
                        } catch (error) {
                          console.error('Error saving education:', error);
                          setNotification({ message: 'Error saving education', type: 'error' });
                        } finally {
                          setIsSavingEducation(false);
                        }
                      }}
                      disabled={isSavingEducation}
                      style={{
                        padding: '8px 16px',
                        background: isSavingEducation ? '#ccc' : '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isSavingEducation ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingEducation ? 0.7 : 1
                      }}
                    >
                      {isSavingEducation ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingEducation(false);
                        setEditingEducation('');
                      }}
                      disabled={isSavingEducation}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: isSavingEducation ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingEducation ? 0.7 : 1
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                  {currentProfile.education ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={() => {
                          const isOwnProfile = isMyProfile();
                          if (isOwnProfile && currentProfile.education) {
                            updatePublishStatus('education', null, !publishedEducation);
                          }
                        }}
                        disabled={isUpdatingPublish || !currentProfile.education || !(currentUser && currentProfile.username === currentUser)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: recentlyChanged.education ? '2px solid #ffcccc' : 'none',
                          borderRadius: '10px',
                          cursor: (isUpdatingPublish || !currentProfile.education || !(currentUser && currentProfile.username === currentUser)) ? 'not-allowed' : 'pointer',
                          backgroundColor: (currentUser && currentProfile.username === currentUser && publishedEducation) ? '#e0f0ff' : '#f5f5f5',
                          color: (currentUser && currentProfile.username === currentUser && publishedEducation) ? '#0066cc' : '#333',
                          transition: 'all 0.2s ease',
                          opacity: (isUpdatingPublish || !currentProfile.education) ? 0.6 : 1,
                          fontFamily: 'inherit',
                          textAlign: 'left'
                        }}
                      >
                        <Tooltip
                          text={(currentUser && currentProfile.username === currentUser) ? (publishedEducation ? 'Published - visible to others' : 'Not published - hidden from others') : ''}
                        >
                          <span style={{ display: 'inline-block' }}>Education: {currentProfile.education}</span>
                        </Tooltip>
                        {recentlyChanged.education && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#cc2d2d',
                              backgroundColor: '#ffecec',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #ffc8c8',
                              lineHeight: 1
                            }}
                          >
                            change
                          </span>
                        )}
                      </button>
                      {showPublishedNotification && showPublishedNotification.key === 'education-single' && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                        </div>
                      )}
                    </div>
                  ) : (
                    currentUser && currentProfile.username === currentUser ? (
                      <span style={{ color: '#999', fontSize: '13px' }}>
                        Education not specified
                      </span>
                    ) : null
                  )}
                  {currentUser && currentProfile.username === currentUser && (
                    <span 
                      className="edit-icon" 
                      onClick={() => {
                        setEditingEducation(currentProfile.education || '');
                        setIsEditingEducation(true);
                      }}
                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </span>
                  )}
                </div>
              )
            ) : null}
            
            {/* Occupation */}
            {(currentUser && currentProfile.username === currentUser) || currentProfile.occupation ? (
              isEditingOccupation && currentUser && currentProfile.username === currentUser ? (
                <div style={{ marginTop: '10px', position: 'relative', marginBottom: '8px' }} className={recentlyChanged.occupation ? 'changed-outline' : ''}>
                  {recentlyChanged.occupation && <span className="change-pill">change</span>}
                  <input
                    type="text"
                    value={editingOccupation}
                    onChange={(e) => setEditingOccupation(e.target.value)}
                    placeholder="e.g., Software Engineer, Designer, Teacher"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button
                      onClick={async () => {
                        if (isSavingOccupation) return;
                        setIsSavingOccupation(true);
                        
                        try {
                          const newOccupation = editingOccupation.trim() || null;
                          
                          // Update profile with new occupation
                          const updatedProfile = {
                            ...currentProfile,
                            occupation: newOccupation
                          };
                          
                          // Update current profile state immediately
                          setCurrentProfile(updatedProfile);
                          
                          // Save to IPFS
                          await encryptAndSaveProfile(updatedProfile);
                          
                          // Clear editing state
                          setIsEditingOccupation(false);
                          setEditingOccupation('');
                          
                          // Mark as recently changed
                          setRecentlyChanged(prev => ({ ...prev, occupation: true }));
                          
                          setNotification({ message: 'Occupation saved successfully', type: 'success' });
                        } catch (error) {
                          console.error('Error saving occupation:', error);
                          setNotification({ message: 'Error saving occupation', type: 'error' });
                        } finally {
                          setIsSavingOccupation(false);
                        }
                      }}
                      disabled={isSavingOccupation}
                      style={{
                        padding: '8px 16px',
                        background: isSavingOccupation ? '#ccc' : '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isSavingOccupation ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingOccupation ? 0.7 : 1
                      }}
                    >
                      {isSavingOccupation ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingOccupation(false);
                        setEditingOccupation('');
                      }}
                      disabled={isSavingOccupation}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: isSavingOccupation ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isSavingOccupation ? 0.7 : 1
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                  {currentProfile.occupation ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={() => {
                          const isOwnProfile = isMyProfile();
                          if (isOwnProfile && currentProfile.occupation) {
                            updatePublishStatus('occupation', null, !publishedOccupation);
                          }
                        }}
                        disabled={isUpdatingPublish || !currentProfile.occupation || !(currentUser && currentProfile.username === currentUser)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: recentlyChanged.occupation ? '2px solid #ffcccc' : 'none',
                          borderRadius: '10px',
                          cursor: (isUpdatingPublish || !currentProfile.occupation || !(currentUser && currentProfile.username === currentUser)) ? 'not-allowed' : 'pointer',
                          backgroundColor: (currentUser && currentProfile.username === currentUser && publishedOccupation) ? '#e0f0ff' : '#f5f5f5',
                          color: (currentUser && currentProfile.username === currentUser && publishedOccupation) ? '#0066cc' : '#333',
                          transition: 'all 0.2s ease',
                          opacity: (isUpdatingPublish || !currentProfile.occupation) ? 0.6 : 1,
                          fontFamily: 'inherit',
                          textAlign: 'left'
                        }}
                      >
                        <Tooltip
                          text={(currentUser && currentProfile.username === currentUser) ? (publishedOccupation ? 'Published - visible to others' : 'Not published - hidden from others') : ''}
                        >
                          <span style={{ display: 'inline-block' }}>
                        Occupation: {currentProfile.occupation}
                        {recentlyChanged.occupation && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-4px',
                              fontSize: '9px',
                              fontWeight: '700',
                              color: '#cc2d2d',
                              backgroundColor: '#ffecec',
                              padding: '1px 4px',
                              borderRadius: '8px',
                              border: '1px solid #ffc8c8',
                              lineHeight: 1
                            }}
                          >
                            change
                          </span>
                        )}
                          </span>
                        </Tooltip>
                      </button>
                      {showPublishedNotification && showPublishedNotification.key === 'occupation-single' && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                        </div>
                      )}
                    </div>
                  ) : (
                    currentUser && currentProfile.username === currentUser ? (
                      <span style={{ color: '#999', fontSize: '13px' }}>
                        Occupation not specified
                      </span>
                    ) : null
                  )}
                  {currentUser && currentProfile.username === currentUser && (
                    <span 
                      className="edit-icon" 
                      onClick={() => {
                        setEditingOccupation(currentProfile.occupation || '');
                        setIsEditingOccupation(true);
                      }}
                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </span>
                  )}
                </div>
              )
            ) : null}
                </>
              );
            })()}
          </div>

          <div className="profile-section">
            <div className="section-title">City</div>
            {isEditingCity && currentUser && currentProfile.username === currentUser ? (
              <div style={{ marginTop: '0px', marginBottom: '0px', position: 'relative', padding: '8px' }} className={cityChanged ? 'changed-outline' : ''}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#333', fontWeight: '500' }}>
                  City:
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={editingCity}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setEditingCity(newValue);
                      // Try to get coordinates when city name matches a known city
                      const coords = getCityCoordinates(newValue);
                      if (coords) {
                        setEditingCityLat(coords.lat);
                        setEditingCityLng(coords.lng);
                      } else {
                        setEditingCityLat(null);
                        setEditingCityLng(null);
                      }
                    }}
                    placeholder="Enter city name or click 'Select on Map'"
                    readOnly={editingCitySelecting}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      setEditingCitySelecting(true);
                      if (mapInstanceRef.current) {
                        // Add click handler to map
                        const handleMapClick = async (e) => {
                          const { lat, lng } = e.latlng;
                          setEditingCitySelecting(false);
                          // Remove click handler
                          mapInstanceRef.current.off('click', handleMapClick);
                          
                          // Try to get city name from coordinates using Nominatim (OpenStreetMap)
                          try {
                            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1&accept-language=en`, {
                              headers: {
                                'Accept-Language': 'en'
                              }
                            });
                            const data = await response.json();
                            const cityName = data.address?.city || data.address?.town || data.address?.village || data.address?.municipality || 
                                            data.address?.county || data.address?.state || data.address?.region || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
                            
                            setEditingCity(cityName);
                            setEditingCityLat(lat);
                            setEditingCityLng(lng);
                          } catch (error) {
                            console.error('Error getting city name:', error);
                            // Fallback to coordinates
                            setEditingCity(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
                            setEditingCityLat(lat);
                            setEditingCityLng(lng);
                          }
                          
                          // Show a marker at selected location
                          if (mapInstanceRef.current.selectedCityMarker) {
                            mapInstanceRef.current.removeLayer(mapInstanceRef.current.selectedCityMarker);
                          }
                          const marker = window.L.marker([lat, lng], { color: 'red' }).addTo(mapInstanceRef.current);
                          marker.bindPopup('Selected location').openPopup();
                          mapInstanceRef.current.selectedCityMarker = marker;
                          mapInstanceRef.current.setView([lat, lng], 13);
                        };
                        mapInstanceRef.current.on('click', handleMapClick);
                      }
                    }}
                    style={{
                      padding: '8px 10px',
                      background: editingCitySelecting ? '#999' : '#4a90e2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: editingCitySelecting ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      whiteSpace: 'nowrap'
                    }}
                    disabled={editingCitySelecting}
                  >
                    {editingCitySelecting ? 'Click on Map...' : 'Select on Map'}
                  </button>
                </div>
                {cityChanged && <span className="change-pill">change</span>}
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginBottom: '10px' }}>
                  Click "Select on Map" and then click on the map to choose your city
                </small>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={async () => {
                      if (!editingCity) {
                        alert('Please enter a city or select one on the map');
                        return;
                      }
                      
                      // Get city coordinates
                      let location;
                      if (editingCityLat && editingCityLng) {
                        // Use coordinates selected from map
                        location = {
                          name: editingCity,
                          city: editingCity,
                          lat: editingCityLat,
                          lng: editingCityLng
                        };
                      } else {
                        // Try to get coordinates from city name
                        const coords = getCityCoordinates(editingCity);
                        location = coords ? {
                          name: editingCity,
                          city: editingCity,
                          lat: coords.lat,
                          lng: coords.lng
                        } : {
                          name: editingCity,
                          city: editingCity,
                          lat: 52.52,
                          lng: 13.405
                        };
                      }
                      
                      // Update profile
                      const updatedProfile = {
                        ...currentProfile,
                        city: editingCity,
                        location: location
                      };
                      setCurrentProfile(updatedProfile);
                      
                      // Encrypt and save to IPFS
                      setIsSavingCity(true);
                      try {
                        const result = await encryptAndSaveProfile(updatedProfile);
                        
                        if (result?.encryptedData) {
                          setEncryptedProfileData(result.encryptedData);
                        }
                        // Mark visual changes immediately (before global commit) - only mark city as changed
                        markRecentlyChanged(updatedProfile, ['city']);
                        
                        setIsEditingCity(false);
                        setEditingCity('');
                        setEditingCityLat(null);
                        setEditingCityLng(null);
                        
                        // Update currentProfile with city/location and ipfsHash
                        if (result?.ipfsHash) {
                          setCurrentProfile(prev => ({
                            ...prev,
                            city: editingCity, // Preserve city field
                            location: location, // Preserve location field
                            ipfsHash: result.ipfsHash
                          }));
                        } else {
                          // Even if ipfsHash update failed, ensure city/location are in state
                          setCurrentProfile(prev => ({
                            ...prev,
                            city: editingCity,
                            location: location
                          }));
                        }
                        // Reload markers in background (don't reload profile to avoid state reset)
                        loadAllProfilesAndCreateMarkers().catch(error => {
                          console.error('Error reloading markers:', error);
                        });
                        
                        // Center map on new location
                        if (mapInstanceRef.current && location) {
                          mapInstanceRef.current.setView([location.lat, location.lng], 13);
                        }
                      } catch (error) {
                        console.error('Error saving city:', error);
                        alert('Error saving city. Please try again.');
                      } finally {
                        setIsSavingCity(false);
                      }
                    }}
                    disabled={isSavingCity}
                    style={{
                      padding: '8px 16px',
                      background: isSavingCity ? '#ccc' : '#4a90e2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isSavingCity ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isSavingCity ? 0.7 : 1
                    }}
                  >
                    {isSavingCity ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingCity(false);
                      setEditingCity('');
                      setEditingCityLat(null);
                      setEditingCityLng(null);
                      setEditingCitySelecting(false);
                      // Remove map click handler if it exists
                      if (mapInstanceRef.current) {
                        mapInstanceRef.current.off('click');
                      }
                    }}
                    disabled={isSavingCity}
                    style={{
                      padding: '8px 16px',
                      background: 'white',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: isSavingCity ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isSavingCity ? 0.7 : 1
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="personal-detail" style={{ marginBottom: '8px', padding: '0' }}>
                {(() => {
                  // Get city from either currentProfile.city or currentProfile.location.city
                  const displayCity = currentProfile.city || currentProfile.location?.city || null;
                  return displayCity ? (
                    <div style={{ position: 'relative', display: 'inline-block' }} className={cityChanged ? 'changed-outline' : ''}>
                      {cityChanged && <span className="change-pill">change</span>}
                      <button
                        onClick={() => {
                          const isOwnProfile = isMyProfile();
                          if (isOwnProfile && displayCity) {
                            updatePublishStatus('city', null, !publishedCity);
                          }
                        }}
                        disabled={isUpdatingPublish || !displayCity || !(currentUser && currentProfile.username === currentUser)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: (isUpdatingPublish || !displayCity || !(currentUser && currentProfile.username === currentUser)) ? 'not-allowed' : 'pointer',
                          backgroundColor: (currentUser && currentProfile.username === currentUser && publishedCity) ? '#e0f0ff' : '#f5f5f5', // Always gray for other users
                          color: (currentUser && currentProfile.username === currentUser && publishedCity) ? '#0066cc' : '#333', // Always dark gray for other users
                          transition: 'all 0.2s ease',
                          opacity: (isUpdatingPublish || !displayCity) ? 0.6 : 1,
                          fontFamily: 'inherit'
                        }}
                      >
                        <Tooltip
                          text={(currentUser && currentProfile.username === currentUser) ? (publishedCity ? 'Published - visible to others' : 'Not published - hidden from others') : ''}
                        >
                          <span style={{ display: 'inline-block' }}>{displayCity}</span>
                        </Tooltip>
                      </button>
                      {showPublishedNotification && showPublishedNotification.key === 'city-single' && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: showPublishedNotification.isPublished ? '#4a90e2' : '#999',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          {showPublishedNotification.isPublished ? 'Published' : 'Unpublished'}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontSize: '13px' }}>
                      {currentUser && currentProfile.username === currentUser ? 'City not specified' : 'No city specified'}
                    </span>
                  );
                })()}
                {currentUser && currentProfile.username === currentUser && (
                  <span 
                    className="edit-icon" 
                    onClick={() => {
                      const displayCity = currentProfile.city || currentProfile.location?.city || '';
                      setEditingCity(displayCity);
                      setEditingCityLat(currentProfile.location?.lat || null);
                      setEditingCityLng(currentProfile.location?.lng || null);
                      setIsEditingCity(true);
                    }}
                    style={{ cursor: 'pointer', marginLeft: '4px' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </span>
                )}
              </div>
            )}
          </div>
                </React.Fragment>
              )}
              </React.Fragment>
            ) : null}
          
          {/* Photo Gallery Popup - works for all profiles */}
          {isPhotoGalleryOpen && currentProfile && (() => {
            const photos = getPhotosFromProfile(currentProfile);
            const isOwnProfile = isMyProfile();
            return photos.length > 0 || isOwnProfile;
          })() && (
            createPortal(
              <div 
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  zIndex: 10001,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px'
                }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setIsPhotoGalleryOpen(false);
                  }
                }}
              >
                <div 
                  style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '1200px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: '20px'
                  }}
                >
                  {/* Close button */}
                  <button
                    onClick={() => setIsPhotoGalleryOpen(false)}
                    style={{
                      position: 'absolute',
                      top: '-40px',
                      right: '0',
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      color: 'white',
                      fontSize: '32px',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10002,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                    onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                  >
                    ×
                  </button>

                  {/* Left side: Main photo and buttons */}
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 0
                  }}>
                    {/* Main photo display */}
                    <div style={{
                      width: '100%',
                      maxHeight: '70vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      marginBottom: '20px'
                    }}>
                      {(() => {
                        const photos = getPhotosFromProfile(currentProfile);
                        const currentPhoto = currentPhotoIndex >= 0 && currentPhotoIndex < photos.length 
                          ? photos[currentPhotoIndex] 
                          : null;
                        
                        return photos.length > 0 ? (
                          <>
                            {/* Previous button */}
                            {photos.length > 1 && (
                              <button
                                onClick={() => {
                                  setCurrentPhotoIndex((prev) => 
                                    prev <= 0 ? photos.length - 1 : prev - 1
                                  );
                                }}
                                style={{
                                  position: 'absolute',
                                  left: '20px',
                                  background: 'rgba(255, 255, 255, 0.2)',
                                  border: 'none',
                                  color: 'white',
                                  fontSize: '24px',
                                  width: '50px',
                                  height: '50px',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 10002,
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                              >
                                ‹
                              </button>
                            )}

                            {/* Current photo */}
                            <img
                              src={currentPhoto}
                              alt={`Photo ${currentPhotoIndex + 1}`}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain',
                                borderRadius: '8px'
                              }}
                            />

                            {/* Next button */}
                            {photos.length > 1 && (
                              <button
                                onClick={() => {
                                  setCurrentPhotoIndex((prev) => 
                                    prev >= photos.length - 1 ? 0 : prev + 1
                                  );
                                }}
                                style={{
                                  position: 'absolute',
                                  right: '20px',
                                  background: 'rgba(255, 255, 255, 0.2)',
                                  border: 'none',
                                  color: 'white',
                                  fontSize: '24px',
                                  width: '50px',
                                  height: '50px',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 10002,
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                              >
                                ›
                              </button>
                            )}

                            {/* Photo counter */}
                            <div style={{
                              position: 'absolute',
                              bottom: '20px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              background: 'rgba(0, 0, 0, 0.6)',
                              color: 'white',
                              padding: '8px 16px',
                              borderRadius: '20px',
                              fontSize: '14px'
                            }}>
                              {currentPhotoIndex + 1} / {photos.length}
                            </div>
                          </>
                        ) : (
                          <div style={{
                            color: 'white',
                            fontSize: '18px',
                            textAlign: 'center',
                            padding: '40px'
                          }}>
                            {currentUser && currentProfile.username === currentUser 
                              ? 'No photos yet. Upload your first photo!'
                              : 'No photos available.'}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Action buttons - only show for own profile */}
                    {currentUser && currentProfile.username === currentUser && (
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        position: 'relative',
                        zIndex: 10003,
                        width: '100%',
                        justifyContent: 'center'
                      }}>
                      {/* Upload button */}
                      <button
                        onClick={() => {
                          if (photosInputRef.current) {
                            photosInputRef.current.click();
                          }
                        }}
                        disabled={getPhotosFromProfile(currentProfile).length >= 30}
                        style={{
                          padding: '12px 24px',
                          background: getPhotosFromProfile(currentProfile).length >= 30 ? '#666' : '#4a90e2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: '500',
                          cursor: getPhotosFromProfile(currentProfile).length >= 30 ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (getPhotosFromProfile(currentProfile).length < 30) {
                            e.target.style.background = '#357abd';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (getPhotosFromProfile(currentProfile).length < 30) {
                            e.target.style.background = '#4a90e2';
                          }
                        }}
                      >
                        {getPhotosFromProfile(currentProfile).length >= 30 ? 'Max 30 photos' : 'Upload Photo'}
                      </button>

                      {/* Set Main button */}
                      {(() => {
                        const photos = getPhotosFromProfile(currentProfile);
                        const hasPhotos = photos.length > 1 && currentPhotoIndex >= 0 && currentPhotoIndex < photos.length;
                        const isMainPhoto = currentPhotoIndex === 0;
                        return hasPhotos && !isMainPhoto ? (
                        <button
                          onClick={async () => {
                            const photos = getPhotosFromProfile(currentProfile);
                            if (photos.length === 0 || currentPhotoIndex < 0 || currentPhotoIndex >= photos.length) return;
                            
                            // Move current photo to first position
                            const newPhotos = [...photos];
                            const [selectedPhoto] = newPhotos.splice(currentPhotoIndex, 1);
                            newPhotos.unshift(selectedPhoto);
                            
                            setIsSavingAvatar(true);
                            try {
                              const updatedProfile = {
                                ...currentProfile,
                                photos: newPhotos,
                                avatar: newPhotos[0] // Set avatar to the new main photo
                              };
                              setCurrentProfile(updatedProfile);
                              setCurrentPhotoIndex(0); // Show the new main photo
                              
                              // Save photos directly to Firestore (not encrypted, like avatar)
                              // Use updateUserPhotos to only update photos/avatar without affecting other fields
                              // This preserves the published state of city, bio, and other fields
                              try {
                                await updateUserPhotos(
                                  walletAddress,
                                  updatedProfile.username,
                                  newPhotos,
                                  newPhotos[0]
                                );
                                console.log('✅ Photos saved directly to Firestore');
                              } catch (saveError) {
                                console.error('Error saving photos to Firestore:', saveError);
                                throw saveError;
                              }
                              
                              setNotification({
                                message: 'Main photo set successfully!',
                                type: 'success'
                              });
                              setTimeout(() => setNotification(null), 3000);
                              
                              // Reload profile in background to ensure consistency
                              if (walletAddress) {
                                getUserProfileByWallet(walletAddress, encryptionKey).then(reloadedProfile => {
                                  if (reloadedProfile) {
                                    const cleanReloaded = cleanProfileData(reloadedProfile);
                                    setCurrentProfile(cleanReloaded);
                                    setCurrentPhotoIndex(0);
                                  }
                                }).catch(err => {
                                  console.error('Error reloading profile:', err);
                                });
                              }
                            } catch (error) {
                              console.error('Error setting main photo:', error);
                              setNotification({
                                message: 'Error setting main photo: ' + (error.message || 'Please try again.'),
                                type: 'error'
                              });
                              setTimeout(() => setNotification(null), 5000);
                            } finally {
                              setIsSavingAvatar(false);
                            }
                          }}
                          style={{
                            padding: '12px 24px',
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#218838'}
                          onMouseLeave={(e) => e.target.style.background = '#28a745'}
                        >
                          Set Main
                        </button>
                      ) : null;
                    })()}

                      {/* Delete button */}
                      {(() => {
                        const photos = getPhotosFromProfile(currentProfile);
                        const hasPhotos = photos.length > 0 && currentPhotoIndex >= 0 && currentPhotoIndex < photos.length;
                        return hasPhotos ? (
                        <button
                          onClick={async () => {
                            const photos = getPhotosFromProfile(currentProfile);
                            if (photos.length === 0 || currentPhotoIndex < 0 || currentPhotoIndex >= photos.length) return;
                            
                            const newPhotos = photos.filter((_, index) => index !== currentPhotoIndex);
                            const newIndex = newPhotos.length > 0 
                              ? Math.min(currentPhotoIndex, newPhotos.length - 1)
                              : -1;
                            
                            setIsSavingAvatar(true);
                            try {
                              const updatedProfile = {
                                ...currentProfile,
                                photos: newPhotos,
                                avatar: newPhotos.length > 0 ? newPhotos[0] : null // Update avatar to first photo
                              };
                              setCurrentProfile(updatedProfile);
                              setCurrentPhotoIndex(newIndex);
                              
                              // Save photos directly to Firestore (not encrypted, like avatar)
                              // Use updateUserPhotos to only update photos/avatar without affecting other fields
                              // This preserves the published state of city, bio, and other fields
                              try {
                                await updateUserPhotos(
                                  walletAddress,
                                  updatedProfile.username,
                                  newPhotos,
                                  newPhotos.length > 0 ? newPhotos[0] : null
                                );
                                console.log('✅ Photos saved directly to Firestore');
                              } catch (saveError) {
                                console.error('Error saving photos to Firestore:', saveError);
                                throw saveError;
                              }
                              
                              setNotification({
                                message: 'Photo deleted successfully!',
                                type: 'success'
                              });
                              setTimeout(() => setNotification(null), 3000);
                              
                              // Reload profile in background to ensure consistency
                              if (walletAddress) {
                                getUserProfileByWallet(walletAddress, encryptionKey).then(reloadedProfile => {
                                  if (reloadedProfile) {
                                    const cleanReloaded = cleanProfileData(reloadedProfile);
                                    setCurrentProfile(cleanReloaded);
                                  }
                                }).catch(err => {
                                  console.error('Error reloading profile:', err);
                                });
                              }
                            } catch (error) {
                              console.error('Error deleting photo:', error);
                              setNotification({
                                message: 'Error deleting photo: ' + (error.message || 'Please try again.'),
                                type: 'error'
                              });
                              setTimeout(() => setNotification(null), 5000);
                            } finally {
                              setIsSavingAvatar(false);
                            }
                          }}
                          style={{
                            padding: '12px 24px',
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#c82333'}
                          onMouseLeave={(e) => e.target.style.background = '#dc3545'}
                        >
                          Delete Photo
                        </button>
                        ) : null;
                      })()}
                    </div>
                  )}
                  </div>

                  {/* Right side: Thumbnail strip */}
                  {(() => {
                    const photos = getPhotosFromProfile(currentProfile);
                    const showScrollbar = photos.length > 3;
                    return photos.length > 0 ? (
                      <div 
                        className="thumbnail-strip-scrollbar"
                        style={{
                          width: '200px',
                          maxHeight: '70vh',
                          overflowY: showScrollbar ? 'auto' : 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                          padding: '8px',
                          paddingRight: showScrollbar ? '8px' : '16px',
                          paddingTop: showScrollbar ? '40px' : '8px',
                          scrollbarWidth: 'thin',
                          scrollbarColor: '#333 #1a1a1a'
                        }}
                      >
                        {photos.map((photo, index) => (
                          <img
                            key={index}
                            src={photo}
                            alt={`Thumbnail ${index + 1}`}
                            onClick={() => setCurrentPhotoIndex(index)}
                            style={{
                              width: '100%',
                              aspectRatio: '1',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              border: currentPhotoIndex === index ? '3px solid #4a90e2' : '3px solid transparent',
                              transition: 'all 0.2s',
                              opacity: currentPhotoIndex === index ? 1 : 0.7
                            }}
                            onMouseEnter={(e) => {
                              if (currentPhotoIndex !== index) {
                                e.target.style.opacity = '1';
                                e.target.style.transform = 'scale(1.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (currentPhotoIndex !== index) {
                                e.target.style.opacity = '0.7';
                                e.target.style.transform = 'scale(1)';
                              }
                            }}
                          />
                        ))}
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>,
              document.body
            )
          )}

          {currentUser && currentProfile && currentProfile.username === currentUser && (
            <>
                <a 
                  href="#" 
                  className="settings-link"
                  onClick={(e) => {
                    e.preventDefault();
                    setSettingsExpanded(!settingsExpanded);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="edit-icon" style={{ marginRight: '8px' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"></circle>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                    </svg>
                  </span>
                  <span>Settings</span>
                </a>
                {settingsExpanded && (
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    {/* Admin buttons moved to Admin section */}
                    {/* Profile Theme/Color */}
                    <div style={{ marginTop: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#333', fontWeight: '500' }}>
                        Profile Theme/Color:
                      </label>
                      <input
                        type="color"
                        value={currentProfile?.profileThemeColor || '#4a90e2'}
                        disabled
                        style={{
                          width: '100%',
                          padding: '2px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          cursor: 'not-allowed',
                          opacity: 0.6
                        }}
                      />
                    </div>
                    
                    {/* Blockchain to store ID */}
                    <div style={{ marginTop: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#333', fontWeight: '500' }}>
                        Blockchain to store ID:
                      </label>
                      <select
                        value={currentProfile?.blockchain || 'base-mainnet'}
                        disabled
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontFamily: 'inherit',
                          backgroundColor: '#f5f5f5',
                          color: '#666',
                          cursor: 'not-allowed'
                        }}
                      >
                        <option value="base-mainnet">Arc Testnet</option>
                        <option value="polygon">Polygon</option>
                        <option value="base">Arc (Circle)</option>
                      </select>
                    </div>
                    
                    {/* Delete my Profile - moved to bottom */}
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                      <button 
                        onClick={handleDeleteProfile}
                        disabled={isLoadingUser}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: 'white',
                          color: '#333',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          cursor: isLoadingUser ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          opacity: isLoadingUser ? 0.6 : 1
                        }}
                      >
                        {isLoadingUser ? 'Deleting...' : 'Delete my profile'}
                      </button>
                    </div>
                  </div>
                )}
                <a 
                  href="#" 
                  className="settings-link"
                  onClick={(e) => {
                    e.preventDefault();
                    setAnalyticsExpanded(!analyticsExpanded);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="edit-icon" style={{ marginRight: '8px' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                      <line x1="12" y1="20" x2="12" y2="10"></line>
                      <line x1="18" y1="20" x2="18" y2="4"></line>
                      <line x1="6" y1="20" x2="6" y2="16"></line>
                    </svg>
                  </span>
                  <span>Profile Views & Analytics</span>
                </a>
                {analyticsExpanded && currentProfile && (
                  <div style={{
                    marginTop: '10px',
                    padding: '16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0',
                    fontSize: '13px'
                  }}>
                    {(() => {
                      const popularSearches = utils.getPopularSearches(10);
                      
                      // Use the gamification service's profile completeness (consistent across the app)
                      const completenessScore = gamificationData?.profileCompleteness || calculateProfileCompleteness(currentProfile) || 0;
                      
                      // Get popular tags from profile
                      const getPopularTags = (profile) => {
                        if (!profile) return [];
                        const tags = profile.tagsList || [];
                        // Count tag occurrences (if we had view data per tag, we'd use that)
                        // For now, just return the tags sorted
                        return tags.map(tag => ({ tag, count: 1 })).sort((a, b) => b.count - a.count);
                      };
                      
                      const popularTags = getPopularTags(currentProfile);
                      
                      return (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h4 style={{ margin: '0', fontSize: '15px', fontWeight: '600', color: '#333' }}>
                              Profile Activity Tracking
                            </h4>
                            {currentUser && currentProfile.username === currentUser && (
                              <button
                                onClick={() => setShowProfileCompletenessWizard(true)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  color: '#4a90e2',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  textAlign: 'center',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                Profile analyzer
                              </button>
                            )}
                          </div>
                          
                          {/* Profile View Counter */}
                          <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <span style={{ fontWeight: '500', color: '#555' }}>Profile Views</span>
                              <span style={{ fontSize: '18px', fontWeight: '600', color: '#4a90e2' }}>{viewStats.totalViews}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ fontSize: '12px', color: '#888' }}>Unique Viewers</span>
                              <span style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>{viewStats.uniqueViewers}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '12px', color: '#888' }}>Unlogged Views</span>
                              <span style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>{viewStats.uniqueUnloggedViewers || 0}</span>
                            </div>
                          </div>
                          
                          {/* View History */}
                          <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
                            <div style={{ marginBottom: '10px' }}>
                              <span style={{ fontWeight: '500', color: '#555' }}>Recent View History</span>
                            </div>
                            {viewStats.recentViews && viewStats.recentViews.length > 0 ? (
                              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                {viewStats.recentViews.slice(0, 10).map((view, index) => {
                                    // Determine if this is an unlogged viewer
                                    // Check if walletAddress is valid (non-empty string) or if isLoggedIn is false
                                    const hasValidWallet = view.viewerWalletAddress && typeof view.viewerWalletAddress === 'string' && view.viewerWalletAddress.trim().length > 0;
                                    const isUnlogged = (!hasValidWallet && view.viewerSessionId) || view.isLoggedIn === false;
                                    
                                    // Get username - prefer from view record, fallback to lookup
                                    let username = view.viewerUsername || null;
                                    if (!username && hasValidWallet) {
                                      const normalizedAddress = view.viewerWalletAddress.toLowerCase();
                                      username = viewerUsernames[view.viewerWalletAddress] || viewerUsernames[normalizedAddress];
                                      // Silently handle username lookup failure - don't spam console
                                    }
                                    
                                    // Determine display name - NEVER show wallet address, only username or (unlogged)
                                    let displayName;
                                    if (isUnlogged) {
                                      displayName = '(unlogged)';
                                    } else if (username) {
                                      // Truncate long usernames
                                      const maxLength = 20;
                                      displayName = username.length > maxLength 
                                        ? `@${username.substring(0, maxLength)}...` 
                                        : `@${username}`;
                                    } else if (hasValidWallet) {
                                      // Username not found - show as "User" instead of wallet address
                                      // This happens for old views or if lookup failed
                                      displayName = 'User';
                                    } else {
                                      displayName = 'Unknown';
                                    }
                                    
                                    const isClickable = username !== null && username !== undefined && !isUnlogged;
                                    
                                    // Format date/time for display
                                    let dateTimeDisplay = 'Recently';
                                    if (view.timestamp) {
                                      const date = new Date(view.timestamp);
                                      dateTimeDisplay = date.toLocaleDateString();
                                    } else if (view.date) {
                                      dateTimeDisplay = new Date(view.date).toLocaleDateString();
                                    }
                                    
                                    // Format time separately
                                    let timeDisplay = '';
                                    if (view.timestamp) {
                                      const date = new Date(view.timestamp);
                                      timeDisplay = date.toLocaleTimeString();
                                    }
                                    
                                    return (
                                      <div key={index} style={{ 
                                        padding: '6px 8px', 
                                        marginBottom: '4px', 
                                        backgroundColor: '#fff', 
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: '12px'
                                      }}>
                                        <span 
                                          onClick={() => {
                                            if (isClickable) {
                                              navigate(`/profile/${username}`);
                                              setProfileVisible(true);
                                            }
                                          }}
                                          style={{ 
                                            color: isClickable ? '#4a90e2' : '#666',
                                            cursor: isClickable ? 'pointer' : 'default',
                                            textDecoration: 'none',
                                            flex: '1',
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                          onMouseEnter={(e) => {
                                            if (isClickable) {
                                              e.target.style.textDecoration = 'underline';
                                              e.target.style.opacity = '0.8';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (isClickable) {
                                              e.target.style.textDecoration = 'none';
                                              e.target.style.opacity = '1';
                                            }
                                          }}
                                        >
                                          {displayName}
                                        </span>
                                        <div style={{ 
                                          display: 'flex', 
                                          flexDirection: 'column', 
                                          alignItems: 'flex-end',
                                          color: '#999', 
                                          fontSize: '11px',
                                          lineHeight: '1.4',
                                          flexShrink: 0
                                        }}>
                                          <span>{dateTimeDisplay}</span>
                                          {timeDisplay && <span>{timeDisplay}</span>}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            ) : (
                              <div style={{ color: '#999', fontSize: '12px', fontStyle: 'italic' }}>No views yet</div>
                            )}
                          </div>
                          
                          {/* Popular Tags Analytics */}
                          <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
                            <div style={{ marginBottom: '10px' }}>
                              <span style={{ fontWeight: '500', color: '#555' }}>Popular Tags</span>
                            </div>
                            {popularTags && popularTags.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {popularTags.slice(0, 10).map((item, index) => (
                                  <span 
                                    key={index}
                                    style={{
                                      padding: '4px 10px',
                                      backgroundColor: '#e8f4f8',
                                      color: '#4a90e2',
                                      borderRadius: '12px',
                                      fontSize: '11px',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {item.tag}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div style={{ color: '#999', fontSize: '12px', fontStyle: 'italic' }}>No tags added yet</div>
                            )}
                          </div>
                          
                          {/* Search Analytics */}
                          <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
                            <div style={{ marginBottom: '10px' }}>
                              <span style={{ fontWeight: '500', color: '#555' }}>Search Analytics</span>
                            </div>
                            {popularSearches && popularSearches.length > 0 ? (
                              <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                {popularSearches.slice(0, 8).map((search, index) => (
                                  <div key={index} style={{ 
                                    padding: '6px 8px', 
                                    marginBottom: '4px', 
                                    backgroundColor: '#fff', 
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}>
                                    <span style={{ color: '#666' }}>{search.query}</span>
                                    <span style={{ color: '#4a90e2', fontWeight: '500', fontSize: '11px' }}>{search.count} searches</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ color: '#999', fontSize: '12px', fontStyle: 'italic' }}>No search data available</div>
                            )}
                          </div>
                          
                          {/* Profile Completeness Score */}
                          <div>
                            <div style={{ marginBottom: '10px' }}>
                              <span style={{ fontWeight: '500', color: '#555' }}>Profile Completeness</span>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                              <div style={{ 
                                width: '100%', 
                                height: '24px', 
                                backgroundColor: '#e0e0e0', 
                                borderRadius: '12px',
                                overflow: 'hidden',
                                position: 'relative'
                              }}>
                                <div style={{
                                  width: `${completenessScore}%`,
                                  height: '100%',
                                  backgroundColor: completenessScore >= 80 ? '#4caf50' : completenessScore >= 50 ? '#ff9800' : '#f44336',
                                  transition: 'width 0.3s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '11px',
                                  fontWeight: '600'
                                }}>
                                  {completenessScore >= 15 && `${completenessScore}%`}
                                </div>
                              </div>
                            </div>
                            <div style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
                              {completenessScore >= 80 ? 'Excellent! Your profile is well-complete.' : 
                               completenessScore >= 50 ? 'Good progress! Add more details to improve your profile.' : 
                               'Consider adding more information to your profile.'}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
                <a 
                  href="#" 
                  className="settings-link"
                  onClick={(e) => {
                    e.preventDefault();
                    setEncryptionDataExpanded(!encryptionDataExpanded);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="edit-icon" style={{ marginRight: '8px' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                  </span>
                  <span>My encryption data</span>
                </a>
                {encryptionDataExpanded && (
                  <div style={{
                    marginTop: '10px',
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Wallet Address:</strong>
                      <div 
                        onClick={() => {
                          if (walletAddress) {
                            navigator.clipboard.writeText(walletAddress);
                            setNotification({ message: 'Wallet address copied to clipboard!', type: 'success' });
                            setTimeout(() => setNotification(null), 2000);
                          }
                        }}
                        style={{ 
                          marginTop: '4px', 
                          color: '#555',
                          cursor: walletAddress ? 'pointer' : 'default',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: walletAddress ? '#f0f0f0' : 'transparent',
                          transition: 'background-color 0.2s',
                          userSelect: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (walletAddress) e.target.style.backgroundColor = '#e0e0e0';
                        }}
                        onMouseLeave={(e) => {
                          if (walletAddress) e.target.style.backgroundColor = '#f0f0f0';
                        }}
                      >
                        {walletAddress ? (
                          <Tooltip text="Click to copy">
                            <span style={{ display: 'inline-block' }}>{walletAddress}</span>
                          </Tooltip>
                        ) : (
                          <span>Not connected</span>
                        )}
                      </div>
                    </div>
                    
                    {encryptionKey && (
                      <>
                        <div style={{ marginBottom: '12px' }}>
                          <strong>Encryption Key:</strong>
                          <div 
                            onClick={() => {
                              setKey1Revealed(!key1Revealed);
                            }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              const hexKey = encryptionKeyToHex(encryptionKey);
                              navigator.clipboard.writeText(hexKey);
                              setNotification({ message: 'Encryption key copied to clipboard!', type: 'success' });
                              setTimeout(() => setNotification(null), 2000);
                            }}
                            style={{ 
                              marginTop: '4px', 
                              color: '#555',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: '#f0f0f0',
                              transition: 'background-color 0.2s',
                              userSelect: 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#e0e0e0'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                          >
                            <Tooltip text={key1Revealed ? "Click to blur / Double-click to copy" : "Click to reveal / Double-click to copy"}>
                              <span 
                                style={{ 
                                  display: 'inline-block',
                                  filter: key1Revealed ? 'none' : 'blur(8px)',
                                  transition: 'filter 0.3s',
                                  cursor: 'pointer'
                                }}
                              >
                                {encryptionKeyToHex(encryptionKey)}
                              </span>
                            </Tooltip>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '12px' }}>
                          <strong>Encryption Key (Hex):</strong>
                          <div 
                            onClick={() => {
                              setKey2Revealed(!key2Revealed);
                            }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              const hexKey = encryptionKeyToHex(encryptionKey);
                              navigator.clipboard.writeText(hexKey);
                              setNotification({ message: 'Encryption key copied to clipboard!', type: 'success' });
                              setTimeout(() => setNotification(null), 2000);
                            }}
                            style={{ 
                              marginTop: '4px', 
                              color: '#555',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: '#f0f0f0',
                              transition: 'background-color 0.2s',
                              userSelect: 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#e0e0e0'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                          >
                            <Tooltip text={key2Revealed ? "Click to blur / Double-click to copy" : "Click to reveal / Double-click to copy"}>
                              <span 
                                style={{ 
                                  display: 'inline-block',
                                  filter: key2Revealed ? 'none' : 'blur(8px)',
                                  transition: 'filter 0.3s',
                                  cursor: 'pointer'
                                }}
                              >
                                {encryptionKeyToHex(encryptionKey)}
                              </span>
                            </Tooltip>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '12px' }}>
                          <strong>AES-GCM Key (32 bytes / 256 bits):</strong>
                          <div 
                            onClick={() => {
                              setKey3Revealed(!key3Revealed);
                            }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              const keyString = Array.from(encryptionKey).map(b => b.toString(16).padStart(2, '0')).join(' ');
                              navigator.clipboard.writeText(keyString);
                              setNotification({ message: 'AES-GCM key copied to clipboard!', type: 'success' });
                              setTimeout(() => setNotification(null), 2000);
                            }}
                            style={{ 
                              marginTop: '4px', 
                              color: '#555',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: '#f0f0f0',
                              transition: 'background-color 0.2s',
                              userSelect: 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#e0e0e0'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                          >
                            <Tooltip text={key3Revealed ? "Click to blur / Double-click to copy" : "Click to reveal / Double-click to copy"}>
                              <span 
                                style={{ 
                                  display: 'inline-block',
                                  filter: key3Revealed ? 'none' : 'blur(8px)',
                                  transition: 'filter 0.3s',
                                  cursor: 'pointer'
                                }}
                              >
                                {Array.from(encryptionKey).map(b => b.toString(16).padStart(2, '0')).join(' ')}
                              </span>
                            </Tooltip>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {console.log('Debug - encryptedProfileData:', encryptedProfileData)}
                    {console.log('Debug - currentProfile?.encryptedData:', currentProfile?.encryptedData)}
                    {console.log('Debug - encryptionDataExpanded:', encryptionDataExpanded)}
                    
                    {(encryptedProfileData || currentProfile?.encryptedData) && (() => {
                      const encryptedData = encryptedProfileData || currentProfile?.encryptedData;
                      // Get IPFS hash from current profile
                      const ipfsHash = currentProfile?.ipfsHash;
                      
                      // Calculate byte count: hex string length / 2 (each byte is 2 hex characters)
                      const ciphertextBytes = encryptedData?.ciphertext ? Math.floor(encryptedData.ciphertext.length / 2) : 0;
                      const nonceBytes = encryptedData?.nonce ? Math.floor(encryptedData.nonce.length / 2) : 0;
                      
                      return (
                        <div style={{ marginBottom: '12px' }}>
                          <strong>Profile data (encrypted with key):</strong>
                          <div style={{ marginTop: '4px', color: '#555' }}>
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Ciphertext ({ciphertextBytes} bytes):</strong>
                              <div 
                                onClick={() => {
                                  if (encryptedData?.ciphertext) {
                                    navigator.clipboard.writeText(encryptedData.ciphertext);
                                    setNotification({ message: 'Ciphertext copied to clipboard!', type: 'success' });
                                    setTimeout(() => setNotification(null), 2000);
                                  }
                                }}
                                style={{ 
                                  marginTop: '4px', 
                                  fontSize: '11px', 
                                  wordBreak: 'break-all',
                                  cursor: encryptedData?.ciphertext ? 'pointer' : 'default',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: encryptedData?.ciphertext ? '#f0f0f0' : 'transparent',
                                  transition: 'background-color 0.2s',
                                  userSelect: 'none'
                                }}
                                onMouseEnter={(e) => {
                                  if (encryptedData?.ciphertext) e.target.style.backgroundColor = '#e0e0e0';
                                }}
                                onMouseLeave={(e) => {
                                  if (encryptedData?.ciphertext) e.target.style.backgroundColor = '#f0f0f0';
                                }}
                              >
                                {encryptedData?.ciphertext ? (
                                  <Tooltip text="Click to copy">
                                    <span style={{ display: 'inline-block' }}>{encryptedData.ciphertext}</span>
                                  </Tooltip>
                                ) : (
                                  <span>N/A</span>
                                )}
                              </div>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Nonce ({nonceBytes} bytes):</strong>
                              <div 
                                onClick={() => {
                                  if (encryptedData?.nonce) {
                                    navigator.clipboard.writeText(encryptedData.nonce);
                                    setNotification({ message: 'Nonce copied to clipboard!', type: 'success' });
                                    setTimeout(() => setNotification(null), 2000);
                                  }
                                }}
                                style={{ 
                                  marginTop: '4px', 
                                  fontSize: '11px', 
                                  wordBreak: 'break-all',
                                  cursor: encryptedData?.nonce ? 'pointer' : 'default',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: encryptedData?.nonce ? '#f0f0f0' : 'transparent',
                                  transition: 'background-color 0.2s',
                                  userSelect: 'none'
                                }}
                                onMouseEnter={(e) => {
                                  if (encryptedData?.nonce) e.target.style.backgroundColor = '#e0e0e0';
                                }}
                                onMouseLeave={(e) => {
                                  if (encryptedData?.nonce) e.target.style.backgroundColor = '#f0f0f0';
                                }}
                              >
                                {encryptedData?.nonce ? (
                                  <Tooltip text="Click to copy">
                                    <span style={{ display: 'inline-block' }}>{encryptedData.nonce}</span>
                                  </Tooltip>
                                ) : (
                                  <span>N/A</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Always show IPFS hash if available (even if encrypted data is not loaded) */}
                    {currentProfile?.ipfsHash && (
                      <div style={{ 
                        marginTop: '12px', 
                        marginBottom: '12px',
                        padding: '10px', 
                        backgroundColor: '#e8f5e9', 
                        borderRadius: '6px', 
                        border: '1px solid #4caf50',
                        position: 'relative'
                      }}>
                        <strong style={{ color: '#2e7d32', display: 'block', marginBottom: '6px' }}>
                          IPFS Hash (CID):
                        </strong>
                        <div 
                          onClick={() => {
                            navigator.clipboard.writeText(currentProfile.ipfsHash);
                            setNotification({ message: 'IPFS hash copied to clipboard!', type: 'success' });
                            setTimeout(() => setNotification(null), 2000);
                          }}
                          style={{ 
                            marginTop: '4px', 
                            fontSize: '11px', 
                            wordBreak: 'break-all', 
                            color: '#1b5e20',
                            fontFamily: 'monospace',
                            padding: '6px',
                            backgroundColor: 'white',
                            borderRadius: '3px',
                            border: '1px solid #c8e6c9',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            userSelect: 'none'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          <Tooltip text="Click to copy">
                            <span style={{ display: 'inline-block' }}>{currentProfile.ipfsHash}</span>
                          </Tooltip>
                        </div>
                        <div style={{ marginTop: '8px' }}>
                          <a 
                            href={`https://gateway.pinata.cloud/ipfs/${currentProfile.ipfsHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              fontSize: '11px', 
                              color: '#2e7d32',
                              textDecoration: 'underline',
                              fontWeight: '500'
                            }}
                          >
                            View on IPFS Gateway →
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {/* Always show Arc (Circle) transaction if available (even if encrypted data is not loaded) */}
                    {(lastBaseTxHash || isSavingToBlockchain) && (
                      <div style={{ 
                        marginTop: '12px', 
                        marginBottom: '12px',
                        padding: '10px', 
                        backgroundColor: '#e3f2fd', 
                        borderRadius: '6px', 
                        border: '1px solid #2196f3',
                        position: 'relative'
                      }}>
                        <strong style={{ color: '#1565c0', display: 'block', marginBottom: '6px' }}>
                          Last Arc (Circle) Transaction:
                        </strong>
                        {isSavingToBlockchain ? (
                          <div style={{ 
                            marginTop: '4px', 
                            fontSize: '11px', 
                            color: '#1565c0',
                            fontStyle: 'italic'
                          }}>
                            Sending transaction...
                          </div>
                        ) : lastBaseTxHash ? (
                          <>
                            <div 
                              onClick={() => {
                                navigator.clipboard.writeText(lastBaseTxHash);
                                setNotification({ message: 'Arc (Circle) transaction hash copied to clipboard!', type: 'success' });
                                setTimeout(() => setNotification(null), 2000);
                              }}
                              style={{ 
                                marginTop: '4px', 
                                fontSize: '11px', 
                                wordBreak: 'break-all', 
                                color: '#0d47a1',
                                fontFamily: 'monospace',
                                padding: '6px',
                                backgroundColor: 'white',
                                borderRadius: '3px',
                                border: '1px solid #90caf9',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                userSelect: 'none'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              <Tooltip text="Click to copy">
                                <span style={{ display: 'inline-block' }}>{lastBaseTxHash}</span>
                              </Tooltip>
                            </div>
                            <div style={{ marginTop: '8px' }}>
                              <a 
                                href={`https://testnet.arcscan.app/tx/${lastBaseTxHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                  fontSize: '11px', 
                                  color: '#1565c0',
                                  textDecoration: 'underline',
                                  fontWeight: '500'
                                }}
                              >
                                View on Arcscan →
                              </a>
                            </div>
                          </>
                        ) : null}
                      </div>
                    )}
                    
                    {/* What PeopleTag know about me - Database Records Section */}
                    <div style={{ 
                      marginTop: '20px', 
                      marginBottom: '12px',
                      padding: '12px', 
                      backgroundColor: '#fff3e0', 
                      borderRadius: '6px', 
                      border: '1px solid #ff9800',
                      position: 'relative'
                    }}>
                      <strong style={{ color: '#e65100', display: 'block', marginBottom: '10px', fontSize: '13px' }}>
                        What PeopleTag know about me
                      </strong>
                      
                      {isLoadingDbRecords ? (
                        <div style={{ color: '#e65100', fontStyle: 'italic', fontSize: '11px' }}>
                          Loading database records...
                        </div>
                      ) : dbRecords ? (
                        <div style={{ fontSize: '11px' }}>
                          {Object.keys(dbRecords).length === 0 ? (
                            <div style={{ color: '#999', fontStyle: 'italic' }}>
                              No database records found
                            </div>
                          ) : (
                            Object.entries(dbRecords).map(([key, record], index) => (
                              <div 
                                key={key} 
                                style={{ 
                                  marginBottom: index < Object.keys(dbRecords).length - 1 ? '16px' : '0',
                                  paddingBottom: index < Object.keys(dbRecords).length - 1 ? '16px' : '0',
                                  borderBottom: index < Object.keys(dbRecords).length - 1 ? '1px solid #ffe0b2' : 'none'
                                }}
                              >
                                <div style={{ fontWeight: 'bold', color: '#e65100', marginBottom: '6px', fontSize: '12px' }}>
                                  {key === 'profile' && '📋 Profile Document'}
                                  {key === 'walletUser' && '🔗 Wallet-to-Username Mapping'}
                                  {key === 'messagesSent' && `💬 Messages Sent (${record.count})`}
                                  {key === 'messagesReceived' && `📨 Messages Received (${record.count})`}
                                  {key === 'conversations' && `💭 Conversations (${record.count})`}
                                  {key === 'messageSignatures' && `✍️ Message Signatures (${record.count})`}
                                  {key === 'blocks' && `🚫 Blocks (${record.count})`}
                                  {key === 'reports' && `⚠️ Reports (${record.count})`}
                                  {key === 'messagesTotal' && `📧 Total Messages (${record.count})`}
                                </div>
                                
                                {record.collection && (
                                  <div style={{ color: '#666', marginBottom: '4px', fontSize: '10px' }}>
                                    Collection: <strong>{record.collection}</strong>
                                    {record.documentId && (
                                      <> | Document ID: <strong>{record.documentId}</strong></>
                                    )}
                                    {record.query && (
                                      <> | Query: <strong>{record.query}</strong></>
                                    )}
                                  </div>
                                )}
                                
                                {record.data && (
                                  <div style={{ 
                                    marginTop: '6px', 
                                    padding: '8px', 
                                    backgroundColor: 'white', 
                                    borderRadius: '4px',
                                    border: '1px solid #ffe0b2',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    fontFamily: 'monospace',
                                    fontSize: '10px',
                                    wordBreak: 'break-word'
                                  }}>
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                      {JSON.stringify(sanitizeDataForDisplay(record.data), null, 2)}
                                    </pre>
                                  </div>
                                )}
                                
                                {record.error && (
                                  <div style={{ color: '#d32f2f', marginTop: '6px', fontSize: '10px', fontStyle: 'italic' }}>
                                    Error: {record.error}
                                  </div>
                                )}
                                
                                {record.documents !== undefined && (
                                  <div style={{ marginTop: '8px' }}>
                                    {record.documents.length === 0 ? (
                                      <div style={{ color: '#999', fontStyle: 'italic', fontSize: '10px' }}>
                                        No {key === 'messagesSent' ? 'messages sent' : key === 'messagesReceived' ? 'messages received' : key === 'conversations' ? 'conversations' : 'documents'} found
                                      </div>
                                    ) : (
                                      record.documents.map((doc, docIndex) => (
                                        <div 
                                          key={doc.id} 
                                          style={{ 
                                            marginBottom: docIndex < record.documents.length - 1 ? '12px' : '0',
                                            paddingBottom: docIndex < record.documents.length - 1 ? '12px' : '0',
                                            borderBottom: docIndex < record.documents.length - 1 ? '1px solid #ffe0b2' : 'none'
                                          }}
                                        >
                                          <div style={{ color: '#666', marginBottom: '4px', fontSize: '10px' }}>
                                            Document ID: <strong>{doc.id}</strong>
                                          </div>
                                          <div style={{ 
                                            padding: '6px', 
                                            backgroundColor: 'white', 
                                            borderRadius: '4px',
                                            border: '1px solid #ffe0b2',
                                            maxHeight: '150px',
                                            overflowY: 'auto',
                                            fontFamily: 'monospace',
                                            fontSize: '10px',
                                            wordBreak: 'break-word'
                                          }}>
                                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                              {JSON.stringify(sanitizeDataForDisplay(doc.data), null, 2)}
                                            </pre>
                                          </div>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      ) : (
                        <div style={{ color: '#999', fontStyle: 'italic', fontSize: '11px' }}>
                          Click to expand and load database records
                        </div>
                      )}
                    </div>
                    
                    {!encryptionKey && (
                      <div style={{ color: '#999', fontStyle: 'italic' }}>
                        Encryption key not available
                      </div>
                    )}
                  </div>
                )}
                {isAdminWallet(walletAddress) && (
                  <a 
                    href="#" 
                    className="settings-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setUtilitiesExpanded(!utilitiesExpanded);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="edit-icon" style={{ marginRight: '8px' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <rect x="3" y="3" width="7" height="7"></rect>
                        <rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect>
                        <rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                    </span>
                    <span>Utilities</span>
                  </a>
                )}
                {utilitiesExpanded && isAdminWallet(walletAddress) && (
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0',
                    maxHeight: '600px',
                    overflowY: 'auto'
                  }}>
                    {/* Utilities Tabs */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px', 
                      marginBottom: '16px',
                      borderBottom: '1px solid #e0e0e0',
                      paddingBottom: '8px'
                    }}>
                      {['profile', 'backup', 'analytics', 'validation', 'format', 'messages'].map(tab => (
                        <button
                          key={tab}
                          onClick={() => setUtilitiesActiveTab(tab)}
                          style={{
                            padding: '6px 12px',
                            background: utilitiesActiveTab === tab ? '#4a90e2' : 'white',
                            color: utilitiesActiveTab === tab ? 'white' : '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            textTransform: 'capitalize'
                          }}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>

                    {/* Profile Utilities Tab */}
                    {utilitiesActiveTab === 'profile' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600' }}>Profile Utilities</h4>
                        
                        <button
                          onClick={() => {
                            if (currentProfile) {
                              const completeness = utils.calculateProfileCompleteness(currentProfile);
                              alert(`Profile Completeness: ${completeness}%`);
                            } else {
                              alert('No profile loaded');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Check Profile Completeness
                        </button>

                        <button
                          onClick={() => {
                            if (currentProfile) {
                              const stats = utils.getProfileStatistics(currentProfile);
                              alert(`Profile Statistics:\n\nCompleteness: ${stats.completeness}%\nTags: ${stats.tagCount}\nSocials: ${stats.socialCount}\nAge: ${stats.age || 'N/A'}\nHas Location: ${stats.hasLocation ? 'Yes' : 'No'}\nHas Bio: ${stats.hasBio ? 'Yes' : 'No'}\nBio Length: ${stats.bioLength} chars\nBio Words: ${stats.bioWordCount}`);
                            } else {
                              alert('No profile loaded');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Get Profile Statistics
                        </button>

                        <button
                          onClick={() => {
                            if (currentProfile) {
                              try {
                                utils.downloadProfileAsFile(currentProfile);
                                setNotification({ message: 'Profile exported successfully!', type: 'success' });
                                setTimeout(() => setNotification(null), 3000);
                              } catch (error) {
                                alert('Error exporting profile: ' + error.message);
                              }
                            } else {
                              alert('No profile loaded');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Export Profile as JSON
                        </button>

                        <button
                          onClick={() => {
                            if (currentProfile && allProfiles.length > 0) {
                              const similar = utils.findSimilarProfiles(currentProfile, allProfiles, 50);
                              if (similar.length > 0) {
                                const top5 = similar.slice(0, 5).map(s => `${s.profile.username} (${s.similarity}%)`).join('\n');
                                alert(`Top Similar Profiles:\n\n${top5}`);
                              } else {
                                alert('No similar profiles found');
                              }
                            } else {
                              alert('No profile or profiles loaded');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Find Similar Profiles
                        </button>

                        <button
                          onClick={async () => {
                            if (currentProfile && currentProfile.username) {
                              try {
                                const qrDataUrl = await utils.generateProfileQRCode(currentProfile.username, window.location.origin);
                                const newWindow = window.open();
                                if (newWindow) {
                                  newWindow.document.write(`<html><body style="display:flex;justify-content:center;align-items:center;height:100vh;margin:0;"><img src="${qrDataUrl}" alt="QR Code" /></body></html>`);
                                }
                              } catch (error) {
                                alert('Error generating QR code: ' + error.message);
                              }
                            } else {
                              alert('No profile loaded');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Generate Profile QR Code
                        </button>

                        <button
                          onClick={() => {
                            if (currentProfile && allProfiles.length > 0) {
                              const popularTags = utils.getPopularTags(allProfiles, 20);
                              const top10 = popularTags.slice(0, 10).map(t => `${t.tag} (${t.count})`).join('\n');
                              alert(`Top 10 Popular Tags:\n\n${top10}`);
                            } else {
                              alert('No profiles loaded');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Get Popular Tags
                        </button>
                      </div>
                    )}

                    {/* Backup Utilities Tab */}
                    {utilitiesActiveTab === 'backup' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600' }}>Backup Utilities</h4>
                        
                        <button
                          onClick={() => {
                            if (currentProfile && walletAddress) {
                              const success = utils.backupProfileToLocalStorage(currentProfile, walletAddress);
                              if (success) {
                                setNotification({ message: 'Profile backed up to localStorage!', type: 'success' });
                                setTimeout(() => setNotification(null), 3000);
                              } else {
                                alert('Failed to backup profile');
                              }
                            } else {
                              alert('No profile or wallet address');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Backup Profile to LocalStorage
                        </button>

                        <button
                          onClick={() => {
                            if (walletAddress) {
                              const backup = utils.restoreProfileFromLocalStorage(walletAddress);
                              if (backup) {
                                alert('Backup found! Check console for details.');
                                console.log('Restored profile:', backup);
                              } else {
                                alert('No backup found for this wallet');
                              }
                            } else {
                              alert('No wallet address');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Restore Profile from LocalStorage
                        </button>

                        <button
                          onClick={() => {
                            if (walletAddress) {
                              try {
                                utils.exportBackupAsFile(walletAddress);
                                setNotification({ message: 'Backup exported successfully!', type: 'success' });
                                setTimeout(() => setNotification(null), 3000);
                              } catch (error) {
                                alert('Error exporting backup: ' + error.message);
                              }
                            } else {
                              alert('No wallet address');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Export Backup as File
                        </button>

                        <button
                          onClick={() => {
                            const backups = utils.listAllBackups();
                            if (backups.length > 0) {
                              const backupList = backups.map(b => 
                                `${utils.formatWalletAddress(b.walletAddress)} - ${utils.formatRelativeTime(new Date(b.timestamp))}`
                              ).join('\n');
                              alert(`Found ${backups.length} backup(s):\n\n${backupList}`);
                            } else {
                              alert('No backups found');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          List All Backups
                        </button>
                      </div>
                    )}

                    {/* Analytics Utilities Tab */}
                    {utilitiesActiveTab === 'analytics' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600' }}>Analytics Utilities</h4>
                        
                        <button
                          onClick={async () => {
                            if (currentProfile && currentProfile.username) {
                              try {
                                const stats = await utils.getProfileViewStats(currentProfile.username);
                                alert(`Profile View Statistics:\n\nTotal Views: ${stats.totalViews}\nUnique Viewers: ${stats.uniqueViewers}\nRecent Views: ${stats.recentViews.length}`);
                              } catch (error) {
                                console.error('Error getting view stats:', error);
                                alert('Error loading view statistics');
                              }
                            } else {
                              alert('No profile loaded');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Get Profile View Stats
                        </button>

                        <button
                          onClick={() => {
                            if (currentProfile && currentProfile.username) {
                              const engagement = utils.calculateEngagementScore(currentProfile.username);
                              alert(`Engagement Score: ${engagement}/100`);
                            } else {
                              alert('No profile loaded');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Calculate Engagement Score
                        </button>

                        <button
                          onClick={() => {
                            const popular = utils.getMostViewedProfiles(10);
                            if (popular.length > 0) {
                              const top5 = popular.slice(0, 5).map(p => `${p.username}: ${p.views} views`).join('\n');
                              alert(`Most Viewed Profiles:\n\n${top5}`);
                            } else {
                              alert('No view data available');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Get Most Viewed Profiles
                        </button>

                        <button
                          onClick={() => {
                            const searches = utils.getPopularSearches(10);
                            if (searches.length > 0) {
                              const top5 = searches.slice(0, 5).map(s => `${s.query} (${s.count})`).join('\n');
                              alert(`Popular Searches:\n\n${top5}`);
                            } else {
                              alert('No search data available');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Get Popular Searches
                        </button>

                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to clear all analytics data? This cannot be undone.')) {
                              const success = utils.clearAnalyticsData();
                              if (success) {
                                setNotification({ message: 'Analytics data cleared!', type: 'success' });
                                setTimeout(() => setNotification(null), 3000);
                              } else {
                                alert('Failed to clear analytics data');
                              }
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: '#ffebee',
                            color: '#c62828',
                            border: '1px solid #ef5350',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Clear All Analytics Data
                        </button>
                      </div>
                    )}

                    {/* Validation Utilities Tab */}
                    {utilitiesActiveTab === 'validation' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600' }}>Validation Utilities</h4>
                        
                        <button
                          onClick={() => {
                            if (currentProfile) {
                              const validation = utils.validateProfile(currentProfile);
                              if (validation.isValid) {
                                alert('Profile is valid! ✓');
                              } else {
                                alert('Profile validation errors:\n\n' + validation.errors.join('\n'));
                              }
                            } else {
                              alert('No profile loaded');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Validate Profile
                        </button>

                        <button
                          onClick={() => {
                            if (currentProfile) {
                              const health = utils.checkProfileHealth(currentProfile);
                              alert(`Profile Health Check:\n\nScore: ${health.score}/100\nHealthy: ${health.healthy ? 'Yes' : 'No'}\n\nIssues:\n${health.issues.length > 0 ? health.issues.join('\n') : 'None'}`);
                            } else {
                              alert('No profile loaded');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Check Profile Health
                        </button>

                        <button
                          onClick={() => {
                            if (currentProfile && currentProfile.tagsList) {
                              const validation = utils.validateTags(currentProfile.tagsList);
                              if (validation.isValid) {
                                alert('All tags are valid! ✓');
                              } else {
                                alert('Tag validation errors:\n\n' + validation.errors.join('\n'));
                              }
                            } else {
                              alert('No tags to validate');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Validate Tags
                        </button>
                      </div>
                    )}

                    {/* Format Utilities Tab */}
                    {utilitiesActiveTab === 'format' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600' }}>Format Utilities</h4>
                        
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                          Format utilities are available in the utils module. Use them programmatically in your code.
                        </div>

                        <button
                          onClick={() => {
                            if (walletAddress) {
                              const formatted = utils.formatWalletAddress(walletAddress);
                              utils.copyToClipboard(formatted);
                              setNotification({ message: 'Formatted address copied!', type: 'success' });
                              setTimeout(() => setNotification(null), 2000);
                            } else {
                              alert('No wallet address');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Copy Formatted Wallet Address
                        </button>

                        <button
                          onClick={() => {
                            if (currentProfile && currentProfile.updatedAt) {
                              const relative = utils.formatRelativeTime(currentProfile.updatedAt);
                              alert(`Last updated: ${relative}`);
                            } else {
                              alert('No update date available');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'white',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Format Last Updated Time
                        </button>
                      </div>
                    )}
                    {utilitiesActiveTab === 'messages' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600' }}>Message Utilities</h4>
                        
                        {/* Delete All Conversations moved to Admin section */}
                      </div>
                    )}
                  </div>
                )}
                {isAdminWallet(walletAddress) && (
                  <a 
                    href="#" 
                    className="settings-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setAdminExpanded(!adminExpanded);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="edit-icon" style={{ marginRight: '8px' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        <path d="M9 12l2 2 4-4"></path>
                      </svg>
                    </span>
                    <span>Admin</span>
                  </a>
                )}
                {adminExpanded && isAdminWallet(walletAddress) && (
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    <button
                      onClick={async () => {
                        // Ask user for number of profiles to generate
                        const input = window.prompt('How many random profiles would you like to generate?', '50');
                        if (input === null) {
                          // User cancelled
                          return;
                        }
                        const count = parseInt(input, 10);
                        if (isNaN(count) || count < 1) {
                          alert('Please enter a valid number greater than 0.');
                          return;
                        }
                        if (count > 1000) {
                          const confirm = window.confirm(`You entered ${count} profiles. This may take a very long time. Are you sure you want to continue?`);
                          if (!confirm) {
                            return;
                          }
                        }
                        setIsPopulating(true);
                        setPopulationResult(null);
                        try {
                          const result = await populateDatabase((progress) => {
                            // Show progress notification
                            if (progress.index && progress.total) {
                              setNotification({
                                message: `Populating... ${progress.index}/${progress.total}`,
                                type: 'info'
                              });
                            }
                            
                            // Progress callback - add markers one by one
                            if (progress.profile && mapInstanceRef.current) {
                              // Add marker for this profile immediately
                              const profile = progress.profile;
                              // Only create marker if profile has both location coordinates and a city name
                              const hasLocation = profile.location && profile.location.lat && profile.location.lng;
                              const hasCity = (profile.city && profile.city.trim() !== '') || (profile.location?.city && profile.location.city.trim() !== '');
                              if (hasLocation && hasCity) {
                                const lat = profile.location.lat;
                                const lng = profile.location.lng;
                                
                                // Create marker
                                const marker = window.L.marker([lat, lng]);
                                
                                // Add marker to cluster group if available, otherwise add directly to map
                                if (markerClusterGroupRef.current) {
                                  markerClusterGroupRef.current.addLayer(marker);
                                } else {
                                  marker.addTo(mapInstanceRef.current);
                                }
                                
                                // Add click handler - use ref to get current walletAddress at click time
                                marker.on('click', async () => {
                                  // Check current walletAddress using ref (always up-to-date)
                                  const currentWalletAddress = walletAddressRef.current;
                                  if (currentWalletAddress) {
                                    if (profile.walletAddress && profile.walletAddress.toLowerCase() === currentWalletAddress.toLowerCase()) {
                                      // This is the current user's profile - load full data from IPFS with published states
                                      try {
                                        console.log('🔄 Loading fresh profile data from IPFS for current user...');
                                        const currentEncryptionKey = encryptionKeyRef.current || encryptionKey;
                                        
                                        // Get Firestore metadata first (contains only published elements) - with retry to prevent race condition
                                        const firestoreMetadata = await fetchFirestoreMetadataWithRetry(currentWalletAddress);
                                        
                                        // Get full profile from IPFS
                                        const freshProfile = await getUserProfileByWallet(currentWalletAddress, currentEncryptionKey);
                                        
                                        if (freshProfile) {
                                          console.log('✅ Loaded fresh profile from IPFS');
                                          const profileWithHash = {
                                            ...freshProfile,
                                            ipfsHash: freshProfile.ipfsHash || firestoreMetadata?.ipfsHash || null
                                          };
                                          const cleanReloaded = cleanProfileData(profileWithHash);
                                          
                                          setCurrentUser(cleanReloaded.username);
                                          setCurrentProfile(cleanReloaded);
                                          setProfileVisible(true);
                                          setAboutVisible(false);
                                          
                                          // Determine published state by comparing Firestore (only published) with IPFS (all elements)
                                          const firestoreTags = firestoreMetadata?.tagsList || [];
                                          const firestoreSocials = firestoreMetadata?.socialLinks || [];
                                          const firestoreBirthDate = firestoreMetadata?.birthDate || null;
                                          const firestoreCity = firestoreMetadata?.city || null;
                                          
                                          // Tags: published if in Firestore
                                          const publishedTagsList = cleanReloaded.tagsList.filter(tag => firestoreTags.includes(tag));
                                          setPublishedTags(publishedTagsList);
                                          
                                          // Socials: published if in Firestore
                                          const publishedSocialsList = cleanReloaded.socialLinks.filter(social => firestoreSocials.includes(social));
                                          setPublishedSocials(publishedSocialsList);
                                          
                                          // BirthDate: published if in Firestore
                                          setPublishedBirthDate(!!firestoreBirthDate && firestoreBirthDate === cleanReloaded.birthDate);
                                          
                                          // City: published if in Firestore
                                          setPublishedCity(!!firestoreCity && firestoreCity === cleanReloaded.city);
                                          
                                          // Wallet: use _isWalletPublishedInFirestore flag (walletAddress always has fallback)
                                          setPublishedWallet(firestoreMetadata?._isWalletPublishedInFirestore === true);
                                        } else {
                                          // If IPFS load failed, try to reload from Firestore metadata as fallback
                                          // but this should rarely happen - log warning
                                          console.warn('⚠️ Failed to load profile from IPFS, using Firestore metadata as fallback');
                                          if (firestoreMetadata) {
                                            // Get IPFS hash from blockchain for fallback (Arc (Circle) network)
                                            const fallbackIpfsHash = await getCIDFromArc(currentWalletAddress);
                                            const fallbackProfile = cleanProfileData({
                                              ...firestoreMetadata,
                                              ipfsHash: fallbackIpfsHash || null
                                            });
                                            setCurrentUser(fallbackProfile.username);
                                            setCurrentProfile(fallbackProfile);
                                            setProfileVisible(true);
                                            setAboutVisible(false);
                                            // Reset published states since we don't have full IPFS data
                                            setPublishedTags([]);
                                            setPublishedSocials([]);
                                            setPublishedBirthDate(false);
                                            setPublishedCity(false);
                                            setPublishedWallet(false);
                                          } else {
                                            console.error('❌ No profile data available from IPFS or Firestore');
                                          }
                                        }
                                      } catch (error) {
                                        console.error('❌ Error loading fresh profile from IPFS:', error);
                                        // Try to reload from Firestore as last resort
                                        try {
                                          const fallbackMetadata = await getUserProfileByWallet(currentWalletAddress, null);
                                          if (fallbackMetadata) {
                                            const fallbackProfile = cleanProfileData({
                                              ...fallbackMetadata,
                                              ipfsHash: fallbackMetadata.ipfsHash || null
                                            });
                                            setCurrentUser(fallbackProfile.username);
                                            setCurrentProfile(fallbackProfile);
                                            setProfileVisible(true);
                                            setAboutVisible(false);
                                            // Reset published states since we don't have full IPFS data
                                            setPublishedTags([]);
                                            setPublishedSocials([]);
                                            setPublishedBirthDate(false);
                                            setPublishedCity(false);
                                            setPublishedWallet(false);
                                          }
                                        } catch (fallbackError) {
                                          console.error('❌ Failed to load fallback profile:', fallbackError);
                                        }
                                      }
                                    } else {
                                      // This is not the current user's profile - clean profile data
                                      const cleanOtherProfile = cleanProfileData(profile);
                                      // Clear change tracking state immediately when switching to another profile
                                      clearChangeTrackingState();
                                      setCurrentUser(null); // Clear currentUser to indicate this is not "My Profile"
                                      setCurrentProfile(cleanOtherProfile);
                                      setProfileVisible(true);
                                      setAboutVisible(false);
                                      // Ensure other right-side panels are closed when opening profile
                                      setGroupsVisible(false);
                                      setMessagesVisible(false);
                                      setBugVisible(false);
                                      setAiAssistantVisible(false);
                                    }
                                  } else {
                                    // Allow viewing profiles even when wallet is not connected.
                                    // Only interactive actions (message/review/report) should be disabled.
                                    const cleanOtherProfile = cleanProfileData(profile);
                                    clearChangeTrackingState();
                                    setCurrentUser(null);
                                    setCurrentProfile(cleanOtherProfile);
                                    setProfileVisible(true);
                                    setAboutVisible(false);
                                    setGroupsVisible(false);
                                    setMessagesVisible(false);
                                    setBugVisible(false);
                                    setAiAssistantVisible(false);
                                    setNotification({ message: 'Connect wallet to message, leave reviews, or report users.', type: 'info' });
                                    setTimeout(() => setNotification(null), 4000);
                                  }
                                  if (mapInstanceRef.current) {
                                    // Pan to location without changing zoom level
                                    mapInstanceRef.current.panTo([lat, lng]);
                                    marker.openPopup();
                                  }
                                });
                                
                                // Add to markers array
                                markersRef.current.push(marker);
                                
                                // Add to allProfiles and filteredProfiles
                                setAllProfiles(prev => [...prev, { ...profile, marker }]);
                                setFilteredProfiles(prev => [...prev, { ...profile, marker }]);
                              } else {
                                // Profile without location - add to allProfiles and filteredProfiles without marker
                                setAllProfiles(prev => [...prev, { ...profile, marker: null }]);
                                setFilteredProfiles(prev => [...prev, { ...profile, marker: null }]);
                              }
                            }
                          }, count);
                          setPopulationResult(result);
                          setNotification({
                            message: `Successfully created ${result.success} profiles!${result.errors > 0 ? ` (${result.errors} errors)` : ''}`,
                            type: 'success'
                          });
                          setTimeout(() => { setNotification(null); }, 5000);
                        } catch (error) {
                          console.error('Error populating database:', error);
                          setNotification({
                            message: 'Error populating database: ' + error.message,
                            type: 'error'
                          });
                          setTimeout(() => { setNotification(null); }, 5000);
                        } finally {
                          setIsPopulating(false);
                        }
                      }}
                      disabled={isPopulating}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: 'white',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: isPopulating ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isPopulating ? 0.6 : 1
                      }}
                    >
                      <Tooltip text="Populate database with custom number of random profiles">
                        <span style={{ display: 'inline-block' }}>{isPopulating ? 'Populating...' : 'Populate DB (x)'}</span>
                      </Tooltip>
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to clear all profiles except your own? This will delete ALL other profiles and wallet mappings. This action cannot be undone!')) {
                          if (window.confirm('This is your last chance. Are you absolutely sure you want to delete all profiles except yours?')) {
                            setIsClearingDatabase(true);
                            try {
                              await clearAllProfiles(walletAddress);
                              // Remove all existing markers from the map immediately
                              if (mapInstanceRef.current && markersRef.current.length > 0) {
                                markersRef.current.forEach(marker => {
                                  if (marker) {
                                    try {
                                      mapInstanceRef.current.removeLayer(marker);
                                    } catch (e) {}
                                  }
                                });
                                markersRef.current = [];
                              }
                              // Clear allProfiles and filteredProfiles immediately
                              setAllProfiles([]);
                              setFilteredProfiles([]);
                              setNotification({
                                message: 'Database cleared successfully! All profiles except yours have been deleted.',
                                type: 'success'
                              });
                              setTimeout(() => {
                                setNotification(null);
                              }, 5000);
                              // Reload profiles on map
                              await loadAllProfilesAndCreateMarkers();
                            } catch (error) {
                              console.error('Error clearing database:', error);
                              setNotification({
                                message: 'Error clearing database: ' + error.message,
                                type: 'error'
                              });
                              setTimeout(() => {
                                setNotification(null);
                              }, 5000);
                            } finally {
                              setIsClearingDatabase(false);
                            }
                          }
                        }
                      }}
                      disabled={isClearingDatabase}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isClearingDatabase ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isClearingDatabase ? 0.6 : 1
                      }}
                    >
                      <Tooltip text="Clear all profiles except your own">
                        <span style={{ display: 'inline-block' }}>{isClearingDatabase ? 'Clearing...' : 'Clear database'}</span>
                      </Tooltip>
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete ALL test records? This will remove every document in profile_test.')) {
                          setIsClearingTestRecords(true);
                          try {
                            await clearAllTestProfiles();
                            // Remove all existing markers from the map immediately
                            if (mapInstanceRef.current && markersRef.current.length > 0) {
                              markersRef.current.forEach(marker => {
                                if (marker) {
                                  try {
                                    mapInstanceRef.current.removeLayer(marker);
                                  } catch (e) {}
                                }
                              });
                              markersRef.current = [];
                            }
                            // Clear profile lists immediately
                            setAllProfiles([]);
                            setFilteredProfiles([]);
                            // Reload remaining profiles (prod) and re-create markers
                            await loadAllProfilesAndCreateMarkers();
                            setNotification({
                              message: 'All test records deleted from profile_test.',
                              type: 'success'
                            });
                            setTimeout(() => { setNotification(null); }, 5000);
                          } catch (error) {
                            console.error('Error deleting test records:', error);
                            setNotification({
                              message: 'Error deleting test records: ' + error.message,
                              type: 'error'
                            });
                            setTimeout(() => { setNotification(null); }, 5000);
                          } finally {
                            setIsClearingTestRecords(false);
                          }
                        }
                      }}
                      disabled={isClearingTestRecords}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: 'white',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: isClearingTestRecords ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: isClearingTestRecords ? 0.6 : 1
                      }}
                    >
                      <Tooltip text="Delete all test records (profile_test)">
                        <span style={{ display: 'inline-block' }}>{isClearingTestRecords ? 'Deleting...' : 'Delete all test records'}</span>
                      </Tooltip>
                    </button>
                  </div>
                )}
            </>
            )}
          </div>
        )}
        {/* Groups section */}
        {walletAddress && groupsVisible && (
          <Groups 
            walletAddress={walletAddress}
            isMobile={isMobile}
            onClose={() => {
              setGroupsVisible(false);
            }}
            onOpenMemberProfile={async (memberAddress) => {
              try {
                // Check if this is the current user's own profile
                const isOwnProfile = walletAddress && memberAddress && 
                  memberAddress.toLowerCase() === walletAddress.toLowerCase();
                
                // Load the member's profile
                const memberProfile = await getUserProfileByWallet(memberAddress, null);
                if (memberProfile) {
                  const cleanMemberProfile = cleanProfileData(memberProfile);
                  
                  // Set currentUser to username for own profile, null for others
                  if (isOwnProfile) {
                    setCurrentUser(cleanMemberProfile.username);
                  } else {
                    setCurrentUser(null); // null for other users
                  }
                  
                  setCurrentProfile(cleanMemberProfile);
                  setProfileVisible(true);
                  setAboutVisible(false);
                  setShowRegistrationForm(false);
                  // Close groups panel
                  setGroupsVisible(false);
                  
                  // Move map to member's location if they have one
                  if (cleanMemberProfile.location && cleanMemberProfile.location.lat && cleanMemberProfile.location.lng) {
                    const lat = cleanMemberProfile.location.lat;
                    const lng = cleanMemberProfile.location.lng;
                    
                    if (mapInstanceRef.current) {
                      // Pan to location with smooth animation
                      mapInstanceRef.current.setView([lat, lng], 13, { animate: true, duration: 1.0 });
                      
                      // Try to find and open their marker popup if it exists
                      // Look for marker by matching location coordinates
                      setTimeout(() => {
                        if (markersRef.current && markersRef.current.length > 0) {
                          const memberMarker = markersRef.current.find(m => {
                            if (m.getLatLng) {
                              const markerLatLng = m.getLatLng();
                              const tolerance = 0.0001; // Small tolerance for floating point comparison
                              return Math.abs(markerLatLng.lat - lat) < tolerance && 
                                     Math.abs(markerLatLng.lng - lng) < tolerance;
                            }
                            return false;
                          });
                          if (memberMarker && typeof memberMarker.openPopup === 'function') {
                            memberMarker.openPopup();
                          }
                        }
                      }, 800);
                    }
                  }
                } else {
                  window.alert('Could not load member profile');
                }
              } catch (error) {
                console.error('Error opening member profile:', error);
                window.alert(`Error loading profile: ${error.message}`);
              }
            }}
          />
        )}
        {/* Messages section */}
        {walletAddress && messagesVisible && (
          <Messaging 
            walletAddress={walletAddress}
            isMobile={isMobile}
            onClose={() => {
              setMessagesVisible(false);
              setSelectedMessageRecipient(null);
              setSelectedMessageRecipientWallet(null);
              navigate('/messages');
            }}
            initialRecipient={selectedMessageRecipient}
            initialRecipientWallet={selectedMessageRecipientWallet}
            navigate={navigate}
            onOpenTransferModal={openTransferModal}
            initialProfile={currentProfile && (
              (currentProfile.walletAddress && currentProfile.walletAddress.toLowerCase() === selectedMessageRecipient?.toLowerCase()) ||
              (currentProfile.username && currentProfile.username.toLowerCase() === selectedMessageRecipient?.toLowerCase())
            ) ? currentProfile : null}
          />
        )}
        {/* Activity Feed section */}
        {walletAddress && activityFeedVisible && (
          <ActivityFeed
            walletAddress={walletAddress}
            userLocation={myProfileForMatch?.location || null}
            isMobile={isMobile}
            onClose={() => {
              setActivityFeedVisible(false);
              // Navigate away from /activityfeed route when closing
              if (normalizedPathname === '/activityfeed') {
                navigate('/');
              }
            }}
            onOpenMemberProfile={async (memberAddress) => {
              try {
                const isOwnProfile = walletAddress && memberAddress && 
                  memberAddress.toLowerCase() === walletAddress.toLowerCase();
                
                // Close activity feed FIRST to prevent overlap
                setActivityFeedVisible(false);
                
                const memberProfile = await getUserProfileByWallet(memberAddress, null);
                if (memberProfile) {
                  const cleanMemberProfile = cleanProfileData(memberProfile);
                  
                  if (isOwnProfile) {
                    setCurrentUser(cleanMemberProfile.username);
                    // Navigate to my profile route
                    navigate('/myprofile');
                  } else {
                    setCurrentUser(null);
                    // Navigate to profile route
                    if (cleanMemberProfile.username) {
                      navigate(`/profile/${cleanMemberProfile.username}`);
                    }
                  }
                  
                  setCurrentProfile(cleanMemberProfile);
                  setProfileVisible(true);
                  setAboutVisible(false);
                  setShowRegistrationForm(false);
                  
                  if (cleanMemberProfile.location && cleanMemberProfile.location.lat && cleanMemberProfile.location.lng) {
                    const lat = cleanMemberProfile.location.lat;
                    const lng = cleanMemberProfile.location.lng;
                    
                    if (mapInstanceRef.current) {
                      mapInstanceRef.current.setView([lat, lng], 13, { animate: true, duration: 1.0 });
                      
                      setTimeout(() => {
                        if (markersRef.current && markersRef.current.length > 0) {
                          const memberMarker = markersRef.current.find(m => {
                            if (m.getLatLng) {
                              const markerLatLng = m.getLatLng();
                              const tolerance = 0.0001;
                              return Math.abs(markerLatLng.lat - lat) < tolerance && 
                                     Math.abs(markerLatLng.lng - lng) < tolerance;
                            }
                            return false;
                          });
                          if (memberMarker && typeof memberMarker.openPopup === 'function') {
                            memberMarker.openPopup();
                          }
                        }
                      }, 800);
                    }
                  }
                } else {
                  window.alert('Could not load member profile');
                }
              } catch (error) {
                console.error('Error opening member profile:', error);
                window.alert(`Error loading profile: ${error.message}`);
              }
            }}
            onOpenGroup={async (groupId) => {
              setGroupsVisible(true);
              setActivityFeedVisible(false);
            }}
          />
        )}
        {/* TagRUN section */}
        {walletAddress && gamificationData && (
          <div id="tagrun-section" className={tagRunVisible ? '' : 'hidden'}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#333', margin: 0 }}>TagRUN</h2>
                <button
                  onClick={() => {
                    setTagRunVisible(false);
                    navigate('/');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#666',
                    cursor: 'pointer',
                    fontSize: '24px',
                    padding: '0',
                    lineHeight: 1,
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>
              <p style={{ 
                fontSize: '13px', 
                color: '#666', 
                margin: 0, 
                lineHeight: '1.5',
              }}>
                Social campaign to help PeopleTag grow and reward early contributors.
              </p>
            </div>

            {/* Your progress section */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Your progress:</span>
              </div>
              
              {/* Quick Stats */}
              <div style={{ display: 'flex', gap: '6px', marginTop: '12px', flexWrap: 'nowrap', width: '100%', boxSizing: 'border-box' }}>
                {/* Profile Completeness */}
                <div style={{
                  flex: 1,
                  padding: '6px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  minWidth: 0,
                  boxSizing: 'border-box'
                }}>
                  <div style={{ fontSize: '10px', color: '#666', marginBottom: '3px' }}>Profile Complete</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                    {gamificationData.profileCompleteness || 0}%
                  </div>
                  <div style={{
                    width: '100%',
                    height: '5px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '3px',
                    marginTop: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${gamificationData.profileCompleteness || 0}%`,
                      height: '100%',
                      backgroundColor: gamificationData.profileCompleteness >= 100 ? '#4caf50' :
                                       gamificationData.profileCompleteness >= 75 ? '#4a90e2' :
                                       gamificationData.profileCompleteness >= 50 ? '#ff9800' : '#ffc107',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                {/* Activity Streak */}
                <div style={{
                  flex: 1,
                  padding: '6px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  minWidth: 0,
                  boxSizing: 'border-box'
                }}>
                  <div style={{ fontSize: '10px', color: '#666', marginBottom: '3px' }}>Current Streak</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    🔥 {gamificationData.currentStreak || 0} days
                  </div>
                  <div style={{ fontSize: '9px', color: '#999', marginTop: '3px' }}>
                    Best: {gamificationData.longestStreak || 0} days
                  </div>
                </div>

                {/* Points */}
                <div style={{
                  flex: 1,
                  padding: '6px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  minWidth: 0,
                  boxSizing: 'border-box'
                }}>
                  <div style={{ fontSize: '10px', color: '#666', marginBottom: '3px' }}>Points</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                    {gamificationData.points || 0}
                  </div>
                  <div style={{ fontSize: '9px', color: '#999', marginTop: '3px' }}>
                    {(gamificationData.badges || []).length} badges
                  </div>
                </div>
              </div>

              {/* Badges Display */}
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '10px', color: '#333' }}>
                  Badges & Achievements
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', position: 'relative' }}>
                    {Object.values(BADGES).map((badge) => {
                      const hasBadge = (gamificationData.badges || []).includes(badge.id);
                      const isHovered = hoveredBadge === badge.id;
                      
                      // Calculate badge progress
                      let progress = { current: 0, target: 0, hasBadge };
                      switch (badge.id) {
                        case 'profile_starter':
                          progress = { current: gamificationData.profileCompleteness || 0, target: 25, hasBadge };
                          break;
                        case 'profile_builder':
                          progress = { current: gamificationData.profileCompleteness || 0, target: 50, hasBadge };
                          break;
                        case 'profile_master':
                          progress = { current: gamificationData.profileCompleteness || 0, target: 75, hasBadge };
                          break;
                        case 'profile_legend':
                          progress = { current: gamificationData.profileCompleteness || 0, target: 100, hasBadge };
                          break;
                        case 'first_steps':
                          progress = { current: gamificationData.totalVisits || 0, target: 1, hasBadge };
                          break;
                        case 'week_warrior':
                          progress = { current: gamificationData.currentStreak || 0, target: 7, hasBadge };
                          break;
                        case 'month_master':
                          progress = { current: gamificationData.currentStreak || 0, target: 30, hasBadge };
                          break;
                        case 'streak_champion':
                          progress = { current: gamificationData.currentStreak || 0, target: 100, hasBadge };
                          break;
                        case 'social_butterfly': {
                          const socialCount = Array.isArray(currentProfile?.socialLinks) ? currentProfile.socialLinks.length : 0;
                          progress = { current: socialCount, target: 5, hasBadge };
                          break;
                        }
                        case 'tag_collector': {
                          const tagCount = Array.isArray(currentProfile?.tagsList) ? currentProfile.tagsList.length : 0;
                          progress = { current: tagCount, target: 10, hasBadge };
                          break;
                        }
                        case 'connector':
                          progress = { current: gamificationData.messagesSent || 0, target: 1, hasBadge };
                          break;
                        case 'explorer':
                          progress = { current: gamificationData.profilesViewed || 0, target: 50, hasBadge };
                          break;
                        case 'early_adopter':
                          progress = { current: hasBadge ? 1 : 0, target: 1, hasBadge };
                          break;
                        case 'x_connector': {
                          const hasXCom = Array.isArray(currentProfile?.socialLinks) && 
                            currentProfile.socialLinks.some(link => {
                              const normalized = link.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                              return normalized.includes('x.com/') || normalized.includes('twitter.com/');
                            });
                          progress = { current: hasXCom ? 1 : 0, target: 1, hasBadge };
                          break;
                        }
                        case 'discord_connector': {
                          const hasDiscord = Array.isArray(currentProfile?.socialLinks) && 
                            currentProfile.socialLinks.some(link => {
                              const normalized = link.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                              return normalized.includes('discord.gg/') || normalized.includes('discord.com/');
                            });
                          progress = { current: hasDiscord ? 1 : 0, target: 1, hasBadge };
                          break;
                        }
                        case 'bullish_poster': {
                          const claimedPosts = gamificationData.claimedXPosts || [];
                          progress = { current: claimedPosts.length, target: 1, hasBadge };
                          break;
                        }
                        case 'referral_starter':
                          progress = { current: referralStats?.referralsCount || 0, target: 1, hasBadge };
                          break;
                        case 'referral_champion':
                          progress = { current: referralStats?.referralsCount || 0, target: 10, hasBadge };
                          break;
                        case 'referral_legend':
                          progress = { current: referralStats?.referralsCount || 0, target: 100, hasBadge };
                          break;
                        case 'referral_master':
                          progress = { current: referralStats?.referralsCount || 0, target: 1000, hasBadge };
                          break;
                        default:
                          progress = { current: 0, target: 0, hasBadge };
                      }
                      
                      return (
                        <div
                          key={badge.id}
                          style={{
                            position: 'relative',
                            width: 'calc((100% - 16px) / 3)',
                            padding: '8px 12px',
                            backgroundColor: hasBadge ? '#e8f5e9' : '#f5f5f5',
                            borderRadius: '6px',
                            border: `1px solid ${hasBadge ? badge.color : '#e0e0e0'}`,
                            opacity: hasBadge ? 1 : 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '12px',
                            cursor: 'help',
                            zIndex: isHovered ? 10001 : 'auto',
                            boxSizing: 'border-box'
                          }}
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTooltipPosition({
                              top: rect.top,
                              left: rect.left + rect.width / 2,
                              badgeId: badge.id
                            });
                            setHoveredBadge(badge.id);
                          }}
                          onMouseLeave={() => {
                            setHoveredBadge(null);
                            setTooltipPosition({ top: 0, left: 0, badgeId: null });
                          }}
                        >
                          <span style={{ fontSize: '16px' }}>{badge.icon}</span>
                          <span style={{ color: hasBadge ? '#333' : '#999' }}>{badge.name}</span>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Tooltip */}
                  {hoveredBadge && tooltipPosition.badgeId && (() => {
                    const badge = Object.values(BADGES).find(b => b.id === hoveredBadge);
                    if (!badge) return null;
                    const hasBadge = (gamificationData.badges || []).includes(badge.id);
                    
                    let progress = { current: 0, target: 0, hasBadge };
                    switch (badge.id) {
                      case 'profile_starter':
                        progress = { current: gamificationData.profileCompleteness || 0, target: 25, hasBadge };
                        break;
                      case 'profile_builder':
                        progress = { current: gamificationData.profileCompleteness || 0, target: 50, hasBadge };
                        break;
                      case 'profile_master':
                        progress = { current: gamificationData.profileCompleteness || 0, target: 75, hasBadge };
                        break;
                      case 'profile_legend':
                        progress = { current: gamificationData.profileCompleteness || 0, target: 100, hasBadge };
                        break;
                      case 'first_steps':
                        progress = { current: gamificationData.totalVisits || 0, target: 1, hasBadge };
                        break;
                      case 'week_warrior':
                        progress = { current: gamificationData.currentStreak || 0, target: 7, hasBadge };
                        break;
                      case 'month_master':
                        progress = { current: gamificationData.currentStreak || 0, target: 30, hasBadge };
                        break;
                      case 'streak_champion':
                        progress = { current: gamificationData.currentStreak || 0, target: 100, hasBadge };
                        break;
                      case 'social_butterfly': {
                        const socialCount = Array.isArray(currentProfile?.socialLinks) ? currentProfile.socialLinks.length : 0;
                        progress = { current: socialCount, target: 5, hasBadge };
                        break;
                      }
                      case 'tag_collector': {
                        const tagCount = Array.isArray(currentProfile?.tagsList) ? currentProfile.tagsList.length : 0;
                        progress = { current: tagCount, target: 10, hasBadge };
                        break;
                      }
                      case 'connector':
                        progress = { current: gamificationData.messagesSent || 0, target: 1, hasBadge };
                        break;
                      case 'explorer':
                        progress = { current: gamificationData.profilesViewed || 0, target: 50, hasBadge };
                        break;
                      case 'early_adopter':
                        progress = { current: hasBadge ? 1 : 0, target: 1, hasBadge };
                        break;
                      case 'x_connector': {
                        const hasXCom = Array.isArray(currentProfile?.socialLinks) && 
                          currentProfile.socialLinks.some(link => {
                            const normalized = link.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                            return normalized.includes('x.com/') || normalized.includes('twitter.com/');
                          });
                        progress = { current: hasXCom ? 1 : 0, target: 1, hasBadge };
                        break;
                      }
                      case 'discord_connector': {
                        const hasDiscord = Array.isArray(currentProfile?.socialLinks) && 
                          currentProfile.socialLinks.some(link => {
                            const normalized = link.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                            return normalized.includes('discord.gg/') || normalized.includes('discord.com/');
                          });
                        progress = { current: hasDiscord ? 1 : 0, target: 1, hasBadge };
                        break;
                      }
                      case 'bullish_poster': {
                        const claimedPosts = gamificationData.claimedXPosts || [];
                        progress = { current: claimedPosts.length, target: 1, hasBadge };
                        break;
                      }
                      case 'referral_starter':
                        progress = { current: referralStats?.referralsCount || 0, target: 1, hasBadge };
                        break;
                      case 'referral_champion':
                        progress = { current: referralStats?.referralsCount || 0, target: 10, hasBadge };
                        break;
                      case 'referral_legend':
                        progress = { current: referralStats?.referralsCount || 0, target: 100, hasBadge };
                        break;
                      case 'referral_master':
                        progress = { current: referralStats?.referralsCount || 0, target: 1000, hasBadge };
                        break;
                      default:
                        progress = { current: 0, target: 0, hasBadge };
                    }
                    
                    return createPortal(
                      <div
                        style={{
                          position: 'fixed',
                          top: `${tooltipPosition.top - 10}px`,
                          left: `${tooltipPosition.left}px`,
                          transform: 'translate(-50%, -100%)',
                          padding: '10px 12px',
                          backgroundColor: '#333',
                          color: '#fff',
                          borderRadius: '6px',
                          fontSize: '12px',
                          zIndex: 10000,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                          pointerEvents: 'none',
                          minWidth: '200px',
                          maxWidth: '300px',
                          whiteSpace: 'normal'
                        }}
                      >
                        <div style={{ fontWeight: '600', marginBottom: '4px', color: badge.color }}>
                          {badge.name}
                        </div>
                        <div style={{ marginBottom: '6px', fontSize: '11px', color: '#ccc' }}>
                          {badge.description}
                        </div>
                        <div style={{ marginBottom: '6px', fontSize: '11px', color: '#ffc107', fontWeight: '500' }}>
                          🎯 {badge.points || 10} points
                        </div>
                        {!hasBadge && progress.target > 0 && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#fff',
                            paddingTop: '6px',
                            borderTop: '1px solid #555'
                          }}>
                            {['profile_starter', 'profile_builder', 'profile_master', 'profile_legend'].includes(badge.id) ? (
                              <>Progress: {progress.current}% / {progress.target}%</>
                            ) : (
                              <>Progress: {progress.current}/{progress.target}</>
                            )}
                          </div>
                        )}
                        {hasBadge && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#4caf50',
                            paddingTop: '6px',
                            borderTop: '1px solid #555'
                          }}>
                            ✓ Completed
                          </div>
                        )}
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: '6px solid #333'
                          }}
                        />
                      </div>,
                      document.body
                    );
                  })()}
                </div>
            </div>

            {/* X.com Post Submission */}
            <div className="profile-section">
              <div style={{ padding: '12px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>📈</span>
                    <span>Post About PeopleTag on X.com</span>
                  </div>
                </div>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
                  Earn 300 points for posting about PeopleTag on X.com!
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <button
                        onClick={handleGenerateTwitterPost}
                        disabled={isGeneratingTwitterPost}
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          background: isGeneratingTwitterPost ? '#ccc' : '#4a90e2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: isGeneratingTwitterPost ? 'wait' : 'pointer',
                          opacity: isGeneratingTwitterPost ? 0.7 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        {isGeneratingTwitterPost ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ animation: 'spin 1s linear infinite' }}>
                              <circle cx="12" cy="12" r="10" strokeDasharray="31.416" strokeDashoffset="15.708" opacity="0.3"/>
                              <circle cx="12" cy="12" r="10" strokeDasharray="15.708 31.416" strokeDashoffset="0" opacity="0.8"/>
                            </svg>
                            <span>Generating...</span>
                          </>
                        ) : (
                          <span>Generate Post</span>
                        )}
                      </button>
                      <button
                        onClick={handlePostToX}
                        disabled={!generatedPostText || isGeneratingTwitterPost}
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          background: (!generatedPostText || isGeneratingTwitterPost) ? '#ccc' : '#1DA1F2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: (!generatedPostText || isGeneratingTwitterPost) ? 'not-allowed' : 'pointer',
                          opacity: (!generatedPostText || isGeneratingTwitterPost) ? 0.7 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                        </svg>
                        <span>Post in X.com</span>
                      </button>
                    </div>
                    {generatedPostText && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        marginBottom: '8px',
                        fontSize: '13px',
                        color: '#333',
                        lineHeight: '1.5',
                        wordBreak: 'break-word'
                      }}>
                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', fontWeight: '500' }}>
                          Generated Post:
                        </div>
                        <div>{generatedPostText}</div>
                      </div>
                    )}
                    <input
                      type="text"
                      value={xPostUrl}
                      onChange={(e) => setXPostUrl(e.target.value)}
                      placeholder="Paste your X.com post URL here (e.g., https://x.com/username/status/123456)"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        marginBottom: '8px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      onClick={async () => {
                        if (!xPostUrl.trim()) {
                          alert('Please enter a post URL');
                          return;
                        }
                        if (isSubmittingXPost) return;
                        
                        setIsSubmittingXPost(true);
                        try {
                          const result = await awardBullishPost(walletAddress, xPostUrl.trim());
                          if (result.success) {
                            setNotification({ message: result.message, type: 'success' });
                            setTimeout(() => setNotification(null), 7000);
                            setSubmittedXPostUrl(xPostUrl.trim()); // Save the submitted URL
                            setXPostUrl(''); // Clear the input field
                            if (walletAddress) {
                              const updatedData = await getUserGamificationData(walletAddress);
                              if (updatedData) {
                                setGamificationData(updatedData);
                              }
                            }
                          } else {
                            setNotification({ message: result.message || 'Error submitting post. Please try again.', type: 'error' });
                            setTimeout(() => setNotification(null), 7000);
                          }
                        } catch (error) {
                          console.error('Error submitting X.com post:', error);
                          setNotification({ message: 'Error submitting post. Please try again.', type: 'error' });
                          setTimeout(() => setNotification(null), 7000);
                        } finally {
                          setIsSubmittingXPost(false);
                        }
                      }}
                      disabled={isSubmittingXPost || !xPostUrl.trim() || submittedXPostUrl !== null}
                      style={{
                        width: '100%',
                        padding: '8px 16px',
                        background: (isSubmittingXPost || !xPostUrl.trim() || submittedXPostUrl !== null) ? '#ccc' : '#ff9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: (isSubmittingXPost || !xPostUrl.trim() || submittedXPostUrl !== null) ? 'not-allowed' : 'pointer',
                        opacity: (isSubmittingXPost || !xPostUrl.trim() || submittedXPostUrl !== null) ? 0.7 : 1
                      }}
                    >
                      {isSubmittingXPost ? 'Submitting...' : 'Submit & Claim 300 Points'}
                    </button>
                    {submittedXPostUrl && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '11px',
                        color: '#666',
                        fontStyle: 'italic',
                        wordBreak: 'break-all'
                      }}>
                        Submitted tweet: {submittedXPostUrl}
                      </div>
                    )}
                  </div>
              </div>
            </div>

            {/* Share & Invite System */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Share & Invite</span>
              </div>
              
              {/* Inviter Information */}
              {inviterProfile && (
                <div 
                  onClick={() => navigate(`/profile/${inviterProfile.username}`)}
                  style={{ 
                    marginBottom: '16px', 
                    padding: '12px', 
                    backgroundColor: '#f0f7ff', 
                    borderRadius: '8px',
                    border: '1px solid #d0e7ff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e0f0ff';
                    e.currentTarget.style.borderColor = '#b0d7ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f7ff';
                    e.currentTarget.style.borderColor = '#d0e7ff';
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                    You were invited by
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {inviterProfile.avatar && (
                      <img 
                        src={inviterProfile.avatar} 
                        alt={inviterProfile.name}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                      {inviterProfile.name}
                    </div>
                    {inviterProfile.username !== inviterProfile.name && (
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        @{inviterProfile.username}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Referral Link */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                  Your Referral Link (Earn 10 points per referral)
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    readOnly
                    value={referralStats?.referralCode ? `${window.location.origin}?ref=${referralStats.referralCode}` : 'Loading...'}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                      backgroundColor: '#f8f9fa',
                      color: '#333'
                    }}
                  />
                  <button
                    onClick={async () => {
                      const refLink = referralStats?.referralCode ? `${window.location.origin}?ref=${referralStats.referralCode}` : '';
                      if (refLink) {
                        try {
                          await navigator.clipboard.writeText(refLink);
                          setNotification({ message: 'Referral link copied!', type: 'success' });
                          setTimeout(() => setNotification(null), 3000);
                        } catch (err) {
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = refLink;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          setNotification({ message: 'Referral link copied!', type: 'success' });
                          setTimeout(() => setNotification(null), 3000);
                        }
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#4a90e2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Copy
                  </button>
                </div>
                {referralStats && (
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}>
                    {referralStats.referralsCount || 0} {referralStats.referralsCount === 1 ? 'referral' : 'referrals'} • {((referralStats.referralsCount || 0) * 10)} points earned
                  </div>
                )}
              </div>

              {/* Personalized Invite Message */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Customize Your Invite Message</span>
                  <button
                    onClick={async () => {
                      // Only check if wallet is connected - profile can be minimal
                      if (!walletAddress) {
                        setNotification({ message: 'Please connect your wallet first', type: 'error' });
                        setTimeout(() => setNotification(null), 3000);
                        return;
                      }
                      
                      if (isGeneratingInvite) return; // Prevent multiple clicks
                      
                      setIsGeneratingInvite(true);
                      try {
                        // Use a COMPLETELY SEPARATE request from TagAI - different endpoint and structure
                        const apiUrl = process.env.REACT_APP_API_URL || 
                          (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
                        
                        // Get profile data for personalization (use currentProfile if available, otherwise empty strings)
                        const profile = currentProfile || {};
                        const name = profile.name || profile.username || '';
                        const bio = profile.bio || '';
                        const city = profile.city || '';
                        const tags = Array.isArray(profile.tagsList) ? profile.tagsList : [];
                        const cleanedTags = tags.map(tag => tag.replace(/^#+/, '').trim()).filter(t => t);
                        
                        // Build profile context in a simple, direct format
                        let profileInfo = [];
                        if (name) profileInfo.push(`I'm ${name}`);
                        if (city) profileInfo.push(`from ${city}`);
                        if (cleanedTags.length > 0) {
                          profileInfo.push(`interested in ${cleanedTags.slice(0, 3).join(', ')}`);
                        }
                        if (bio) {
                          const shortBio = bio.substring(0, 100).trim();
                          if (shortBio) profileInfo.push(`About me: ${shortBio}`);
                        }
                        const profileContext = profileInfo.join('. ') + '.';
                        
                        // Build the complete prompt using the template from App.js
                        const generationPrompt = INVITE_MESSAGE_AI_PROMPT.replace('{PROFILE_CONTEXT}', profileContext);
                        
                        // Use a completely different request structure - single user message, no system message
                        // This ensures it's treated as a pure text generation task, not TagAI
                        const controller = new AbortController();
                        const timeoutId = setTimeout(() => controller.abort(), 30000);
                        
                        let response;
                        try {
                          // Make a simple, direct request - send the complete prompt as user message
                          // The prompt is built in App.js using INVITE_MESSAGE_AI_PROMPT constant
                          response = await fetch(`${apiUrl}/api/v1/ai/chat`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              messages: [
                                {
                                  role: 'user',
                                  content: generationPrompt
                                }
                              ],
                              model: selectedModel || 'llama-3.1-8b'
                            }),
                            signal: controller.signal
                          });
                        } catch (fetchError) {
                          clearTimeout(timeoutId);
                          if (fetchError.name === 'AbortError') {
                            throw new Error('Request timed out. Please try again.');
                          }
                          throw fetchError;
                        }
                        clearTimeout(timeoutId);
                        
                        if (!response.ok) {
                          const errorText = await response.text().catch(() => 'Unknown error');
                          throw new Error(`API error: ${response.status} - ${errorText}`);
                        }
                        
                        const contentType = response.headers.get('content-type') || '';
                        if (!contentType.includes('application/json')) {
                          const text = await response.text();
                          throw new Error(`Server returned ${contentType} instead of JSON: ${text.substring(0, 200)}`);
                        }
                        
                        const data = await response.json();
                        let aiMessage = data.message || data.text || data.response || '';
                        
                        console.log('🤖 Invite message AI response (raw):', aiMessage);
                        
                        // Check if AI generated TagAI-style response (should NEVER happen)
                        if (aiMessage.includes('TagAI') || 
                            aiMessage.includes('I searched the PeopleTag database') ||
                            aiMessage.includes('I\'m TagAI') ||
                            aiMessage.toLowerCase().includes('i\'m tagai')) {
                          console.error('❌ AI generated TagAI-style response! This should never happen!');
                          console.error('❌ Full response:', aiMessage);
                          // Reject this response and use fallback
                          throw new Error('AI generated TagAI-style response instead of invite message');
                        }
                        
                        // Clean up the AI response
                        aiMessage = aiMessage.trim();
                        
                        // Remove markdown formatting (**text** becomes text)
                        aiMessage = aiMessage.replace(/\*\*(.+?)\*\*/g, '$1');
                        aiMessage = aiMessage.replace(/\*(.+?)\*/g, '$1');
                        
                        // Remove surrounding quotes if AI added them
                        if ((aiMessage.startsWith('"') && aiMessage.endsWith('"')) ||
                            (aiMessage.startsWith("'") && aiMessage.endsWith("'"))) {
                          aiMessage = aiMessage.slice(1, -1).trim();
                        }
                        
                        // Remove any leading explanation text (e.g., "Here's a message:" or "Message:")
                        const messageMatch = aiMessage.match(/(?:message|invite|text)[:]\s*(.+)/is);
                        if (messageMatch && messageMatch[1] && messageMatch[1].trim().length >= 10) {
                          aiMessage = messageMatch[1].trim();
                        }
                        
                        // Extract just the message if AI wrapped it in quotes within explanation
                        const quotedMatch = aiMessage.match(/"([^"]+)"/);
                        if (quotedMatch && quotedMatch[1] && quotedMatch[1].trim().length >= 10) {
                          aiMessage = quotedMatch[1].trim();
                        }
                        
                        // Final cleanup - remove any remaining markdown or formatting
                        aiMessage = aiMessage.replace(/^\*\*|\*\*$/g, '').replace(/^\*|\*$/g, '').trim();
                        
                        // Validate response - must be reasonable length and not empty
                        if (!aiMessage || aiMessage.trim().length < 10) {
                          console.error('❌ Message too short after cleanup:', aiMessage);
                          throw new Error('AI returned an invalid or empty message after cleanup');
                        }
                        
                        // Truncate if too long (keep under 170 chars as per prompt requirement)
                        if (aiMessage.length > 170) {
                          // Try to truncate at a sentence boundary if possible
                          const truncated = aiMessage.substring(0, 170);
                          const lastSentenceEnd = Math.max(
                            truncated.lastIndexOf('.'),
                            truncated.lastIndexOf('!'),
                            truncated.lastIndexOf('?')
                          );
                          if (lastSentenceEnd > 100) {
                            aiMessage = aiMessage.substring(0, lastSentenceEnd + 1).trim();
                          } else {
                            aiMessage = truncated.trim();
                          }
                        }
                        
                        console.log('✅ Final invite message:', aiMessage);
                        
                        // Append invite link to the message
                        const refLink = referralStats?.referralCode ? `${window.location.origin}?ref=${referralStats.referralCode}` : window.location.origin;
                        const messageWithLink = `${aiMessage} ${refLink}`;
                        
                        setInviteMessage(messageWithLink);
                        setNotification({ message: 'AI-generated invite message created!', type: 'success' });
                        setTimeout(() => setNotification(null), 3000);
                      } catch (error) {
                        console.error('Error generating invite message with AI:', error);
                        // Check if it's a connection error
                        const isConnectionError = error.message?.includes('Failed to fetch') || 
                                                  error.message?.includes('ERR_CONNECTION_REFUSED') ||
                                                  error.message?.includes('Connection refused');
                        
                        // Fallback to template-based generation if AI fails
                        const fallbackMessages = [
                          `Join me on PeopleTag! It's a great platform to connect with people who share your interests.`,
                          `Hey! I just discovered PeopleTag - a platform to connect with like-minded people. Join me!`,
                          `Join me on PeopleTag and let's build meaningful connections together!`
                        ];
                        const fallbackMessage = fallbackMessages[Math.floor(Math.random() * fallbackMessages.length)];
                        // Append invite link to fallback message too
                        const refLink = referralStats?.referralCode ? `${window.location.origin}?ref=${referralStats.referralCode}` : window.location.origin;
                        const fallbackMessageWithLink = `${fallbackMessage} ${refLink}`;
                        setInviteMessage(fallbackMessageWithLink);
                        setNotification({ 
                          message: isConnectionError 
                            ? 'AI server not available. Using template message. Please start the API server to use AI generation.' 
                            : 'AI generation failed, using template. Please try again later.', 
                          type: 'warning' 
                        });
                        setTimeout(() => setNotification(null), 5000);
                      } finally {
                        setIsGeneratingInvite(false);
                      }
                    }}
                    disabled={isGeneratingInvite}
                    style={{
                      padding: '6px 12px',
                      background: isGeneratingInvite ? '#ccc' : '#4a90e2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: isGeneratingInvite ? 'wait' : 'pointer',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      opacity: isGeneratingInvite ? 0.7 : 1
                    }}
                  >
                    {isGeneratingInvite ? (
                      <>
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        <span style={{
                          display: 'inline-block',
                          animation: 'spin 1s linear infinite',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>#</span>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                          <path d="M2 17l10 5 10-5"/>
                          <path d="M2 12l10 5 10-5"/>
                        </svg>
                        Generate with AI
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder="Add a personal message (optional)..."
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Social Sharing Buttons */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                  Share on Social Media
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      // Invite message already includes the link, so use it directly
                      // If no custom message, create default with link
                      const refLink = referralStats?.referralCode ? `${window.location.origin}?ref=${referralStats.referralCode}` : window.location.origin;
                      const baseMessage = inviteMessage || `Join me on PeopleTag! Connect with amazing people through shared interests. ${refLink}`;
                      // Check if message already has the link (if it was AI-generated), otherwise append it
                      const text = baseMessage.includes(refLink) ? baseMessage : `${baseMessage} ${refLink}`;
                      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
                      window.open(twitterUrl, '_blank');
                    }}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      padding: '8px 16px',
                      background: '#1DA1F2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                    </svg>
                    Twitter/X
                  </button>
                  <button
                    onClick={() => {
                      // Invite message already includes the link, so use it directly
                      // If no custom message, create default with link
                      const refLink = referralStats?.referralCode ? `${window.location.origin}?ref=${referralStats.referralCode}` : window.location.origin;
                      const baseMessage = inviteMessage || `Join me on PeopleTag! Connect with amazing people through shared interests. ${refLink}`;
                      // Check if message already has the link (if it was AI-generated), otherwise append it
                      const text = baseMessage.includes(refLink) ? baseMessage : `${baseMessage} ${refLink}`;
                      // Discord doesn't have a direct share URL, so we'll copy to clipboard
                      try {
                        navigator.clipboard.writeText(text);
                        setNotification({ message: 'Message copied! Paste it in Discord.', type: 'success' });
                        setTimeout(() => setNotification(null), 3000);
                      } catch (err) {
                        const textArea = document.createElement('textarea');
                        textArea.value = text;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        setNotification({ message: 'Message copied! Paste it in Discord.', type: 'success' });
                        setTimeout(() => setNotification(null), 3000);
                      }
                    }}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      padding: '8px 16px',
                      background: '#5865F2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    Discord
                  </button>
                </div>
              </div>

            </div>

            {/* Leaderboard */}
            <div className="profile-section" style={{ marginBottom: '24px' }}>
              <div style={{
                  marginTop: '16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '12px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: 'white'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>Leaderboard</div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        value={leaderboardType}
                        onChange={async (e) => {
                          const type = e.target.value;
                          setLeaderboardType(type);
                          try {
                            const leaderboard = await getLeaderboard(type, 50);
                            setLeaderboardData(leaderboard || []);
                            if (walletAddress) {
                              const rank = await getUserRank(walletAddress, type);
                              setUserRank(rank);
                            }
                            
                            // Use cached profile lookup map (no additional queries)
                            const profileLookupMap = profileLookupMapRef.current;
                            
                            // Build username map efficiently from cache
                            const usernameMap = {};
                            (leaderboard || []).forEach((entry) => {
                              const normalizedAddr = entry.walletAddress.toLowerCase();
                              let username = profileLookupMap[normalizedAddr];
                              
                              // Fallback: try temporary address lookup
                              if (!username) {
                                username = getUsernameFromTemporaryAddress(normalizedAddr);
                              }
                              
                              // Final fallback: generate username for test users
                              if (!username) {
                                const shortAddr = normalizedAddr.slice(2, 10);
                                username = `testuser_${shortAddr}`;
                              }
                              
                              usernameMap[normalizedAddr] = username;
                            });
                            
                            setLeaderboardUsernames(usernameMap);
                          } catch (error) {
                            console.error('Error loading leaderboard:', error);
                            setLeaderboardData([]);
                            setUserRank(null);
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <option value="points">Points</option>
                        <option value="streak">Streak</option>
                        <option value="badges">Badges</option>
                        <option value="completeness">Completeness</option>
                        <option value="referral">Referral</option>
                      </select>
                    </div>
                  </div>
                  <div style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    padding: '12px'
                  }}>
                    {userRank && (
                      <div style={{
                        padding: '10px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '6px',
                        marginBottom: '10px',
                        fontSize: '12px',
                        color: '#333'
                      }}>
                        Your rank: <strong>#{userRank}</strong>
                      </div>
                    )}
                    {leaderboardData.length === 0 ? (
                      <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '13px'
                      }}>
                        No leaderboard data available yet. Be the first!
                      </div>
                    ) : (
                      leaderboardData.map((entry, index) => {
                        const isCurrentUser = entry.walletAddress === walletAddress?.toLowerCase();
                        return (
                          <div
                            key={entry.walletAddress}
                            style={{
                              padding: '8px',
                              marginBottom: '6px',
                              backgroundColor: isCurrentUser ? '#e3f2fd' : 'white',
                              borderRadius: '6px',
                              border: isCurrentUser ? '2px solid #4a90e2' : '1px solid #e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: index < 3 ? '#333' : '#666',
                                flexShrink: 0
                              }}>
                                {index + 1}
                              </div>
                              <div>
                                <div style={{ fontSize: '11px', fontWeight: '500', color: '#333' }}>
                                  {leaderboardUsernames[entry.walletAddress.toLowerCase()] || 
                                   entry.walletAddress.slice(0, 6) + '...' + entry.walletAddress.slice(-4)}
                                </div>
                                <div style={{ fontSize: '10px', color: '#666' }}>
                                  {leaderboardType === 'points' && `${entry.points} points`}
                                  {leaderboardType === 'streak' && `${entry.value} day streak`}
                                  {leaderboardType === 'badges' && `${entry.value} badges`}
                                  {leaderboardType === 'completeness' && `${entry.value}% complete`}
                                  {leaderboardType === 'referral' && `${entry.value || 0} ${entry.value === 1 ? 'referral' : 'referrals'}`}
                                </div>
                              </div>
                            </div>
                            {isCurrentUser && (
                              <span style={{ fontSize: '10px', color: '#4a90e2', fontWeight: '500' }}>You</span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
            </div>
          </div>
        )}
        {walletAddress && (
          <div 
            id="bug-section" 
            className={bugVisible ? '' : 'hidden'}
            style={{
              display: bugVisible ? 'block' : 'none',
              zIndex: 1005
            }}
          >
            <div className="profile-section">
              <div className="section-title">Report Bug</div>
              <form onSubmit={handleSendBugReport} style={{ padding: '20px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="bug-description" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    Bug Description
                  </label>
                  <textarea
                    id="bug-description"
                    value={bugReport.text}
                    onChange={(e) => setBugReport({ ...bugReport, text: e.target.value })}
                    placeholder="Please describe the bug you encountered..."
                    rows={6}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="bug-file-input" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    Attach File (optional, max 2MB)
                  </label>
                  <input
                    id="bug-file-input"
                    type="file"
                    onChange={handleBugFileChange}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {bugReport.file && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      Selected: {bugReport.file.name} ({(bugReport.file.size / 1024).toFixed(2)} KB)
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#357abd'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4a90e2'}
                >
                  Send Report
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
      <footer 
        className="app-footer"
        style={{
          width: '100%',
          maxWidth: '100%',
          zIndex: activityFeedVisible ? 1000 : 1000
        }}
      >
        <div className="footer-black-bar"></div>
        <div 
          ref={footerBottomRef}
          className="footer-bottom"
          style={{
            maxWidth: messagesVisible ? '50%' : '100%',
            marginRight: messagesVisible ? 'auto' : 
                        ((walletAddress && showRegistrationForm && profileVisible && !currentProfile) ? '700px' :
                        ((tagRunVisible || activityFeedVisible) ? '400px' : '0')),
            marginLeft: '0',
            justifyContent: 'flex-start',
            width: '100%'
          }}
        >
          <div className="footer-copyright">
          © 2026 PeopleTag (v0.3.5-beta)
          <a
            href="https://aidrivenlabs.xyz"
            target="_blank"
            rel="noopener noreferrer"
            className="aidl-credit"
            aria-label="by AI Driven Labs"
          >
            <svg width="14" height="10" viewBox="0 0 40 24" fill="none" aria-hidden="true">
              <defs>
                <linearGradient id="aidlGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#22c1ff"/>
                  <stop offset="50%" stopColor="#7a8cff"/>
                  <stop offset="100%" stopColor="#ff5cc8"/>
                </linearGradient>
              </defs>
              <path
                d="M12 4a8 8 0 1 0 0 16c4 0 6-4 8-8s4-8 8-8a8 8 0 1 1 0 16c-4 0-6-4-8-8s-4-8-8-8z"
                stroke="url(#aidlGrad)"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
            <span>by <strong>AI Driven</strong> Labs</span>
          </a>
          </div>
          <div className="footer-links">
            <a href="#" className="nav-link">Premium</a>
            <a href="#" className="nav-link">Streams</a>
            <a href="#" className="nav-link">Developers</a>
            <a href="#" className={`nav-link ${showCatAds || showCookingAds ? 'active' : ''}`}>Ads</a>
          </div>
          <div className="footer-separator"></div>
          <div
            className="footer-social"
            style={{
              // Match header behavior: shift footer items left when right-side panels are open,
              // even if wallet is disconnected.
              marginRight: (walletAddress && showRegistrationForm && profileVisible && !currentProfile) ? 710 :
                        (profileVisible || groupsVisible || bugVisible || tagRunVisible || activityFeedVisible) ? 360 : 10
            }}
          >
            <a href="https://x.com/peopletaginc" className="social-icon" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
              </svg>
            </a>
            <a href={DISCORD_LINK} className="social-icon" aria-label="Discord" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </a>
            <a href="https://github.com/lxssl/peopletag-protocol" className="social-icon" aria-label="GitHub" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
              </svg>
            </a>
            <NetworkStatus />
          </div>
        </div>
      </footer>
      <HobbyMascot
        userTags={currentProfile?.tagsList || []}
        popularTags={popularTags}
      />
      
      {/* About PeopleTag Section */}
      {aboutVisible && (
        <div className="about-section" ref={aboutSectionRef}>
          <div className="about-content">
            <div className="about-nav">
              <a href="#what-is-peopletag" className={`about-nav-link ${aboutSection === 'what-is-peopletag' ? 'active' : ''}`} onClick={(e) => {
                e.preventDefault();
                navigate('/about/what-is-peopletag');
                // Scroll to top when switching sections
                if (aboutSectionRef.current) {
                  aboutSectionRef.current.scrollTop = 0;
                }
              }}>What is PeopleTag</a>
              <a href="#how-it-works" className={`about-nav-link ${aboutSection === 'how-it-works' ? 'active' : ''}`} onClick={(e) => {
                e.preventDefault();
                navigate('/about/how-it-works');
                // Scroll to top when switching sections
                if (aboutSectionRef.current) {
                  aboutSectionRef.current.scrollTop = 0;
                }
              }}>How it works</a>
              <a href="#api" className={`about-nav-link ${aboutSection === 'api' ? 'active' : ''}`} onClick={(e) => {
                e.preventDefault();
                navigate('/about/api');
                // Scroll to top when switching sections
                if (aboutSectionRef.current) {
                  aboutSectionRef.current.scrollTop = 0;
                }
              }}>API</a>
              <a href="#public-testnet" className={`about-nav-link ${aboutSection === 'public-testnet' ? 'active' : ''}`} onClick={(e) => {
                e.preventDefault();
                navigate('/about/public-testnet');
                // Scroll to top when switching sections
                if (aboutSectionRef.current) {
                  aboutSectionRef.current.scrollTop = 0;
                }
              }}>Public testnet</a>
              <a href="#whitepaper" className={`about-nav-link ${aboutSection === 'whitepaper' ? 'active' : ''}`} onClick={(e) => {
                e.preventDefault();
                navigate('/about/whitepaper');
                // Scroll to top when switching sections
                if (aboutSectionRef.current) {
                  aboutSectionRef.current.scrollTop = 0;
                }
              }}>Whitepaper</a>
              <a href="#roadmap" className={`about-nav-link disabled`} onClick={(e) => {
                e.preventDefault();
              }}>Roadmap</a>
              <a href="#team" className={`about-nav-link ${aboutSection === 'team' ? 'active' : ''}`} onClick={(e) => {
                e.preventDefault();
                navigate('/about/team');
                // Scroll to top when switching sections
                if (aboutSectionRef.current) {
                  aboutSectionRef.current.scrollTop = 0;
                }
              }}>Team</a>
              <a href="#contact-us" className={`about-nav-link ${aboutSection === 'contact-us' ? 'active' : ''}`} onClick={(e) => {
                e.preventDefault();
                navigate('/about/contact-us');
                // Scroll to top when switching sections
                if (aboutSectionRef.current) {
                  aboutSectionRef.current.scrollTop = 0;
                }
              }}>Contact us</a>
            </div>
            
            {aboutSection === 'what-is-peopletag' && (
              <div id="what-is-peopletag">
                <h1>🔍 What Is PeopleTag?</h1>

                <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '20px', marginBottom: '20px', color: '#333' }}>
                  AI-powered meaningful connections for Web3 communities
                </h2>

                <p>PeopleTag powers AI-driven connections in Web3 communities through encrypted interest graphs — with revenue from contextual matching.</p>

                - Imagine AI finding people who are doing what you love to, in your city — in just 10 seconds.<br />
                - Imagine having a robust recommendation AI engine that suggests people, groups, event and activities based on your interests and preferences.<br />
                <p>- Imagine your profile of interests being yours forever on blockchain, working across any app — not locked inside any centralized platform.</p>
                
                <p>That's what PeopleTag enables.</p>

                <p>PeopleTag is an open interest graph protocol for Web3 that enables AI-driven contextual discovery and matching. Unlike centralized social graphs, PeopleTag stores encrypted user profiles in IPFS and anchors ownership on-chain via Arc. This creates a portable, user-owned layer of identity where individuals decide what to share, with whom, and when — without relying on centralized intermediaries.</p>
                
                <p>It's not another social network.</p>
                <p>It's the language Web3 needs to describe who users are — beyond wallet balances and transaction history.</p>
                <p>Any dApp (social, marketplace, DAO, event platform) can plug into the Interest Graph via simple API.</p>
                <p>Users own their profiles data and decide where and how to share them.</p>
                <p>Developers get a standardized way to understand user context without building discovery from scratch.</p>
                <p>It's a portable identity layer that puts users in control of their interests, connections, and attention — powered by AI for meaningful connections.</p>

                <div style={{
                  background: 'linear-gradient(135deg, #fff3e0, #ffe0b2)',
                  border: '2px solid #ff9800',
                  borderRadius: '12px',
                  padding: '20px 24px',
                  marginTop: '30px',
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(255, 152, 0, 0.15)'
                }}>
                  <p style={{ fontSize: '15px', color: '#bf360c', margin: 0 }}>
                    If you would like to support PeopleTag, you may donate to:<br />
                    <span style={{ fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all', userSelect: 'all' }}>
                      0xEC5f14aF3B4fEEaE699eB6B85c76b97a98d3e921
                    </span> 🙏
                  </p>
                </div>

              </div>
            )}

            {aboutSection === 'how-it-works' && (
              <div id="how-it-works">
                <h1>🧪 How it works</h1>
                
                <p>1. User connects wallet (e.g., MetaMask).</p>
                <p>2. Encryption key is derived from a one-time signature (never exposed).</p>
                <p>3. User fills profile (interests, skills, tags), joining groups, sending messages to other users → data is AES-256 encrypted client-side.</p>
                <p>4. Encrypted profile data is stored in IPFS via Pinata, returning a CID.</p>
                <p>5. The CID is written to a smart contract on Arc Testnet, binding it to the user's address. <br />
                Contract Address: <Tooltip text="Click to copy contract address">
                  <span
                    onClick={() => copyToClipboard('0x36014Af11c60FbbB2Db7EF7E419E40201dC43fFe')}
                    style={{ cursor: 'pointer', color: '#4a90e2', textDecoration: 'underline', display: 'inline-block' }}
                  >0x36014Af11c60FbbB2Db7EF7E419E40201dC43fFe</span>
                </Tooltip> → <a
                  href="https://testnet.arcscan.app/address/0x36014Af11c60FbbB2Db7EF7E419E40201dC43fFe"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#4a90e2' }}
                >View on Arcscan</a>
                
                <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                  <button
                    onClick={() => setContractSourceExpanded(!contractSourceExpanded)}
                    style={{
                      padding: '12px 20px',
                      backgroundColor: contractSourceExpanded ? '#007bff' : '#f0f0f0',
                      color: contractSourceExpanded ? '#fff' : '#333',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 600,
                      width: '100%',
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!contractSourceExpanded) {
                        e.currentTarget.style.backgroundColor = '#e0e0e0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!contractSourceExpanded) {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                      }
                    }}
                  >
                    <span>📄 {contractSourceExpanded ? 'Hide' : 'Show'} Contract Source Code</span>
                    <span style={{ fontSize: '20px', transition: 'transform 0.2s', transform: contractSourceExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </button>
                  
                  {contractSourceExpanded && (
                    <div style={{
                      marginTop: '20px',
                      padding: '32px',
                      backgroundColor: '#fff',
                      borderRadius: '12px',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <pre style={{
                        margin: 0,
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        overflowX: 'auto',
                        lineHeight: '1.6',
                        color: '#333'
                      }}>{`// SPDX-License-Identifier: MIT
pragma solidity ^0.8.20;

contract PeopleTagProfile {
    mapping(address => string) public profileCID;

    event ProfileUpdated(address indexed user, string cid);

    function setProfileCID(string calldata cid) external {
        require(bytes(cid).length > 0, "CID cannot be empty");
        profileCID[msg.sender] = cid;
        emit ProfileUpdated(msg.sender, cid);
    }

    function getProfileCID(address user) external view returns (string memory) {
        return profileCID[user];
    }
`}</pre>
                      
                      <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid #e0e0e0' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '24px', color: '#333', fontSize: '24px' }}>What this contract actually does (plain English)</h3>
                        
                        <div style={{ marginBottom: '24px' }}>
                          <h4 style={{ color: '#007bff', marginBottom: '12px', fontSize: '18px', fontWeight: 600 }}>mapping(address {'=>'} string) public profileCID;</h4>
                          <ul style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', paddingLeft: '20px' }}>
                            <li>Stores one string per address</li>
                            <li>Key = user wallet</li>
                            <li>Value = IPFS / Arweave CID (or any string)</li>
                          </ul>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                          <h4 style={{ color: '#007bff', marginBottom: '12px', fontSize: '18px', fontWeight: 600 }}>function setProfileCID(string calldata cid) external</h4>
                          <ul style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', paddingLeft: '20px' }}>
                            <li>Anyone can call it</li>
                            <li>It only writes data for msg.sender</li>
                            <li>You cannot modify anyone else's data</li>
                          </ul>
                          <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', fontFamily: 'monospace', backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                            profileCID[msg.sender] = cid;
                          </p>
                          <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px' }}>
                            <strong>The contract cannot:</strong>
                          </p>
                          <ul style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', paddingLeft: '20px' }}>
                            <li>move ETH</li>
                            <li>move ERC-20 tokens</li>
                            <li>approve anything</li>
                            <li>call other contracts</li>
                          </ul>
                          <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px' }}>
                            It just saves a string
                          </p>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                          <h4 style={{ color: '#007bff', marginBottom: '12px', fontSize: '18px', fontWeight: 600 }}>event ProfileUpdated(address indexed user, string cid);</h4>
                          <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px' }}>
                            Emits an event for indexing (frontend / subgraph use)
                          </p>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                          <h4 style={{ color: '#007bff', marginBottom: '12px', fontSize: '18px', fontWeight: 600 }}>function getProfileCID(address user) external view</h4>
                          <ul style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', paddingLeft: '20px' }}>
                            <li>Read-only</li>
                            <li>No state change</li>
                          </ul>
                        </div>

                        <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                          <h4 style={{ color: '#856404', marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>🔐 Can this contract steal funds?</h4>
                          <p style={{ lineHeight: '1.8', color: '#856404', marginBottom: '12px', fontWeight: 600 }}>
                            Short answer: <strong>NO.</strong>
                          </p>
                          <p style={{ lineHeight: '1.8', color: '#856404', marginBottom: '12px' }}>
                            <strong>Long answer:</strong><br />
                            There is literally no code path that can:
                          </p>
                          <ul style={{ lineHeight: '1.8', color: '#856404', marginBottom: '12px', paddingLeft: '20px' }}>
                            <li>transfer ETH</li>
                            <li>transfer ERC-20 / ERC-721 / ERC-1155</li>
                            <li>set approvals</li>
                            <li>execute delegatecall</li>
                            <li>call another contract</li>
                            <li>access your private key</li>
                            <li>modify storage of another address</li>
                          </ul>
                          <p style={{ lineHeight: '1.8', color: '#856404', marginBottom: 0 }}>
                            This is a pure storage contract.
                          </p>
                        </div>

                        <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: '#d1ecf1', borderRadius: '8px', border: '1px solid #bee5eb' }}>
                          <h4 style={{ color: '#0c5460', marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>🧾 What happens when you "sign / use" it?</h4>
                          <div style={{ marginBottom: '16px' }}>
                            <p style={{ lineHeight: '1.8', color: '#0c5460', marginBottom: '8px', fontWeight: 600 }}>
                              Case 1: You send a transaction (setProfileCID)
                            </p>
                            <p style={{ lineHeight: '1.8', color: '#0c5460', marginBottom: '8px' }}>
                              You will:
                            </p>
                            <ul style={{ lineHeight: '1.8', color: '#0c5460', marginBottom: '12px', paddingLeft: '20px' }}>
                              <li>Pay a small gas fee on Arc</li>
                              <li>Store a string under your own address</li>
                            </ul>
                            <p style={{ lineHeight: '1.8', color: '#0c5460', marginBottom: 0 }}>
                              ✅ Safe
                            </p>
                          </div>
                          <div>
                            <p style={{ lineHeight: '1.8', color: '#0c5460', marginBottom: '8px', fontWeight: 600 }}>
                              Case 2: You sign a message (off-chain signature)
                            </p>
                            <p style={{ lineHeight: '1.8', color: '#0c5460', marginBottom: '8px' }}>
                              If the dApp asks you to sign a message (not a transaction):
                            </p>
                            <ul style={{ lineHeight: '1.8', color: '#0c5460', marginBottom: '12px', paddingLeft: '20px' }}>
                              <li>The contract cannot even see that signature</li>
                              <li>This contract has no signature verification logic</li>
                            </ul>
                            <p style={{ lineHeight: '1.8', color: '#0c5460', marginBottom: 0 }}>
                              ✅ Safe
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <br />
                Every interaction is transparent and verifiable. This is not a simulation — it's functional infrastructure powering AI-driven connections in Web3 communities through encrypted interest graphs — with revenue from contextual matching.</p>
                <p>6. You can selectively publish specific fields (e.g., tags) with PeopleTag or any dApps, allowing granular control, enabling search and matching while keeping sensitive data private. Publishing parts of your profile is optional and you can unpublish anytime. <br />
                <b>Important:</b> Publishing parts of profile makes PeopleTag store published data for search and matching purposes.</p>
                <p>7. When you make any change to your profile, PeopleTag re-encrypts the data and updates the CID in the smart contract on Arc Testnet.
                   We don't want you to spend gas multiple times, so changes are stacked - when you make one or multiple changes to profile, when finished - press green Save button only once to save changes to the contract, or red to discard changes.</p>
                
                <h2>💬 Messaging</h2>
                <p>PeopleTag includes messaging that works seamlessly with the platform. All messages are automatically encrypted using military-grade AES-256-GCM encryption and stored securely in Firestore. Every message is encrypted end-to-end using ECDH (Elliptic Curve Diffie-Hellman) key exchange, ensuring that only you and the recipient can read them. Not even PeopleTag can access your message content.</p>
                
                <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                  <button
                    onClick={() => setMessagingHelpExpanded(!messagingHelpExpanded)}
                    style={{
                      padding: '12px 20px',
                      backgroundColor: messagingHelpExpanded ? '#007bff' : '#f0f0f0',
                      color: messagingHelpExpanded ? '#fff' : '#333',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 600,
                      width: '100%',
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!messagingHelpExpanded) {
                        e.currentTarget.style.backgroundColor = '#e0e0e0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!messagingHelpExpanded) {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                      }
                    }}
                  >
                    <span>📖 {messagingHelpExpanded ? 'Hide' : 'Show'} Complete Messaging Guide</span>
                    <span style={{ fontSize: '20px', transition: 'transform 0.2s', transform: messagingHelpExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </button>
                  
                  {messagingHelpExpanded && (
                    <div style={{
                      marginTop: '20px',
                      padding: '32px',
                      backgroundColor: '#fff',
                      borderRadius: '12px',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ marginTop: 0, marginBottom: '24px', color: '#333', fontSize: '24px' }}>How Messaging Works in PeopleTag</h3>
                      
                      <div style={{ marginBottom: '32px' }}>
                        <h4 style={{ color: '#007bff', marginBottom: '16px', fontSize: '18px' }}>🔒 End-to-End Encrypted Messages</h4>
                        <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px' }}>
                          All messages in PeopleTag are <strong>encrypted using AES-256-GCM encryption</strong>, which is military-grade security. 
                          Only you and the person you're messaging can read them. The encryption key is generated using <strong>ECDH (Elliptic Curve Diffie-Hellman) key exchange</strong>, 
                          ensuring that only people with access to their wallet addresses can decrypt messages. Not even PeopleTag can read them.
                        </p>
                        <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '6px' }}>
                          <strong>🔐 Security Architecture:</strong> PeopleTag uses <strong>ECDH key exchange</strong> with deterministic keypairs derived from wallet addresses. This means:
                        </p>
                        <ul style={{ lineHeight: '2', color: '#666', paddingLeft: '24px', marginBottom: '12px' }}>
                          <li><strong>No signatures required</strong> - Messages can be sent immediately without any wallet signatures</li>
                          <li><strong>Only you and the recipient</strong> can decrypt messages (requires wallet addresses to generate the shared key)</li>
                          <li><strong>True end-to-end encryption</strong> - Messages are encrypted on your device before being stored in Firestore</li>
                          <li><strong>Industry-standard security</strong> - ECDH ensures only parties with the correct wallet addresses can decrypt</li>
                          <li><strong>Deterministic keypairs</strong> - Keypairs are derived from wallet addresses using HKDF (HMAC-based Key Derivation Function), ensuring both parties generate the same shared key</li>
                          <li><strong>Encrypted storage in Firestore</strong> - Encrypted messages (ciphertext + nonce) are stored directly in Firestore, ensuring fast delivery and real-time synchronization</li>
                          <li><strong>Fast and reliable</strong> - Messages are delivered instantly via Firestore's real-time database, with no IPFS gateway delays</li>
                          <li><strong>Client-side encryption</strong> - All encryption and decryption happens on your device - Firestore only stores encrypted data</li>
                        </ul>
                        <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', padding: '12px', backgroundColor: '#d4edda', borderRadius: '6px', border: '1px solid #28a745' }}>
                          <strong>🔒 All conversations are encrypted</strong> - Every message you send and receive is protected with end-to-end encryption. 
                          Look for the lock icon (🔒) in the conversation header to confirm your conversation is encrypted.
                        </p>
                      </div>

                      <div style={{ marginBottom: '32px' }}>
                        <h4 style={{ color: '#007bff', marginBottom: '16px', fontSize: '18px' }}>📤 What Happens When You Send a Message</h4>
                        <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px' }}>
                          Every message you send goes through a secure encryption process before leaving your device. Here's the detailed step-by-step process:
                        </p>
                        <ol style={{ lineHeight: '2', color: '#666', paddingLeft: '24px', marginBottom: '12px' }}>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Key Generation (ECDH):</strong> A shared encryption key is generated using ECDH (Elliptic Curve Diffie-Hellman) key exchange. 
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>Deterministic keypairs are derived from both your wallet address and the recipient's wallet address using HKDF (HMAC-based Key Derivation Function)</li>
                              <li>The system computes a shared secret using ECDH, which only works if both parties have the correct wallet addresses</li>
                              <li>This shared secret is then used to derive a 256-bit AES encryption key</li>
                              <li>No signatures are required - messages can be sent immediately without any wallet interaction</li>
                            </ul>
                          </li>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Message Encryption (AES-256-GCM):</strong> Your message text is encrypted on your device using AES-256-GCM encryption.
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>A random nonce (number used once) is generated for each message to ensure uniqueness</li>
                              <li>The message is encrypted with the shared key and nonce using AES-256-GCM mode</li>
                              <li>GCM mode provides both confidentiality (encryption) and authenticity (prevents tampering)</li>
                              <li>The encrypted output includes both the ciphertext and authentication tag</li>
                            </ul>
                          </li>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Firestore Storage:</strong> The encrypted message (ciphertext + nonce + authentication tag) is stored directly in Firestore.
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>Firestore is Google's real-time database that provides instant message delivery</li>
                              <li>The encrypted message data (ciphertext, nonce) is stored in the messages collection</li>
                              <li>Each message document includes: encrypted ciphertext, nonce, sender address, recipient address, timestamp, and read status</li>
                              <li>The actual plaintext message content is never stored - only the encrypted data</li>
                              <li>Firestore provides real-time synchronization, so recipients are notified instantly</li>
                            </ul>
                          </li>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Conversation Metadata:</strong> Conversation metadata is also stored in Firestore for quick access and real-time notifications.
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>Conversation ID (deterministic from both wallet addresses)</li>
                              <li>Last message preview (truncated to 50 characters for display)</li>
                              <li>Last message timestamp</li>
                              <li>Unread message count</li>
                              <li>Participant information</li>
                              <li>This metadata enables fast conversation list loading and real-time updates</li>
                            </ul>
                          </li>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Real-time Notification:</strong> The recipient is notified instantly via Firestore listeners when a new encrypted message is stored.
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>Firestore's real-time listeners detect the new encrypted message immediately</li>
                              <li>The recipient sees a notification badge and the conversation appears in their conversations list</li>
                              <li>The encrypted message is automatically fetched from Firestore when they open the conversation</li>
                              <li>No delays from IPFS gateway lookups - messages are delivered instantly</li>
                            </ul>
                          </li>
                        </ol>
                        <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '6px', marginTop: '16px' }}>
                          <strong>🔐 Security Guarantee:</strong> At no point in this process is your message content visible to PeopleTag servers. 
                          The message is encrypted on your device, stored as encrypted data in Firestore, and can only be decrypted by the intended recipient using their wallet address. 
                          Even though messages are stored in Firestore, they remain fully encrypted and unreadable without the correct decryption key.
                        </p>
                      </div>

                      <div style={{ marginBottom: '32px' }}>
                        <h4 style={{ color: '#007bff', marginBottom: '16px', fontSize: '18px' }}>📥 What Happens When You Receive a Message</h4>
                        <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px' }}>
                          When someone sends you an encrypted message, here's the detailed process of how it's received and decrypted:
                        </p>
                        <ol style={{ lineHeight: '2', color: '#666', paddingLeft: '24px', marginBottom: '12px' }}>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Real-time Notification:</strong> Firestore detects the new encrypted message and notifies you instantly via onSnapshot listeners.
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>You'll see a notification badge indicating you have unread messages</li>
                              <li>The conversation appears in your conversations list with a preview</li>
                              <li>The encrypted message data is already in Firestore - no need to fetch from external storage</li>
                              <li>No message content is visible until you open the conversation and decrypt it</li>
                            </ul>
                          </li>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Load Encrypted Message:</strong> When you open the conversation, the app loads the encrypted message data from Firestore.
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>The encrypted message (ciphertext + nonce) is retrieved directly from Firestore</li>
                              <li>This happens instantly - no network delays from IPFS gateway lookups</li>
                              <li>The message data is loaded automatically when you open the conversation</li>
                              <li>All message data remains encrypted until decryption happens on your device</li>
                            </ul>
                          </li>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Key Generation (ECDH):</strong> The same shared encryption key is generated using ECDH.
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>Deterministic keypairs are derived from both your wallet address and the sender's wallet address</li>
                              <li>The system computes the same shared secret using ECDH that was used to encrypt the message</li>
                              <li>This shared secret is used to derive the same 256-bit AES encryption key</li>
                              <li>No signatures are required - decryption happens automatically using your wallet address</li>
                              <li>Because the keypairs are deterministic (derived from wallet addresses), both parties always generate the same shared key</li>
                            </ul>
                          </li>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Decryption (AES-256-GCM):</strong> The message is decrypted on your device using AES-256-GCM.
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>The encrypted message is decrypted using the shared key and nonce</li>
                              <li>AES-256-GCM verifies the authentication tag to ensure the message hasn't been tampered with</li>
                              <li>If the decryption fails (wrong key or tampered message), an error is shown instead of corrupted text</li>
                              <li>The decryption happens entirely on your device - the plaintext never leaves your browser</li>
                            </ul>
                          </li>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Display:</strong> The decrypted message is displayed in your conversation thread.
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>The message appears with the sender's information and timestamp</li>
                              <li>Messages are displayed in chronological order</li>
                              <li>The conversation metadata (preview, last message time) is also decrypted if it was encrypted</li>
                            </ul>
                          </li>
                          <li style={{ marginBottom: '12px' }}>
                            <strong>Read Receipt:</strong> When you view the conversation, messages are automatically marked as read.
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>The read status is updated in Firestore metadata</li>
                              <li>The sender can see that you've read their message (✓)</li>
                              <li>This happens automatically when you open the conversation</li>
                            </ul>
                          </li>
                        </ol>
                        <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '6px', marginTop: '16px' }}>
                          <strong>🔐 Privacy Guarantee:</strong> The entire decryption process happens on your device. 
                          Your wallet address is used to generate the decryption key, but no private keys or signatures are required. 
                          The message content is never visible to PeopleTag servers or anyone else - only you can decrypt and read it.
                        </p>
                      </div>

                      <div style={{ marginBottom: '32px' }}>
                        <h4 style={{ color: '#007bff', marginBottom: '16px', fontSize: '18px' }}>💬 How to Send a Message</h4>
                        <ol style={{ lineHeight: '2', color: '#666', paddingLeft: '24px', marginBottom: '12px' }}>
                          <li style={{ marginBottom: '8px' }}>
                            <strong>Click "New Message"</strong> button (or select an existing conversation from the left)
                          </li>
                          <li style={{ marginBottom: '8px' }}>
                            <strong>Enter the wallet address</strong> (0x followed by 40 characters) or <strong>PeopleTag username</strong> of the person you want to message
                          </li>
                          <li style={{ marginBottom: '8px' }}>
                            <strong>Type your message</strong> in the input field at the bottom
                          </li>
                          <li style={{ marginBottom: '8px' }}>
                            <strong>Click "Send"</strong> - your message will be automatically encrypted using ECDH key exchange, then stored in Firestore
                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                              <li>The encryption happens instantly on your device before the message leaves your browser</li>
                              <li>No wallet signatures are required - encryption uses your wallet address automatically</li>
                              <li>The encrypted message (ciphertext + nonce) is stored directly in Firestore</li>
                              <li>Message metadata (sender, recipient, timestamp, read status) is also stored in Firestore</li>
                              <li>Recipients are notified instantly via Firestore's real-time listeners</li>
                            </ul>
                          </li>
                        </ol>
                        <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', padding: '12px', backgroundColor: '#d4edda', borderRadius: '6px', border: '1px solid #28a745', marginTop: '12px' }}>
                          <strong>🔒 Security:</strong> Every message you send is encrypted end-to-end. The encryption key is generated from wallet addresses using ECDH, 
                          ensuring that only the intended recipient can decrypt and read your message.
                        </p>
                      </div>

                      <div style={{ marginBottom: '32px' }}>
                        <h4 style={{ color: '#007bff', marginBottom: '16px', fontSize: '18px' }}>🔐 Technical Details</h4>
                        <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '16px' }}>
                          PeopleTag uses industry-standard cryptographic protocols to ensure all messages are encrypted end-to-end. Here's the complete technical stack:
                        </p>
                        <ul style={{ lineHeight: '2', color: '#666', paddingLeft: '24px', marginBottom: '12px' }}>
                          <li><strong>Message Encryption Algorithm:</strong> AES-256-GCM (Advanced Encryption Standard with Galois/Counter Mode) - military-grade encryption with authenticated encryption</li>
                          <li><strong>Message Key Exchange:</strong> ECDH (Elliptic Curve Diffie-Hellman) - industry-standard protocol for secure key exchange without requiring signatures</li>
                          <li><strong>Keypair Generation:</strong> Deterministic keypairs derived from wallet addresses using HKDF (HMAC-based Key Derivation Function) with SHA-256</li>
                          <li><strong>Key Derivation Process:</strong> 
                            <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                              <li>Wallet addresses are used to derive deterministic keypairs</li>
                              <li>ECDH computes a shared secret from both parties' keypairs</li>
                              <li>HKDF with SHA-256 derives the final 256-bit AES encryption key from the shared secret</li>
                              <li>Both parties generate the exact same key independently</li>
                            </ul>
                          </li>
                          <li><strong>Security Model:</strong> Both parties generate the same shared key from their wallet addresses - no signatures required for sending/receiving messages</li>
                          <li><strong>Message Storage:</strong> 
                            <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                              <li><strong>Firestore:</strong> Encrypted message content (ciphertext + nonce + authentication tag) stored directly in Firestore messages collection</li>
                              <li><strong>Firestore Conversations:</strong> Conversation metadata (conversationId, lastMessagePreview, lastMessageTime, unreadCount, participant info) stored in conversations collection</li>
                              <li><strong>Fast Delivery:</strong> Messages are delivered instantly via Firestore's real-time database, with no IPFS gateway delays</li>
                              <li><strong>Client-side Encryption:</strong> All message content is encrypted before storage - Firestore only stores encrypted data</li>
                            </ul>
                          </li>
                          <li><strong>Real-time Updates:</strong> Firestore onSnapshot listeners provide instant message notifications without polling</li>
                          <li><strong>Key Management:</strong> 
                            <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                              <li>Message encryption keys are generated on-demand from wallet addresses (no storage needed)</li>
                              <li>Firestore encryption key is derived from your saved signature and persists across page refreshes</li>
                              <li>Keys never leave your device - all encryption/decryption happens client-side</li>
                            </ul>
                          </li>
                          <li><strong>No Setup Required:</strong> Messages can be sent immediately - no signatures or setup needed for message encryption. Firestore encryption key is automatically available if you have a saved signature.</li>
                          <li><strong>True E2E Security:</strong> Messages are encrypted on your device before leaving your browser and can only be decrypted by the intended recipient using their wallet address</li>
                          <li><strong>Decentralized Architecture:</strong> Message content is stored on IPFS (peer-to-peer network), not on PeopleTag servers, ensuring true decentralization</li>
                        </ul>
                        <p style={{ lineHeight: '1.8', color: '#666', marginBottom: '12px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '6px', marginTop: '16px' }}>
                          <strong>🔐 All Messages Are Encrypted:</strong> Every message in PeopleTag uses this encryption stack. There are no unencrypted messages - 
                          all communication is protected with end-to-end encryption from the moment you type until the recipient reads it.
                        </p>
                      </div>

                      <div style={{ marginBottom: '32px' }}>
                        <h4 style={{ color: '#007bff', marginBottom: '16px', fontSize: '18px' }}>✨ Features</h4>
                        <ul style={{ lineHeight: '2', color: '#666', paddingLeft: '24px', marginBottom: '12px' }}>
                          <li><strong>Real-time notifications:</strong> Get notified instantly when you receive new messages</li>
                          <li><strong>Read receipts:</strong> See when your messages have been read (✓)</li>
                          <li><strong>Message search:</strong> Search through your conversation history</li>
                          <li><strong>Block users:</strong> Block users you don't want to hear from</li>
                          <li><strong>Report users:</strong> Report inappropriate behavior</li>
                          <li><strong>Unread counts:</strong> Track unread messages across all conversations</li>
                        </ul>
                      </div>

                      <div style={{
                        marginTop: '32px',
                        padding: '20px',
                        backgroundColor: '#e7f3ff',
                        borderRadius: '8px',
                        border: '1px solid #b3d9ff'
                      }}>
                        <p style={{ margin: 0, lineHeight: '1.8', color: '#0066cc', fontSize: '15px' }}>
                          <strong>🔒 Privacy First:</strong> Your messages are private, secure, and decentralized. 
                          PeopleTag never has access to the content of your messages. All encryption and decryption 
                          happens on your device using keys derived from wallet addresses via ECDH. Messages are stored 
                          on IPFS, not on PeopleTag servers, ensuring true decentralization and privacy.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                <p>Ultimately all operations can occur client-side, so no server ever sees unencrypted data. All messages are encrypted end-to-end using wallet-based key exchange, ensuring complete privacy and security.</p>

                <ul>
                  <li>🔒 Everything is private from the start</li>
                  <li>🌐 Built for Interoperability</li>
                  <li>💵 On-chain verification on low-cost L2s</li>
                  <li>🔗 API-first design for easy dApp integration</li>
                </ul>
                
                <h2>FAQ: </h2>
                <ul>
                  <li><b>What is Pinata? And how is it related to PeopleTag?</b>
                <p>Pinata is a service for uploading and "pinning" files to IPFS.<br />
                IPFS (InterPlanetary File System) is a decentralized file storage network.<br />
                Instead of "files on my server," it's "files in a global network, accessible by a unique hash (CID)."<br />
                Pinata is like "Dropbox for IPFS":<br />
                You send a file → Pinata stores it in IPFS and guarantees it won't disappear (regular IPFS without pinning may lose files over time).</p>
                <p>Connection to PeopleTag:<br />
                You store the encrypted user profile (email, tags, bio, etc.) as a JSON file in Pinata → you get a CID (e.g., QmXo...).<br />
                This CID is like the "address" of your profile in the decentralized web.</p></li>
                <li><b>Why write anything to a blockchain (Arc)? Why not just store the CID in Pinata and be done?</b>
                <p>Because a CID by itself belongs to no one.<br />
                Imagine:<br />
                You upload a profile → get CID: QmXo... <br />
                But how does PeopleTag or another dApp know this CID belongs to you?<br />
                How do you prove you have the right to update it?<br />
                👉 The blockchain solves the ownership problem:<br />
                You write to a contract on blockchain, address 0xAbC... → CID = QmXo...<br />
                Now the entire network knows: "this CID belongs to the owner of address 0xAbC...”<br />
                Only you (the holder of the private key for 0xAbC...) can update this record.<br />
                🔐Important: since CID leading to IPFS encrypted data, link itself is useless without the encryption key, so link itself is not encrypted. 
                </p>
                </li>
                <li><b>What user-owned data really means:</b>
                <p>Not your server decides who you are.<br />
                Not Pinata decides whose profile it is.<br />
                You prove ownership through cryptography and the blockchain.<br />
                If needed, you can get CID from contact, with CID you can get the encrypted data from IPFS.<br />
                Step by step guide on how to get your owned data:<br />
                1. Get CID from contract: go https://testnet.arcscan.app/address/0x36014Af11c60FbbB2Db7EF7E419E40201dC43fFe#readContract
                (it's PeopleTag contract on Arc). Press profileCID function. Input your wallet address and press Query. You will see the CID string.<br />
                2. Paste CID into any IPFS gateway: https://ipfs.io/ipfs/QmXo...<br />
                3. Get the encrypted data from IPFS and decrypt the data with your encryption key from wallet: 
                ask any GPT to make you a script to decrypt the data with your encryption key from wallet. This script will run running locally (.html file).<br />
                </p></li>
                <li><b>How does PeopleTag compare to Lens Protocol and Farcaster?</b>
                <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                  
                  <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold' }}>Feature</th>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold' }}>PeopleTag</th>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold' }}>Lens Protocol</th>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold' }}>Farcaster</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}><strong>Core Identity</strong></td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>Interests & skills (tags)</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>Social graph (NFT profiles)</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>Message feed (hubs)</td>
                        </tr>
                        <tr style={{ background: '#fafafa' }}>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}><strong>Privacy Level</strong></td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>✅ High (encrypted by default)</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>❌ Low (everything public)</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>⚠️ Medium (public posts, private channels planned)</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}><strong>Data Storage</strong></td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>IPFS + Arc</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>IPFS + Polygon</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>Hubs (decentralized servers)</td>
                        </tr>
                        <tr style={{ background: '#fafafa' }}>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}><strong>Primary Use Case</strong></td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>Interest-based discovery, matching, events</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>Influencer expression, monetization</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>Decentralized content sharing</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}><strong>Type</strong></td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}><strong>Interest Graph Protocol</strong></td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}><strong>Social Graph</strong></td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}><strong>Feed Protocol</strong></td>
                        </tr>
                        <tr style={{ background: '#fafafa' }}>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}><strong>Economic Model</strong></td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>Revenue sharing from attention</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>NFT subscriptions, grants</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>None (yet)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div style={{ marginTop: '30px', padding: '20px', background: '#f0f7ff', borderRadius: '8px', borderLeft: '4px solid #4a90e2' }}>
                    <h4 style={{ marginTop: '0', marginBottom: '15px', color: '#1a1a1a' }}>🎯 Why PeopleTag Isn't Competition — It's Complementary</h4>
                    <p style={{ marginBottom: '15px' }}>PeopleTag <strong>does not replace</strong> Lens or Farcaster.<br />
                    It <strong>enhances</strong> them.</p>
                    <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                      <li>A user on <strong>Farcaster</strong> can attach their <strong>PeopleTag profile</strong> → now others see their <strong>interests</strong>, not just their tweets.</li>
                      <li>A <strong>Lens</strong> app can integrate PeopleTag API → to recommend profiles by <strong>tags</strong>, not just by follows.</li>
                      <li>A <strong>DAO</strong> can use PeopleTag → to find members with specific <strong>skills</strong> (#Solidity, #DAO-governance).</li>
                    </ul>
                    <p style={{ marginTop: '15px', fontWeight: 'bold' }}>👉 <strong>PeopleTag is the missing layer</strong>: the language that helps Web3 understand <strong>who you are</strong>, not just <strong>what you said</strong>.</p>
                  </div>

                  <div style={{ marginTop: '25px', padding: '20px', background: '#f9f9f9', borderRadius: '8px', border: '2px solid #4a90e2' }}>
                    <h4 style={{ marginTop: '0', marginBottom: '15px', color: '#1a1a1a' }}>✅ Conclusion</h4>
                    <blockquote style={{ margin: '15px 0', paddingLeft: '20px', borderLeft: '4px solid #4a90e2', fontStyle: 'italic', color: '#555' }}>
                      <p style={{ marginBottom: '10px' }}><strong>Lens</strong> is <strong>Web3 Twitter</strong>.</p>
                      <p style={{ marginBottom: '10px' }}><strong>Farcaster</strong> is a <strong>decentralized microblogging protocol</strong>.</p>
                      <p style={{ marginBottom: '0' }}><strong>PeopleTag</strong> is the <strong>language Web3 uses to describe user interests</strong>.</p>
                    </blockquote>
                    <p style={{ marginTop: '15px', marginBottom: '10px' }}>We are not standing beside them.</p>
                    <p style={{ marginBottom: '10px' }}>We are <strong>below them</strong> — as the foundational layer on which they can build smarter recommendations, better matching, and fairer economies.</p>
                    <p style={{ marginBottom: '0' }}>We are building <strong>AI-driven connections in Web3 communities through encrypted interest graphs — with revenue from contextual matching</strong>.</p>
                  </div>
                </div>
                </li>
                </ul>
                <h2>🎯 Key takeaway</h2>  
                <b>User-owned data = data that:</b>
                <ul>
                <li>Is stored off centralized servers (in IPFS)</li>
                <li>Is linked to the user via blockchain (Arc)</li>
                <li>Is encrypted so that only the user can read it</li>
                <li>Is portable across apps without developer permission</li>
                <p>We've built exactly this — and that's real Web3, not just "Web2 with a token".</p>
                </ul>

                <p>Open source. Privacy-first. Community-governed.</p>
                
             </div>
            )}

            {aboutSection === 'api' && (
              <div id="api">
                <h1>🔌 API</h1>
                
                <p>PeopleTag provides a simple REST API that allows developers to search and discover profiles based on public tags. This enables any dApp to integrate interest-based discovery and matching capabilities.</p>
                
                <h2>How it works</h2>
                
                <p>The API searches through publicly shared profile data (tags that users have chosen to publish). When users publish specific fields of their profile for discovery purposes, that data becomes searchable via the API while keeping the rest of their encrypted profile private.</p>
                
                <h2>Example Usage</h2>
                
                <p>Search for profiles by tags using a simple GET request:</p>
                
                <div style={{
                  backgroundColor: '#f5f5f5',
                  padding: '15px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  overflowX: 'auto'
                }}>
                  <strong>Request:</strong><br />
                  <a 
                    href="https://peopletag.xyz/api/v1/profiles/search?tags=cats&limit=10" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#4a90e2' }}
                  >
                    https://peopletag.xyz/api/v1/profiles/search?tags=cats&limit=10
                  </a>
                </div>
                
                <p><strong>Response:</strong></p>
                
                <div style={{
                  backgroundColor: '#f5f5f5',
                  padding: '15px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  overflowX: 'auto',
                  maxHeight: '600px',
                  overflowY: 'auto'
                }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
{`{
  "status": "success",
  "data": [
    {
      "userAddress": "0xbd2862ee9935575cbe5a94a9b8e5e1643059db4a",
      "publicTags": ["#DeFi", "#hiking", "#coffee", "#biohacking", "#cats", "#music", "#sun", "#foodie", "#cars", "#serbia"],
      "displayName": "Alex L P",
      "location": "Berlin",
      "lastUpdated": "2025-11-21T16:43:08.776Z"
    },
    {
      "publicTags": ["#cats", "#foodie", "#happy", "#music", "#videogames"],
      "displayName": "AlisaSachu",
      "location": "Berlin",
      "lastUpdated": "2025-11-17T07:44:01.772Z"
    },
    {
      "publicTags": ["#cinema", "#ballet", "#cooking", "#business", "#cats", "#automotive", "#networking"],
      "displayName": "jordan_a_944",
      "location": "Islamabad",
      "lastUpdated": "2025-11-16T10:08:33.490Z"
    },
    {
      "publicTags": ["#cycling", "#blogging", "#party", "#travel", "#cats"],
      "displayName": "Stephanie Hill",
      "location": "Seattle",
      "lastUpdated": "2025-11-16T10:06:20.092Z"
    },
    {
      "userAddress": "0x85016eb947e4fa7c1caa7f189eb320d51da71817",
      "publicTags": ["#research", "#cooking", "#flowers", "#blogging", "#cats", "#guitar"],
      "displayName": "sofia_c_829",
      "location": null,
      "lastUpdated": "2025-11-16T10:05:40.352Z"
    },
    {
      "userAddress": "0x58cd6f09542c9f72f448c83b1f1b8caf1e12cfc9",
      "publicTags": ["#nature", "#gaming", "#cats", "#journalism", "#literature", "#style"],
      "displayName": "Jennifer Martinez",
      "location": null,
      "lastUpdated": "2025-11-16T10:05:30.718Z"
    },
    {
      "userAddress": "0x669fb52392a73b09ab92dceb23480df52eecb92a",
      "publicTags": ["#photography", "#foodie", "#teaching", "#cats", "#design"],
      "displayName": "Olivia Evans",
      "location": null,
      "lastUpdated": "2025-11-16T10:05:03.148Z"
    },
    {
      "userAddress": "0xbc70fdce8bf23559ec3cc60c53d514a99e3e7511",
      "publicTags": ["#driving", "#cats"],
      "displayName": "Maria Garcia",
      "location": null,
      "lastUpdated": "2025-11-16T10:04:46.214Z"
    },
    {
      "userAddress": "0x9aff15b15f25cba089375c938a4f7d571586e621",
      "publicTags": ["#cinema", "#ballet", "#cats", "#leadership", "#coding"],
      "displayName": "Kayla Turner",
      "location": null,
      "lastUpdated": "2025-11-16T10:04:07.019Z"
    },
    {
      "userAddress": "0x777e54551b8efe682b191dcb90b2f77480e11d8a",
      "publicTags": ["#tech", "#tennis", "#journalism", "#cinema", "#cats", "#travel", "#strategy", "#meditation"],
      "displayName": "Ethan Edwards",
      "location": "Auckland",
      "lastUpdated": "2025-11-16T10:02:44.442Z"
    }
  ],
  "totalResults": 41,
  "limit": 10
}`}
                  </pre>
                </div>
                
                <p><strong>Response Fields:</strong></p>
                <ul>
                  <li><strong>status</strong>: Response status ("success" or "error")</li>
                  <li><strong>data</strong>: Array of matching profile objects</li>
                  <li><strong>totalResults</strong>: Total number of profiles matching the search criteria</li>
                  <li><strong>limit</strong>: Maximum number of results returned in this response</li>
                </ul>
                
                <p><strong>Profile Object Fields:</strong></p>
                <ul>
                  <li><strong>userAddress</strong>: Wallet address (may be omitted for privacy)</li>
                  <li><strong>publicTags</strong>: Array of tags the user has chosen to make public</li>
                  <li><strong>displayName</strong>: User's chosen display name</li>
                  <li><strong>location</strong>: User's location (may be null)</li>
                  <li><strong>lastUpdated</strong>: ISO timestamp of when the profile was last updated</li>
                </ul>
                
                <h2>API Endpoints</h2>
                
                <p><strong>Search Profiles:</strong></p>
                <ul>
                  <li><strong>Endpoint:</strong> <code>/api/v1/profiles/search</code></li>
                  <li><strong>Method:</strong> GET</li>
                  <li><strong>Query Parameters:</strong>
                    <ul>
                      <li><code>tags</code>: Comma-separated list of tags to search for (e.g., "cats" or "cats,foodie")</li>
                      <li><code>limit</code>: Maximum number of results to return (default: 10, max: 100)</li>
                    </ul>
                  </li>
                </ul>
                
                <h2>Integration</h2>
                
                <p>Integrating PeopleTag API into your dApp is straightforward. Simply make HTTP requests to search for profiles matching your criteria, then use the returned data to power discovery features, recommendations, or matching algorithms.</p>
                
                <p>For more detailed API documentation, including authentication, rate limits, and additional endpoints, please refer to the complete API documentation:   
                   <a href="https://github.com/lxssl/peopletag-api" target="_blank">https://github.com/lxssl/peopletag-api</a>.</p>
              </div>
            )}

            {aboutSection === 'whitepaper' && (
              <div id="whitepaper">
                <h1>PeopleTag Whitepaper</h1>
                
                <p><strong>AI-Powered Meaningful Connections for Web3 Communities</strong></p>
                
                <p><strong>Version 1.0</strong></p>
                <p><em>November 2025</em></p>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>Abstract</h2>
                
                <p>PeopleTag introduces a protocol that powers AI-driven connections in Web3 communities through encrypted interest graphs — with revenue from contextual matching. It enables users to create, own, and control their digital identity based on interests and skills, stored in a privacy-preserving and portable manner. By leveraging client-side encryption, decentralized storage (IPFS), and blockchain anchoring (Arc), PeopleTag provides a user-owned alternative to centralized social graphs. This paper outlines the protocol's architecture, core principles, and its potential to enable contextual discovery, collaboration, and user-centric economic models within the decentralized web.</p>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>1. Introduction</h2>
                
                <p>The current Web3 ecosystem lacks a standardized, user-controlled method for representing and discovering individuals based on their interests, skills, and affinities beyond financial activity. Existing identity systems often focus on financial history or are tied to centralized platforms, creating friction for meaningful connections and limiting user sovereignty over their personal data.</p>
                
                <p>PeopleTag addresses this gap by introducing an <strong>Open Interest Graph Protocol</strong>. It allows users to create encrypted profiles of their interests and skills, store them in a decentralized manner, and anchor their ownership on-chain. This creates a portable, user-owned layer of identity, enabling AI-driven contextual discovery and interaction without relying on centralized intermediaries. PeopleTag powers AI-driven connections in Web3 communities through encrypted interest graphs — with revenue from contextual matching that doesn't sell user data.</p>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>2. The Problem</h2>
                
                <ol>
                  <li><strong>Centralized Identity:</strong> Current social discovery relies heavily on centralized platforms (e.g., Twitter, LinkedIn) where users do not own their data or control its usage.</li>
                  <li><strong>Lack of Contextual Discovery:</strong> Existing Web3 identity standards (e.g., ENS, POAPs) provide limited information about user interests or skills, hindering contextual matching.</li>
                  <li><strong>Data Portability:</strong> User profiles and connections are siloed within specific applications, preventing seamless transfer of identity and social context.</li>
                  <li><strong>Privacy Concerns:</strong> Traditional systems often harvest and monetize user data without explicit consent or fair compensation.</li>
                  <li><strong>Identity Beyond Finance:</strong> Web3 identity is predominantly linked to wallet addresses and transaction history, neglecting the rich context of user interests and preferences.</li>
                </ol>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>3. The PeopleTag Solution</h2>
                
                <p>PeopleTag is an open protocol enabling the creation of portable, encrypted user profiles. Key features include:</p>
                
                <ul>
                  <li><strong>User Ownership:</strong> Users retain full control over their profile data through cryptographic ownership via their wallet.</li>
                  <li><strong>Privacy-First:</strong> Profiles are encrypted client-side using a key derived from the user's wallet signature, ensuring only the user can decrypt their data.</li>
                  <li><strong>Decentralized Storage:</strong> Encrypted profile data is stored on IPFS via Pinata, ensuring data persistence and immutability.</li>
                  <li><strong>Blockchain Anchoring:</strong> The Content Identifier (CID) of the encrypted profile is stored on the Arc blockchain, cryptographically linking the profile to the user's wallet address.</li>
                  <li><strong>Granular Sharing:</strong> Users control which aspects of their profile (e.g., specific tags) are shared with PeopleTag or other dApps, enabling privacy-preserving discovery.</li>
                  <li><strong>Interoperability:</strong> An API-first design allows any dApp to integrate PeopleTag for interest-based discovery and matching.</li>
                  <li><strong>AI-Driven Matching:</strong> The protocol enables intelligent contextual matching powered by encrypted interest graphs, generating revenue through contextual connections without selling user data.</li>
                </ul>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>4. Technical Architecture</h2>
                
                <h3>4.1. Core Components</h3>
                
                <p><strong>1. Client-Side Encryption:</strong></p>
                <ul>
                  <li>Upon first use, the user connects their wallet (e.g., MetaMask).</li>
                  <li>A one-time message is signed by the user (e.g., "Sign to generate your PeopleTag encryption key").</li>
                  <li>The signature is used via a deterministic function (e.g., SHA-256) to derive a 256-bit encryption key (<code>encryptionKey</code>) <em>without exposing the wallet's private key</em>.</li>
                  <li>User profile data (interests, skills, tags, etc.) is encrypted using AES-256-GCM using this <code>encryptionKey</code>.</li>
                </ul>
                
                <p><strong>2. Decentralized Storage (IPFS):</strong></p>
                <ul>
                  <li>The encrypted profile data is uploaded to IPFS via Pinata.</li>
                  <li>Pinata returns a unique Content Identifier (CID).</li>
                </ul>
                
                <p><strong>3. Blockchain Anchoring (Arc):</strong></p>
                <ul>
                  <li>The user's wallet address and the corresponding CID are recorded in a smart contract deployed on Arc Testnet.</li>
                  <li><strong>Contract Address:</strong> <Tooltip text="Click to copy contract address">
                    <span
                      onClick={() => copyToClipboard('0x36014Af11c60FbbB2Db7EF7E419E40201dC43fFe')}
                      style={{ cursor: 'pointer', color: '#4a90e2', textDecoration: 'underline', display: 'inline-block' }}
                    >0x36014Af11c60FbbB2Db7EF7E419E40201dC43fFe</span>
                  </Tooltip> → <a
                    href="https://testnet.arcscan.app/address/0x36014Af11c60FbbB2Db7EF7E419E40201dC43fFe"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#4a90e2' }}
                  >View on Arcscan</a></li>
                  <li>This creates a public, immutable link between the user's address and the location of their encrypted profile.</li>
                </ul>
                
                <p><strong>4. Public Interest Indexing (Future):</strong></p>
                <ul>
                  <li>Optionally, users may choose to share specific, non-sensitive fields (e.g., public tags) with a centralized or decentralized indexing service (e.g., built on The Graph) for efficient search and discovery. This data remains separate from the encrypted profile.</li>
                </ul>
                
                <h3>4.2. Data Flow</h3>
                
                <ol>
                  <li>User connects wallet.</li>
                  <li><code>encryptionKey</code> is derived from wallet signature.</li>
                  <li>User inputs profile data.</li>
                  <li>Profile data is encrypted client-side using <code>encryptionKey</code>.</li>
                  <li>Encrypted data is stored on IPFS via Pinata, returning <code>CID</code>.</li>
                  <li><code>setProfileCID(CID)</code> transaction is sent to the PeopleTag contract on Arc, linking <code>userAddress</code> to <code>CID</code>.</li>
                  <li>User can selectively share decrypted data (e.g., public tags) for discovery features.</li>
                </ol>
                
                <h3>4.3. User Data Retrieval</h3>
                
                <ol>
                  <li>User connects the same wallet.</li>
                  <li><code>encryptionKey</code> is re-derived using the same signature process.</li>
                  <li>The contract is queried for the <code>CID</code> associated with the <code>userAddress</code>.</li>
                  <li>The encrypted data is fetched from IPFS using the <code>CID</code>.</li>
                  <li>The data is decrypted client-side using the <code>encryptionKey</code>.</li>
                </ol>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>5. Key Features & Benefits</h2>
                
                <ul>
                  <li><strong>User-Owned Data:</strong> Users have ultimate control over their profile data.</li>
                  <li><strong>Privacy:</strong> Core profile data is encrypted and only accessible by the user.</li>
                  <li><strong>Portability:</strong> Profiles are not tied to a single application; the CID can be used across compatible dApps.</li>
                  <li><strong>Interoperability:</strong> API allows easy integration for other dApps.</li>
                  <li><strong>Transparency:</strong> Blockchain anchoring provides a verifiable record of ownership.</li>
                  <li><strong>Economic Agency:</strong> Experimental models for user-controlled monetization of attention/data (e.g., revenue sharing from contextual offers).</li>
                </ul>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>6. Experimental Research Areas</h2>
                
                <h3>6.1. Improved Community Matching</h3>
                
                <p><strong>Hypothesis:</strong> Structured interest data (tags) can significantly improve the quality of user matching in communities compared to basic social graphs.</p>
                
                <p><strong>Method:</strong> Develop an open-source algorithm to analyze tag correlations and interaction patterns for suggestions.</p>
                
                <h3>6.2. Fair Attention Monetization</h3>
                
                <p><strong>Hypothesis:</strong> Users will engage more positively if they share in the economic value generated from their contextual data, without sacrificing privacy.</p>
                
                <p><strong>Method:</strong> Experiment with models where contextual offers are placed based on tags, with revenue shared with users who engage.</p>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>7. Conclusion</h2>
                
                <p>PeopleTag presents a viable, technically sound protocol for creating a user-owned, privacy-preserving interest graph for Web3. By combining client-side encryption, decentralized storage, and blockchain anchoring, it offers a practical solution to the challenges of identity, discovery, and data ownership in the decentralized web. PeopleTag powers AI-driven connections in Web3 communities through encrypted interest graphs — with revenue from contextual matching that enables more meaningful, user-centric interactions and economic models within the Web3 ecosystem.</p>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>8. Links & Resources</h2>
                
                <ul>
                  <li><strong>Live Demo:</strong> <a href="https://peopletag.xyz" target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>https://peopletag.xyz</a></li>
                  <li><strong>Contract on Arc (Arcscan):</strong> <a href="https://testnet.arcscan.app/address/0x36014Af11c60FbbB2Db7EF7E419E40201dC43fFe" target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>https://testnet.arcscan.app/address/0x36014Af11c60FbbB2Db7EF7E419E40201dC43fFe</a></li>
                  <li><strong>GitHub:</strong> <a href="https://github.com/lxssl/peopletag-protocol" target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>https://github.com/lxssl/peopletag-protocol</a></li>
                  <li><strong>Twitter:</strong> <a href="https://x.com/peopletaginc" target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>https://x.com/peopletaginc</a></li>
                  <li><strong>Discord:</strong> <a href={DISCORD_LINK} target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>{DISCORD_LINK}</a></li>
                </ul>
              </div>
            )}
            
            {aboutSection === 'roadmap' && (
              <div id="roadmap">
                <h1>PeopleTag Roadmap</h1>
                
                <p><strong>AI-Powered Meaningful Connections for Web3 Communities</strong></p>
                
                <p>This roadmap outlines the strategic development of the PeopleTag protocol, from its current functional prototype to a fully-featured platform that powers AI-driven connections in Web3 communities through encrypted interest graphs — with revenue from contextual matching.</p>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>Phase 1: Foundation & Validation (Q4 2025 – Q1 2026)</h2>
                
                <p><em><strong>Goal:</strong> Establish core functionality, gain initial traction, and validate key hypotheses.</em></p>
                
                <p><strong>Key Milestones:</strong></p>
                
                <ul>
                  <li>Launch public beta with full profile creation, encryption, and on-chain anchoring.</li>
                  <li>Achieve 100 active users creating and managing their profiles.</li>
                  <li>Publish open-source client code and contract ABI.</li>
                  <li>Secure initial grant funding (e.g., Ethereum Foundation, Filecoin Foundation).</li>
                  <li>Begin integration discussions with early partner dApps.</li>
                  <li>Develop and test the core algorithm for interest-based matching.</li>
                  <li>Implement a basic version of the user-controlled sharing interface.</li>
                </ul>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>Phase 2: Growth & Interoperability (Q2 – Q3 2026)</h2>
                
                <p><em><strong>Goal:</strong> Expand the ecosystem, enhance discovery features, and improve developer tooling.</em></p>
                
                <p><strong>Key Milestones:</strong></p>
                
                <ul>
                  <li>Release Developer SDK and comprehensive API documentation.</li>
                  <li>Integrate with 3+ major Web3 applications (e.g., DAO platforms, event organizers, marketplaces) as pilot partners.</li>
                  <li>Launch the "Contextual Offers" module for experimental attention monetization.</li>
                  <li>Migrate public data indexing to The Graph for scalable, decentralized search.</li>
                  <li>Achieve 1,000+ active users and 50+ indexed profiles for discovery.</li>
                  <li>Host first community hackathon to foster third-party innovation.</li>
                </ul>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>Phase 3: Scaling & Decentralization (Q4 2026 – Q2 2027)</h2>
                
                <p><em><strong>Goal:</strong> Scale the protocol, implement advanced privacy features, and transition towards community governance.</em></p>
                
                <p><strong>Key Milestones:</strong></p>
                
                <ul>
                  <li>Explore and integrate zero-knowledge (ZK) proofs for private profile verification and matching.</li>
                  <li>Optimize storage costs by evaluating alternative L2s or IPFS pinning strategies.</li>
                  <li>Launch a tokenized incentive model (if applicable) to reward network participation and curation.</li>
                  <li>Formally establish a governance framework (e.g., via a DAO).</li>
                  <li>Achieve 10,000+ active users and become a standard identity layer for several prominent dApps.</li>
                  <li>Publish a detailed research paper on the findings from the community matching and attention monetization experiments.</li>
                </ul>
                
                <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                
                <h2>Phase 4: Maturation & Ecosystem Leadership (2027+)</h2>
                
                <p><em><strong>Goal:</strong> Solidify PeopleTag as the leading platform for AI-driven connections in Web3 communities.</em></p>
                
                <p><strong>Vision:</strong></p>
                
                <ul>
                  <li>Become the de facto standard for portable interest graphs across the Web3 landscape.</li>
                  <li>Foster a vibrant ecosystem of dApps built on top of the PeopleTag protocol.</li>
                  <li>Enable meaningful connections through AI-powered contextual matching with sustainable revenue models.</li>
                  <li>Continuously innovate on privacy-preserving technologies for user identity and AI-driven discovery.</li>
                </ul>
              </div>
            )}

            {aboutSection === 'public-testnet' && (
              <div id="public-testnet">
                <h1>Public Testnet</h1>
                
                <h2>🛠️ For Developers</h2>
                
                <p>Want to add AI-driven interest-based discovery and matching to your dApp?</p>
                
                <p>→ Coming soon: SDK, API docs, and integration examples.</p>
                
                <h2>🌱 For Users</h2>
                
                <p>Want to make meaningful connections in Web3 communities through AI-powered matching?</p>
                
                <p>→ Join our beta waitlist.</p>
              </div>
            )}
            
            {aboutSection === 'team' && (
              <div id="team">
                <h1>Team</h1>
                
                <div style={{ marginTop: '30px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '30px',
                    marginBottom: '20px',
                    flexWrap: 'wrap'
                  }}>
                    <img 
                      src="/alex-smith-avatar.jpg" 
                      alt="Alex"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                      }}
                      style={{
                        width: '220px',
                        height: '220px',
                        borderRadius: '12px',
                        objectFit: 'cover',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        flexShrink: 0,
                        transition: 'transform 0.3s ease',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ flex: '1', minWidth: '250px', alignSelf: 'flex-start' }}>
                      <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '15px', marginTop: '0' }}>Alex</h2>
                      <p style={{ fontSize: '16px', color: '#666', marginBottom: '10px' }}><strong>Location:</strong> Serbia</p>
                      <p style={{ fontSize: '16px', lineHeight: '1.8', marginTop: '15px' }}>
                        Alex is the founder and lead developer of PeopleTag, passionate about building AI-driven connections in Web3 communities through encrypted interest graphs — with revenue from contextual matching. 
                        With a focus on privacy-first design and decentralized technologies, Alex is dedicated to creating tools that empower users 
                        to own and control their digital identity.
                      </p>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '30px',
                    marginBottom: '20px',
                    marginTop: '40px',
                    flexWrap: 'wrap'
                  }}>
                    <div
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                      }}
                      style={{
                        width: '220px',
                        height: '220px',
                        borderRadius: '12px',
                        backgroundColor: '#e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        flexShrink: 0,
                        transition: 'transform 0.3s ease',
                        cursor: 'pointer',
                        color: '#999',
                        fontSize: '14px',
                        textAlign: 'center',
                        padding: '20px'
                      }}
                    >
                      Coming Soon
                    </div>
                    <div style={{ flex: '1', minWidth: '250px', alignSelf: 'flex-start' }}>
                      <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '15px', marginTop: '0' }}>Team Member</h2>
                      <p style={{ fontSize: '16px', color: '#666', marginBottom: '10px' }}><strong>Location:</strong> To be announced</p>
                      <p style={{ fontSize: '16px', lineHeight: '1.8', marginTop: '15px', fontStyle: 'italic', color: '#888' }}>
                        We're excited to announce this team member soon. Stay tuned for updates!
                      </p>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '30px',
                    marginBottom: '20px',
                    marginTop: '40px',
                    flexWrap: 'wrap'
                  }}>
                    <div
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                      }}
                      style={{
                        width: '220px',
                        height: '220px',
                        borderRadius: '12px',
                        backgroundColor: '#e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        flexShrink: 0,
                        transition: 'transform 0.3s ease',
                        cursor: 'pointer',
                        color: '#999',
                        fontSize: '14px',
                        textAlign: 'center',
                        padding: '20px'
                      }}
                    >
                      Coming Soon
                    </div>
                    <div style={{ flex: '1', minWidth: '250px', alignSelf: 'flex-start' }}>
                      <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '15px', marginTop: '0' }}>Team Member</h2>
                      <p style={{ fontSize: '16px', color: '#666', marginBottom: '10px' }}><strong>Location:</strong> To be announced</p>
                      <p style={{ fontSize: '16px', lineHeight: '1.8', marginTop: '15px', fontStyle: 'italic', color: '#888' }}>
                        We're excited to announce this team member soon. Stay tuned for updates!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {aboutSection === 'contact-us' && (
              <div id="contact-us">
                <h1>Contact us</h1>
                
                <p>Please email us to <a href="mailto:info@peopletag.xyz" className="contact-email">info@peopletag.xyz</a> for any questions or want to get in touch.</p>
                <p>Or reach out to us in socials:</p>
                
                
                <div className="contact-social-icons">
                  <a href="https://x.com/peopletaginc" target="_blank" rel="noopener noreferrer" className="contact-social-icon" aria-label="Twitter">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                    </svg>
                  </a>
                  <a href={DISCORD_LINK} target="_blank" rel="noopener noreferrer" className="contact-social-icon" aria-label="Discord">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                  </a>
                  <a href="https://github.com/lxssl/peopletag-protocol" target="_blank" rel="noopener noreferrer" className="contact-social-icon" aria-label="GitHub">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                    </svg>
                  </a>
                </div>
               
                <p style={{ marginTop: '30px' }}>If you would like to support PeopleTag - you may donate to{' '}
                  <button
                    onClick={async () => {
                      const address = '0xEC5f14aF3B4fEEaE699eB6B85c76b97a98d3e921';
                      try {
                        await navigator.clipboard.writeText(address);
                        setDonationAddressCopied(true);
                        setTimeout(() => {
                          setDonationAddressCopied(false);
                        }, 5000);
                      } catch (err) {
                        console.error('Failed to copy address:', err);
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#4a90e2',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      padding: 0,
                      font: 'inherit',
                      margin: 0
                    }}
                  >
                    0xEC5f14aF3B4fEEaE699eB6B85c76b97a98d3e921
                  </button>
                  {' '}🙏
                </p>
                {donationAddressCopied && (
                  <p style={{ marginTop: '10px', color: '#4a90e2', fontSize: '14px' }}>Copied to clipboard!</p>
                )}
                
              </div>
            )}
            
          </div>
        </div>
      )}
      
      {/* Terms of Service Section - appears on left when registration form is visible */}
      {(termsVisible && !aboutVisible) && (
        <div className="about-section" style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: termsExpanded ? (walletAddress && showRegistrationForm && profileVisible && !currentProfile ? 'calc(100% - 700px)' : 'calc(100% - 350px)') : '350px', // Expand to cover map when "Read here" is clicked
          height: '100vh',
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e0e0e0',
          zIndex: termsExpanded ? 1020 : 1006, // Higher z-index when expanded to cover the buttons (buttons are 1010)
          overflowY: 'auto',
          padding: termsExpanded ? '40px 60px' : '20px', // More padding when expanded
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ 
            marginBottom: '20px', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            maxWidth: termsExpanded ? '900px' : '100%',
            margin: termsExpanded ? '0 auto 20px auto' : '0 0 20px 0'
          }}>
            <h1 style={{ margin: 0, fontSize: termsExpanded ? '32px' : '24px', fontWeight: '600', color: '#333' }}>Terms of Service</h1>
            <button
              onClick={() => {
                setTermsVisible(false);
                setTermsExpanded(false); // Reset expanded state when closed
                setTermsClosedByUser(true);
                // If registration form is visible, allow reopening by clicking "Read here"
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              ×
            </button>
          </div>
          
          <div style={{ 
            fontSize: '14px', 
            lineHeight: '1.8', 
            color: '#333',
            maxWidth: termsExpanded ? '900px' : '100%',
            margin: termsExpanded ? '0 auto' : '0'
          }}>
            <p style={{ marginBottom: '10px', color: '#666', fontSize: '13px' }}>
              <strong>Last Updated:</strong> November 2025
            </p>
            
            <p style={{ marginBottom: '20px' }}>
              Welcome to PeopleTAG ("we," "us," or "our"). PeopleTAG is an open, decentralized protocol for user-owned interest graphs on Web3. By connecting your wallet and using any feature of PeopleTAG (including our website, API, or any integrated dApp), you agree to the following Terms of Service ("Terms"). If you do not agree, please do not use PeopleTAG.
            </p>

            <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#333' }}>1. You Own Your Data — We Don't</h2>
            <p style={{ marginBottom: '15px' }}>
              All data you input into PeopleTAG (interests, tags, bio, links, etc.) is encrypted client-side in your browser using a key derived from your wallet signature.
            </p>
            <p style={{ marginBottom: '15px' }}>
              You are the sole owner of your encrypted profile data. We do not store, access, or control it.
            </p>
            <p style={{ marginBottom: '15px' }}>
              We do not collect, sell, or monetize your personal information. Your data is stored on IPFS (via Pinata or similar), and only you can decrypt it.
              <b>Important:</b> Publishing parts of profile makes PeopleTag store published data for search and matching purposes.
            </p>
            <p style={{ marginBottom: '20px', fontWeight: '500' }}>
              🔐 PeopleTAG is not a social network. It is infrastructure. You control your identity.
            </p>

            <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#333' }}>2. No Guarantees — "As Is" Service</h2>
            <p style={{ marginBottom: '15px' }}>
              PeopleTAG is provided "as is" and "as available."
            </p>
            <p style={{ marginBottom: '15px' }}>
              We make no warranties of any kind — express or implied — including but not limited to:
            </p>
            <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
              <li>The accuracy, completeness, or reliability of any data you or others post.</li>
              <li>The availability, uptime, or performance of the service.</li>
              <li>That your profile will be discoverable, indexed, or visible to others.</li>
              <li>That your data will never be lost, corrupted, or inaccessible.</li>
            </ul>
            <p style={{ marginBottom: '20px', fontWeight: '500' }}>
              ⚠️ You use PeopleTAG at your own risk.
            </p>

            <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#333' }}>3. No Liability for Loss or Damage</h2>
            <p style={{ marginBottom: '15px' }}>
              Under no circumstances shall PeopleTAG, its developers, or contributors be liable for:
            </p>
            <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
              <li>Any loss of data, including your encrypted profile, if you lose your wallet or encryption key.</li>
              <li>Any financial loss, reputational harm, or emotional distress resulting from use of or inability to use PeopleTAG.</li>
              <li>Any third-party actions, including:
                <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                  <li>Misuse of your public tags by other users or dApps.</li>
                  <li>Failure of IPFS nodes to serve your data.</li>
                  <li>Changes to Arc (Circle), Ethereum, or other blockchains affecting the protocol.</li>
                  <li>Hacks, exploits, or bugs in third-party tools (e.g., MetaMask, Pinata, or wallets).</li>
                </ul>
              </li>
            </ul>
            <p style={{ marginBottom: '20px', fontWeight: '500' }}>
              💡 Your wallet and encryption key are your responsibility. If you lose them, your data is permanently inaccessible. We cannot recover it.
            </p>

            <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#333' }}>4. We Are Not a Service Provider</h2>
            <p style={{ marginBottom: '15px' }}>
              PeopleTAG is a non-custodial, open-source protocol.
            </p>
            <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
              <li>We do not act as a data processor, intermediary, or fiduciary.</li>
              <li>We do not provide customer support, account recovery, or technical assistance.</li>
              <li>We do not verify identities, interests, or claims made by users.</li>
              <li>We are not responsible for how other dApps use your public tags (e.g., if a dApp shows your email or location without permission — that's their responsibility, not ours).</li>
            </ul>

            <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#333' }}>5. Smart Contract Risk</h2>
            <p style={{ marginBottom: '15px' }}>
              Your profile's CID is anchored on Arc Testnet via a smart contract (0x0954...).
            </p>
            <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
              <li>The contract is open-source and audited by its code — not by us.</li>
              <li>Smart contracts are inherently experimental. Bugs, reverts, or unforeseen behavior are possible.</li>
              <li>We are not liable for any loss or failure caused by the contract's behavior, network congestion, or EVM execution errors.</li>
            </ul>

            <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#333' }}>6. No Endorsement or Affiliation</h2>
            <p style={{ marginBottom: '20px' }}>
              The presence of your tags (e.g., #DeFi, #Biohacking) does not imply endorsement, affiliation, or partnership with PeopleTAG or any third party. We do not control, monitor, or approve content posted by users.
            </p>

            <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#333' }}>7. Changes to These Terms</h2>
            <p style={{ marginBottom: '20px' }}>
              We may update these Terms from time to time. The latest version will always be posted at <a href="https://peopletag.xyz" target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>https://peopletag.xyz</a>. Continued use of PeopleTAG after changes constitutes acceptance.
            </p>

            <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#333' }}>8. Governing Law & Dispute Resolution</h2>
            <p style={{ marginBottom: '15px' }}>
              These Terms are governed by the laws of Switzerland, a jurisdiction known for its neutrality and crypto-friendly framework.
            </p>
            <p style={{ marginBottom: '15px' }}>
              Any dispute arising from your use of PeopleTAG shall be resolved individually, not in a class action or representative proceeding. You agree to waive any right to participate in class actions or collective litigation against us.
            </p>

            <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#333' }}>9. No Warranty of Future Functionality</h2>
            <p style={{ marginBottom: '15px' }}>
              Features like "AI matching," "contextual offers," or "revenue sharing" are experimental research hypotheses. They are not guaranteed to be implemented, launched, or sustained. We reserve the right to modify, suspend, or discontinue any feature without notice.
            </p>

            <h2 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#333' }}>10. Open Source & Decentralization</h2>
            <p style={{ marginBottom: '15px' }}>
              PeopleTAG is built on open-source code: <a href="https://github.com/lxssl/peopletag-protocol" target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>https://github.com/lxssl/peopletag-protocol</a>
            </p>
            <p style={{ marginBottom: '15px' }}>
              You are free to fork, audit, or build upon it. We are not responsible for forks, clones, or derivative projects.
            </p>

            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f0f7ff', 
              borderRadius: '8px', 
              borderLeft: '4px solid #4a90e2' 
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginTop: '0', marginBottom: '15px', color: '#333' }}>
                ✅ By Connecting Your Wallet, You Agree:
              </h3>
              <p style={{ marginBottom: '0', fontStyle: 'italic' }}>
                "I understand that PeopleTAG is a decentralized, non-custodial tool. I am solely responsible for my wallet, my encryption key, and my data. I accept all risks, including total loss of access. I release PeopleTAG and its developers from any and all liability."
              </p>
            </div>

            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f9f9f9', 
              borderRadius: '8px' 
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginTop: '0', marginBottom: '15px', color: '#333' }}>
                📌 Final Note
              </h3>
              <p style={{ marginBottom: '15px' }}>
                PeopleTAG exists to empower users — not to assume responsibility for them. We believe in true ownership. That means you own the risks, too.
              </p>
              <p style={{ marginBottom: '0' }}>
                Thank you for being part of the future of Web3 identity.
              </p>
              <p style={{ marginTop: '15px', marginBottom: '0', fontWeight: '500' }}>
                — The PeopleTAG Team
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Cat Ads Section - appears on right when searching for cats */}
      {showCatAds && (
        <div className="cat-ads-section" style={{ 
          right: messagesVisible ? '50%' : 
                 tagRunVisible ? '400px' :
                 (walletAddress && (profileVisible || groupsVisible || bugVisible)) ? '350px' : '0' 
        }}>
          <div className="cat-ads-header">
            <button 
              className="cat-ads-close" 
              onClick={() => setShowCatAds(false)}
              aria-label="Close ads"
              style={{ marginRight: '10px' }}
            >
              ×
            </button>
            <h3>Sponsored - Cat Related</h3>
          </div>
          <div className="cat-ads-container">
            <div className="cat-ad-item">
              <div className="cat-ad-image" style={{ background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', height: '150px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '20px', fontWeight: 'bold' }}>
                🐱 Cat Food
              </div>
              <div className="cat-ad-content">
                <h4>Premium Cat Food</h4>
                <p>Nutritious meals for your feline friend. 100% natural ingredients.</p>
                <a href="#" className="cat-ad-link">Shop Now →</a>
              </div>
            </div>
            <div className="cat-ad-item">
              <div className="cat-ad-image" style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', height: '150px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: '20px', fontWeight: 'bold' }}>
                🐾 Cat Toys
              </div>
              <div className="cat-ad-content">
                <h4>Interactive Cat Toys</h4>
                <p>Keep your cat entertained with our selection of fun toys.</p>
                <a href="#" className="cat-ad-link">Browse →</a>
              </div>
            </div>
            <div className="cat-ad-item">
              <div className="cat-ad-image" style={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', height: '150px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: '20px', fontWeight: 'bold' }}>
                🏠 Cat Furniture
              </div>
              <div className="cat-ad-content">
                <h4>Cat Trees & Scratching Posts</h4>
                <p>Stylish furniture that your cat will love to climb and scratch.</p>
                <a href="#" className="cat-ad-link">View Collection →</a>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Cooking Ads Section - appears on right when searching for cooking */}
      {showCookingAds && (
        <div className="cat-ads-section" style={{ 
          right: messagesVisible ? '50%' : 
                 tagRunVisible ? '400px' :
                 (walletAddress && (profileVisible || groupsVisible || bugVisible)) ? '350px' : '0' 
        }}>
          <div className="cat-ads-header">
            <button 
              className="cat-ads-close" 
              onClick={() => setShowCookingAds(false)}
              aria-label="Close ads"
              style={{ marginRight: '10px'}}
            >
              ×
            </button>
            <h3>Sponsored - Cooking Related</h3>
          </div>
          <div className="cat-ads-container">
            <div className="cat-ad-item">
              <div className="cat-ad-image" style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', height: '150px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '20px', fontWeight: 'bold' }}>
                👨‍🍳 Cooking Classes
              </div>
              <div className="cat-ad-content">
                <h4>Professional Cooking Classes</h4>
                <p>Learn from expert chefs and master the art of cooking. Online and in-person classes available.</p>
                <a href="#" className="cat-ad-link">Learn More →</a>
              </div>
            </div>
            <div className="cat-ad-item">
              <div className="cat-ad-image" style={{ background: 'linear-gradient(135deg, #feca57 0%, #ff9ff3 100%)', height: '150px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: '20px', fontWeight: 'bold' }}>
                🍳 Cookware Sets
              </div>
              <div className="cat-ad-content">
                <h4>Premium Cookware Collection</h4>
                <p>Professional-grade pots, pans, and utensils for the serious home chef.</p>
                <a href="#" className="cat-ad-link">Shop Now →</a>
              </div>
            </div>
            <div className="cat-ad-item">
              <div className="cat-ad-image" style={{ background: 'linear-gradient(135deg, #48c6ef 0%, #6f86d6 100%)', height: '150px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '20px', fontWeight: 'bold' }}>
                📚 Recipe Books
              </div>
              <div className="cat-ad-content">
                <h4>Best-Selling Recipe Books</h4>
                <p>Discover thousands of delicious recipes from around the world. Cook like a pro!</p>
                <a href="#" className="cat-ad-link">Browse Books →</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Verification Modal */}
      {emailVerificationOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isVerifyingEmailCode) {
              setEmailVerificationOpen(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              zIndex: 10002
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Verify your email</h2>
              <button
                onClick={() => setEmailVerificationOpen(false)}
                disabled={isVerifyingEmailCode}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: isVerifyingEmailCode ? 'not-allowed' : 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  opacity: isVerifyingEmailCode ? 0.6 : 1
                }}
              >
                ×
              </button>
            </div>

            <div style={{ fontSize: '13px', color: '#555', marginBottom: '12px' }}>
              We sent a 6-digit code to <strong>{emailToVerify}</strong>.
            </div>

            <input
              inputMode="numeric"
              autoFocus
              value={emailVerificationCode}
              onChange={(e) => setEmailVerificationCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '16px',
                letterSpacing: '2px',
                boxSizing: 'border-box'
              }}
            />

            {emailVerificationError && (
              <div style={{ marginTop: '10px', color: '#cc2d2d', fontSize: '13px' }}>
                {emailVerificationError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <button
                onClick={async () => {
                  if (isSendingEmailCode) return;
                  await openEmailVerificationModal({
                    email: emailToVerify,
                    purpose: emailVerificationPurpose,
                    onVerified: pendingEmailVerificationActionRef.current
                  });
                }}
                disabled={isSendingEmailCode || isVerifyingEmailCode}
                style={{
                  padding: '10px 14px',
                  background: 'white',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: (isSendingEmailCode || isVerifyingEmailCode) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: (isSendingEmailCode || isVerifyingEmailCode) ? 0.7 : 1,
                  flex: 1
                }}
              >
                {isSendingEmailCode ? 'Sending…' : 'Resend code'}
              </button>
              <button
                onClick={verifyEmailCode}
                disabled={isVerifyingEmailCode || emailVerificationCode.length !== 6}
                style={{
                  padding: '10px 14px',
                  background: (isVerifyingEmailCode || emailVerificationCode.length !== 6) ? '#ccc' : '#4a90e2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (isVerifyingEmailCode || emailVerificationCode.length !== 6) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  flex: 1
                }}
              >
                {isVerifyingEmailCode ? 'Verifying…' : 'Verify'}
              </button>
            </div>

            <div style={{ marginTop: '10px', fontSize: '12px', color: '#777' }}>
              If you don't see it, check spam/junk. Codes expire in ~10 minutes.
            </div>
          </div>
        </div>
      )}

      {/* Publish All Confirmation Modal */}
      {showPublishAllConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isPublishingAll) {
              setShowPublishAllConfirm(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Publish Everything</h2>
              <button
                onClick={() => setShowPublishAllConfirm(false)}
                disabled={isPublishingAll}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: isPublishingAll ? 'not-allowed' : 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  opacity: isPublishingAll ? 0.6 : 1
                }}
              >
                ×
              </button>
            </div>

            <div style={{ fontSize: '13px', color: '#555', marginBottom: '16px', lineHeight: '1.5' }}>
              This will <strong>publish all unpublished items</strong> in your profile, making them visible to everyone.
              <br /><br />
              Published items include: name, bio, tags, social links, music, email, wallet address, birth date, city, contact preferences, and other profile information.
            </div>

            <div style={{ 
              backgroundColor: '#fff8e1', 
              border: '1px solid #ffe082', 
              borderRadius: '8px', 
              padding: '12px', 
              marginBottom: '16px',
              fontSize: '12px',
              color: '#f57c00'
            }}>
              <strong>⚠️ Note:</strong> Only items that have values will be published. Empty fields will remain unpublished.
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowPublishAllConfirm(false)}
                disabled={isPublishingAll}
                style={{
                  padding: '10px 14px',
                  background: 'white',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: isPublishingAll ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: isPublishingAll ? 0.7 : 1,
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={publishAllUnpublished}
                disabled={isPublishingAll}
                style={{
                  padding: '10px 14px',
                  background: isPublishingAll ? '#ccc' : '#4a90e2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isPublishingAll ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  flex: 1
                }}
              >
                {isPublishingAll ? 'Publishing…' : 'Publish All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unpublish All Confirmation Modal */}
      {showUnpublishAllConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isUnpublishingAll) {
              setShowUnpublishAllConfirm(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Unpublish Everything</h2>
              <button
                onClick={() => setShowUnpublishAllConfirm(false)}
                disabled={isUnpublishingAll}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: isUnpublishingAll ? 'not-allowed' : 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  opacity: isUnpublishingAll ? 0.6 : 1
                }}
              >
                ×
              </button>
            </div>

            <div style={{ fontSize: '13px', color: '#555', marginBottom: '16px', lineHeight: '1.5' }}>
              This will <strong>unpublish all published items</strong> in your profile, hiding them from everyone.
              <br /><br />
              Your data will still be saved privately, but it will no longer be visible to others or searchable.
            </div>

            <div style={{ 
              backgroundColor: '#fff3e0', 
              border: '1px solid #ffcc80', 
              borderRadius: '8px', 
              padding: '12px', 
              marginBottom: '16px',
              fontSize: '12px',
              color: '#e65100'
            }}>
              <strong>⚠️ Note:</strong> This action will hide your name, bio, tags, social links, email, wallet address, and all other profile information from public view.
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowUnpublishAllConfirm(false)}
                disabled={isUnpublishingAll}
                style={{
                  padding: '10px 14px',
                  background: 'white',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: isUnpublishingAll ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: isUnpublishingAll ? 0.7 : 1,
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={unpublishAllPublished}
                disabled={isUnpublishingAll}
                style={{
                  padding: '10px 14px',
                  background: isUnpublishingAll ? '#ccc' : '#4a90e2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isUnpublishingAll ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  flex: 1
                }}
              >
                {isUnpublishingAll ? 'Unpublishing…' : 'Unpublish All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Completeness Wizard Modal */}
      {showProfileCompletenessWizard && currentProfile && currentUser && currentProfile.username === currentUser && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowProfileCompletenessWizard(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px', paddingBottom: '20px', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>Profile Completeness Wizard</h2>
                <button
                  onClick={() => setShowProfileCompletenessWizard(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {(() => {
              // Use the gamification service's profile completeness calculation (consistent across the app)
              // Calculate from current form data for real-time preview (use currentProfile as fallback if wizardFormData is empty)
              const formData = wizardFormData && Object.keys(wizardFormData).length > 0 ? wizardFormData : {};
              const profileForCalc = { ...currentProfile, ...formData };
              const percentage = calculateProfileCompleteness(profileForCalc) ?? 0;
              
              // Get language suggestions based on city
              const getLanguageForCity = (cityName) => {
                if (!cityName) return null;
                const cityLower = cityName.toLowerCase();
                const languages = [];
                
                // Always add English
                languages.push('English');
                
                // Add local language based on city
                if (cityLower.includes('berlin') || cityLower.includes('munich') || cityLower.includes('frankfurt') || cityLower.includes('hamburg') || cityLower.includes('cologne')) {
                  languages.push('German');
                } else if (cityLower.includes('paris') || cityLower.includes('lyon') || cityLower.includes('marseille') || cityLower.includes('toulouse')) {
                  languages.push('French');
                } else if (cityLower.includes('madrid') || cityLower.includes('barcelona') || cityLower.includes('valencia') || cityLower.includes('seville')) {
                  languages.push('Spanish');
                } else if (cityLower.includes('rome') || cityLower.includes('milan') || cityLower.includes('naples') || cityLower.includes('turin')) {
                  languages.push('Italian');
                } else if (cityLower.includes('amsterdam') || cityLower.includes('rotterdam') || cityLower.includes('utrecht')) {
                  languages.push('Dutch');
                } else if (cityLower.includes('warsaw') || cityLower.includes('krakow')) {
                  languages.push('Polish');
                } else if (cityLower.includes('prague')) {
                  languages.push('Czech');
                } else if (cityLower.includes('budapest')) {
                  languages.push('Hungarian');
                } else if (cityLower.includes('bucharest')) {
                  languages.push('Romanian');
                } else if (cityLower.includes('athens')) {
                  languages.push('Greek');
                } else if (cityLower.includes('lisbon') || cityLower.includes('porto')) {
                  languages.push('Portuguese');
                } else if (cityLower.includes('stockholm') || cityLower.includes('gothenburg')) {
                  languages.push('Swedish');
                } else if (cityLower.includes('oslo')) {
                  languages.push('Norwegian');
                } else if (cityLower.includes('copenhagen')) {
                  languages.push('Danish');
                } else if (cityLower.includes('helsinki')) {
                  languages.push('Finnish');
                } else if (cityLower.includes('moscow') || cityLower.includes('saint petersburg') || cityLower.includes('novosibirsk')) {
                  languages.push('Russian');
                } else if (cityLower.includes('beijing') || cityLower.includes('shanghai') || cityLower.includes('guangzhou')) {
                  languages.push('Chinese');
                } else if (cityLower.includes('tokyo') || cityLower.includes('osaka') || cityLower.includes('kyoto')) {
                  languages.push('Japanese');
                } else if (cityLower.includes('seoul')) {
                  languages.push('Korean');
                } else if (cityLower.includes('mumbai') || cityLower.includes('delhi') || cityLower.includes('bangalore') || cityLower.includes('hyderabad')) {
                  languages.push('Hindi');
                } else if (cityLower.includes('tel aviv') || cityLower.includes('jerusalem')) {
                  languages.push('Hebrew');
                } else if (cityLower.includes('dubai') || cityLower.includes('riyadh') || cityLower.includes('cairo')) {
                  languages.push('Arabic');
                } else if (cityLower.includes('istanbul') || cityLower.includes('ankara')) {
                  languages.push('Turkish');
                }
                
                // Return formatted language string
                return languages.length > 1 ? languages.join(', ') : languages[0] || 'English';
              };
              
              // Calculate individual field completion for display purposes (matching gamification service logic exactly)
              const checkField = (field) => {
                if (typeof field === 'boolean') return field;
                if (Array.isArray(field)) return field.length > 0;
                return field && field.toString().trim().length > 0;
              };
              
              // Check original profile fields (for determining which fields to show)
              const originalFields = {
                username: checkField(currentProfile.username),
                name: checkField(currentProfile.name),
                bio: checkField(currentProfile.bio),
                city: checkField(currentProfile.city),
                birthDate: checkField(currentProfile.birthDate),
                tags: checkField(currentProfile.tagsList && currentProfile.tagsList.length > 0),
                socialLinks: checkField(currentProfile.socialLinks && currentProfile.socialLinks.length > 0),
                relationship: checkField(currentProfile.relationshipStatus),
                lookingFor: checkField(currentProfile.lookingFor),
                languages: checkField(currentProfile.languages),
                education: checkField(currentProfile.education),
                occupation: checkField(currentProfile.occupation),
                email: checkField(currentProfile.email),
                preferredContact: checkField(currentProfile.preferredContactMethod)
              };
              
              // Check fields including form data (for real-time completeness calculation)
              const fields = {
                username: checkField(currentProfile.username),
                name: checkField(formData.name || currentProfile.name),
                bio: checkField(formData.bio || currentProfile.bio),
                city: checkField(formData.city || currentProfile.city),
                birthDate: checkField(formData.birthDate || currentProfile.birthDate),
                tags: checkField((formData.tagsList || currentProfile.tagsList) && (formData.tagsList || currentProfile.tagsList).length > 0),
                socialLinks: checkField((formData.socialLinks || currentProfile.socialLinks) && (formData.socialLinks || currentProfile.socialLinks).length > 0),
                relationship: checkField(formData.relationshipStatus || currentProfile.relationshipStatus),
                lookingFor: checkField(formData.lookingFor || currentProfile.lookingFor),
                languages: checkField(formData.languages || currentProfile.languages),
                education: checkField(formData.education || currentProfile.education),
                occupation: checkField(formData.occupation || currentProfile.occupation),
                email: checkField(formData.email || currentProfile.email),
                preferredContact: checkField(formData.preferredContactMethod || currentProfile.preferredContactMethod)
              };

              // Handle saving wizard changes
              const handleSaveWizard = async () => {
                if (!walletAddress || !currentProfile) return;
                
                setIsSavingWizard(true);
                try {
                  // Handle birth date conversion: HTML date input returns YYYY-MM-DD, but profile expects DD.MM.YYYY
                  let birthDateFormatted = wizardFormData.birthDate !== undefined ? wizardFormData.birthDate : currentProfile.birthDate;
                  let calculatedAge = currentProfile.age;
                  
                  if (wizardFormData.birthDate && wizardFormData.birthDate !== currentProfile.birthDate) {
                    // Convert YYYY-MM-DD to DD.MM.YYYY format
                    if (typeof wizardFormData.birthDate === 'string' && wizardFormData.birthDate.includes('-')) {
                      try {
                        const birth = new Date(wizardFormData.birthDate + 'T00:00:00');
                        const day = String(birth.getDate()).padStart(2, '0');
                        const month = String(birth.getMonth() + 1).padStart(2, '0');
                        birthDateFormatted = `${day}.${month}.${birth.getFullYear()}`;
                        
                        // Calculate age
                        const today = new Date();
                        calculatedAge = today.getFullYear() - birth.getFullYear();
                        const monthDiff = today.getMonth() - birth.getMonth();
                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                          calculatedAge--;
                        }
                      } catch (error) {
                        console.error('Error converting birth date:', error);
                        birthDateFormatted = wizardFormData.birthDate;
                        calculatedAge = utils.calculateAge(birthDateFormatted);
                      }
                    } else {
                      // Already in DD.MM.YYYY format or some other format, use as-is and calculate age
                      birthDateFormatted = wizardFormData.birthDate;
                      calculatedAge = utils.calculateAge(birthDateFormatted);
                    }
                  }
                  
                  // Merge wizard form data with current profile
                  let updatedProfile = {
                    ...currentProfile,
                    ...wizardFormData,
                    // Override birthDate and age with formatted/converted values
                    birthDate: birthDateFormatted,
                    age: calculatedAge,
                    // Ensure arrays are properly set
                    tagsList: wizardFormData.tagsList || currentProfile.tagsList || [],
                    socialLinks: wizardFormData.socialLinks || currentProfile.socialLinks || []
                  };
                  
                  // If city was changed, set location coordinates
                  if (wizardFormData.city && wizardFormData.city !== currentProfile.city) {
                    // Get city coordinates - use stored coordinates if available, otherwise try to get from city name
                    let location;
                    if (wizardFormData.cityLat && wizardFormData.cityLng) {
                      // Use coordinates that were set when city was selected
                      location = {
                        name: wizardFormData.city,
                        city: wizardFormData.city,
                        lat: wizardFormData.cityLat,
                        lng: wizardFormData.cityLng
                      };
                    } else {
                      // Try to get coordinates from city name
                      const coords = getCityCoordinates(wizardFormData.city);
                      location = coords ? {
                        name: wizardFormData.city,
                        city: wizardFormData.city,
                        lat: coords.lat,
                        lng: coords.lng
                      } : {
                        name: wizardFormData.city,
                        city: wizardFormData.city,
                        lat: 52.52,
                        lng: 13.405
                      };
                    }
                    updatedProfile.city = wizardFormData.city;
                    updatedProfile.location = location;
                  }
                  // #region agent log
                  fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'App.js:22406',message:'Wizard save - updatedProfile before encryptAndSaveProfile',data:{updatedProfileBio:updatedProfile.bio,updatedProfileBioLength:updatedProfile.bio?.length,wizardFormDataBio:wizardFormData.bio,currentProfileBio:currentProfile.bio,publishedBio:publishedBio},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
                  // #endregion
                  
                  // Track which fields were actually changed (compare with original profile)
                  const changedFields = {};
                  
                  // Helper to compare values (handles arrays and strings)
                  const hasChanged = (fieldName, oldVal, newVal) => {
                    if (Array.isArray(oldVal) && Array.isArray(newVal)) {
                      if (oldVal.length !== newVal.length) return true;
                      return oldVal.some((val, idx) => val !== newVal[idx]);
                    }
                    const oldStr = oldVal ? String(oldVal).trim() : '';
                    const newStr = newVal ? String(newVal).trim() : '';
                    return oldStr !== newStr;
                  };
                  
                  // Helper to compare birth dates (handles different formats: DD.MM.YYYY vs YYYY-MM-DD)
                  const hasBirthDateChanged = (oldVal, newVal) => {
                    // If newVal is undefined, user didn't change it, so it hasn't changed
                    if (newVal === undefined) return false;
                    if (!oldVal && !newVal) return false;
                    if (!oldVal || !newVal) return true;
                    
                    try {
                      let oldDate, newDate;
                      
                      // Parse old date (DD.MM.YYYY format)
                      if (typeof oldVal === 'string' && oldVal.includes('.')) {
                        const parts = oldVal.split('.');
                        if (parts.length === 3) {
                          oldDate = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                        } else {
                          oldDate = new Date(oldVal);
                        }
                      } else {
                        oldDate = new Date(oldVal);
                      }
                      
                      // Parse new date (YYYY-MM-DD format from date input)
                      if (typeof newVal === 'string' && newVal.includes('-')) {
                        newDate = new Date(newVal + 'T00:00:00');
                      } else if (typeof newVal === 'string' && newVal.includes('.')) {
                        const parts = newVal.split('.');
                        if (parts.length === 3) {
                          newDate = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
                        } else {
                          newDate = new Date(newVal);
                        }
                      } else {
                        newDate = new Date(newVal);
                      }
                      
                      // Compare dates (ignore time)
                      return oldDate.getTime() !== newDate.getTime();
                    } catch (error) {
                      // If parsing fails, do string comparison
                      return oldVal !== newVal;
                    }
                  };
                  
                  // Check each field that could be changed in wizard
                  if (hasChanged('name', currentProfile.name, wizardFormData.name)) {
                    changedFields.name = true;
                  }
                  if (hasChanged('bio', currentProfile.bio, wizardFormData.bio)) {
                    changedFields.bio = true;
                  }
                  if (hasChanged('city', currentProfile.city, wizardFormData.city)) {
                    changedFields.city = true;
                  }
                  if (hasBirthDateChanged(currentProfile.birthDate, wizardFormData.birthDate)) {
                    changedFields.birthDate = true;
                  }
                  if (hasChanged('tagsList', currentProfile.tagsList, wizardFormData.tagsList)) {
                    changedFields.tagsList = true;
                  }
                  if (hasChanged('socialLinks', currentProfile.socialLinks, wizardFormData.socialLinks)) {
                    changedFields.socialLinks = true;
                  }
                  if (hasChanged('relationshipStatus', currentProfile.relationshipStatus, wizardFormData.relationshipStatus)) {
                    changedFields.relationshipStatus = true;
                  }
                  if (hasChanged('lookingFor', currentProfile.lookingFor, wizardFormData.lookingFor)) {
                    changedFields.lookingFor = true;
                  }
                  if (hasChanged('languages', currentProfile.languages, wizardFormData.languages)) {
                    changedFields.languages = true;
                  }
                  if (hasChanged('education', currentProfile.education, wizardFormData.education)) {
                    changedFields.education = true;
                  }
                  if (hasChanged('occupation', currentProfile.occupation, wizardFormData.occupation)) {
                    changedFields.occupation = true;
                  }
                  const emailChanged = hasChanged('email', currentProfile.email, wizardFormData.email);
                  if (emailChanged) {
                    changedFields.email = true;
                  }
                  if (hasChanged('preferredContactMethod', currentProfile.preferredContactMethod, wizardFormData.preferredContactMethod)) {
                    changedFields.preferredContactMethod = true;
                  }
                  
                  // Check if email was changed and needs verification
                  const newEmail = wizardFormData.email ? wizardFormData.email.trim() : null;
                  if (emailChanged && newEmail && !isEmailRecentlyVerified(newEmail, 'change')) {
                    setIsSavingWizard(false);
                    return openEmailVerificationModal({
                      email: newEmail,
                      purpose: 'change',
                      onVerified: async () => {
                        // After verification, proceed with saving the wizard
                        try {
                          setIsSavingWizard(true);
                          // Handle birth date conversion: HTML date input returns YYYY-MM-DD, but profile expects DD.MM.YYYY
                          let verifiedBirthDateFormatted = wizardFormData.birthDate !== undefined ? wizardFormData.birthDate : currentProfile.birthDate;
                          let verifiedCalculatedAge = currentProfile.age;
                          
                          if (wizardFormData.birthDate && wizardFormData.birthDate !== currentProfile.birthDate) {
                            // Convert YYYY-MM-DD to DD.MM.YYYY format
                            if (typeof wizardFormData.birthDate === 'string' && wizardFormData.birthDate.includes('-')) {
                              try {
                                const birth = new Date(wizardFormData.birthDate + 'T00:00:00');
                                const day = String(birth.getDate()).padStart(2, '0');
                                const month = String(birth.getMonth() + 1).padStart(2, '0');
                                verifiedBirthDateFormatted = `${day}.${month}.${birth.getFullYear()}`;
                                
                                // Calculate age
                                const today = new Date();
                                verifiedCalculatedAge = today.getFullYear() - birth.getFullYear();
                                const monthDiff = today.getMonth() - birth.getMonth();
                                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                                  verifiedCalculatedAge--;
                                }
                              } catch (error) {
                                console.error('Error converting birth date:', error);
                                verifiedBirthDateFormatted = wizardFormData.birthDate;
                                verifiedCalculatedAge = utils.calculateAge(verifiedBirthDateFormatted);
                              }
                            } else {
                              // Already in DD.MM.YYYY format or some other format, use as-is and calculate age
                              verifiedBirthDateFormatted = wizardFormData.birthDate;
                              verifiedCalculatedAge = utils.calculateAge(verifiedBirthDateFormatted);
                            }
                          }
                          
                          // Merge wizard form data with current profile, including verified email
                          let verifiedProfile = {
                            ...currentProfile,
                            ...wizardFormData,
                            email: newEmail,
                            emailVerifiedAt: new Date().toISOString(),
                            // Override birthDate and age with formatted/converted values
                            birthDate: verifiedBirthDateFormatted,
                            age: verifiedCalculatedAge,
                            // Ensure arrays are properly set
                            tagsList: wizardFormData.tagsList || currentProfile.tagsList || [],
                            socialLinks: wizardFormData.socialLinks || currentProfile.socialLinks || []
                          };
                          
                          // If city was changed, set location coordinates
                          if (wizardFormData.city && wizardFormData.city !== currentProfile.city) {
                            // Get city coordinates - use stored coordinates if available, otherwise try to get from city name
                            let location;
                            if (wizardFormData.cityLat && wizardFormData.cityLng) {
                              // Use coordinates that were set when city was selected
                              location = {
                                name: wizardFormData.city,
                                city: wizardFormData.city,
                                lat: wizardFormData.cityLat,
                                lng: wizardFormData.cityLng
                              };
                            } else {
                              // Try to get coordinates from city name
                              const coords = getCityCoordinates(wizardFormData.city);
                              location = coords ? {
                                name: wizardFormData.city,
                                city: wizardFormData.city,
                                lat: coords.lat,
                                lng: coords.lng
                              } : {
                                name: wizardFormData.city,
                                city: wizardFormData.city,
                                lat: 52.52,
                                lng: 13.405
                              };
                            }
                            verifiedProfile.city = wizardFormData.city;
                            verifiedProfile.location = location;
                          }
                          
                          // Use encryptAndSaveProfile to save (handles encryption and IPFS upload)
                          // NOTE: New fields are saved to IPFS but NOT published to Firestore
                          // User must explicitly publish fields they want visible to others
                          const result = await encryptAndSaveProfile(verifiedProfile, false);
                          // #region agent log
                          fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'App.js:22473',message:'Wizard encryptAndSaveProfile result',data:{hasResult:!!result,wasDeferred:result?.deferred,hasPendingData:!!pendingProfileRef.current},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
                          // #endregion
                          
                          // If save was deferred, commit pending changes now (wizard should save immediately)
                          if (result?.deferred) {
                            await commitPendingProfileChanges();
                          }
                          
                          // Don't mark fields as recentlyChanged - the save is immediate, so commitPendingProfileChanges
                          // already cleared the recentlyChanged state. Fields are saved to IPFS (unpublished), no outlines needed.
                          
                          // Update current profile state
                          setCurrentProfile(verifiedProfile);
                          
                          // Update profile completeness
                          if (walletAddress) {
                            await updateProfileCompleteness(walletAddress, verifiedProfile);
                          }
                          
                          setNotification({ message: 'Email verified and profile updated successfully!', type: 'success' });
                          // Auto-dismiss notification after 5 seconds
                          setTimeout(() => setNotification(null), 5000);
                          setShowProfileCompletenessWizard(false);
                        } catch (error) {
                          console.error('Error saving wizard changes after email verification:', error);
                          setNotification({ 
                            message: error.message || 'Email verified but failed to save profile changes. Please try again.', 
                            type: 'error' 
                          });
                          // Auto-dismiss error notification after 5 seconds
                          setTimeout(() => setNotification(null), 5000);
                        } finally {
                          setIsSavingWizard(false);
                        }
                      }
                    });
                  }
                  
                  // If email wasn't changed or was recently verified, proceed with normal save
                  // Use encryptAndSaveProfile to save (handles encryption and IPFS upload)
                  // NOTE: New fields are saved to IPFS but NOT published to Firestore
                  // User must explicitly publish fields they want visible to others
                  const result = await encryptAndSaveProfile(updatedProfile, false);
                  // #region agent log
                  fetch('http://127.0.0.1:7242/ingest/9142f40e-ecc7-45d8-bf15-d3885953eeb6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'App.js:22473',message:'Wizard encryptAndSaveProfile result',data:{hasResult:!!result,wasDeferred:result?.deferred,hasPendingData:!!pendingProfileRef.current},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
                  // #endregion
                  
                  // If save was deferred, commit pending changes now (wizard should save immediately)
                  if (result?.deferred) {
                    await commitPendingProfileChanges();
                  }
                  
                  // Don't mark fields as recentlyChanged - the save is immediate, so commitPendingProfileChanges
                  // already cleared the recentlyChanged state. Fields are saved to IPFS (unpublished), no outlines needed.
                  
                  // Update current profile state
                  setCurrentProfile(updatedProfile);
                  
                  // Update profile completeness
                  if (walletAddress) {
                    await updateProfileCompleteness(walletAddress, updatedProfile);
                  }
                  
                  setNotification({ message: 'Profile updated successfully!', type: 'success' });
                  // Auto-dismiss notification after 5 seconds
                  setTimeout(() => setNotification(null), 5000);
                  setShowProfileCompletenessWizard(false);
                } catch (error) {
                  console.error('Error saving wizard changes:', error);
                  setNotification({ 
                    message: error.message || 'Failed to save profile changes. Please try again.', 
                    type: 'error' 
                  });
                  // Auto-dismiss error notification after 5 seconds
                  setTimeout(() => setNotification(null), 5000);
                } finally {
                  setIsSavingWizard(false);
                }
              };

              // Get suggestions for a field type
              const getFieldSuggestions = (fieldType) => {
                switch (fieldType) {
                  case 'tags':
                    // Show all tag suggestions (don't filter out already-added ones)
                    // This allows users to keep clicking to add more tags, just like in registration form
                    return allTags.slice(0, 50); // Increased from 20 to 50
                  case 'socialLinks':
                    const currentSocials = formData.socialLinks || currentProfile.socialLinks || [];
                    // Show all available social platforms (no limit)
                    return socialPlatforms.filter(social => !currentSocials.some(s => s.includes(social)));
                  case 'relationship':
                    return personalDataFields.find(f => f.label === 'Relationship')?.values || [];
                  case 'languages':
                    const cityLanguage = getLanguageForCity(formData.city || currentProfile.city);
                    const baseLanguages = personalDataFields.find(f => f.label === 'Languages')?.values || [];
                    // Always include English-based suggestions and city-specific language
                    const languageSuggestions = [];
                    if (cityLanguage && typeof cityLanguage === 'string' && cityLanguage !== 'English') {
                      const nonEnglishLang = cityLanguage.split(', ').filter(l => l !== 'English')[0];
                      if (nonEnglishLang) {
                        languageSuggestions.push(`English, ${nonEnglishLang}`);
                      }
                    }
                    languageSuggestions.push('English');
                    languageSuggestions.push(...baseLanguages.filter(l => !languageSuggestions.includes(l)));
                    // Add more language combinations
                    const additionalLanguages = [
                      'English, Russian', 'English, Arabic', 'English, Portuguese', 'English, Dutch',
                      'English, Swedish', 'English, Norwegian', 'English, Danish', 'English, Finnish',
                      'English, Polish', 'English, Czech', 'English, Hungarian', 'English, Romanian',
                      'English, Greek', 'English, Turkish', 'English, Korean', 'English, Hindi',
                      'Spanish, French', 'French, German', 'German, Italian', 'Multilingual'
                    ];
                    languageSuggestions.push(...additionalLanguages.filter(l => !languageSuggestions.includes(l)));
                    return languageSuggestions;
                  case 'education':
                    const baseEducation = personalDataFields.find(f => f.label === 'Education')?.values || [];
                    // Add more education options
                    return [
                      ...baseEducation,
                      'Associate Degree', 'Certificate', 'Diploma', 'Trade School', 'Vocational Training',
                      'Online Course', 'Self-Taught', 'Bootcamp', 'Apprenticeship', 'Continuing Education'
                    ];
                  case 'occupation':
                    // Get all occupation options from personalDataFields (which already has many options)
                    const baseOccupation = personalDataFields.find(f => f.label === 'Occupation')?.values || [];
                    // Add additional occupation options that might not be in the base list
                    const additionalOccupations = [
                      'Social Worker', 'Counselor', 'Pharmacist', 'Paramedic', 'Firefighter',
                      'Police Officer', 'Military', 'Government Employee', 'Non-Profit Worker', 'Volunteer',
                      'Retired', 'Freelancer', 'Contractor', 'Intern', 'Trainee', 'Apprentice',
                      'Manager', 'Director', 'Executive', 'CEO', 'CTO', 'CFO', 'COO',
                      'Professor', 'Lecturer', 'Tutor', 'Instructor', 'Trainer', 'Coach',
                      'Nanny', 'Babysitter', 'Caregiver', 'Home Health Aide', 'Personal Assistant',
                      'Event Planner', 'Wedding Planner', 'Party Planner', 'Travel Agent',
                      'Tour Guide', 'Interpreter', 'Sign Language Interpreter', 'Court Reporter',
                      'Bailiff', 'Judge', 'Paralegal', 'Legal Assistant', 'Notary',
                      'Insurance Agent', 'Real Estate Broker', 'Mortgage Broker', 'Loan Officer',
                      'Banker', 'Teller', 'Financial Planner', 'Investment Advisor', 'Stockbroker',
                      'Auditor', 'Bookkeeper', 'Tax Preparer', 'Payroll Specialist', 'HR Specialist',
                      'Recruiter', 'Talent Acquisition', 'Compensation Analyst', 'Benefits Administrator'
                    ];
                    // Combine and remove duplicates, but ensure "Other..." is always at the bottom
                    const allOccupationsSet = new Set([...baseOccupation, ...additionalOccupations]);
                    const otherOption = 'Other...';
                    allOccupationsSet.delete(otherOption); // Remove it if it exists
                    const allOccupations = [...allOccupationsSet].sort(); // Sort alphabetically
                    allOccupations.push(otherOption); // Always add "Other..." at the end
                    return allOccupations;
                  case 'city':
                    // Get unique cities from worldLocations - show more cities
                    const uniqueCities = [...new Set(worldLocations.map(loc => loc.city))];
                    return uniqueCities.slice(0, 50); // Increased from 20 to 50
                  case 'name':
                    // Suggest more common first names
                    return [
                      'Emma', 'Michael', 'Sarah', 'David', 'Lisa', 'James', 'Maria', 'Robert', 'Jennifer', 'Thomas',
                      'Emily', 'Christopher', 'Jessica', 'Daniel', 'Ashley', 'Matthew', 'Amanda', 'Joshua', 'Melissa',
                      'Andrew', 'Michelle', 'Joseph', 'Nicole', 'Ryan', 'Stephanie', 'John', 'Lauren', 'William',
                      'Kimberly', 'Anthony', 'Amy', 'Mark', 'Angela', 'Donald', 'Rebecca', 'Steven', 'Sharon',
                      'Paul', 'Cynthia', 'Kevin', 'Kathleen', 'Brian', 'Samantha', 'George', 'Deborah', 'Kenneth',
                      'Rachel', 'Edward', 'Carolyn', 'Ronald', 'Janet', 'Timothy', 'Virginia', 'Jason', 'Maria',
                      'Jeffrey', 'Heather', 'Ryan', 'Diane', 'Jacob', 'Julie', 'Gary', 'Joyce', 'Nicholas', 'Victoria'
                    ];
                  case 'bio':
                    // Return AI-generated suggestions if available, otherwise return static fallbacks
                    if (aiBioSuggestions.length > 0) {
                      return aiBioSuggestions;
                    }
                    // Static fallbacks (shown before AI generates personalized ones)
                    return [
                      'Click "Generate AI Suggestions" for personalized bios',
                      'Passionate about life and always learning new things.',
                      'Creative mind with diverse interests.',
                      'Here to connect with interesting people.'
                    ];
                  case 'lookingFor':
                    return [
                      'Friends and connections',
                      'Professional networking',
                      'Collaboration opportunities',
                      'Learning and mentorship',
                      'Social activities and events',
                      'Travel companions',
                      'Business partners',
                      'Creative collaborators',
                      'Romantic relationship',
                      'Platonic friendships',
                      'Study partners',
                      'Fitness buddies',
                      'Hobby enthusiasts',
                      'Investment partners',
                      'Startup co-founders',
                      'Mentors and mentees',
                      'Language exchange',
                      'Cultural exchange',
                      'Volunteer opportunities',
                      'Event organizers',
                      'Content creators',
                      'Photography models',
                      'Music collaborators',
                      'Writing partners',
                      'Gaming teammates'
                    ];
                  case 'preferredContactMethod':
                    return [
                      'PeopleTag',
                      'Email',
                      'Telegram',
                      'Instagram',
                      'Twitter/X',
                      'LinkedIn',
                      'WhatsApp',
                      'Phone call',
                      'In-app messaging',
                      'Discord',
                      'Signal',
                      'Facebook Messenger',
                      'Skype',
                      'Zoom',
                      'WeChat',
                      'Line',
                      'Viber',
                      'Snapchat',
                      'TikTok',
                      'Reddit',
                      'Slack',
                      'Microsoft Teams'
                    ];
                  default:
                    return [];
                }
              };

              // Handle suggestion click
              const handleSuggestionClick = (fieldType, value) => {
                if (fieldType === 'tags') {
                  const currentTags = wizardFormData.tagsList || [];
                  if (!currentTags.includes(value)) {
                    setWizardFormData({ ...wizardFormData, tagsList: [...currentTags, value] });
                  }
                } else if (fieldType === 'socialLinks') {
                  const currentSocials = wizardFormData.socialLinks || [];
                  const socialUrl = value.includes('://') ? value : `https://${value}`;
                  if (!currentSocials.includes(socialUrl)) {
                    setWizardFormData({ ...wizardFormData, socialLinks: [...currentSocials, socialUrl] });
                  }
                } else if (fieldType === 'city') {
                  // When city is selected, get coordinates and set both city and location
                  const coords = getCityCoordinates(value);
                  if (coords) {
                    setWizardFormData({ 
                      ...wizardFormData, 
                      city: value,
                      // Store location coordinates in a way that will be used when saving
                      cityLat: coords.lat,
                      cityLng: coords.lng
                    });
                  } else {
                    setWizardFormData({ ...wizardFormData, city: value });
                  }
                } else {
                  setWizardFormData({ ...wizardFormData, [fieldType]: value });
                }
              };

              // Get profile strength
              const getProfileStrength = (percentage) => {
                if (percentage >= 80) return { level: 'Excellent', color: '#4caf50', emoji: '🌟' };
                if (percentage >= 60) return { level: 'Good', color: '#4a90e2', emoji: '✨' };
                if (percentage >= 40) return { level: 'Fair', color: '#ff9800', emoji: '📝' };
                return { level: 'Basic', color: '#999', emoji: '🔰' };
              };

              const strength = getProfileStrength(percentage);

              // Field configuration for rendering
              const allFieldConfigs = [
                { key: 'name', label: 'Name', type: 'text', placeholder: 'Enter your name', required: true, suggestions: 'name', fieldCheckKey: 'name' },
                { key: 'city', label: 'City', type: 'text', placeholder: 'Enter your city', required: true, suggestions: 'city', fieldCheckKey: 'city' },
                { key: 'birthDate', label: 'Birth Date', type: 'date', placeholder: 'Select your birth date', required: true, fieldCheckKey: 'birthDate' },
                { key: 'tagsList', label: 'Tags', type: 'tags', placeholder: 'Add tags', required: true, suggestions: 'tags', fieldCheckKey: 'tags' },
                { key: 'socialLinks', label: 'Social Links', type: 'socialLinks', placeholder: 'Add social media links', required: true, suggestions: 'socialLinks', fieldCheckKey: 'socialLinks' },
                { key: 'relationshipStatus', label: 'Relationship Status', type: 'select', placeholder: 'Select relationship status', required: true, suggestions: 'relationship', fieldCheckKey: 'relationship' },
                { key: 'lookingFor', label: 'Looking For', type: 'text', placeholder: 'What are you looking for?', required: true, suggestions: 'lookingFor', fieldCheckKey: 'lookingFor' },
                { key: 'languages', label: 'Languages', type: 'select', placeholder: 'Select languages', required: true, suggestions: 'languages', fieldCheckKey: 'languages' },
                { key: 'education', label: 'Education', type: 'select', placeholder: 'Select education level', required: true, suggestions: 'education', fieldCheckKey: 'education' },
                { key: 'occupation', label: 'Occupation', type: 'select', placeholder: 'Select occupation', required: true, suggestions: 'occupation', fieldCheckKey: 'occupation' },
                { key: 'email', label: 'Email', type: 'email', placeholder: 'Enter your email', required: true, fieldCheckKey: 'email' },
                { key: 'preferredContactMethod', label: 'Preferred Contact', type: 'text', placeholder: 'How should people contact you?', required: true, suggestions: 'preferredContactMethod', fieldCheckKey: 'preferredContact' },
                { key: 'bio', label: 'Bio', type: 'textarea', placeholder: 'Tell others about yourself', required: true, suggestions: 'bio', fieldCheckKey: 'bio' }
              ];

              // Filter to only show incomplete fields (check original profile, not form data)
              const fieldConfigs = allFieldConfigs.filter(config => {
                const checkKey = config.fieldCheckKey;
                // Check the original profile, not the form data, to determine if field was already filled
                return !originalFields[checkKey];
              });

              return (
                <>
                  {/* Progress Bar - Fixed Top */}
                  <div style={{ padding: '24px', paddingBottom: '20px', flexShrink: 0 }}>
                    <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Profile Completion</span>
                      <span style={{ fontSize: '18px', fontWeight: '600', color: strength.color }}>{percentage}%</span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '24px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        backgroundColor: strength.color,
                        transition: 'width 0.3s ease',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: '8px',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {percentage >= 15 && `${percentage}%`}
                      </div>
                    </div>
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{strength.emoji}</span>
                      <span style={{ fontSize: '14px', color: strength.color, fontWeight: '500' }}>{strength.level} Profile Strength</span>
                    </div>
                    </div>
                  </div>

                  {/* Form Fields - Scrollable Middle */}
                  <div style={{ padding: '0 24px', overflowY: 'auto', flex: 1 }}>
                    <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '16px' }}>Complete Your Profile</h3>
                    {fieldConfigs.length === 0 ? (
                      <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        backgroundColor: '#f0f9ff', 
                        borderRadius: '8px',
                        border: '1px solid #4a90e2'
                      }}>
                        <div style={{ fontSize: '18px', marginBottom: '8px' }}>🎉</div>
                        <div style={{ fontSize: '14px', color: '#333', fontWeight: '500', marginBottom: '4px' }}>
                          All required fields are complete!
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Your profile is fully set up.
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {fieldConfigs.map((config) => {
                          const isComplete = fields[config.key === 'tagsList' ? 'tags' : config.key === 'socialLinks' ? 'socialLinks' : config.key];
                          const suggestions = config.suggestions ? getFieldSuggestions(config.suggestions) : [];
                          
                          // Handle birth date conversion: profile stores DD.MM.YYYY, but date input needs YYYY-MM-DD
                          let fieldValue = wizardFormData[config.key] !== undefined ? wizardFormData[config.key] : (currentProfile[config.key] || '');
                          if (config.key === 'birthDate' && config.type === 'date' && fieldValue) {
                            // If we have a birth date in DD.MM.YYYY format, convert to YYYY-MM-DD for the date input
                            if (typeof fieldValue === 'string' && fieldValue.includes('.')) {
                              const parts = fieldValue.split('.');
                              if (parts.length === 3) {
                                fieldValue = `${parts[2]}-${parts[1]}-${parts[0]}`;
                              }
                            }
                          }
                          
                          return (
                            <div key={config.key} style={{ marginBottom: '8px' }}>
                              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                                {config.label} {config.required && <span style={{ color: '#ff4444' }}>*</span>}
                                {!isComplete && <span style={{ fontSize: '12px', color: '#4a90e2', marginLeft: '8px' }}>+7%</span>}
                              </label>
                              
                              {/* Input Field */}
                              {config.type === 'textarea' ? (
                                <textarea
                                  value={fieldValue}
                                  onChange={(e) => setWizardFormData({ ...wizardFormData, [config.key]: e.target.value })}
                                  placeholder={config.placeholder}
                                  style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: `1px solid ${isComplete ? '#ddd' : '#ff9800'}`,
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                    minHeight: '80px',
                                    resize: 'vertical'
                                  }}
                                />
                              ) : config.type === 'tags' ? (
                                <div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px', minHeight: '40px', padding: '8px', border: `1px solid ${isComplete ? '#ddd' : '#ff9800'}`, borderRadius: '8px' }}>
                                    {(wizardFormData.tagsList || currentProfile.tagsList || []).map((tag, idx) => (
                                      <span
                                        key={idx}
                                        style={{
                                          padding: '4px 10px',
                                          backgroundColor: '#4a90e2',
                                          color: 'white',
                                          borderRadius: '12px',
                                          fontSize: '12px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px'
                                        }}
                                      >
                                        {tag}
                                        <button
                                          onClick={() => {
                                            const currentTags = wizardFormData.tagsList || currentProfile.tagsList || [];
                                            const newTags = currentTags.filter((_, i) => i !== idx);
                                            setWizardFormData({ ...wizardFormData, tagsList: newTags });
                                          }}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'white',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            padding: '0',
                                            marginLeft: '4px'
                                          }}
                                        >
                                          ×
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ) : config.type === 'socialLinks' ? (
                                <div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                                    {(wizardFormData.socialLinks || currentProfile.socialLinks || []).map((link, idx) => (
                                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                          type="text"
                                          value={link}
                                          onChange={(e) => {
                                            const currentLinks = wizardFormData.socialLinks || currentProfile.socialLinks || [];
                                            const newLinks = [...currentLinks];
                                            newLinks[idx] = e.target.value;
                                            setWizardFormData({ ...wizardFormData, socialLinks: newLinks });
                                          }}
                                          placeholder="https://..."
                                          style={{
                                            flex: 1,
                                            padding: '8px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                          }}
                                        />
                                        <button
                                          onClick={() => {
                                            const currentLinks = wizardFormData.socialLinks || currentProfile.socialLinks || [];
                                            const newLinks = currentLinks.filter((_, i) => i !== idx);
                                            setWizardFormData({ ...wizardFormData, socialLinks: newLinks });
                                          }}
                                          style={{
                                            padding: '8px 12px',
                                            background: '#ff4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      onClick={() => {
                                        const currentLinks = wizardFormData.socialLinks || currentProfile.socialLinks || [];
                                        const newLinks = [...currentLinks, ''];
                                        setWizardFormData({ ...wizardFormData, socialLinks: newLinks });
                                      }}
                                      style={{
                                        padding: '8px 12px',
                                        background: '#4a90e2',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                      }}
                                    >
                                      + Add Social Link
                                    </button>
                                  </div>
                                </div>
                              ) : config.type === 'select' ? (
                                <div>
                                  <select
                                    value={config.key === 'occupation' && (fieldValue === 'Other...' || (fieldValue && !suggestions.includes(fieldValue))) ? 'Other...' : fieldValue}
                                    onChange={(e) => {
                                      const selectedValue = e.target.value;
                                      if (config.key === 'occupation' && selectedValue === 'Other...') {
                                        // When "Other..." is selected for occupation, set it and clear any previous custom value
                                        setWizardFormData({ ...wizardFormData, [config.key]: 'Other...' });
                                      } else {
                                        // When a regular option is selected, set it normally
                                        setWizardFormData({ ...wizardFormData, [config.key]: selectedValue });
                                      }
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '10px',
                                      border: `1px solid ${isComplete ? '#ddd' : '#ff9800'}`,
                                      borderRadius: '8px',
                                      fontSize: '14px',
                                      fontFamily: 'inherit'
                                    }}
                                  >
                                    <option value="">{config.placeholder}</option>
                                    {suggestions.map((suggestion, idx) => (
                                      <option key={idx} value={suggestion}>{suggestion}</option>
                                    ))}
                                  </select>
                                  {/* Show text input when "Other..." is selected for occupation */}
                                  {config.key === 'occupation' && (fieldValue === 'Other...' || (fieldValue && !suggestions.includes(fieldValue) && fieldValue !== '')) && (
                                    <input
                                      type="text"
                                      value={fieldValue === 'Other...' ? '' : fieldValue}
                                      onChange={(e) => {
                                        // Store the custom occupation value (limit to 25 characters)
                                        const newValue = e.target.value.slice(0, 25);
                                        setWizardFormData({ ...wizardFormData, [config.key]: newValue });
                                      }}
                                      placeholder="Enter your occupation"
                                      maxLength={25}
                                      style={{
                                        width: '100%',
                                        padding: '10px',
                                        marginTop: '8px',
                                        border: `1px solid ${isComplete ? '#ddd' : '#ff9800'}`,
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontFamily: 'inherit'
                                      }}
                                    />
                                  )}
                                </div>
                              ) : (
                                <input
                                  type={config.type}
                                  value={fieldValue}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    // If this is the city field, try to get coordinates when city name matches
                                    if (config.key === 'city') {
                                      const coords = getCityCoordinates(newValue);
                                      if (coords) {
                                        setWizardFormData({ 
                                          ...wizardFormData, 
                                          [config.key]: newValue,
                                          cityLat: coords.lat,
                                          cityLng: coords.lng
                                        });
                                      } else {
                                        setWizardFormData({ 
                                          ...wizardFormData, 
                                          [config.key]: newValue,
                                          cityLat: null,
                                          cityLng: null
                                        });
                                      }
                                    } else {
                                      setWizardFormData({ ...wizardFormData, [config.key]: newValue });
                                    }
                                  }}
                                  placeholder={config.placeholder}
                                  style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: `1px solid ${isComplete ? '#ddd' : '#ff9800'}`,
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontFamily: 'inherit'
                                  }}
                                />
                              )}
                              
                              {/* Suggestion Cloud - Special handling for bio with AI generation */}
                              {!isComplete && config.key === 'bio' && (
                                <div style={{ marginTop: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <div style={{ fontSize: '12px', color: '#666' }}>AI-Powered Suggestions:</div>
                                    <button
                                      onClick={async () => {
                                        setIsLoadingBioSuggestions(true);
                                        try {
                                          const apiUrl = process.env.REACT_APP_API_URL || 
                                            (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
                                          
                                          // Build profile data for AI from current profile and form data
                                          const profileForAI = {
                                            name: wizardFormData.name || currentProfile.name || '',
                                            tagsList: wizardFormData.tagsList || currentProfile.tagsList || [],
                                            occupation: wizardFormData.occupation || currentProfile.occupation || '',
                                            city: wizardFormData.city || currentProfile.city || '',
                                            education: wizardFormData.education || currentProfile.education || '',
                                            lookingFor: wizardFormData.lookingFor || currentProfile.lookingFor || '',
                                            languages: wizardFormData.languages || currentProfile.languages || '',
                                            relationshipStatus: wizardFormData.relationshipStatus || currentProfile.relationshipStatus || '',
                                            preferredContactMethod: wizardFormData.preferredContactMethod || currentProfile.preferredContactMethod || ''
                                          };
                                          
                                          const response = await fetch(`${apiUrl}/api/v1/ai/bio-suggestions`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ profile: profileForAI, count: 5 })
                                          });
                                          
                                          if (response.ok) {
                                            const data = await response.json();
                                            if (data.status === 'success' && data.suggestions) {
                                              setAiBioSuggestions(data.suggestions);
                                            }
                                          } else {
                                            console.error('Failed to generate bio suggestions');
                                          }
                                        } catch (error) {
                                          console.error('Error generating bio suggestions:', error);
                                        } finally {
                                          setIsLoadingBioSuggestions(false);
                                        }
                                      }}
                                      disabled={isLoadingBioSuggestions}
                                      style={{
                                        padding: '4px 10px',
                                        background: isLoadingBioSuggestions ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        cursor: isLoadingBioSuggestions ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      {isLoadingBioSuggestions ? (
                                        <>
                                          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                                          Generating...
                                        </>
                                      ) : (
                                        <>
                                          ✨ Generate AI Suggestions
                                        </>
                                      )}
                                    </button>
                                  </div>
                                  {aiBioSuggestions.length > 0 && (
                                    <div style={{ 
                                      display: 'flex', 
                                      flexWrap: 'wrap',
                                      gap: '8px',
                                      marginTop: '4px'
                                    }}>
                                      {aiBioSuggestions.map((suggestion, idx) => (
                                        <button
                                          key={idx}
                                          onClick={() => handleSuggestionClick('bio', suggestion)}
                                          style={{
                                            padding: '8px 14px',
                                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '20px',
                                            fontSize: '12px',
                                            color: '#495057',
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            transition: 'all 0.2s',
                                            lineHeight: '1.4',
                                            maxWidth: '100%',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                          }}
                                          onMouseOver={(e) => {
                                            e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                                            e.target.style.color = 'white';
                                            e.target.style.borderColor = '#667eea';
                                            e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.4)';
                                          }}
                                          onMouseOut={(e) => {
                                            e.target.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
                                            e.target.style.color = '#495057';
                                            e.target.style.borderColor = '#dee2e6';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                                          }}
                                        >
                                          {suggestion}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  {!aiBioSuggestions.length && !isLoadingBioSuggestions && (
                                    <div style={{ 
                                      padding: '12px', 
                                      backgroundColor: '#f0f7ff', 
                                      borderRadius: '8px',
                                      border: '1px dashed #4a90e2',
                                      textAlign: 'center',
                                      color: '#666',
                                      fontSize: '12px'
                                    }}>
                                      Click the button above to generate personalized bio suggestions based on your profile data
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Regular Suggestion Cloud for other fields */}
                              {/* For tags, always show suggestions (like registration form). For other fields, only show when incomplete. */}
                              {((config.key === 'tagsList' && suggestions.length > 0) || (!isComplete && config.key !== 'bio' && suggestions.length > 0)) && (
                                <div style={{ marginTop: '8px' }}>
                                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>Suggestions:</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {suggestions.map((suggestion, idx) => {
                                      // For tags, check if already selected to show visual indicator
                                      const isTagSelected = config.suggestions === 'tags' && 
                                        (wizardFormData.tagsList || currentProfile.tagsList || []).includes(suggestion);
                                      
                                      return (
                                        <button
                                          key={idx}
                                          onClick={() => handleSuggestionClick(config.suggestions === 'tags' ? 'tags' : config.suggestions === 'socialLinks' ? 'socialLinks' : config.key, suggestion)}
                                          style={{
                                            padding: '6px 12px',
                                            backgroundColor: isTagSelected ? '#4a90e2' : '#f0f0f0',
                                            border: `1px solid ${isTagSelected ? '#4a90e2' : '#ddd'}`,
                                            borderRadius: '16px',
                                            fontSize: '12px',
                                            color: isTagSelected ? 'white' : '#333',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            opacity: isTagSelected ? 0.7 : 1
                                          }}
                                          onMouseOver={(e) => {
                                            if (!isTagSelected) {
                                              e.target.style.backgroundColor = '#4a90e2';
                                              e.target.style.color = 'white';
                                              e.target.style.borderColor = '#4a90e2';
                                            }
                                          }}
                                          onMouseOut={(e) => {
                                            if (!isTagSelected) {
                                              e.target.style.backgroundColor = '#f0f0f0';
                                              e.target.style.color = '#333';
                                              e.target.style.borderColor = '#ddd';
                                            }
                                          }}
                                        >
                                          {suggestion}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    </div>
                  </div>

                  {/* Save Button - Fixed Bottom */}
                  <div style={{ padding: '24px', paddingTop: '20px', flexShrink: 0, borderTop: '1px solid #eee' }}>
                    {percentage === 100 && fieldConfigs.length === 0 ? (
                      // When 100% complete and nothing to add, show only Close button
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button
                          onClick={() => setShowProfileCompletenessWizard(false)}
                          style={{
                            padding: '12px 24px',
                            background: '#4a90e2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            minWidth: '120px'
                          }}
                        >
                          Close
                        </button>
                      </div>
                    ) : (
                      // When there are changes to save, show both Cancel and Save buttons
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                        <button
                          onClick={() => setShowProfileCompletenessWizard(false)}
                          style={{
                            padding: '12px 24px',
                            background: '#f0f0f0',
                            color: '#333',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                          disabled={isSavingWizard}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveWizard}
                          disabled={isSavingWizard}
                          style={{
                            padding: '12px 24px',
                            background: isSavingWizard ? '#ccc' : '#4a90e2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: isSavingWizard ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            flex: 1
                          }}
                        >
                          {isSavingWizard ? 'Saving...' : 'Save All Changes'}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Review Form Modal */}
      {reviewFormVisible && currentProfile && walletAddress && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setReviewFormVisible(false);
          }
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {existingReview ? 'Edit Review' : 'Leave a Review'}
              </h2>
              <button
                onClick={() => setReviewFormVisible(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Rating *
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewRating(star)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '32px',
                      color: star <= reviewRating ? '#ffc107' : '#e0e0e0',
                      padding: '0',
                      lineHeight: 1,
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (star > reviewRating) {
                        e.target.style.color = '#ffc107';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (star > reviewRating) {
                        e.target.style.color = '#e0e0e0';
                      }
                    }}
                  >
                    ★
                  </button>
                ))}
                <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                  {reviewRating > 0 ? `${reviewRating} star${reviewRating !== 1 ? 's' : ''}` : 'Select rating'}
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Review (optional)
              </label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Share your experience with this user..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  minHeight: '100px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '8px', fontSize: '12px', lineHeight: '1.5' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ cursor: 'help', color: '#4a90e2', fontSize: '14px', fontWeight: 'bold' }}>?</span>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>Rating System</div>
                  <div style={{ marginBottom: '4px' }}>
                    Rate users from 1 to 5 stars. Your rating helps build trust in the community.
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>How it works:</strong> Each user starts with 0 stars. Ratings are averaged and displayed on profiles.
                  </div>
                  <div>
                    <strong>Appeals:</strong> If you believe a review is unfair, report it via "Report this user" on the reviewer's profile.
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              {existingReview && (
                <button
                  onClick={async () => {
                    if (!window.confirm('Are you sure you want to delete this review?')) {
                      return;
                    }
                    setIsDeletingReview(true);
                    try {
                      await deleteReview(existingReview.id, walletAddress);
                      
                      // Reload reviews and rating summary
                      const isOwnProfile = isMyProfile();
                      let profileWalletAddress = isOwnProfile 
                        ? walletAddress 
                        : (viewedProfileWalletAddress || currentProfile.walletAddress);
                      
                      if (!profileWalletAddress && currentProfile.username) {
                        try {
                          profileWalletAddress = await getWalletFromUsername(currentProfile.username);
                        } catch (error) {
                          console.error('Error getting wallet from username:', error);
                        }
                      }
                      
                      if (!profileWalletAddress && currentProfile.username) {
                        try {
                          profileWalletAddress = generateTemporaryWalletFromUsername(currentProfile.username);
                        } catch (error) {
                          console.error('Error generating temporary wallet address:', error);
                        }
                      }
                      
                      if (profileWalletAddress) {
                        const summary = await getUserRatingSummary(profileWalletAddress);
                        setUserRatingSummary(summary || { averageRating: 0, reviewCount: 0, totalRating: 0 });
                        
                        const reviews = await getUserReviews(profileWalletAddress, { limitCount: 50 });
                        setUserReviews(reviews || []);
                      }
                      
                      setExistingReview(null);
                      setReviewFormVisible(false);
                      setReviewRating(0);
                      setReviewText('');
                      
                      setNotification({
                        message: 'Review deleted successfully!',
                        type: 'success'
                      });
                      setTimeout(() => setNotification(null), 7000);
                    } catch (error) {
                      console.error('Error deleting review:', error);
                      setNotification({
                        message: error.message || 'Error deleting review. Please try again.',
                        type: 'error'
                      });
                      setTimeout(() => setNotification(null), 7000);
                    } finally {
                      setIsDeletingReview(false);
                    }
                  }}
                  disabled={isDeletingReview || isSubmittingReview}
                  style={{
                    padding: '10px 20px',
                    background: (isDeletingReview || isSubmittingReview) ? '#ccc' : '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (isDeletingReview || isSubmittingReview) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {isDeletingReview ? 'Deleting...' : 'Delete'}
                </button>
              )}
              <button
                onClick={() => {
                  setReviewFormVisible(false);
                  setReviewRating(0);
                  setReviewText('');
                }}
                disabled={isSubmittingReview}
                style={{
                  padding: '10px 20px',
                  background: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: isSubmittingReview ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (reviewRating < 1 || reviewRating > 5) {
                    setNotification({
                      message: 'Please select a rating from 1 to 5 stars',
                      type: 'error'
                    });
                    setTimeout(() => setNotification(null), 7000);
                    return;
                  }

                  // Use same logic as useEffect - if own profile, use walletAddress; otherwise use viewedProfileWalletAddress
                  const isOwnProfile = isMyProfile();
                  let profileWalletAddress = isOwnProfile 
                    ? walletAddress 
                    : (viewedProfileWalletAddress || currentProfile.walletAddress);
                  
                  // If no wallet address found, try to get it from username (similar to messaging system)
                  if (!profileWalletAddress && currentProfile.username) {
                    try {
                      profileWalletAddress = await getWalletFromUsername(currentProfile.username);
                    } catch (error) {
                      console.error('Error getting wallet from username:', error);
                    }
                  }
                  
                  // If still no wallet address, generate temporary one from username (for users without published wallets)
                  if (!profileWalletAddress && currentProfile.username) {
                    try {
                      profileWalletAddress = generateTemporaryWalletFromUsername(currentProfile.username);
                    } catch (error) {
                      console.error('Error generating temporary wallet address:', error);
                    }
                  }
                  
                  if (!profileWalletAddress) {
                    setNotification({
                      message: 'Cannot submit review: User wallet address not found',
                      type: 'error'
                    });
                    setTimeout(() => setNotification(null), 7000);
                    return;
                  }

                  if (walletAddress.toLowerCase() === profileWalletAddress.toLowerCase()) {
                    setNotification({
                      message: 'Cannot review yourself',
                      type: 'error'
                    });
                    setTimeout(() => setNotification(null), 7000);
                    return;
                  }

                  setIsSubmittingReview(true);
                  try {
                    await submitReview(walletAddress, profileWalletAddress, reviewRating, reviewText);
                    
                    // Reload ratings and reviews for the REVIEWED user (not the reviewer)
                    const summary = await getUserRatingSummary(profileWalletAddress);
                    setUserRatingSummary(summary || { averageRating: 0, reviewCount: 0, totalRating: 0 });
                    
                    const reviews = await getUserReviews(profileWalletAddress, { limitCount: 50 });
                    setUserReviews(reviews || []);
                    
                    // Reload existing review
                    const review = await getReviewByReviewer(walletAddress, profileWalletAddress);
                    setExistingReview(review);
                    
                    setReviewFormVisible(false);
                    // Use notification instead of alert
                    setNotification({
                      message: 'Review submitted successfully!',
                      type: 'success'
                    });
                    setTimeout(() => setNotification(null), 7000);
                  } catch (error) {
                    console.error('Error submitting review:', error);
                    setNotification({
                      message: error.message || 'Error submitting review. Please try again.',
                      type: 'error'
                    });
                    setTimeout(() => setNotification(null), 7000);
                  } finally {
                    setIsSubmittingReview(false);
                  }
                }}
                disabled={isSubmittingReview || reviewRating < 1}
                style={{
                  padding: '10px 20px',
                  background: (isSubmittingReview || reviewRating < 1) ? '#ccc' : '#4a90e2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (isSubmittingReview || reviewRating < 1) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {isSubmittingReview ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews List Modal */}
      {reviewsListModalVisible && currentProfile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setReviewsListModalVisible(false);
          }
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                Reviews ({userReviews.length})
              </h2>
              <button
                onClick={() => setReviewsListModalVisible(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px'
                }}
              >
                ×
              </button>
            </div>

            {userReviews.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {userReviews.map((review) => {
                  const isOwnReview = walletAddress && review.reviewerAddress && 
                    walletAddress.toLowerCase() === review.reviewerAddress.toLowerCase();
                  
                  return (
                    <div 
                      key={review.id} 
                      style={{
                        padding: '16px',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                          <div style={{ display: 'flex', gap: '2px', color: '#ffc107' }}>
                            {'★'.repeat(review.rating || 0)}
                            {'☆'.repeat(5 - (review.rating || 0))}
                          </div>
                          <span 
                            style={{ 
                              fontSize: '13px', 
                              color: '#4a90e2', 
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                            onClick={async () => {
                              // Close reviews list modal immediately
                              setReviewsListModalVisible(false);
                              
                              // Show profile sidebar with loading state
                              setProfileVisible(true);
                              setIsLoadingProfileFromIPFS(true);
                              
                              try {
                                // Get username from reviewer wallet address
                                let reviewerUsername = reviewerUsernames[review.reviewerAddress];
                                if (!reviewerUsername && review.reviewerAddress) {
                                  reviewerUsername = await getUsernameFromWallet(review.reviewerAddress);
                                  if (reviewerUsername) {
                                    setReviewerUsernames(prev => ({ ...prev, [review.reviewerAddress]: reviewerUsername }));
                                  }
                                }
                                if (reviewerUsername || review.reviewerAddress) {
                                  // Load the reviewer's profile (this will update isLoadingProfileFromIPFS)
                                  await loadUserProfile(review.reviewerAddress, true);
                                } else {
                                  setIsLoadingProfileFromIPFS(false);
                                  setNotification({
                                    message: 'Could not find reviewer profile',
                                    type: 'error'
                                  });
                                  setTimeout(() => setNotification(null), 7000);
                                }
                              } catch (error) {
                                console.error('Error loading reviewer profile:', error);
                                setIsLoadingProfileFromIPFS(false);
                                setNotification({
                                  message: 'Error loading reviewer profile',
                                  type: 'error'
                                });
                                setTimeout(() => setNotification(null), 7000);
                              }
                            }}
                          >
                            {review.reviewerAddress 
                              ? (reviewerUsernames[review.reviewerAddress] 
                                  ? `@${reviewerUsernames[review.reviewerAddress]}` 
                                  : `@${review.reviewerAddress.substring(0, 6)}...${review.reviewerAddress.substring(review.reviewerAddress.length - 4)}`)
                              : 'Anonymous'}
                          </span>
                          <span style={{ fontSize: '12px', color: '#999' }}>
                            • {review.createdAt?.toDate ? new Date(review.createdAt.toDate()).toLocaleDateString() : 
                               review.timestamp ? new Date(review.timestamp).toLocaleDateString() : 'Recently'}
                          </span>
                        </div>
                        {isOwnReview && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => {
                                setReviewFormVisible(true);
                                setReviewsListModalVisible(false);
                                setExistingReview(review);
                                setReviewRating(review.rating || 0);
                                setReviewText(review.reviewText || '');
                              }}
                              style={{
                                padding: '6px 12px',
                                background: '#4a90e2',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={async () => {
                                if (!window.confirm('Are you sure you want to delete this review?')) {
                                  return;
                                }
                                setIsDeletingReview(true);
                                try {
                                  await deleteReview(review.id, walletAddress);
                                  
                                  // Reload reviews and rating summary
                                  const isOwnProfile = isMyProfile();
                                  let profileWalletAddress = isOwnProfile 
                                    ? walletAddress 
                                    : (viewedProfileWalletAddress || currentProfile.walletAddress);
                                  
                                  if (!profileWalletAddress && currentProfile.username) {
                                    try {
                                      profileWalletAddress = await getWalletFromUsername(currentProfile.username);
                                    } catch (error) {
                                      console.error('Error getting wallet from username:', error);
                                    }
                                  }
                                  
                                  if (!profileWalletAddress && currentProfile.username) {
                                    try {
                                      profileWalletAddress = generateTemporaryWalletFromUsername(currentProfile.username);
                                    } catch (error) {
                                      console.error('Error generating temporary wallet address:', error);
                                    }
                                  }
                                  
                                  if (profileWalletAddress) {
                                    const summary = await getUserRatingSummary(profileWalletAddress);
                                    setUserRatingSummary(summary || { averageRating: 0, reviewCount: 0, totalRating: 0 });
                                    
                                    const reviews = await getUserReviews(profileWalletAddress, { limitCount: 50 });
                                    setUserReviews(reviews || []);
                                  }
                                  
                                  setNotification({
                                    message: 'Review deleted successfully!',
                                    type: 'success'
                                  });
                                  setTimeout(() => setNotification(null), 7000);
                                } catch (error) {
                                  console.error('Error deleting review:', error);
                                  setNotification({
                                    message: error.message || 'Error deleting review. Please try again.',
                                    type: 'error'
                                  });
                                  setTimeout(() => setNotification(null), 7000);
                                } finally {
                                  setIsDeletingReview(false);
                                }
                              }}
                              disabled={isDeletingReview}
                              style={{
                                padding: '6px 12px',
                                background: isDeletingReview ? '#ccc' : '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: isDeletingReview ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}
                            >
                              {isDeletingReview ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        )}
                      </div>
                      {review.reviewText && review.reviewText.trim() && (
                        <div style={{ fontSize: '13px', color: '#333', lineHeight: '1.5', marginTop: '8px' }}>
                          {review.reviewText}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No reviews yet
              </div>
            )}

            {walletAddress && (!currentUser || currentProfile.username !== currentUser) && (
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setReviewsListModalVisible(false);
                    setReviewFormVisible(true);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {existingReview ? 'Edit Review' : 'Leave a Review'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {transferModalVisible && (
        <div
          onClick={closeTransferModal}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '420px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Send USDC</h3>
              <button
                onClick={closeTransferModal}
                disabled={isTransferring}
                style={{ border: 'none', background: 'transparent', fontSize: '20px', cursor: isTransferring ? 'not-allowed' : 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#666' }}>
              Available: <strong>{isLoadingUsdcBalance ? '…' : formatUsdcBalance(usdcBalance)}</strong> USDC on Arc Testnet
            </div>

            <label style={{ display: 'block', fontSize: '13px', color: '#333', marginBottom: '4px' }}>
              Recipient (PeopleTag username)
            </label>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <input
                type="text"
                value={transferRecipientInput}
                onChange={(e) => {
                  setTransferRecipientInput(e.target.value);
                  setTransferSelectedProfile(null);
                  setTransferShowSuggestions(true);
                  setTransferError('');
                }}
                onFocus={() => setTransferShowSuggestions(true)}
                onBlur={() => setTimeout(() => setTransferShowSuggestions(false), 150)}
                placeholder="@username"
                disabled={isTransferring}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: transferSelectedProfile ? '1px solid #4a90e2' : '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
              {transferShowSuggestions && (() => {
                const raw = (transferRecipientInput || '').trim().replace(/^@/, '').toLowerCase();
                const pool = Array.isArray(allProfiles) ? allProfiles.filter(p => {
                  if (!p || !p.username) return false;
                  if (walletAddress && p.walletAddress && p.walletAddress.toLowerCase() === walletAddress.toLowerCase()) return false;
                  return true;
                }) : [];
                const matches = (!raw ? pool : pool.filter(p => {
                  const u = (p.username || '').toLowerCase();
                  const n = (p.name || '').toLowerCase();
                  return u.includes(raw) || n.includes(raw);
                })).slice(0, 8);
                if (matches.length === 0) return null;
                return (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      maxHeight: '220px',
                      overflowY: 'auto',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      zIndex: 30000
                    }}
                  >
                    {matches.map(p => {
                      const isTest = p._isTestRecord || isTempWalletAddress(p.walletAddress) || !p.walletAddress;
                      return (
                        <div
                          key={p.username}
                          onMouseDown={(e) => { e.preventDefault(); selectTransferRecipient(p); }}
                          style={{
                            padding: '8px 10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5faff'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                        >
                          <div style={{ minWidth: 0, overflow: 'hidden' }}>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#0a3d80', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              @{p.username}
                            </div>
                            {p.name && (
                              <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {p.name}
                              </div>
                            )}
                          </div>
                          {isTest && (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 600,
                              color: '#a06200',
                              backgroundColor: '#fff3dc',
                              border: '1px solid #f0d9a0',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              whiteSpace: 'nowrap'
                            }}>
                              test user
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            {transferSelectedProfile && (
              (() => {
                const isTest = transferSelectedProfile._isTestRecord || isTempWalletAddress(transferSelectedProfile.walletAddress) || !transferSelectedProfile.walletAddress;
                return (
                  <div style={{
                    marginTop: '-6px',
                    marginBottom: '12px',
                    fontSize: '12px',
                    color: isTest ? '#a06200' : '#5a7fb0'
                  }}>
                    {isTest
                      ? 'Test user — has no real wallet, sending is disabled.'
                      : `Sending to ${transferSelectedProfile.walletAddress.slice(0, 6)}…${transferSelectedProfile.walletAddress.slice(-4)}`}
                  </div>
                );
              })()
            )}

            <label style={{ display: 'block', fontSize: '13px', color: '#333', marginBottom: '4px' }}>
              Amount (USDC)
            </label>
            <input
              type="number"
              min="0"
              step="0.000001"
              value={transferAmount}
              onChange={(e) => setTransferAmount(e.target.value)}
              placeholder="0.00"
              disabled={isTransferring}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '12px',
                boxSizing: 'border-box'
              }}
            />

            {transferError && (
              <div style={{ color: '#cc2d2d', fontSize: '13px', marginBottom: '12px', wordBreak: 'break-word' }}>
                {transferError}
              </div>
            )}
            {transferTxHash && (
              <div style={{ color: '#0a8a3a', fontSize: '13px', marginBottom: '12px', wordBreak: 'break-all' }}>
                Sent! Will be there in seconds. Tx:{' '}
                <a
                  href={`${ARC_TESTNET_NETWORK.blockExplorer}/tx/${transferTxHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#0a8a3a', textDecoration: 'underline' }}
                >
                  {transferTxHash}
                </a>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeTransferModal}
                disabled={isTransferring}
                style={{
                  padding: '8px 14px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  cursor: isTransferring ? 'not-allowed' : 'pointer'
                }}
              >
                {transferTxHash ? 'Close' : 'Cancel'}
              </button>
              {!transferTxHash && (() => {
                const selectedIsTest = transferSelectedProfile && (
                  transferSelectedProfile._isTestRecord ||
                  isTempWalletAddress(transferSelectedProfile.walletAddress) ||
                  !transferSelectedProfile.walletAddress
                );
                const disabled = isTransferring || !transferSelectedProfile || selectedIsTest || !transferAmount;
                return (
                  <button
                    onClick={handleSendUSDC}
                    disabled={disabled}
                    style={{
                      padding: '8px 14px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: '#4a90e2',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.6 : 1
                    }}
                  >
                    {isTransferring ? 'Sending…' : 'Send'}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

    </>
  );

  return (
    <div className={`App ${isMobile ? 'mobile' : ''}`}>
      {isMobile ? (
        <MobileLayout header={mobileHeader} bottomNav={mobileBottomNav}>
          {appBody}
        </MobileLayout>
      ) : (
        appBody
      )}
    </div>
  );
}

export default App;





